import React, { useState, useEffect, useMemo, useRef } from 'react';
import './App.css';
import { useLanguage } from './contexts/LanguageContext';
import { useAppContext, registerVoid } from './contexts/AppContext';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
// Real Supabase Auth -sends actual password reset emails
import { auth, db } from './supabaseAuth';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  confirmPasswordReset,
  signOut,
  onAuthStateChanged,
  initialUrlSnapshot,
  isRecoveryUrl,
} from './supabaseAuth';
import { doc, setDoc, getDoc, sendOtpToEmail, verifyEmailOtp } from './supabaseAuth';
import { syncCollection, fetchAllData, deleteAllUserData, uploadBusinessLogo, deleteBusinessLogo, getConflictLog } from './supabaseDB';
import {
  PLANS,
  loadSubscription,
  saveSubscription,
  initTrialSubscription,
  isSubscriptionActive,
  getDaysRemaining,
  createPayment,
  pollPaymentVerification,
  activateSubscription,
  fetchSubscriptionStatus,
} from './subscriptionService';
import { computeWAC, computeSaleTotals, saleVAT, purchaseVAT, saleTaxOfType, purchaseTaxOfType, grossProfitExVAT, periodProfitExVAT, netRevenueExVAT, cogsSnapshotVsReplay, perProductCOGSBreakdown, periodReplayCOGS, normProductName } from './moneyMath';
import { activeRecords, softDeleteById, mergeById, tombstone, archiveById, notArchived } from './syncMerge';
import { getPeriodRange, inPeriod, dateKey } from './periods';
import { lineMatchesProduct, reconcileProductStock, rebuildStoredStock, returnsWithApproxCost } from './inventory';
import { historicalCostStockExceptions, exceptionsToCSV, fixReturnDate, backfillZeroCostLines, productTimeline, replaySaleUnitCosts } from './costAudit';
import { getSaleDependencies, getPurchaseDependencies, buildDeletePreview, validReturns, quarantinedReturns } from './deleteIntegrity';
import { buildCashLedger, walletBalance as ledgerWalletBalance, aggregateBalances as ledgerAggregate, isBridgeManualTransaction, walletStatement } from './cashLedger';
import { useBusiness } from './contexts/BusinessContext';
import { usePermissions, usePermissionMatrix, PERMISSION_GRID, ROLE_INFO } from './hooks/usePermissions';
import { persistSale, persistPurchase, persistExpense, persistServiceTransaction, persistSalesReturn, persistPurchaseReturn, persistCashAdjustment, persistProduct, persistCustomer, persistSupplier, persistExpenseCategory, persistService, persistPaymentAccount, persistInvoice, bridgeRecordSalePayment, bridgeRecordPurchasePayment, bridgeRecordInvoiceDeposit, bridgeConvertInvoiceToSale, resolveRid } from './services/bridge';
import { fetchAllBusinessData, fetchTeamMembers, fetchAvailableRoles, sendInviteEmail } from './services/queries';
import { inviteMember, acceptInvitation, revokeInvitation, changeMemberRole, setMemberStatus, transferOwnership, leaveTeam, voidSale, voidPurchase, voidExpense, voidServiceTxn, voidSalesReturn, voidPurchaseReturn } from './services/commands';
import { adaptAllBusinessData } from './services/dataAdapter';

// Plan-specific ClickPesa payment page URLs
const CLICKPESA_PLAN_URLS = {
  faida_basic:   process.env.REACT_APP_CLICKPESA_URL_BASIC   || '',
  faida_growth:  process.env.REACT_APP_CLICKPESA_URL_GROWTH  || '',
  faida_pro:     process.env.REACT_APP_CLICKPESA_URL_PRO     || '',
  faida_premium: process.env.REACT_APP_CLICKPESA_URL_PREMIUM || '',
};

// ── Privacy helpers ────────────────────────────────────────────────
// SHA-256 hash of a string (async). Returns hex string. Used to store PIN + recovery code.
const sha256Hex = async (s) => {
  try {
    const buf = new TextEncoder().encode(String(s));
    const hash = await crypto.subtle.digest('SHA-256', buf);
    return Array.from(new Uint8Array(hash)).map(b => b.toString(16).padStart(2, '0')).join('');
  } catch {
    // Fallback for environments without SubtleCrypto (rare -old browsers).
    // Weaker but still not plaintext. In practice this branch never runs on modern devices.
    let h = 0;
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
    return String(h);
  }
};
// Human-readable one-time recovery code, e.g. "TRACK-9K7X-M2AB". Excludes ambiguous chars.
const generateRecoveryCode = () => {
  const ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // no O/0, I/1
  const chunk = (n) => Array.from({length: n}, () => ALPHABET[Math.floor(Math.random() * ALPHABET.length)]).join('');
  return `TRACK-${chunk(4)}-${chunk(4)}`;
};

// ── Relational id guard ──────────────────────────────────────────────────────
// Every commands.void_* function casts its id parameter to uuid, e.g.
//   v_sale uuid := (p_payload->>'sale_id')::uuid;   (021_write_commands.up.sql)
// A locally-created record's id is Date.now() (a number), so sending it makes
// Postgres raise "invalid input syntax for type uuid" -the void can never
// succeed. Detecting that here lets us report the real reason instead of
// surfacing an opaque server error, and avoids two pointless round trips.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isRelationalUuid = (v) => typeof v === 'string' && UUID_RE.test(v.trim());

const downloadCSV = (rows, filename) => {
  const csv = (rows || []).map(r => (r || []).map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\r\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const downloadExcel = (rows, filename) => {
  const ws = XLSX.utils.aoa_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Report');
  XLSX.writeFile(wb, filename);
};

// Multi-sheet XLSX export. `sheets` is an array of { name, rows } objects.
const downloadExcelMultiSheet = (sheets, filename) => {
  const wb = XLSX.utils.book_new();
  sheets.forEach(({ name, rows }) => {
    const ws = XLSX.utils.aoa_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, name);
  });
  XLSX.writeFile(wb, filename);
};

const exportAllDataToExcel = ({ products, sales, purchases, expenses, customers, suppliers, expenseCategories, businessName }) => {
  const wb = XLSX.utils.book_new();
  const addSheet = (name, headers, rows) => {
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    XLSX.utils.book_append_sheet(wb, ws, name);
  };
  // ── Sales: order-level summary + item-level detail (best-practice 2-sheet export) ──
  addSheet('Sales Orders',
    ['Date', 'Invoice', 'Customer', 'Item Count', 'Total Qty', 'Subtotal', 'Discount %', 'Tax', 'Grand Total', 'Payment', 'Status'],
    sales.map(s => {
      const items = Array.isArray(s.items) && s.items.length > 0 ? s.items : [{ productName: s.productName, quantity: s.quantity, sellingPrice: s.sellingPrice }];
      const subTotal = items.reduce((sum, it) => {
        const qty = it.type === 'service' ? 1 : parseFloat(it.quantity || 0);
        return sum + qty * parseFloat(it.sellingPrice || 0);
      }, 0);
      const totalQty = items.reduce((sum, it) => sum + parseFloat(it.quantity || 0), 0);
      const discPct = parseFloat(s.discount || 0);
      const taxRate = (s.taxes || []).reduce((sum, t) => sum + parseFloat(t.rate || 0), 0);
      const grand = parseFloat(s.totalRevenue || 0);
      const tax = Math.max(0, grand - subTotal * (1 - discPct / 100));
      return [s.date, s.invoiceNumber || '—', s.customerName || 'Walk-in', items.length, totalQty, subTotal.toFixed(2), discPct, tax.toFixed(2), grand.toFixed(2), s.paymentMethod || 'cash', s.isPaid === false ? 'Unpaid' : 'Paid'];
    })
  );
  addSheet('Sales Line Items',
    ['Date', 'Invoice', 'Customer', 'Product', 'Qty', 'Unit Price', 'Line Total', 'Cost', 'Order Total', 'Payment', 'Status'],
    sales.flatMap(s => {
      const items = Array.isArray(s.items) && s.items.length > 0 ? s.items : [{ productName: s.productName, quantity: s.quantity, sellingPrice: s.sellingPrice, cost: s.cost }];
      return items.map(it => {
        const qty = it.type === 'service' ? 1 : parseFloat(it.quantity || 0);
        const price = parseFloat(it.sellingPrice || 0);
        return [s.date, s.invoiceNumber || '—', s.customerName || 'Walk-in', it.productName || '—', qty, price.toFixed(2), (qty * price).toFixed(2), parseFloat(it.cost || 0).toFixed(2), parseFloat(s.totalRevenue || 0).toFixed(2), s.paymentMethod || 'cash', s.isPaid === false ? 'Unpaid' : 'Paid'];
      });
    })
  );
  // ── Purchases: order-level summary + item-level detail ──
  addSheet('Purchase Orders',
    ['Date', 'Supplier', 'Item Count', 'Total Qty', 'Subtotal', 'Discount %', 'Tax', 'Grand Total', 'Payment', 'Status'],
    purchases.map(p => {
      const items = Array.isArray(p.items) && p.items.length > 0 ? p.items : [{ productName: p.productName, quantity: p.quantity, unitPrice: p.unitPrice, totalCost: p.totalCost }];
      const subTotal = items.reduce((sum, it) => sum + parseFloat(it.totalCost || 0), 0);
      const totalQty = items.reduce((sum, it) => sum + parseFloat(it.quantity || 0), 0);
      const discPct = parseFloat(p.discount || 0);
      const grand = parseFloat(p.totalCost || subTotal);
      const tax = Math.max(0, grand - subTotal * (1 - discPct / 100));
      return [p.date, p.supplier || '—', items.length, totalQty, subTotal.toFixed(2), discPct, tax.toFixed(2), grand.toFixed(2), p.paymentMethod || 'cash', p.isPaid === false ? 'Unpaid' : 'Paid'];
    })
  );
  addSheet('Purchase Line Items',
    ['Date', 'Supplier', 'Product', 'Qty', 'Unit Cost', 'Line Total', 'Order Total', 'Payment', 'Status'],
    purchases.flatMap(p => {
      const items = Array.isArray(p.items) && p.items.length > 0 ? p.items : [{ productName: p.productName, quantity: p.quantity, unitPrice: p.unitPrice, totalCost: p.totalCost }];
      return items.map(it => {
        const qty = parseFloat(it.quantity || 0);
        const unit = parseFloat(it.unitPrice ?? it.unitCost ?? (qty > 0 ? parseFloat(it.totalCost || 0) / qty : 0));
        return [p.date, p.supplier || '—', it.productName || '—', qty, unit.toFixed(2), parseFloat(it.totalCost || 0).toFixed(2), parseFloat(p.totalCost || 0).toFixed(2), p.paymentMethod || 'cash', p.isPaid === false ? 'Unpaid' : 'Paid'];
      });
    })
  );
  addSheet('Expenses',
    ['Date', 'Description', 'Category', 'Amount', 'Payment', 'Status'],
    expenses.map(e => [e.date, e.description, e.category || '—', e.amount, e.paymentMethod || 'cash', e.isPaid === false ? 'Unpaid' : 'Paid'])
  );
  addSheet('Products',
    ['Name', 'Category', 'Stock', 'Buying Price', 'Selling Price'],
    products.map(p => [p.productName || '—', p.category || '—', p.balance ?? p.currentStock ?? p.openingStockQty ?? 0, p.buyingPrice || p.costPrice || 0, p.sellingPrice || 0])
  );
  addSheet('Customers',
    ['Name', 'Phone', 'Email', 'Address'],
    customers.map(c => [c.name, c.phone || '—', c.email || '—', c.address || '—'])
  );
  addSheet('Suppliers',
    ['Name', 'Phone', 'Email', 'Address'],
    suppliers.map(s => [s.name, s.phone || '—', s.email || '—', s.address || '—'])
  );
  const biz = businessName || 'faida';
  const date = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(wb, `${biz.replace(/\s+/g, '-')}-data-export-${date}.xlsx`);
};

const downloadPDF = (headers, rows, title, filename) => {
  const doc = new jsPDF({ orientation: rows[0] && rows[0].length > 6 ? 'landscape' : 'portrait' });
  doc.setFontSize(14);
  doc.text(title, 14, 16);
  autoTable(doc, {
    head: [headers],
    body: rows,
    startY: 24,
    styles: { fontSize: 8 },
    headStyles: { fillColor: [163, 230, 53], textColor: [17, 24, 39] }
  });
  doc.save(filename);
};

const compressAndValidateImage = (file, onSuccess, onError) => {
  const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB max
  const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

  // Validate file type
  if (!ALLOWED_TYPES.includes(file.type)) {
    onError('Please upload a JPG, PNG, or WebP image file');
    return;
  }

  // Validate file size
  if (file.size > MAX_FILE_SIZE) {
    onError(`Image is too large. Maximum size is 2MB. Your image is ${(file.size / 1024 / 1024).toFixed(1)}MB`);
    return;
  }

  // Read and compress the image
  const reader = new FileReader();
  reader.onload = (ev) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // Resize to max 200x200 for logo
      let width = img.width;
      let height = img.height;
      const maxSize = 200;

      if (width > height) {
        if (width > maxSize) {
          height = Math.round(height * (maxSize / width));
          width = maxSize;
        }
      } else {
        if (height > maxSize) {
          width = Math.round(width * (maxSize / height));
          height = maxSize;
        }
      }

      canvas.width = width;
      canvas.height = height;

      // Fill white background for PNGs with transparency
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, width, height);

      // Draw the image
      ctx.drawImage(img, 0, 0, width, height);

      // Convert to base64 with compression
      // Try JPEG first for smaller file size, fall back to PNG if needed
      let compressedBase64;
      try {
        compressedBase64 = canvas.toDataURL('image/jpeg', 0.85); // 85% quality JPEG
        // Check if result is too large, if so try lower quality
        if (compressedBase64.length > 300000) { // ~300KB base64 (larger than file size due to encoding)
          compressedBase64 = canvas.toDataURL('image/jpeg', 0.7); // 70% quality
        }
      } catch (e) {
        // Fall back to PNG if JPEG fails
        try {
          compressedBase64 = canvas.toDataURL('image/png');
        } catch (e2) {
          onError('Failed to process image. Please try a different file.');
          return;
        }
      }
      onSuccess(compressedBase64);
    };
    img.onerror = () => onError('Failed to process image. Please try a different file.');
    img.src = ev.target.result;
  };
  reader.onerror = () => onError('Failed to read image file');
  reader.readAsDataURL(file);
};

const amountInWords = (amount) => {
  const ones = ['','ONE','TWO','THREE','FOUR','FIVE','SIX','SEVEN','EIGHT','NINE','TEN','ELEVEN','TWELVE','THIRTEEN','FOURTEEN','FIFTEEN','SIXTEEN','SEVENTEEN','EIGHTEEN','NINETEEN'];
  const tens = ['','','TWENTY','THIRTY','FORTY','FIFTY','SIXTY','SEVENTY','EIGHTY','NINETY'];
  const toW = (n) => {
    if (n === 0) return '';
    if (n < 20) return ones[n];
    if (n < 100) return tens[Math.floor(n/10)] + (n%10 ? ' '+ones[n%10] : '');
    if (n < 1000) return ones[Math.floor(n/100)] + ' HUNDRED' + (n%100 ? ' '+toW(n%100) : '');
    if (n < 1e6) return toW(Math.floor(n/1000)) + ' THOUSAND' + (n%1000 ? ' '+toW(n%1000) : '');
    if (n < 1e9) return toW(Math.floor(n/1e6)) + ' MILLION' + (n%1e6 ? ' '+toW(n%1e6) : '');
    return toW(Math.floor(n/1e9)) + ' BILLION' + (n%1e9 ? ' '+toW(n%1e9) : '');
  };
  const num = Math.floor(Math.abs(amount || 0));
  const cents = Math.round((Math.abs(amount || 0) - num) * 100);
  let w = toW(num) || 'ZERO';
  if (cents > 0) w += ' AND ' + toW(cents) + ' CENTS';
  return w + ' ONLY';
};

const generateInvoicePDF = (sale, bizInfo, paymentAccounts = []) => {
  if (typeof bizInfo === 'string') bizInfo = { name: bizInfo };
  const { name: bizName = 'My Business', address: bizAddr = '', logo: bizLogo = '',
    tin = '', vrn = '', regNo = '', licenseNo = '',
    invoiceTerms: bizTerms = ''
  } = bizInfo;

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const M = 14;
  const innerW = W - M * 2;
  const rEdge = W - M;
  const fmtN = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const ucFirst = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : '';

  // Shared palette (matches proforma)
  const GRN   = [26, 75, 58];
  const DARK  = [26, 26, 46];
  const TEXT  = [40, 40, 40];
  const GRAY  = [110, 110, 110];
  const PGBG  = [240, 242, 245];
  const BORDER= [210, 215, 220];
  const WHITE = [255, 255, 255];
  const STRIPE= [248, 250, 252];

  // Payments are still used later in the Payment Breakdown section.
  const _pmts = sale.payments || [];

  // Page background + white content area
  doc.setFillColor(...PGBG); doc.rect(0, 0, W, H, 'F');
  doc.setFillColor(...WHITE); doc.rect(M - 3, M - 3, innerW + 6, H - 2 * M + 6, 'F');

  // ── HEADER -taller header so logo + name + multi-line address breathe ──
  const hdrY = M + 4;
  const logoW = 38, logoH = 38;
  const logoX = M + 2;

  if (bizLogo) {
    try { doc.addImage(bizLogo, 'PNG', logoX, hdrY, logoW, logoH); } catch(e) {}
  } else {
    doc.setFillColor(232, 235, 238);
    doc.setDrawColor(...BORDER); doc.setLineWidth(0.5);
    doc.rect(logoX, hdrY, logoW, logoH, 'FD');
  }

  const nameX = logoX + logoW + 10;
  doc.setFont('helvetica', 'bold'); doc.setFontSize(20); doc.setTextColor(...DARK);
  doc.text(bizName, nameX, hdrY + 11, { maxWidth: 100 });
  if (bizAddr) {
    // Allow address to wrap to multiple lines
    const addrLines = doc.splitTextToSize(bizAddr, 100);
    doc.setFont('helvetica', 'italic'); doc.setFontSize(8.5); doc.setTextColor(...GRAY);
    addrLines.forEach((ln, i) => doc.text(ln, nameX, hdrY + 19 + i * 4.5));
  }
  const cmpParts = [];
  if (tin) cmpParts.push(`TIN: ${tin}`);
  if (vrn) cmpParts.push(`VRN: ${vrn}`);
  if (regNo) cmpParts.push(`Reg: ${regNo}`);
  if (licenseNo) cmpParts.push(`Lic: ${licenseNo}`);
  if (cmpParts.length > 0) {
    doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.setTextColor(...GRAY);
    // Drop the cmp line near the bottom of the logo
    doc.text(cmpParts.join('  |  '), nameX, hdrY + logoH - 2);
  }

  // Big bold INVOICE word (Kenran-style) at the right
  doc.setFont('helvetica', 'bold'); doc.setFontSize(32); doc.setTextColor(...DARK);
  doc.text('INVOICE', rEdge, hdrY + 22, { align: 'right' });

  // Thick separator -sits below the bigger logo for breathing room
  const sepY = hdrY + logoH + 5;
  doc.setDrawColor(...BORDER); doc.setLineWidth(0.5);
  doc.line(M, sepY, rEdge, sepY);

  // ── 3-COLUMN INFO ROW: Bill To | Issue Date | Status ─────────────────
  const infoY = sepY + 5;
  // Kenran-style 2-column info row: BILL TO (left) + label-value list (right)
  const sectionLabel = (txt, x, y) => {
    doc.setFont('helvetica', 'bold'); doc.setFontSize(7.5); doc.setTextColor(...GRAY);
    doc.text(txt, x, y);
  };

  // ── Left side: BILL TO ─────────────────────────────────────────────
  doc.setFont('helvetica', 'bold'); doc.setFontSize(8); doc.setTextColor(...GRAY);
  doc.text('To:', M + 2, infoY + 1);
  let bY = infoY + 7;
  if (sale.customerName) {
    doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.setTextColor(...DARK);
    doc.text(sale.customerName, M + 2, bY, { maxWidth: innerW * 0.55 });
    bY += 5.5;
    const cd = sale.customerDetails || {};
    [cd.phone, cd.email, cd.address,
      cd.tin ? `TIN: ${cd.tin}` : null,
      cd.vrn ? `VRN: ${cd.vrn}` : null,
    ].filter(Boolean).forEach(line => {
      doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); doc.setTextColor(...GRAY);
      doc.text(line, M + 2, bY, { maxWidth: innerW * 0.55 });
      bY += 5;
    });
  } else {
    doc.setFont('helvetica', 'italic'); doc.setFontSize(10); doc.setTextColor(...GRAY);
    doc.text('Walk-in customer', M + 2, bY);
    bY += 5;
  }

  // ── Right side: label-value list (Date / Invoice No. / Status) ──────
  // Labels are left-aligned in the right column, values are right-aligned
  // against the page edge -like the Kenran reference.
  const rightColX = M + innerW * 0.6;       // left edge of the right column
  const labelX    = rightColX;
  const valueX    = rEdge;
  let kvY = infoY + 1;
  const drawKV = (label, value, valueBold = true) => {
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(...GRAY);
    doc.text(label, labelX, kvY);
    if (valueBold) {
      doc.setFont('helvetica', 'bold'); doc.setFontSize(9.5); doc.setTextColor(...DARK);
    } else {
      doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(...TEXT);
    }
    doc.text(String(value || '—'), valueX, kvY, { align: 'right' });
    kvY += 6.5;
  };

  drawKV('Date:', sale.invoiceIssuedDate || sale.date || '—');
  drawKV('Invoice No.:', sale.invoiceNumber || '—');
  // If invoice was issued on a different day than the sale, surface both
  if (sale.invoiceIssuedDate && sale.date && sale.invoiceIssuedDate !== sale.date) {
    drawKV('Sale Date:', sale.date, false);
  }
  // Status removed per user request -payment status is communicated via the
  // payment breakdown / totals section instead.

  // divider below info row -accounts for whichever column is taller
  const billHeightEst = bY - infoY + 2;
  const rightHeightEst = kvY - infoY + 2;
  const dividerY = infoY + Math.max(billHeightEst, rightHeightEst, 24);
  doc.setDrawColor(...BORDER); doc.setLineWidth(0.3);
  doc.line(M, dividerY, rEdge, dividerY);

  // ── DYNAMIC SIZING ────────────────────────────────────────────────────
  // Pre-compute approximate heights of post-items content so we can decide
  // how generously to render the items table:
  //   - Few items (1-3) on a single page → expand row height + add padding
  //     before totals so the page looks balanced top-to-bottom.
  //   - Many items → use compact rows, paginate.
  // Normalize service / product key aliases so the same template renders both.
  // Service records may have { serviceName, price } instead of { productName, sellingPrice }.
  const _normItem = it => ({
    ...it,
    productName: it.productName || it.serviceName || it.name || '—',
    sellingPrice: it.sellingPrice != null && it.sellingPrice !== ''
      ? it.sellingPrice
      : (it.price != null ? it.price : 0),
    quantity: it.quantity || 1,
  });
  const saleItemRows = (sale.items && sale.items.length > 0)
    ? sale.items.map(_normItem)
    : [_normItem({ type: sale.isService ? 'service' : 'product', productName: sale.productName || sale.serviceName, quantity: sale.quantity || '1', sellingPrice: sale.sellingPrice != null ? sale.sellingPrice : sale.price })];
  const N = saleItemRows.length;

  // Pre-compute totals (needed before drawing items so we can size correctly).
  // Both products and services compute as qty × price -services with hours/sessions
  // are honored correctly (previous code force-cast service qty to 1).
  let _subtotalAll = 0;
  saleItemRows.forEach(item => {
    const iqty = parseFloat(item.quantity || 1);
    const iPrice = parseFloat(item.sellingPrice || 0);
    _subtotalAll += iqty * iPrice;
  });
  const discAmtInv = sale.discountAmount != null
    ? parseFloat(sale.discountAmount)
    : _subtotalAll * (parseFloat(sale.discount || 0) / 100);
  const afterDiscountInv = Math.max(0, _subtotalAll - discAmtInv);
  const taxes = sale.taxes || [];
  let totalTax = 0;
  taxes.forEach(tax => { totalTax += afterDiscountInv * (parseFloat(tax.rate) || 0) / 100; });
  const grandTotal = afterDiscountInv + totalTax;

  // Estimate post-items content height
  const _taxLines = Math.max(1, taxes.length);
  const _totalsH = 5.5 + (discAmtInv > 0 ? 11 : 0) + _taxLines * 5.5 + 14;
  const _efdH = sale.efdReceiptNo ? 10 : 0;
  const _notesH = (sale.invoiceNotes && sale.invoiceNotes.trim())
    ? 10 + Math.ceil(sale.invoiceNotes.length / 80) * 5 + 5
    : 0;
  const _wordsH = grandTotal > 0 ? 14 : 0;
  const _invFooterSec = Array.isArray(sale.footerSections)
    ? sale.footerSections.filter(s => s.content && s.content.trim())
    : (bizTerms && bizTerms.trim() ? [{ label: 'Terms & Conditions', content: bizTerms }] : []);
  let _footerSecH = 0;
  if (_invFooterSec.length === 1) {
    _footerSecH = 8 + _invFooterSec[0].content.split('\n').filter(l => l.trim()).length * 6;
  } else if (_invFooterSec.length >= 2) {
    const lc = _invFooterSec[0].content.split('\n').filter(l => l.trim()).length;
    const rc = _invFooterSec[1].content.split('\n').filter(l => l.trim()).length;
    _footerSecH = 8 + Math.max(lc, rc) * 6;
  }
  const _acctsList = (paymentAccounts || []).filter(a => a.type !== 'cash');
  const _paymentInfoH = _acctsList.length > 0 ? 22 + 14 : 0;
  const _belowItemsH = _totalsH + _efdH + _notesH + _wordsH + _footerSecH + _paymentInfoH;

  // Items table -start position + available height
  const tblTop = dividerY + 3;
  const FOOTER_H = 26;
  const availForItems = (H - FOOTER_H - 4) - tblTop - _belowItemsH;

  // Compute dynamic row height. ROW_MIN compact (for many items), ROW_MAX
  // expanded (for few items). Header is fixed at 7mm.
  const ROW_MIN = 6, ROW_TARGET = 8, ROW_MAX = 13;
  const HDR_H = 7;
  let ROW_H;
  if (N === 0) ROW_H = ROW_TARGET;
  else if (N * ROW_TARGET + HDR_H >= availForItems) {
    // Too many items for one page comfortably → compact + paginate
    ROW_H = ROW_MIN;
  } else {
    // Single page with room to spare → expand to fill ~75% of available
    const idealRowH = (availForItems - HDR_H) * 0.75 / N;
    ROW_H = Math.max(ROW_TARGET, Math.min(ROW_MAX, idealRowH));
  }
  // Totals always sit close to the items (compact spacing). Any extra slack on
  // the page belongs at the BOTTOM after all sections, not between items and
  // totals -that's the more natural reading order on a typical invoice.
  const preTotalsPad = 0;

  // Column positions (matches proforma -# | DESCRIPTION | QTY | UNIT PRICE | LINE TOTAL)
  const cNoR    = M + 10;
  const cDescL  = M + 13;
  const cQtyR   = M + 110;
  const cPriceR = M + 150;
  const cAmtR   = rEdge;
  const invPageBottom = H - FOOTER_H - 6;

  const drawInvItemsHeader = (top) => {
    doc.setFillColor(...GRN);
    doc.rect(M, top, innerW, HDR_H, 'F');
    doc.setFont('helvetica', 'bold'); doc.setFontSize(7.5); doc.setTextColor(...WHITE);
    doc.text('#',           cNoR,    top + 5.6, { align: 'right' });
    doc.text('DESCRIPTION', cDescL,  top + 5.6);
    doc.text('QTY',         cQtyR,   top + 5.6, { align: 'right' });
    doc.text('UNIT PRICE',  cPriceR, top + 5.6, { align: 'right' });
    doc.text('LINE TOTAL',  cAmtR,   top + 5.6, { align: 'right' });
    return top + HDR_H;
  };

  const startInvContinuationPage = () => {
    doc.addPage();
    doc.setFillColor(...PGBG); doc.rect(0, 0, W, H, 'F');
    doc.setFillColor(...WHITE); doc.rect(M - 3, M - 3, innerW + 6, H - 2 * M + 6, 'F');
    doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor(...DARK);
    doc.text(bizName, M, M + 4);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(...GRAY);
    doc.text(`Invoice ${sale.invoiceNumber || ''} (continued)`, rEdge, M + 4, { align: 'right' });
    doc.setDrawColor(...GRN); doc.setLineWidth(0.5);
    doc.line(M, M + 7, rEdge, M + 7);
    return M + 11;
  };

  // ── ITEMS TABLE (page-aware) ─────────────────────────────────────────
  let rowY = drawInvItemsHeader(tblTop);
  saleItemRows.forEach((item, i) => {
    if (rowY + ROW_H > invPageBottom) {
      const yAfter = startInvContinuationPage();
      rowY = drawInvItemsHeader(yAfter);
    }
    const iqty = parseFloat(item.quantity || 1);
    const iPrice = parseFloat(item.sellingPrice || 0);
    const iLine = iqty * iPrice;
    const bg = i % 2 === 0 ? STRIPE : WHITE;
    doc.setFillColor(...bg); doc.rect(M, rowY, innerW, ROW_H, 'F');
    // Vertically center text in the row
    const iY = rowY + ROW_H / 2 + 1.5;
    // Font scales slightly with row height for visual balance
    const fontSize = ROW_H >= 10 ? 10 : ROW_H >= 8 ? 9 : 8.5;
    doc.setFont('helvetica', 'normal'); doc.setFontSize(fontSize); doc.setTextColor(...TEXT);
    doc.text(String(i + 1), cNoR, iY, { align: 'right' });
    doc.text(item.productName || '—', cDescL, iY, { maxWidth: 88 });
    doc.text(String(iqty), cQtyR, iY, { align: 'right' });
    doc.text(fmtN(iPrice), cPriceR, iY, { align: 'right' });
    doc.setFont('helvetica', 'bold');
    doc.text(fmtN(iLine), cAmtR, iY, { align: 'right' });
    rowY += ROW_H;
  });
  doc.setDrawColor(...BORDER); doc.setLineWidth(0.3);
  doc.line(M, rowY, rEdge, rowY);

  // ── Helper: page-break for sections after items ──────────────────────
  let tY = rowY + 4 + preTotalsPad;
  const ensureSpace = (needed) => {
    if (tY + needed > H - FOOTER_H - 4) {
      const yAfter = startInvContinuationPage();
      tY = yAfter + 2;
    }
  };

  // ── TOTALS ────────────────────────────────────────────────────────────
  ensureSpace(_totalsH);
  const totLX = M + 128, totVX = rEdge;
  const totRow = (label, val, lColor = GRAY, vColor = TEXT) => {
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5);
    doc.setTextColor(...lColor); doc.text(label, totLX, tY, { align: 'right' });
    doc.setTextColor(...vColor); doc.text(val, totVX, tY, { align: 'right' });
    tY += 5.5;
  };
  if (discAmtInv > 0) {
    totRow('Gross Amount', fmtN(_subtotalAll));
    totRow('Discount', `-${fmtN(discAmtInv)}`, GRAY, [220, 38, 38]);
    totRow('Subtotal', fmtN(afterDiscountInv));
  } else {
    totRow('Subtotal', fmtN(_subtotalAll));
  }
  if (taxes.length === 0) {
    totRow('Tax', fmtN(0));
  } else {
    taxes.forEach(tax => {
      const rate = parseFloat(tax.rate) || 0;
      const amt  = afterDiscountInv * rate / 100;
      const lbl  = tax.type === 'vat' ? `VAT (${rate}%)` : tax.type === 'withholding' ? `Withholding (${rate}%)` : `Tax (${rate}%)`;
      totRow(lbl, fmtN(amt));
    });
  }
  // Total band
  tY += 1;
  doc.setFillColor(...GRN);
  doc.rect(M + 100, tY - 4, rEdge - M - 100, 10, 'F');
  doc.setFont('helvetica', 'bold'); doc.setFontSize(9.5); doc.setTextColor(180, 220, 200);
  doc.text('Total', totLX, tY + 3, { align: 'right' });
  doc.setFontSize(10.5); doc.setTextColor(...WHITE);
  doc.text(fmtN(grandTotal), totVX - 1, tY + 3, { align: 'right' });
  tY += 10;

  // EFD
  if (sale.efdReceiptNo) {
    ensureSpace(10);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(...GRAY);
    doc.text(`EFD Receipt No: ${sale.efdReceiptNo}`, M, tY);
    tY += 8;
  }

  // Notes
  if (sale.invoiceNotes && sale.invoiceNotes.trim()) {
    const noteLines = doc.splitTextToSize(sale.invoiceNotes.trim(), innerW - 8);
    const noteH = 10 + noteLines.length * 5;
    ensureSpace(noteH + 6);
    doc.setFillColor(255, 249, 230);
    doc.setDrawColor(234, 179, 8); doc.setLineWidth(0.3);
    doc.roundedRect(M, tY, innerW, noteH, 2, 2, 'FD');
    doc.setFillColor(234, 179, 8); doc.roundedRect(M, tY, 3, noteH, 1.5, 1.5, 'F');
    doc.setFont('helvetica', 'bold'); doc.setFontSize(7.5); doc.setTextColor(120, 80, 0);
    doc.text('NOTES', M + 7, tY + 7);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(...TEXT);
    doc.text(noteLines, M + 7, tY + 14);
    tY += noteH + 5;
  }

  // ── AMOUNT IN WORDS ──────────────────────────────────────────────────
  if (grandTotal > 0) {
    const wordsStr = amountInWords(grandTotal);
    ensureSpace(11);
    doc.setDrawColor(...BORDER); doc.setLineWidth(0.3);
    doc.setLineDash([1.5, 1.5]);
    doc.line(M, tY, rEdge, tY);
    doc.line(M, tY + 8, rEdge, tY + 8);
    doc.setLineDash([]);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.setTextColor(...GRAY);
    doc.text(wordsStr, M + 2, tY + 5.5, { maxWidth: innerW - 4 });
    tY += 11;
  }

  // ── FOOTER SECTIONS -same logic as proforma ─────────────────────────
  const invRenderSection = (sec, xStart, maxW) => {
    const lines = sec.content.split('\n').filter(l => l.trim());
    const lbl = (sec.label === 'Custom' && sec.customLabel) ? sec.customLabel : (sec.label || 'Section');
    doc.setFont('helvetica', 'bold'); doc.setFontSize(7.5); doc.setTextColor(...GRN);
    doc.text(lbl.toUpperCase(), xStart, tY);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(...TEXT);
    lines.forEach((line, i) => doc.text(`• ${line}`, xStart + 2, tY + 7 + i * 6, { maxWidth: maxW - 4 }));
    return lines.length;
  };

  if (_invFooterSec.length === 1) {
    const lcnt = _invFooterSec[0].content.split('\n').filter(l => l.trim()).length;
    ensureSpace(8 + lcnt * 6);
    invRenderSection(_invFooterSec[0], M, innerW);
    tY += 8 + lcnt * 6;
  } else if (_invFooterSec.length >= 2) {
    const colW2 = (innerW - 4) / 2;
    const lc = _invFooterSec[0].content.split('\n').filter(l => l.trim()).length;
    const rc = _invFooterSec[1].content.split('\n').filter(l => l.trim()).length;
    const secH = 8 + Math.max(lc, rc) * 6;
    ensureSpace(secH);
    invRenderSection(_invFooterSec[0], M, colW2);
    doc.setDrawColor(...BORDER); doc.setLineWidth(0.3);
    doc.line(M + colW2 + 2, tY, M + colW2 + 2, tY + secH);
    invRenderSection(_invFooterSec[1], M + colW2 + 6, colW2);
    tY += secH;
  }

  // ── PAYMENT DETAILS -single column, group headers, green left bar ──
  if (_acctsList.length > 0) {
    const banks = _acctsList.filter(a => a.type === 'bank');
    const mobiles = _acctsList.filter(a => a.type === 'mobilemoney');
    const PAD_X = 5;             // horizontal inner padding
    const PAD_Y = 5;             // vertical inner padding
    const BAR_W = 2.5;           // green left accent bar width
    const TITLE_H = 6;           // PAYMENT DETAILS title
    const GROUP_H_TITLE = 5;     // "Bank" / "Mobile" sub-header
    const ROW_H = 5;             // each account row
    const GROUP_GAP = 3;         // gap between groups

    // No more sub-headers -each row already says "Bank:" or "Mobile:" as its label.
    // Just count rows (with a small gap between bank and mobile blocks if both exist).
    const computeGroupBlockH = (count) => count > 0 ? count * ROW_H : 0;
    const blockH = PAD_Y * 2 + TITLE_H +
      computeGroupBlockH(banks.length) +
      (banks.length > 0 && mobiles.length > 0 ? GROUP_GAP : 0) +
      computeGroupBlockH(mobiles.length);
    ensureSpace(blockH + 6);

    tY += 3;
    // Container background (very light cream-gray, similar to screenshot)
    doc.setFillColor(244, 246, 240);
    doc.roundedRect(M, tY, innerW, blockH, 2, 2, 'F');
    // Thick green left accent bar
    doc.setFillColor(...GRN);
    doc.rect(M, tY, BAR_W, blockH, 'F');

    const contentX = M + BAR_W + PAD_X;
    let lineY = tY + PAD_Y + 4;

    // PAYMENT DETAILS title
    doc.setFont('helvetica', 'bold'); doc.setFontSize(8); doc.setTextColor(...GRN);
    doc.text('PAYMENT DETAILS', contentX, lineY);
    lineY += TITLE_H + 1;

    const renderGroupRow = (acc) => {
      const isBank = acc.type === 'bank';
      const typeLbl = isBank ? 'Bank:' : 'Mobile:';
      const brand = isBank ? (acc.bankName || 'Bank') : (acc.networkName || 'Mobile');
      const number = isBank ? (acc.accountNumber || '') : (acc.phoneNumber || '');
      const acctName = acc.accountName || '';
      // Type label (bold, dark)
      doc.setFont('helvetica', 'bold'); doc.setFontSize(8.5); doc.setTextColor(...DARK);
      doc.text(typeLbl, contentX, lineY);
      // Detail line (brand -number | A/C name)
      const detailX = contentX + 18;
      let detailStr = `${brand}  - ${number}`;
      if (acctName) detailStr += `   |   ${acctName}`;
      doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); doc.setTextColor(...TEXT);
      doc.text(detailStr, detailX, lineY, { maxWidth: innerW - BAR_W - PAD_X * 2 - 18 });
      lineY += ROW_H;
    };

    // Bank rows
    if (banks.length > 0) banks.forEach(renderGroupRow);
    // Small gap, then mobile rows
    if (mobiles.length > 0) {
      if (banks.length > 0) lineY += GROUP_GAP;
      mobiles.forEach(renderGroupRow);
    }
    tY += blockH + 4;
  }

  // ── FOOTER ────────────────────────────────────────────────────────────
  const totalPages = doc.internal.getNumberOfPages();
  for (let pg = 1; pg <= totalPages; pg++) {
    doc.setPage(pg);
    const footY = H - 30;
    doc.setDrawColor(...BORDER); doc.setLineWidth(0.4);
    doc.line(M, footY, rEdge, footY);
    doc.setFont('helvetica', 'italic'); doc.setFontSize(8); doc.setTextColor(...GRAY);
    doc.text('Thank you for your business!', W / 2, footY + 9, { align: 'center' });
  }

  doc.save(`Invoice-${sale.invoiceNumber || 'INV'}.pdf`);
};

const generateProformaPDF = (proforma, bizInfo, paymentAccounts = []) => {
  if (typeof bizInfo === 'string') bizInfo = { name: bizInfo };
  const { name: bizName = 'My Business', address: bizAddr = '', logo: bizLogo = '',
    tin = '', vrn = '', regNo = '', licenseNo = '',
    invoiceTerms: pfBizTerms = ''
  } = bizInfo;

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const M = 14;
  const innerW = W - M * 2;
  const rEdge = W - M;
  const fmtN = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const ucFirst = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : '';

  // Identical palette to invoice
  const GRN   = [26, 75, 58];
  const DARK  = [26, 26, 46];
  const TEXT  = [40, 40, 40];
  const GRAY  = [110, 110, 110];
  const PGBG  = [240, 242, 245];
  const BORDER= [210, 215, 220];
  const WHITE = [255, 255, 255];
  const STRIPE= [248, 250, 252];

  // Page background + white content area
  doc.setFillColor(...PGBG); doc.rect(0, 0, W, H, 'F');
  doc.setFillColor(...WHITE); doc.rect(M - 3, M - 3, innerW + 6, H - 2 * M + 6, 'F');

  // ── HEADER -taller area for logo + name + multi-line address ──
  const hdrY = M + 4;
  const logoW = 38, logoH = 38;
  const logoX = M + 2;

  if (bizLogo) {
    try { doc.addImage(bizLogo, 'PNG', logoX, hdrY, logoW, logoH); } catch(e) {}
  } else {
    doc.setFillColor(232, 235, 238);
    doc.setDrawColor(...BORDER); doc.setLineWidth(0.5);
    doc.rect(logoX, hdrY, logoW, logoH, 'FD');
  }

  const nameX = logoX + logoW + 10;
  doc.setFont('helvetica', 'bold'); doc.setFontSize(20); doc.setTextColor(...DARK);
  doc.text(bizName, nameX, hdrY + 11, { maxWidth: 100 });
  if (bizAddr) {
    const addrLines = doc.splitTextToSize(bizAddr, 100);
    doc.setFont('helvetica', 'italic'); doc.setFontSize(8.5); doc.setTextColor(...GRAY);
    addrLines.forEach((ln, i) => doc.text(ln, nameX, hdrY + 19 + i * 4.5));
  }
  const cmpParts = [];
  if (tin) cmpParts.push(`TIN: ${tin}`);
  if (vrn) cmpParts.push(`VRN: ${vrn}`);
  if (regNo) cmpParts.push(`Reg: ${regNo}`);
  if (licenseNo) cmpParts.push(`Lic: ${licenseNo}`);
  if (cmpParts.length > 0) {
    doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.setTextColor(...GRAY);
    doc.text(cmpParts.join('  |  '), nameX, hdrY + logoH - 2);
  }

  // Big bold PROFORMA word (Kenran-style) at the right
  doc.setFont('helvetica', 'bold'); doc.setFontSize(30); doc.setTextColor(...DARK);
  doc.text('PROFORMA', rEdge, hdrY + 22, { align: 'right' });

  // Thick separator -sits below the bigger logo
  const sepY = hdrY + logoH + 5;
  doc.setDrawColor(...BORDER); doc.setLineWidth(0.5);
  doc.line(M, sepY, rEdge, sepY);

  // Kenran-style 2-column info row: BILL TO (left) + label-value list (right)
  const proformaPayments = (proforma.payments && proforma.payments.length > 0)
    ? proforma.payments : [{ type: proforma.paymentMethod || 'cash' }];

  const infoY = sepY + 5;
  const _today = new Date(); _today.setHours(0,0,0,0);
  const _expDate = proforma.expiryDate ? new Date(proforma.expiryDate) : null;
  const _isExpired = _expDate && _expDate < _today;

  // ── Left side: BILL TO ─────────────────────────────────────────────
  doc.setFont('helvetica', 'bold'); doc.setFontSize(8); doc.setTextColor(...GRAY);
  doc.text('To:', M + 2, infoY + 1);
  let bY = infoY + 7;
  if (proforma.customerName) {
    doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.setTextColor(...DARK);
    doc.text(proforma.customerName, M + 2, bY, { maxWidth: innerW * 0.55 });
    bY += 5.5;
    const _pfCustLines = [
      proforma.customerPhone,
      proforma.customerEmail,
      proforma.customerAddress,
      proforma.customerTIN ? `TIN: ${proforma.customerTIN}` : null,
      proforma.customerVRN ? `VRN: ${proforma.customerVRN}` : null,
    ].filter(Boolean);
    _pfCustLines.forEach(line => {
      doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); doc.setTextColor(...GRAY);
      doc.text(line, M + 2, bY, { maxWidth: innerW * 0.55 });
      bY += 5;
    });
  }

  // ── Right side: label-value list (Date / Proforma No. / Valid Until / Validity) ──
  const rightColX = M + innerW * 0.6;
  const labelX    = rightColX;
  const valueX    = rEdge;
  let kvY = infoY + 1;
  const drawKV = (label, value, valueBold = true) => {
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(...GRAY);
    doc.text(label, labelX, kvY);
    if (valueBold) {
      doc.setFont('helvetica', 'bold'); doc.setFontSize(9.5); doc.setTextColor(...DARK);
    } else {
      doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(...TEXT);
    }
    doc.text(String(value || '—'), valueX, kvY, { align: 'right' });
    kvY += 6.5;
  };
  drawKV('Date:', proforma.date || '—');
  drawKV('Proforma No.:', proforma.proformaNumber || '—');
  const valDays = proforma.validityDays || 30;
  drawKV('Validity:', `${valDays} day${valDays !== 1 ? 's' : ''}`, false);
  drawKV('Valid Until:', proforma.expiryDate || '—');
  if (_isExpired) {
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(...GRAY);
    doc.text('Status:', labelX, kvY);
    const expW = 22;
    doc.setFillColor(254, 226, 226);
    doc.roundedRect(rEdge - expW, kvY - 4, expW, 6, 1.5, 1.5, 'F');
    doc.setFont('helvetica', 'bold'); doc.setFontSize(7); doc.setTextColor(153, 27, 27);
    doc.text('EXPIRED', rEdge - expW / 2, kvY, { align: 'center' });
    kvY += 6.5;
  }

  // divider below info row
  const billHeightEst = bY - infoY + 2;
  const rightHeightEst = kvY - infoY + 2;
  const dividerY = infoY + Math.max(billHeightEst, rightHeightEst, 24);
  doc.setDrawColor(...BORDER); doc.setLineWidth(0.3);
  doc.line(M, dividerY, rEdge, dividerY);

  // ── ITEMS TABLE (page-aware, dynamic sizing) ─────────────────────────
  // Pre-compute totals so we can estimate post-items height and set row size
  const items = proforma.items || [];
  const _pfSub = items.reduce((s, it) => s + (parseFloat(it.total || '') || parseFloat(it.quantity || 0) * parseFloat(it.unitPrice || 0)), 0);
  const _pfDiscPct = parseFloat(proforma.discount || 0);
  const _pfDiscAmt = _pfSub * (_pfDiscPct / 100);
  const _pfAfterDisc = Math.max(0, _pfSub - _pfDiscAmt);
  const _pfTaxes = proforma.taxes || [];
  const _pfTotalTax = _pfTaxes.reduce((s, tx) => s + _pfAfterDisc * (parseFloat(tx.rate) || 0) / 100, 0);
  const _pfGT = _pfAfterDisc + _pfTotalTax;

  const _pfTaxLines = Math.max(1, _pfTaxes.length);
  const _pfTotalsH = 5.5 + (_pfDiscAmt > 0 ? 5.5 : 0) + _pfTaxLines * 5.5 + 14;
  const _pfWordsH = _pfGT > 0 ? 11 : 0;
  const _pfFooterSections = proforma.footerSections && proforma.footerSections.length > 0
    ? proforma.footerSections
    : (proforma.terms != null && proforma.terms.trim() ? [{ label: 'Terms & Conditions', content: proforma.terms }] : []);
  const _pfSecWithContent = _pfFooterSections.filter(s => s.content && s.content.trim());
  let _pfFooterSecH = 0;
  if (_pfSecWithContent.length === 1) {
    _pfFooterSecH = 8 + _pfSecWithContent[0].content.split('\n').filter(l => l.trim()).length * 6;
  } else if (_pfSecWithContent.length >= 2) {
    const lc = _pfSecWithContent[0].content.split('\n').filter(l => l.trim()).length;
    const rc = _pfSecWithContent[1].content.split('\n').filter(l => l.trim()).length;
    _pfFooterSecH = 8 + Math.max(lc, rc) * 6;
  }
  const _pfAcctsList = (paymentAccounts || []).filter(a => a.type !== 'cash');
  const _pfPaymentInfoH = _pfAcctsList.length > 0 ? 22 + 10 : 0;
  const _pfDisclaimerH = 12;
  const _pfBelowItemsH = _pfTotalsH + _pfWordsH + _pfFooterSecH + _pfPaymentInfoH + _pfDisclaimerH;

  const tblTop = dividerY + 3;
  const cNoR    = M + 10;
  const cDescL  = M + 13;
  const cQtyR   = M + 110;
  const cPriceR = M + 150;
  const cAmtR   = rEdge;

  const PF_HDR_H = 7;
  const PF_FOOTER_RES = 26;
  const pfPageBottom = H - PF_FOOTER_RES - 6;
  const _pfAvailForItems = (H - PF_FOOTER_RES - 4) - tblTop - _pfBelowItemsH;

  // Compute dynamic row height
  const _N = items.length;
  const PF_ROW_MIN = 6, PF_ROW_TARGET = 8, PF_ROW_MAX = 13;
  let PF_ROW_H;
  if (_N === 0) PF_ROW_H = PF_ROW_TARGET;
  else if (_N * PF_ROW_TARGET + PF_HDR_H >= _pfAvailForItems) {
    PF_ROW_H = PF_ROW_MIN; // multi-page
  } else {
    const idealRowH = (_pfAvailForItems - PF_HDR_H) * 0.75 / _N;
    PF_ROW_H = Math.max(PF_ROW_TARGET, Math.min(PF_ROW_MAX, idealRowH));
  }
  // Totals always sit close to items -slack goes at the bottom of the page,
  // not between sections (mirrors the reference invoice layout).
  const _pfPreTotalsPad = 0;

  const drawPfItemsHeader = (top) => {
    doc.setFillColor(...GRN);
    doc.rect(M, top, innerW, PF_HDR_H, 'F');
    doc.setFont('helvetica', 'bold'); doc.setFontSize(7.5); doc.setTextColor(...WHITE);
    doc.text('#',           cNoR,    top + 5.6, { align: 'right' });
    doc.text('DESCRIPTION', cDescL,  top + 5.6);
    doc.text('QTY',         cQtyR,   top + 5.6, { align: 'right' });
    doc.text('UNIT PRICE',  cPriceR, top + 5.6, { align: 'right' });
    doc.text('LINE TOTAL',  cAmtR,   top + 5.6, { align: 'right' });
    return top + PF_HDR_H;
  };

  const startPfContinuationPage = () => {
    doc.addPage();
    doc.setFillColor(...PGBG); doc.rect(0, 0, W, H, 'F');
    doc.setFillColor(...WHITE); doc.rect(M - 3, M - 3, innerW + 6, H - 2 * M + 6, 'F');
    doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor(...DARK);
    doc.text(bizName, M, M + 4);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(...GRAY);
    doc.text(`Proforma ${proforma.proformaNumber || ''} (continued)`, rEdge, M + 4, { align: 'right' });
    doc.setDrawColor(...GRN); doc.setLineWidth(0.5);
    doc.line(M, M + 7, rEdge, M + 7);
    return M + 11;
  };

  let rowY = drawPfItemsHeader(tblTop);
  items.forEach((item, i) => {
    if (rowY + PF_ROW_H > pfPageBottom) {
      const yAfter = startPfContinuationPage();
      rowY = drawPfItemsHeader(yAfter);
    }
    const bg = i % 2 === 0 ? STRIPE : WHITE;
    doc.setFillColor(...bg); doc.rect(M, rowY, innerW, PF_ROW_H, 'F');
    const iY = rowY + PF_ROW_H / 2 + 1.5;
    const fontSize = PF_ROW_H >= 10 ? 10 : PF_ROW_H >= 8 ? 9 : 8.5;
    doc.setFont('helvetica', 'normal'); doc.setFontSize(fontSize); doc.setTextColor(...TEXT);
    doc.text(String(i + 1), cNoR, iY, { align: 'right' });
    doc.text(item.productName || '—', cDescL, iY, { maxWidth: 88 });
    doc.text(String(item.quantity || 0), cQtyR,   iY, { align: 'right' });
    doc.text(fmtN(item.unitPrice),       cPriceR, iY, { align: 'right' });
    const lineTotal = parseFloat(item.total || '') || (parseFloat(item.quantity || 0) * parseFloat(item.unitPrice || 0));
    doc.setFont('helvetica', 'bold');
    doc.text(fmtN(lineTotal), cAmtR, iY, { align: 'right' });
    rowY += PF_ROW_H;
  });
  doc.setDrawColor(...BORDER); doc.setLineWidth(0.3);
  doc.line(M, rowY, rEdge, rowY);
  // Extra padding before totals for short documents to balance the page
  rowY += _pfPreTotalsPad;

  // Helper for page-breaks in remaining (totals, footer sections, payment info, disclaimer)
  const pfEnsureSpace = (needed) => {
    if (tY + needed > H - PF_FOOTER_RES - 4) {
      const yAfter = startPfContinuationPage();
      tY = yAfter + 2;
    }
  };

  // ── TOTALS ────────────────────────────────────────────────────────────
  const subtotal = items.reduce((s, it) => s + (parseFloat(it.total || '') || parseFloat(it.quantity || 0) * parseFloat(it.unitPrice || 0)), 0);
  const discPct  = parseFloat(proforma.discount || 0);           // percentage entered by user (e.g. 5 for 5%)
  const discAmt  = subtotal * (discPct / 100);                    // actual money discount
  const afterDisc = Math.max(0, subtotal - discAmt);
  const taxes = proforma.taxes || [];
  let totalTax = 0;
  taxes.forEach(tax => { totalTax += afterDisc * (parseFloat(tax.rate) || 0) / 100; });
  const grandTotal = afterDisc + totalTax;

  let tY = rowY + 4;
  // Reserve approx total block height -page-break before drawing
  const pfTaxLines = Math.max(1, (taxes || []).length);
  const pfTotalsBlockH = 5.5 + (discAmt > 0 ? 5.5 : 0) + pfTaxLines * 5.5 + 14;
  pfEnsureSpace(pfTotalsBlockH);
  const totLX = M + 128, totVX = rEdge;
  const totRow = (label, val, lColor = GRAY, vColor = TEXT) => {
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5);
    doc.setTextColor(...lColor); doc.text(label, totLX, tY, { align: 'right' });
    doc.setTextColor(...vColor); doc.text(val,   totVX, tY, { align: 'right' });
    tY += 5.5;
  };
  totRow('Subtotal', fmtN(subtotal));
  if (discAmt > 0) totRow(`Discount (${discPct}%)`, `-${fmtN(discAmt)}`, GRAY, [220, 38, 38]);
  if (taxes.length === 0) {
    totRow('Sales Tax', fmtN(0));
  } else {
    taxes.forEach(tax => {
      const rate = parseFloat(tax.rate) || 0;
      const amt  = afterDisc * rate / 100;
      const lbl  = tax.type === 'vat' ? `VAT (${rate}%)` : tax.type === 'withholding' ? `Withholding (${rate}%)` : `Tax (${rate}%)`;
      totRow(lbl, fmtN(amt));
    });
  }
  // Total band -tighter
  tY += 1;
  doc.setFillColor(...GRN);
  doc.rect(M + 100, tY - 4, rEdge - M - 100, 10, 'F');
  doc.setFont('helvetica', 'bold'); doc.setFontSize(9.5); doc.setTextColor(180, 220, 200);
  doc.text('Total', totLX, tY + 3, { align: 'right' });
  doc.setFontSize(10.5); doc.setTextColor(...WHITE);
  doc.text(fmtN(grandTotal), totVX - 1, tY + 3, { align: 'right' });
  tY += 10;

  // ── PAYMENT SPLIT BREAKDOWN ───────────────────────────────────────────
  const filteredProformaPmts = (proformaPayments || []).filter(p => p.amount && parseFloat(p.amount) > 0);
  if (filteredProformaPmts.length > 1) {
    pfEnsureSpace(10 + filteredProformaPmts.length * 7);
    doc.setDrawColor(...BORDER); doc.setLineWidth(0.3); doc.line(M, tY, rEdge, tY); tY += 7;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(7.5); doc.setTextColor(...GRN);
    doc.text('PAYMENT BREAKDOWN', M, tY); tY += 7;
    filteredProformaPmts.forEach(p => {
      doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(...GRAY);
      doc.text(ucFirst(p.type || 'cash') + ':', M, tY);
      doc.setFont('helvetica', 'bold'); doc.setTextColor(...TEXT);
      doc.text(fmtN(p.amount), rEdge, tY, { align: 'right' }); tY += 7;
    });
  }

  // Validity is now shown in the 3-column header -skip the old amber note.
  tY += 2;

  // ── AMOUNT IN WORDS ───────────────────────────────────────────────────
  const pfGrandTotal = (() => {
    const items = (proforma.items && proforma.items.length > 0) ? proforma.items : [{ sellingPrice: proforma.sellingPrice, quantity: proforma.quantity || 1 }];
    let sub = items.reduce((s, it) => s + parseFloat(it.quantity || 1) * parseFloat(it.sellingPrice || 0), 0);
    const disc = proforma.discountAmount != null ? parseFloat(proforma.discountAmount) : sub * (parseFloat(proforma.discount || 0) / 100);
    const aft = Math.max(0, sub - disc);
    const tax = (proforma.taxes || []).reduce((s, t) => s + aft * (parseFloat(t.rate || 0) / 100), 0);
    return aft + tax;
  })();
  if (pfGrandTotal > 0) {
    const pfWordsStr = amountInWords(pfGrandTotal);
    pfEnsureSpace(11);
    doc.setDrawColor(...BORDER); doc.setLineWidth(0.3);
    doc.setLineDash([1.5, 1.5]);
    doc.line(M, tY, rEdge, tY);
    doc.line(M, tY + 8, rEdge, tY + 8);
    doc.setLineDash([]);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.setTextColor(...GRAY);
    doc.text(pfWordsStr, M + 2, tY + 5.5, { maxWidth: innerW - 4 });
    tY += 11;
  }

  // ── FOOTER SECTIONS (1 = full width, 2 = side by side) ──────────────
  const pfFooterSections = proforma.footerSections && proforma.footerSections.length > 0
    ? proforma.footerSections
    : (proforma.terms != null && proforma.terms.trim()
        ? [{ label: 'Terms & Conditions', content: proforma.terms }]
        : []);
  const pfSectionsWithContent = pfFooterSections.filter(s => s.content && s.content.trim());

  const pfRenderSection = (sec, xStart, maxW) => {
    const lines = sec.content.split('\n').filter(l => l.trim());
    const lbl = (sec.label === 'Custom' && sec.customLabel) ? sec.customLabel : (sec.label || 'Section');
    doc.setFont('helvetica', 'bold'); doc.setFontSize(7.5); doc.setTextColor(...GRN);
    doc.text(lbl.toUpperCase(), xStart, tY);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(...TEXT);
    lines.forEach((line, i) => doc.text(`• ${line}`, xStart + 2, tY + 7 + i * 6, { maxWidth: maxW - 4 }));
    return lines.length;
  };

  if (pfSectionsWithContent.length === 1) {
    const lineCount = pfSectionsWithContent[0].content.split('\n').filter(l => l.trim()).length;
    pfEnsureSpace(8 + lineCount * 6);
    pfRenderSection(pfSectionsWithContent[0], M, innerW);
    tY += 8 + lineCount * 6;
  } else if (pfSectionsWithContent.length >= 2) {
    const colW2 = (innerW - 4) / 2;
    const lCount = pfSectionsWithContent[0].content.split('\n').filter(l => l.trim()).length;
    const rCount = pfSectionsWithContent[1].content.split('\n').filter(l => l.trim()).length;
    const secH = 8 + Math.max(lCount, rCount) * 6;
    pfEnsureSpace(secH);
    pfRenderSection(pfSectionsWithContent[0], M, colW2);
    doc.setDrawColor(...BORDER); doc.setLineWidth(0.3);
    doc.line(M + colW2 + 2, tY, M + colW2 + 2, tY + secH);
    pfRenderSection(pfSectionsWithContent[1], M + colW2 + 6, colW2);
    tY += secH;
  }

  // ── PAYMENT DETAILS -single column, group headers, green left bar ──
  const pfAccts = (paymentAccounts || []).filter(a => a.type !== 'cash');
  if (pfAccts.length > 0) {
    const pfBanks = pfAccts.filter(a => a.type === 'bank');
    const pfMobiles = pfAccts.filter(a => a.type === 'mobilemoney');
    const PF_PAD_X = 5, PF_PAD_Y = 5, PF_BAR_W = 2.5;
    const PF_TITLE_H = 6, PF_GROUP_H_TITLE = 5, PF_ROW_H = 5, PF_GROUP_GAP = 3;

    // No more sub-headers -each row already says "Bank:" or "Mobile:" as its label.
    const pfComputeGroupBlockH = (count) => count > 0 ? count * PF_ROW_H : 0;
    const pfBlockH = PF_PAD_Y * 2 + PF_TITLE_H +
      pfComputeGroupBlockH(pfBanks.length) +
      (pfBanks.length > 0 && pfMobiles.length > 0 ? PF_GROUP_GAP : 0) +
      pfComputeGroupBlockH(pfMobiles.length);
    pfEnsureSpace(pfBlockH + 6);

    tY += 3;
    doc.setFillColor(244, 246, 240);
    doc.roundedRect(M, tY, innerW, pfBlockH, 2, 2, 'F');
    doc.setFillColor(...GRN);
    doc.rect(M, tY, PF_BAR_W, pfBlockH, 'F');

    const pfContentX = M + PF_BAR_W + PF_PAD_X;
    let pfLineY = tY + PF_PAD_Y + 4;

    doc.setFont('helvetica', 'bold'); doc.setFontSize(8); doc.setTextColor(...GRN);
    doc.text('PAYMENT DETAILS', pfContentX, pfLineY);
    pfLineY += PF_TITLE_H + 1;

    const pfRenderGroupRow = (acc) => {
      const isBank = acc.type === 'bank';
      const typeLbl = isBank ? 'Bank:' : 'Mobile:';
      const brand = isBank ? (acc.bankName || 'Bank') : (acc.networkName || 'Mobile');
      const number = isBank ? (acc.accountNumber || '') : (acc.phoneNumber || '');
      const acctName = acc.accountName || '';
      doc.setFont('helvetica', 'bold'); doc.setFontSize(8.5); doc.setTextColor(...DARK);
      doc.text(typeLbl, pfContentX, pfLineY);
      let detailStr = `${brand}  - ${number}`;
      if (acctName) detailStr += `   |   ${acctName}`;
      doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); doc.setTextColor(...TEXT);
      doc.text(detailStr, pfContentX + 18, pfLineY, { maxWidth: innerW - PF_BAR_W - PF_PAD_X * 2 - 18 });
      pfLineY += PF_ROW_H;
    };

    if (pfBanks.length > 0) pfBanks.forEach(pfRenderGroupRow);
    if (pfMobiles.length > 0) {
      if (pfBanks.length > 0) pfLineY += PF_GROUP_GAP;
      pfMobiles.forEach(pfRenderGroupRow);
    }
    tY += pfBlockH + 4;
  }

  // ── DISCLAIMER BANNER -dashed dark-green border, single prominent line ──
  tY += 2;
  pfEnsureSpace(12);
  const bannerY = tY;
  const bannerH = 9;
  doc.setFillColor(240, 250, 245);
  doc.rect(M, bannerY, innerW, bannerH, 'F');
  doc.setDrawColor(...GRN); doc.setLineWidth(0.4);
  doc.setLineDash([2, 2]);
  doc.rect(M, bannerY, innerW, bannerH, 'D');
  doc.setLineDash([]);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(8); doc.setTextColor(...GRN);
  doc.text('This document is a PROFORMA INVOICE, quotation only. It is not a tax invoice.',
    W / 2, bannerY + 7.5, { align: 'center' });

  // ── FOOTER ────────────────────────────────────────────────────────────
  const pfTotalPages = doc.internal.getNumberOfPages();
  for (let pg = 1; pg <= pfTotalPages; pg++) {
    doc.setPage(pg);
    const pfFootY = H - 30; // clearly inside the white content area
    doc.setDrawColor(...BORDER); doc.setLineWidth(0.4);
    doc.line(M, pfFootY, rEdge, pfFootY);
    doc.setFont('helvetica', 'italic'); doc.setFontSize(8); doc.setTextColor(...GRAY);
    doc.text('Thank you for your business!', W / 2, pfFootY + 9, { align: 'center' });
  }

  doc.save(`Proforma-${proforma.proformaNumber || 'PROF'}.pdf`);
};

const CURRENCY_LIST = [
  { code: 'TSh',  name: 'Tanzanian Shilling',  symbol: 'TSh'  },
  { code: 'KES',  name: 'Kenyan Shilling',      symbol: 'KSh'  },
  { code: 'UGX',  name: 'Ugandan Shilling',     symbol: 'USh'  },
  { code: 'RWF',  name: 'Rwandan Franc',         symbol: 'RF'   },
  { code: 'ETB',  name: 'Ethiopian Birr',        symbol: 'Br'   },
  { code: 'NGN',  name: 'Nigerian Naira',        symbol: '₦'    },
  { code: 'GHS',  name: 'Ghanaian Cedi',         symbol: '₵'    },
  { code: 'ZAR',  name: 'South African Rand',    symbol: 'R'    },
  { code: 'USD',  name: 'US Dollar',             symbol: '$'    },
  { code: 'EUR',  name: 'Euro',                  symbol: '€'    },
  { code: 'GBP',  name: 'British Pound',         symbol: '£'    },
  { code: 'AED',  name: 'UAE Dirham',            symbol: 'AED'  },
  { code: 'INR',  name: 'Indian Rupee',          symbol: '₹'    },
  { code: 'CNY',  name: 'Chinese Yuan',          symbol: '¥'    },
  { code: 'BRL',  name: 'Brazilian Real',        symbol: 'R$'   },
  { code: 'MXN',  name: 'Mexican Peso',          symbol: 'MX$'  },
  { code: 'JPY',  name: 'Japanese Yen',          symbol: '¥'    },
  { code: 'CAD',  name: 'Canadian Dollar',       symbol: 'CA$'  },
  { code: 'AUD',  name: 'Australian Dollar',     symbol: 'A$'   },
  { code: 'CHF',  name: 'Swiss Franc',           symbol: 'CHF'  },
];

const formatCurrency = (value, currCode = 'TSh') => {
  const found = CURRENCY_LIST.find(c => c.code === currCode);
  const sym = found ? found.symbol : currCode;
  // Whole numbers only -TSh amounts have no cents, and it avoids ugly
  // fractional profit values like "11,929,442.86" from average-cost math.
  return `${sym} ${Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

const getGreeting = (t) => {
  const hour = new Date().getHours();
  if (hour < 12) return t.goodMorning;
  if (hour < 17) return t.goodAfternoon;
  return t.goodEvening;
};

const getFormattedDate = (t) => {
  const now = new Date();
  const dayName = (t.dayNames || ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'])[now.getDay()];
  const monthName = (t.monthNames || ['January','February','March','April','May','June','July','August','September','October','November','December'])[now.getMonth()];
  return `${dayName}, ${now.getDate()} ${monthName} ${now.getFullYear()}`;
};

// ── Synchronous recovery-token extraction at module load ──────────────────
// Runs BEFORE React mounts. This is the only way to guarantee detection
// before onAuthStateChanged or first render can race.
const extractRecoveryTokenAtLoad = () => {
  try {
    const hash = initialUrlSnapshot.hash || '';
    const search = initialUrlSnapshot.search || '';
    console.log('⌕ [RECOVERY DETECTION] at module load -hash:', hash || '(empty)', '| search:', search || '(empty)');

    // Implicit flow: #access_token=...&type=recovery
    if (hash && hash.includes('type=recovery')) {
      const params = new URLSearchParams(hash.replace(/^#/, ''));
      const token = params.get('access_token');
      if (token) {
        console.log('✓ [RECOVERY] Found access_token in hash:', token.substring(0, 20) + '...');
        return { token, source: 'hash' };
      }
      console.warn('⚠️ [RECOVERY] type=recovery in hash but no access_token');
    }

    // Some configs put it in query string
    if (search && search.includes('type=recovery')) {
      const params = new URLSearchParams(search.replace(/^\?/, ''));
      const token = params.get('access_token');
      if (token) {
        console.log('✓ [RECOVERY] Found access_token in query:', token.substring(0, 20) + '...');
        return { token, source: 'query' };
      }
    }

    // PKCE flow: ?code=...  (Supabase project may be set to PKCE-only)
    if (search && search.includes('code=')) {
      const params = new URLSearchParams(search.replace(/^\?/, ''));
      const code = params.get('code');
      if (code) {
        console.warn('⚠️ [RECOVERY] PKCE code detected (?code=). Supabase project is set to PKCE flow, but this app uses implicit flow. Switch the project to "Implicit" flow OR implement code exchange.');
        return { code, source: 'pkce' };
      }
    }

    // Error returned by Supabase (expired link, invalid token, etc.)
    if (hash && hash.includes('error')) {
      const params = new URLSearchParams(hash.replace(/^#/, ''));
      const err = params.get('error_description') || params.get('error');
      if (err) {
        console.warn('⚠️ [RECOVERY] Supabase returned error in URL:', err);
        return { error: err, source: 'hash-error' };
      }
    }
    if (search && search.includes('error')) {
      const params = new URLSearchParams(search.replace(/^\?/, ''));
      const err = params.get('error_description') || params.get('error');
      if (err) {
        console.warn('⚠️ [RECOVERY] Supabase returned error in URL:', err);
        return { error: err, source: 'query-error' };
      }
    }

    return null;
  } catch (e) {
    console.error('⌕ [RECOVERY] detection threw:', e);
    return null;
  }
};

const INITIAL_RECOVERY = extractRecoveryTokenAtLoad();
// Clear the URL immediately so refreshes don't re-trigger and the token isn't exposed in the address bar.
if (INITIAL_RECOVERY && typeof window !== 'undefined' && window.history?.replaceState) {
  window.history.replaceState({}, document.title, window.location.pathname);
}

// ── Error Boundary -catches render crashes, shows recovery screen instead of blank ──
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error('Biashara Track app render error:', error, info); }
  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-tab)', padding: 24, textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>!</div>
        <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 8 }}>Something went wrong</div>
        <div style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 28, maxWidth: 320, lineHeight: 1.6 }}>
          The app hit an unexpected error. Your data is safe. Tap below to reload.
        </div>
        <button
          onClick={() => { this.setState({ hasError: false, error: null }); window.location.reload(); }}
          style={{ padding: '14px 32px', borderRadius: 14, background: '#111827', color: '#A3E635', border: 'none', fontWeight: 800, fontSize: 16, cursor: 'pointer' }}>
          Reload App
        </button>
        {process.env.NODE_ENV === 'development' && this.state.error && (
          <pre style={{ marginTop: 24, fontSize: 11, color: '#EF4444', textAlign: 'left', maxWidth: '90vw', overflow: 'auto', background: '#FEF2F2', padding: 16, borderRadius: 10 }}>
            {this.state.error.toString()}
          </pre>
        )}
      </div>
    );
  }
}

function App() {
  const { t, language, setLanguage } = useLanguage();
  const {
    products, setProducts,
    sales, setSales,
    purchases, setPurchases,
    expenses, setExpenses,
    expenseCategories, setExpenseCategories,
    suppliers, setSuppliers,
    customers, setCustomers,
    legacyBalances: legacyBalanceMovements, receivables, totalReceivables, payables, totalPayables,
    manualTransactions, setManualTransactions,
    serviceTransactions, setServiceTransactions,
    filterByPeriod,
    isOnline,
    cloudSynced,
  } = useAppContext();

  const { businessId, isOwner, userRole, refreshBusinesses, currentBusiness, businesses, loading: businessLoading } = useBusiness();
  const perms = usePermissions();
  const permMatrix = usePermissionMatrix();

  // Primary loading gate: true once cloud JSONB has loaded AND business context is ready.
  // Used by skeleton loaders across all tabs to show placeholders until data arrives.
  const isDataReady = cloudSynced && !businessLoading;

  // Services state (serviceTransactions comes from AppContext -included in balances)
  const [servicesRaw, setServices] = useState([]);
  const services = React.useMemo(() => activeRecords(servicesRaw), [servicesRaw]);
  const [showServiceForm, setShowServiceForm] = useState(false);
  const [serviceFormData, setServiceFormData] = useState({ name: '', defaultPrice: '', defaultCost: '' });
  const [serviceEditId, setServiceEditId] = useState(null);
  const [showServiceTransactionForm, setShowServiceTransactionForm] = useState(false);
  const [serviceTransactionFormData, setServiceTransactionFormData] = useState({
    serviceId: '', serviceNameOverride: '', quantity: '', price: '', cost: '',
    customerName: '', customerPhone: '', customerEmail: '', customerAddress: '', customerTIN: '', customerVRN: '',
    paymentMethod: 'cash', date: new Date().toISOString().split('T')[0],
    discount: '', efdReceiptNo: '', taxes: []
  });
  const [serviceTransactionEditId, setServiceTransactionEditId] = useState(null);
  const [serviceCustomerType, setServiceCustomerType] = useState('walkin');
  // ── Cart + payment/footer state for the service form (mirrors product form) ──
  const [serviceLineItems, setServiceLineItems] = useState([]);
  const [serviceLineDraft, setServiceLineDraft] = useState({ serviceId: '', serviceName: '', quantity: '', price: '', cost: '' });
  const [servicePayments, setServicePayments] = useState([{ type: 'cash', accountKey: 'cash', amount: '' }]);
  const [serviceFooterSections, setServiceFooterSections] = useState([]);
  const [serviceIssueInvoice, setServiceIssueInvoice] = useState(false);
  const serviceIssueInvoiceTouched = useRef(false);
  const [showServiceInvoicePreview, setShowServiceInvoicePreview] = useState(false);
  const [lastSavedServiceTransaction, setLastSavedServiceTransaction] = useState(null);
  const [serviceInvoiceCounter, setServiceInvoiceCounter] = useState(() => parseInt(localStorage.getItem('faida_serviceInvoiceCounter') || '0'));
  const [serviceTimeFilter, setServiceTimeFilter] = useState('month');
  const [serviceCustomFrom, setServiceCustomFrom] = useState('');
  const [serviceCustomTo, setServiceCustomTo] = useState('');
  const [showServiceReports, setShowServiceReports] = useState(false);
  const [serviceReportServiceFilter, setServiceReportServiceFilter] = useState('');
  const [serviceReportPaymentFilter, setServiceReportPaymentFilter] = useState('');
  const [serviceReportCustomerFilter, setServiceReportCustomerFilter] = useState('');

  const [activeTab, setActiveTab] = useState(() => { try { return localStorage.getItem('faida_activeTab') || 'dashboard'; } catch(e) { return 'dashboard'; } });
  const [dashboardTimeFilter, setDashboardTimeFilter] = useState('month');
  const [dashboardCustomFrom, setDashboardCustomFrom] = useState('');
  const [dashboardCustomTo, setDashboardCustomTo] = useState('');
  const [stockTimeFilter, setStockTimeFilter] = useState('month');
  const [stockCustomFrom, setStockCustomFrom] = useState('');
  const [stockCustomTo, setStockCustomTo] = useState('');
  const [stockHistoryProduct, setStockHistoryProduct] = useState(null);
  const [expenseHistoryCategory, setExpenseHistoryCategory] = useState(null);
  const [serviceHistoryName, setServiceHistoryName] = useState(null);
  // Tracks which tab the user opened a drilldown from, so Back returns there.
  const [drilldownFromTab, setDrilldownFromTab] = useState(null);
  const [stockHistoryFilter, setStockHistoryFilter] = useState('month');
  const [stockHistoryFrom, setStockHistoryFrom] = useState('');
  const [stockHistoryTo, setStockHistoryTo] = useState('');

  const [showPurchaseForm, setShowPurchaseForm] = useState(false);
  const [isPurchasesReportOpen, setIsPurchasesReportOpen] = useState(false);
  const [purchasesTimeFilter, setPurchasesTimeFilter] = useState('month');
  const [purchasesCustomFrom, setPurchasesCustomFrom] = useState('');
  const [purchasesCustomTo, setPurchasesCustomTo] = useState('');
  const [purchasesAnnualYear, setPurchasesAnnualYear] = useState(new Date().getFullYear());
  const [showSalesForm, setShowSalesForm] = useState(false);
  const [isSalesReportOpen, setIsSalesReportOpen] = useState(false);
  const [salesTimeFilter, setSalesTimeFilter] = useState('month');
  const [salesCustomFrom, setSalesCustomFrom] = useState('');
  const [salesCustomTo, setSalesCustomTo] = useState('');
  const [salesAnnualYear, setSalesAnnualYear] = useState(new Date().getFullYear());
  const [salesChartHover, setSalesChartHover] = useState(null);
  const [purchasesChartHover, setPurchasesChartHover] = useState(null);
  const [expensesChartHover, setExpensesChartHover] = useState(null);
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [isExpensesReportOpen, setIsExpensesReportOpen] = useState(false);
  const [detailItemsModal, setDetailItemsModal] = useState({ open: false, items: [], title: '' });
  // Hide voided transactions toggles (QuickBooks/Xero style)
  const [hideVoidedSales, setHideVoidedSales] = useState(false);
  const [hideVoidedPurchases, setHideVoidedPurchases] = useState(false);
  const [hideVoidedExpenses, setHideVoidedExpenses] = useState(false);
  const [hideVoidedServices, setHideVoidedServices] = useState(false);
  // Full transaction view modal -purchase or sale record
  const [viewTxnModal, setViewTxnModal] = useState({ open: false, kind: null, record: null });
  const [invoicePreviewModal, setInvoicePreviewModal] = useState({ open: false, invoice: null });
  const [editingPaymentIdx, setEditingPaymentIdx] = useState(null);
  const editingPaymentAmtRef = React.useRef('');
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [showSessionExpiredModal, setShowSessionExpiredModal] = useState(false);
  const [expensesTimeFilter, setExpensesTimeFilter] = useState('month');
  const [expensesCustomFrom, setExpensesCustomFrom] = useState('');
  const [expensesCustomTo, setExpensesCustomTo] = useState('');
  const [expensesAnnualYear, setExpensesAnnualYear] = useState(new Date().getFullYear());
  const [serviceAnnualYear, setServiceAnnualYear] = useState(new Date().getFullYear());
  const [serviceChartHover, setServiceChartHover] = useState(null);

  // Edit mode IDs for report overlays
  const [editSaleId, setEditSaleId] = useState(null);
  const [editPurchaseId, setEditPurchaseId] = useState(null);
  const [editExpenseId, setEditExpenseId] = useState(null);
  // Active period pill in each report overlay
  const [salesReportPeriod, setSalesReportPeriod] = useState('month');
  const [purchasesReportPeriod, setPurchasesReportPeriod] = useState('month');
  const [expensesReportPeriod, setExpensesReportPeriod] = useState('month');
  const [expensesReportCategory, setExpensesReportCategory] = useState('all');
  const [purchasesReportProduct, setPurchasesReportProduct] = useState('all');

  // A. FOR PRODUCTS (Setup Tab) - Isolated with prefixes
  const [setup_Name, setSetup_Name] = useState('');
  const [setup_StockQty, setSetup_StockQty] = useState('');
  const [setup_StockValue, setSetup_StockValue] = useState('');
  const [setup_MinAlert, setSetup_MinAlert] = useState('');
  const [setup_EditId, setSetup_EditId] = useState(null);
  const [showSetup_ProductForm, setShowSetup_ProductForm] = useState(false);
  const [setup_SellingPrice, setSetup_SellingPrice] = useState('');
  const [setup_Unit, setSetup_Unit] = useState('piece');
  const [setup_Category, setSetup_Category] = useState('');
  const [setup_ReorderQty, setSetup_ReorderQty] = useState('');
  const [currency, setCurrency] = useState(() => {
    try {
      const keys = Object.keys(localStorage);
      for (const k of keys) {
        if (k.startsWith('faida_') && k.endsWith('_currency')) {
          const v = localStorage.getItem(k);
          if (v) return v;
        }
      }
      const anon = localStorage.getItem('faida_currency');
      if (anon) return anon;
    } catch {}
    return 'TSh';
  });
  const [theme, setTheme] = useState(() => { try { return localStorage.getItem('faida_theme') || 'light'; } catch(e) { return 'light'; } });
  const [fontSize, setFontSize] = useState(() => {
    try { const saved = localStorage.getItem('faida-font-size'); return ['small','medium','large'].includes(saved) ? saved : 'medium'; } catch(e) { return 'medium'; }
  });
  // Real-time sync status
  const [syncStatus, setSyncStatus] = useState('synced'); // 'synced', 'syncing', 'pending'

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  // Session expiry listener -fires when supabaseDB gets a 401
  useEffect(() => {
    const handler = () => {
      // Don't show the "Session Expired" modal during password recovery —
      // the user is unauthenticated by design and the modal would mask the
      // reset form.
      if (isRecoveryUrl || (INITIAL_RECOVERY && INITIAL_RECOVERY.token)) {
        console.log('◇ [session-expired] suppressed during recovery flow');
        return;
      }
      setShowSessionExpiredModal(true);
    };
    window.addEventListener('faida:session-expired', handler);
    return () => window.removeEventListener('faida:session-expired', handler);
  }, []);

  // Input validation -block negative values and 'e' (scientific notation) on all number inputs
  useEffect(() => {
    const blockNegative = (e) => {
      if (e.target.type === 'number') {
        if (e.key === '-' || e.key === '+' || e.key === 'e' || e.key === 'E') {
          e.preventDefault();
        }
      }
    };
    // Also prevent scroll from changing number inputs
    const blockScroll = (e) => {
      if (e.target.type === 'number') e.preventDefault();
    };
    document.addEventListener('keydown', blockNegative);
    document.addEventListener('wheel', blockScroll, { passive: false });
    return () => {
      document.removeEventListener('keydown', blockNegative);
      document.removeEventListener('wheel', blockScroll);
    };
  }, []);

  // Authentication state
  const [currentUser, setCurrentUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  // Lazy initial state -picks recovery screen synchronously before first render.
  // Login is the landing screen (Welcome screen has been retired).
  const [authScreen, setAuthScreen] = useState(() =>
    INITIAL_RECOVERY && INITIAL_RECOVERY.token ? 'resetNewPassword' : 'login'
  ); // 'login' | 'signup' | 'forgot' | 'resetNewPassword' | 'subscription'
  const [loginForm, setLoginForm] = useState({ identifier: '', password: '' });
  const [signupForm, setSignupForm] = useState({ 
    fullName: '',
    email: '', 
    password: '', 
    confirmPassword: '',
    agree: false,
  });
  const [forgotForm, setForgotForm] = useState({ email: '' });
  const [resetForm, setResetForm] = useState({ password: '', confirmPassword: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('monthly');
  const [authError, setAuthError] = useState('');
  const [recoveryToken, setRecoveryToken] = useState(
    () => (INITIAL_RECOVERY && INITIAL_RECOVERY.token) || null
  );

  const dataLoaded = useRef(false);
  const [businessName, setBusinessName] = useState('');
  const [businessAddress, setBusinessAddress] = useState('');
  const [businessLogo, setBusinessLogo] = useState('');
  const [businessLogoUrl, setBusinessLogoUrl] = useState(''); // Store Supabase URL
  const [businessLogoError, setBusinessLogoError] = useState('');
  const [businessTIN, setBusinessTIN] = useState('');
  const [businessVRN, setBusinessVRN] = useState('');
  const [businessRegNo, setBusinessRegNo] = useState('');
  const [tradeLicenseNo, setTradeLicenseNo] = useState('');
  const [complianceEditMode, setComplianceEditMode] = useState(false);
  const [bankName, setBankName] = useState('');
  const [bankAccountName, setBankAccountName] = useState('');
  const [bankAccountNumber, setBankAccountNumber] = useState('');
  const [mobileMoneyProvider, setMobileMoneyProvider] = useState('');
  const [mobileMoneyNumber, setMobileMoneyNumber] = useState('');
  const [invoiceCounter, setInvoiceCounter] = useState(0);
  const [invoiceTerms, setInvoiceTerms] = useState('');
  const [showInvoicePreview, setShowInvoicePreview] = useState(false);
  const [lastSavedSale, setLastSavedSale] = useState(null);
  const [proformaCounter, setProformaCounter] = useState(0);
  const [showProformaForm, setShowProformaForm] = useState(false);
  const [proformaValidityDays, setProformaValidityDays] = useState(7);
  // proformaFooterSections: up to 2 sections, each { label, content }
  const [proformaFooterSections, setProformaFooterSections] = useState([{ label: 'Terms & Conditions', content: '' }]);
  const [savedProformaRecord, setSavedProformaRecord] = useState(null); // non-null = show success screen
  const [proformaData, setProformaData] = useState({
    customerName: '',
    customerPhone: '',
    customerAddress: '',
    date: new Date().toISOString().split('T')[0],
    payments: [{ type: 'cash', amount: '' }],
    discount: '',
    items: [{ productName: '', quantity: '', unitPrice: '', total: '' }]
  });

  // Invoice Management
  const [showInvoiceManagement, setShowInvoiceManagement] = useState(false);
  const [invoiceManagementSection, setInvoiceManagementSection] = useState(null); // 'proforma' | 'issued' | null
  const [invoiceMgmtContext, setInvoiceMgmtContext] = useState('sales'); // 'sales' | 'services'
  const [proformaInvoicesRaw, setProformaInvoices] = useState([]);
  const proformaInvoices = React.useMemo(() => activeRecords(proformaInvoicesRaw), [proformaInvoicesRaw]);
  const [issuedInvoicesRaw, setIssuedInvoices] = useState([]);
  const issuedInvoices = React.useMemo(() => activeRecords(issuedInvoicesRaw), [issuedInvoicesRaw]);
  const [manualInvoicesRaw, setManualInvoices] = useState([]);
  const manualInvoices = React.useMemo(() => activeRecords(manualInvoicesRaw), [manualInvoicesRaw]);
  const [editingManualInvoiceId, setEditingManualInvoiceId] = useState(null);
  const [showManualInvoiceListScreen, setShowManualInvoiceListScreen] = useState(false);
  const [manualInvoiceSearchQuery, setManualInvoiceSearchQuery] = useState('');
  const [manualInvoiceSortOption, setManualInvoiceSortOption] = useState('dateDesc');
  // Dedicated Manual-Invoice form (proforma-styled, separate from POS sales modal)
  const [showManualInvoiceForm, setShowManualInvoiceForm] = useState(false);
  const [manualInvoiceCustomerType, setManualInvoiceCustomerType] = useState('walkin');
  const [manualInvoiceData, setManualInvoiceData] = useState({
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    customerAddress: '',
    customerTIN: '',
    customerVRN: '',
    date: new Date().toISOString().split('T')[0],
    discount: '',
    taxes: [],
    items: [{ productName: '', quantity: '', unitPrice: '', total: '' }],
  });
  const [manualInvoiceFooterSections, setManualInvoiceFooterSections] = useState([{ label: 'Terms & Conditions', content: '' }]);
  const [editingProformaId, setEditingProformaId] = useState(null);
  const [viewingProforma, setViewingProforma] = useState(null);
  const [proformaCustomerType, setProformaCustomerType] = useState('walkin'); // 'walkin' | 'registered'
  const [showProformaListScreen, setShowProformaListScreen] = useState(false);
  const [showIssuedListScreen, setShowIssuedListScreen] = useState(false);
  const [proformaSearchQuery, setProformaSearchQuery] = useState('');
  const [issuedSearchQuery, setIssuedSearchQuery] = useState('');
  const [proformaSortOption, setProformaSortOption] = useState('dateDesc'); // 'dateDesc' | 'dateAsc' | 'amountDesc' | 'amountAsc'
  const [issuedSortOption, setIssuedSortOption] = useState('dateDesc');

  // B. FOR EXPENSES (Expenses Tab) - Isolated with prefixes
  const [expense_Name, setExpense_Name] = useState('');
  const [expense_Desc, setExpense_Desc] = useState('');
  const [expense_EditId, setExpense_EditId] = useState(null);
  const [showExpense_SetupForm, setShowExpense_SetupForm] = useState(false);
  
  // C. FOR SUPPLIERS (Setup Tab)
  const [supplier_Name, setSupplier_Name] = useState('');
  const [supplier_Contact, setSupplier_Contact] = useState('');
  const [supplier_EditId, setSupplier_EditId] = useState(null);
  const [showSupplier_Form, setShowSupplier_Form] = useState(false);
  const [setupScreen, setSetupScreen] = useState(null); // null = menu hub
  // Inline edit states for My Business fields
  const [bizNameDraft, setBizNameDraft] = useState('');
  const [bizNameEditing, setBizNameEditing] = useState(false);
  const [bizAddressDraft, setBizAddressDraft] = useState('');
  const [bizAddressEditing, setBizAddressEditing] = useState(false);

  // Payment Accounts
  const [paymentAccountsRaw, setPaymentAccounts] = useState([]);
  const paymentAccounts = React.useMemo(() => activeRecords(paymentAccountsRaw), [paymentAccountsRaw]);
  const [payForm, setPayForm] = useState({ type: 'bank', bankName: '', networkName: '', accountName: '', accountNumber: '', phoneNumber: '', openingBalance: '', openingBalanceDate: '' });
  const [showPayForm, setShowPayForm] = useState(false);
  const [payEditId, setPayEditId] = useState(null);
  const [payFormErrors, setPayFormErrors] = useState({});

  // ── LEGACY balance engine (kept only for the reconciliation card) ──────
  // The old transaction-based balance + account opening balances. The live
  // dashboard no longer uses this -it now reads `adjustedBalances` below, which
  // is derived from the single cash ledger. This remains so the reconciliation
  // card can still show old-vs-new until we retire it.
  const legacyBalances = React.useMemo(() => {
    let cashOB = 0, bankOB = 0, mmOB = 0;
    (paymentAccounts || []).forEach(acc => {
      const amt = parseFloat(acc.openingBalance) || 0;
      if (!amt) return;
      if (acc.type === 'cash') cashOB += amt;
      else if (acc.type === 'bank') bankOB += amt;
      else if (acc.type === 'mobilemoney') mmOB += amt;
    });
    return {
      cash: (legacyBalanceMovements.cash || 0) + cashOB,
      bank: (legacyBalanceMovements.bank || 0) + bankOB,
      mobileMoney: (legacyBalanceMovements.mobileMoney || 0) + mmOB,
    };
  }, [legacyBalanceMovements, paymentAccounts]);

  // Business Type -read the saved value synchronously on mount so the UI
  // never flashes the hardcoded default before the async loaders catch up.
  const [businessType, setBusinessType] = useState(() => {
    try {
      // Look for any user-keyed businessType key first (covers signed-in users)
      const keys = Object.keys(localStorage);
      for (const k of keys) {
        if (k.startsWith('faida_') && k.endsWith('_businessType')) {
          const v = localStorage.getItem(k);
          if (v) return v;
        }
      }
      // Anon key fallback
      const anon = localStorage.getItem('faida_businessType');
      if (anon) return anon;
    } catch {}
    return 'products_and_services';
  });
  // Business type transition warning dialog
  const [businessTypeTransitionWarning, setBusinessTypeTransitionWarning] = useState(null); // null | { newType, risk }

  // ── Privacy Mode state ──────────────────────────────────────────
  // Configuration (SYNCED to cloud -same on every device):
  const [privacyEnabled, setPrivacyEnabled] = useState(false);                    // master switch
  const [privacyPinHash, setPrivacyPinHash] = useState('');                       // sha256(pin), never plaintext
  const [privacyRecoveryCodeHash, setPrivacyRecoveryCodeHash] = useState('');     // sha256(recovery code)
  const [privacyHiddenTabs, setPrivacyHiddenTabs] = useState([]);                 // e.g. ['dashboard','reports']
  const [privacyHiddenItems, setPrivacyHiddenItems] = useState({});               // { dashboard: ['profitCard','bankBalance'], sales: ['costPrice'] }
  const [privacyAutoLockMinutes, setPrivacyAutoLockMinutes] = useState(5);        // 5|15|30|-1 (never)
  // Unlock state (LOCAL ONLY -in-memory, resets on refresh):
  const [privacyUnlocked, setPrivacyUnlocked] = useState(false);
  const privacyLastActivityRef = useRef(Date.now());
  // 3-attempt lockout for wrong PINs
  const [privacyPinAttempts, setPrivacyPinAttempts] = useState(0);
  const [privacyLockoutUntil, setPrivacyLockoutUntil] = useState(0);              // epoch ms
  // Modals
  const [showPinModal, setShowPinModal] = useState(null);                         // null | 'unlock' | 'setup' | 'change' | 'disable' | 'recover' | 'regenCode'
  // PIN modal input state -hoisted here (not inside a nested component)
  // so React doesn't wipe it on re-render when e.g. privacyPinAttempts changes.
  const [pmStep, setPmStep] = useState('enter');
  const [pmPin, setPmPin] = useState('');
  const [pmPin2, setPmPin2] = useState('');
  const [pmOldPin, setPmOldPin] = useState('');
  const [pmRecoveryInput, setPmRecoveryInput] = useState('');
  const [pmOtpInput, setPmOtpInput] = useState('');
  const [pmError, setPmError] = useState('');
  const [pmBusy, setPmBusy] = useState(false);
  const [showRecoveryCodeOnce, setShowRecoveryCodeOnce] = useState('');           // shown once at PIN creation

  // Settings sub-screens
  const [settingsScreen, setSettingsScreen] = useState(null); // null | 'privacyPolicy' | 'contactUs' | 'team'

  // Team management state
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamInvitations, setTeamInvitations] = useState([]);
  const [teamRoles, setTeamRoles] = useState([]);
  const [teamLoading, setTeamLoading] = useState(false);
  const [teamError, setTeamError] = useState('');
  const [teamInviteEmail, setTeamInviteEmail] = useState('');
  const [teamInviteRole, setTeamInviteRole] = useState('');
  const [teamInviting, setTeamInviting] = useState(false);
  const [teamInviteCode, setTeamInviteCode] = useState(''); // shown after invite
  const [teamActionBusy, setTeamActionBusy] = useState(null); // memberId being acted on
  const [showTeamInviteModal, setShowTeamInviteModal] = useState(false);
  const [teamActionMember, setTeamActionMember] = useState(null); // member object for actions sheet
  const [joinInviteCode, setJoinInviteCode] = useState('');
  const [joinBusy, setJoinBusy] = useState(false);
  const [joinError, setJoinError] = useState('');
  const [selectedInvitation, setSelectedInvitation] = useState(null);
  const [inviteCodes, setInviteCodes] = useState({}); // { invitationEmail: code }
  const [teamView, setTeamView] = useState('main'); // 'main' | 'invitations'
  const [invFilter, setInvFilter] = useState('all'); // 'all' | 'pending' | 'accepted' | 'revoked'
  const [expandedRole, setExpandedRole] = useState(null); // role system_key or null

  // Subscription data
  const [subscriptionData, setSubscriptionData] = useState(() => {
    try {
      const saved = localStorage.getItem(getUserKey('subscription'));
      return saved ? JSON.parse(saved) : { plan: 'trial', trialEndsAt: null };
    } catch(e) { return { plan: 'trial', trialEndsAt: null }; }
  });
  const [paymentFlow, setPaymentFlow] = useState(null); // null | { step, planId, paymentId, phone, error }
  const [paymentSuccessOverlay, setPaymentSuccessOverlay] = useState(false);
  const [subscriptionExpired, setSubscriptionExpired] = useState(false);

  // D. FOR CUSTOMERS (Setup Tab)
  const [customer_Name, setCustomer_Name] = useState('');
  const [customer_Phone, setCustomer_Phone] = useState('');
  const [customer_Email, setCustomer_Email] = useState('');
  const [customer_Address, setCustomer_Address] = useState('');
  const [customer_TIN, setCustomer_TIN] = useState('');
  const [customer_VRN, setCustomer_VRN] = useState('');
  const [customer_Notes, setCustomer_Notes] = useState('');
  const [customer_EditId, setCustomer_EditId] = useState(null);
  const [showCustomer_Form, setShowCustomer_Form] = useState(false);

  // LEDGER state
  const [ledgerEntity, setLedgerEntity] = useState(null); // { type: 'customer'|'supplier', name, contact }
  const [showLedger, setShowLedger] = useState(false);

  // E. EXTENDED SALES FORM state
  const [salesCustomerType, setSalesCustomerType] = useState('walkin');
  // Whether to issue a formal invoice (INV-XXXX, added to Issued Invoices list, PDF preview)
  // for a fully-paid sale. Auto-defaults based on customer (registered/named ⇒ ON,
  // blank walk-in ⇒ OFF). User can override before saving.
  const [salesIssueInvoice, setSalesIssueInvoice] = useState(true);
  // Tracks whether the user has explicitly toggled the invoice checkbox; we
  // only auto-default until they touch it, then we respect their choice.
  const salesIssueInvoiceTouched = useRef(false);
  const [salePayments, setSalePayments] = useState([{ type: 'cash', amount: '' }]);
  // Sales item kind: 'product' or 'service'. Auto-derived from businessType when fixed.
  const [saleItemKind, setSaleItemKind] = useState('product');
  // saveMode: 'receipt' = payment received now (isPaid=true); 'invoice' = issue invoice, payment pending
  const [saleSaveMode, setSaleSaveMode] = useState('receipt');
  const [saleFromInvoiceManagement, setSaleFromInvoiceManagement] = useState(false);
  const [saleInvoiceNotes, setSaleInvoiceNotes] = useState('');
  // Footer sections for invoice (up to 2, shown side by side if both filled)
  const [saleFooterSections, setSaleFooterSections] = useState([{ label: 'Terms & Conditions', content: '' }]);

  // ── Multi-item cart for sales ──────────────────────────────────────
  // Each item: { id, type:'product'|'service', productName, quantity, sellingPrice, cost }
  const [saleLineItems, setSaleLineItems] = useState([]);
  const [saleLineDraft, setSaleLineDraft] = useState({ type: 'product', productName: '', quantity: '', sellingPrice: '', cost: '' }); // always product -services recorded in Services tab
  // ── Multi-item cart for purchases ─────────────────────────────────
  // Each item: { id, productName, quantity, unitPrice, totalCost }
  const [purchaseLineItems, setPurchaseLineItems] = useState([]);
  const [purchaseLineDraft, setPurchaseLineDraft] = useState({ productName: '', quantity: '', unitPrice: '', totalCost: '' });

  // ── WEIGHTED AVERAGE COST (WAC) -single source of truth for unit cost ──
  // Delegates to the pure, unit-tested implementation in moneyMath.js.
  // Used for sale COGS snapshots AND stock valuation so profit and inventory
  // reports always agree. Recomputed from full purchase history on demand —
  // editing/deleting old purchases can never corrupt it.
  // products/purchases/sales are already active-only (from AppContext). Returns
  // are filtered to ACTIVE + PARENT-VALID so a soft-deleted OR orphaned return
  // can't shift WAC/COGS. (validSalesReturns is defined below; getProductWAC is
  // only ever invoked after render initialisation, so the reference is safe.)
  // Void-filter inline: getProductWAC is declared before the acct* memos
  // (which depend on validSalesReturns/validPurchaseReturns that are declared
  // later). Direct inline filtering avoids temporal-dead-zone issues.
  const getProductWAC = (productName, asOf = null) => computeWAC(
    productName, products,
    (purchases || []).filter(p => p.status !== 'void'),
    (sales || []).filter(s => s.status !== 'void'),
    (validSalesReturns || []).filter(r => r.status !== 'void'),
    (validPurchaseReturns || []).filter(r => r.status !== 'void'),
    null, asOf
  );

  // F. SALES REPORT extra filters
  const [salesReportProductFilter, setSalesReportProductFilter] = useState('');
  const [salesReportPaymentFilter, setSalesReportPaymentFilter] = useState('');
  const [salesReportCustomerFilter, setSalesReportCustomerFilter] = useState('');
  
  const [successMessage, setSuccessMessage] = useState('');
  
  // Apply font size changes dynamically
  useEffect(() => {
    document.documentElement.classList.remove('font-small', 'font-medium', 'font-large');
    document.documentElement.classList.add(`font-${fontSize}`);
    try { localStorage.setItem('faida-font-size', fontSize); } catch(e) {}
  }, [fontSize]);

  // If business type changes to service_only and user is on a hidden tab, bounce to dashboard
  useEffect(() => {
    if (businessType === 'service_only' && (activeTab === 'purchases' || activeTab === 'inventory' || activeTab === 'sales')) {
      setActiveTab('dashboard');
    }
    if (businessType === 'products_only' && activeTab === 'services') {
      setActiveTab('dashboard');
    }
  }, [businessType, activeTab]);

  // If user is on a tab they don't have permission to view, bounce to dashboard
  useEffect(() => {
    // Don't redirect while business context is still loading — perms are ALL_FALSE until resolved
    if (businessLoading) return;
    const tabPermMap = {
      dashboard: 'viewDashboard',
      sales: 'viewSales', purchases: 'viewPurchases', expenses: 'viewExpenses',
      services: 'viewServices', inventory: 'viewInventory', reports: 'viewReports',
    };
    const needed = tabPermMap[activeTab];
    if (needed && perms[needed] === false) {
      // Find first visible tab to redirect to
      const fallback = Object.entries(tabPermMap).find(([, perm]) => perms[perm] !== false);
      setActiveTab(fallback ? fallback[0] : 'settings');
    }
  }, [activeTab, perms, businessLoading]);

  // Reset PIN modal inputs when the modal opens (or mode changes)
  useEffect(() => {
    if (!showPinModal) return;
    const mode = showPinModal;
    setPmStep(
      mode === 'setup'  ? 'new'
      : mode === 'recover' ? 'chooseMethod'
      : 'enter'
    );
    setPmPin('');
    setPmPin2('');
    setPmOldPin('');
    setPmRecoveryInput('');
    setPmOtpInput('');
    setPmError('');
    setPmBusy(false);
  }, [showPinModal]);

  // ── Privacy Mode: auto-lock timer + activity listener ─────────────
  // Only runs when Privacy is enabled AND currently unlocked. Any user
  // interaction resets the idle clock. After N minutes of no activity,
  // we re-lock the device. Set to -1 to disable auto-lock entirely.
  useEffect(() => {
    if (!privacyEnabled) return;
    if (!privacyUnlocked) return;
    if (privacyAutoLockMinutes < 0) return; // "never" -user opted out

    const bump = () => { privacyLastActivityRef.current = Date.now(); };
    // Listen to all common activity signals
    const evts = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
    evts.forEach(e => window.addEventListener(e, bump, { passive: true }));

    // Check every 15s -cheaper than a per-second timer and still snappy enough
    const idleMs = privacyAutoLockMinutes * 60 * 1000;
    const timer = setInterval(() => {
      if (Date.now() - privacyLastActivityRef.current >= idleMs) {
        setPrivacyUnlocked(false); // re-lock
      }
    }, 15000);

    return () => {
      clearInterval(timer);
      evts.forEach(e => window.removeEventListener(e, bump));
    };
  }, [privacyEnabled, privacyUnlocked, privacyAutoLockMinutes]);

  // When Privacy master switch turns OFF, drop unlock state (safety)
  useEffect(() => {
    if (!privacyEnabled) setPrivacyUnlocked(false);
  }, [privacyEnabled]);

  // If the current tab is now hidden by Privacy Mode, bounce to a visible one.
  // Prefer the Expenses tab (always available); fall back to Setup.
  useEffect(() => {
    if (privacyEnabled && !privacyUnlocked && privacyHiddenTabs.includes(activeTab)) {
      setActiveTab(privacyHiddenTabs.includes('expenses') ? 'setup' : 'expenses');
    }
  }, [privacyEnabled, privacyUnlocked, privacyHiddenTabs, activeTab]);

  // Sync sale item kind with business type
  useEffect(() => {
    if (businessType === 'service_only') setSaleItemKind('service');
    else if (businessType === 'products_only') setSaleItemKind('product');
  }, [businessType]);
  
  const [purchaseFormData, setPurchaseFormData] = useState({
    productName: '',
    quantity: '',
    unitPrice: '',
    totalCost: '',
    supplier: '',
    date: new Date().toISOString().split('T')[0],
    taxes: [],
    discount: '',
    efdReceiptNo: '',
  });

  const [expenseFormData, setExpenseFormData] = useState({
    category: '',
    amount: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    paymentMethod: 'cash',
  });
  const [expensePayments, setExpensePayments] = useState([{ type: 'cash', amount: '' }]);

  const [salesFormData, setSalesFormData] = useState({
    productName: '',
    quantity: '',
    sellingPrice: '',
    cost: '',
    customerName: '',
    // Walk-in customer details (for invoices that need full contact info)
    customerPhone: '',
    customerEmail: '',
    customerAddress: '',
    customerTIN: '',
    customerVRN: '',
    date: new Date().toISOString().split('T')[0],
    taxes: [],
    discount: '',
    efdReceiptNo: '',
  });
  
  const [purchaseSupplierType, setPurchaseSupplierType] = useState('registered');
  // eslint-disable-next-line no-unused-vars
  const [purchasePaymentMethod, setPurchasePaymentMethod] = useState('cash');
  const [purchasePayments, setPurchasePayments] = useState([{ type: 'cash', amount: '' }]);

  // Convenience wrapper around the existing successMessage toast
  const showToast = (msg, ms = 3000) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(''), ms);
  };

  // ── In-app alert/confirm modal (replaces browser alert()/confirm()) ─
  const [appAlert, setAppAlert] = useState({ open: false, title: '', message: '', tone: 'info', mode: 'alert', onConfirm: null });
  const showAlert = (message, opts = {}) => {
    setAppAlert({
      open: true,
      title: opts.title || (opts.tone === 'error' ? (language === 'sw' ? 'Hitilafu' : 'Error') : (language === 'sw' ? 'Taarifa' : 'Notice')),
      message,
      tone: opts.tone || 'info',
      mode: 'alert',
      onConfirm: null,
    });
  };
  const showConfirm = (message, onConfirm, opts = {}) => {
    setAppAlert({
      open: true,
      title: opts.title || (language === 'sw' ? 'Thibitisha' : 'Confirm'),
      message,
      tone: opts.tone || 'warn',
      mode: 'confirm',
      onConfirm,
    });
  };

  // Default date filters MUST match the default report period ('month'),
  // otherwise the Month tab looks selected on load but the data shows the whole
  // year until the tab is clicked. So: current month start → today.
  const _defMonthFrom = (() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-01`; })();
  const _defToday = (() => new Date().toISOString().split('T')[0])();
  const [salesDateFilters, setSalesDateFilters] = useState(() => ({ fromDate: _defMonthFrom, toDate: _defToday }));

  const [expenseStartDate, setExpenseStartDate] = useState(() => _defMonthFrom);
  const [expenseEndDate, setExpenseEndDate] = useState(() => _defToday);

  const [reportDateFilters, setReportDateFilters] = useState(() => ({ fromDate: _defMonthFrom, toDate: _defToday }));

  // ── Dashboard drilldown ───────────────────────────────────────────
  const [activeDrilldown, setActiveDrilldown] = useState(null);
  const [drilldownPeriod, setDrilldownPeriod] = useState('month');
  // Default the Custom date pickers to current year so clicking "Custom"
  // never shows empty dd/mm/yyyy placeholders -user can then narrow if needed.
  const [drilldownCustomFrom, setDrilldownCustomFrom] = useState(() => `${new Date().getFullYear()}-01-01`);
  const [drilldownCustomTo, setDrilldownCustomTo] = useState(() => `${new Date().getFullYear()}-12-31`);
  // When a drilldown opens from a dashboard box that's on Custom, copy the
  // dashboard's custom dates so the drilldown shows the exact same period.
  useEffect(() => {
    if (activeDrilldown && dashboardTimeFilter === 'custom') {
      if (dashboardCustomFrom) setDrilldownCustomFrom(dashboardCustomFrom);
      if (dashboardCustomTo) setDrilldownCustomTo(dashboardCustomTo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDrilldown]);
  const [showWithdrawalForm, setShowWithdrawalForm] = useState(false);
  const [showDebtPaymentForm, setShowDebtPaymentForm] = useState(false);
  const [debtPaymentData, setDebtPaymentData] = useState({ saleId: '', amount: '', paymentMethod: 'cash', date: new Date().toISOString().split('T')[0] });
  const [showLoanPaymentForm, setShowLoanPaymentForm] = useState(false);
  const [loanPaymentData, setLoanPaymentData] = useState({ purchaseId: '', amount: '', paymentMethod: 'cash', date: new Date().toISOString().split('T')[0] });
  // Feature 5: Sales Returns
  const [showReturnsModal, setShowReturnsModal] = useState(false);
  const [showReturnsReport, setShowReturnsReport] = useState(false);
  const [returnsReportFrom, setReturnsReportFrom] = useState(`${new Date().getFullYear()}-01-01`);
  const [returnsReportTo, setReturnsReportTo] = useState(new Date().toISOString().split('T')[0]);
  const [showPurchaseReturnsReport, setShowPurchaseReturnsReport] = useState(false);
  const [purchaseReturnsReportFrom, setPurchaseReturnsReportFrom] = useState(`${new Date().getFullYear()}-01-01`);
  const [purchaseReturnsReportTo, setPurchaseReturnsReportTo] = useState(new Date().toISOString().split('T')[0]);
  const [salesReturnsRaw, setSalesReturns] = useState([]);
  const salesReturns = React.useMemo(() => activeRecords(salesReturnsRaw), [salesReturnsRaw]);
  // Feature 6: Stock Adjustments
  const [showStockAdjModal, setShowStockAdjModal] = useState(false);
  const [stockAdjFormData, setStockAdjFormData] = useState({ productId: '', qty: '', reason: 'damaged', direction: 'decrease', notes: '', date: new Date().toISOString().split('T')[0] });
  const [stockAdjHistory, setStockAdjHistory] = useState([]);
  // Feature 7: Partial Payments
  const [showPartialPayModal, setShowPartialPayModal] = useState(false);
  const [partialPaySale, setPartialPaySale] = useState(null);
  // Feature 8: Customer Statement
  const [showCustomerStatement, setShowCustomerStatement] = useState(false);
  // Mobile dashboard "Reports" section drill-in: null | 'primary' | 'debts' | 'cashflow' | 'quick'
  const [mobileReportsSection, setMobileReportsSection] = useState(null);
  // Feature 9: Aging Report
  const [showAgingReport, setShowAgingReport] = useState(false);
  // Feature 10: Daily Summary
  const [showDailySummary, setShowDailySummary] = useState(false);
  const [dailySummaryDate, setDailySummaryDate] = useState(new Date().toISOString().split('T')[0]);
  // Feature 11: Stock Valuation
  const [showStockValuation, setShowStockValuation] = useState(false);
  // Feature 11.5: Stock Report
  const [showStockReport, setShowStockReport] = useState(false);
  // Reports Tab -Sales/Purchases/Expenses reports are unified with their
  // parent-tab counterparts (isSalesReportOpen / isPurchasesReportOpen /
  // isExpensesReportOpen). The Reports tab buttons redirect to those flags.
  // Cashflow Reports
  const [showCashflowReport, setShowCashflowReport] = useState(false);
  const [cashflowReportType, setCashflowReportType] = useState('cash'); // 'cash', 'bank', 'mobilemoney', 'total'
  // Default to current year (Jan 1 → Dec 31) so balances and totals reflect
  // a sensible period instead of "everything ever" (which causes confusing
  // negative closing balances from accumulating all historical transactions).
  const _yr = new Date().getFullYear();
  const [cashflowReportFilter, setCashflowReportFilter] = useState({ startDate: `${_yr}-01-01`, endDate: `${_yr}-12-31`, category: '' }); // category: 'sales_products', 'sales_services', 'purchases', 'expenses'
  const [selectedBankAccount, setSelectedBankAccount] = useState('all'); // 'all' or account index
  const [selectedMobileMoneyAccount, setSelectedMobileMoneyAccount] = useState('all'); // 'all' or account index
  // Filters for the cashflow drilldown (most relevant on Total Cashflow view)
  const [drilldownFilterAcctType, setDrilldownFilterAcctType] = useState('all'); // 'all' | 'cash' | 'bank' | 'mobilemoney'
  const [drilldownFilterSource, setDrilldownFilterSource] = useState('all');     // 'all' | 'sale' | 'purchase' | 'expense' | 'manual' | 'return'
  const [drilldownFilterSearch, setDrilldownFilterSearch] = useState('');
  // Feature 12: WHT Report
  const [showWHTReport, setShowWHTReport] = useState(false);
  const [whtTab, setWhtTab] = useState('sales'); // 'sales' | 'purchases'
  // Feature 13: VAT Report
  const [showVATReport, setShowVATReport] = useState(false);
  const [vatTab, setVatTab] = useState('sales'); // 'sales' | 'purchases'
  const [vatFrom, setVatFrom] = useState(`${new Date().getFullYear()}-01-01`);
  const [vatTo, setVatTo] = useState(new Date().toISOString().split('T')[0]);
  const [defaultVatRate, setDefaultVatRate] = useState(() => { try { return parseFloat(localStorage.getItem('faida-vat-rate') || '18'); } catch(e) { return 18; } });
  // VAT is OFF by default. Most micro-SMEs are below the TRA VAT threshold and
  // should never see VAT. Only when the owner marks the business VAT-registered
  // do the tax controls appear. This prevents casual mis-use of VAT (which
  // would otherwise affect profit/receipts incorrectly).
  const [vatRegistered, setVatRegistered] = useState(() => { try { return localStorage.getItem('faida-vat-registered') === '1'; } catch(e) { return false; } });
  const [showPandL, setShowPandL] = useState(false);
  const [plPeriod, setPlPeriod] = useState('month'); // month | quarter | year | custom
  const [plFrom, setPlFrom] = useState(`${new Date().getFullYear()}-01-01`);
  const [plTo, setPlTo] = useState(new Date().toISOString().split('T')[0]);
  // Lifted from IIFE modals to fix React hooks-in-conditional crash in production
  const [stmtCustId, setStmtCustId] = useState('');
  const [stmtFrom, setStmtFrom] = useState(`${new Date().getFullYear()}-01-01`);
  const [stmtTo, setStmtTo] = useState(new Date().toISOString().split('T')[0]);
  const [whtFrom, setWhtFrom] = useState(`${new Date().getFullYear()}-01-01`);
  const [whtTo, setWhtTo] = useState(new Date().toISOString().split('T')[0]);
  const [returnSaleId, setReturnSaleId] = useState('');
  const [returnSaleDate, setReturnSaleDate] = useState('');
  const [returnSaleProduct, setReturnSaleProduct] = useState('');
  const [returnSaleCustomer, setReturnSaleCustomer] = useState('');
  const [returnQty, setReturnQty] = useState(''); // legacy single-item
  const [salesReturnItemQtys, setSalesReturnItemQtys] = useState({}); // per-item idx → qty (multi-item)
  const [returnReason, setReturnReason] = useState('');
  const [returnDate, setReturnDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [returnRefundMethod, setReturnRefundMethod] = useState('cash');
  const [payDate, setPayDate] = useState('');
  const payAmtRef = useRef('');
  const [payRows, setPayRows] = useState([{ id: 1, type: 'cash', amountRef: { current: '' } }]);
  const [retPurchaseId, setRetPurchaseId] = useState('');
  const [retQty, setRetQty] = useState(''); // legacy single-item field, kept for back-compat
  const [retItemQtys, setRetItemQtys] = useState({}); // {itemIdx: qty} for multi-item returns
  const [retRestock, setRetRestock] = useState(true);
  const [retRefundMethod, setRetRefundMethod] = useState('cash');
  const [retReason, setRetReason] = useState('');
  const [retDate, setRetDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [retSearchQuery, setRetSearchQuery] = useState('');
  const [retFilterDate, setRetFilterDate] = useState('');
  const [retFilterProduct, setRetFilterProduct] = useState('');
  const [retFilterSupplier, setRetFilterSupplier] = useState('');
  const [faqOpenIdx, setFaqOpenIdx] = useState(null);
  const [userManualOpenIdx, setUserManualOpenIdx] = useState(null);
  // Feature 13: Recurring Expenses
  const [recurringExpenses, setRecurringExpenses] = useState([]);
  const [showRecurringModal, setShowRecurringModal] = useState(false);
  const [recurringForm, setRecurringForm] = useState({ name: '', category: '', amount: '', frequency: 'monthly', paymentMethod: 'cash', nextDueDate: new Date().toISOString().split('T')[0] });
  const [recurringEditId, setRecurringEditId] = useState(null);
  // Feature 15: Purchase Returns
  const [showPurchaseReturnModal, setShowPurchaseReturnModal] = useState(false);
  const [purchaseReturnsRaw, setPurchaseReturns] = useState([]);
  const purchaseReturns = React.useMemo(() => activeRecords(purchaseReturnsRaw), [purchaseReturnsRaw]);

  // Returns durability: only ACTIVE returns whose PARENT is still active feed any
  // calculation. A stale return whose parent was deleted (e.g. arriving from
  // another device) is quarantined -it affects no cash/COGS/stock/AR/AP. The
  // quarantined set is surfaced in the dev stock card, never silently discarded.
  const validSalesReturns = React.useMemo(() => validReturns(salesReturns, { sales, purchases }), [salesReturns, sales, purchases]);
  const validPurchaseReturns = React.useMemo(() => validReturns(purchaseReturns, { sales, purchases }), [purchaseReturns, sales, purchases]);
  const quarantinedReturnsList = React.useMemo(() => [
    ...quarantinedReturns(salesReturns, { sales, purchases }),
    ...quarantinedReturns(purchaseReturns, { sales, purchases }),
  ], [salesReturns, purchaseReturns, sales, purchases]);

  // ── Cash Ledger (PARALLEL, read-only) ─────────────────────────────────
  // Derived single-source ledger for the Payment Ledger migration. It does NOT
  // drive any displayed balance yet -it only lets us reconcile against the
  // current engines on real data before switching over. Manual transactions are
  // split: genuine adjustments (top-ups/withdrawals/drawings) feed the ledger;
  // "bridge" entries (collections/refunds/reversals the old system created) are
  // excluded because the ledger derives those from their source records.
  // NOTE: declared AFTER salesReturns/purchaseReturns so it never references
  // them before initialisation (that caused a temporal-dead-zone crash).

  // ── VOID-SAFE accounting arrays ──────────────────────────────────────
  // Voided transactions must contribute ZERO to all accounting. The frozen
  // engines (moneyMath, cashLedger, inventory, costAudit) have no void
  // awareness — the filter is applied HERE at the call sites.
  // For returns: exclude returns that are themselves voided OR whose parent
  // transaction is voided (an orphan return of a voided parent must not
  // create accounting impact).
  const acctSales = React.useMemo(() => (sales || []).filter(s => s.status !== 'void'), [sales]);
  const acctPurchases = React.useMemo(() => (purchases || []).filter(p => p.status !== 'void'), [purchases]);
  const acctExpenses = React.useMemo(() => (expenses || []).filter(e => e.status !== 'void'), [expenses]);
  const acctServiceTxns = React.useMemo(() => (serviceTransactions || []).filter(st => st.status !== 'void'), [serviceTransactions]);

  // Void-safe returns: exclude voided returns AND returns whose parent is voided
  const acctSalesReturns = React.useMemo(() => {
    const voidedSaleIds = new Set((sales || []).filter(s => s.status === 'void').map(s => String(s.id)));
    return (validSalesReturns || []).filter(r =>
      r.status !== 'void' && !voidedSaleIds.has(String(r.originalSaleId))
    );
  }, [validSalesReturns, sales]);

  const acctPurchaseReturns = React.useMemo(() => {
    const voidedPurchaseIds = new Set((purchases || []).filter(p => p.status === 'void').map(p => String(p.id)));
    return (validPurchaseReturns || []).filter(r =>
      r.status !== 'void' && !voidedPurchaseIds.has(String(r.originalPurchaseId))
    );
  }, [validPurchaseReturns, purchases]);

  const cashLedgerData = React.useMemo(() => {
    // Exclude "bridge" manual transactions (see isBridgeManualTransaction in
    // cashLedger.js -flag-first, description fallback, fully unit-tested).
    // sales/purchases/expenses/serviceTransactions/manualTransactions arrive
    // already filtered (active-only) from AppContext. The App.js-local
    // collections must be filtered here so a soft-deleted account, invoice or
    // return never contributes cash. (C3 deletion safety.)
    const manualAdjustments = activeRecords(manualTransactions).filter(mt => !isBridgeManualTransaction(mt));
    const ledger = buildCashLedger({
      paymentAccounts: activeRecords(paymentAccounts),
      sales: acctSales, purchases: acctPurchases, expenses: acctExpenses, serviceTransactions: acctServiceTxns,
      manualInvoices: activeRecords(manualInvoices),
      salesReturns: acctSalesReturns,
      purchaseReturns: acctPurchaseReturns,
      manualAdjustments,
    });
    // H5: the CURRENT position excludes future-dated entries (a post-dated
    // transaction hasn't happened yet). Period reports keep the full `ledger`
    // and apply their own window (Custom may include the future). `ledgerToDate`
    // + `agg` are the today-capped figures the dashboard/current balances use.
    const todayStr = getPeriodRange('today').to;
    const ledgerToDate = ledger.filter(r => dateKey(r.date) <= todayStr);
    return { ledger, ledgerToDate, agg: ledgerAggregate(ledgerToDate) };
  }, [paymentAccounts, acctSales, acctPurchases, acctExpenses, acctServiceTxns, manualInvoices, acctSalesReturns, acctPurchaseReturns, manualTransactions]);

  // ── CUTOVER: the dashboard cash position now reads the single cash ledger ──
  // (opening balances are already included in the ledger aggregate). Every
  // wallet figure the dashboard shows is now the ledger's -the correct,
  // partial-payment-aware, de-double-counted number.
  //
  // Use STRICT per-wallet balances (each row counts only for its exact wallet),
  // the SAME logic the wallet drilldowns, reports and payment forms use -so the
  // dashboard, drilldowns and reports always agree. (aggregateBalances lumped
  // any unrecognized wallet type into cash, which could inflate the cash figure
  // relative to the strict per-wallet view.)
  // Uses the today-capped ledger so a future-dated entry doesn't move the
  // CURRENT balance (it will once its date arrives). Matches a period report
  // whose window ends today.
  const adjustedBalances = React.useMemo(() => ({
    cash: ledgerWalletBalance(cashLedgerData.ledgerToDate, 'cash'),
    bank: ledgerWalletBalance(cashLedgerData.ledgerToDate, 'bank'),
    mobileMoney: ledgerWalletBalance(cashLedgerData.ledgerToDate, 'mobilemoney'),
  }), [cashLedgerData]);

  // ── C2/H6: stored-stock reconciliation guard (dev card + rebuild) ─────────
  // Compares each product's stored balance against the quantity implied by its
  // full transaction history (opening + purchases − sales + customer returns −
  // supplier returns ± adjustments), matched by stable productId. All inputs
  // are active-only so tombstoned records are excluded. Read-only -surfacing a
  // mismatch never changes live stock on its own.
  const stockReconciliation = React.useMemo(() => reconcileProductStock({
    products,
    purchases: acctPurchases,
    sales: acctSales,
    salesReturns: acctSalesReturns,
    purchaseReturns: acctPurchaseReturns,
    stockAdjustments: activeRecords(stockAdjHistory),
  }), [products, acctPurchases, acctSales, acctSalesReturns, acctPurchaseReturns, stockAdjHistory]);

  // ── Chronological replay COGS -the SINGLE source of truth for period COGS ──
  // (Business Statement, dashboard KPI, daily summary, product report, exports).
  // Replays ALL prior history per product for a correct perpetual WAC, but
  // accrues COGS only for in-period sale/return events. Customer returns are
  // reversed at cost INSIDE the replay, so callers must NOT also subtract a
  // separate returns-cost figure. Memoized on the valid (non-deleted, non-
  // quarantined) records; getReplayCOGS(from,to) is O(products × events) per call.
  const replayCOGSInputs = React.useMemo(() => ({
    products, purchases: acctPurchases, sales: acctSales,
    salesReturns: acctSalesReturns, purchaseReturns: acctPurchaseReturns,
  }), [products, acctPurchases, acctSales, acctSalesReturns, acctPurchaseReturns]);
  const getReplayCOGS = React.useCallback(
    (from, to) => periodReplayCOGS(replayCOGSInputs, from || null, to || null),
    [replayCOGSInputs]
  );

  // Rebuild ONLY the stored quantity from history (never WAC/records), after an
  // explicit preview + confirmation. Unresolved-legacy products are never touched.
  const handleRebuildStock = () => {
    const { changes, apply } = rebuildStoredStock(stockReconciliation);
    if (changes.length === 0) { showAlert('Stock is already reconciled -nothing to rebuild.', { title: 'Stock reconciliation' }); return; }
    const preview = changes.map(c => `• ${c.productName}: ${c.from} → ${c.to}`).join('\n');
    showConfirm(
      `Rebuild stored stock from transaction history?\n\nThis updates ONLY the stored quantity for ${changes.length} product(s) (WAC and transactions are untouched). Unresolved/duplicate-name products are left alone.\n\n${preview}`,
      () => { setProducts(prev => apply(prev)); showToast('Stored stock rebuilt from history'); },
      { tone: 'warn', title: 'Rebuild stored stock' }
    );
  };

  // ── Correction 1: re-date an impossible-date return (return before its sale) ─
  // Operator-approved; keeps the original date for audit. Do this BEFORE any
  // stock rebuild or cost backfill, because the return's COGS reversal feeds the
  // reconciliation totals.
  const handleFixReturnDate = (row) => {
    if (!row || !row.parentSaleDate) { showAlert('No valid sale date to re-date this return to.', { title: 'Cannot fix return' }); return; }
    showConfirm(
      `Re-date this customer return?\n\n${row.product} -return #${row.returnId}\nCurrent (impossible) date: ${row.date}\nParent sale date: ${row.parentSaleDate}\n\nThe return will be moved to ${row.parentSaleDate} (the sale date). The original date is kept for audit. Nothing else changes.`,
      () => {
        setSalesReturns(prev => fixReturnDate(prev, row.returnId, row.parentSaleDate));
        showToast('Return re-dated to its sale date');
      },
      { tone: 'warn', title: 'Fix impossible return date' }
    );
  };

  // ── Correction 2: preview + approve backfill of ONLY zero-cost sale lines ────
  // Corrects zero_cost_snapshot lines to the replay WAC; WAC-variation lines are
  // never touched. Originals retained on each line for audit.
  const handleBackfillZeroCost = (rep) => {
    const zero = (rep && rep.cost ? rep.cost : []).filter(r => r.reason === 'zero_cost_snapshot');
    if (zero.length === 0) { showAlert('No zero-cost sale lines to correct.', { title: 'Nothing to backfill' }); return; }
    const preview = zero.slice(0, 12).map(r => `• ${r.product}${r.invoice ? ` (${r.invoice})` : ''}: ${r.quantity} × ${fmt(r.replayUnitCost)} = ${fmt(r.replayTotalCost)}`).join('\n');
    const total = zero.reduce((s, r) => s + parseFloat(r.replayTotalCost || 0), 0);
    showConfirm(
      `Backfill ${zero.length} zero-cost sale line(s) to the replay cost?\n\nThese sales were recorded with cost 0 despite a known cost basis. Each line's cost becomes its as-of-date WAC; the original 0 is retained (originalRecordedUnitCost) with a correction reason. WAC-variation lines are NOT changed.\n\n${preview}\n\nTotal COGS added: ${fmt(total)}\n\nTip: fix any impossible-date return FIRST so the reconciliation is clean.`,
      () => {
        setSales(prev => backfillZeroCostLines(prev, zero));
        showToast(`${zero.length} zero-cost line(s) corrected to replay cost`);
      },
      { tone: 'warn', title: 'Backfill zero-cost lines' }
    );
  };

  // Per-product transaction timeline (opened from an exceptions row) -read-only.
  const [timelineProduct, setTimelineProduct] = useState(null);

  // Dev conflict log: re-render the conflict card when a same-record conflict is
  // detected at sync time (behind the faida-conflict-check flag).
  const [conflictTick, setConflictTick] = useState(0);
  useEffect(() => {
    const h = () => setConflictTick(t => t + 1);
    window.addEventListener('faida:conflict', h);
    return () => window.removeEventListener('faida:conflict', h);
  }, []);

  // ── Human labels for a ledger row (shared by the wallet drilldown and the
  // wallet report so both describe movements the same way). ────────────────
  const ledgerRowLabel = (r) => {
    const sw = language === 'sw';
    if (r.source === 'opening') return sw ? 'Salio la awali' : 'Opening balance';
    if (r.source === 'sale') { const s = (sales || []).find(x => String(x.id) === String(r.refId)); return (sw ? 'Mauzo' : 'Sale') + (s?.customerName ? ': ' + s.customerName : (s?.invoiceNumber ? ': ' + s.invoiceNumber : '')); }
    if (r.source === 'manualInvoice') return sw ? 'Malipo ya ankara' : 'Invoice payment';
    if (r.source === 'service') return sw ? 'Huduma' : 'Service';
    if (r.source === 'purchase') { const p = (purchases || []).find(x => String(x.id) === String(r.refId)); return (sw ? 'Ununuzi' : 'Purchase') + (p?.supplier ? ': ' + p.supplier : ''); }
    if (r.source === 'expense') { const e = (expenses || []).find(x => String(x.id) === String(r.refId)); return (sw ? 'Matumizi' : 'Expense') + (e?.category ? ': ' + e.category : ''); }
    if (r.source === 'salesReturn') return sw ? 'Marejesho ya mteja' : 'Customer refund';
    if (r.source === 'purchaseReturn') return sw ? 'Marejesho ya muuzaji' : 'Supplier refund';
    if (r.source === 'manual') { const m = (manualTransactions || []).find(x => String(x.id) === String(r.refId)); return m?.description || (sw ? 'Marekebisho' : 'Adjustment'); }
    return r.source || '—';
  };
  const ledgerRowRef = (r) => {
    if (r.source === 'sale') { const s = (sales || []).find(x => String(x.id) === String(r.refId)); return s?.invoiceNumber || '—'; }
    if (r.source === 'manualInvoice') { const m = (manualInvoices || []).find(x => String(x.id) === String(r.refId)); return m?.invoiceNumber || '—'; }
    if (r.source === 'service') { const s = (serviceTransactions || []).find(x => String(x.id) === String(r.refId)); return s?.invoiceNumber || '—'; }
    if (r.source === 'purchase') { const p = (purchases || []).find(x => String(x.id) === String(r.refId)); return p?.invoiceNumber || p?.efdReceiptNo || '—'; }
    if (r.source === 'salesReturn') { const rr = (salesReturns || []).find(x => String(x.id) === String(r.refId)); return rr?.invoiceNumber || '—'; }
    return '—';
  };

  const [withdrawalFormData, setWithdrawalFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    type: 'debit',
    amount: '',
    description: ''
  });

  const handleSalesInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'quantity' && salesFormData.productName) {
      const prod = (products || []).find(p => p.productName === salesFormData.productName);
      const stock = prod ? (prod.balance ?? prod.currentStock ?? prod.openingStockQty ?? 0) : Infinity;
      const numVal = parseInt(value) || 0;
      const capped = Math.min(Math.max(0, numVal), stock);
      setSalesFormData(prev => ({ ...prev, quantity: value === '' ? '' : String(capped) }));
      return;
    }
    setSalesFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleExpenseInputChange = (e) => {
    const { name, value } = e.target;
    setExpenseFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleExpensePaymentTypeChange = (idx, value) => {
    // value may be a composite ('bank|abc123') for a specific registered account,
    // or a plain base ('cash'/'credit'). Store both fields.
    setExpensePayments(prev => prev.map((p, i) => i === idx ? { ...p, type: baseType(value), accountKey: value } : p));
  };
  const handleExpensePaymentAmountChange = (idx, amount) => {
    setExpensePayments(prev => prev.map((p, i) => i === idx ? { ...p, amount } : p));
  };
  const addExpensePayment = () => {
    const opts = buildPaymentOptions(true);
    if ((expensePayments || []).length >= opts.length) return;
    const used = new Set((expensePayments || []).map(p => p.accountKey || p.type));
    const next = opts.find(o => !used.has(o.value)) || opts[0];
    setExpensePayments(prev => [...prev, { type: next.type, accountKey: next.value, amount: '' }]);
  };
  const removeExpensePayment = (idx) => {
    if ((expensePayments || []).length <= 1) return;
    setExpensePayments(prev => prev.filter((_, i) => i !== idx));
  };

  // Tab press: if already on that tab, reset to its main screen;
  // otherwise just switch to the tab.
  // Track last tab tap for double-click detection (350ms window)
  const lastTabTapRef = useRef({ key: null, time: 0 });

  // Reset all sub-views / drilldowns / modals for a given tab so the user
  // lands on that tab's main "home" screen. Called for same-tab tap or
  // double-tap on any tab.
  const resetTabToHome = (tabKey) => {
    switch (tabKey) {
      case 'dashboard':
        setActiveDrilldown(null);
        setShowWithdrawalForm(false);
        setShowDebtPaymentForm(false);
        setShowLoanPaymentForm(false);
        setDashboardTimeFilter('month');
        setDashboardCustomFrom('');
        setDashboardCustomTo('');
        break;
      case 'sales':
        setShowSalesForm(false);
        setIsSalesReportOpen(false);
        setEditSaleId(null);
        setShowInvoicePreview(false);
        setShowProformaForm(false);
        setShowInvoiceManagement(false);
        setInvoiceManagementSection(null);
        setShowIssuedListScreen(false);
        setShowManualInvoiceListScreen(false);
        break;
      case 'purchases':
        setShowPurchaseForm(false);
        setIsPurchasesReportOpen(false);
        setEditPurchaseId(null);
        setShowPurchaseReturnModal(false);
        break;
      case 'expenses':
        setShowExpenseForm(false);
        setIsExpensesReportOpen(false);
        setEditExpenseId(null);
        setShowRecurringModal(false);
        break;
      case 'inventory':
        setActiveDrilldown(null);
        setStockHistoryProduct(null);
        setShowStockReport(false);
        setShowStockAdjModal(false);
        break;
      case 'services':
        setShowServiceTransactionForm(false);
        setShowServiceReports(false);
        setServiceTransactionEditId(null);
        break;
      case 'reports':
        setShowCashflowReport(false);
        setShowStockReport(false);
        break;
      default:
        break;
    }
    // Universal closers -apply for any tab
    setViewTxnModal({ open: false, kind: null, record: null });
    setExpenseHistoryCategory(null);
    setServiceHistoryName(null);
  };

  const handleTabPress = (tabKey) => {
    const now = Date.now();
    const last = lastTabTapRef.current;
    const isDoubleTap = last.key === tabKey && (now - last.time) < 350;
    lastTabTapRef.current = { key: tabKey, time: now };

    if (activeTab === tabKey || isDoubleTap) {
      // Same tab tapped (or double-tapped anywhere) -reset back to its home screen
      resetTabToHome(tabKey);
      if (activeTab !== tabKey) setActiveTab(tabKey);
      return;
    }
    setActiveTab(tabKey);
  };

  const handlePurchaseInputChange = (e) => {
    const { name, value } = e.target;
    setPurchaseFormData(prev => {
      const updated = { ...prev, [name]: value };
      if (name === 'quantity' || name === 'unitPrice') {
        const qty = parseFloat(name === 'quantity' ? value : prev.quantity) || 0;
        const up = parseFloat(name === 'unitPrice' ? value : prev.unitPrice) || 0;
        if (qty > 0 && up > 0) updated.totalCost = (qty * up).toFixed(2);
      }
      return updated;
    });
  };

  const addPurchaseTax = () => {
    setPurchaseFormData(prev => ({ ...prev, taxes: [...(prev.taxes || []), { type: 'vat', rate: defaultVatRate }] }));
  };
  const removePurchaseTax = (index) => {
    setPurchaseFormData(prev => ({ ...prev, taxes: (prev.taxes || []).filter((_, i) => i !== index) }));
  };
  const updatePurchaseTax = (index, field, value) => {
    setPurchaseFormData(prev => {
      const taxes = (prev.taxes || []).map((t, i) => i === index ? { ...t, [field]: value } : t);
      return { ...prev, taxes };
    });
  };

  const getPurchaseTotalForPayments = () => {
    // Multi-item: sum all line items first
    const cartBase = (purchaseLineItems || []).reduce((s, item) => s + parseFloat(item.totalCost || 0), 0);
    // Include any draft row that's filled in but not yet added to cart
    const d = purchaseLineDraft;
    const draftBase = (d && d.productName && d.quantity && d.totalCost) ? parseFloat(d.totalCost) : 0;
    // Legacy single-product fallback (kept for old edit flows)
    const qty = parseFloat(purchaseFormData.quantity) || 0;
    const up = parseFloat(purchaseFormData.unitPrice) || 0;
    const legacyBase = (cartBase === 0 && draftBase === 0)
      ? (purchaseFormData.totalCost ? parseFloat(purchaseFormData.totalCost) : (qty * up))
      : 0;
    const base = cartBase + draftBase + legacyBase;
    const discPct = parseFloat(purchaseFormData.discount || 0);
    const afterDisc = Math.max(0, base - base * (discPct / 100));
    const taxExtra = (purchaseFormData.taxes || []).reduce((sum, tax) => {
      return sum + (afterDisc * (parseFloat(tax.rate || 0) / 100));
    }, 0);
    return afterDisc + taxExtra;
  };

  const handlePurchasePaymentTypeChange = (idx, value) => {
    setPurchasePayments(prev => prev.map((p, i) => i === idx ? { ...p, type: baseType(value), accountKey: value } : p));
  };
  const handlePurchasePaymentAmountChange = (idx, amount) => {
    setPurchasePayments(prev => prev.map((p, i) => i === idx ? { ...p, amount } : p));
  };
  const addPurchasePayment = () => {
    const opts = buildPaymentOptions(true);
    if ((purchasePayments || []).length >= opts.length) return;
    const used = new Set((purchasePayments || []).map(p => p.accountKey || p.type));
    const next = opts.find(o => !used.has(o.value)) || opts[0];
    setPurchasePayments(prev => [...prev, { type: next.type, accountKey: next.value, amount: '' }]);
  };
  const removePurchasePayment = (idx) => {
    if ((purchasePayments || []).length <= 1) return;
    setPurchasePayments(prev => prev.filter((_, i) => i !== idx));
  };

  const getReportFilteredPurchases = () => {
    let base;
    if (!reportDateFilters.fromDate && !reportDateFilters.toDate) {
      const currentYear = new Date().getFullYear();
      const fromDate = `${currentYear}-01-01`;
      const toDate = `${currentYear}-12-31`;
      base = (purchases || []).filter(purchase => {
        const purchaseDate = new Date(purchase.date);
        return purchaseDate >= new Date(fromDate) && purchaseDate <= new Date(toDate);
      });
    } else {
      base = (purchases || []).filter(purchase => {
        const purchaseDate = new Date(purchase.date);
        const fromDate = reportDateFilters.fromDate ? new Date(reportDateFilters.fromDate) : new Date('1900-01-01');
        const toDate = reportDateFilters.toDate ? new Date(reportDateFilters.toDate) : new Date('2100-12-31');
        return purchaseDate >= fromDate && purchaseDate <= toDate;
      });
    }
    if (purchasesReportProduct && purchasesReportProduct !== 'all') {
      base = base.filter(p => p.productName === purchasesReportProduct);
    }
    return base;
  };

  const resetPurchaseForm = () => {
    setPurchaseFormData({ productName: '', quantity: '', unitPrice: '', totalCost: '', supplier: '', date: new Date().toISOString().split('T')[0], taxes: [], discount: '', efdReceiptNo: '' });
    setPurchaseSupplierType('registered');
    setPurchasePaymentMethod('cash');
    setPurchasePayments([{ type: 'cash', amount: '' }]);
    setPurchaseLineItems([]);
    setPurchaseLineDraft({ productName: '', quantity: '', unitPrice: '', totalCost: '' });
  };

  const handleSavePurchase = () => {
    // SAFETY: if the user has filled in the draft row but hasn't clicked "Add to order",
    // auto-add it before saving so the line isn't silently lost.
    let workingLineItems = purchaseLineItems;
    const d = purchaseLineDraft;
    if (d && d.productName && d.quantity && d.totalCost) {
      workingLineItems = [...purchaseLineItems, { ...d, id: Date.now() }];
      setPurchaseLineItems(workingLineItems);
      setPurchaseLineDraft({ productName: '', quantity: '', unitPrice: '', totalCost: '' });
    }
    if (workingLineItems.length === 0) return;
    const supplierValue = (purchaseFormData.supplier || '').trim();
    const validPayments = (purchasePayments || []).filter(p => p.amount && parseFloat(p.amount) > 0);
    const subTotalAll = workingLineItems.reduce((s, item) => s + parseFloat(item.totalCost || 0), 0);
    const discPctP = parseFloat(purchaseFormData.discount || 0);
    const discountAmountP = subTotalAll * (discPctP / 100);
    const afterDiscountP = Math.max(0, subTotalAll - discountAmountP);
    const taxAmount = (purchaseFormData.taxes || []).reduce((s, tax) => s + afterDiscountP * (parseFloat(tax.rate || 0) / 100), 0);
    const totalCostAll = afterDiscountP + taxAmount; // grand total incl. discount + taxes
    // Credit doesn't represent real money paid -it's a deferred obligation.
    // Only actual cash/bank/mobilemoney rows count toward "paid".
    const paidPayments = validPayments.filter(p => p.type !== 'credit');
    const creditPayments = validPayments.filter(p => p.type === 'credit');
    const paymentsTotal = paidPayments.reduce((s, p) => s + parseFloat(p.amount), 0);
    const creditTotal = creditPayments.reduce((s, p) => s + parseFloat(p.amount), 0);
    const coverageTotal = paymentsTotal + creditTotal; // money paid + amount on credit

    // ── Payment coverage check ──
    // Total must be fully covered by payments + credit. Refuse to save when
    // there's still a gap.
    if (Math.round(coverageTotal * 100) < Math.round(totalCostAll * 100)) {
      const remaining = totalCostAll - coverageTotal;
      const sw = language === 'sw';
      showAlert(sw
        ? `Malipo hayajakamilika.\nJumla: ${fmt(totalCostAll)}\nImefunikwa: ${fmt(coverageTotal)}\nBakaza: ${fmt(remaining)}\n\nWeka kiasi kilichobaki kama Mkopo (Credit) au ongeza njia nyingine ya malipo.`
        : `Payment is incomplete.\nTotal: ${fmt(totalCostAll)}\nCovered: ${fmt(coverageTotal)}\nRemaining: ${fmt(remaining)}\n\nPut the remainder on Credit (Loan) or add another payment method.`,
        { tone: 'warn', title: sw ? 'Malipo hayajakamilika' : 'Payment incomplete' });
      return;
    }

    // Fully paid only when actual paid methods cover the total
    // (credit alone does NOT count as paid).
    const isPaidFull = Math.round(paymentsTotal * 100) >= Math.round(totalCostAll * 100);
    const primaryMethod = purchasePayments[0]?.type || 'cash';

    // ── HARD BALANCE CHECK: cannot spend more than the wallet has ─────
    // Sums every non-credit payment row per wallet and refuses to save when
    // any wallet would go negative. Strict -also applies to edits (we offset
    // by the editing purchase's prior contribution so the user can stay
    // within available funds).
    {
      const sw = language === 'sw';
      // Group required spend per accountKey
      const spendPerAcct = {};
      validPayments.forEach(p => {
        if (p.type === 'credit') return;
        const key = p.accountKey || p.type || 'cash';
        spendPerAcct[key] = (spendPerAcct[key] || 0) + parseFloat(p.amount || 0);
      });
      // If editing, add back what the OLD purchase took from each wallet
      // (so the user's available balance properly reflects them adjusting the
      // same record rather than spending on top of their own prior spend).
      if (editPurchaseId) {
        const oldPurchase = (purchases || []).find(p => p.id === editPurchaseId);
        const oldPmts = oldPurchase && Array.isArray(oldPurchase.payments) ? oldPurchase.payments : [];
        oldPmts.forEach(p => {
          if (p.type === 'credit') return;
          const key = p.accountKey || p.type || 'cash';
          spendPerAcct[key] = (spendPerAcct[key] || 0) - parseFloat(p.amount || 0);
        });
      }
      for (const [key, needed] of Object.entries(spendPerAcct)) {
        if (needed <= 0) continue;
        const available = getWalletBalance(key);
        if (available < needed) {
          // Resolve a friendly label for the wallet
          let label = key;
          if (key === 'cash') label = sw ? 'Taslimu' : 'Cash';
          else {
            const acc = (paymentAccounts || []).find(a => `${a.type}|${a.id}` === key);
            if (acc) label = `${acc.bankName || acc.networkName || 'Account'} · ${acc.accountName || ''}`;
            else if (key === 'bank') label = sw ? 'Benki' : 'Bank';
            else if (key === 'mobilemoney') label = sw ? 'Pesa ya Simu' : 'Mobile Money';
          }
          showAlert(sw
            ? `Salio halitoshi kwa ${label}.\nKilichopo: ${fmt(available)}\nKinachohitajika: ${fmt(needed)}\nUpungufu: ${fmt(needed - available)}\n\nPunguza kiasi, badili akaunti, au weka kiwango kilichobaki kama Mkopo.`
            : `Insufficient balance in ${label}.\nAvailable: ${fmt(available)}\nRequired: ${fmt(needed)}\nShortfall: ${fmt(needed - available)}\n\nReduce the amount, switch accounts, or put the shortfall on Credit.`,
            { tone: 'error', title: sw ? 'Salio halitoshi' : 'Insufficient balance' });
          return;
        }
      }
    }

    const firstItem = workingLineItems[0];
    const updatedPurchase = {
      updatedAt: new Date().toISOString(), // record-level conflict resolution
      // backward-compat flat fields from first item
      productName: firstItem.productName,
      quantity: firstItem.quantity,
      unitPrice: firstItem.unitPrice,
      totalCost: String(totalCostAll),
      // multi-item array
      items: workingLineItems,
      supplier: supplierValue || '',
      supplierType: purchaseSupplierType,
      paymentMethod: primaryMethod,
      payments: validPayments.length > 0 ? validPayments : undefined,
      isPaid: isPaidFull,
      date: purchaseFormData.date || new Date().toISOString().split('T')[0],
      taxes: purchaseFormData.taxes || [],
      discount: purchaseFormData.discount || '',
      discountAmount: discountAmountP,
      subTotal: subTotalAll,
      efdReceiptNo: purchaseFormData.efdReceiptNo || '',
    };

    // Stock update helper for purchases
    // Name matching is forgiving: case + whitespace-insensitive so "InfinixGT 50",
    // "Infinix GT 50", "infinix gt50" all match the same product record.
    const normName = (s) => (s || '').trim().toLowerCase().replace(/\s+/g, ' ');
    const applyPurchaseStock = (prevProducts, itemsToApply, sign = 1, oldItems = []) => {
      let updated = [...prevProducts];
      // Reverse old stock first (for edits)
      oldItems.forEach(oldItem => {
        const oldQty = parseFloat(oldItem.quantity || 0);
        const oldKey = normName(oldItem.productName);
        updated = updated.map(p => {
          if (normName(p.productName) === oldKey) {
            const nb = Math.max(0, (p.balance ?? p.currentStock ?? p.openingStockQty ?? 0) - oldQty);
            return { ...p, stockIn: Math.max(0, (p.stockIn ?? 0) - oldQty), balance: nb, currentStock: nb };
          }
          return p;
        });
      });
      // Apply new
      itemsToApply.forEach(item => {
        const qty = parseFloat(item.quantity || 0);
        const cp = item.unitPrice ? parseFloat(item.unitPrice).toFixed(2) : (qty > 0 ? (parseFloat(item.totalCost || 0) / qty).toFixed(2) : '0.00');
        const itemKey = normName(item.productName);
        let matched = false;
        updated = updated.map(p => {
          if (normName(p.productName) === itemKey) {
            matched = true;
            const nb = (p.balance ?? p.currentStock ?? p.openingStockQty ?? 0) + sign * qty;
            return { ...p, stockIn: Math.max(0, (p.stockIn ?? 0) + sign * qty), balance: Math.max(0, nb), currentStock: Math.max(0, nb), costPrice: cp };
          }
          return p;
        });
        // SAFETY NET: if the purchase references a product not yet in the catalog
        // (typed manually, imported from old data, etc.), auto-register it so the
        // purchase doesn't silently fail to update stock.
        if (!matched && itemKey) {
          updated.push({
            id: Date.now() + Math.random(),
            productName: item.productName,
            category: '',
            unit: 'piece',
            balance: Math.max(0, sign * qty),
            currentStock: Math.max(0, sign * qty),
            stockIn: Math.max(0, sign * qty),
            costPrice: cp,
            sellingPrice: '0',
            minStockAlert: 0,
            openingStockQty: 0,
            openingStockValue: 0,
          });
        }
      });
      return updated;
    };

    const wasEdit = !!editPurchaseId;
    if (editPurchaseId) {
      const old = purchases.find(p => p.id === editPurchaseId);
      const oldItems = old?.items || (old?.productName ? [{ productName: old.productName, quantity: old.quantity }] : []);
      setPurchases(prev => (prev || []).map(p => p.id === editPurchaseId ? { ...updatedPurchase, id: editPurchaseId } : p));
      setProducts(prev => applyPurchaseStock(prev, workingLineItems, 1, oldItems));
      // NOTE: No RPC for editing posted purchases (only update_purchase_draft exists).
      // During transition, local state is authoritative for edits. Post-transition:
      // the app will use void→recreate or a dedicated update-posted command.
      setEditPurchaseId(null);
    } else {
      const newPurchase = { ...updatedPurchase, id: Date.now() };
      setPurchases(prev => [...prev, newPurchase]);
      setProducts(prev => applyPurchaseStock(prev, workingLineItems));
      // MIGRATION-ONLY dual-write: persist to relational DB
      if (businessId && currentUser?.accessToken) {
        persistPurchase(businessId, newPurchase, currentUser.accessToken, paymentAccounts).then(r => {
          const rid = r?.data?.purchase_id;
          if (rid) setPurchases(prev => prev.map(p => p.id === newPurchase.id ? { ...p, _rid: rid, _rv: 1 } : p));
        }).catch((err) =>
          console.warn('[MIGRATION-ONLY] purchase persist failed (non-blocking):', err.message));
      }
    }
    resetPurchaseForm();
    setShowPurchaseForm(false);
    const sw = language === 'sw';
    showToast(wasEdit
      ? (sw ? 'Manunuzi yamehaririwa' : 'Purchase updated successfully')
      : (sw ? 'Manunuzi yamehifadhiwa' : 'Purchase saved successfully'));
  };

  const resetSaleForm = () => {
    setSalesFormData({ productName: '', quantity: '', sellingPrice: '', cost: '', customerName: '', customerPhone: '', customerEmail: '', customerAddress: '', customerTIN: '', customerVRN: '', date: new Date().toISOString().split('T')[0], taxes: [], discount: '', efdReceiptNo: '' });
    setSalesCustomerType('walkin');
    setSalePayments([{ type: 'cash', amount: '' }]);
    setSaleSaveMode('receipt');
    setSaleFromInvoiceManagement(false);
    setSaleInvoiceNotes('');
    setSaleFooterSections([{ label: 'Terms & Conditions', content: '' }]);
    setSaleLineItems([]);
    setSaleLineDraft({ type: 'product', productName: '', quantity: '', sellingPrice: '', cost: '' });
    setSalesIssueInvoice(true);
    salesIssueInvoiceTouched.current = false;
  };

  // Auto-default the "issue invoice" toggle based on whether the user has
  // entered a customer name. Only nudges while the user hasn't manually
  // toggled the checkbox (then we respect their choice).
  useEffect(() => {
    if (!showSalesForm) return;
    if (salesIssueInvoiceTouched.current) return;
    const hasCustomer = (salesFormData.customerName || '').trim().length > 0;
    setSalesIssueInvoice(hasCustomer);
  }, [showSalesForm, salesFormData.customerName]);

  // Same auto-default for the service form
  useEffect(() => {
    if (!showServiceTransactionForm) return;
    if (serviceIssueInvoiceTouched.current) return;
    const hasCustomer = (serviceTransactionFormData.customerName || '').trim().length > 0;
    setServiceIssueInvoice(hasCustomer);
  }, [showServiceTransactionForm, serviceTransactionFormData.customerName]);

  // One-shot flag + reason: set only when the operator explicitly confirms booking
  // a costed product at zero cost (with a reason), so the re-entered save skips the
  // guard once and stamps the reason. Paired with the zeroCostPrompt reason modal.
  const zeroCostOverrideRef = React.useRef(false);
  const zeroCostReasonRef = React.useRef(null);
  const zeroCostReasonTextRef = React.useRef(null);
  const [zeroCostPrompt, setZeroCostPrompt] = useState(null);
  const handleSaveSale = () => {
    // SAFETY: if the user has filled in the draft row but hasn't clicked "Add to order",
    // auto-add it before saving so the line isn't silently lost.
    let workingSaleItems = saleLineItems;
    const sd = saleLineDraft;
    if (sd && sd.productName && sd.quantity && sd.sellingPrice) {
      workingSaleItems = [...saleLineItems, { ...sd, id: Date.now() }];
      setSaleLineItems(workingSaleItems);
      setSaleLineDraft({ type: 'product', productName: '', quantity: '', sellingPrice: '', cost: '' });
    }
    // Must have at least one line item
    if (workingSaleItems.length === 0) return;

    // ── Block overselling -stock may NEVER go negative ──────────────────
    // Applies to the paths that actually deduct stock (record sale + edit).
    // Manual invoices are documents and don't deduct stock, so they're exempt.
    if (!saleFromInvoiceManagement) {
      const normN = (n) => String(n || '').trim().toLowerCase();
      // On an edit, the old sale's quantities are already deducted from stock,
      // so add them back when working out what's available now.
      const oldSaleForStock = editSaleId ? (sales || []).find(s => s.id === editSaleId) : null;
      const oldItemsForStock = oldSaleForStock
        ? (Array.isArray(oldSaleForStock.items) && oldSaleForStock.items.length > 0
            ? oldSaleForStock.items
            : [{ productName: oldSaleForStock.productName, quantity: oldSaleForStock.quantity, type: oldSaleForStock.isService ? 'service' : 'product' }])
        : [];
      // C2/H6: resolve each requested product by stable productId (lineMatchesProduct
      // falls back to a normalized name for legacy lines), so a rename or a
      // duplicate name can't misroute the oversell check.
      const swS = language === 'sw';
      for (const prod of (products || [])) {
        const requested = workingSaleItems.reduce((s, it) => (it.type !== 'service' && lineMatchesProduct(it, prod)) ? s + parseFloat(it.quantity || 0) : s, 0);
        if (requested <= 0) continue; // this product isn't in the order
        const oldQty = oldItemsForStock.reduce((s, it) => (it.type !== 'service' && lineMatchesProduct(it, prod)) ? s + parseFloat(it.quantity || 0) : s, 0);
        const available = (prod.balance ?? prod.currentStock ?? prod.openingStockQty ?? 0) + oldQty;
        if (requested > available + 0.0001) {
          showAlert(
            swS
              ? `Hisa haitoshi kwa "${prod.productName}".\nInapatikana: ${available}\nUnauza: ${requested}\n\nPunguza idadi au ongeza hisa kwanza.`
              : `Not enough stock for "${prod.productName}".\nAvailable: ${available}\nSelling: ${requested}\n\nReduce the quantity or add stock first.`,
            { tone: 'error', title: swS ? 'Hisa haitoshi' : 'Insufficient stock' }
          );
          return;
        }
      }
    }

    // ── Zero-cost booking guard (H4 integrity) ──────────────────────────────
    // A non-service line saved with cost 0 while the product HAS a known cost
    // basis (perpetual WAC / costPrice / opening value) produces a false 100%
    // margin and silently corrupts COGS -exactly how the historical gap arose.
    // We DON'T hard-block (a costed product can legitimately be given away as a
    // sample/donation): we require an EXPLICIT override confirming zero COGS.
    // Products with no cost basis (genuinely free / zero-valued) pass silently.
    if (!saleFromInvoiceManagement && !zeroCostOverrideRef.current) {
      const swZ = language === 'sw';
      const saleDateZ = salesFormData.date || new Date().toISOString().split('T')[0];
      const bad = [];
      for (const it of workingSaleItems) {
        if (it.type === 'service') continue;
        if (parseFloat(it.quantity || 0) <= 0) continue;
        if (parseFloat(it.cost || 0) > 0) continue; // cost already set -fine
        const prod = (products || []).find(p => lineMatchesProduct(it, p));
        const openQ = prod ? parseFloat(prod.openingStockQty || 0) : 0;
        const basis = prod
          ? (getProductWAC(prod.productName, saleDateZ)
             || parseFloat(prod.costPrice || 0)
             || (openQ > 0 ? parseFloat(prod.openingStockValue || 0) / openQ : 0))
          : 0;
        if (basis > 0) bad.push({ name: it.productName || prod?.productName, basis });
      }
      if (bad.length > 0) {
        // Require an explicit REASON for a legitimate zero-cost sale (sample,
        // give-away, etc.), not just a yes/no -so intentional zero-cost is
        // distinguishable from a missing-cost error later. The reason picker
        // re-invokes save with the override + reason set.
        setZeroCostPrompt({ name: bad[0].name, basis: bad[0].basis, count: bad.length });
        return;
      }
    }
    const zeroCostOverridden = zeroCostOverrideRef.current;
    zeroCostOverrideRef.current = false; // consume the one-shot override
    const zeroCostReason = zeroCostReasonRef.current;
    zeroCostReasonRef.current = null;
    const zeroCostReasonText = zeroCostReasonTextRef.current;
    zeroCostReasonTextRef.current = null;

    // Order-level totals (sum across all items) -pure, unit-tested math
    // (see moneyMath.js + moneyMath.test.js).
    const saleTotals = computeSaleTotals(workingSaleItems, salesFormData.discount, salesFormData.taxes);
    const { baseRevenue, discountAmount, afterDiscount, taxTotal, totalRevenue, profit } = saleTotals;
    const totalCostAll = saleTotals.totalCost;

    const validPayments = (salePayments || []).filter(p => p.amount && parseFloat(p.amount) > 0);
    // Credit (customer owes you) is NOT money received -only cash/bank/mobile count as paid.
    const paidPayments = validPayments.filter(p => p.type !== 'credit');
    const creditPayments = validPayments.filter(p => p.type === 'credit');
    const paymentsTotal = paidPayments.reduce((s, p) => s + parseFloat(p.amount), 0);
    const creditTotal = creditPayments.reduce((s, p) => s + parseFloat(p.amount), 0);
    const coverageTotal = paymentsTotal + creditTotal;

    // ── Payment coverage check (skip when saving as invoice -invoice is deferred) ──
    if (saleSaveMode !== 'invoice') {
      if (Math.round(coverageTotal * 100) < Math.round(totalRevenue * 100)) {
        const remaining = totalRevenue - coverageTotal;
        const sw = language === 'sw';
        showAlert(sw
          ? `Malipo hayajakamilika.\nJumla: ${fmt(totalRevenue)}\nImefunikwa: ${fmt(coverageTotal)}\nBakaza: ${fmt(remaining)}\n\nWeka kiasi kilichobaki kama Mkopo (Credit) au ongeza njia nyingine ya malipo.`
          : `Payment is incomplete.\nTotal: ${fmt(totalRevenue)}\nCovered: ${fmt(coverageTotal)}\nRemaining: ${fmt(remaining)}\n\nPut the remainder on Credit (Loan) or add another payment method.`,
          { tone: 'warn', title: sw ? 'Malipo hayajakamilika' : 'Payment incomplete' });
        return;
      }
      // Over-coverage: payments sum higher than the new total (typical after edit)
      if (Math.round(coverageTotal * 100) > Math.round(totalRevenue * 100)) {
        const excess = coverageTotal - totalRevenue;
        const sw = language === 'sw';
        showAlert(sw
          ? `Malipo yamezidi jumla.\nJumla mpya: ${fmt(totalRevenue)}\nMalipo: ${fmt(coverageTotal)}\nZiada: ${fmt(excess)}\n\nPunguza Mkopo kwanza, kisha akaunti zingine (pesa/benki/simu), ili malipo yalingane na jumla mpya.`
          : `Payments exceed the total.\nNew total: ${fmt(totalRevenue)}\nPayments cover: ${fmt(coverageTotal)}\nExcess: ${fmt(excess)}\n\nReduce Credit first, then cash/bank/mobile, so the payment lines match the new total.`,
          { tone: 'warn', title: sw ? 'Malipo yamezidi' : 'Payments exceed total' });
        return;
      }
    }

    // Sale is paid only when actual cash/bank/mobile cover the total.
    // Credit-only sales remain Unpaid (customer owes you).
    const isPaid = saleSaveMode === 'invoice'
      ? false
      : Math.round(paymentsTotal * 100) >= Math.round(totalRevenue * 100);
    const primaryPaymentMethod = salePayments[0]?.type || 'cash';
    // For walk-in sales, leave customerName empty so invoices/receipts don't print "Walk-in"
    // (best practice -shops don't put "Walk-in" on a printed invoice). Reports still fall
    // back to "Walk-in" at display time when customerName is empty.
    const resolvedCustomer = salesCustomerType === 'registered'
      ? salesFormData.customerName
      : (salesFormData.customerName || '');

    // First item used as primary for backward-compat fields
    const firstItem = workingSaleItems[0];
    const hasService = workingSaleItems.some(i => i.type === 'service');
    const isService = workingSaleItems.length === 1 && hasService;

    const updatedSaleData = {
      // backward-compat single-item fields (first item)
      productName: firstItem.productName,
      quantity: firstItem.type === 'service' ? '1' : firstItem.quantity,
      sellingPrice: firstItem.sellingPrice,
      cost: firstItem.cost || '0',
      // multi-item array
      items: workingSaleItems,
      totalRevenue, totalCost: totalCostAll, profit,
      ...(zeroCostOverridden ? {
        zeroCostOverride: true,
        zeroCostOverrideReason: zeroCostReason || 'other',
        ...(zeroCostReasonText ? { zeroCostOverrideReasonText: zeroCostReasonText } : {}),
        zeroCostOverrideAt: new Date().toISOString(),
        zeroCostOverrideBy: (currentUser && (currentUser.email || currentUser.uid)) || null,
      } : {}),
      updatedAt: new Date().toISOString(), // record-level conflict resolution
      customerName: resolvedCustomer,
      customerType: salesCustomerType,
      customerDetails: (() => {
        if (salesCustomerType === 'registered') {
          const c = (customers || []).find(x => x.name === resolvedCustomer);
          if (c) return { phone: c.phone || '', email: c.email || '', address: c.address || '', tin: c.tin || '', vrn: c.vrn || '' };
        }
        // Walk-in mode -pull whatever the user typed into the form
        return {
          phone: salesFormData.customerPhone || '',
          email: salesFormData.customerEmail || '',
          address: salesFormData.customerAddress || '',
          tin: salesFormData.customerTIN || '',
          vrn: salesFormData.customerVRN || '',
        };
      })(),
      paymentMethod: primaryPaymentMethod,
      payments: validPayments.length > 0 ? validPayments : undefined,
      isPaid,
      saveMode: saleSaveMode,
      invoiceNotes: saleInvoiceNotes.trim() || '',
      footerSections: saleFooterSections.filter(s => s.content && s.content.trim()),
      isService,
      date: salesFormData.date || new Date().toISOString().split('T')[0],
      taxes: salesFormData.taxes,
      discount: salesFormData.discount || '',
      discountAmount,
      efdReceiptNo: salesFormData.efdReceiptNo || '',
    };

    // Stock update helper
    // C2/H6: match by stable productId (lineMatchesProduct falls back to a
    // normalized name for legacy lines). Balances are NOT clamped to zero -a
    // reversal/edit that reveals a negative is preserved (signed) so the
    // discrepancy surfaces in the reconciliation guard instead of being hidden.
    // (Normal oversells are still blocked before save.)
    const applyStockChange = (prevProducts, itemsToApply, sign = -1, oldItems = []) => {
      let updated = [...prevProducts];
      // Restore old stock first (for edits)
      oldItems.forEach(oldItem => {
        if (oldItem.type === 'service') return;
        const oldQty = parseFloat(oldItem.quantity || 0);
        updated = updated.map(p => {
          if (lineMatchesProduct(oldItem, p)) {
            const nb = (p.balance ?? p.currentStock ?? p.openingStockQty ?? 0) + oldQty;
            return { ...p, stockOut: (p.stockOut ?? 0) - oldQty, balance: nb, currentStock: nb };
          }
          return p;
        });
      });
      // Apply new stock changes
      itemsToApply.forEach(item => {
        if (item.type === 'service') return;
        const qty = parseFloat(item.quantity || 0);
        updated = updated.map(p => {
          if (lineMatchesProduct(item, p)) {
            const nb = (p.balance ?? p.currentStock ?? p.openingStockQty ?? 0) + sign * qty;
            return { ...p, stockOut: (p.stockOut ?? 0) + (sign === -1 ? qty : -qty), balance: nb, currentStock: nb };
          }
          return p;
        });
      });
      return updated;
    };

    if (editSaleId) {
      const old = sales.find(s => s.id === editSaleId);
      const oldItems = old?.items || (old?.productName ? [{ type: old.isService ? 'service' : 'product', productName: old.productName, quantity: old.quantity }] : []);
      // If user just flipped "issue invoice" ON for a sale that didn't have
      // one before, assign an INV number now and add to the issued list.
      // Track invoiceIssuedDate as today (sale date stays the original date).
      const oldHadInvoice = old?.invoiceIssued !== false && !!old?.invoiceNumber;
      const wantInvoice = isPaid === false ? true : !!salesIssueInvoice;
      let extraFields = {};
      if (!oldHadInvoice && wantInvoice) {
        const nextCounter = invoiceCounter + 1;
        const invNo = `INV-${String(nextCounter).padStart(4, '0')}`;
        const todayStr = new Date().toISOString().split('T')[0];
        extraFields = { invoiceNumber: invNo, invoiceIssued: true, invoiceIssuedDate: todayStr };
        setInvoiceCounter(nextCounter);
        localStorage.setItem(getUserKey('invoiceCounter'), String(nextCounter));
        const newIssued = {
          id: editSaleId, invoiceNumber: invNo,
          customerName: updatedSaleData.customerName, date: updatedSaleData.date,
          invoiceIssuedDate: todayStr,
          totalRevenue: updatedSaleData.totalRevenue, isPaid: updatedSaleData.isPaid,
          paymentMethod: updatedSaleData.paymentMethod,
          productName: updatedSaleData.productName, quantity: updatedSaleData.quantity,
          sellingPrice: updatedSaleData.sellingPrice, items: updatedSaleData.items,
          createdAt: new Date().toISOString(),
        };
        setIssuedInvoices(prev => [...prev, newIssued]);
      } else if (oldHadInvoice && !wantInvoice) {
        // User flipped invoice OFF -remove from issued list, keep INV num on record
        extraFields = { invoiceIssued: false };
        setIssuedInvoices(prev => (prev || []).filter(i => i.id !== editSaleId));
      } else if (oldHadInvoice) {
        // Keep existing invoice fields, update issued-list row
        extraFields = { invoiceIssued: true, invoiceNumber: old.invoiceNumber, invoiceIssuedDate: old.invoiceIssuedDate || old.date };
        setIssuedInvoices(prev => (prev || []).map(i => i.id === editSaleId
          ? { ...i, customerName: updatedSaleData.customerName, date: updatedSaleData.date, totalRevenue: updatedSaleData.totalRevenue, isPaid: updatedSaleData.isPaid, items: updatedSaleData.items }
          : i));
      }
      const merged = { ...old, ...updatedSaleData, ...extraFields };
      setSales(prev => (prev || []).map(s => s.id === editSaleId ? merged : s));
      setProducts(prev => applyStockChange(prev, workingSaleItems, -1, oldItems));
      // NOTE: No RPC for editing posted sales (only update_sale_draft exists).
      // Local state authoritative during transition.
      setEditSaleId(null);
      resetSaleForm();
      setShowSalesForm(false);
      if (!oldHadInvoice && wantInvoice) {
        setLastSavedSale(merged);
        setShowInvoicePreview(true);
      } else {
        showToast(language === 'sw' ? 'Mauzo yamehaririwa' : 'Sale updated successfully');
      }
    } else if (saleFromInvoiceManagement) {
      // ── MANUAL INVOICE -not a sale yet ──────────────────────────────
      const nextCounter = invoiceCounter + 1;
      const invoiceNumber = `INV-${String(nextCounter).padStart(4, '0')}`;
      const manualId = editingManualInvoiceId || Date.now();
      const manualInvoice = {
        updatedAt: new Date().toISOString(),
        id: manualId,
        invoiceNumber: editingManualInvoiceId
          ? (manualInvoices.find(i => i.id === editingManualInvoiceId)?.invoiceNumber || invoiceNumber)
          : invoiceNumber,
        customerName: updatedSaleData.customerName,
        customerPhone: updatedSaleData.customerPhone || '',
        customerAddress: updatedSaleData.customerAddress || '',
        date: updatedSaleData.date,
        items: updatedSaleData.items,
        totalRevenue: totalRevenue,
        taxes: updatedSaleData.taxes,
        discount: updatedSaleData.discount,
        discountAmount: discountAmount,
        notes: updatedSaleData.invoiceNotes || '',
        footerSections: updatedSaleData.footerSections || [],
        isPaid: false,
        payments: [],
        createdAt: new Date().toISOString(),
        isManualInvoice: true,
      };
      if (editingManualInvoiceId) {
        setManualInvoices(prev => prev.map(i => i.id === editingManualInvoiceId ? manualInvoice : i));
        setEditingManualInvoiceId(null);
      } else {
        setManualInvoices(prev => [...prev, manualInvoice]);
        // MIGRATION-ONLY dual-write: persist invoice draft to relational DB
        persistInvoice(businessId, manualInvoice, currentUser.accessToken).then(r => {
          const rid = r?.data?.invoice_id;
          if (rid) setManualInvoices(prev => prev.map(i => i.id === manualInvoice.id ? { ...i, _rid: rid, _rv: 1 } : i));
        }).catch(err => console.warn('[dual-write] invoice:', err.message));
        setInvoiceCounter(nextCounter);
        localStorage.setItem(getUserKey('invoiceCounter'), String(nextCounter));
      }
      resetSaleForm();
      setShowSalesForm(false);
      setSuccessMessage(`Invoice ${manualInvoice.invoiceNumber} saved - awaiting payment`);
      setTimeout(() => setSuccessMessage(''), 4000);
    } else {
      // ── RECORD SALE ───────────────────────────────────────────────────
      // Unpaid (credit) sales ALWAYS get an invoice -they're proof of debt.
      // Fully-paid sales get an invoice only when the user opts in via the
      // "Issue formal invoice" toggle (default ON for named customers, OFF
      // for blank walk-in cash sales).
      const willIssueInvoice = isPaid === false ? true : !!salesIssueInvoice;
      const todayStr = new Date().toISOString().split('T')[0];
      let newSale;
      if (willIssueInvoice) {
        const nextCounter = invoiceCounter + 1;
        const invoiceNumber = `INV-${String(nextCounter).padStart(4, '0')}`;
        newSale = {
          ...updatedSaleData,
          id: Date.now(),
          invoiceNumber,
          invoiceIssued: true,
          invoiceIssuedDate: updatedSaleData.date || todayStr, // when invoice was first issued
        };
        setSales(prev => [...prev, newSale]);
        setInvoiceCounter(nextCounter);
        localStorage.setItem(getUserKey('invoiceCounter'), String(nextCounter));
        const issuedInvoice = {
          updatedAt: new Date().toISOString(),
          id: newSale.id, invoiceNumber,
          customerName: newSale.customerName, date: newSale.date,
          invoiceIssuedDate: newSale.invoiceIssuedDate,
          totalRevenue: newSale.totalRevenue, isPaid: newSale.isPaid,
          paymentMethod: newSale.paymentMethod,
          productName: newSale.productName, quantity: newSale.quantity,
          sellingPrice: newSale.sellingPrice, items: newSale.items,
          createdAt: new Date().toISOString(),
        };
        setIssuedInvoices(prev => [...prev, issuedInvoice]);
        localStorage.setItem(getUserKey('issuedInvoices'), JSON.stringify([...issuedInvoicesRaw, issuedInvoice]));
      } else {
        // No invoice -just a recorded sale (no INV number, not in issued list)
        newSale = { ...updatedSaleData, id: Date.now(), invoiceIssued: false };
        setSales(prev => [...prev, newSale]);
      }
      setProducts(prev => applyStockChange(prev, workingSaleItems));
      resetSaleForm();
      setShowSalesForm(false);
      // MIGRATION-ONLY dual-write: persist to relational DB
      if (businessId && currentUser?.accessToken) {
        persistSale(businessId, newSale, currentUser.accessToken, paymentAccounts).then(r => {
          const rid = r?.data?.sale_id;
          if (rid) setSales(prev => prev.map(s => s.id === newSale.id ? { ...s, _rid: rid, _rv: 1 } : s));
        }).catch((err) =>
          console.warn('[MIGRATION-ONLY] sale persist failed (non-blocking):', err.message));
      }
      // Preview modal only when there IS an invoice (either unpaid credit, or
      // user opted to issue one for a paid sale). Otherwise quick toast.
      if (newSale.isPaid === false || willIssueInvoice) {
        setLastSavedSale(newSale);
        setShowInvoicePreview(true);
      } else {
        const sw = language === 'sw';
        showToast(sw
          ? `Mauzo yamehifadhiwa ${fmt(newSale.totalRevenue)}`
          : `Sale recorded ${fmt(newSale.totalRevenue)}`);
      }
    }
  };

  const getFilteredSales = () => {
    if (!salesDateFilters.fromDate && !salesDateFilters.toDate) {
      // Default to current year
      const currentYear = new Date().getFullYear();
      const fromDate = `${currentYear}-01-01`;
      const toDate = `${currentYear}-12-31`;
      
      return (sales || []).filter(sale => {
        const saleDate = new Date(sale.date);
        return saleDate >= new Date(fromDate) && saleDate <= new Date(toDate);
      });
    }
    
    return (sales || []).filter(sale => {
      const saleDate = new Date(sale.date);
      const fromDate = salesDateFilters.fromDate ? new Date(salesDateFilters.fromDate) : new Date('1900-01-01');
      const toDate = salesDateFilters.toDate ? new Date(salesDateFilters.toDate) : new Date('2100-12-31');
      return saleDate >= fromDate && saleDate <= toDate;
    });
  };

  const getFilteredExpenses = () => {
    let base;
    if (!expenseStartDate && !expenseEndDate) {
      const currentYear = new Date().getFullYear();
      const fromDate = `${currentYear}-01-01`;
      const toDate = `${currentYear}-12-31`;
      base = (expenses || []).filter(expense => {
        const expenseDate = new Date(expense.date);
        return expenseDate >= new Date(fromDate) && expenseDate <= new Date(toDate);
      });
    } else {
      base = (expenses || []).filter(expense => {
        const expenseDate = new Date(expense.date);
        const fromDate = expenseStartDate ? new Date(expenseStartDate) : new Date('1900-01-01');
        const toDate = expenseEndDate ? new Date(expenseEndDate) : new Date('2100-12-31');
        return expenseDate >= fromDate && expenseDate <= toDate;
      });
    }
    if (expensesReportCategory && expensesReportCategory !== 'all') {
      base = base.filter(e => e.category === expensesReportCategory);
    }
    return base;
  };

  const handleSaveExpense = () => {
    if (expenseFormData.category && expenseFormData.amount) {
      const totalAmt = parseFloat(expenseFormData.amount || 0);
      const validPayments = (expensePayments || []).filter(p => p.amount && parseFloat(p.amount) > 0);
      const paymentsTotal = validPayments.reduce((s, p) => s + parseFloat(p.amount), 0);
      const isPaidFull = Math.round(paymentsTotal * 100) >= Math.round(totalAmt * 100);
      const primaryMethod = expensePayments[0]?.type || 'cash';
      // ── HARD BALANCE CHECK: per-account, includes edits ────────────────
      {
        const sw = language === 'sw';
        const spendPerAcct = {};
        validPayments.forEach(p => {
          if (p.type === 'credit') return;
          const key = p.accountKey || p.type || 'cash';
          spendPerAcct[key] = (spendPerAcct[key] || 0) + parseFloat(p.amount || 0);
        });
        if (editExpenseId) {
          const oldExpense = (expenses || []).find(e => e.id === editExpenseId);
          const oldPmts = oldExpense && Array.isArray(oldExpense.payments) ? oldExpense.payments : [];
          oldPmts.forEach(p => {
            if (p.type === 'credit') return;
            const key = p.accountKey || p.type || 'cash';
            spendPerAcct[key] = (spendPerAcct[key] || 0) - parseFloat(p.amount || 0);
          });
        }
        for (const [key, needed] of Object.entries(spendPerAcct)) {
          if (needed <= 0) continue;
          const available = getWalletBalance(key);
          if (available < needed) {
            let label = key;
            if (key === 'cash') label = sw ? 'Taslimu' : 'Cash';
            else {
              const acc = (paymentAccounts || []).find(a => `${a.type}|${a.id}` === key);
              if (acc) label = `${acc.bankName || acc.networkName || 'Account'} · ${acc.accountName || ''}`;
              else if (key === 'bank') label = sw ? 'Benki' : 'Bank';
              else if (key === 'mobilemoney') label = sw ? 'Pesa ya Simu' : 'Mobile Money';
            }
            showAlert(sw
              ? `Salio halitoshi kwa ${label}.\nKilichopo: ${fmt(available)}\nKinachohitajika: ${fmt(needed)}\nUpungufu: ${fmt(needed - available)}\n\nPunguza kiasi, badili akaunti, au weka kiwango kilichobaki kama Mkopo.`
              : `Insufficient balance in ${label}.\nAvailable: ${fmt(available)}\nRequired: ${fmt(needed)}\nShortfall: ${fmt(needed - available)}\n\nReduce the amount, switch accounts, or put the shortfall on Credit.`,
              { tone: 'error', title: sw ? 'Salio halitoshi' : 'Insufficient balance' });
            return;
          }
        }
      }
      const expenseData = {
        category: expenseFormData.category,
        amount: totalAmt,
        description: expenseFormData.description,
        date: expenseFormData.date || new Date().toISOString().split('T')[0],
        paymentMethod: primaryMethod,
        payments: validPayments.length > 0 ? validPayments : undefined,
        isPaid: isPaidFull,
        updatedAt: new Date().toISOString(),
      };
      if (editExpenseId) {
        setExpenses(prev => (prev || []).map(e => e.id === editExpenseId ? { ...e, ...expenseData } : e));
        // NOTE: No RPC for editing posted expenses (only update_expense_draft exists).
        // Local state authoritative during transition.
        setEditExpenseId(null);
      } else {
        const newExpense = { ...expenseData, id: Date.now() + Math.floor(Math.random() * 1000) };
        setExpenses(prev => [...prev, newExpense]);
        // MIGRATION-ONLY dual-write: persist to relational DB
        if (businessId && currentUser?.accessToken) {
          persistExpense(businessId, newExpense, currentUser.accessToken, paymentAccounts).then(r => {
            const rid = r?.data?.expense_id;
            if (rid) setExpenses(prev => prev.map(e => e.id === newExpense.id ? { ...e, _rid: rid, _rv: 1 } : e));
          }).catch((err) =>
            console.warn('[MIGRATION-ONLY] expense persist failed (non-blocking):', err.message));
        }
      }
      setExpenseFormData({ category: '', amount: '', description: '', date: new Date().toISOString().split('T')[0], paymentMethod: 'cash' });
      setExpensePayments([{ type: 'cash', amount: '' }]);
      setShowExpenseForm(false);
    }
  };

  // ── Report period date helper (H5) ────────────────────────────────
  // Delegates to the ONE shared range helper (src/periods.js) so labels and
  // filters here match the dashboard, the tabs and the wallet reports exactly.
  // Convention: Today; Week = last 7 days; Month = 1st → today; Year = Jan 1 →
  // today; presets exclude future dates. Returns null for unknown periods
  // (callers treat that as "no preset", e.g. Custom).
  const getReportDates = (period) => {
    if (!['today', 'week', 'month', 'year'].includes(period)) return null;
    return getPeriodRange(period);
  };

  // ── Void handlers (QuickBooks/Xero style — transactions stay visible) ───
  const handleVoidSale = (id) => {
    const sw = language === 'sw';
    const sale = (sales || []).find(s => s.id === id);
    if (!sale || sale.status === 'void') return;
    showConfirm(
      sw ? 'Batilisha mauzo haya? Athari za kifedha zitatenduliwa lakini kumbukumbu itabaki ionekane kwa ukaguzi.'
         : 'Void this sale? Financial effects will be reversed but the record stays visible for your audit trail.',
      () => doVoidSale(id),
      { tone: 'error', title: sw ? 'Batilisha Mauzo' : 'Void Sale' }
    );
  };
  // Immediate localStorage persistence for void — ensures status survives
  // even if the user refreshes before the React useEffect save fires.
  const flushVoidToStorage = (collection, id) => {
    try {
      const key = currentUser ? `faida_${currentUser.uid}_${collection}` : `faida_${collection}`;
      const raw = JSON.parse(localStorage.getItem(key) || '[]');
      const now = new Date().toISOString();
      const updated = raw.map(r => String(r.id) === String(id)
        ? { ...r, status: 'void', voidedAt: now, updatedAt: now }
        : r);
      localStorage.setItem(key, JSON.stringify(updated));
    } catch (e) { /* ignore */ }
  };

  // ── Authoritative void sync ───────────────────────────────────────────────
  // ONE place that decides whether the server actually voided the record.
  // Returns { ok, reason, detail }. ok===true ONLY when the relational write is
  // confirmed, so a caller can never show a success message for a failed void.
  //
  // reason values:
  //   'ok'          - server confirmed
  //   'no-session'  - no businessId/token, RPC never attempted
  //   'no-rid'      - record has no relational UUID (pre-migration / failed
  //                   create). commands.void_* casts sale_id to uuid, so a local
  //                   numeric id can NEVER succeed -we do not even try.
  //   'rpc-failed'  - attempted (twice) and the server rejected it
  const syncVoidToServer = async ({ label, record, localId, call }) => {
    if (!businessId || !currentUser?.accessToken) {
      console.warn(`[VOID] ${label} ${localId}: no active session — relational void NOT attempted.`);
      return { ok: false, reason: 'no-session' };
    }
    // The relational UUID is the ONLY value commands.void_* can accept. After a
    // relational rehydrate the uuid lives on .id; before that it lives on ._rid.
    const rid = record?._rid || localId;
    if (!isRelationalUuid(rid)) {
      console.warn(
        `[VOID] ${label} ${localId}: no relational UUID (_rid missing and id is not a uuid). ` +
        `This record was never persisted relationally, so commands.void_${label} ` +
        `cannot void it (its sale_id/purchase_id/expense_id parameter is cast to uuid).`
      );
      return { ok: false, reason: 'no-rid' };
    }
    try {
      await call(rid);
      return { ok: true, reason: 'ok' };
    } catch (err) {
      console.warn(`[VOID] ${label} void RPC failed (rid=${rid}):`, err.message);
      try {
        await call(rid);
        return { ok: true, reason: 'ok' };
      } catch (retryErr) {
        console.warn(`[VOID] ${label} void RPC retry failed (rid=${rid}):`, retryErr.message);
        return { ok: false, reason: 'rpc-failed', detail: retryErr.message };
      }
    }
  };

  // Honest, reason-specific failure message. Never says "voided" on its own.
  const voidFailureMessage = (label, outcome, sw) => {
    const noun = sw
      ? ({ sale: 'Mauzo', purchase: 'Manunuzi', expense: 'Matumizi', service: 'Shughuli', salesReturn: 'Urudishaji', purchaseReturn: 'Urudishaji' }[label] || 'Muamala')
      : ({ sale: 'sale', purchase: 'purchase', expense: 'expense', service: 'service transaction', salesReturn: 'return', purchaseReturn: 'purchase return' }[label] || 'transaction');
    if (outcome.reason === 'no-session') {
      return sw
        ? `HAIKUKAMILIKA: ${noun} umewekwa alama ya kubatilishwa kwenye kifaa hiki tu. Hakuna muunganisho wa akaunti, kwa hivyo seva HAIJABATILISHA. Ingia tena na ujaribu upya.`
        : `NOT COMPLETED: this ${noun} is marked void on this device only. There is no active session, so the server has NOT voided it. Sign in again and retry.`;
    }
    if (outcome.reason === 'no-rid') {
      return sw
        ? `HAIKUKAMILIKA: ${noun} huu haujahifadhiwa kwenye seva (hakuna kitambulisho cha seva), kwa hivyo HAUWEZI kubatilishwa kwenye seva. Umebatilishwa kwenye kifaa hiki tu na hauhesabiki kwenye hesabu. Wasiliana na msaada kuhusu kumbukumbu hii.`
        : `NOT COMPLETED ON SERVER: this ${noun} has no server record (missing relational id), so it cannot be voided on the server. It is marked void on this device and is excluded from all accounting, but the server copy is unchanged. Please report this record to support.`;
    }
    return sw
      ? `HAIKUKAMILIKA: ${noun} umebatilishwa kwenye kifaa hiki lakini seva ILIKATAA kubatilisha. Hauhesabiki kwenye hesabu, lakini jaribu tena mtandao ukiwa sawa.`
      : `NOT COMPLETED ON SERVER: this ${noun} is marked void on this device and excluded from all accounting, but the server REJECTED the void${outcome.detail ? ` (${outcome.detail})` : ''}. Please retry when you have a stable connection.`;
  };

  const doVoidSale = async (id) => {
    const sw = language === 'sw';
    const sale = sales.find(s => s.id === id);
    // Mark locally as void immediately (optimistic)
    setSales(prev => prev.map(s => String(s.id) === String(id)
      ? { ...s, status: 'void', voidedAt: new Date().toISOString(), updatedAt: new Date().toISOString() }
      : s));
    flushVoidToStorage('sales', id);
    // Register BOTH identities (local id + relational uuid) so the void survives
    // the id change that happens when the record is re-hydrated relationally.
    registerVoid(currentUser?.uid, 'sales', id, sale?._rid);
    // Void the issued invoice too
    if (sale) {
      setIssuedInvoices(prev => prev.map(i =>
        (String(i.id) === String(id) || (sale.invoiceNumber && i.invoiceNumber === sale.invoiceNumber))
          ? { ...i, status: 'void', voided: true, voidedAt: new Date().toISOString(), updatedAt: new Date().toISOString() }
          : i));
    }
    // Persist to database via existing void RPC. serverVoided stays false unless
    // the authoritative write is confirmed, so the user is never told the server
    // void succeeded when it did not.
    const outcome = await syncVoidToServer({
      label: 'sale',
      record: sale,
      localId: id,
      call: (rid) => voidSale(businessId, rid, currentUser.accessToken),
    });
    if (!outcome.ok) { showAlert(voidFailureMessage('sale', outcome, sw), { tone: 'error' }); return; }
    showAlert(sw ? 'Mauzo yamebatilishwa.' : 'Sale voided.', { tone: 'success' });
  };

  const handleVoidPurchase = (id) => {
    const sw = language === 'sw';
    const purchase = (purchases || []).find(p => p.id === id);
    if (!purchase || purchase.status === 'void') return;
    showConfirm(
      sw ? 'Batilisha manunuzi haya? Athari za kifedha zitatenduliwa lakini kumbukumbu itabaki ionekane kwa ukaguzi.'
         : 'Void this purchase? Financial effects will be reversed but the record stays visible for your audit trail.',
      () => doVoidPurchase(id),
      { tone: 'error', title: sw ? 'Batilisha Manunuzi' : 'Void Purchase' }
    );
  };
  const doVoidPurchase = async (id) => {
    const sw = language === 'sw';
    const purchase = purchases.find(p => p.id === id);
    setPurchases(prev => prev.map(p => String(p.id) === String(id)
      ? { ...p, status: 'void', voidedAt: new Date().toISOString(), updatedAt: new Date().toISOString() }
      : p));
    flushVoidToStorage('purchases', id);
    registerVoid(currentUser?.uid, 'purchases', id, purchase?._rid);
    const outcome = await syncVoidToServer({
      label: 'purchase',
      record: purchase,
      localId: id,
      call: (rid) => voidPurchase(businessId, rid, currentUser.accessToken),
    });
    if (!outcome.ok) { showAlert(voidFailureMessage('purchase', outcome, sw), { tone: 'error' }); return; }
    showAlert(sw ? 'Manunuzi yamebatilishwa.' : 'Purchase voided.', { tone: 'success' });
  };

  const handleVoidExpense = (id) => {
    const sw = language === 'sw';
    const expense = (expenses || []).find(e => e.id === id);
    if (!expense || expense.status === 'void') return;
    showConfirm(
      sw ? 'Batilisha matumizi haya? Athari za kifedha zitatenduliwa lakini kumbukumbu itabaki ionekane kwa ukaguzi.'
         : 'Void this expense? Financial effects will be reversed but the record stays visible for your audit trail.',
      () => doVoidExpense(id),
      { tone: 'error', title: sw ? 'Batilisha Matumizi' : 'Void Expense' }
    );
  };
  const doVoidExpense = async (id) => {
    const sw = language === 'sw';
    const expense = (expenses || []).find(e => e.id === id);
    setExpenses(prev => prev.map(e => String(e.id) === String(id)
      ? { ...e, status: 'void', voidedAt: new Date().toISOString(), updatedAt: new Date().toISOString() }
      : e));
    flushVoidToStorage('expenses', id);
    registerVoid(currentUser?.uid, 'expenses', id, expense?._rid);
    const outcome = await syncVoidToServer({
      label: 'expense',
      record: expense,
      localId: id,
      call: (rid) => voidExpense(businessId, rid, currentUser.accessToken),
    });
    if (!outcome.ok) { showAlert(voidFailureMessage('expense', outcome, sw), { tone: 'error' }); return; }
    showAlert(sw ? 'Matumizi yamebatilishwa.' : 'Expense voided.', { tone: 'success' });
  };

  const handleVoidPurchaseReturn = (id) => {
    const sw = language === 'sw';
    const pr = (purchaseReturns || []).find(r => r.id === id);
    if (!pr || pr.status === 'void') return;
    showConfirm(
      sw ? 'Batilisha urudishaji huu wa manunuzi? Athari za kifedha zitatenduliwa lakini kumbukumbu itabaki ionekane kwa ukaguzi.'
         : 'Void this purchase return? Financial effects will be reversed but the record stays visible for your audit trail.',
      async () => {
        setPurchaseReturns(prev => prev.map(r => String(r.id) === String(id)
          ? { ...r, status: 'void', voidedAt: new Date().toISOString(), updatedAt: new Date().toISOString() }
          : r));
        flushVoidToStorage('purchaseReturns', id);
        registerVoid(currentUser?.uid, 'purchaseReturns', id, pr?._rid);
        const outcome = await syncVoidToServer({
          label: 'purchaseReturn',
          record: pr,
          localId: id,
          call: (rid) => voidPurchaseReturn(businessId, rid, currentUser.accessToken),
        });
        if (!outcome.ok) { showAlert(voidFailureMessage('purchaseReturn', outcome, sw), { tone: 'error' }); return; }
        showAlert(sw ? 'Urudishaji wa manunuzi umebatilishwa.' : 'Purchase return voided.', { tone: 'success' });
      },
      { tone: 'error', title: sw ? 'Batilisha Urudishaji' : 'Void Purchase Return' }
    );
  };

  const handleDeleteManualTransaction = (id) => {
    {
      const sw = language === 'sw';
      showConfirm(
        sw ? 'Una uhakika unataka kufuta shughuli hii? Hatua hii haiwezi kutenduliwa.' : 'Are you sure you want to permanently delete this transaction? This cannot be undone.',
        () => setManualTransactions(prev => softDeleteById(prev, id)),
        { tone: 'error', title: sw ? 'Futa Shughuli' : 'Delete Transaction' }
      );
    }
  };

  // ── Edit initializers ─────────────────────────────────────────────
  const handleEditSale = (sale) => {
    setEditSaleId(sale.id);
    const _cd = sale.customerDetails || {};
    setSalesFormData({
      productName: sale.productName,
      quantity: sale.quantity,
      sellingPrice: sale.sellingPrice,
      cost: sale.cost,
      customerName: sale.customerName || '',
      customerPhone: _cd.phone || '',
      customerEmail: _cd.email || '',
      customerAddress: _cd.address || '',
      customerTIN: _cd.tin || '',
      customerVRN: _cd.vrn || '',
      date: sale.date,
      taxes: sale.taxes || [],
      discount: sale.discount || '',
      efdReceiptNo: sale.efdReceiptNo || '',
    });
    setSalesCustomerType(sale.customerType || 'walkin');
    // Load the issue-invoice toggle: legacy sales (no field) keep their invoice
    // if they already have an invoiceNumber; otherwise default ON. Mark touched
    // so the auto-default effect doesn't overwrite the loaded value.
    setSalesIssueInvoice(sale.invoiceIssued !== false ? true : false);
    salesIssueInvoiceTouched.current = true;
    // Restore line items -use items array if present, else build from flat fields
    const lineItems = sale.items && sale.items.length > 0
      ? sale.items
      : [{ id: Date.now(), type: sale.isService ? 'service' : 'product', productName: sale.productName, quantity: sale.quantity || '1', sellingPrice: sale.sellingPrice, cost: sale.cost || '0' }];
    setSaleLineItems(lineItems);
    if (sale.isService) setSaleItemKind('service');
    else setSaleItemKind('product');
    setSalePayments(
      sale.payments && sale.payments.length > 0
        ? sale.payments
        : [{ type: sale.paymentMethod || 'cash', amount: String(sale.totalRevenue || '') }]
    );
    setShowSalesForm(true);
  };

  const handleEditPurchase = (purchase) => {
    setEditPurchaseId(purchase.id);
    setPurchaseFormData({
      productName: purchase.productName,
      quantity: purchase.quantity,
      unitPrice: purchase.unitPrice || purchase.costPrice || '',
      totalCost: purchase.totalCost,
      supplier: purchase.supplier || '',
      date: purchase.date,
      taxes: purchase.taxes || [],
      discount: purchase.discount || '',
      efdReceiptNo: purchase.efdReceiptNo || '',
    });
    // Restore line items
    const lineItems = purchase.items && purchase.items.length > 0
      ? purchase.items
      : [{ id: Date.now(), productName: purchase.productName, quantity: purchase.quantity, unitPrice: purchase.unitPrice || purchase.costPrice || '', totalCost: purchase.totalCost }];
    setPurchaseLineItems(lineItems);
    setPurchaseSupplierType(purchase.supplierType || 'registered');
    setPurchasePaymentMethod(purchase.paymentMethod || 'cash');
    setPurchasePayments(
      purchase.payments && purchase.payments.length > 0
        ? purchase.payments
        : [{ type: purchase.paymentMethod || 'cash', amount: String(purchase.totalCost || '') }]
    );
    setShowPurchaseForm(true);
  };

  const handleEditExpense = (expense) => {
    setEditExpenseId(expense.id);
    setExpenseFormData({
      category: expense.category,
      amount: String(expense.amount),
      description: expense.description || '',
      date: expense.date,
      paymentMethod: expense.paymentMethod || 'cash',
    });
    setExpensePayments(
      expense.payments && expense.payments.length > 0
        ? expense.payments
        : [{ type: expense.paymentMethod || 'cash', amount: String(expense.amount || '') }]
    );
    setShowExpenseForm(true);
  };

  // ── Sale split-payment helpers ────────────────────────────────────
  const getSaleTotalForPayments = () => {
    // Sum across all cart items
    const cartBase = saleLineItems.reduce((sum, item) => {
      const qty = item.type === 'service' ? 1 : parseFloat(item.quantity || 0);
      return sum + qty * parseFloat(item.sellingPrice || 0);
    }, 0);
    // Include draft row if user filled it in but didn't click "Add item"
    const sd = saleLineDraft;
    const draftBase = (sd && sd.productName && sd.quantity && sd.sellingPrice)
      ? parseFloat(sd.quantity) * parseFloat(sd.sellingPrice)
      : 0;
    const base = cartBase + draftBase;
    const discPct = parseFloat(salesFormData.discount || 0);
    const afterDisc2 = Math.max(0, base - base * (discPct / 100));
    const taxExtra = (salesFormData.taxes || []).reduce((sum, tax) => {
      return sum + afterDisc2 * (parseFloat(tax.rate || 0) / 100);
    }, 0);
    return afterDisc2 + taxExtra;
  };

  const handleSalePaymentTypeChange = (idx, value) => {
    // value may be a composite ('bank|abc123') for a specific account, or plain ('cash'/'credit').
    // Store both fields so balance lookups (which key by base type) still work.
    setSalePayments(prev => (prev || []).map((p, i) =>
      i === idx ? { ...p, type: baseType(value), accountKey: value } : p
    ));
  };

  const handleSalePaymentAmountChange = (idx, amount) => {
    setSalePayments(prev => (prev || []).map((p, i) => i === idx ? { ...p, amount } : p));
  };

  const addSalePayment = () => {
    const opts = buildPaymentOptions(true);
    if ((salePayments || []).length >= opts.length) return;
    const used = new Set((salePayments || []).map(p => p.accountKey || p.type));
    const next = opts.find(o => !used.has(o.value)) || opts[0];
    setSalePayments(prev => [...(prev || []), { type: next.type, accountKey: next.value, amount: '' }]);
  };

  const removeSalePayment = (idx) => {
    if ((salePayments || []).length <= 1) return;
    setSalePayments(prev => (prev || []).filter((_, i) => i !== idx));
  };

  // ── Service form helpers (mirror sale form helpers) ─────────────
  const addServiceTax = () => {
    setServiceTransactionFormData(prev => ({ ...prev, taxes: [...(prev.taxes || []), { type: 'vat', rate: defaultVatRate }] }));
  };
  const removeServiceTax = (index) => {
    setServiceTransactionFormData(prev => ({ ...prev, taxes: (prev.taxes || []).filter((_, i) => i !== index) }));
  };
  const updateServiceTax = (index, field, value) => {
    setServiceTransactionFormData(prev => {
      const taxes = (prev.taxes || []).map((t, i) => i === index ? { ...t, [field]: value } : t);
      return { ...prev, taxes };
    });
  };
  const handleServicePaymentTypeChange = (idx, value) => {
    setServicePayments(prev => (prev || []).map((p, i) =>
      i === idx ? { ...p, type: baseType(value), accountKey: value } : p
    ));
  };
  const handleServicePaymentAmountChange = (idx, amount) => {
    setServicePayments(prev => (prev || []).map((p, i) => i === idx ? { ...p, amount } : p));
  };
  const addServicePayment = () => {
    const opts = buildPaymentOptions(true);
    if ((servicePayments || []).length >= opts.length) return;
    const used = new Set((servicePayments || []).map(p => p.accountKey || p.type));
    const next = opts.find(o => !used.has(o.value)) || opts[0];
    setServicePayments(prev => [...(prev || []), { type: next.type, accountKey: next.value, amount: '' }]);
  };
  const removeServicePayment = (idx) => {
    if ((servicePayments || []).length <= 1) return;
    setServicePayments(prev => (prev || []).filter((_, i) => i !== idx));
  };
  const getServiceTotalForPayments = () => {
    const baseRev = (serviceLineItems || []).reduce((sum, it) => sum + parseFloat(it.quantity || 0) * parseFloat(it.price || 0), 0);
    const discPct = parseFloat(serviceTransactionFormData.discount || 0);
    const afterDisc = Math.max(0, baseRev - baseRev * (discPct / 100));
    const taxExtra = (serviceTransactionFormData.taxes || []).reduce((sum, tax) => sum + afterDisc * (parseFloat(tax.rate || 0) / 100), 0);
    return afterDisc + taxExtra;
  };
  const resetServiceForm = () => {
    setServiceLineItems([]);
    setServiceLineDraft({ serviceId: '', serviceName: '', quantity: '', price: '', cost: '' });
    setServicePayments([{ type: 'cash', accountKey: 'cash', amount: '' }]);
    setServiceFooterSections([]);
    setServiceIssueInvoice(false);
    serviceIssueInvoiceTouched.current = false;
    setServiceCustomerType('walkin');
    setServiceTransactionEditId(null);
    setServiceTransactionFormData({
      serviceId: '', serviceNameOverride: '', quantity: '', price: '', cost: '',
      customerName: '', customerPhone: '', customerEmail: '', customerAddress: '', customerTIN: '', customerVRN: '',
      paymentMethod: 'cash', date: new Date().toISOString().split('T')[0],
      discount: '', efdReceiptNo: '', taxes: []
    });
  };

  // ── Drilldown computed data ───────────────────────────────────────
  const drilldownData = useMemo(() => {
    if (!activeDrilldown) return {};
    // Use CALENDAR periods with a proper end date -identical to the dashboard
    // KPI boxes (filterByPeriod). Previously this was a rolling window with no
    // upper bound, so the drilldown total (e.g. TSh 700,000) didn't match the
    // box (TSh 200,000): it pulled in the previous month AND future-dated rows.
    // Range = period start → TODAY (never into the future). Identical to the
    // Sales/Reports convention (getReportDates), so the drilldown, the
    // dashboard boxes and the reports all show the same total for a period —
    // future-dated entries are excluded everywhere.
    // H5: derive the window from the ONE shared helper so this drilldown matches
    // the dashboard boxes, the tabs and the wallet reports exactly. Presets are
    // to-date (future excluded); Custom honours the chosen dates.
    const _range = getPeriodRange(drilldownPeriod, drilldownCustomFrom, drilldownCustomTo);
    const _mkLocal = (s, end) => {
      if (!s) return end ? new Date(2999, 11, 31, 23, 59, 59, 999) : new Date(1900, 0, 1);
      const [y, m, d] = s.split('-').map(Number);
      return end ? new Date(y, m - 1, d, 23, 59, 59, 999) : new Date(y, m - 1, d);
    };
    let fromDate = _mkLocal(_range.from, false);
    let toDate = _mkLocal(_range.to, true);
    // String-based membership (matches the ledger's YYYY-MM-DD convention).
    const inP = d => inPeriod(d, _range);
    const beforeP = d => !!_range.from && dateKey(d) < _range.from;
    const matchAcct = (pm, acct) => {
      if (acct === 'total') return pm !== 'credit'; // sum of all real wallets
      if (acct === 'bank') return pm === 'bank';
      if (acct === 'mobilemoney') return pm === 'mobilemoney';
      return pm !== 'bank' && pm !== 'mobilemoney';
    };
    if (activeDrilldown === 'sales' || activeDrilldown === 'products_only' || activeDrilldown === 'services_only' || activeDrilldown === 'total_revenue') {
      let items = (sales || []).filter(s => inP(s.date) && s.status !== 'void');
      if (activeDrilldown === 'products_only') {
        items = items.filter(s => !s.isService);
      } else if (activeDrilldown === 'services_only') {
        // Service transactions live in serviceTransactions state, not sales
        const svcItems = (serviceTransactions || []).filter(s => inP(s.date) && s.status !== 'void').map(s => ({
          ...s,
          productName: s.serviceName || s.name,
          customerName: s.customerName || s.clientName,
          totalRevenue: s.totalRevenue || (parseFloat(s.price || 0) * parseFloat(s.quantity || 1)),
          isPaid: s.isPaid !== false,
        }));
        items = svcItems.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
      } else if (activeDrilldown === 'total_revenue') {
        // Include service transactions too (they live in a separate state)
        const svcItems = (serviceTransactions || []).filter(s => inP(s.date) && s.status !== 'void').map(s => ({
          ...s,
          productName: s.serviceName || s.name,
          customerName: s.customerName || s.clientName,
          totalRevenue: s.totalRevenue || (s.price * s.quantity),
          isPaid: s.isPaid !== false,
        }));
        items = [...items, ...svcItems].sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
      }
      // Give each non-return row its GRAND TOTAL (incl. taxes/discount) so the
      // drilldown matches the box and the report to the shilling.
      items = items.map(x => x._isReturn ? x : { ...x, totalRevenue: getSaleGrandTotal(x) });
      // Add sales returns of the listed sales as NEGATIVE rows so the drilldown
      // nets out exactly like the box and the report. (Not for services-only.)
      if (activeDrilldown !== 'services_only') {
        const _ids = new Set(items.map(x => String(x.id)));
        const retRows = (salesReturns || []).filter(r => _ids.has(String(r.originalSaleId)) && r.status !== 'void').map(r => ({
          id: 'ret-' + r.id, _isReturn: true,
          productName: (r.productName || (r.items && r.items[0] && r.items[0].productName) || (language === 'sw' ? 'Marejesho' : 'Return')),
          customerName: r.customerName || '—',
          paymentMethod: r.refundMethod || 'cash',
          totalRevenue: -parseFloat(r.refundAmt || r.totalRefund || 0),
          date: r.date, isPaid: true,
        }));
        items = [...items, ...retRows].sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
      }
      const total = items.reduce((s, i) => s + parseFloat(i.totalRevenue || 0), 0);
      const headers = ['Date', 'Product/Service', 'Customer', 'Amount', 'Payment', 'Status'];
      const rows = (items || []).map(i => [i.date, i.productName || i.serviceName || '—', i.customerName || '—', parseFloat(i.totalRevenue || 0).toFixed(2), i.paymentMethod || 'cash', i._isReturn ? 'Return' : (i.isPaid === false ? 'Unpaid' : 'Paid')]);
      return { items, total, headers, rows };
    }
    if (activeDrilldown === 'purchases') {
      let items = (purchases || []).filter(p => inP(p.date) && p.status !== 'void').map(p => ({ ...p, totalCost: getPurchaseGrandTotal(p) }));
      const _pids = new Set(items.map(x => String(x.id)));
      const retRows = (purchaseReturns || []).filter(r => _pids.has(String(r.originalPurchaseId)) && r.status !== 'void').map(r => ({
        id: 'pret-' + r.id, _isReturn: true,
        productName: (r.productName || (r.items && r.items[0] && r.items[0].productName) || (language === 'sw' ? 'Marejesho' : 'Return')),
        supplier: r.supplier || '—',
        paymentMethod: r.refundMethod || 'cash',
        totalCost: -parseFloat(r.refundAmt || r.totalRefund || 0),
        date: r.date, isPaid: true,
      }));
      items = [...items, ...retRows].sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
      const total = items.reduce((s, i) => s + parseFloat(i.totalCost || 0), 0);
      const headers = ['Date', 'Product', 'Supplier', 'Amount', 'Payment', 'Status'];
      const rows = (items || []).map(i => [i.date, i.productName, i.supplier || '—', parseFloat(i.totalCost || 0).toFixed(2), i.paymentMethod || 'cash', i._isReturn ? 'Return' : (i.isPaid === false ? 'Unpaid' : 'Paid')]);
      return { items, total, headers, rows };
    }
    if (activeDrilldown === 'expenses') {
      const items = (expenses || []).filter(e => inP(e.date) && e.status !== 'void');
      const total = items.reduce((s, i) => s + parseFloat(i.amount || 0), 0);
      const headers = ['Date', 'Category', 'Amount', 'Description'];
      const rows = (items || []).map(i => [i.date, i.category, parseFloat(i.amount || 0).toFixed(2), i.description || '']);
      return { items, total, headers, rows };
    }
    if (['cash', 'bank', 'mobilemoney', 'total'].includes(activeDrilldown)) {
      const acct = activeDrilldown;
      // Sub-account filter (specific CRDB / NMB / M-PESA), narrowed by accountKey.
      const subAccts = (paymentAccounts || []).filter(a => a.type === acct);
      const subIdx = acct === 'bank' ? selectedBankAccount
                   : acct === 'mobilemoney' ? selectedMobileMoneyAccount
                   : 'all';
      const targetSub = subIdx !== 'all' && subAccts[subIdx] ? subAccts[subIdx] : null;
      const targetKey = targetSub ? `${targetSub.type}|${targetSub.id}` : null;

      // Wallet/sub-account matcher against the SINGLE cash ledger -the same
      // engine the dashboard and the wallet report use, so all three agree.
      const matchFn = (r) => {
        if (acct === 'total') return true;
        if (r.wallet !== acct) return false;
        if (!targetSub) return true;
        return r.accountKey === targetKey;
      };
      const toStr = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
      const stmt = walletStatement(cashLedgerData.ledger, matchFn, toStr(fromDate), toStr(toDate));
      const openingBalance = stmt.opening;
      const closingBalance = stmt.closing;
      // Build a dated statement of the period's movements with a running balance.
      const sorted = [...(stmt.periodRows || [])].sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
      let run = openingBalance;
      const items = sorted.map(r => {
        const signed = r.direction === 'in' ? r.amount : -r.amount;
        run += signed;
        return { date: r.date, source: r.source, name: ledgerRowLabel(r), ref: ledgerRowRef(r), amount: r.amount, type: r.direction === 'in' ? 'credit' : 'debit', running: run, id: r.refId, returnAmt: 0 };
      });
      const headers = ['Date', 'Reference', 'Description', 'Amount', 'Balance'];
      const rows = items.map(i => [i.date, i.ref, i.name, (i.type === 'credit' ? '+' : '-') + i.amount.toFixed(2), i.running.toFixed(2)]);
      return { items, openingBalance, closingBalance, current: stmt.current, headers, rows, isBalance: true, acct };
    }
    if (activeDrilldown === 'receivables') {
      const headers = ['Date', 'Customer', 'Product', 'Amount'];
      const rows = (receivables || []).map(i => [i.date, i.customerName, i.productName, parseFloat(i.amount).toFixed(2)]);
      return { items: receivables, total: totalReceivables, headers, rows, noFilter: true };
    }
    if (activeDrilldown === 'payables') {
      const headers = ['Date', 'Supplier', 'Product', 'Amount'];
      const rows = (payables || []).map(i => [i.date, i.supplier, i.productName, parseFloat(i.amount).toFixed(2)]);
      return { items: payables, total: totalPayables, headers, rows, noFilter: true };
    }
    if (activeDrilldown === 'stock') {
      const headers = ['Product Name', 'Quantity', 'Unit Cost', 'Value', 'Status'];
      const items = (products || []).map(p => {
        const qty = parseInt(p.balance ?? p.currentStock ?? p.openingStockQty ?? 0) || 0;
        const lowLevel = parseInt(p.lowStockLevel ?? p.minStockAlert ?? 0) || 0;
        const wacUnit = getProductWAC(p.productName);
        const unitCost = wacUnit > 0 ? wacUnit : (parseFloat(p.costPrice ?? p.cost ?? p.unitCost ?? p.buyingPrice ?? p.purchasePrice ?? 0) || 0);
        const value = qty * unitCost;
        const status = qty === 0 ? 'Out of Stock' : (qty <= lowLevel ? 'Low' : 'In Stock');
        return { ...p, _qty: qty, _unitCost: unitCost, _value: value, _status: status, _lowLevel: lowLevel };
      });
      const rows = items.map(p => [
        p.productName || p.name || '—',
        String(p._qty),
        p._unitCost.toFixed(2),
        p._value.toFixed(2),
        p._status
      ]);
      return { items, headers, rows, noFilter: true, emptyMessage: 'No items found' };
    }
    if (activeDrilldown === 'lowstock') {
      const headers = ['Product Name', 'Quantity', 'Alert Level', 'Status'];
      const items = (products || []).map(p => {
        const qty = parseInt(p.balance ?? p.currentStock ?? p.openingStockQty ?? 0) || 0;
        const lowLevel = parseInt(p.lowStockLevel ?? p.minStockAlert ?? 0) || 0;
        const status = qty === 0 ? 'Out of Stock' : (qty <= lowLevel ? 'Low' : 'In Stock');
        return { ...p, _qty: qty, _lowLevel: lowLevel, _status: status };
      }).filter(p => p._qty <= (p._lowLevel || 0));
      const rows = items.map(p => [
        p.productName || p.name || '—',
        String(p._qty),
        String(p._lowLevel || 0),
        p._status
      ]);
      return { items, headers, rows, noFilter: true, emptyMessage: 'No items found' };
    }
    return {};
  }, [activeDrilldown, drilldownPeriod, drilldownCustomFrom, drilldownCustomTo, sales, purchases, expenses, manualTransactions, receivables, payables, totalReceivables, totalPayables, products, serviceTransactions, salesReturns, purchaseReturns, paymentAccounts, selectedBankAccount, selectedMobileMoneyAccount]);

  const handleWithdrawalSave = () => {
    if (!withdrawalFormData.amount || parseFloat(withdrawalFormData.amount) <= 0) return;
    const amount = parseFloat(withdrawalFormData.amount);
    // HARD BALANCE CHECK on withdrawals (debits only -deposits add money)
    if (withdrawalFormData.type === 'debit') {
      // Use sub-account if selected, otherwise broad-type aggregate
      const subAccts = (paymentAccounts || []).filter(a => a.type === activeDrilldown);
      const subIdx = activeDrilldown === 'bank' ? selectedBankAccount
                   : activeDrilldown === 'mobilemoney' ? selectedMobileMoneyAccount
                   : 'all';
      const targetSub = subIdx !== 'all' && subAccts[subIdx] ? subAccts[subIdx] : null;
      const key = targetSub ? `${targetSub.type}|${targetSub.id}` : activeDrilldown;
      const available = getWalletBalance(key);
      if (available < amount) {
        const sw = language === 'sw';
        const label = targetSub
          ? `${targetSub.bankName || targetSub.networkName || 'Account'} · ${targetSub.accountName || ''}`
          : activeDrilldown === 'cash' ? (sw ? 'Taslimu' : 'Cash')
          : activeDrilldown === 'bank' ? (sw ? 'Benki' : 'Bank')
          : activeDrilldown === 'mobilemoney' ? (sw ? 'Pesa ya Simu' : 'Mobile Money') : activeDrilldown;
        showAlert(sw
          ? `Salio halitoshi kwa ${label}.\nKilichopo: ${fmt(available)}\nUnachotaka kutoa: ${fmt(amount)}\nUpungufu: ${fmt(amount - available)}\n\nPunguza kiasi au tumia akaunti nyingine.`
          : `Insufficient balance in ${label}.\nAvailable: ${fmt(available)}\nWithdrawing: ${fmt(amount)}\nShortfall: ${fmt(amount - available)}\n\nReduce the amount or use a different account.`,
          { tone: 'error', title: sw ? 'Salio halitoshi' : 'Insufficient balance' });
        return;
      }
    }
    // Tag with accountKey if a specific sub-account is selected
    const subAcctsW = (paymentAccounts || []).filter(a => a.type === activeDrilldown);
    const subIdxW = activeDrilldown === 'bank' ? selectedBankAccount
                  : activeDrilldown === 'mobilemoney' ? selectedMobileMoneyAccount
                  : 'all';
    const targetSubW = subIdxW !== 'all' && subAcctsW[subIdxW] ? subAcctsW[subIdxW] : null;
    const newManualTxn = {
      id: Date.now(),
      date: withdrawalFormData.date || new Date().toISOString().split('T')[0],
      account: activeDrilldown,
      accountKey: targetSubW ? `${targetSubW.type}|${targetSubW.id}` : activeDrilldown,
      type: withdrawalFormData.type,
      amount,
      description: withdrawalFormData.description || (withdrawalFormData.type === 'debit' ? 'Withdrawal' : 'Deposit'),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    setManualTransactions(prev => [...prev, newManualTxn]);
    // MIGRATION-ONLY dual-write: persist to relational DB
    if (businessId && currentUser?.accessToken) {
      persistCashAdjustment(businessId, newManualTxn, currentUser.accessToken, paymentAccounts).then(r => {
        const rid = r?.data?.adjustment_id || r?.data?.id;
        if (rid) setManualTransactions(prev => prev.map(t => t.id === newManualTxn.id ? { ...t, _rid: rid } : t));
      }).catch((err) =>
        console.warn('[MIGRATION-ONLY] cash adjustment persist failed (non-blocking):', err.message));
    }
    setWithdrawalFormData({ date: new Date().toISOString().split('T')[0], type: 'debit', amount: '', description: '' });
    setShowWithdrawalForm(false);
  };

  // ── Debt payment: record a real collection on the chosen account ──────
  // Instead of just flipping isPaid, we RECORD the cash: append a dated,
  // non-credit payment on the selected registered account and clear the
  // matching credit line. This keeps the payments[] the true history, so both
  // the wallet balances and the cash ledger count the collection exactly once
  // (previously it only flipped a flag, so the ledger never saw the money and
  // the old engine only "looked right" by counting the leftover credit as cash).
  const handleDebtPayment = () => {
    const { saleId, paymentMethod, date } = debtPaymentData;
    if (!saleId) return;
    const finalDate = date || new Date().toISOString().split('T')[0];
    const baseT = baseType(paymentMethod);
    setSales(prev => prev.map(s => {
      if (!(s.id === Number(saleId) || s.id === saleId)) return s;
      const grand = getSaleGrandTotal(s);
      const pmts = Array.isArray(s.payments) ? [...s.payments] : [];
      const reduced = parseFloat(s.creditReducedByReturns || 0);
      const cashPaid = pmts.filter(p => p.type !== 'credit').reduce((a, p) => a + parseFloat(p.amount || 0), 0);
      const outstanding = Math.max(0, grand - cashPaid - reduced);
      const requested = parseFloat(debtPaymentData.amount || outstanding) || outstanding;
      const collect = Math.min(requested, outstanding);
      if (collect <= 0) return { ...s, isPaid: true, paidDate: finalDate };
      // Reduce credit line(s) by the collected amount -credit turns into cash.
      let remaining = collect;
      const rebuilt = [];
      for (const p of pmts) {
        if (p.type === 'credit' && remaining > 0.0001) {
          const amt = parseFloat(p.amount || 0);
          if (amt <= remaining + 0.0001) { remaining -= amt; } // drop this credit line
          else { rebuilt.push({ ...p, amount: amt - remaining }); remaining = 0; }
        } else { rebuilt.push(p); }
      }
      rebuilt.push({ type: baseT, accountKey: paymentMethod, amount: collect, date: finalDate });
      const newCashPaid = rebuilt.filter(p => p.type !== 'credit').reduce((a, p) => a + parseFloat(p.amount || 0), 0);
      const stillOwed = Math.max(0, grand - newCashPaid - reduced);
      // Keep a clean bare paymentMethod for display, and preserve the specific
      // account (bank|id) in accountKey so nothing loses the sub-account.
      return { ...s, payments: rebuilt, isPaid: stillOwed < 0.005, paidDate: stillOwed < 0.005 ? finalDate : s.paidDate, paymentMethod: baseT, accountKey: paymentMethod, updatedAt: new Date().toISOString() };
    }));
    // MIGRATION-ONLY: record payment in relational DB if sale has UUID
    const targetSale = (sales || []).find(s => s.id === Number(saleId) || s.id === saleId);
    const saleRid = resolveRid(targetSale);
    if (saleRid && businessId && currentUser?.accessToken) {
      const requested = parseFloat(debtPaymentData.amount || 0);
      const collectAmt = requested > 0 ? requested : parseFloat(targetSale?.totalRevenue || 0);
      bridgeRecordSalePayment(businessId, saleRid, { type: baseT, accountKey: paymentMethod, amount: collectAmt, date: finalDate }, currentUser.accessToken, paymentAccounts)
        .catch(err => console.warn('[MIGRATION-ONLY] sale payment record failed:', err.message));
    }
    setDebtPaymentData({ saleId: '', amount: '', paymentMethod: 'cash', date: new Date().toISOString().split('T')[0] });
    setShowDebtPaymentForm(false);
  };

  // ── Loan payment: record a real supplier settlement on the chosen account ─
  const handleLoanPayment = () => {
    const { purchaseId, paymentMethod, date } = loanPaymentData;
    if (!purchaseId) return;
    const finalDate = date || new Date().toISOString().split('T')[0];
    const baseT = baseType(paymentMethod);
    setPurchases(prev => prev.map(p => {
      if (!(p.id === Number(purchaseId) || p.id === purchaseId)) return p;
      const grand = getPurchaseGrandTotal(p);
      const pmts = Array.isArray(p.payments) ? [...p.payments] : [];
      const reduced = parseFloat(p.payableReducedByReturns || 0);
      const cashPaid = pmts.filter(x => x.type !== 'credit').reduce((a, x) => a + parseFloat(x.amount || 0), 0);
      const outstanding = Math.max(0, grand - cashPaid - reduced);
      const requested = parseFloat(loanPaymentData.amount || outstanding) || outstanding;
      const settle = Math.min(requested, outstanding);
      if (settle <= 0) return { ...p, isPaid: true, paidDate: finalDate };
      let remaining = settle;
      const rebuilt = [];
      for (const x of pmts) {
        if (x.type === 'credit' && remaining > 0.0001) {
          const amt = parseFloat(x.amount || 0);
          if (amt <= remaining + 0.0001) { remaining -= amt; }
          else { rebuilt.push({ ...x, amount: amt - remaining }); remaining = 0; }
        } else { rebuilt.push(x); }
      }
      rebuilt.push({ type: baseT, accountKey: paymentMethod, amount: settle, date: finalDate });
      const newCashPaid = rebuilt.filter(x => x.type !== 'credit').reduce((a, x) => a + parseFloat(x.amount || 0), 0);
      const stillOwed = Math.max(0, grand - newCashPaid - reduced);
      return { ...p, payments: rebuilt, isPaid: stillOwed < 0.005, paidDate: stillOwed < 0.005 ? finalDate : p.paidDate, paymentMethod: baseT, accountKey: paymentMethod, updatedAt: new Date().toISOString() };
    }));
    // MIGRATION-ONLY: record payment in relational DB if purchase has UUID
    const targetPurchase = (purchases || []).find(p => p.id === Number(purchaseId) || p.id === purchaseId);
    const purchaseRid = resolveRid(targetPurchase);
    if (purchaseRid && businessId && currentUser?.accessToken) {
      const requested = parseFloat(loanPaymentData.amount || 0);
      const settleAmt = requested > 0 ? requested : parseFloat(targetPurchase?.totalCost || 0);
      bridgeRecordPurchasePayment(businessId, purchaseRid, { type: baseT, accountKey: paymentMethod, amount: settleAmt, date: finalDate }, currentUser.accessToken, paymentAccounts)
        .catch(err => console.warn('[MIGRATION-ONLY] purchase payment record failed:', err.message));
    }
    setLoanPaymentData({ purchaseId: '', amount: '', paymentMethod: 'cash', date: new Date().toISOString().split('T')[0] });
    setShowLoanPaymentForm(false);
  };

  // A. FOR PRODUCTS (Setup Tab) - Isolated handler
  const handleSetupSubmit = (e) => {
    e.preventDefault();
    
    if (setup_Name && setup_StockQty && setup_StockValue && setup_MinAlert) {
      // Prevent duplicate product names
      const nameExists = (products || []).some(p =>
        p.productName?.toLowerCase().trim() === setup_Name.toLowerCase().trim() && p.id !== setup_EditId
      );
      if (nameExists) {
        setSuccessMessage(`A product named "${setup_Name}" already exists. Please use a unique name.`);
        setTimeout(() => setSuccessMessage(''), 4000);
        return;
      }
      if (setup_EditId) {
        // Update existing product in context
        const existingProd = (products || []).find(p => p.id === setup_EditId);
        const updatedProd = {
          productName: setup_Name,
          openingStockQty: setup_StockQty,
          openingStockValue: setup_StockValue,
          minStockAlert: setup_MinAlert,
          sellingPrice: setup_SellingPrice,
          unit: setup_Unit || 'piece',
          category: setup_Category,
          reorderQty: setup_ReorderQty,
          updatedAt: new Date().toISOString(),
        };
        setProducts(prev => prev.map(item =>
          item.id === setup_EditId ? { ...item, ...updatedProd } : item
        ));
        // MIGRATION-ONLY: sync edit to relational DB
        if (businessId && currentUser?.accessToken && resolveRid(existingProd)) {
          persistProduct(businessId, { ...updatedProd, _rid: resolveRid(existingProd), _rv: existingProd._rv, rowVersion: existingProd.rowVersion }, currentUser.accessToken)
            .then(r => { const rid = r?.data?.id; if (rid) setProducts(prev => prev.map(p => p.id === setup_EditId ? { ...p, _rid: rid, _rv: (p._rv || p.rowVersion || 0) + 1 } : p)); })
            .catch(err => console.warn('[MIGRATION-ONLY] product update failed:', err.message));
        }
        setSuccessMessage('Product updated successfully!');
      } else {
        // Add new product to context (unified structure)
        const qty = parseInt(setup_StockQty) || 0;
        const openingUnitCost = qty > 0 ? (parseFloat(setup_StockValue) / qty) : 0;
        const item = {
          id: Date.now() + Math.floor(Math.random() * 1000),
          productName: setup_Name,
          openingStockQty: setup_StockQty,
          openingStockValue: setup_StockValue,
          minStockAlert: setup_MinAlert,
          sellingPrice: setup_SellingPrice,
          unit: setup_Unit || 'piece',
          category: setup_Category,
          reorderQty: setup_ReorderQty,
          currentStock: qty,
          stockIn: 0,
          stockOut: 0,
          balance: qty,
          costPrice: openingUnitCost > 0 ? openingUnitCost.toFixed(2) : undefined,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        setProducts(prev => [...prev, item]);
        // MIGRATION-ONLY dual-write: persist to relational DB
        if (businessId && currentUser?.accessToken) {
          persistProduct(businessId, item, currentUser.accessToken).then(r => {
            const rid = r?.data?.id;
            if (rid) setProducts(prev => prev.map(p => p.id === item.id ? { ...p, _rid: rid, _rv: 1 } : p));
          }).catch((err) =>
            console.warn('[MIGRATION-ONLY] product persist failed (non-blocking):', err.message));
        }
        setSuccessMessage('Product saved successfully!');
      }

      // Reset all fields and hide form
      setSetup_Name('');
      setSetup_StockQty('');
      setSetup_StockValue('');
      setSetup_MinAlert('');
      setSetup_SellingPrice('');
      setSetup_Unit('piece');
      setSetup_Category('');
      setSetup_ReorderQty('');
      setSetup_EditId(null);
      setShowSetup_ProductForm(false);
      
      setTimeout(() => setSuccessMessage(''), 3000);
    }
  };

  // B. FOR EXPENSE CATEGORIES (Setup Tab) - Isolated handler
  const handleExpenseSubmit = (e) => {
    e.preventDefault();
    
    if (expense_Name) {
      if (expense_EditId) {
        const existingCat = (expenseCategories || []).find(c => c.id === expense_EditId);
        setExpenseCategories(prev => prev.map(item =>
          item.id === expense_EditId
            ? { ...item, name: expense_Name, desc: expense_Desc, updatedAt: new Date().toISOString() }
            : item
        ));
        // MIGRATION-ONLY: sync edit to relational DB
        if (businessId && currentUser?.accessToken && resolveRid(existingCat)) {
          persistExpenseCategory(businessId, { name: expense_Name, _rid: resolveRid(existingCat), _rv: existingCat._rv, rowVersion: existingCat.rowVersion }, currentUser.accessToken)
            .then(r => { const rid = r?.data?.id; if (rid) setExpenseCategories(prev => prev.map(c => c.id === expense_EditId ? { ...c, _rid: rid, _rv: (c._rv || c.rowVersion || 0) + 1 } : c)); })
            .catch(err => console.warn('[MIGRATION-ONLY] category update failed:', err.message));
        }
        setSuccessMessage('Category updated successfully!');
      } else {
        const item = {
          id: Date.now(),
          name: expense_Name,
          desc: expense_Desc,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        setExpenseCategories(prev => [...prev, item]);
        // MIGRATION-ONLY dual-write: persist to relational DB
        if (businessId && currentUser?.accessToken) {
          persistExpenseCategory(businessId, item, currentUser.accessToken).then(r => {
            const rid = r?.data?.id;
            if (rid) setExpenseCategories(prev => prev.map(c => c.id === item.id ? { ...c, _rid: rid, _rv: 1 } : c));
          }).catch(err => console.warn('[dual-write] expenseCategory:', err.message));
        }
        setSuccessMessage('Category saved successfully!');
      }
      
      // Reset
      setExpense_Name('');
      setExpense_Desc('');
      setExpense_EditId(null);
      setShowExpense_SetupForm(false);
      
      setTimeout(() => setSuccessMessage(''), 3000);
    }
  };

  const handleSetupEdit = (item) => {
    setSetup_EditId(item.id);
    setSetup_Name(item.productName || '');
    setSetup_StockQty(item.openingStockQty || '');
    setSetup_StockValue(item.openingStockValue || '');
    setSetup_MinAlert(item.minStockAlert || '');
    setSetup_SellingPrice(item.sellingPrice || '');
    setSetup_Unit(item.unit || 'piece');
    setSetup_Category(item.category || '');
    setSetup_ReorderQty(item.reorderQty || '');
    setShowSetup_ProductForm(true);
  };

  const handleExpenseEdit = (item) => {
    setExpense_EditId(item.id);
    setExpense_Name(item.name);
    setExpense_Desc(item.desc);
    setShowExpense_SetupForm(true);
  };

  // C. FOR SUPPLIERS - Handler
  const handleSupplierSubmit = (e) => {
    e.preventDefault();
    if (supplier_Name) {
      if (supplier_EditId) {
        const existingSupp = (suppliers || []).find(s => s.id === supplier_EditId);
        setSuppliers(prev => prev.map(item =>
          item.id === supplier_EditId ? { ...item, name: supplier_Name, contact: supplier_Contact, updatedAt: new Date().toISOString() } : item
        ));
        // MIGRATION-ONLY: sync edit to relational DB
        if (businessId && currentUser?.accessToken && resolveRid(existingSupp)) {
          persistSupplier(businessId, { name: supplier_Name, phone: supplier_Contact, _rid: resolveRid(existingSupp), _rv: existingSupp._rv, rowVersion: existingSupp.rowVersion }, currentUser.accessToken)
            .then(r => { const rid = r?.data?.id; if (rid) setSuppliers(prev => prev.map(s => s.id === supplier_EditId ? { ...s, _rid: rid, _rv: (s._rv || s.rowVersion || 0) + 1 } : s)); })
            .catch(err => console.warn('[MIGRATION-ONLY] supplier update failed:', err.message));
        }
      } else {
        const newSupplier = { id: Date.now(), name: supplier_Name, contact: supplier_Contact, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() };
        setSuppliers(prev => [...prev, newSupplier]);
        // MIGRATION-ONLY dual-write: persist to relational DB
        if (businessId && currentUser?.accessToken) {
          persistSupplier(businessId, newSupplier, currentUser.accessToken).then(r => {
            const rid = r?.data?.id;
            if (rid) setSuppliers(prev => prev.map(s => s.id === newSupplier.id ? { ...s, _rid: rid, _rv: 1 } : s));
          }).catch(err => console.warn('[dual-write] supplier:', err.message));
        }
      }
      setSupplier_Name('');
      setSupplier_Contact('');
      setSupplier_EditId(null);
      setShowSupplier_Form(false);
    }
  };

  const handleSupplierEdit = (item) => {
    setSupplier_EditId(item.id);
    setSupplier_Name(item.name);
    setSupplier_Contact(item.contact || '');
    setShowSupplier_Form(true);
  };

  const handleSupplierDelete = (id) => {
    const sw = language === 'sw';
    showConfirm(
      sw ? 'Una uhakika unataka kuhifadhi muuzaji huyu (archive)? Ataondolewa kwenye orodha mpya lakini historia yake itabaki.' : 'Archive this supplier? They will be hidden from new entry lists, but their history stays intact and resolvable.',
      () => setSuppliers(prev => archiveById(prev, id)),
      { tone: 'error', title: sw ? 'Futa Muuzaji' : 'Delete Supplier' }
    );
  };

  const handleSetupProductDelete = (id) => {
    const sw = language === 'sw';
    showConfirm(
      sw ? 'Una uhakika unataka kuhifadhi bidhaa hii (archive)? Itaondolewa kwenye orodha mpya lakini historia na thamani yake itabaki.' : 'Archive this product? It will be hidden from new sale/purchase lists, but its history, stock valuation and reports stay intact.',
      () => setProducts(prev => archiveById(prev, id)),
      { tone: 'error', title: sw ? 'Futa Bidhaa' : 'Delete Product' }
    );
  };

  const handleExpenseTypeDelete = (id) => {
    const sw = language === 'sw';
    showConfirm(
      sw ? 'Una uhakika unataka kufuta aina hii ya matumizi? Hatua hii haiwezi kutenduliwa.' : 'Are you sure you want to permanently delete this expense category? This cannot be undone.',
      () => setExpenseCategories(prev => softDeleteById(prev, id)),
      { tone: 'error', title: sw ? 'Futa Aina ya Matumizi' : 'Delete Expense Category' }
    );
  };

  const handleCustomerSubmit = (e) => {
    e.preventDefault();
    if (!customer_Name.trim()) return;
    const customerData = { name: customer_Name, phone: customer_Phone, email: customer_Email, address: customer_Address, tin: customer_TIN, vrn: customer_VRN, notes: customer_Notes };
    if (customer_EditId) {
      const existingCust = (customers || []).find(c => c.id === customer_EditId);
      setCustomers(prev => prev.map(c => c.id === customer_EditId ? { ...c, ...customerData, updatedAt: new Date().toISOString() } : c));
      // MIGRATION-ONLY: sync edit to relational DB
      if (businessId && currentUser?.accessToken && resolveRid(existingCust)) {
        persistCustomer(businessId, { ...customerData, _rid: resolveRid(existingCust), _rv: existingCust._rv, rowVersion: existingCust.rowVersion }, currentUser.accessToken)
          .then(r => { const rid = r?.data?.id; if (rid) setCustomers(prev => prev.map(c => c.id === customer_EditId ? { ...c, _rid: rid, _rv: (c._rv || c.rowVersion || 0) + 1 } : c)); })
          .catch(err => console.warn('[MIGRATION-ONLY] customer update failed:', err.message));
      }
    } else {
      const newCust = { id: Date.now(), ...customerData, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() };
      setCustomers(prev => [...prev, newCust]);
      // MIGRATION-ONLY dual-write: persist to relational DB
      if (businessId && currentUser?.accessToken) {
        persistCustomer(businessId, customerData, currentUser.accessToken).then(r => {
          const rid = r?.data?.id;
          if (rid) setCustomers(prev => prev.map(c => c.id === newCust.id ? { ...c, _rid: rid, _rv: 1 } : c));
        }).catch(err => console.warn('[dual-write] customer:', err.message));
      }
    }
    setCustomer_Name(''); setCustomer_Phone(''); setCustomer_Email(''); setCustomer_Address(''); setCustomer_TIN(''); setCustomer_VRN(''); setCustomer_Notes('');
    setCustomer_EditId(null);
    setShowCustomer_Form(false);
  };

  const handleCustomerDelete = (id) => {
    const sw = language === 'sw';
    showConfirm(
      sw ? 'Una uhakika unataka kuhifadhi mteja huyu (archive)? Ataondolewa kwenye orodha mpya lakini historia yake itabaki.' : 'Archive this customer? They will be hidden from new entry lists, but their history stays intact and resolvable.',
      () => setCustomers(prev => archiveById(prev, id)),
      { tone: 'error', title: sw ? 'Futa Mteja' : 'Delete Customer' }
    );
  };

  const handleCustomerEdit = (customer) => {
    setCustomer_EditId(customer.id);
    setCustomer_Name(customer.name);
    setCustomer_Phone(customer.phone || '');
    setCustomer_Email(customer.email || '');
    setCustomer_Address(customer.address || '');
    setCustomer_TIN(customer.tin || '');
    setCustomer_VRN(customer.vrn || '');
    setCustomer_Notes(customer.notes || '');
    setShowCustomer_Form(true);
  };

  // Ledger helpers
  const markSalePaid = (saleId) => {
    setSales(prev => (prev || []).map(s => s.id === saleId ? { ...s, isPaid: true, paymentMethod: s.paymentMethod || 'cash' } : s));
  };
  const markPurchasePaid = (purchaseId) => {
    setPurchases(prev => (prev || []).map(p => p.id === purchaseId ? { ...p, isPaid: true } : p));
  };
  const openLedger = (type, entity) => {
    setLedgerEntity({ type, name: entity.name, contact: entity.contact || entity.phone || '' });
    setShowLedger(true);
  };

  // Service handlers
  const handleServiceSubmit = (e) => {
    e.preventDefault();
    if (!serviceFormData.name.trim()) return;
    if (serviceEditId) {
      const existingSvc = (services || []).find(s => s.id === serviceEditId);
      setServices(prev => prev.map(s =>
        s.id === serviceEditId ? { ...s, name: serviceFormData.name, defaultPrice: serviceFormData.defaultPrice, defaultCost: serviceFormData.defaultCost, updatedAt: new Date().toISOString() } : s
      ));
      // MIGRATION-ONLY: sync edit to relational DB
      if (businessId && currentUser?.accessToken && resolveRid(existingSvc)) {
        persistService(businessId, { name: serviceFormData.name, defaultPrice: serviceFormData.defaultPrice, defaultCost: serviceFormData.defaultCost, _rid: resolveRid(existingSvc), _rv: existingSvc._rv, rowVersion: existingSvc.rowVersion }, currentUser.accessToken)
          .then(r => { const rid = r?.data?.id; if (rid) setServices(prev => prev.map(s => s.id === serviceEditId ? { ...s, _rid: rid, _rv: (s._rv || s.rowVersion || 0) + 1 } : s)); })
          .catch(err => console.warn('[MIGRATION-ONLY] service update failed:', err.message));
      }
    } else {
      const newService = {
        id: Date.now() + Math.floor(Math.random() * 1000),
        name: serviceFormData.name,
        defaultPrice: serviceFormData.defaultPrice,
        defaultCost: serviceFormData.defaultCost,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      setServices(prev => [...prev, newService]);
      // MIGRATION-ONLY dual-write: persist to relational DB
      if (businessId && currentUser?.accessToken) {
        persistService(businessId, newService, currentUser.accessToken).then(r => {
          const rid = r?.data?.id;
          if (rid) setServices(prev => prev.map(s => s.id === newService.id ? { ...s, _rid: rid, _rv: 1 } : s));
        }).catch(err => console.warn('[dual-write] service:', err.message));
      }
    }
    setServiceFormData({ name: '', defaultPrice: '', defaultCost: '' });
    setServiceEditId(null);
    setShowServiceForm(false);
  };

  const handleServiceEdit = (service) => {
    setServiceEditId(service.id);
    setServiceFormData({
      name: service.name,
      defaultPrice: service.defaultPrice,
      defaultCost: service.defaultCost || '',
    });
    setShowServiceForm(true);
  };

  const handleServiceDelete = (id) => {
    const sw = language === 'sw';
    showConfirm(
      sw ? 'Una uhakika unataka kufuta huduma hii? Hatua hii haiwezi kutenduliwa.' : 'Are you sure you want to permanently delete this service? This cannot be undone.',
      () => setServices(prev => softDeleteById(prev, id)),
      { tone: 'error', title: sw ? 'Futa Huduma' : 'Delete Service' }
    );
  };

  // Service Transaction handlers
  const handleServiceTransactionSubmit = (e) => {
    e.preventDefault();
    // SAFETY: if the user filled the draft row but hasn't clicked "+ Add",
    // auto-add it before saving so the line isn't silently lost.
    let workingItems = serviceLineItems;
    const sd = serviceLineDraft;
    if (sd && sd.serviceName && sd.quantity && sd.price) {
      workingItems = [...serviceLineItems, { ...sd, id: Date.now() }];
      setServiceLineItems(workingItems);
      setServiceLineDraft({ serviceId: '', serviceName: '', quantity: '', price: '', cost: '' });
    }
    if (workingItems.length === 0) return;

    // Order-level totals
    let baseRevenue = 0;
    let totalCostAll = 0;
    workingItems.forEach(it => {
      const qty = parseFloat(it.quantity || 0);
      const price = parseFloat(it.price || 0);
      const cost = parseFloat(it.cost || 0);
      baseRevenue += qty * price;
      totalCostAll += qty * cost;
    });

    const discPct = parseFloat(serviceTransactionFormData.discount || 0);
    const discountAmount = baseRevenue * (discPct / 100);
    const afterDiscount = Math.max(0, baseRevenue - discountAmount);
    const taxTotal = (serviceTransactionFormData.taxes || []).reduce((sum, tx) => sum + afterDiscount * (parseFloat(tx.rate || 0) / 100), 0);
    const totalRevenue = Math.max(0, afterDiscount + taxTotal);
    const profit = totalRevenue - totalCostAll;

    // Payments
    const validPayments = (servicePayments || []).filter(p => p.amount && parseFloat(p.amount) > 0);
    const paidPayments = validPayments.filter(p => p.type !== 'credit');
    const creditPayments = validPayments.filter(p => p.type === 'credit');
    const paymentsTotal = paidPayments.reduce((s, p) => s + parseFloat(p.amount), 0);
    const creditTotal = creditPayments.reduce((s, p) => s + parseFloat(p.amount), 0);
    const coverageTotal = paymentsTotal + creditTotal;

    if (Math.round(coverageTotal * 100) < Math.round(totalRevenue * 100)) {
      const remaining = totalRevenue - coverageTotal;
      const sw = language === 'sw';
      showAlert(sw
        ? `Malipo hayajakamilika.\nJumla: ${fmt(totalRevenue)}\nImefunikwa: ${fmt(coverageTotal)}\nBakaza: ${fmt(remaining)}\n\nWeka kiasi kilichobaki kama Mkopo (Credit) au ongeza njia nyingine ya malipo.`
        : `Payment is incomplete.\nTotal: ${fmt(totalRevenue)}\nCovered: ${fmt(coverageTotal)}\nRemaining: ${fmt(remaining)}\n\nPut the remainder on Credit or add another payment method.`,
        { tone: 'warn', title: sw ? 'Malipo hayajakamilika' : 'Payment incomplete' });
      return;
    }
    if (Math.round(coverageTotal * 100) > Math.round(totalRevenue * 100)) {
      const excess = coverageTotal - totalRevenue;
      const sw = language === 'sw';
      showAlert(sw
        ? `Malipo yamezidi jumla.\nJumla mpya: ${fmt(totalRevenue)}\nMalipo: ${fmt(coverageTotal)}\nZiada: ${fmt(excess)}\n\nPunguza Mkopo kwanza, kisha akaunti zingine (pesa/benki/simu), ili malipo yalingane na jumla mpya.`
        : `Payments exceed the total.\nNew total: ${fmt(totalRevenue)}\nPayments cover: ${fmt(coverageTotal)}\nExcess: ${fmt(excess)}\n\nReduce Credit first, then cash/bank/mobile, so the payment lines match the new total.`,
        { tone: 'warn', title: sw ? 'Malipo yamezidi' : 'Payments exceed total' });
      return;
    }
    const isPaid = Math.round(paymentsTotal * 100) >= Math.round(totalRevenue * 100);
    const primaryPaymentMethod = validPayments[0]?.type || 'cash';

    const customerName = (serviceTransactionFormData.customerName || '').trim();
    const customerDetails = serviceCustomerType === 'registered'
      ? (() => {
          const c = (customers || []).find(x => x.name === customerName);
          return c ? { phone: c.phone || '', email: c.email || '', address: c.address || '', tin: c.tin || '', vrn: c.vrn || '' } : {};
        })()
      : {
          phone: serviceTransactionFormData.customerPhone || '',
          email: serviceTransactionFormData.customerEmail || '',
          address: serviceTransactionFormData.customerAddress || '',
          tin: serviceTransactionFormData.customerTIN || '',
          vrn: serviceTransactionFormData.customerVRN || '',
        };

    // First item populates legacy single-item fields for backward compat
    const firstItem = workingItems[0];
    // Mirror keys so the PDF generator (which expects productName / sellingPrice) renders correctly
    const itemsForStorage = workingItems.map(it => ({
      ...it,
      productName: it.serviceName,
      sellingPrice: it.price,
      type: 'service'
    }));
    const transactionData = {
      // Legacy single-item shape (first item)
      serviceId: firstItem.serviceId || null,
      serviceName: firstItem.serviceName,
      quantity: parseFloat(firstItem.quantity || 0),
      price: parseFloat(firstItem.price || 0),
      cost: parseFloat(firstItem.cost || 0),
      // Multi-item array
      items: itemsForStorage,
      // Order-level
      totalRevenue,
      totalCost: totalCostAll,
      profit,
      customerName,
      customerType: serviceCustomerType,
      customerDetails,
      paymentMethod: primaryPaymentMethod,
      payments: validPayments.length > 0 ? validPayments : undefined,
      isPaid,
      discount: serviceTransactionFormData.discount || '',
      discountAmount,
      taxes: serviceTransactionFormData.taxes || [],
      efdReceiptNo: serviceTransactionFormData.efdReceiptNo || '',
      footerSections: serviceFooterSections.filter(s => s.content && s.content.trim()),
      invoiceIssued: !!serviceIssueInvoice || !isPaid, // unpaid always issues an invoice (proof of debt)
      date: serviceTransactionFormData.date || new Date().toISOString().split('T')[0],
      updatedAt: new Date().toISOString(),
    };

    if (serviceTransactionEditId) {
      setServiceTransactions(prev => prev.map(st =>
        st.id === serviceTransactionEditId ? { ...st, ...transactionData } : st
      ));
      // NOTE: No RPC for editing posted service transactions. Local state authoritative during transition.
    } else {
      const nextCounter = serviceInvoiceCounter + 1;
      const invoiceNumber = `SRV-${String(nextCounter).padStart(4, '0')}`;
      const newServiceTransaction = {
        id: Date.now() + Math.floor(Math.random() * 1000),
        invoiceNumber,
        ...transactionData
      };
      setServiceTransactions(prev => [...prev, newServiceTransaction]);
      setServiceInvoiceCounter(nextCounter);
      localStorage.setItem('faida_serviceInvoiceCounter', String(nextCounter));
      // MIGRATION-ONLY dual-write: persist to relational DB
      if (businessId && currentUser?.accessToken) {
        persistServiceTransaction(businessId, newServiceTransaction, currentUser.accessToken, paymentAccounts).then(r => {
          const rid = r?.data?.service_txn_id;
          if (rid) setServiceTransactions(prev => prev.map(st => st.id === newServiceTransaction.id ? { ...st, _rid: rid, _rv: 1 } : st));
        }).catch((err) =>
          console.warn('[MIGRATION-ONLY] service txn persist failed (non-blocking):', err.message));
      }
      setLastSavedServiceTransaction(newServiceTransaction);
      setShowServiceInvoicePreview(true);
    }

    resetServiceForm();
    setShowServiceTransactionForm(false);
  };

  const handleServiceTransactionEdit = (transaction) => {
    setServiceTransactionEditId(transaction.id);
    // Re-hydrate cart from items[] if present, otherwise from legacy single-item fields
    const items = (transaction.items && transaction.items.length > 0)
      ? transaction.items.map((it, i) => ({
          id: it.id || Date.now() + i,
          serviceId: it.serviceId || '',
          serviceName: it.serviceName || it.name || '',
          quantity: String(it.quantity || ''),
          price: String(it.price || ''),
          cost: String(it.cost || '')
        }))
      : [{
          id: Date.now(),
          serviceId: transaction.serviceId || '',
          serviceName: transaction.serviceName || '',
          quantity: String(transaction.quantity || ''),
          price: String(transaction.price || ''),
          cost: String(transaction.cost || '')
        }];
    setServiceLineItems(items);
    setServiceLineDraft({ serviceId: '', serviceName: '', quantity: '', price: '', cost: '' });
    const cd = transaction.customerDetails || {};
    setServiceTransactionFormData({
      serviceId: transaction.serviceId || '',
      serviceNameOverride: transaction.serviceId ? '' : (transaction.serviceName || ''),
      quantity: transaction.quantity,
      price: transaction.price,
      cost: transaction.cost || '',
      customerName: transaction.customerName || '',
      customerPhone: cd.phone || '',
      customerEmail: cd.email || '',
      customerAddress: cd.address || '',
      customerTIN: cd.tin || '',
      customerVRN: cd.vrn || '',
      paymentMethod: transaction.paymentMethod || 'cash',
      date: transaction.date,
      discount: transaction.discount || '',
      efdReceiptNo: transaction.efdReceiptNo || '',
      taxes: transaction.taxes || []
    });
    // Set customer type based on whether customerName matches a registered customer
    const isRegistered = (customers || []).some(c => c.name === transaction.customerName);
    setServiceCustomerType(transaction.customerType || (isRegistered ? 'registered' : 'walkin'));
    // Payments
    if (transaction.payments && transaction.payments.length > 0) {
      setServicePayments(transaction.payments.map(p => ({ type: p.type, accountKey: p.accountKey || p.type, amount: String(p.amount || '') })));
    } else {
      const amt = parseFloat(transaction.totalRevenue || 0);
      setServicePayments([{ type: transaction.paymentMethod || 'cash', accountKey: transaction.paymentMethod || 'cash', amount: amt > 0 ? String(amt) : '' }]);
    }
    setServiceFooterSections(transaction.footerSections || []);
    setServiceIssueInvoice(transaction.invoiceIssued !== false);
    serviceIssueInvoiceTouched.current = true;
    setShowServiceTransactionForm(true);
  };

  const handleVoidServiceTransaction = (id) => {
    const sw = language === 'sw';
    const st = (serviceTransactions || []).find(s => s.id === id);
    if (!st || st.status === 'void') return;
    showConfirm(
      sw ? 'Batilisha shughuli hii ya huduma? Athari za kifedha zitatenduliwa lakini kumbukumbu itabaki ionekane kwa ukaguzi.'
         : 'Void this service transaction? Financial effects will be reversed but the record stays visible for your audit trail.',
      async () => {
        setServiceTransactions(prev => prev.map(s => String(s.id) === String(id)
          ? { ...s, status: 'void', voidedAt: new Date().toISOString(), updatedAt: new Date().toISOString() }
          : s));
        flushVoidToStorage('serviceTransactions', id);
        registerVoid(currentUser?.uid, 'serviceTransactions', id, st?._rid);
        const outcome = await syncVoidToServer({
          label: 'service',
          record: st,
          localId: id,
          call: (rid) => voidServiceTxn(businessId, rid, currentUser.accessToken),
        });
        if (!outcome.ok) { showAlert(voidFailureMessage('service', outcome, sw), { tone: 'error' }); return; }
        showAlert(sw ? 'Shughuli imebatilishwa.' : 'Service transaction voided.', { tone: 'success' });
      },
      { tone: 'error', title: sw ? 'Batilisha Shughuli' : 'Void Transaction' }
    );
  };

  const handleCancelEdit = () => {
    setSetup_EditId(null);
    setExpense_EditId(null);
    setSupplier_EditId(null);
    setCustomer_EditId(null);
    setSetup_Name('');
    setSetup_StockQty('');
    setSetup_StockValue('');
    setSetup_MinAlert('');
    setSetup_SellingPrice('');
    setSetup_Unit('piece');
    setSetup_Category('');
    setSetup_ReorderQty('');
    setExpense_Name('');
    setExpense_Desc('');
    setSupplier_Name('');
    setSupplier_Contact('');
    setCustomer_Name(''); setCustomer_Phone(''); setCustomer_Email(''); setCustomer_Address(''); setCustomer_TIN(''); setCustomer_VRN(''); setCustomer_Notes('');
    setShowSetup_ProductForm(false);
    setShowExpense_SetupForm(false);
    setShowSupplier_Form(false);
    setShowCustomer_Form(false);
  };

  // User-specific localStorage keys
  const getUserKey = useMemo(() => {
    return (key) => currentUser ? `faida_${currentUser.uid}_${key}` : `faida_${key}`;
  }, [currentUser]);
  
  // ── Persist individual settings to localStorage ──────────────────
  useEffect(() => { if (!dataLoaded.current) return; localStorage.setItem(getUserKey('businessName'), businessName); }, [businessName, getUserKey]);
  useEffect(() => { if (!dataLoaded.current) return; localStorage.setItem(getUserKey('businessAddress'), businessAddress); }, [businessAddress, getUserKey]);
  useEffect(() => { if (!dataLoaded.current) return; localStorage.setItem(getUserKey('businessTIN'), businessTIN); }, [businessTIN, getUserKey]);
  useEffect(() => { if (!dataLoaded.current) return; localStorage.setItem(getUserKey('businessVRN'), businessVRN); }, [businessVRN, getUserKey]);
  useEffect(() => { if (!dataLoaded.current) return; localStorage.setItem(getUserKey('businessRegNo'), businessRegNo); }, [businessRegNo, getUserKey]);
  useEffect(() => { if (!dataLoaded.current) return; localStorage.setItem(getUserKey('tradeLicenseNo'), tradeLicenseNo); }, [tradeLicenseNo, getUserKey]);
  useEffect(() => { if (!dataLoaded.current) return; localStorage.setItem(getUserKey('invoiceTerms'), invoiceTerms); }, [invoiceTerms, getUserKey]);
  useEffect(() => {
    if (!dataLoaded.current) return;
    if (businessLogo) localStorage.setItem(getUserKey('businessLogo'), businessLogo);
    else localStorage.removeItem(getUserKey('businessLogo'));
  }, [businessLogo, getUserKey]);

  // Save logo URL to Supabase when it changes (optional - only if Supabase Storage is set up)
  useEffect(() => {
    if (!dataLoaded.current || !businessLogoUrl) return;
    if (!currentUser || !currentUser.accessToken) return;
    // Only sync if URL is from Supabase (starts with https://)
    if (!businessLogoUrl.startsWith('https://')) return;
    const settings = { businessLogoUrl };
    syncCollection(currentUser.uid, currentUser.accessToken, 'settings', settings).catch(e => console.warn('Optional: Failed to sync logo URL:', e));
  }, [businessLogoUrl, currentUser]);
  useEffect(() => { if (!dataLoaded.current) return; localStorage.setItem(getUserKey('bankName'), bankName); }, [bankName, getUserKey]);
  useEffect(() => { if (!dataLoaded.current) return; localStorage.setItem(getUserKey('bankAccountName'), bankAccountName); }, [bankAccountName, getUserKey]);
  useEffect(() => { if (!dataLoaded.current) return; localStorage.setItem(getUserKey('bankAccountNumber'), bankAccountNumber); }, [bankAccountNumber, getUserKey]);
  useEffect(() => { if (!dataLoaded.current) return; localStorage.setItem(getUserKey('mobileMoneyProvider'), mobileMoneyProvider); }, [mobileMoneyProvider, getUserKey]);
  useEffect(() => { if (!dataLoaded.current) return; localStorage.setItem(getUserKey('mobileMoneyNumber'), mobileMoneyNumber); }, [mobileMoneyNumber, getUserKey]);
  // ── SAFE DATA PERSISTENCE ─────────────────────────────────────────
  // The `dataLoaded.current` gate already prevents the initial-mount empty
  // state from wiping localStorage before the loader has run. We intentionally
  // DO write empty arrays once dataLoaded is true -that's the user explicitly
  // deleting their last item, and refusing to persist would resurrect the
  // deleted records on the next refresh (the bug we were hitting).
  const safelyPersist = (key, data) => {
    if (!dataLoaded.current) return; // wait until initial localStorage load finishes
    if (!key || !getUserKey) return;
    try {
      localStorage.setItem(getUserKey(key), typeof data === 'string' ? data : JSON.stringify(data));
    } catch (e) {
      console.error(`❌ Failed to save ${key}:`, e);
    }
  };

  useEffect(() => { safelyPersist('paymentAccounts', paymentAccountsRaw); }, [paymentAccountsRaw, getUserKey]);
  useEffect(() => { if (!dataLoaded.current) return; localStorage.setItem(getUserKey('businessType'), businessType); }, [businessType, getUserKey]);
  useEffect(() => { if (!dataLoaded.current) return; localStorage.setItem(getUserKey('currency'), currency); }, [currency, getUserKey]);
  useEffect(() => { try { localStorage.setItem('faida_activeTab', activeTab); } catch(e) {} }, [activeTab]);

  // ── SCROLL TO TOP ON SCREEN CHANGE ────────────────────────────────
  // Every tab/screen switch starts at the very top (back arrow visible)
  // instead of inheriting the previous screen's scroll position.
  useEffect(() => {
    try {
      window.scrollTo(0, 0);
      const mc = document.querySelector('.main-content');
      if (mc) mc.scrollTop = 0;
    } catch (e) {}
  }, [activeTab, setupScreen, settingsScreen]);

  // Auto-load team data when team screen opens
  useEffect(() => {
    if (settingsScreen !== 'team' || !businessId || !currentUser?.accessToken) return;
    setTeamLoading(true);
    setTeamError('');
    Promise.all([
      fetchTeamMembers(businessId, currentUser.accessToken),
      fetchAvailableRoles(currentUser.accessToken),
    ]).then(([teamData, roles]) => {
      setTeamMembers(teamData?.members || []);
      setTeamInvitations(teamData?.invitations || []);
      setTeamRoles(roles || []);
      if (roles?.length) setTeamInviteRole(prev => prev || roles[roles.length - 1]?.id || '');
    }).catch(err => {
      setTeamError(err?.message || 'Failed to load team data');
    }).finally(() => setTeamLoading(false));
  }, [settingsScreen, businessId, currentUser?.accessToken]);

  // ── PERSIST CURRENT VIEW ACROSS REFRESH ───────────────────────────────
  // Snapshots which sub-screen the user is on (list / report / drilldown /
  // settings sub-screen) so a browser refresh returns them to the same place
  // instead of dumping them on the tab home. Form modals (Record Sale, Record
  // Return, etc.) are intentionally NOT restored -those have transient input
  // that wouldn't survive the reload anyway.
  const viewRestoredRef = useRef(false);
  // Restore FIRST (runs once on mount, before any persist could overwrite it)
  useEffect(() => {
    try {
      const raw = localStorage.getItem('faida_currentView');
      if (raw) {
        const v = JSON.parse(raw);
        if (v.proformaList) setShowProformaListScreen(true);
        if (v.issuedList) setShowIssuedListScreen(true);
        if (v.manualInvoiceList) setShowManualInvoiceListScreen(true);
        if (v.invoiceManagement) setShowInvoiceManagement(true);
        if (v.invoiceManagementSection) setInvoiceManagementSection(v.invoiceManagementSection);
        if (v.salesReport) setIsSalesReportOpen(true);
        if (v.purchasesReport) setIsPurchasesReportOpen(true);
        if (v.expensesReport) setIsExpensesReportOpen(true);
        if (v.returnsReport) setShowReturnsReport(true);
        if (v.purchaseReturnsReport) setShowPurchaseReturnsReport(true);
        if (v.drilldown) setActiveDrilldown(v.drilldown);
        if (v.settingsScreen) setSettingsScreen(v.settingsScreen);
      }
    } catch (e) {}
    // Unlock persisting AFTER the restore values have been applied.
    // (One render tick is enough -the persist effect below sees true on its next fire.)
    viewRestoredRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!viewRestoredRef.current) return; // skip until restore has had its chance
    try {
      const view = {
        proformaList: showProformaListScreen,
        issuedList: showIssuedListScreen,
        manualInvoiceList: showManualInvoiceListScreen,
        invoiceManagement: showInvoiceManagement,
        invoiceManagementSection,
        salesReport: isSalesReportOpen,
        purchasesReport: isPurchasesReportOpen,
        expensesReport: isExpensesReportOpen,
        returnsReport: showReturnsReport,
        purchaseReturnsReport: showPurchaseReturnsReport,
        drilldown: activeDrilldown,
        settingsScreen,
      };
      localStorage.setItem('faida_currentView', JSON.stringify(view));
    } catch (e) {}
  }, [
    showProformaListScreen, showIssuedListScreen, showManualInvoiceListScreen,
    showInvoiceManagement, invoiceManagementSection,
    isSalesReportOpen, isPurchasesReportOpen, isExpensesReportOpen,
    showReturnsReport, showPurchaseReturnsReport,
    activeDrilldown, settingsScreen,
  ]);
  useEffect(() => {
    localStorage.setItem(getUserKey('subscription'), JSON.stringify(subscriptionData));
    if (currentUser && subscriptionData) saveSubscription(currentUser.uid, subscriptionData);
  }, [subscriptionData, getUserKey, currentUser]);
  useEffect(() => { safelyPersist('services', servicesRaw); }, [servicesRaw, getUserKey]);
  // serviceTransactions persistence is handled by AppContext (syncs to Supabase too)

  // ── One-time data migration: repair service items[] missing productName/sellingPrice ─
  // Some records were saved before key-mirroring was added -patch them in place so the
  // View modal, PDF, reports, and edit form all read the same shape as product sales.
  const _svcMigratedRef = useRef(false);
  useEffect(() => {
    if (_svcMigratedRef.current) return;
    if (!Array.isArray(serviceTransactions) || serviceTransactions.length === 0) return;
    let needsPatch = false;
    const patched = serviceTransactions.map(st => {
      let txDirty = false;
      // Fix items[] keys
      const newItems = Array.isArray(st.items) ? st.items.map(it => {
        if (!it) return it;
        const hasPN = it.productName != null && it.productName !== '' && it.productName !== '—';
        const hasSP = it.sellingPrice != null && it.sellingPrice !== '';
        const wantPN = !hasPN && (it.serviceName || it.name);
        const wantSP = !hasSP && (it.price != null && it.price !== '');
        if (wantPN || wantSP) {
          txDirty = true;
          return {
            ...it,
            productName: hasPN ? it.productName : (it.serviceName || it.name || it.productName),
            sellingPrice: hasSP ? it.sellingPrice : (it.price != null ? it.price : it.sellingPrice),
            type: it.type || 'service',
          };
        }
        return it;
      }) : st.items;
      // For records with NO items[] but with legacy single-item fields, synthesize one
      let finalItems = newItems;
      if (!Array.isArray(newItems) && (st.serviceName || st.price)) {
        // leave as-is; View modal already handles the single-item fallback
      }
      if (txDirty) {
        needsPatch = true;
        return { ...st, items: finalItems };
      }
      return st;
    });
    _svcMigratedRef.current = true;
    if (needsPatch) {
      setServiceTransactions(patched);
    }
  }, [serviceTransactions, setServiceTransactions]);
  useEffect(() => { safelyPersist('proformaInvoices', proformaInvoicesRaw); }, [proformaInvoicesRaw, getUserKey]);
  useEffect(() => { safelyPersist('issuedInvoices', issuedInvoicesRaw); }, [issuedInvoicesRaw, getUserKey]);
  useEffect(() => { safelyPersist('manualInvoices', manualInvoicesRaw); }, [manualInvoicesRaw, getUserKey]);
  useEffect(() => { safelyPersist('salesReturns', salesReturnsRaw); }, [salesReturnsRaw, getUserKey]);
  useEffect(() => { safelyPersist('purchaseReturns', purchaseReturnsRaw); }, [purchaseReturnsRaw, getUserKey]);
  useEffect(() => { safelyPersist('stockAdjHistory', stockAdjHistory); }, [stockAdjHistory, getUserKey]);
  useEffect(() => { safelyPersist('recurringExpenses', recurringExpenses); }, [recurringExpenses, getUserKey]);

  // Flag set true only AFTER cloud settings have been loaded (or load definitively failed).
  // Prevents the empty-initial-state from being synced UP before we've heard back from cloud,
  // which used to wipe paymentAccounts and other settings on fresh-browser logins.
  const [settingsCloudLoaded, setSettingsCloudLoaded] = useState(false);

  // ── Sync all business settings to Supabase (debounced 3s) ────────
  // Bundles all scalar settings into one 'settings' collection row.
  // GATED on settingsCloudLoaded to prevent overwriting cloud data with the empty initial state.
  useEffect(() => {
    if (!currentUser?.uid || !currentUser?.accessToken) return;
    if (!settingsCloudLoaded) return; // wait for cloud load before syncing UP
    const timer = setTimeout(() => {
      const settings = {
        businessName, businessAddress, businessLogo,
        businessTIN, businessVRN, businessRegNo, tradeLicenseNo,
        bankName, bankAccountName, bankAccountNumber,
        mobileMoneyProvider, mobileMoneyNumber,
        paymentAccounts: paymentAccountsRaw, businessType, currency,
        businessTypeUpdatedAt: localStorage.getItem(getUserKey('businessTypeUpdatedAt')) || null,
        trialStartedAt: localStorage.getItem(getUserKey('trialStartedAt')) || null,
        // Privacy Mode config (configuration only -unlock state stays local)
        privacyEnabled, privacyPinHash, privacyRecoveryCodeHash,
        privacyHiddenTabs, privacyHiddenItems, privacyAutoLockMinutes,
      };
      syncCollection(currentUser.uid, currentUser.accessToken, 'settings', settings)
        .then(() => console.log('✅ Settings synced to cloud'))
        .catch(err => console.error('❌ Settings sync failed:', err));
    }, 3000);
    return () => clearTimeout(timer);
  }, [currentUser, settingsCloudLoaded, businessName, businessAddress, businessLogo,
      businessTIN, businessVRN, businessRegNo, tradeLicenseNo,
      bankName, bankAccountName, bankAccountNumber,
      mobileMoneyProvider, mobileMoneyNumber,
      paymentAccounts, businessType, currency,
      privacyEnabled, privacyPinHash, privacyRecoveryCodeHash,
      privacyHiddenTabs, privacyHiddenItems, privacyAutoLockMinutes]);

  // ── Immediate cloud save for businessType + currency + privacy config (no debounce) ──
  // These are toggled/picked, not typed character-by-character -a rapid reload within
  // the 3s debounce window would let the stale cloud value stomp the fresh pick.
  // Every other setting keeps the debounced sync above.
  useEffect(() => {
    if (!currentUser?.uid || !currentUser?.accessToken) return;
    if (!settingsCloudLoaded) return;
    const settings = {
      businessName, businessAddress, businessLogo,
      businessTIN, businessVRN, businessRegNo, tradeLicenseNo,
      bankName, bankAccountName, bankAccountNumber,
      mobileMoneyProvider, mobileMoneyNumber,
      paymentAccounts: paymentAccountsRaw, businessType, currency,
      businessTypeUpdatedAt: localStorage.getItem(getUserKey('businessTypeUpdatedAt')) || null,
      trialStartedAt: localStorage.getItem(getUserKey('trialStartedAt')) || null,
      privacyEnabled, privacyPinHash, privacyRecoveryCodeHash,
      privacyHiddenTabs, privacyHiddenItems, privacyAutoLockMinutes,
    };
    syncCollection(currentUser.uid, currentUser.accessToken, 'settings', settings)
      .catch(err => console.error('Immediate settings sync failed:', err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessType, currency, privacyEnabled, privacyPinHash, privacyRecoveryCodeHash,
      privacyHiddenTabs, privacyHiddenItems, privacyAutoLockMinutes]);

  // ── Sync services, service transactions, invoices ────────────────
  useEffect(() => {
    if (!currentUser?.uid || !currentUser?.accessToken) return;
    // RACE GUARD: same fix as proformaInvoices -wait for cloud load.
    if (!settingsCloudLoaded) return;
    const timer = setTimeout(() => {
      // Sync the RAW array (with tombstones) so deletions propagate to the cloud.
      syncCollection(currentUser.uid, currentUser.accessToken, 'services', servicesRaw)
        .then(() => console.log('✅ Services synced to cloud'))
        .catch(err => console.error('❌ Services sync failed:', err));
    }, 2000);
    return () => clearTimeout(timer);
  }, [currentUser, settingsCloudLoaded, servicesRaw]);

  // serviceTransactions sync handled by AppContext

  useEffect(() => {
    if (!currentUser?.uid || !currentUser?.accessToken) return;
    // RACE GUARD: wait for the cloud-load to complete before pushing UP.
    // Without this, the initial empty state would overwrite cloud proformas/invoices
    // after the debounce, and we'd then re-load the empty cloud on next open.
    if (!settingsCloudLoaded) return;
    // 500ms debounce (was 2000ms) -narrows the window where the cloud copy
    // could diverge from local if the user navigates/refreshes right after a
    // delete or save. Local-first policy still protects against full data loss.
    const timer = setTimeout(() => {
      // RAW arrays so tombstones (deletions) reach the cloud and cannot be
      // resurrected by another device.
      syncCollection(currentUser.uid, currentUser.accessToken, 'proformaInvoices', proformaInvoicesRaw);
      syncCollection(currentUser.uid, currentUser.accessToken, 'issuedInvoices', issuedInvoicesRaw);
      syncCollection(currentUser.uid, currentUser.accessToken, 'manualInvoices', manualInvoicesRaw);
      // RETURNS are financially significant (stock, COGS, refunds, AR/AP) -now
      // first-class synced collections with tombstones, not local-only.
      syncCollection(currentUser.uid, currentUser.accessToken, 'salesReturns', salesReturnsRaw);
      syncCollection(currentUser.uid, currentUser.accessToken, 'purchaseReturns', purchaseReturnsRaw);
    }, 500);
    return () => clearTimeout(timer);
  }, [currentUser, settingsCloudLoaded, proformaInvoicesRaw, issuedInvoicesRaw, manualInvoicesRaw, salesReturnsRaw, purchaseReturnsRaw]);

  // Dynamic currency formatter using current state
  const fmt = (value) => formatCurrency(value, currency);

  // ── Reusable period-preset buttons (Today / Week / Month / Year) ──────
  // Renders 4 quick-set buttons above any pair of date inputs. Calling
  // `onChange(from, to)` updates the consumer's state. The buttons compute
  // standard ranges (today; current ISO week Mon-Sun; current calendar month;
  // current calendar year). Custom is implicit -user can edit the date
  // inputs directly to fall out of any preset.
  // ── Privacy Mode helpers ──────────────────────────────────────────
  // Master gate: is privacy actively hiding things right now?
  // Requires a PIN to be set -otherwise the user could get locked out with
  // no way to unlock, and turning master ON without a PIN is meaningless.
  const privacyActive = privacyEnabled && !privacyUnlocked && !!privacyPinHash;
  // Is a whole tab hidden right now?
  const isTabHidden = (tabKey) => privacyActive && Array.isArray(privacyHiddenTabs) && privacyHiddenTabs.includes(tabKey);
  // Is a specific item within a tab hidden right now?
  const isItemHidden = (tabKey, itemKey) => {
    if (!privacyActive) return false;
    if (isTabHidden(tabKey)) return true; // whole tab hidden ⇒ every item hidden
    const items = privacyHiddenItems?.[tabKey];
    return Array.isArray(items) && items.includes(itemKey);
  };

  const periodPresetBar = (currentFrom, currentTo, onChange) => {
    const fmtDate = (d) => d.toISOString().split('T')[0];
    const todayStr = fmtDate(new Date());
    const setRange = (preset) => {
      const now = new Date();
      const y = now.getFullYear();
      const m = now.getMonth();
      const d = now.getDate();
      if (preset === 'today') {
        onChange(todayStr, todayStr);
      } else if (preset === 'week') {
        const dow = now.getDay(); // 0 = Sunday
        const monday = new Date(y, m, d - (dow === 0 ? 6 : dow - 1));
        const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6);
        onChange(fmtDate(monday), fmtDate(sunday));
      } else if (preset === 'month') {
        const m1 = new Date(y, m, 1);
        const m2 = new Date(y, m + 1, 0);
        onChange(fmtDate(m1), fmtDate(m2));
      } else if (preset === 'year') {
        onChange(`${y}-01-01`, `${y}-12-31`);
      }
    };
    // Determine which preset (if any) the current range matches so the active
    // pill highlights correctly.
    const matchPreset = (() => {
      if (currentFrom === todayStr && currentTo === todayStr) return 'today';
      const now = new Date();
      const y = now.getFullYear();
      if (currentFrom === `${y}-01-01` && currentTo === `${y}-12-31`) return 'year';
      const m = now.getMonth();
      const m1 = fmtDate(new Date(y, m, 1));
      const m2 = fmtDate(new Date(y, m + 1, 0));
      if (currentFrom === m1 && currentTo === m2) return 'month';
      const dow = now.getDay();
      const monday = new Date(y, m, now.getDate() - (dow === 0 ? 6 : dow - 1));
      const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6);
      if (currentFrom === fmtDate(monday) && currentTo === fmtDate(sunday)) return 'week';
      return 'custom';
    })();
    const labels = { today: t.today || 'Today', week: t.week || 'Week', month: t.month || 'Month', year: t.year || 'Year', custom: t.custom || 'Custom' };
    return (
      <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
        {['today', 'week', 'month', 'year', 'custom'].map(p => (
          <button key={p} type="button" onClick={() => p !== 'custom' && setRange(p)}
            disabled={p === 'custom'}
            style={{ flex: 1, padding: '10px 6px', borderRadius: 16, fontSize: 12, fontWeight: matchPreset === p ? 700 : 600, cursor: p === 'custom' ? 'default' : 'pointer', border: 'none', whiteSpace: 'nowrap',
              background: matchPreset === p ? 'var(--bg-tab-active)' : 'var(--bg-tab-inactive)',
              color: matchPreset === p ? 'var(--text-tab-active)' : 'var(--text-tab-inactive)',
              opacity: p === 'custom' && matchPreset !== 'custom' ? 0.55 : 1,
              transition: 'background 0.15s, color 0.15s' }}>
            {labels[p]}
          </button>
        ))}
      </div>
    );
  };

  // Weighted Average Cost for a given product name (case + whitespace-insensitive)
  // Single source of truth -the perpetual moving-average WAC.
  const wacForProduct = (productName, asOf = null) => getProductWAC(productName, asOf);

  // Recompute purchase/sale grand total from stored items + taxes + discount.
  // Falls back to the stored totalCost / totalRevenue when items[] not present.
  // Use this in reports so historical records (saved before the tax-inclusive
  // totalCost fix) still display the right amount on screen.
  const getPurchaseGrandTotal = (p) => {
    if (Array.isArray(p?.items) && p.items.length > 0) {
      const subTotal = p.items.reduce((s, it) => s + parseFloat(it.totalCost || 0), 0);
      const discPct = parseFloat(p.discount || 0);
      const afterDisc = Math.max(0, subTotal - subTotal * (discPct / 100));
      const tax = (p.taxes || []).reduce((s, t) => s + afterDisc * (parseFloat(t.rate || 0) / 100), 0);
      const computed = afterDisc + tax;
      // Prefer the larger of stored vs computed in case a stored value is already grand total
      const stored = parseFloat(p?.totalCost || 0);
      return Math.max(stored, computed);
    }
    return parseFloat(p?.totalCost || 0);
  };
  const getSaleGrandTotal = (s) => {
    if (Array.isArray(s?.items) && s.items.length > 0) {
      const subTotal = s.items.reduce((sum, it) => {
        const qty = it.type === 'service' ? 1 : parseFloat(it.quantity || 0);
        return sum + qty * parseFloat(it.sellingPrice || 0);
      }, 0);
      const discPct = parseFloat(s.discount || 0);
      const afterDisc = Math.max(0, subTotal - subTotal * (discPct / 100));
      const tax = (s.taxes || []).reduce((acc, t) => acc + afterDisc * (parseFloat(t.rate || 0) / 100), 0);
      const computed = afterDisc + tax;
      const stored = parseFloat(s?.totalRevenue || 0);
      return Math.max(stored, computed);
    }
    return parseFloat(s?.totalRevenue || 0);
  };
  // ── VAT / tax portion of a transaction ────────────────────────────────
  // The tax amount inside a sale/purchase grand total (tax is charged on the
  // discounted subtotal). Output VAT (on sales) is money collected FOR TRA —
  // not income -so profit is computed on the ex-VAT amount and the VAT is
  // reported separately. Input VAT (on purchases) is what you paid suppliers.
  // Output/Input VAT -delegates to the single pure helper in moneyMath.js so
  // the VAT Report, the Business Statement, and the dashboard KPI all compute
  // VAT the SAME way (from the pre-tax base, VAT-type taxes only) and therefore
  // can never diverge. (Previously the VAT Report multiplied the VAT-inclusive
  // grand total by the rate, overstating VAT -see moneyMath.js saleVAT.)
  const getSaleTax = (s) => saleVAT(s);
  const getPurchaseTax = (p) => purchaseVAT(p);
  // Outstanding (credit still owed) on a single sale -the SAME formula the
  // receivables use: grand total minus real payments minus credit knocked off
  // by returns. A fully-paid sale owes 0; a partial owes only its balance.
  const saleOutstanding = (s) => {
    if (!s || s.isPaid !== false) return 0;
    const total = getSaleGrandTotal(s);
    const cashPaid = Array.isArray(s.payments)
      ? s.payments.filter(p => p.type !== 'credit').reduce((a, p) => a + parseFloat(p.amount || 0), 0)
      : 0;
    const reducedByReturns = parseFloat(s.creditReducedByReturns || 0);
    return Math.max(0, total - cashPaid - reducedByReturns);
  };

  // Cost of goods on a customer return. Accounting-correct: this reverses the
  // ORIGINAL sale's recorded cost snapshot (returned qty × the per-unit cost
  // captured when the sale was made), NOT the current WAC -which may have
  // drifted since the sale and would distort profit. Preference order:
  // 1) the return item's own snapshot (unitCost), 2) the original sale item's
  // cost, 3) current WAC as a last-resort fallback for legacy data.
  const returnGoodsCost = (r) => {
    if (!r) return 0;
    const its = (r.items && r.items.length > 0)
      ? r.items
      : [{ productName: r.productName, qty: r.qty, unitCost: r.unitCost, type: r.type }];
    const origSale = (sales || []).find(s => String(s.id) === String(r.originalSaleId));
    const origItems = origSale
      ? (Array.isArray(origSale.items) && origSale.items.length > 0
          ? origSale.items
          : [{ productName: origSale.productName, cost: origSale.cost }])
      : [];
    const norm = (n) => String(n || '').trim().toLowerCase();
    return its.reduce((a, it) => {
      if (it.type === 'service') return a; // services carry no COGS
      const q = parseFloat(it.qty || 0);
      let unit = parseFloat(it.unitCost || 0);
      if (!unit) {
        const m = origItems.find(oi => norm(oi.productName) === norm(it.productName));
        if (m) unit = parseFloat(m.cost || 0);
      }
      if (!unit) unit = getProductWAC(it.productName); // legacy fallback
      return a + q * unit;
    }, 0);
  };

  // Operating cash flow -a wallet-to-wallet TRANSFER (mt.transfer) is internal,
  // not business cash in/out, so its legs are excluded here (they would otherwise
  // fabricate an equal inflow + outflow). Wallet balances/statements still show
  // both legs. Mirrors cashInPeriod's default in cashLedger.js.
  const cashflowInflow = useMemo(() => {
    const salesIn = (sales || []).filter(s => s.isPaid !== false).reduce((sum, s) => sum + parseFloat(s.totalRevenue || 0), 0);
    const svcIn = (serviceTransactions || []).reduce((sum, st) => sum + parseFloat(st.totalRevenue || 0), 0);
    const manualIn = (manualTransactions || []).filter(mt => mt.type === 'credit' && !mt.transfer).reduce((sum, mt) => sum + parseFloat(mt.amount || 0), 0);
    return salesIn + svcIn + manualIn;
  }, [sales, serviceTransactions, manualTransactions]);

  const cashflowOutflow = useMemo(() => {
    const purchasesOut = (purchases || []).filter(p => p.isPaid !== false).reduce((sum, p) => sum + parseFloat(p.totalCost || 0), 0);
    const expensesOut = (expenses || []).filter(e => e.isPaid !== false).reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);
    const manualOut = (manualTransactions || []).filter(mt => mt.type !== 'credit' && !mt.transfer).reduce((sum, mt) => sum + parseFloat(mt.amount || 0), 0);
    return purchasesOut + expensesOut + manualOut;
  }, [purchases, expenses, manualTransactions]);

  const netCashflowKpi = useMemo(() => {
    return cashflowInflow - cashflowOutflow;
  }, [cashflowInflow, cashflowOutflow]);

  // Build payment options from registered accounts, falling back to generic labels
  const buildPaymentOptions = (includeCredit = true) => {
    // Each option has:
    //   value      -UNIQUE per registered account (used as dropdown value + uniqueness key)
    //   type       -broad category ('cash'/'bank'/'mobilemoney'/'credit') for balance lookups
    //   label      -display
    // Two CRDB accounts get two distinct values; you can pick them both on different rows.
    const opts = [{ value: 'cash', type: 'cash', label: t.cash }];
    if ((paymentAccounts || []).length > 0) {
      (paymentAccounts || []).filter(a => a.type === 'bank').forEach(a => {
        opts.push({ value: `bank|${a.id}`, type: 'bank', label: `${a.bankName} · ${a.accountName}` });
      });
      (paymentAccounts || []).filter(a => a.type === 'mobilemoney').forEach(a => {
        opts.push({ value: `mobilemoney|${a.id}`, type: 'mobilemoney', label: `${a.networkName} · ${a.accountName}` });
      });
    } else {
      opts.push({ value: 'bank', type: 'bank', label: t.bank });
      opts.push({ value: 'mobilemoney', type: 'mobilemoney', label: t.mobileMoney });
    }
    if (includeCredit) opts.push({ value: 'credit', type: 'credit', label: `${t.credit}` });
    return opts;
  };

  // Convert a payment-row to its broad base category (cash/bank/mobilemoney/credit).
  // Handles both new composite types ('bank|abc123') and legacy ('bank') values.
  const baseType = (v) => (typeof v === 'string' ? v.split('|')[0] : v);

  // ── Wallet balance: current available balance for a specific account ──
  // accountKey is the composite from buildPaymentOptions: 'cash', 'bank|<id>',
  // 'mobilemoney|<id>', or legacy bare 'bank'/'mobilemoney' (no sub-account).
  // Per-account available balance -now derived from the SINGLE cash ledger,
  // the same engine the dashboard uses, so the two can never disagree. Handles
  // partial payments, refunds (cash only), healed old collections, and excludes
  // bridge/double-count entries automatically. Kept as a function so the many
  // callers don't need to change.
  const getWalletBalance = (accountKey) => {
    if (!accountKey || accountKey === 'credit') return 0;
    // Available NOW = today-capped (a future-dated entry isn't spendable yet).
    return ledgerWalletBalance(cashLedgerData.ledgerToDate, accountKey);
  };

  const handlePayFormSave = () => {
    const errs = {};
    if (!payForm.accountName.trim()) errs.accountName = language === 'sw' ? 'Jina la akaunti linahitajika' : 'Account name is required';
    if (payForm.type === 'bank' && !payForm.bankName.trim()) errs.bankName = language === 'sw' ? 'Jina la benki linahitajika' : 'Bank name is required';
    if (payForm.type === 'mobilemoney' && !payForm.networkName.trim()) errs.networkName = language === 'sw' ? 'Jina la mtandao linahitajika' : 'Network name is required';
    // cash type only needs accountName (already validated above)
    if (payForm.openingBalance === '' || payForm.openingBalance === null) errs.openingBalance = language === 'sw' ? 'Salio la awali linahitajika' : 'Opening balance is required';
    if (!payForm.openingBalanceDate) errs.openingBalanceDate = language === 'sw' ? 'Tarehe ya salio la awali inahitajika' : 'Opening balance date is required';
    if (Object.keys(errs).length > 0) { setPayFormErrors(errs); return; }
    setPayFormErrors({});
    const resetPayForm = { type: 'bank', bankName: '', networkName: '', accountName: '', accountNumber: '', phoneNumber: '', openingBalance: '', openingBalanceDate: '' };
    if (payEditId) {
      const existingAcct = (paymentAccounts || []).find(a => a.id === payEditId);
      setPaymentAccounts(prev => prev.map(a => a.id === payEditId ? { ...payForm, id: payEditId, updatedAt: new Date().toISOString() } : a));
      // MIGRATION-ONLY: sync edit to relational DB
      if (businessId && currentUser?.accessToken && resolveRid(existingAcct)) {
        persistPaymentAccount(businessId, { ...payForm, _rid: resolveRid(existingAcct), _rv: existingAcct._rv, rowVersion: existingAcct.rowVersion }, currentUser.accessToken)
          .then(r => { const rid = r?.data?.id; if (rid) setPaymentAccounts(prev => prev.map(a => a.id === payEditId ? { ...a, _rid: rid, _rv: (a._rv || a.rowVersion || 0) + 1 } : a)); })
          .catch(err => console.warn('[MIGRATION-ONLY] paymentAccount update failed:', err.message));
      }
    } else {
      const newAcct = { ...payForm, id: Date.now().toString(), createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() };
      setPaymentAccounts(prev => [...prev, newAcct]);
      // MIGRATION-ONLY dual-write: persist to relational DB
      if (businessId && currentUser?.accessToken) {
        persistPaymentAccount(businessId, newAcct, currentUser.accessToken).then(r => {
          const rid = r?.data?.id;
          if (rid) setPaymentAccounts(prev => prev.map(a => a.id === newAcct.id ? { ...a, _rid: rid, _rv: 1 } : a));
        }).catch(err => console.warn('[dual-write] paymentAccount:', err.message));
      }
    }
    setShowPayForm(false);
    setPayEditId(null);
    setPayForm(resetPayForm);
  };

  const handlePayEdit = (account) => {
    setPayForm({ ...account });
    setPayEditId(account.id);
    setShowPayForm(true);
  };

  const handlePayDelete = (id) => {
    const sw = language === 'sw';
    showConfirm(
      sw ? 'Una uhakika unataka kufuta akaunti hii ya malipo? Hatua hii haiwezi kutenduliwa.' : 'Are you sure you want to permanently delete this payment account? This cannot be undone.',
      () => setPaymentAccounts(prev => softDeleteById(prev, id)),
      { tone: 'error', title: sw ? 'Futa Akaunti' : 'Delete Account' }
    );
  };

  // ── Self-heal invoice / proforma counters ────────────────────────────
  // The counters live in localStorage, so on a new device -or after a data
  // clear -they can reset to 0 while synced sales still hold INV-0007, etc.
  // Reissuing a used number would corrupt tax records. This effect bumps each
  // counter to the highest number already present, so the next issued number
  // is always unique. (Trailing digits are read regardless of prefix.)
  useEffect(() => {
    const maxNum = (arr) => (arr || []).reduce((m, x) => {
      const mm = String(x?.invoiceNumber || '').match(/(\d+)\s*$/);
      return mm ? Math.max(m, parseInt(mm[1], 10) || 0) : m;
    }, 0);
    const highestInv = Math.max(maxNum(sales), maxNum(issuedInvoices), maxNum(manualInvoices));
    if (highestInv > invoiceCounter) {
      setInvoiceCounter(highestInv);
      try { localStorage.setItem(getUserKey('invoiceCounter'), String(highestInv)); } catch {}
    }
    const highestPro = maxNum(proformaInvoices);
    if (highestPro > proformaCounter) {
      setProformaCounter(highestPro);
      try { localStorage.setItem(getUserKey('proformaCounter'), String(highestPro)); } catch {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sales, issuedInvoices, manualInvoices, proformaInvoices]);

  // Authentication handlers
  // Recovery-token detection runs SYNCHRONOUSLY at module load (see
  // extractRecoveryTokenAtLoad above). This effect only surfaces the result
  // to the UI and handles error/PKCE cases that need user-facing messaging.
  useEffect(() => {
    if (!INITIAL_RECOVERY) {
      console.log('⌕ [RECOVERY] No recovery payload at mount -normal app flow.');
      return;
    }
    console.log('⌕ [RECOVERY] mount -payload:', INITIAL_RECOVERY);
    if (INITIAL_RECOVERY.token) {
      // Already set via lazy initial state; just clear any error.
      setAuthError('');
    } else if (INITIAL_RECOVERY.error) {
      setAuthScreen('forgot');
      setAuthError(INITIAL_RECOVERY.error);
    } else if (INITIAL_RECOVERY.source === 'pkce') {
      setAuthScreen('forgot');
      setAuthError(
        'This reset link uses PKCE flow which is not supported by this app. ' +
        'In Supabase Dashboard → Authentication → Providers → Email, set "Flow type" to "Implicit", then request a new reset email.'
      );
    }
  }, []);

  useEffect(() => {

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      // Reset cloud-loaded flag so settings don't get pushed UP before fetch completes
      setSettingsCloudLoaded(false);
      if (user) {
        const p = `faida_${user.uid}_`;

        // ── 0. Clear anonymous/old data when user logs in ─────────────
        try {
          // MIGRATE (not delete) a business type chosen before signup/login —
          // deleting it was one cause of "type reverts to Products & Services".
          const anonBT = localStorage.getItem('faida_businessType');
          if (anonBT && !localStorage.getItem(p + 'businessType')) {
            localStorage.setItem(p + 'businessType', anonBT);
            localStorage.setItem(p + 'businessTypeUpdatedAt',
              localStorage.getItem('faida_businessTypeUpdatedAt') || new Date().toISOString());
          }
          localStorage.removeItem('faida_businessTypeUpdatedAt');
          const anonKeys = ['businessName', 'businessAddress', 'businessLogo', 'bankName', 'bankAccountName', 'bankAccountNumber', 'mobileMoneyProvider', 'mobileMoneyNumber', 'currency', 'invoiceCounter', 'proformaCounter', 'paymentAccounts', 'businessType', 'services', 'proformaInvoices', 'issuedInvoices', 'salesReturns', 'purchaseReturns', 'stockAdjHistory', 'recurringExpenses', 'businessTIN', 'businessVRN', 'businessRegNo', 'tradeLicenseNo'];
          anonKeys.forEach(key => {
            const anonKey = 'faida_' + key;
            localStorage.removeItem(anonKey);
          });
        } catch (e) {
          console.warn('Failed to clear anonymous data:', e);
        }

        // ── 1. Load from localStorage immediately (instant UX) ──────
        // ALWAYS set state (with a clean default when this account has no
        // saved value). Previously these were `if (value) set(...)`, which
        // let the PREVIOUS account's on-screen values leak into a new
        // account -e.g. another user's business name appearing after
        // signup -and then get synced into the new account's cloud.
        const localBiz = localStorage.getItem(p + 'businessName');
        setBusinessName(localBiz || '');
        const localAddr = localStorage.getItem(p + 'businessAddress');
        setBusinessAddress(localAddr || '');
        const localLogo = localStorage.getItem(p + 'businessLogo');
        setBusinessLogo(localLogo || '');
        const localBankName = localStorage.getItem(p + 'bankName');
        setBankName(localBankName || '');
        const localBankAccName = localStorage.getItem(p + 'bankAccountName');
        setBankAccountName(localBankAccName || '');
        const localBankAccNum = localStorage.getItem(p + 'bankAccountNumber');
        setBankAccountNumber(localBankAccNum || '');
        const localMMP = localStorage.getItem(p + 'mobileMoneyProvider');
        setMobileMoneyProvider(localMMP || '');
        const localMMN = localStorage.getItem(p + 'mobileMoneyNumber');
        setMobileMoneyNumber(localMMN || '');
        const savedCurrency = localStorage.getItem(p + 'currency');
        setCurrency(savedCurrency || 'TSh');
        setInvoiceCounter(parseInt(localStorage.getItem(p + 'invoiceCounter') || '0'));
        setProformaCounter(parseInt(localStorage.getItem(p + 'proformaCounter') || '0'));
        const storedAccounts = localStorage.getItem(p + 'paymentAccounts');
        try { setPaymentAccounts(storedAccounts ? JSON.parse(storedAccounts) : []); } catch { setPaymentAccounts([]); }
        const storedType = localStorage.getItem(p + 'businessType');
        if (storedType) {
          setBusinessType(storedType);
          // Backfill a timestamp for choices made before timestamps existed,
          // so the cloud can never stomp an explicit local choice (see below).
          if (!localStorage.getItem(p + 'businessTypeUpdatedAt')) {
            localStorage.setItem(p + 'businessTypeUpdatedAt', new Date().toISOString());
          }
        } else {
          // No saved choice for THIS account on this device: reset to default
          // so a different user who previously used this browser can't leak
          // their business type into this account. The cloud fetch below will
          // restore this account's real choice if they have one.
          setBusinessType('products_and_services');
        }
        const storedServices = localStorage.getItem(p + 'services');
        try { setServices(storedServices ? JSON.parse(storedServices) : []); } catch { setServices([]); }
        // serviceTransactions loaded by AppContext
        const storedProforma = localStorage.getItem(p + 'proformaInvoices');
        try { setProformaInvoices(storedProforma ? JSON.parse(storedProforma) : []); } catch { setProformaInvoices([]); }
        const storedIssued = localStorage.getItem(p + 'issuedInvoices');
        const storedManual = localStorage.getItem(p + 'manualInvoices');
        if (!storedIssued) setIssuedInvoices([]);
        if (!storedManual && !storedIssued) setManualInvoices([]);
        if (storedIssued) try {
          const issuedArr = JSON.parse(storedIssued);
          const manualArr = storedManual ? JSON.parse(storedManual) : [];
          // Migration: move unpaid issued invoices to manualInvoices if they aren't already there
          const alreadyMigratedIds = new Set(manualArr.map(i => i.id));
          const unpaidToMigrate = issuedArr.filter(i => i.isPaid === false && !alreadyMigratedIds.has(i.id));
          const remainingIssued = issuedArr.filter(i => i.isPaid !== false || alreadyMigratedIds.has(i.id));
          if (unpaidToMigrate.length > 0) {
            const migrated = [...manualArr, ...unpaidToMigrate.map(i => ({ ...i, isManualInvoice: true }))];
            setManualInvoices(migrated);
            localStorage.setItem(p + 'manualInvoices', JSON.stringify(migrated));
            setIssuedInvoices(remainingIssued);
            localStorage.setItem(p + 'issuedInvoices', JSON.stringify(remainingIssued));
          } else {
            setIssuedInvoices(issuedArr);
            if (storedManual) setManualInvoices(manualArr);
          }
        } catch {}
        if (!storedIssued && storedManual) try { setManualInvoices(JSON.parse(storedManual)); } catch {}
        const storedSalesReturns = localStorage.getItem(p + 'salesReturns');
        try { setSalesReturns(storedSalesReturns ? JSON.parse(storedSalesReturns) : []); } catch { setSalesReturns([]); }
        const storedPurchaseReturns = localStorage.getItem(p + 'purchaseReturns');
        try { setPurchaseReturns(storedPurchaseReturns ? JSON.parse(storedPurchaseReturns) : []); } catch { setPurchaseReturns([]); }
        const storedStockAdj = localStorage.getItem(p + 'stockAdjHistory');
        try { setStockAdjHistory(storedStockAdj ? JSON.parse(storedStockAdj) : []); } catch { setStockAdjHistory([]); }
        const storedRecurring = localStorage.getItem(p + 'recurringExpenses');
        try { setRecurringExpenses(storedRecurring ? JSON.parse(storedRecurring) : []); } catch { setRecurringExpenses([]); }
        const localTIN = localStorage.getItem(p + 'businessTIN');
        setBusinessTIN(localTIN || '');
        const localVRN = localStorage.getItem(p + 'businessVRN');
        setBusinessVRN(localVRN || '');
        const localRegNo = localStorage.getItem(p + 'businessRegNo');
        setBusinessRegNo(localRegNo || '');
        const localLicenseNo = localStorage.getItem(p + 'tradeLicenseNo');
        setTradeLicenseNo(localLicenseNo || '');
        const localTerms = localStorage.getItem(p + 'invoiceTerms');
        setInvoiceTerms(localTerms || '');

        // ── DATA INTEGRITY CHECK: Log what was loaded ──
        console.log('✅ Data loaded from localStorage:', {
          hasAccounts: !!storedAccounts,
          hasServices: !!storedServices,
          hasInvoices: !!storedIssued,
          accountCount: storedAccounts ? JSON.parse(storedAccounts).length : 0,
          serviceCount: storedServices ? JSON.parse(storedServices).length : 0,
        });

        // ── 2. Fetch cloud settings in background (overrides local) ─
        if (user.accessToken) {
          fetchAllData(user.uid, user.accessToken).then(cloudData => {
            if (!cloudData) return;
            const s = cloudData.settings;
            if (s && typeof s === 'object' && !Array.isArray(s)) {
              if (s.businessName)       setBusinessName(s.businessName);
              if (s.businessAddress)    setBusinessAddress(s.businessAddress);
              if (s.businessLogo)       setBusinessLogo(s.businessLogo);
              if (s.businessTIN)        setBusinessTIN(s.businessTIN);
              if (s.businessVRN)        setBusinessVRN(s.businessVRN);
              if (s.businessRegNo)      setBusinessRegNo(s.businessRegNo);
              if (s.tradeLicenseNo)     setTradeLicenseNo(s.tradeLicenseNo);
              if (s.bankName)           setBankName(s.bankName);
              if (s.bankAccountName)    setBankAccountName(s.bankAccountName);
              if (s.bankAccountNumber)  setBankAccountNumber(s.bankAccountNumber);
              if (s.mobileMoneyProvider) setMobileMoneyProvider(s.mobileMoneyProvider);
              if (s.mobileMoneyNumber)  setMobileMoneyNumber(s.mobileMoneyNumber);
              if (Array.isArray(s.paymentAccounts)) setPaymentAccounts(prev => mergeById(s.paymentAccounts, prev));
              // LAST-WRITE-WINS for businessType. The old unconditional
              // override here was THE "business type reverts after logout/
              // login" bug: a stale cloud value stomped the device's saved
              // choice on every login, then got re-saved everywhere. Now the
              // cloud only wins when its choice is strictly NEWER than this
              // device's choice (i.e. it was changed on another device), or
              // when this device has no explicit choice at all.
              if (s.businessType) {
                const localBtTs = localStorage.getItem(p + 'businessTypeUpdatedAt') || '';
                const cloudBtTs = s.businessTypeUpdatedAt || '';
                if (!localBtTs || (cloudBtTs && cloudBtTs > localBtTs)) {
                  setBusinessType(s.businessType);
                  localStorage.setItem(p + 'businessType', s.businessType);
                  if (cloudBtTs) localStorage.setItem(p + 'businessTypeUpdatedAt', cloudBtTs);
                }
              }
              if (s.currency)           setCurrency(s.currency);
              // TRIAL ANTI-RESET: adopt the EARLIEST known trial start for
              // this account. Stops "clear browser data → fresh 7-day trial".
              if (s.trialStartedAt) {
                const localTrialTs = localStorage.getItem(p + 'trialStartedAt');
                if (!localTrialTs || s.trialStartedAt < localTrialTs) {
                  localStorage.setItem(p + 'trialStartedAt', s.trialStartedAt);
                  const curSub = loadSubscription(user.uid);
                  if (curSub && curSub.plan === 'trial') {
                    const fixedTrial = { ...curSub, startDate: s.trialStartedAt, endDate: new Date(new Date(s.trialStartedAt).getTime() + 7 * 86400000).toISOString() };
                    saveSubscription(user.uid, fixedTrial);
                    setSubscriptionData(fixedTrial);
                    setSubscriptionExpired(!isSubscriptionActive(fixedTrial));
                  }
                }
              }
              // Privacy Mode configuration (unlock state stays local, not synced)
              if (typeof s.privacyEnabled === 'boolean')  setPrivacyEnabled(s.privacyEnabled);
              if (typeof s.privacyPinHash === 'string')    setPrivacyPinHash(s.privacyPinHash);
              if (typeof s.privacyRecoveryCodeHash === 'string') setPrivacyRecoveryCodeHash(s.privacyRecoveryCodeHash);
              if (Array.isArray(s.privacyHiddenTabs))      setPrivacyHiddenTabs(s.privacyHiddenTabs);
              if (s.privacyHiddenItems && typeof s.privacyHiddenItems === 'object') setPrivacyHiddenItems(s.privacyHiddenItems);
              if (typeof s.privacyAutoLockMinutes === 'number') setPrivacyAutoLockMinutes(s.privacyAutoLockMinutes);
            }
            // Services & invoices from cloud -TOMBSTONE-SAFE MERGE (C3).
            // The old "local-first, only pull if key missing" hack existed
            // because pulling cloud could resurrect a local deletion. Now that
            // deletions are tombstones carried in the raw arrays, we mergeById:
            // newest wins and a deletion beats an older live copy, so cross-
            // device edits load AND local deletions are never resurrected.
            if (Array.isArray(cloudData.services)) setServices(prev => mergeById(cloudData.services, prev));
            // serviceTransactions synced by AppContext
            if (Array.isArray(cloudData.proformaInvoices)) setProformaInvoices(prev => mergeById(cloudData.proformaInvoices, prev));
            if (Array.isArray(cloudData.issuedInvoices)) setIssuedInvoices(prev => mergeById(cloudData.issuedInvoices, prev));
            if (Array.isArray(cloudData.manualInvoices)) setManualInvoices(prev => mergeById(cloudData.manualInvoices, prev));
            if (Array.isArray(cloudData.salesReturns)) setSalesReturns(prev => mergeById(cloudData.salesReturns, prev));
            if (Array.isArray(cloudData.purchaseReturns)) setPurchaseReturns(prev => mergeById(cloudData.purchaseReturns, prev));
          }).catch(() => {/* offline -local data already loaded */})
            .finally(() => setSettingsCloudLoaded(true)); // unlock settings sync
        } else {
          setSettingsCloudLoaded(true); // no token, no cloud -allow local-only sync attempts
        }


        // Load subscription and check expiry
        let sub = loadSubscription(user.uid);
        if (!sub) {
          sub = initTrialSubscription(user.uid);
          // ANTI-ABUSE: if this account already used its trial (recorded in
          // the user-keyed marker, restored from cloud below), resume the
          // ORIGINAL trial window instead of granting a fresh 7 days after
          // clearing browser data / reinstalling.
          const priorStart = localStorage.getItem(p + 'trialStartedAt');
          if (priorStart) {
            sub = { ...sub, startDate: priorStart, endDate: new Date(new Date(priorStart).getTime() + 7 * 86400000).toISOString() };
            saveSubscription(user.uid, sub);
          }
        }
        try { if (!localStorage.getItem(p + 'trialStartedAt')) localStorage.setItem(p + 'trialStartedAt', sub.startDate); } catch (e) {}
        // ── OWNER OVERRIDE (owner accounts ONLY) ─────────────────────────
        // Previously this auto-extended EVERY expired user to a free 1-year
        // Premium -meaning no customer would ever see the paywall or renew.
        // Now it applies only to the listed owner account(s).
        const OWNER_EMAILS = ['hesabusolutionstz@gmail.com', 'venisiazephania@gmail.com'];
        if (!isSubscriptionActive(sub) && OWNER_EMAILS.includes((user.email || '').toLowerCase())) {
          sub = {
            plan: 'faida_premium',
            status: 'active',
            startDate: new Date().toISOString(),
            endDate: new Date(Date.now() + 365 * 86400000).toISOString(),
            paymentId: 'owner-account',
            trialUsed: true,
          };
          saveSubscription(user.uid, sub);
          console.log('🔓 Owner account: subscription auto-extended until', sub.endDate);
        }
        setSubscriptionData(sub);
        setSubscriptionExpired(!isSubscriptionActive(sub));

        // ── BACKEND RECONCILIATION ───────────────────────────────────────
        // Paid subscriptions live on the payment backend (keyed by email).
        // Adopt the backend's ACTIVE subscription when it's better than what
        // this device knows -so paying customers keep Premium on new
        // devices or after clearing storage. Never auto-downgrades on
        // network errors.
        if (user.email && user.accessToken) {
          fetchSubscriptionStatus(user.email, user.accessToken).then(liveSub => {
            if (liveSub && isSubscriptionActive(liveSub)) {
              const localNow = loadSubscription(user.uid);
              const liveEnd = new Date(liveSub.endDate).getTime() || 0;
              const localEnd = localNow ? (new Date(localNow.endDate).getTime() || 0) : 0;
              if (liveEnd > localEnd) {
                saveSubscription(user.uid, liveSub);
                setSubscriptionData(liveSub);
                setSubscriptionExpired(false);
              }
            }
          }).catch(() => {});
        }

        // Handle return from ClickPesa checkout. The backend NEVER activates
        // from the return URL anymore (that was a self-activation hole) -it
        // just redirects here with ?payment=return. We then read the TRUE,
        // webhook-verified status from the backend using the user's token.
        // (Older ?success=true links are still handled for backward compat.)
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('payment') === 'return' || urlParams.get('success') === 'true') {
          window.history.replaceState({}, document.title, window.location.pathname);
          try {
            const liveSub = await fetchSubscriptionStatus(user.email, user.accessToken);
            if (liveSub && isSubscriptionActive(liveSub)) {
              saveSubscription(user.uid, liveSub);
              setSubscriptionData(liveSub);
              setSubscriptionExpired(false);
              setPaymentSuccessOverlay(true);
              setTimeout(() => setPaymentSuccessOverlay(false), 4000);
            }
          } catch (e) {
            console.error('Failed to fetch subscription after payment:', e);
          }
        }
      } else {
        // Logged out -reset to the anonymous keys' values, or clean defaults.
        // ALWAYS set state: keeping the previous account's values here let
        // them leak into the anon keys (via the persist effects) and then
        // into the next account created on this device.
        const p = 'faida_';
        try {
          setBusinessName(localStorage.getItem(p + 'businessName') || '');
          setBusinessAddress(localStorage.getItem(p + 'businessAddress') || '');
          setBusinessLogo(localStorage.getItem(p + 'businessLogo') || '');
          setBusinessTIN(localStorage.getItem(p + 'businessTIN') || '');
          setBusinessVRN(localStorage.getItem(p + 'businessVRN') || '');
          setBusinessRegNo(localStorage.getItem(p + 'businessRegNo') || '');
          setTradeLicenseNo(localStorage.getItem(p + 'tradeLicenseNo') || '');
          setBankName(localStorage.getItem(p + 'bankName') || '');
          setBankAccountName(localStorage.getItem(p + 'bankAccountName') || '');
          setBankAccountNumber(localStorage.getItem(p + 'bankAccountNumber') || '');
          setMobileMoneyProvider(localStorage.getItem(p + 'mobileMoneyProvider') || '');
          setMobileMoneyNumber(localStorage.getItem(p + 'mobileMoneyNumber') || '');
          const pa = localStorage.getItem(p + 'paymentAccounts');
          try { setPaymentAccounts(pa ? JSON.parse(pa) : []); } catch { setPaymentAccounts([]); }
          setBusinessType(localStorage.getItem(p + 'businessType') || 'products_and_services');
          setCurrency(localStorage.getItem(p + 'currency') || 'TSh');
          const sv = localStorage.getItem(p + 'services');
          try { setServices(sv ? JSON.parse(sv) : []); } catch { setServices([]); }
        } catch(e) {}
        setSubscriptionData({ plan: 'trial', trialEndsAt: null });
        setSubscriptionExpired(false);
        setPaymentFlow(null);
        setSetupScreen(null);
        setSettingsScreen(null);
      }
      dataLoaded.current = true;
      setAuthLoading(false);
    });
    return unsubscribe;
  }, []);

  // ── RELATIONAL DB READ (Phase 8 production path) ─────────────────
  // Loads App.js-managed collections from the relational schema when
  // businessId is available. TRANSITION SAFETY: only replaces collections
  // that are currently empty (no JSONB data), to avoid wiping pre-migration
  // records. Once a data migration backfills all records, this becomes the
  // sole read path and the empty-check is removed.
  useEffect(() => {
    if (!businessId || !currentUser?.accessToken || !dataLoaded.current) return;
    let cancelled = false;
    setDataLoading(true);
    (async () => {
      try {
        const raw = await fetchAllBusinessData(businessId, currentUser.accessToken);
        if (cancelled || !raw) return;
        const adapted = adaptAllBusinessData(raw);
        if (!adapted) return;
        // Only load relational into collections that JSONB left empty
        if (adapted.services?.length > 0 && (!servicesRaw || servicesRaw.length === 0)) setServices(adapted.services);
        if (adapted.paymentAccounts?.length > 0 && (!paymentAccountsRaw || paymentAccountsRaw.length === 0)) setPaymentAccounts(adapted.paymentAccounts);
        if (adapted.salesReturns?.length > 0 && (!salesReturnsRaw || salesReturnsRaw.length === 0)) setSalesReturns(adapted.salesReturns);
        if (adapted.purchaseReturns?.length > 0 && (!purchaseReturnsRaw || purchaseReturnsRaw.length === 0)) setPurchaseReturns(adapted.purchaseReturns);
        if (adapted.manualInvoices?.length > 0 && (!manualInvoicesRaw || manualInvoicesRaw.length === 0)) setManualInvoices(adapted.manualInvoices);
        console.log('[App.js] Relational data loaded for App.js-managed collections');
      } catch (err) {
        console.warn('[App.js] Relational load failed (falling back to JSONB):', err.message);
      } finally {
        if (!cancelled) setDataLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [businessId, currentUser?.accessToken]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── REAL-TIME CROSS-DEVICE SYNC ──────────────────────────────────
  // Poll Supabase every 30 seconds to sync data from other devices
  // BUT: Only sync if cloud data is non-empty (prevents overwriting with incomplete syncs)
  useEffect(() => {
    if (!currentUser?.uid || !currentUser?.accessToken) return;

    const syncFromCloud = async () => {
      try {
        setSyncStatus('syncing');
        const cloudData = await fetchAllData(currentUser.uid, currentUser.accessToken);
        if (!cloudData) {
          setSyncStatus('synced');
          return;
        }

        // LOCAL-FIRST for scalar settings: the periodic poll must NEVER stomp
        // on the user's local edits. Cloud-down for scalar settings happens
        // ONLY on initial login (where it's a one-shot restore for a new
        // device). After that, this device is the source of truth.
        // (Removing this block was the fix for "I change business type and it
        // reverts after 5 seconds" -the 30-second poll was pulling the old
        // cloud value back before sync-UP could push the new one.)

        // TOMBSTONE-SAFE MERGE (C3): the periodic poll now merges cloud into
        // local by id (newest wins; a deletion beats an older live copy), so
        // cross-device edits arrive AND local deletions are never resurrected.
        if (Array.isArray(cloudData.services)) setServices(prev => mergeById(cloudData.services, prev));
        if (Array.isArray(cloudData.proformaInvoices)) setProformaInvoices(prev => mergeById(cloudData.proformaInvoices, prev));
        if (Array.isArray(cloudData.issuedInvoices)) setIssuedInvoices(prev => mergeById(cloudData.issuedInvoices, prev));
        if (Array.isArray(cloudData.manualInvoices)) setManualInvoices(prev => mergeById(cloudData.manualInvoices, prev));
        if (Array.isArray(cloudData.salesReturns)) setSalesReturns(prev => mergeById(cloudData.salesReturns, prev));
        if (Array.isArray(cloudData.purchaseReturns)) setPurchaseReturns(prev => mergeById(cloudData.purchaseReturns, prev));

        setSyncStatus('synced');
        console.log('✅ Real-time sync complete');
      } catch (e) {
        console.warn('⚠️ Real-time sync failed (will retry):', e.message);
        setSyncStatus('pending');
      }
    };

    // Poll every 30 seconds
    const syncInterval = setInterval(syncFromCloud, 30000);

    // Also sync when window regains focus (user switched back to browser)
    const handleFocus = () => {
      console.log('📱 App focused - syncing from cloud...');
      syncFromCloud();
    };
    window.addEventListener('focus', handleFocus);

    // Do an immediate sync on mount (but with delay to let local data save first)
    const initialSyncDelay = setTimeout(() => syncFromCloud(), 2000);

    return () => {
      clearInterval(syncInterval);
      window.removeEventListener('focus', handleFocus);
      clearTimeout(initialSyncDelay);
    };
  }, [currentUser, services]);

  // ── Login rate limiting ──
  const loginAttemptsRef = useRef({ count: 0, lockedUntil: 0 });
  const MAX_LOGIN_ATTEMPTS = 5;
  const LOCKOUT_MS = 60_000; // 1 minute cooldown

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError('');

    const now = Date.now();
    const la = loginAttemptsRef.current;
    if (la.lockedUntil > now) {
      const secsLeft = Math.ceil((la.lockedUntil - now) / 1000);
      setAuthError(`Too many attempts. Try again in ${secsLeft}s.`);
      return;
    }

    try {
      await signInWithEmailAndPassword(auth, loginForm.identifier, loginForm.password);
      la.count = 0; // reset on success
    } catch (error) {
      la.count += 1;
      if (la.count >= MAX_LOGIN_ATTEMPTS) {
        la.lockedUntil = now + LOCKOUT_MS;
        la.count = 0;
        setAuthError('Too many failed attempts. Please wait 1 minute before trying again.');
      } else if (error.message === 'auth/wrong-password') {
        setAuthError(t.wrongPassword || 'Incorrect email or password. Please try again.');
      } else {
        setAuthError(t.loginError || 'Login failed. Please check your email and password.');
      }
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setAuthError('');
    
    if (signupForm.password !== signupForm.confirmPassword) {
      setAuthError(t.passwordMismatch);
      return;
    }

    if (signupForm.password.length < 8) {
      setAuthError(t.passwordTooShort || 'Password must be at least 8 characters');
      return;
    }

    if (!signupForm.email.includes('@')) {
      setAuthError(t.invalidEmail);
      return;
    }

    if (!signupForm.agree) {
      setAuthError(t.agreeToTerms);
      return;
    }
    
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, signupForm.email, signupForm.password);
      const user = userCredential.user;
      // Initialize trial subscription
      const trialSub = initTrialSubscription(user.uid);
      // Save business profile to Firestore / mock storage
      await setDoc(doc(db, 'users', user.uid), {
        businessName: '',
        ownerName: signupForm.fullName,
        email: signupForm.email,
        createdAt: new Date().toISOString(),
        subscription: trialSub
      });
      setBusinessName('');
      setAuthScreen('login');
      setAuthError('');
      setSuccessMessage(t.signupSuccess);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      if (error.message === 'auth/email-already-in-use') {
        setAuthError('An account with this email already exists. Please login instead.');
      } else {
        setAuthError(t.signupError);
      }
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setAuthError('');
    try {
      await sendPasswordResetEmail(auth, forgotForm.email);
    } catch (error) {
      // Silently ignore errors -always show success to prevent email enumeration
    }
    // Always show success message regardless of whether email exists
    setSuccessMessage(t.resetEmailSent || 'If an account exists with this email, a reset link has been sent. Check your inbox.');
    setAuthScreen('login');
    setTimeout(() => setSuccessMessage(''), 7000);
  };

  const handleResetNewPassword = async (e) => {
    e.preventDefault();
    setAuthError('');
    if (resetForm.password !== resetForm.confirmPassword) {
      setAuthError(t.passwordMismatch);
      return;
    }
    if (resetForm.password.length < 6) {
      setAuthError(t.passwordTooShort || 'Password must be at least 6 characters.');
      return;
    }
    try {
      await confirmPasswordReset(auth, resetForm.password, recoveryToken);
      setSuccessMessage(t.passwordResetSuccess || 'Password reset successfully! Please login.');
      setAuthScreen('login');
      setResetForm({ password: '', confirmPassword: '' });
      setForgotForm({ email: '' });
      setRecoveryToken(null);
      setTimeout(() => setSuccessMessage(''), 4000);
    } catch (error) {
      // Map specific Supabase errors to clear, actionable messages.
      const code = error?.message || '';
      if (code === 'auth/same-password') {
        setAuthError(
          t.samePasswordError ||
          'Your new password must be different from your current password. Please choose a different one.'
        );
        // Clear the inputs so they actually type something new
        setResetForm({ password: '', confirmPassword: '' });
      } else if (code === 'auth/weak-password') {
        setAuthError(
          t.weakPasswordError ||
          'This password is too weak. Use at least 6 characters with a mix of letters and numbers.'
        );
      } else if (code === 'auth/expired-action-code') {
        setAuthError(
          t.resetLinkExpired ||
          'This reset link has expired. Please request a new one from the Forgot Password page.'
        );
      } else if (code === 'auth/invalid-action-code') {
        setAuthError(
          t.resetLinkInvalid ||
          'This reset link is invalid or has already been used. Please request a new one.'
        );
      } else {
        setAuthError(t.resetError || 'Failed to reset password. Please try again.');
      }
    }
  };

  // ── Subscription Payment Flow ──────────────────────────────────────────
  const handleStartPayment = (planId) => {
    // Use plan-specific ClickPesa payment page if URL is configured
    const baseUrl = CLICKPESA_PLAN_URLS[planId];
    if (baseUrl && currentUser?.email) {
      const checkoutUrl = `${baseUrl}&email=${encodeURIComponent(currentUser.email)}`;
      window.location.href = checkoutUrl;
      return;
    }
    // Fallback: show phone input modal (legacy flow)
    setPaymentFlow({ step: 'phone', planId, paymentId: null, phone: '', error: '' });
  };

  const handleSubmitPayment = async () => {
    if (!paymentFlow || !currentUser) return;
    const { planId, phone } = paymentFlow;
    if (!phone || phone.length < 9) {
      setPaymentFlow(prev => ({ ...prev, error: t.invalidPhone || 'Enter a valid phone number' }));
      return;
    }
    setPaymentFlow(prev => ({ ...prev, step: 'processing', error: '' }));
    try {
      // Step 1: Create payment via backend API
      const result = await createPayment(currentUser.uid, planId, phone);
      setPaymentFlow(prev => ({ ...prev, step: 'waiting', paymentId: result.paymentId }));

      // Step 2: Poll backend for payment verification (user completes on phone)
      // Backend checks with payment provider -frontend never decides payment status
      await pollPaymentVerification(currentUser.uid, result.paymentId, {
        maxAttempts: 24,
        intervalMs: 5000,
        onStatus: (status) => {
          if (status === 'completed' || status === 'verified') {
            setPaymentFlow(prev => ({ ...prev, step: 'verifying' }));
          }
        },
      });
      setPaymentFlow(prev => ({ ...prev, step: 'verifying' }));

      // Step 3: Activate subscription on backend (backend re-verifies before activating)
      const newSub = await activateSubscription(currentUser.uid, result.paymentId);
      setSubscriptionData(newSub);
      setSubscriptionExpired(false);
      setPaymentFlow({ step: 'success', planId, paymentId: result.paymentId, phone, error: '' });

      // Auto-dismiss after 3 seconds
      setTimeout(() => {
        setPaymentFlow(null);
        setSettingsScreen(null);
      }, 3000);
    } catch (error) {
      setPaymentFlow(prev => ({ ...prev, step: 'failed', error: error.message || 'Payment failed' }));
    }
  };

  const handleCancelPayment = () => {
    setPaymentFlow(null);
  };

  // ── Load Sample / Demo Data ─────────────────────────────────────
  const loadSampleData = () => {
    showConfirm('This will add sample data (products, sales, expenses, customers, suppliers) to your account so you can explore Biashara Track. Your existing data will NOT be deleted. Continue?', () => doLoadSampleData(), { tone: 'info', title: 'Load sample data?' });
  };
  const doLoadSampleData = () => {
    const now = Date.now();
    const today = new Date();
    // d(n) = date string n days ago
    const d = (offset = 0) => { const dt = new Date(today); dt.setDate(dt.getDate() - offset); return dt.toISOString().split('T')[0]; };

    // Sample customers
    const sampleCustomers = [
      { id: now + 1, name: 'Amina Juma', phone: '+255712345678', email: 'amina@gmail.com', address: 'Sinza, Dar es Salaam' },
      { id: now + 2, name: 'Baraka Enterprises', phone: '+255754321098', email: 'baraka@enterprise.co.tz', address: 'Kariakoo, Dar es Salaam' },
      { id: now + 3, name: 'Fatuma Hassan', phone: '+255765432109', email: '', address: 'Mwanza City' },
      { id: now + 4, name: 'Joram Mwangi', phone: '+255789012345', email: '', address: 'Arusha' },
      { id: now + 5, name: 'Grace Mollel', phone: '+255733456789', email: 'grace@mollel.co.tz', address: 'Dodoma' },
    ];

    // Sample suppliers
    const sampleSuppliers = [
      { id: now + 10, name: 'Kariakoo Wholesalers', phone: '+255712000001', email: 'kariakoo@wholesale.co.tz', address: 'Kariakoo Market, Dar es Salaam' },
      { id: now + 11, name: 'Urafiki Textiles Ltd', phone: '+255754000002', email: 'urafiki@textiles.co.tz', address: 'Industrial Area, Dar es Salaam' },
    ];

    // Sample products -using correct stock fields read by the app
    const mkProd = (offset, name, buyingPrice, sellingPrice, qty, minAlert) => ({
      id: now + offset,
      productName: name,
      buyingPrice,
      sellingPrice,
      costPrice: buyingPrice,
      balance: qty,
      currentStock: qty,
      openingStockQty: qty,
      stockIn: qty,
      stockOut: 0,
      minStockAlert: minAlert,
      lowStockLevel: minAlert,
      reorderQty: minAlert * 5,
      totalStockValue: buyingPrice * qty,
    });
    const sampleProducts = [
      mkProd(20, 'Unga wa Ngano 2kg',   3200,  4000,  85, 10),
      mkProd(21, 'Mchele Pemba 5kg',   11000, 14000,   6, 20),  // LOW -needs restock
      mkProd(22, 'Mafuta ya Alizeti 2L', 7500,  9500,  60, 10),
      mkProd(23, 'Sukari 1kg',           2800,  3500, 120, 20),
      mkProd(24, 'Sabuni ya Kufulia',    1500,  2000,   7, 15),  // LOW -needs restock
      mkProd(25, 'Chai Rangi 500g',      2200,  2800,  11, 20),  // LOW -needs restock
      mkProd(26, 'Maji ya Kunywa 1.5L',   600,   900, 200, 30),
    ];

    // Sample expense categories
    const sampleExpCats = [
      { id: now + 30, name: 'Kodi' },
      { id: now + 31, name: 'Huduma' },
      { id: now + 32, name: 'Mishahara' },
      { id: now + 33, name: 'Usafiri' },
      { id: now + 34, name: 'Masoko' },
    ];

    // Helper for a sale record
    const mkSale = (offset, num, customer, product, qty, sell, cost, paid, method, discPct = '', taxes = []) => {
      const discAmt = discPct ? Math.round(qty * sell * parseFloat(discPct) / 100) : 0;
      return {
        id: now + offset,
        invoiceNumber: `INV-${String(num).padStart(4, '0')}`,
        date: d(offset > 200 ? offset - 200 : offset),
        customerName: customer,
        productName: product,
        quantity: qty,
        sellingPrice: sell,
        cost,
        totalRevenue: qty * sell - discAmt,
        totalCost: qty * cost,
        isPaid: paid,
        paymentMethod: method,
        discount: discPct,
        discountAmount: discAmt,
        taxes,
      };
    };
    const wht2 = [{ type: 'withholding', rate: 2, label: 'WHT 2%' }];

    // Sales spread: today(0), week(1-6), month(7-27), last month(28-58), 2mo ago(59-88), 3mo ago(89-120)
    const sampleSales = [
      // TODAY -~250k revenue
      mkSale(0,   1,  'Amina Juma',        'Unga wa Ngano 2kg',   30,  4000,  3200, true,  'cash'),
      mkSale(0,   2,  'Walk-in Customer',  'Chai Rangi 500g',     50,  2800,  2200, true,  'cash'),
      // THIS WEEK -~1.2M revenue
      mkSale(1,   3,  'Baraka Enterprises','Mchele Pemba 5kg',    20, 14000, 11000, true,  'mobilemoney', '', wht2),
      mkSale(2,   4,  'Fatuma Hassan',     'Mafuta ya Alizeti 2L',20,  9500,  7500, true,  'cash'),
      mkSale(3,   5,  'Joram Mwangi',      'Sukari 1kg',          80,  3500,  2800, false, 'bank','5', wht2),
      mkSale(4,   6,  'Grace Mollel',      'Maji ya Kunywa 1.5L',200,   900,   600, true,  'cash'),
      mkSale(5,   7,  'Amina Juma',        'Sabuni ya Kufulia',   40,  2000,  1500, true,  'cash'),
      mkSale(6,   8,  'Walk-in Customer',  'Unga wa Ngano 2kg',   25,  4000,  3200, true,  'cash'),
      // THIS MONTH -~3M revenue
      mkSale(7,   9,  'Baraka Enterprises','Mchele Pemba 5kg',    25, 14000, 11000, true,  'bank',  '', wht2),
      mkSale(9,  10,  'Fatuma Hassan',     'Sukari 1kg',          80,  3500,  2800, false, 'cash'),
      mkSale(11, 11,  'Joram Mwangi',      'Mafuta ya Alizeti 2L',18,  9500,  7500, true,  'mobilemoney', '', wht2),
      mkSale(13, 12,  'Grace Mollel',      'Chai Rangi 500g',     80,  2800,  2200, true,  'cash'),
      mkSale(15, 13,  'Walk-in Customer',  'Maji ya Kunywa 1.5L',250,   900,   600, true,  'cash'),
      mkSale(17, 14,  'Amina Juma',        'Unga wa Ngano 2kg',   45,  4000,  3200, true,  'cash'),
      mkSale(20, 15,  'Baraka Enterprises','Sabuni ya Kufulia',   60,  2000,  1500, true,  'bank',  '', wht2),
      mkSale(23, 16,  'Fatuma Hassan',     'Mchele Pemba 5kg',    15, 14000, 11000, true,  'cash'),
      mkSale(26, 17,  'Walk-in Customer',  'Sukari 1kg',          60,  3500,  2800, true,  'cash'),
      // LAST MONTH -~2.8M revenue
      mkSale(228, 18, 'Joram Mwangi',      'Unga wa Ngano 2kg',   50,  4000,  3200, true,  'cash',  '', wht2),
      mkSale(232, 19, 'Amina Juma',        'Mafuta ya Alizeti 2L',18,  9500,  7500, true,  'mobilemoney'),
      mkSale(236, 20, 'Baraka Enterprises','Sukari 1kg',          100, 3500,  2800, false, 'bank','3', wht2),
      mkSale(240, 21, 'Grace Mollel',      'Chai Rangi 500g',     70,  2800,  2200, true,  'cash'),
      mkSale(244, 22, 'Walk-in Customer',  'Maji ya Kunywa 1.5L',220,   900,   600, true,  'cash'),
      mkSale(250, 23, 'Fatuma Hassan',     'Mchele Pemba 5kg',    20, 14000, 11000, true,  'cash'),
      // 2 MONTHS AGO -~2.5M revenue
      mkSale(259, 24, 'Amina Juma',        'Unga wa Ngano 2kg',   55,  4000,  3200, true,  'cash'),
      mkSale(265, 25, 'Baraka Enterprises','Mafuta ya Alizeti 2L',22,  9500,  7500, true,  'bank',  '', wht2),
      mkSale(272, 26, 'Walk-in Customer',  'Sukari 1kg',          90,  3500,  2800, true,  'cash'),
      mkSale(280, 27, 'Grace Mollel',      'Chai Rangi 500g',     65,  2800,  2200, true,  'mobilemoney'),
      // 3 MONTHS AGO -~2.2M revenue
      mkSale(289, 28, 'Joram Mwangi',      'Mchele Pemba 5kg',    18, 14000, 11000, true,  'cash',  '', wht2),
      mkSale(298, 29, 'Fatuma Hassan',     'Unga wa Ngano 2kg',   60,  4000,  3200, true,  'cash'),
      mkSale(310, 30, 'Amina Juma',        'Maji ya Kunywa 1.5L',300,   900,   600, true,  'cash'),
      // 4 MONTHS AGO -~2M revenue
      mkSale(321, 31, 'Baraka Enterprises','Sukari 1kg',           80, 3500,  2800, true,  'bank',  '', wht2),
      mkSale(330, 32, 'Grace Mollel',      'Mafuta ya Alizeti 2L',20,  9500,  7500, true,  'mobilemoney'),
      mkSale(340, 33, 'Walk-in Customer',  'Chai Rangi 500g',     60,  2800,  2200, true,  'cash'),
      mkSale(348, 34, 'Amina Juma',        'Unga wa Ngano 2kg',   45,  4000,  3200, true,  'cash'),
      // 5 MONTHS AGO -~1.8M revenue
      mkSale(351, 35, 'Joram Mwangi',      'Mchele Pemba 5kg',    15, 14000, 11000, true,  'cash',  '', wht2),
      mkSale(360, 36, 'Fatuma Hassan',     'Sabuni ya Kufulia',   50,  2000,  1500, true,  'cash'),
      mkSale(368, 37, 'Baraka Enterprises','Sukari 1kg',          80,  3500,  2800, false, 'bank',  '', wht2),
      mkSale(375, 38, 'Walk-in Customer',  'Maji ya Kunywa 1.5L',200,   900,   600, true,  'cash'),
      // 6 MONTHS AGO -~1.7M revenue
      mkSale(381, 39, 'Grace Mollel',      'Unga wa Ngano 2kg',   40,  4000,  3200, true,  'cash'),
      mkSale(390, 40, 'Amina Juma',        'Mafuta ya Alizeti 2L',18,  9500,  7500, true,  'mobilemoney'),
      mkSale(400, 41, 'Walk-in Customer',  'Chai Rangi 500g',     55,  2800,  2200, true,  'cash'),
      mkSale(408, 42, 'Fatuma Hassan',     'Mchele Pemba 5kg',    14, 14000, 11000, true,  'bank'),
      // 7 MONTHS AGO -~1.5M revenue
      mkSale(411, 43, 'Joram Mwangi',      'Sukari 1kg',          70,  3500,  2800, true,  'cash',  '', wht2),
      mkSale(420, 44, 'Baraka Enterprises','Unga wa Ngano 2kg',   40,  4000,  3200, true,  'bank',  '', wht2),
      mkSale(430, 45, 'Amina Juma',        'Maji ya Kunywa 1.5L',200,   900,   600, true,  'cash'),
      // 8 MONTHS AGO -~1.4M revenue
      mkSale(441, 46, 'Grace Mollel',      'Mafuta ya Alizeti 2L',16,  9500,  7500, true,  'mobilemoney'),
      mkSale(450, 47, 'Walk-in Customer',  'Mchele Pemba 5kg',    12, 14000, 11000, true,  'cash'),
      mkSale(460, 48, 'Fatuma Hassan',     'Chai Rangi 500g',     55,  2800,  2200, true,  'cash'),
      mkSale(468, 49, 'Baraka Enterprises','Sabuni ya Kufulia',   40,  2000,  1500, true,  'bank',  '', wht2),
      // 9 MONTHS AGO -~1.3M revenue
      mkSale(471, 50, 'Amina Juma',        'Sukari 1kg',          70,  3500,  2800, true,  'cash'),
      mkSale(480, 51, 'Joram Mwangi',      'Unga wa Ngano 2kg',   38,  4000,  3200, true,  'cash',  '', wht2),
      mkSale(490, 52, 'Walk-in Customer',  'Maji ya Kunywa 1.5L',180,   900,   600, true,  'cash'),
      // 10 MONTHS AGO -~1.2M revenue
      mkSale(501, 53, 'Grace Mollel',      'Mchele Pemba 5kg',    12, 14000, 11000, true,  'bank'),
      mkSale(510, 54, 'Fatuma Hassan',     'Mafuta ya Alizeti 2L',14,  9500,  7500, true,  'cash'),
      mkSale(520, 55, 'Baraka Enterprises','Sukari 1kg',          60,  3500,  2800, true,  'mobilemoney', '', wht2),
      // 11 MONTHS AGO -~1M revenue
      mkSale(531, 56, 'Amina Juma',        'Unga wa Ngano 2kg',   35,  4000,  3200, true,  'cash'),
      mkSale(540, 57, 'Walk-in Customer',  'Chai Rangi 500g',     50,  2800,  2200, true,  'cash'),
      mkSale(550, 58, 'Joram Mwangi',      'Mchele Pemba 5kg',    10, 14000, 11000, true,  'cash',  '', wht2),
      // 12 MONTHS AGO -~800k revenue
      mkSale(556, 59, 'Fatuma Hassan',     'Sabuni ya Kufulia',   35,  2000,  1500, true,  'cash'),
      mkSale(561, 60, 'Baraka Enterprises','Maji ya Kunywa 1.5L',200,   900,   600, true,  'bank',  '', wht2),
    ];

    // Helper for a purchase record
    const mkPurch = (offset, num, supplier, product, qty, price, paid, method) => ({
      id: now + 500 + offset,
      date: d(offset > 200 ? offset - 200 : offset),
      supplierName: supplier,
      productName: product,
      quantity: qty,
      buyingPrice: price,
      totalCost: qty * price,
      isPaid: paid,
      paymentMethod: method,
      invoiceNumber: `PO-${String(num).padStart(3, '0')}`,
    });

    // Purchases: cash and mobile money (how real small shops pay wholesalers)
    const samplePurchases = [
      // This week
      mkPurch(1,   1, 'Kariakoo Wholesalers', 'Unga wa Ngano 2kg',    200, 3200,  true,  'cash'),
      mkPurch(4,   2, 'Kariakoo Wholesalers', 'Mchele Pemba 5kg',      80, 11000, true,  'mobilemoney'),
      // This month
      mkPurch(8,   3, 'Urafiki Textiles Ltd', 'Mafuta ya Alizeti 2L',  100, 7500, true,  'cash'),
      mkPurch(12,  4, 'Kariakoo Wholesalers', 'Sukari 1kg',            300, 2800,  true,  'mobilemoney'),
      mkPurch(18,  5, 'Kariakoo Wholesalers', 'Chai Rangi 500g',       400, 2200,  true,  'cash'),
      mkPurch(25,  6, 'Urafiki Textiles Ltd', 'Maji ya Kunywa 1.5L',   800,  600,  true,  'cash'),
      // Last month
      mkPurch(235, 7, 'Kariakoo Wholesalers', 'Unga wa Ngano 2kg',    200, 3200,  true,  'cash'),
      mkPurch(245, 8, 'Urafiki Textiles Ltd', 'Mafuta ya Alizeti 2L',   90, 7500, true,  'mobilemoney'),
      // 2 months ago
      mkPurch(262, 9, 'Kariakoo Wholesalers', 'Mchele Pemba 5kg',       80, 11000, true, 'cash'),
      mkPurch(275,10, 'Kariakoo Wholesalers', 'Sukari 1kg',            280, 2800,  true,  'mobilemoney'),
      // 3 months ago
      mkPurch(291,11, 'Urafiki Textiles Ltd', 'Chai Rangi 500g',       350, 2200,  true,  'cash'),
      mkPurch(305,12, 'Kariakoo Wholesalers', 'Maji ya Kunywa 1.5L',   700,  600,  true,  'cash'),
      // 4 months ago
      mkPurch(321,13, 'Kariakoo Wholesalers', 'Unga wa Ngano 2kg',     180, 3200,  true,  'cash'),
      mkPurch(335,14, 'Urafiki Textiles Ltd', 'Mafuta ya Alizeti 2L',   80, 7500,  true,  'mobilemoney'),
      // 5 months ago
      mkPurch(351,15, 'Kariakoo Wholesalers', 'Mchele Pemba 5kg',       70, 11000, true,  'cash'),
      mkPurch(365,16, 'Kariakoo Wholesalers', 'Sukari 1kg',            250, 2800,  true,  'mobilemoney'),
      // 6 months ago
      mkPurch(381,17, 'Urafiki Textiles Ltd', 'Chai Rangi 500g',       300, 2200,  true,  'cash'),
      mkPurch(395,18, 'Kariakoo Wholesalers', 'Unga wa Ngano 2kg',     160, 3200,  true,  'cash'),
      // 7 months ago
      mkPurch(411,19, 'Kariakoo Wholesalers', 'Maji ya Kunywa 1.5L',   600,  600,  true,  'cash'),
      mkPurch(425,20, 'Urafiki Textiles Ltd', 'Mafuta ya Alizeti 2L',   70, 7500,  true,  'mobilemoney'),
      // 8 months ago
      mkPurch(441,21, 'Kariakoo Wholesalers', 'Mchele Pemba 5kg',       65, 11000, true,  'cash'),
      mkPurch(455,22, 'Kariakoo Wholesalers', 'Sukari 1kg',            220, 2800,  true,  'mobilemoney'),
      // 9 months ago
      mkPurch(471,23, 'Urafiki Textiles Ltd', 'Chai Rangi 500g',       250, 2200,  true,  'cash'),
      mkPurch(485,24, 'Kariakoo Wholesalers', 'Unga wa Ngano 2kg',     140, 3200,  true,  'cash'),
      // 10 months ago
      mkPurch(501,25, 'Kariakoo Wholesalers', 'Mchele Pemba 5kg',       55, 11000, true,  'cash'),
      mkPurch(515,26, 'Urafiki Textiles Ltd', 'Mafuta ya Alizeti 2L',   60, 7500,  true,  'mobilemoney'),
      // 11 months ago
      mkPurch(531,27, 'Kariakoo Wholesalers', 'Sukari 1kg',            180, 2800,  true,  'mobilemoney'),
      mkPurch(545,28, 'Kariakoo Wholesalers', 'Maji ya Kunywa 1.5L',   500,  600,  true,  'cash'),
      // 12 months ago
      mkPurch(556,29, 'Urafiki Textiles Ltd', 'Chai Rangi 500g',       200, 2200,  true,  'cash'),
      mkPurch(561,30, 'Kariakoo Wholesalers', 'Unga wa Ngano 2kg',     120, 3200,  true,  'mobilemoney'),
    ];

    // Sample services
    const sampleServices = [
      { id: now + 70, name: 'Kukata Nywele',            defaultPrice: 15000, defaultCost: 3000 },
      { id: now + 71, name: 'Kuosha Nywele',            defaultPrice: 12000, defaultCost: 2500 },
      { id: now + 72, name: 'Kusuka Nywele',            defaultPrice: 45000, defaultCost: 8000 },
      { id: now + 73, name: 'Manicure na Pedicure',     defaultPrice: 20000, defaultCost: 4000 },
      { id: now + 74, name: 'Huduma ya Utoaji',         defaultPrice: 10000, defaultCost: 3500 },
    ];

    // Helper for service transaction
    const mkSvc = (offset, svcOffset, svcName, qty, price, cost, customer, method) => {
      const svcId = now + svcOffset;
      return {
        id: now + 800 + offset,
        serviceId: svcId,
        serviceName: svcName,
        quantity: qty,
        price,
        cost,
        totalRevenue: qty * price,
        totalCost: qty * cost,
        profit: qty * (price - cost),
        customerName: customer,
        paymentMethod: method,
        isPaid: true,
        date: d(offset > 200 ? offset - 200 : offset),
      };
    };

    const sampleServiceTransactions = [
      // Today
      mkSvc(0,  70, 'Kukata Nywele',    1, 15000, 3000, 'Amina Juma',        'cash'),
      // This week
      mkSvc(1,  72, 'Kusuka Nywele', 1, 45000, 8000, 'Fatuma Hassan',     'mobilemoney'),
      mkSvc(2,  71, 'Kuosha Nywele', 2, 12000, 2500, 'Walk-in Customer',  'cash'),
      mkSvc(3,  73, 'Manicure na Pedicure',  1, 20000, 4000, 'Baraka Enterprises','bank'),
      mkSvc(4,  74, 'Huduma ya Utoaji',   3, 10000, 3500, 'Joram Mwangi',      'cash'),
      mkSvc(5,  70, 'Kukata Nywele',    2, 15000, 3000, 'Grace Mollel',      'cash'),
      mkSvc(6,  72, 'Kusuka Nywele', 1, 45000, 8000, 'Walk-in Customer',  'mobilemoney'),
      // This month
      mkSvc(8,  71, 'Kuosha Nywele', 3, 12000, 2500, 'Amina Juma',        'cash'),
      mkSvc(10, 73, 'Manicure na Pedicure',  2, 20000, 4000, 'Fatuma Hassan',     'cash'),
      mkSvc(13, 74, 'Huduma ya Utoaji',   5, 10000, 3500, 'Baraka Enterprises','mobilemoney'),
      mkSvc(16, 70, 'Kukata Nywele',    1, 15000, 3000, 'Walk-in Customer',  'cash'),
      mkSvc(20, 72, 'Kusuka Nywele', 2, 45000, 8000, 'Grace Mollel',      'bank'),
      mkSvc(25, 71, 'Kuosha Nywele', 1, 12000, 2500, 'Joram Mwangi',      'cash'),
      // Last month
      mkSvc(231, 73, 'Manicure na Pedicure', 1, 20000, 4000, 'Amina Juma',        'cash'),
      mkSvc(237, 74, 'Huduma ya Utoaji',  4, 10000, 3500, 'Walk-in Customer',  'mobilemoney'),
      mkSvc(243, 70, 'Kukata Nywele',   3, 15000, 3000, 'Fatuma Hassan',     'cash'),
      mkSvc(250, 72, 'Kusuka Nywele',1, 45000, 8000,  'Baraka Enterprises','bank'),
      // 2 months ago
      mkSvc(261, 71, 'Kuosha Nywele',2, 12000, 2500, 'Grace Mollel',      'cash'),
      mkSvc(273, 73, 'Manicure na Pedicure', 1, 20000, 4000, 'Joram Mwangi',      'cash'),
      mkSvc(285, 70, 'Kukata Nywele',   2, 15000, 3000, 'Amina Juma',        'mobilemoney'),
      // 3 months ago
      mkSvc(292, 74, 'Huduma ya Utoaji',  3, 10000, 3500, 'Walk-in Customer',  'cash'),
      mkSvc(305, 72, 'Kusuka Nywele',1, 45000, 8000,  'Fatuma Hassan',     'cash'),
      // 4 months ago
      mkSvc(321, 70, 'Kukata Nywele',   2, 15000, 3000, 'Amina Juma',        'cash'),
      mkSvc(332, 73, 'Manicure na Pedicure', 2, 20000, 4000, 'Grace Mollel',      'cash'),
      mkSvc(345, 74, 'Huduma ya Utoaji',  4, 10000, 3500, 'Baraka Enterprises','mobilemoney'),
      // 5 months ago
      mkSvc(351, 72, 'Kusuka Nywele',2, 45000, 8000,  'Walk-in Customer',  'cash'),
      mkSvc(363, 71, 'Kuosha Nywele',3, 12000, 2500, 'Fatuma Hassan',     'cash'),
      mkSvc(375, 70, 'Kukata Nywele',   1, 15000, 3000, 'Joram Mwangi',      'mobilemoney'),
      // 6 months ago
      mkSvc(381, 73, 'Manicure na Pedicure', 1, 20000, 4000, 'Amina Juma',        'cash'),
      mkSvc(393, 74, 'Huduma ya Utoaji',  5, 10000, 3500, 'Walk-in Customer',  'cash'),
      mkSvc(406, 72, 'Kusuka Nywele',1, 45000, 8000,  'Grace Mollel',      'bank'),
      // 7 months ago
      mkSvc(411, 70, 'Kukata Nywele',   3, 15000, 3000, 'Fatuma Hassan',     'cash'),
      mkSvc(423, 71, 'Kuosha Nywele',2, 12000, 2500, 'Baraka Enterprises','mobilemoney'),
      // 8 months ago
      mkSvc(441, 73, 'Manicure na Pedicure', 2, 20000, 4000, 'Walk-in Customer',  'cash'),
      mkSvc(453, 72, 'Kusuka Nywele',1, 45000, 8000,  'Amina Juma',        'cash'),
      mkSvc(465, 74, 'Huduma ya Utoaji',  3, 10000, 3500, 'Joram Mwangi',      'cash'),
      // 9 months ago
      mkSvc(471, 70, 'Kukata Nywele',   2, 15000, 3000, 'Grace Mollel',      'mobilemoney'),
      mkSvc(483, 71, 'Kuosha Nywele',1, 12000, 2500, 'Fatuma Hassan',     'cash'),
      // 10 months ago
      mkSvc(501, 72, 'Kusuka Nywele',1, 45000, 8000,  'Walk-in Customer',  'cash'),
      mkSvc(513, 73, 'Manicure na Pedicure', 1, 20000, 4000, 'Amina Juma',        'cash'),
      // 11 months ago
      mkSvc(531, 74, 'Huduma ya Utoaji',  2, 10000, 3500, 'Baraka Enterprises','mobilemoney'),
      mkSvc(543, 70, 'Kukata Nywele',   1, 15000, 3000, 'Walk-in Customer',  'cash'),
      // 12 months ago
      mkSvc(556, 72, 'Kusuka Nywele',1, 45000, 8000,  'Fatuma Hassan',     'cash'),
    ];

    // Helper for an expense record
    const mkExp = (offset, desc, cat, amount, method) => ({
      id: now + 600 + offset,
      date: d(offset > 200 ? offset - 200 : offset),
      description: desc,
      category: cat,
      amount,
      paymentMethod: method,
      notes: '',
    });

    const sampleExpenses = [
      // Today & this week (month 1)
      mkExp(10, 'Kodi ya Duka - Mwezi Huu',     'Kodi',       200000, 'bank'),
      mkExp(2,  'Bili ya Umeme TANESCO',         'Huduma',      48000, 'mobilemoney'),
      mkExp(18, 'Posho ya Msaidizi',             'Mishahara',  300000, 'bank'),
      mkExp(5,  'Usafiri wa Utoaji',             'Usafiri',     28000, 'cash'),
      mkExp(6,  'Matangazo ya Facebook',         'Masoko',      15000, 'mobilemoney'),
      // This month (more items)
      mkExp(9,  'Bili ya Maji',                  'Huduma',       8000, 'cash'),
      mkExp(12, 'Mafuta ya Utoaji',              'Usafiri',     25000, 'cash'),
      mkExp(16, 'Matangazo ya Instagram',        'Masoko',      12000, 'mobilemoney'),
      mkExp(20, 'Vifaa vya Kufungashia',         'Usafiri',     10000, 'cash'),
      mkExp(25, 'Bili ya Simu',                  'Huduma',       5000, 'mobilemoney'),
      // Last month
      mkExp(231,'Kodi ya Duka - Mwezi Uliopita', 'Kodi',       200000, 'bank'),
      mkExp(234,'Bili ya Umeme - Mwezi Uliopita','Huduma',      45000, 'mobilemoney'),
      mkExp(237,'Posho ya Msaidizi',             'Mishahara',  300000, 'bank'),
      mkExp(241,'Usafiri wa Utoaji',             'Usafiri',     22000, 'cash'),
      mkExp(248,'Matangazo ya Mitandao',         'Masoko',      12000, 'mobilemoney'),
      // 2 months ago
      mkExp(262,'Kodi ya Duka',                  'Kodi',       200000, 'bank'),
      mkExp(267,'Bili ya Umeme',                 'Huduma',      50000, 'mobilemoney'),
      mkExp(274,'Posho ya Msaidizi',             'Mishahara',  300000, 'bank'),
      mkExp(281,'Usafiri na Vifaa',              'Usafiri',     30000, 'cash'),
      // 3 months ago
      mkExp(292,'Kodi ya Duka',                  'Kodi',       200000, 'bank'),
      mkExp(300,'Bili ya Umeme',                 'Huduma',      46000, 'mobilemoney'),
      mkExp(308,'Posho ya Msaidizi',             'Mishahara',  300000, 'bank'),
      mkExp(315,'Mafuta ya Utoaji',              'Usafiri',     20000, 'cash'),
      // 4 months ago
      mkExp(321,'Kodi ya Duka',                  'Kodi',       200000, 'bank'),
      mkExp(328,'Bili ya Umeme',                 'Huduma',      44000, 'mobilemoney'),
      mkExp(335,'Posho ya Msaidizi',             'Mishahara',  280000, 'bank'),
      mkExp(340,'Mafuta ya Utoaji',              'Usafiri',     22000, 'cash'),
      // 5 months ago
      mkExp(351,'Kodi ya Duka',                  'Kodi',       200000, 'bank'),
      mkExp(358,'Bili ya Umeme',                 'Huduma',      42000, 'mobilemoney'),
      mkExp(365,'Posho ya Msaidizi',             'Mishahara',  280000, 'bank'),
      mkExp(372,'Matangazo ya Facebook',         'Masoko',      10000, 'mobilemoney'),
      // 6 months ago
      mkExp(381,'Kodi ya Duka',                  'Kodi',       180000, 'bank'),
      mkExp(388,'Bili ya Umeme',                 'Huduma',      40000, 'mobilemoney'),
      mkExp(395,'Posho ya Msaidizi',             'Mishahara',  260000, 'bank'),
      mkExp(402,'Usafiri wa Utoaji',             'Usafiri',     20000, 'cash'),
      // 7 months ago
      mkExp(411,'Kodi ya Duka',                  'Kodi',       180000, 'bank'),
      mkExp(418,'Bili ya Umeme',                 'Huduma',      38000, 'mobilemoney'),
      mkExp(425,'Posho ya Msaidizi',             'Mishahara',  250000, 'bank'),
      // 8 months ago
      mkExp(441,'Kodi ya Duka',                  'Kodi',       180000, 'bank'),
      mkExp(448,'Bili ya Umeme',                 'Huduma',      36000, 'mobilemoney'),
      mkExp(455,'Posho ya Msaidizi',             'Mishahara',  250000, 'bank'),
      mkExp(460,'Mafuta ya Utoaji',              'Usafiri',     18000, 'cash'),
      // 9 months ago
      mkExp(471,'Kodi ya Duka',                  'Kodi',       180000, 'bank'),
      mkExp(478,'Bili ya Umeme',                 'Huduma',      35000, 'mobilemoney'),
      mkExp(485,'Posho ya Msaidizi',             'Mishahara',  230000, 'bank'),
      // 10 months ago
      mkExp(501,'Kodi ya Duka',                  'Kodi',       150000, 'bank'),
      mkExp(508,'Bili ya Umeme',                 'Huduma',      32000, 'mobilemoney'),
      mkExp(515,'Posho ya Msaidizi',             'Mishahara',  220000, 'bank'),
      // 11 months ago
      mkExp(531,'Kodi ya Duka',                  'Kodi',       150000, 'bank'),
      mkExp(538,'Bili ya Umeme',                 'Huduma',      30000, 'mobilemoney'),
      mkExp(545,'Posho ya Msaidizi',             'Mishahara',  200000, 'bank'),
      // 12 months ago
      mkExp(556,'Kodi ya Duka',                  'Kodi',       150000, 'bank'),
      mkExp(561,'Posho ya Msaidizi',             'Mishahara',  200000, 'bank'),
    ];

    // Sample proforma invoices
    const sampleProformas = [
      {
        id: now + 90,
        proformaNumber: 'PROF-0001',
        customerName: 'Baraka Enterprises',
        customerPhone: '+255754321098',
        customerAddress: 'Kariakoo, Dar es Salaam',
        date: d(5),
        discount: '',
        taxes: [],
        payments: [{ type: 'cash', amount: '' }],
        items: [
          { description: 'Mchele Pemba 5kg',  qty: '20', unitPrice: '14000', total: 280000 },
          { description: 'Unga wa Ngano 2kg', qty: '10', unitPrice: '4000',  total: 40000 },
        ],
        createdAt: new Date(now - 5 * 86400000).toISOString(),
      },
      {
        id: now + 91,
        proformaNumber: 'PROF-0002',
        customerName: 'Joram Mwangi',
        customerPhone: '+255789012345',
        customerAddress: 'Arusha',
        date: d(3),
        discount: '5',
        taxes: [],
        payments: [{ type: 'bank', amount: '' }],
        items: [
          { description: 'Kusuka Nywele', qty: '1', unitPrice: '45000', total: 45000 },
          { description: 'Manicure na Pedicure',  qty: '1', unitPrice: '20000', total: 20000 },
        ],
        createdAt: new Date(now - 3 * 86400000).toISOString(),
      },
      {
        id: now + 92,
        proformaNumber: 'PROF-0003',
        customerName: 'Fatuma Hassan',
        customerPhone: '+255765432109',
        customerAddress: 'Mwanza City',
        date: d(1),
        discount: '',
        taxes: [],
        payments: [{ type: 'mobilemoney', amount: '' }],
        items: [
          { description: 'Huduma ya Utoaji', qty: '5', unitPrice: '10000', total: 50000 },
        ],
        createdAt: new Date(now - 1 * 86400000).toISOString(),
      },
      {
        id: now + 93,
        proformaNumber: 'PROF-0004',
        customerName: 'Grace Mollel',
        customerPhone: '+255733456789',
        customerAddress: 'Dodoma',
        date: d(20),
        discount: '',
        taxes: [],
        payments: [{ type: 'cash', amount: '' }],
        items: [
          { description: 'Chai Rangi 500g',       qty: '30', unitPrice: '2800', total: 84000 },
          { description: 'Maji ya Kunywa 1.5L',   qty: '50', unitPrice: '900',  total: 45000 },
        ],
        createdAt: new Date(now - 20 * 86400000).toISOString(),
      },
    ];

    // Issued invoices -one per sale for the most recent sales
    const mkInv = (offset, num, customer, product, qty, sell, paid, method) => ({
      id: now + 700 + offset,
      invoiceNumber: `INV-${String(num).padStart(4, '0')}`,
      customerName: customer,
      date: d(offset > 200 ? offset - 200 : offset),
      totalRevenue: qty * sell,
      isPaid: paid,
      paymentMethod: method,
      productName: product,
      quantity: qty,
      sellingPrice: sell,
      createdAt: new Date(now - (offset > 200 ? offset - 200 : offset) * 86400000).toISOString(),
    });
    const sampleIssuedInvoices = [
      mkInv(0,   1,  'Amina Juma',        'Unga wa Ngano 2kg',    5,  4000,  true,  'cash'),
      mkInv(1,   2,  'Baraka Enterprises','Mchele Pemba 5kg',     10, 14000, true,  'mobilemoney'),
      mkInv(2,   3,  'Fatuma Hassan',     'Mafuta ya Alizeti 2L',  8,  9500, true,  'cash'),
      mkInv(3,   4,  'Joram Mwangi',      'Sukari 1kg',           20,  3500, false, 'bank'),
      mkInv(4,   5,  'Grace Mollel',      'Maji ya Kunywa 1.5L',  30,   900, true,  'cash'),
      mkInv(5,   6,  'Amina Juma',        'Sabuni ya Kufulia',    12,  2000, true,  'cash'),
      mkInv(6,   7,  'Walk-in Customer',  'Unga wa Ngano 2kg',     3,  4000, true,  'cash'),
      mkInv(7,   8,  'Baraka Enterprises','Mchele Pemba 5kg',      6, 14000, true,  'bank'),
      mkInv(9,   9,  'Fatuma Hassan',     'Sukari 1kg',           15,  3500, false, 'cash'),
      mkInv(11, 10,  'Joram Mwangi',      'Mafuta ya Alizeti 2L',  4,  9500, true,  'mobilemoney'),
      mkInv(1,  11,  'Fatuma Hassan',     'Kusuka Nywele',   1, 45000, true,  'mobilemoney'),
      mkInv(2,  12,  'Walk-in Customer',  'Kuosha Nywele',  2, 12000, true,  'cash'),
      mkInv(3,  13,  'Baraka Enterprises','Manicure na Pedicure',   1, 20000, true,  'bank'),
      mkInv(4,  14,  'Joram Mwangi',      'Huduma ya Utoaji',    3, 10000, true,  'cash'),
      mkInv(231,15,  'Joram Mwangi',      'Unga wa Ngano 2kg',    10,  4000, true,  'cash'),
      mkInv(237,16,  'Amina Juma',        'Mafuta ya Alizeti 2L',  6,  9500, true,  'mobilemoney'),
    ];

    setCustomers(prev => [...(prev || []), ...sampleCustomers]);
    setSuppliers(prev => [...(prev || []), ...sampleSuppliers]);
    setProducts(prev => [...(prev || []), ...sampleProducts]);
    setServices(prev => [...(prev || []), ...sampleServices]);
    setExpenseCategories(prev => {
      const existing = new Set((prev || []).map(c => c.name.toLowerCase()));
      return [...(prev || []), ...sampleExpCats.filter(c => !existing.has(c.name.toLowerCase()))];
    });
    // Opening balance deposits -seeds realistic account balances
    const sampleManualTransactions = [
      // Opening capitals -set 12 months ago so all history starts from positive
      { id: now + 9001, date: d(365), account: 'cash',        type: 'credit', amount: 9000000,  description: 'Mtaji wa Kuanzia - Taslimu' },
      { id: now + 9002, date: d(365), account: 'bank',        type: 'credit', amount: 3000000,  description: 'Mtaji wa Kuanzia - Benki' },
      { id: now + 9003, date: d(365), account: 'mobilemoney', type: 'credit', amount: 6500000,  description: 'Mtaji wa Kuanzia - Pesa ya Simu' },
      // Mid-year capital injections
      { id: now + 9004, date: d(200), account: 'bank',        type: 'credit', amount: 1500000,  description: 'Mtaji wa Ziada - Benki' },
      { id: now + 9005, date: d(200), account: 'cash',        type: 'credit', amount: 500000,   description: 'Mtaji wa Ziada - Taslimu' },
      // Recent top-ups
      { id: now + 9006, date: d(90),  account: 'bank',        type: 'credit', amount: 800000,   description: 'Mapato ya Ziada - Benki' },
      { id: now + 9007, date: d(45),  account: 'mobilemoney', type: 'credit', amount: 300000,   description: 'Ongeza Pesa ya Simu' },
      { id: now + 9008, date: d(15),  account: 'cash',        type: 'credit', amount: 300000,   description: 'Ongeza Taslimu' },
    ];

    // REPLACE all existing data (not append) so reloading always gives clean fresh figures
    setSales(sampleSales);
    setPurchases(samplePurchases);
    setExpenses(sampleExpenses);
    setServiceTransactions(sampleServiceTransactions);
    setProformaInvoices(sampleProformas);
    setIssuedInvoices(sampleIssuedInvoices);
    setManualTransactions(sampleManualTransactions);
    setCustomers(sampleCustomers);
    setSuppliers(sampleSuppliers);
    setProducts(sampleProducts);
    setServices(sampleServices);
    setExpenseCategories(sampleExpCats);
    setInvoiceCounter(60);
    setProformaCounter(4);

    showAlert('Takwimu za mfano zimepakiwa! Angalia Dashibodi, Mauzo, Huduma, Matumizi - data inashughulikia miezi 12 iliyopita.', { tone: 'info', title: 'Sample data loaded' });
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      // Clear local state
      setProducts([]);
      setSales([]);
      setPurchases([]);
      setExpenses([]);
      setExpenseCategories([]);
      setSuppliers([]);
      setCustomers([]);
      setManualTransactions([]);
      setInvoiceCounter(0);
      setProformaCounter(0);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') return;
    setDeletingAccount(true);
    try {
      const user = auth.currentUser;
      const userId = user?.uid;
      const token = user?.accessToken;

      // 1. Wipe all cloud data
      if (userId && token) {
        await deleteAllUserData(userId, token);
      }

      // 2. Wipe all localStorage keys for this user
      const prefix = userId ? `faida_${userId}_` : 'faida_';
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith(prefix)) keysToRemove.push(k);
      }
      keysToRemove.forEach(k => localStorage.removeItem(k));

      // 3. Sign out (account auth deletion requires admin SDK -user signs out and data is gone)
      await signOut(auth);
      setProducts([]); setSales([]); setPurchases([]); setExpenses([]);
      setExpenseCategories([]); setSuppliers([]); setCustomers([]);
      setManualTransactions([]); setInvoiceCounter(0); setProformaCounter(0);
      setShowDeleteAccountModal(false);
      setDeleteConfirmText('');
    } catch (err) {
      console.error('Delete account error:', err);
    } finally {
      setDeletingAccount(false);
    }
  };

  const getBizInfo = () => ({
    name: businessName,
    address: businessAddress,
    logo: businessLogo,
    bankName, bankAccountName, bankAccountNumber,
    mobileMoneyProvider, mobileMoneyNumber,
    tin: businessTIN,
    vrn: businessVRN,
    regNo: businessRegNo,
    licenseNo: tradeLicenseNo,
    invoiceTerms,
  });

  const resetProformaData = () => {
    setProformaData({
      customerName: '',
      customerPhone: '',
      customerAddress: '',
      date: new Date().toISOString().split('T')[0],
      payments: [{ type: 'cash', amount: '' }],
      discount: '',
      taxes: [],
      items: [{ productName: '', quantity: '', unitPrice: '', total: '' }]
    });
    setProformaValidityDays(7);
    setProformaFooterSections([{ label: 'Terms & Conditions', content: '' }]);
  };

  // ── Manual Invoice helpers (proforma-style dedicated form) ───────────
  const resetManualInvoiceData = () => {
    setManualInvoiceData({
      customerName: '', customerPhone: '', customerEmail: '', customerAddress: '',
      customerTIN: '', customerVRN: '',
      date: new Date().toISOString().split('T')[0],
      discount: '', taxes: [],
      items: [{ productName: '', quantity: '', unitPrice: '', total: '' }],
    });
    setManualInvoiceCustomerType('walkin');
    setManualInvoiceFooterSections([{ label: 'Terms & Conditions', content: '' }]);
  };

  const handleManualInvoiceItemChange = (index, field, value) => {
    setManualInvoiceData(prev => {
      const items = (prev.items || []).map((it, i) => {
        if (i !== index) return it;
        const updated = { ...it, [field]: value };
        if (field === 'productName') {
          const selProd = (products || []).find(p => p.productName === value);
          if (selProd) {
            updated.unitPrice = String(selProd.sellingPrice || selProd.lastSellingPrice || '');
            const qty = parseFloat(updated.quantity) || 0;
            const price = parseFloat(updated.unitPrice) || 0;
            if (qty > 0 && price > 0) updated.total = String(qty * price);
          }
        }
        if (field === 'quantity' || field === 'unitPrice') {
          const qty = parseFloat(field === 'quantity' ? value : it.quantity) || 0;
          const price = parseFloat(field === 'unitPrice' ? value : it.unitPrice) || 0;
          if (qty > 0 && price > 0) updated.total = String(qty * price);
          else if (field === 'unitPrice' && !value) updated.total = '';
        }
        return updated;
      });
      return { ...prev, items };
    });
  };
  const addManualInvoiceItem = () => {
    setManualInvoiceData(prev => ({ ...prev, items: [...(prev.items || []), { productName: '', quantity: '', unitPrice: '', total: '' }] }));
  };
  const removeManualInvoiceItem = (index) => {
    setManualInvoiceData(prev => ({ ...prev, items: (prev.items || []).filter((_, i) => i !== index) }));
  };
  const addManualInvoiceTax = () => {
    setManualInvoiceData(prev => ({ ...prev, taxes: [...(prev.taxes || []), { type: 'vat', rate: 18 }] }));
  };
  const removeManualInvoiceTax = (index) => {
    setManualInvoiceData(prev => ({ ...prev, taxes: (prev.taxes || []).filter((_, i) => i !== index) }));
  };
  const updateManualInvoiceTax = (index, field, value) => {
    setManualInvoiceData(prev => {
      const taxes = (prev.taxes || []).map((t, i) => i === index ? { ...t, [field]: value } : t);
      return { ...prev, taxes };
    });
  };

  const handleSaveManualInvoice = () => {
    const validItems = (manualInvoiceData.items || []).filter(it => it.productName && it.quantity && it.unitPrice);
    if (validItems.length === 0) {
      showAlert(language === 'sw' ? 'Tafadhali jaza bidhaa, idadi, na bei kwa kila kipengele.' : 'Please fill in product, quantity and unit price for each item.', { tone: 'warn' });
      return;
    }
    if (!manualInvoiceData.customerName?.trim()) {
      showAlert(language === 'sw' ? 'Jina la mteja linahitajika kwa ankara isiyolipwa.' : 'Customer name is required for an unpaid invoice.', { tone: 'warn' });
      return;
    }
    // Compute totals
    const subtotal = validItems.reduce((s, it) => s + (parseFloat(it.quantity) || 0) * (parseFloat(it.unitPrice) || 0), 0);
    const discPct = parseFloat(manualInvoiceData.discount || 0);
    const discountAmount = subtotal * (discPct / 100);
    const afterDisc = Math.max(0, subtotal - discountAmount);
    const totalTax = (manualInvoiceData.taxes || []).reduce((s, tx) => s + afterDisc * (parseFloat(tx.rate) || 0) / 100, 0);
    const totalRevenue = afterDisc + totalTax;
    // Assign or preserve invoice number
    let invoiceNumber;
    if (editingManualInvoiceId) {
      const existing = (manualInvoices || []).find(m => m.id === editingManualInvoiceId);
      invoiceNumber = existing?.invoiceNumber || `INV-${String(invoiceCounter + 1).padStart(4, '0')}`;
    } else {
      const next = invoiceCounter + 1;
      invoiceNumber = `INV-${String(next).padStart(4, '0')}`;
      setInvoiceCounter(next);
      localStorage.setItem(getUserKey('invoiceCounter'), String(next));
    }
    // Build manual invoice record (shape compatible with existing manualInvoices/PDF)
    const items = validItems.map(it => ({
      type: 'product',
      productName: it.productName,
      quantity: it.quantity,
      sellingPrice: it.unitPrice,
      cost: '0',
    }));
    const inv = {
      id: editingManualInvoiceId || Date.now(),
      invoiceNumber,
      productName: items[0]?.productName || '',
      quantity: items[0]?.quantity || '1',
      sellingPrice: items[0]?.sellingPrice || '0',
      cost: '0',
      items,
      totalRevenue,
      totalCost: 0,
      profit: totalRevenue,
      customerName: manualInvoiceData.customerName,
      customerType: manualInvoiceCustomerType,
      customerDetails: {
        phone: manualInvoiceData.customerPhone || '',
        email: manualInvoiceData.customerEmail || '',
        address: manualInvoiceData.customerAddress || '',
        tin: manualInvoiceData.customerTIN || '',
        vrn: manualInvoiceData.customerVRN || '',
      },
      paymentMethod: 'credit',
      payments: [{ type: 'credit', amount: totalRevenue }],
      isPaid: false,
      isManualInvoice: true,
      saveMode: 'invoice',
      date: manualInvoiceData.date,
      discount: discPct,
      discountAmount,
      taxes: manualInvoiceData.taxes || [],
      footerSections: manualInvoiceFooterSections.filter(s => s.label || s.content),
      kind: (editingManualInvoiceId
        ? (((manualInvoices || []).find(m => m.id === editingManualInvoiceId) || {}).kind)
        : null) || (invoiceMgmtContext === 'services' ? 'service' : 'sale'),
      createdAt: new Date().toISOString(),
    };
    if (editingManualInvoiceId) {
      setManualInvoices(prev => (prev || []).map(m => m.id === editingManualInvoiceId ? inv : m));
      // NOTE: No update_invoice command exists. Invoice edits are local-only during transition.
      setEditingManualInvoiceId(null);
    } else {
      setManualInvoices(prev => [...(prev || []), inv]);
      // MIGRATION-ONLY dual-write: persist invoice draft to relational DB
      if (businessId && currentUser?.accessToken) {
        persistInvoice(businessId, inv, currentUser.accessToken).then(r => {
          const rid = r?.data?.invoice_id;
          if (rid) setManualInvoices(prev => prev.map(i => i.id === inv.id ? { ...i, _rid: rid, _rv: 1 } : i));
        }).catch(err => console.warn('[dual-write] invoice:', err.message));
      }
    }
    setSuccessMessage(`Invoice ${inv.invoiceNumber} saved - awaiting payment`);
    setTimeout(() => setSuccessMessage(''), 3000);
    resetManualInvoiceData();
    setShowManualInvoiceForm(false);
  };

  const handleProformaItemChange = (index, field, value) => {
    setProformaData(prev => {
      const items = (prev.items || []).map((it, i) => {
        if (i !== index) return it;
        const updated = { ...it, [field]: value };
        if (field === 'quantity' || field === 'unitPrice') {
          const qty = parseFloat(field === 'quantity' ? value : it.quantity) || 0;
          const price = parseFloat(field === 'unitPrice' ? value : it.unitPrice) || 0;
          if (qty > 0 && price > 0) updated.total = String(qty * price);
          else if (field === 'unitPrice' && !value) updated.total = '';
        }
        return updated;
      });
      return { ...prev, items };
    });
  };

  const addProformaItem = () => {
    setProformaData(prev => ({ ...prev, items: [...(prev.items || []), { productName: '', quantity: '', unitPrice: '', total: '' }] }));
  };

  const addSaleTax = () => {
    setSalesFormData(prev => ({ ...prev, taxes: [...(prev.taxes || []), { type: 'vat', rate: defaultVatRate }] }));
  };

  const removeSaleTax = (index) => {
    setSalesFormData(prev => ({ ...prev, taxes: (prev.taxes || []).filter((_, i) => i !== index) }));
  };

  const updateSaleTax = (index, field, value) => {
    setSalesFormData(prev => {
      const taxes = (prev.taxes || []).map((t, i) => i === index ? { ...t, [field]: value } : t);
      return { ...prev, taxes };
    });
  };

  const removeProformaItem = (index) => {
    setProformaData(prev => ({ ...prev, items: (prev.items || []).filter((_, i) => i !== index) }));
  };

  const addTax = () => {
    setProformaData(prev => ({ ...prev, taxes: [...(prev.taxes || []), { type: 'vat', rate: 18 }] }));
  };

  const removeTax = (index) => {
    setProformaData(prev => ({ ...prev, taxes: (prev.taxes || []).filter((_, i) => i !== index) }));
  };

  const updateTax = (index, field, value) => {
    setProformaData(prev => {
      const taxes = (prev.taxes || []).map((t, i) => i === index ? { ...t, [field]: value } : t);
      return { ...prev, taxes };
    });
  };

  const handleGenerateProforma = () => {
    const validItems = (proformaData.items || []).filter(it => it.productName && it.quantity && it.unitPrice);
    if (validItems.length === 0) { showAlert(language === 'sw' ? 'Tafadhali jaza bidhaa, idadi, na bei kwa kila kipengele.' : 'Please fill in product, quantity and unit price for each item.', { tone: 'warn' }); return; }
    const incomplete = (proformaData.items || []).some(it => it.productName && (!it.quantity || !it.unitPrice));
    if (incomplete) { showAlert(language === 'sw' ? 'Baadhi ya vipengele havina idadi au bei. Tafadhali jaza.' : 'Some items are missing quantity or unit price.', { tone: 'warn' }); return; }

    // Only assign a new number when creating -preserve original number on edit
    let proformaNumber;
    if (editingProformaId) {
      const existing = (proformaInvoices || []).find(p => p.id === editingProformaId);
      proformaNumber = existing?.proformaNumber || `PROF-${String(proformaCounter).padStart(4, '0')}`;
    } else {
      const nextCount = proformaCounter + 1;
      proformaNumber = `PROF-${String(nextCount).padStart(4, '0')}`;
      setProformaCounter(nextCount);
      localStorage.setItem(getUserKey('proformaCounter'), String(nextCount));
    }

    // Calculate expiry date from validity days
    const issueDate = new Date(proformaData.date || new Date());
    const expiryDate = new Date(issueDate);
    expiryDate.setDate(expiryDate.getDate() + parseInt(proformaValidityDays || 7));
    const expiryStr = expiryDate.toISOString().split('T')[0];

    // Preserve existing 'kind' on edits; otherwise stamp it from the open context.
    const existingKind = editingProformaId
      ? ((proformaInvoices || []).find(p => p.id === editingProformaId) || {}).kind
      : null;
    const resolvedKind = existingKind
      || (invoiceMgmtContext === 'services' ? 'service' : 'sale');
    const proformaInvoice = {
      updatedAt: new Date().toISOString(),
      id: editingProformaId || Date.now(),
      proformaNumber,
      kind: resolvedKind,
      customerType: proformaCustomerType,
      customerName: proformaData.customerName,
      customerPhone: proformaData.customerPhone,
      customerAddress: proformaData.customerAddress,
      customerEmail: proformaData.customerEmail || '',
      customerTIN: proformaData.customerTIN || '',
      customerVRN: proformaData.customerVRN || '',
      date: proformaData.date,
      discount: proformaData.discount,
      taxes: proformaData.taxes,
      items: validItems,
      validityDays: parseInt(proformaValidityDays || 7),
      expiryDate: expiryStr,
      footerSections: proformaFooterSections.filter(s => s.label || s.content),
      createdAt: new Date().toISOString()
    };

    if (editingProformaId) {
      setProformaInvoices(prev => (prev || []).map(p => p.id === editingProformaId ? proformaInvoice : p));
      setEditingProformaId(null);
    } else {
      setProformaInvoices(prev => [...(prev || []), proformaInvoice]);
    }

    localStorage.setItem(getUserKey('proformaInvoices'), JSON.stringify(editingProformaId
      ? (proformaInvoicesRaw || []).map(p => p.id === editingProformaId ? proformaInvoice : p)
      : [...(proformaInvoicesRaw || []), proformaInvoice]));

    setSavedProformaRecord(proformaInvoice);
    resetProformaData();
    setShowProformaForm(false);
  };

  const drilldownTitle =
    activeDrilldown === 'sales' ? t.salesDetails :
    activeDrilldown === 'products_only' ? t.productRevenue :
    activeDrilldown === 'services_only' ? t.serviceRevenue :
    activeDrilldown === 'total_revenue' ? t.totalRevenue :
    activeDrilldown === 'purchases' ? t.purchaseDetails :
    activeDrilldown === 'expenses' ? t.expenseDetails :
    activeDrilldown === 'cash' ? t.cashAccountTitle :
    activeDrilldown === 'bank' ? t.bankAccountTitle :
    activeDrilldown === 'mobilemoney' ? t.mobileMoneyAccountTitle :
    activeDrilldown === 'total' ? (language === 'sw' ? 'Mtiririko wa Ki-Fedha (Jumla)' : 'Total Cashflow') :
    activeDrilldown === 'receivables' ? t.receivablesLabel :
    activeDrilldown === 'payables' ? t.payablesLabel :
    activeDrilldown === 'stock' ? t.itemsInStock :
    activeDrilldown === 'lowstock' ? t.lowStock : '';

  // Show loading screen while checking auth
  if (authLoading) {
    return (
      <div className="App" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: 'var(--bg-tab)' }}>
        <div style={{ textAlign: 'center' }}>
          <div className="loading-spinner loading-spinner-lg" style={{ margin: '0 auto 16px' }}></div>
          <div style={{ color: 'var(--text-secondary)', fontSize: 14 }}>{t.loadingApp || 'Loading...'}</div>
        </div>
      </div>
    );
  }

  // Show authentication screens if not logged in
  if (!currentUser) {
    // Hero tagline -appears on Login/Signup/Forgot/Reset
    const tagline = t.appTagline || 'Run your business smarter. Invoices, inventory, and reports in one place.';

    // Success toast
    const authToast = successMessage ? (
      <div style={{ position: 'fixed', top: 14, left: '50%', transform: 'translateX(-50%)', zIndex: 1200, background: '#0A3D2E', color: '#FDE047', padding: '10px 14px', borderRadius: 12, fontWeight: 700, fontSize: 13, boxShadow: '0 10px 25px rgba(0,0,0,0.18)', maxWidth: 'min(520px, calc(100vw - 24px))', textAlign: 'center' }}>
        {successMessage}
      </div>
    ) : null;

    // Language switcher
    const langSwitcher = (
      <div className="auth-lang">
        <button type="button" onClick={() => setLanguage('en')} className={language === 'en' ? 'active' : ''}>EN</button>
        <button type="button" onClick={() => setLanguage('sw')} className={language === 'sw' ? 'active' : ''}>SW</button>
      </div>
    );

    // Hero panel (top on mobile, left side on desktop) -matches Option C mockup exactly
    // backTo: screen name to navigate back to (null = no back button, this is the landing)
    const renderHero = ({ title, accent, sub, backTo = null }) => (
      <div className="auth-hero">
        <div className="auth-hero-topbar">
          {backTo ? (
            <button type="button" onClick={() => { setAuthScreen(backTo); setAuthError(''); setShowPassword(false); setShowConfirmPassword(false); }} className="auth-back" aria-label="Back">‹</button>
          ) : <div style={{ width: 26 }} />}
          {langSwitcher}
        </div>

        {/* Brand cluster (logo + wordmark) -top */}
        <div className="auth-hero-brand">
          <div className="auth-logo-mark"><span>B</span></div>
          <div>
            <p className="auth-brand-name">BIASHARA TRACK</p>
            <p className="auth-brand-sub">PROFESSIONAL BUSINESS</p>
          </div>
        </div>

        {/* Headline + tagline -middle */}
        <div className="auth-hero-headline">
          <h1 className="auth-hero-h1">
            {title}{accent ? <><br /><span className="accent">{accent}</span></> : null}
          </h1>
          {sub && <p className="auth-hero-sub">{sub}</p>}
        </div>

        {/* Decorative dots -bottom (desktop only) */}
        <div className="auth-hero-dots" aria-hidden="true">
          <span className="dot dot-y"></span>
          <span className="dot dot-t"></span>
          <span className="dot dot-tf"></span>
        </div>
      </div>
    );

    // Footer link helper
    const footerLink = (label, linkText, onClick) => (
      <div style={{ textAlign: 'center', marginTop: 8, fontSize: 13, color: 'var(--text-muted)', fontWeight: 500 }}>
        {label}{' '}
        <button type="button" onClick={onClick} style={{ background: 'none', border: 'none', color: '#0A3D2E', fontWeight: 900, cursor: 'pointer', padding: 0, fontSize: 13, fontFamily: 'inherit' }}>
          {linkText}
        </button>
      </div>
    );

    // ── WELCOME SCREEN ──
    if (authScreen === 'welcome') {
      return (
        <div className="auth-welcome">
          {authToast}
          <div className="auth-welcome-lang">{langSwitcher}</div>
          <img src="/faida-logo.png" alt="Biashara Track" className="auth-welcome-logo" />
          <h1 className="auth-welcome-title">BIASHARA TRACK</h1>
          <p className="auth-welcome-sub">{t.professionalBusiness || 'Professional Business'}</p>
          <div className="auth-welcome-actions">
            <button type="button" onClick={() => { setAuthScreen('login'); setAuthError(''); }} className="auth-cta">{t.login}</button>
            <button type="button" onClick={() => { setAuthScreen('signup'); setAuthError(''); }} className="auth-cta-outline">{t.signup}</button>
          </div>
        </div>
      );
    }

    // ── LOGIN SCREEN ──
    if (authScreen === 'login') {
      return (
        <div className="auth-shell">
          {authToast}
          {renderHero({ title: t.welcomeHeroLine || 'Welcome', accent: (t.welcomeHeroAccent || 'back.'), sub: tagline, backTo: null })}
          <div className="auth-form-wrap">
            <div className="auth-form-inner">
              <div>
                <span className="auth-eyebrow">{t.signInEyebrow || 'Sign in'}</span>
                <h2 className="auth-h1">{t.hiThere || 'Hi there,'}</h2>
                <p className="auth-sub">{t.loginSub || 'Enter your details to continue.'}</p>
              </div>

              <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <label className="auth-label" htmlFor="login-identifier">{t.emailOrPhone}</label>
                  <div className="auth-field-row">
                    <input id="login-identifier" type="text" value={loginForm.identifier} onChange={(e) => setLoginForm({ ...loginForm, identifier: e.target.value })} placeholder={t.emailPh || 'you@example.com'} required className="auth-input" autoComplete="username" />
                  </div>
                </div>

                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <label className="auth-label" htmlFor="login-password" style={{ marginBottom: 0 }}>{t.password}</label>
                    <button type="button" onClick={() => { setAuthScreen('forgot'); setAuthError(''); setShowPassword(false); }} className="auth-link">{t.forgotPassword}</button>
                  </div>
                  <div className="auth-field-row">
                    <input id="login-password" type={showPassword ? 'text' : 'password'} value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} placeholder="••••••••" required className="auth-input" style={{ paddingRight: 44 }} autoComplete="current-password" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="auth-eye" aria-label="Toggle password visibility">{showPassword ? (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-10-7-10-7a17.83 17.83 0 0 1 4-5"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 10 7 10 7a17.78 17.78 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>) : (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>)}</button>
                  </div>
                </div>

                {authError && <div className="auth-error" role="alert">{authError}</div>}

                <button type="submit" className="auth-cta" style={{ marginTop: 8 }}>{t.login}</button>

                {footerLink(t.dontHaveAccount, t.signup, () => { setAuthScreen('signup'); setAuthError(''); setShowPassword(false); })}
              </form>
            </div>
          </div>
        </div>
      );
    }

    // ── SIGNUP SCREEN ──
    if (authScreen === 'signup') {
      return (
        <div className="auth-shell">
          {authToast}
          {renderHero({ title: t.signupHeroLine || 'Create your', accent: (t.signupHeroAccent || 'account.'), sub: tagline, backTo: 'login' })}
          <div className="auth-form-wrap">
            <div className="auth-form-inner">
              <div>
                <span className="auth-eyebrow">{t.signupEyebrow || 'Sign up'}</span>
                <h2 className="auth-h1">{t.signupTitle || "Let's get you set up"}</h2>
                <p className="auth-sub">{t.signupSub || 'Just a few quick details to create your account.'}</p>
              </div>

              <form onSubmit={handleSignup} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div>
                  <label className="auth-label" htmlFor="signup-name">{t.fullName}</label>
                  <div className="auth-field-row">
                    <input id="signup-name" type="text" value={signupForm.fullName} onChange={(e) => setSignupForm({ ...signupForm, fullName: e.target.value })} placeholder={t.fullNamePh || 'Your full name'} required className="auth-input" autoComplete="name" />
                  </div>
                </div>
                <div>
                  <label className="auth-label" htmlFor="signup-email">{t.email}</label>
                  <div className="auth-field-row">
                    <input id="signup-email" type="email" value={signupForm.email} onChange={(e) => setSignupForm({ ...signupForm, email: e.target.value })} placeholder={t.emailPh || 'you@example.com'} required className="auth-input" autoComplete="email" />
                  </div>
                </div>
                <div>
                  <label className="auth-label" htmlFor="signup-password">{t.password}</label>
                  <div className="auth-field-row">
                    <input id="signup-password" type={showPassword ? 'text' : 'password'} value={signupForm.password} onChange={(e) => setSignupForm({ ...signupForm, password: e.target.value })} placeholder="••••••••" required className="auth-input" style={{ paddingRight: 44 }} autoComplete="new-password" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="auth-eye" aria-label="Toggle password visibility">{showPassword ? (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-10-7-10-7a17.83 17.83 0 0 1 4-5"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 10 7 10 7a17.78 17.78 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>) : (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>)}</button>
                  </div>
                </div>
                <div>
                  <label className="auth-label" htmlFor="signup-confirm-password">{t.confirmPassword}</label>
                  <div className="auth-field-row">
                    <input id="signup-confirm-password" type={showConfirmPassword ? 'text' : 'password'} value={signupForm.confirmPassword} onChange={(e) => setSignupForm({ ...signupForm, confirmPassword: e.target.value })} placeholder="••••••••" required className="auth-input" style={{ paddingRight: 44 }} autoComplete="new-password" />
                    <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="auth-eye" aria-label="Toggle password visibility">{showConfirmPassword ? (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-10-7-10-7a17.83 17.83 0 0 1 4-5"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 10 7 10 7a17.78 17.78 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>) : (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>)}</button>
                  </div>
                </div>

                <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer', marginTop: 4 }}>
                  <div onClick={() => setSignupForm({ ...signupForm, agree: !signupForm.agree })} style={{ flexShrink: 0, marginTop: 2, width: 18, height: 18, borderRadius: 4, border: '1.5px solid #D1D5DB', display: 'flex', alignItems: 'center', justifyContent: 'center', background: signupForm.agree ? '#0A3D2E' : 'transparent', borderColor: signupForm.agree ? '#0A3D2E' : '#D1D5DB', cursor: 'pointer' }}>
                    {signupForm.agree && <span style={{ fontSize: 11, color: '#FDE047', fontWeight: 900 }}>Yes</span>}
                  </div>
                  <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 500, lineHeight: 1.5 }}>
                    {t.iAgreeToThe || 'I agree to the'}{' '}
                    <button type="button" onClick={(e) => { e.stopPropagation(); setShowTermsModal(true); }} style={{ background: 'none', border: 'none', color: '#1D9E75', fontWeight: 800, cursor: 'pointer', padding: 0, fontSize: 12, textDecoration: 'underline', fontFamily: 'inherit' }}>
                      {t.termsAndPrivacy || 'Terms & Privacy Policy'}
                    </button>
                  </span>
                </label>

                {authError && <div className="auth-error" role="alert">{authError}</div>}

                <button type="submit" className="auth-cta" style={{ marginTop: 8 }}>{t.signup}</button>

                {footerLink(t.alreadyHaveAccount, t.login, () => { setAuthScreen('login'); setAuthError(''); setShowPassword(false); setShowConfirmPassword(false); })}
              </form>
            </div>
          </div>
        </div>
      );
    }

    // ── FORGOT PASSWORD SCREEN ──
    if (authScreen === 'forgot') {
      return (
        <div className="auth-shell">
          {authToast}
          {renderHero({ title: t.forgotHeroLine || 'Forgot', accent: (t.forgotHeroAccent || 'password?'), sub: t.forgotHeroSub || "Enter the email you signed up with and we'll send you a secure link to reset it.", backTo: 'login' })}
          <div className="auth-form-wrap">
            <div className="auth-form-inner">
              <div>
                <span className="auth-eyebrow">{t.resetEyebrow || 'Reset'}</span>
                <h2 className="auth-h1">{t.forgotTitle || 'Check your inbox'}</h2>
                <p className="auth-sub">{t.forgotSub || "We'll email you a link to set a new password."}</p>
              </div>

              <form onSubmit={handleForgotPassword} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <label className="auth-label" htmlFor="forgot-email">{t.email}</label>
                  <div className="auth-field-row">
                    <input id="forgot-email" type="email" value={forgotForm.email} onChange={(e) => setForgotForm({ email: e.target.value })} placeholder={t.emailPh || 'you@example.com'} required className="auth-input" autoComplete="email" />
                  </div>
                </div>

                {authError && <div className="auth-error" role="alert">{authError}</div>}

                <button type="submit" className="auth-cta" style={{ marginTop: 8 }}>{t.send || 'SEND'}</button>

                {footerLink(t.rememberYourPassword || 'Remember your password?', t.login, () => { setAuthScreen('login'); setAuthError(''); })}
              </form>
            </div>
          </div>
        </div>
      );
    }

    // ── RESET NEW PASSWORD SCREEN (after email link) ──
    if (authScreen === 'resetNewPassword') {
      return (
        <div className="auth-shell">
          {authToast}
          {renderHero({ title: t.setNewHeroLine || 'Set new', accent: (t.setNewHeroAccent || 'password.'), sub: t.setNewHeroSub || 'Choose a strong password to secure your account.', backTo: null })}
          <div className="auth-form-wrap">
            <div className="auth-form-inner">
              <div>
                <span className="auth-eyebrow">{t.resetEyebrow || 'Reset'}</span>
                <h2 className="auth-h1">{t.almostDone || 'Almost done'}</h2>
                <p className="auth-sub">{t.resetEmailSentDesc || 'Enter your new password below to reset your account.'}</p>
              </div>

              <form onSubmit={handleResetNewPassword} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <label className="auth-label">{t.newPassword || 'New password'}</label>
                  <div className="auth-field-row">
                    <input type={showPassword ? 'text' : 'password'} value={resetForm.password} onChange={(e) => setResetForm({ ...resetForm, password: e.target.value })} placeholder="••••••••" required className="auth-input" style={{ paddingRight: 44 }} />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="auth-eye" aria-label="Toggle password visibility">{showPassword ? (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-10-7-10-7a17.83 17.83 0 0 1 4-5"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 10 7 10 7a17.78 17.78 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>) : (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>)}</button>
                  </div>
                </div>
                <div>
                  <label className="auth-label">{t.confirmNewPassword || 'Confirm new password'}</label>
                  <div className="auth-field-row">
                    <input type={showConfirmPassword ? 'text' : 'password'} value={resetForm.confirmPassword} onChange={(e) => setResetForm({ ...resetForm, confirmPassword: e.target.value })} placeholder="••••••••" required className="auth-input" style={{ paddingRight: 44 }} />
                    <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="auth-eye" aria-label="Toggle password visibility">{showConfirmPassword ? (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-10-7-10-7a17.83 17.83 0 0 1 4-5"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 10 7 10 7a17.78 17.78 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>) : (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>)}</button>
                  </div>
                </div>

                {authError && <div className="auth-error" role="alert">{authError}</div>}

                <button type="submit" className="auth-cta" style={{ marginTop: 8 }}>{t.resetPassword}</button>

                {footerLink(t.rememberYourPassword || 'Remember your password?', t.login, () => { setAuthScreen('login'); setAuthError(''); setResetForm({ password: '', confirmPassword: '' }); setShowPassword(false); setShowConfirmPassword(false); })}
              </form>
            </div>
          </div>
        </div>
      );
    }

    if (authScreen === 'subscription') {
      return (
        <div className="App" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', padding: 20 }}>
          <div className="auth-card" style={{ width: '100%', maxWidth: 480, background: 'white', borderRadius: 16, padding: 36, boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }}>
            <div style={{ textAlign: 'center', marginBottom: 28 }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 60, height: 60, borderRadius: 16, background: '#111827', marginBottom: 16 }}>
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none"><path d="M12 2L2 7l10 5 10-5-10-5z" fill="#A3E635"/><path d="M2 17l10 5 10-5" stroke="#A3E635" strokeWidth="2" strokeLinecap="round"/><path d="M2 12l10 5 10-5" stroke="#A3E635" strokeWidth="2" strokeLinecap="round"/></svg>
              </div>
              <h1 style={{ fontSize: 24, fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 6px' }}>{t.choosePlan}</h1>
              <p style={{ color: 'var(--text-muted)', fontSize: 14, margin: 0 }}>{t.subscriptionMessage}</p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 24 }}>
              {/* Monthly Plan */}
              <div
                onClick={() => setSelectedPlan('monthly')}
                style={{
                  padding: 20, borderRadius: 14,
                  border: `2px solid ${selectedPlan === 'monthly' ? '#111827' : 'var(--border-default)'}`,
                  background: selectedPlan === 'monthly' ? '#F9FAFB' : 'white',
                  cursor: 'pointer', transition: 'all 0.15s'
                }}
              >
                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>{t.monthly}</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 2 }}>{t.monthlyPrice}</div>
                <div style={{ fontSize: 11, color: '#10B981', fontWeight: 600, marginBottom: 12 }}>{t.freeTrial}</div>
                <ul style={{ margin: 0, padding: '0 0 0 14px', fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.8 }}>
                  <li>{t.allFeatures}</li>
                  <li>{t.unlimitedData}</li>
                  <li>{t.cancelAnytime}</li>
                </ul>
              </div>

              {/* Yearly Plan */}
              <div
                onClick={() => setSelectedPlan('yearly')}
                style={{
                  padding: 20, borderRadius: 14, position: 'relative',
                  border: `2px solid ${selectedPlan === 'yearly' ? '#111827' : 'var(--border-default)'}`,
                  background: selectedPlan === 'yearly' ? '#F9FAFB' : 'white',
                  cursor: 'pointer', transition: 'all 0.15s'
                }}
              >
                <div style={{ position: 'absolute', top: -10, right: 10, background: '#10B981', color: 'white', padding: '3px 10px', borderRadius: 16, fontSize: 11, fontWeight: 700 }}>
                  {t.yearlySavings}
                </div>
                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>{t.yearly}</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 2 }}>{t.yearlyPrice}</div>
                <div style={{ fontSize: 11, color: '#10B981', fontWeight: 600, marginBottom: 12 }}>{t.freeTrial}</div>
                <ul style={{ margin: 0, padding: '0 0 0 14px', fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.8 }}>
                  <li>{t.allFeatures}</li>
                  <li>{t.unlimitedData}</li>
                  <li>{t.prioritySupport}</li>
                </ul>
              </div>
            </div>

            <button
              onClick={() => {
                // Phase 3: record subscription and proceed to app
                setAuthScreen('login');
              }}
              style={{ width: '100%', padding: 15, borderRadius: 12, background: '#111827', color: '#A3E635', border: 'none', fontWeight: 700, fontSize: 15, cursor: 'pointer', marginBottom: 12 }}
            >
              {t.subscribe} {selectedPlan === 'monthly' ? t.monthlyPrice : t.yearlyPrice}
            </button>

            <div style={{ textAlign: 'center' }}>
              <p style={{ color: 'var(--text-muted)', fontSize: 12, margin: 0 }}>{t.cancelAnytime} · {t.freeTrial}</p>
            </div>
          </div>
        </div>
      );
    }
  }

  // ── PAYMENT SUCCESS OVERLAY (shown after ClickPesa redirect) ─────────────
  if (paymentSuccessOverlay) {
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16 }}>
        <div style={{ width: 80, height: 80, borderRadius: '50%', background: '#D1FAE5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="3" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
        </div>
        <div style={{ fontSize: 22, fontWeight: 900, color: '#fff' }}>Payment Successful!</div>
        <div style={{ fontSize: 15, color: 'rgba(255,255,255,0.7)' }}>Your subscription is now active.</div>
      </div>
    );
  }

  // ── SUBSCRIPTION EXPIRY GATE ──────────────────────────────────────────
  if (currentUser && subscriptionExpired) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg-tab)', padding: '0 20px', display: 'flex', flexDirection: 'column', maxWidth: 480, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ paddingTop: 48, textAlign: 'center', marginBottom: 32 }}>
          <div style={{ width: 52, height: 42, borderRadius: 14, background: '#111827', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <span style={{ fontSize: 24, fontWeight: 900, color: '#A3E635', letterSpacing: -1 }}>F</span>
          </div>
          <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-primary)', letterSpacing: -0.5 }}>Biashara Track</div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>{t.subscriptionRequired || 'Subscription Required'}</div>
        </div>

        {/* Expired notice */}
        <div style={{ background: '#FEF2F2', borderRadius: 12, padding: '10px 14px', marginBottom: 28, border: '1px solid #FECACA', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#DC2626', marginBottom: 2 }}>{t.subExpiredTitle || 'Your subscription has expired'}</div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5 }}>{t.subExpiredDesc || 'Choose a plan below to continue using Biashara Track.'}</div>
          </div>
        </div>

        {/* Plan list */}
        <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10, paddingBottom: 20 }}>
          {PLANS.map(plan => (
            <button key={plan.id} onClick={() => handleStartPayment(plan.id)}
              style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 18px', background: 'var(--bg-white)', borderRadius: 14, border: '1.5px solid var(--border-default)', cursor: 'pointer', width: '100%', textAlign: 'left', position: 'relative', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
              {plan.badge && <div style={{ position: 'absolute', top: -9, right: 14, background: '#111827', color: '#A3E635', fontSize: 10, fontWeight: 800, padding: '2px 10px', borderRadius: 16, letterSpacing: 0.3 }}>{plan.badge}</div>}
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: 15 }}>{plan.name}</div>
                <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 2 }}>{plan.duration}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontWeight: 800, color: 'var(--text-primary)', fontSize: 16 }}>{Number(plan.price).toLocaleString()}</div>
                <div style={{ color: 'var(--text-muted)', fontSize: 11 }}>{plan.currency}</div>
              </div>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>
            </button>
          ))}
        </div>

        <button onClick={handleLogout}
          style={{ padding: 14, borderRadius: 12, background: '#F3F4F6', color: 'var(--text-secondary)', border: 'none', fontWeight: 600, fontSize: 14, cursor: 'pointer', marginBottom: 30, width: '100%' }}>
          {t.logout}
        </button>

        {/* Terms & Privacy Modal */}
        {showTermsModal && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)' }} onClick={() => setShowTermsModal(false)} />
            <div style={{ position: 'relative', zIndex: 1, background: 'var(--bg-white)', borderRadius: 16, padding: 28, width: '100%', maxWidth: 480, maxHeight: '80vh', overflowY: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#0A3D2E' }}>{t.termsAndPrivacy || 'Terms & Privacy Policy'}</h2>
                <button onClick={() => setShowTermsModal(false)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: 'var(--text-muted)', lineHeight: 1 }}>×</button>
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.7 }}>
                <p style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 14, marginTop: 0 }}>Last updated: May 2025 · Hesabu Solutions</p>

                {/* ── TERMS OF SERVICE ── */}
                <p style={{ fontWeight: 800, fontSize: 14, color: '#0A3D2E', margin: '0 0 10px', paddingBottom: 4, borderBottom: '2px solid #0A3D2E' }}>TERMS OF SERVICE</p>

                <h3 style={{ fontSize: 13, fontWeight: 700, color: '#0A3D2E', margin: '12px 0 4px' }}>1. Acceptance of Terms</h3>
                <p style={{ margin: '0 0 10px' }}>By creating an account on Biashara Track, you agree to these Terms of Service and our Privacy Policy. If you do not agree, please do not use the app.</p>

                <h3 style={{ fontSize: 13, fontWeight: 700, color: '#0A3D2E', margin: '12px 0 4px' }}>2. Use of the Service</h3>
                <p style={{ margin: '0 0 10px' }}>Biashara Track is a business management tool for tracking sales, purchases, expenses, and inventory. You are responsible for the accuracy of data you enter and for keeping your login credentials secure. You must not use Biashara Track for unlawful purposes or to store fraudulent data.</p>

                <h3 style={{ fontSize: 13, fontWeight: 700, color: '#0A3D2E', margin: '12px 0 4px' }}>3. Cloud Data Storage</h3>
                <p style={{ margin: '0 0 10px' }}>Your business data is stored both on your device (for offline access) and on secure cloud servers provided by Supabase (supabase.com). By using Biashara Track, you consent to this cloud storage. Your data is protected by Row Level Security -only you can access your own data.</p>

                <h3 style={{ fontSize: 13, fontWeight: 700, color: '#0A3D2E', margin: '12px 0 4px' }}>4. Subscriptions & Payments</h3>
                <p style={{ margin: '0 0 10px' }}>Biashara Track offers subscription plans billed in advance. Payments are processed securely via ClickPesa. Subscriptions are non-refundable except where required by law. You may cancel at any time; access continues until the end of the billing period.</p>

                <h3 style={{ fontSize: 13, fontWeight: 700, color: '#0A3D2E', margin: '12px 0 4px' }}>5. Intellectual Property</h3>
                <p style={{ margin: '0 0 10px' }}>The Biashara Track app, its design, and all content (excluding your business data) are owned by Hesabu Solutions. You may not copy, modify, or distribute any part of the app without written permission.</p>

                <h3 style={{ fontSize: 13, fontWeight: 700, color: '#0A3D2E', margin: '12px 0 4px' }}>6. Limitation of Liability</h3>
                <p style={{ margin: '0 0 10px' }}>Biashara Track is provided "as is" without warranty. We are not liable for data loss, financial loss, or business decisions made using the app. Always verify records independently and keep backups.</p>

                <h3 style={{ fontSize: 13, fontWeight: 700, color: '#0A3D2E', margin: '12px 0 4px' }}>7. Account Termination</h3>
                <p style={{ margin: '0 0 10px' }}>You may delete your account at any time in Settings. Cloud data is permanently erased within 30 days. We may suspend accounts that violate these terms.</p>

                <h3 style={{ fontSize: 13, fontWeight: 700, color: '#0A3D2E', margin: '12px 0 4px' }}>8. Changes to Terms</h3>
                <p style={{ margin: '0 0 20px' }}>We may update these terms. Significant changes will be communicated via in-app notice. Continued use after changes constitutes acceptance.</p>

                {/* ── PRIVACY POLICY ── */}
                <p style={{ fontWeight: 800, fontSize: 14, color: '#0A3D2E', margin: '0 0 10px', paddingBottom: 4, borderBottom: '2px solid #0A3D2E' }}>PRIVACY POLICY</p>

                <h3 style={{ fontSize: 13, fontWeight: 700, color: '#0A3D2E', margin: '12px 0 4px' }}>1. Information We Collect</h3>
                <p style={{ margin: '0 0 10px' }}>We collect: your email and business name (at registration); business data you enter (sales, purchases, expenses, products, customers, suppliers); and basic usage data to improve app performance.</p>

                <h3 style={{ fontSize: 13, fontWeight: 700, color: '#0A3D2E', margin: '12px 0 4px' }}>2. How Your Data Is Stored</h3>
                <p style={{ margin: '0 0 10px' }}>Data is stored locally on your device AND on Supabase cloud servers (AES-256 encrypted). This enables offline access, automatic backup, and sync across devices.</p>

                <h3 style={{ fontSize: 13, fontWeight: 700, color: '#0A3D2E', margin: '12px 0 4px' }}>3. Third-Party Services</h3>
                <p style={{ margin: '0 0 10px' }}>• <strong>Supabase</strong> (supabase.com) -cloud database &amp; authentication{'\n'}• <strong>ClickPesa</strong> -payment processing{'\n'}Both providers are contractually required to protect your data.</p>

                <h3 style={{ fontSize: 13, fontWeight: 700, color: '#0A3D2E', margin: '12px 0 4px' }}>4. Your Rights</h3>
                <p style={{ margin: '0 0 10px' }}>You can access, export, correct, or delete your data at any time through Settings. Use "Delete Account" in Settings for full data removal.</p>

                <h3 style={{ fontSize: 13, fontWeight: 700, color: '#0A3D2E', margin: '12px 0 4px' }}>5. Data Sharing</h3>
                <p style={{ margin: '0 0 10px' }}>We do not sell or share your data with third parties for commercial purposes. Data may be disclosed only if required by law.</p>

                <h3 style={{ fontSize: 13, fontWeight: 700, color: '#0A3D2E', margin: '12px 0 4px' }}>6. Contact</h3>
                <p style={{ margin: 0 }}>hesabusolutionstz@gmail.com · +255 680 072 640</p>
              </div>
              <button onClick={() => { setShowTermsModal(false); setSignupForm(f => ({ ...f, agree: true })); }}
                style={{ marginTop: 20, width: '100%', padding: '12px 0', borderRadius: 12, background: '#0A3D2E', color: '#FDE047', border: 'none', fontWeight: 800, fontSize: 14, cursor: 'pointer' }}>
                {t.agreeAndContinue || 'Agree & Continue'}
              </button>
            </div>
          </div>
        )}

        {/* Session Expired Modal */}
        {showSessionExpiredModal && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 2200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.7)' }} />
            <div style={{ position: 'relative', zIndex: 1, background: 'var(--bg-card)', borderRadius: 16, padding: 28, width: '100%', maxWidth: 360, textAlign: 'center' }}>
              <div style={{ width: 56, height: 46, borderRadius: '50%', background: '#FEF3C7', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              </div>
              <h2 style={{ margin: '0 0 8px', fontSize: 17, fontWeight: 800, color: 'var(--text-primary)' }}>{t.sessionExpiredTitle || 'Session Expired'}</h2>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, margin: '0 0 24px' }}>
                {t.sessionExpiredDesc || 'Your session has expired. Please log in again to continue syncing your data. Your local data is safe.'}
              </p>
              <button onClick={async () => { setShowSessionExpiredModal(false); await handleLogout(); }}
                style={{ width: '100%', padding: '11px 0', borderRadius: 12, background: '#0A3D2E', color: '#FDE047', border: 'none', fontWeight: 800, fontSize: 15, cursor: 'pointer' }}>
                {t.logInAgain || 'Log In Again'}
              </button>
            </div>
          </div>
        )}

        {/* Delete Account Modal */}
        {showDeleteAccountModal && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 2100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)' }} onClick={() => !deletingAccount && setShowDeleteAccountModal(false)} />
            <div style={{ position: 'relative', zIndex: 1, background: 'var(--bg-card)', borderRadius: 16, padding: 28, width: '100%', maxWidth: 400 }}>
              {/* Warning icon */}
              <div style={{ textAlign: 'center', marginBottom: 16 }}>
                <div style={{ width: 60, height: 60, borderRadius: '50%', background: '#FEE2E2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                </div>
                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#DC2626' }}>{t.deleteAccount || 'Delete Account'}</h2>
              </div>

              <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, margin: '0 0 8px', textAlign: 'center' }}>
                {t.deleteAccountWarning || 'This will permanently delete your account and all your business data from the cloud. This action cannot be undone.'}
              </p>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, margin: '0 0 20px', textAlign: 'center' }}>
                {t.deleteAccountNote || 'Your data will be erased within 30 days per our Privacy Policy.'}
              </p>

              {/* Type DELETE to confirm */}
              <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 8px' }}>
                {t.typeDeleteToConfirm || 'Type DELETE to confirm:'}
              </p>
              <input
                value={deleteConfirmText}
                onChange={e => setDeleteConfirmText(e.target.value)}
                placeholder="DELETE"
                disabled={deletingAccount}
                style={{ width: '100%', padding: '9px 12px', borderRadius: 10, border: '1.5px solid var(--border-color)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 14, fontWeight: 700, letterSpacing: 2, marginBottom: 16, boxSizing: 'border-box', outline: 'none' }}
              />

              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => setShowDeleteAccountModal(false)} disabled={deletingAccount}
                  style={{ flex: 1, padding: '13px 0', borderRadius: 12, background: 'var(--bg-tab)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
                  {t.cancel || 'Cancel'}
                </button>
                <button onClick={handleDeleteAccount}
                  disabled={deleteConfirmText !== 'DELETE' || deletingAccount}
                  style={{ flex: 1, padding: '13px 0', borderRadius: 12, background: deleteConfirmText === 'DELETE' ? '#DC2626' : '#F3F4F6', color: deleteConfirmText === 'DELETE' ? '#FFFFFF' : '#9CA3AF', border: 'none', fontWeight: 800, fontSize: 14, cursor: deleteConfirmText === 'DELETE' ? 'pointer' : 'not-allowed', transition: 'all 0.2s' }}>
                  {deletingAccount ? (t.deleting || 'Deleting…') : (t.deleteAccount || 'Delete Account')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Payment Modal */}
        {paymentFlow && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 1400, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)' }} onClick={paymentFlow.step === 'phone' || paymentFlow.step === 'failed' ? handleCancelPayment : undefined} />
            <div style={{ position: 'relative', zIndex: 1, background: 'var(--bg-white)', borderTopLeftRadius: 24, borderTopRightRadius: 24, padding: '24px 20px 32px', width: '100%', maxWidth: 480 }}>
              <div style={{ width: 40, height: 4, borderRadius: 2, background: '#D1D5DB', margin: '0 auto 20px' }} />

              {/* Phone Input Step */}
              {paymentFlow.step === 'phone' && (() => {
                const plan = PLANS.find(p => p.id === paymentFlow.planId);
                return (
                  <div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 4 }}>{t.payForPlan || 'Pay for'} {plan?.name}</div>
                    <div style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 20 }}>{plan?.currency} {Number(plan?.price).toLocaleString()} · {plan?.duration}</div>
                    <label style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 6, display: 'block' }}>{t.mobileMoneyPhone || 'Mobile Money Number'}</label>
                    <input
                      type="tel"
                      inputMode="numeric"
                      value={paymentFlow.phone}
                      onChange={(e) => setPaymentFlow(prev => ({ ...prev, phone: e.target.value, error: '' }))}
                      placeholder="07XXXXXXXX"
                      style={{ width: '100%', padding: '10px 14px', borderRadius: 12, border: '2px solid var(--border-default)', fontSize: 16, boxSizing: 'border-box', outline: 'none', marginBottom: 8 }}
                    />
                    {paymentFlow.error && <div style={{ color: '#EF4444', fontSize: 13, fontWeight: 600, marginBottom: 8 }}>{paymentFlow.error}</div>}
                    <button onClick={handleSubmitPayment}
                      style={{ width: '100%', padding: 15, borderRadius: 14, background: '#111827', color: '#A3E635', border: 'none', fontWeight: 800, fontSize: 15, cursor: 'pointer', marginTop: 10 }}>
                      {t.payNow || 'Pay Now'}
                    </button>
                    <button onClick={handleCancelPayment}
                      style={{ width: '100%', padding: 12, borderRadius: 14, background: 'transparent', color: 'var(--text-muted)', border: 'none', fontWeight: 700, fontSize: 14, cursor: 'pointer', marginTop: 4 }}>
                      {t.cancel}
                    </button>
                  </div>
                );
              })()}

              {/* Processing / Waiting / Verifying */}
              {['processing', 'waiting', 'verifying'].includes(paymentFlow.step) && (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                  <div style={{ width: 56, height: 46, borderRadius: '50%', border: '4px solid var(--border-default)', borderTopColor: '#111827', margin: '0 auto 20px', animation: 'spin 1s linear infinite' }} />
                  <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                  <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 6 }}>
                    {paymentFlow.step === 'processing' ? (t.creatingPayment || 'Creating payment...') :
                     paymentFlow.step === 'waiting' ? (t.waitingForPayment || 'Waiting for payment...') :
                     (t.verifyingPayment || 'Verifying payment...')}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                    {paymentFlow.step === 'waiting' ? (t.checkYourPhone || 'Check your phone for the payment prompt') : (t.pleaseWait || 'Please wait')}
                  </div>
                </div>
              )}

              {/* Success */}
              {paymentFlow.step === 'success' && (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                  <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#D1FAE5', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="3" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: '#059669', marginBottom: 6 }}>{t.paymentSuccess || 'Payment Successful!'}</div>
                  <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{t.subscriptionActivated || 'Your subscription has been activated.'}</div>
                </div>
              )}

              {/* Failed */}
              {paymentFlow.step === 'failed' && (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                  <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#FEE2E2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                    <span style={{ fontSize: 32 }}>×</span>
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: '#DC2626', marginBottom: 6 }}>{t.paymentFailed || 'Payment Failed'}</div>
                  <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>{paymentFlow.error}</div>
                  <button onClick={() => setPaymentFlow(prev => ({ ...prev, step: 'phone', error: '' }))}
                    style={{ width: '100%', padding: 14, borderRadius: 14, background: '#111827', color: '#A3E635', border: 'none', fontWeight: 800, fontSize: 15, cursor: 'pointer' }}>
                    {t.tryAgain || 'Try Again'}
                  </button>
                  <button onClick={handleCancelPayment}
                    style={{ width: '100%', padding: 12, borderRadius: 14, background: 'transparent', color: 'var(--text-muted)', border: 'none', fontWeight: 700, fontSize: 14, cursor: 'pointer', marginTop: 4 }}>
                    {t.cancel}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="App">
      {/* Accessibility: skip-to-content link for keyboard users */}
      <a href="#main-content" className="skip-to-content">{t.skipToContent || 'Skip to main content'}</a>
      {appAlert.open && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(10,15,25,0.55)', zIndex: 250000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: 'var(--bg-white)', borderRadius: 16, maxWidth: 420, width: '100%', boxShadow: '0 25px 60px rgba(0,0,0,0.25)', overflow: 'hidden', fontFamily: 'Nunito, sans-serif' }}>
            <div style={{ padding: '18px 20px 8px', display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{
                width: 34, height: 34, borderRadius: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0,
                background: appAlert.tone === 'error' ? '#FEE2E2' : appAlert.tone === 'warn' ? '#FEF3C7' : '#DCFCE7',
                color: appAlert.tone === 'error' ? '#DC2626' : appAlert.tone === 'warn' ? '#92400E' : '#166534',
              }}>{appAlert.tone === 'error' ? '!' : appAlert.tone === 'warn' ? '⚠' : 'ℹ'}</span>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#0A3D2E' }}>{appAlert.title}</h3>
            </div>
            <div style={{ padding: '4px 20px 18px', whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.5, color: 'var(--text-primary)' }}>{appAlert.message}</div>
            <div style={{ display: 'flex', gap: 8, padding: '12px 16px 16px', borderTop: '1px solid var(--border-default)', background: 'var(--bg-tab)' }}>
              {appAlert.mode === 'confirm' && (
                <button onClick={() => setAppAlert({ open: false, title: '', message: '', tone: 'info', mode: 'alert', onConfirm: null })}
                  style={{ flex: 1, padding: '10px 14px', borderRadius: 10, border: '1px solid #D1D5DB', background: 'var(--bg-white)', color: 'var(--text-secondary)', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                  {language === 'sw' ? 'Ghairi' : 'Cancel'}
                </button>
              )}
              <button onClick={() => {
                const cb = appAlert.onConfirm;
                setAppAlert({ open: false, title: '', message: '', tone: 'info', mode: 'alert', onConfirm: null });
                if (cb) cb();
              }} style={{ flex: 1, padding: '10px 14px', borderRadius: 10, border: 'none', background: appAlert.tone === 'error' ? '#DC2626' : '#0A3D2E', color: '#A3E635', fontWeight: 800, fontSize: 13, cursor: 'pointer' }} autoFocus>
                {appAlert.mode === 'confirm' ? (language === 'sw' ? 'Endelea' : 'Continue') : 'OK'}
              </button>
            </div>
          </div>
        </div>
      )}
      {successMessage && (
        <div style={{ position: 'fixed', top: 14, left: '50%', transform: 'translateX(-50%)', zIndex: 200000, background: '#111827', color: '#A3E635', padding: '10px 14px', borderRadius: 12, fontWeight: 700, fontSize: 13, boxShadow: '0 10px 25px rgba(0,0,0,0.18)', maxWidth: 'min(520px, calc(100vw - 24px))', textAlign: 'center' }}>
          {successMessage}
        </div>
      )}
      {/* ── Payment Ledger reconciliation check (dev-only, read-only) ──────
          Shows ONLY when localStorage 'faida-ledger-check' === '1'. Compares the
          new derived ledger against the current balances on real data. Nothing
          here changes any displayed balance. */}
      {(() => {
        if (process.env.NODE_ENV === 'production') return null;
        let on = false;
        try { on = localStorage.getItem('faida-ledger-check') === '1'; } catch (e) {}
        if (!on) return null;
        const cur = legacyBalances; // the retired engine, for comparison only
        const nw = cashLedgerData.agg;
        const rows = [
          ['Cash', cur.cash || 0, nw.cash || 0],
          ['Bank', cur.bank || 0, nw.bank || 0],
          ['Mobile', cur.mobileMoney || 0, nw.mobileMoney || 0],
        ];
        return (
          <div style={{ position: 'fixed', bottom: 12, right: 12, zIndex: 200001, background: '#0B1020', color: 'var(--border-default)', padding: '12px 14px', borderRadius: 12, fontSize: 12, boxShadow: '0 10px 30px rgba(0,0,0,0.35)', width: 320, fontFamily: 'ui-monospace, Menlo, monospace' }}>
            <div style={{ fontWeight: 800, marginBottom: 8, color: '#A3E635', letterSpacing: 0.4 }}>LEDGER RECONCILIATION</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 4, fontSize: 11 }}>
              <div style={{ opacity: 0.6 }}>Wallet</div>
              <div style={{ opacity: 0.6, textAlign: 'right' }}>Current</div>
              <div style={{ opacity: 0.6, textAlign: 'right' }}>Ledger</div>
              <div style={{ opacity: 0.6, textAlign: 'right' }}>Diff</div>
              {rows.map(([label, c, n]) => {
                const diff = Math.round((n - c) * 100) / 100;
                const ok = Math.abs(diff) < 1;
                return (
                  <React.Fragment key={label}>
                    <div>{label}</div>
                    <div style={{ textAlign: 'right' }}>{Math.round(c).toLocaleString()}</div>
                    <div style={{ textAlign: 'right' }}>{Math.round(n).toLocaleString()}</div>
                    <div style={{ textAlign: 'right', fontWeight: 800, color: ok ? '#34D399' : '#F87171' }}>{diff === 0 ? 'OK' : diff.toLocaleString()}</div>
                  </React.Fragment>
                );
              })}
            </div>
            {(() => {
              // Diagnostic: sum every ledger row by its wallet TYPE, so any
              // unexpected type (that would otherwise be lumped into cash) shows.
              const byType = {};
              (cashLedgerData.ledger || []).forEach(r => {
                const k = r.wallet || r.accountType || '(none)';
                byType[k] = (byType[k] || 0) + (r.direction === 'in' ? r.amount : -r.amount);
              });
              const known = ['cash', 'bank', 'mobilemoney'];
              return (
                <div style={{ marginTop: 8, borderTop: '1px solid rgba(255,255,255,0.15)', paddingTop: 6, fontSize: 10, opacity: 0.8 }}>
                  <div style={{ opacity: 0.6, marginBottom: 2 }}>ledger by wallet type:</div>
                  {Object.keys(byType).map(k => (
                    <div key={k} style={{ display: 'flex', justifyContent: 'space-between', color: known.includes(k) ? '#9CA3AF' : '#F87171' }}>
                      <span>{k}{known.includes(k) ? '' : '  ⚠ unexpected'}</span>
                      <span>{Math.round(byType[k]).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              );
            })()}
            <div style={{ marginTop: 8, fontSize: 10, opacity: 0.55 }}>{cashLedgerData.ledger.length} ledger rows · read-only</div>
          </div>
        );
      })()}

      {/* ── Stock reconciliation card (dev-only, read-only) ──────────────────
          Shows ONLY when localStorage 'faida-stock-check' === '1'. Compares each
          product's stored balance vs the transaction-history replay (matched by
          productId). A 'Rebuild' action corrects stored quantity ONLY, after a
          preview + confirm; unresolved/duplicate-name products are never auto-fixed. */}
      {(() => {
        if (process.env.NODE_ENV === 'production') return null;
        let on = false;
        try { on = localStorage.getItem('faida-stock-check') === '1'; } catch (e) {}
        if (!on) return null;
        const rows = stockReconciliation;
        const mismatches = rows.filter(r => r.status === 'mismatch');
        const unresolved = rows.filter(r => r.status === 'unresolved_legacy');
        return (
          <div style={{ position: 'fixed', bottom: 12, left: 12, zIndex: 200001, background: '#0B1020', color: 'var(--border-default)', padding: '12px 14px', borderRadius: 12, fontSize: 12, boxShadow: '0 10px 30px rgba(0,0,0,0.35)', width: 360, maxHeight: '70vh', overflowY: 'auto', fontFamily: 'ui-monospace, Menlo, monospace' }}>
            <div style={{ fontWeight: 800, marginBottom: 8, color: '#A3E635', letterSpacing: 0.4 }}>STOCK RECONCILIATION</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 0.8fr 0.8fr 0.7fr', gap: 4, fontSize: 10.5 }}>
              <div style={{ opacity: 0.6 }}>Product</div>
              <div style={{ opacity: 0.6, textAlign: 'right' }}>Stored</div>
              <div style={{ opacity: 0.6, textAlign: 'right' }}>Replay</div>
              <div style={{ opacity: 0.6, textAlign: 'right' }}>Diff</div>
              {rows.map((r) => {
                const color = r.status === 'ok' ? '#34D399' : r.status === 'mismatch' ? '#F87171' : '#FBBF24';
                return (
                  <React.Fragment key={r.productId || r.productName}>
                    <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.productName}>{r.productName}{r.status === 'unresolved_legacy' ? ' ⚠' : ''}</div>
                    <div style={{ textAlign: 'right' }}>{Math.round(r.storedQty * 1000) / 1000}</div>
                    <div style={{ textAlign: 'right' }}>{Math.round(r.replayQty * 1000) / 1000}</div>
                    <div style={{ textAlign: 'right', fontWeight: 800, color }}>{r.difference === 0 ? 'OK' : r.difference}</div>
                  </React.Fragment>
                );
              })}
            </div>
            {unresolved.length > 0 && (
              <div style={{ marginTop: 8, fontSize: 10, color: '#FBBF24' }}>⚠ {unresolved.length} unresolved (duplicate normalized name -resolve manually; never auto-rebuilt).</div>
            )}
            {(() => {
              const approx = returnsWithApproxCost(activeRecords(salesReturns));
              return approx.length > 0 ? (
                <div style={{ marginTop: 6, fontSize: 10, color: '#FBBF24' }}>⚠ {approx.length} customer return(s) restored at CURRENT WAC (no snapshot cost) -costing is approximate, not exact history.</div>
              ) : null;
            })()}
            {quarantinedReturnsList.length > 0 && (
              <div style={{ marginTop: 6, fontSize: 10, color: '#F87171' }}>⚠ {quarantinedReturnsList.length} return(s) QUARANTINED (parent deleted/missing) -excluded from all calculations, not discarded: {quarantinedReturnsList.slice(0, 4).map(x => x.status).join(', ')}</div>
            )}
            {(() => {
              // H4 impact preview (all-time): frozen-snapshot COGS vs chronological
              // replay COGS. Shown BEFORE any report is switched to the replay engine.
              const cmp = cogsSnapshotVsReplay({ products, purchases: acctPurchases, sales: acctSales, salesReturns: acctSalesReturns, purchaseReturns: acctPurchaseReturns });
              const off = Math.abs(cmp.difference) > 0.5;
              return (
                <div style={{ marginTop: 8, borderTop: '1px solid rgba(255,255,255,0.15)', paddingTop: 6, fontSize: 10, color: off ? '#FBBF24' : '#9CA3AF' }}>
                  COGS: snapshot {Math.round(cmp.snapshotCOGS).toLocaleString()} · replay {Math.round(cmp.replayCOGS).toLocaleString()} · diff <b style={{ color: off ? '#F87171' : '#34D399' }}>{cmp.difference === 0 ? 'OK' : Math.round(cmp.difference).toLocaleString()}</b>
                  {off ? ' P&L now uses REPLAY COGS; the gap above is the snapshot error it corrects (snapshots kept for audit).' : ' snapshots agree with replay.'}
                </div>
              );
            })()}
            {(() => {
              // Part C: per-product breakdown of the snapshot-vs-replay COGS gap, so
              // the operator can SEE which products drive it before switching reports.
              const rows = perProductCOGSBreakdown({ products, purchases: acctPurchases, sales: acctSales, salesReturns: acctSalesReturns, purchaseReturns: acctPurchaseReturns });
              const drivers = rows.filter((r) => Math.abs(r.difference) > 0.5);
              if (drivers.length === 0) return null;
              const fmtK = (v) => Math.round(v).toLocaleString();
              return (
                <div style={{ marginTop: 8, borderTop: '1px solid rgba(255,255,255,0.15)', paddingTop: 6 }}>
                  <div style={{ fontSize: 10, opacity: 0.7, marginBottom: 4 }}>COGS gap by product ({drivers.length} of {rows.length} drive it):</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 0.8fr 0.8fr 0.8fr', gap: 3, fontSize: 9.5 }}>
                    <div style={{ opacity: 0.55 }}>Product</div>
                    <div style={{ opacity: 0.55, textAlign: 'right' }}>Snap</div>
                    <div style={{ opacity: 0.55, textAlign: 'right' }}>Replay</div>
                    <div style={{ opacity: 0.55, textAlign: 'right' }}>Diff</div>
                    {drivers.slice(0, 12).map((r) => (
                      <React.Fragment key={r.productId || r.productName}>
                        <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.productName}>{r.productName}{r.ambiguous ? ' ⚠' : ''}</div>
                        <div style={{ textAlign: 'right' }}>{fmtK(r.snapshotCOGS)}</div>
                        <div style={{ textAlign: 'right' }}>{fmtK(r.replayCOGS)}</div>
                        <div style={{ textAlign: 'right', fontWeight: 800, color: '#F87171' }}>{fmtK(r.difference)}</div>
                      </React.Fragment>
                    ))}
                  </div>
                  {drivers.some((r) => r.ambiguous) && (
                    <div style={{ marginTop: 5, fontSize: 9.5, color: '#FBBF24' }}>⚠ = duplicate normalized name -replay may misattribute; resolve identity before trusting.</div>
                  )}
                </div>
              );
            })()}
            {(() => {
              // ── Historical Cost & Stock Exceptions (read-only reconciliation) ──
              // Recorded vs replay cost per sale line, return-integrity issues, and
              // stored-vs-replay stock -so the operator can resolve bad rows WITHOUT
              // silently rewriting history. Nothing here mutates records.
              const rep = historicalCostStockExceptions({ products, purchases: acctPurchases, sales: acctSales, salesReturns: acctSalesReturns, purchaseReturns: acctPurchaseReturns });
              const { summary } = rep;
              const anyEx = rep.rows.length > 0;
              if (!anyEx) return (
                <div style={{ marginTop: 8, borderTop: '1px solid rgba(255,255,255,0.15)', paddingTop: 6, fontSize: 10, color: '#34D399' }}>No cost / return / stock exceptions.</div>
              );
              const fmtK = (v) => (v == null ? '—' : Math.round(v).toLocaleString());
              const downloadCSV = () => {
                try {
                  const csv = exceptionsToCSV(rep);
                  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url; a.download = `cost-stock-exceptions-${new Date().toISOString().slice(0,10)}.csv`;
                  document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
                } catch (e) { /* no-op */ }
              };
              return (
                <div style={{ marginTop: 8, borderTop: '1px solid rgba(255,255,255,0.15)', paddingTop: 6 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#FBBF24', marginBottom: 4 }}>
                    COST &amp; STOCK EXCEPTIONS: {summary.zeroCostLines} zero-cost · {summary.wacVariationLines} WAC-drift · {summary.returnExceptions} return · {summary.stockExceptions} stock
                  </div>
                  {(() => {
                    // Reconcile row-level GROSS corrections to the period P&L gap:
                    // customer returns reverse COGS at the period level, so the
                    // line-sum is larger than the net P&L gap by exactly that
                    // reversal. Showing the identity answers "why don't the rows
                    // sum to the P&L total?".
                    const periodGap = -cogsSnapshotVsReplay({ products, purchases: acctPurchases, sales: acctSales, salesReturns: acctSalesReturns, purchaseReturns: acctPurchaseReturns }).difference;
                    const returnReversal = summary.totalCostUnderstatement - periodGap;
                    return (
                      <div style={{ fontSize: 9.5, opacity: 0.8, marginBottom: 5 }}>
                        Row corrections <b style={{ color: '#F87171' }}>{fmtK(summary.totalCostUnderstatement)}</b> − return reversals {fmtK(returnReversal)} = P&amp;L gap <b style={{ color: '#FBBF24' }}>{fmtK(periodGap)}</b>
                      </div>
                    );
                  })()}
                  <div style={{ maxHeight: 150, overflowY: 'auto', fontSize: 9 }}>
                    {rep.rows.slice(0, 40).map((row, i) => (
                      <div key={i} onClick={() => row.product && setTimelineProduct(row.product)} title="Open transaction timeline"
                        style={{ borderTop: i ? '1px solid rgba(255,255,255,0.08)' : 'none', padding: '3px 0', cursor: row.product ? 'pointer' : 'default' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6 }}>
                          <span style={{ color: row.type === 'cost' ? '#F87171' : row.type === 'return' ? '#FBBF24' : '#93C5FD', fontWeight: 700 }}>{row.reason}</span>
                          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.product}{row.invoice ? ` · ${row.invoice}` : ''} ↗</span>
                        </div>
                        <div style={{ opacity: 0.75 }}>
                          {row.type === 'cost' && `qty ${row.quantity} · rec ${fmtK(row.recordedUnitCost)}/u → replay ${fmtK(row.replayUnitCost)}/u · Δ ${fmtK(row.difference)}`}
                          {row.type === 'return' && `ret ${row.returnQuantity} · sold ${row.parentSoldQty ?? '—'} · maxRet ${row.maxReturnable ?? '—'} · ret ${row.date} vs sale ${row.parentSaleDate ?? '—'}`}
                          {row.type === 'stock' && `stored ${row.storedStock} · replay ${row.replayStock} · Δ ${row.stockDifference}`}
                        </div>
                        {row.type === 'return' && row.reason === 'return_before_sale' && (
                          <button onClick={(e) => { e.stopPropagation(); handleFixReturnDate(row); }}
                            style={{ marginTop: 3, padding: '3px 8px', borderRadius: 6, border: '1px solid #FBBF24', cursor: 'pointer', fontWeight: 700, fontSize: 9, background: 'transparent', color: '#FBBF24' }}>
                            Fix date → {row.parentSaleDate}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  {summary.zeroCostLines > 0 && (
                    <>
                      <div style={{ marginTop: 8, fontSize: 9, opacity: 0.6 }}>Order: fix any impossible-date return first, then backfill.</div>
                      <button onClick={() => handleBackfillZeroCost(rep)}
                        style={{ marginTop: 4, width: '100%', padding: '7px 0', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 10.5, background: '#F59E0B', color: 'var(--text-primary)' }}>
                        Preview &amp; fix zero-cost lines ({summary.zeroCostLines})
                      </button>
                    </>
                  )}
                  <button onClick={downloadCSV}
                    style={{ marginTop: 6, width: '100%', padding: '7px 0', borderRadius: 8, border: '1px solid #FBBF24', cursor: 'pointer', fontWeight: 700, fontSize: 10.5, background: 'transparent', color: '#FBBF24' }}>
                    Download exceptions CSV ({rep.rows.length})
                  </button>
                </div>
              );
            })()}
            <button onClick={handleRebuildStock} disabled={mismatches.length === 0}
              style={{ marginTop: 10, width: '100%', padding: '8px 0', borderRadius: 8, border: 'none', cursor: mismatches.length ? 'pointer' : 'not-allowed', fontWeight: 700, fontSize: 11, background: mismatches.length ? '#A3E635' : '#374151', color: mismatches.length ? '#0F172A' : '#9CA3AF' }}>
              Rebuild stored stock from history ({mismatches.length})
            </button>
            <div style={{ marginTop: 8, fontSize: 10, opacity: 0.55 }}>{rows.length} products · read-only until you confirm</div>
          </div>
        );
      })()}

      {/* ── Per-product transaction timeline (opened from an exceptions row) ── */}
      {timelineProduct && (() => {
        const tl = productTimeline(timelineProduct, {
          products, purchases, sales,
          salesReturns: validSalesReturns, purchaseReturns: validPurchaseReturns,
          stockAdjustments: activeRecords(stockAdjHistory),
        });
        const fmtN = (v) => (v == null ? '—' : Math.round(v).toLocaleString());
        const kindLabel = { opening: 'Opening', purchase: 'Purchase', sale: 'Sale', customer_return: 'Cust. return', supplier_return: 'Suppl. return', adjustment: 'Adjustment' };
        const kindColor = { opening: '#9CA3AF', purchase: '#34D399', sale: '#F87171', customer_return: '#FBBF24', supplier_return: '#93C5FD', adjustment: '#C4B5FD' };
        return (
          <div onClick={() => setTimelineProduct(null)}
            style={{ position: 'fixed', inset: 0, zIndex: 200002, background: 'rgba(3,7,18,0.72)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div onClick={(e) => e.stopPropagation()}
              style={{ background: '#0B1020', color: 'var(--border-default)', borderRadius: 14, padding: 16, width: 'min(760px, 96vw)', maxHeight: '86vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.5)', fontFamily: 'ui-monospace, Menlo, monospace' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <div>
                  <div style={{ fontWeight: 800, color: '#A3E635', fontSize: 13 }}>TRANSACTION TIMELINE</div>
                  <div style={{ fontSize: 12, marginTop: 2 }}>{tl.productName}</div>
                </div>
                <button onClick={() => setTimelineProduct(null)} style={{ background: 'transparent', border: '1px solid #4B5563', color: 'var(--border-default)', borderRadius: 8, padding: '4px 10px', cursor: 'pointer', fontWeight: 700 }}>Close ✕</button>
              </div>
              <div style={{ fontSize: 11, marginBottom: 8, color: Math.abs(tl.difference) > 0.001 ? '#FBBF24' : '#34D399' }}>
                Replay closing qty <b>{fmtN(tl.replayQty)}</b> · stored balance <b>{fmtN(tl.storedQty)}</b> · difference <b style={{ color: Math.abs(tl.difference) > 0.001 ? '#F87171' : '#34D399' }}>{tl.difference === 0 ? 'Match' : fmtN(tl.difference)}</b>
                {Math.abs(tl.difference) > 0.001 ? ' stored ≠ history; inspect the movements below before rebuilding.' : ' stored matches history.'}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '86px 96px 1fr 60px 70px 88px 90px', gap: 4, fontSize: 10.5, alignItems: 'center' }}>
                {['Date', 'Type', 'Ref', 'Δ qty', 'Run qty', 'Unit cost', 'Run value'].map((h) => (
                  <div key={h} style={{ opacity: 0.55, fontWeight: 700, borderBottom: '1px solid rgba(255,255,255,0.15)', paddingBottom: 3 }}>{h}</div>
                ))}
                {tl.rows.map((r, i) => (
                  <React.Fragment key={i}>
                    <div>{r.date || '—'}</div>
                    <div style={{ color: kindColor[r.kind] || 'var(--border-default)', fontWeight: 700 }}>{kindLabel[r.kind] || r.kind}</div>
                    <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={String(r.ref ?? '')}>
                      {r.ref ?? '—'}
                      {r.kind === 'sale' && r.zeroCost && <span style={{ color: '#F87171' }}> · cost 0!</span>}
                      {r.kind === 'sale' && !r.zeroCost && r.snapshotUnitCost != null && <span style={{ opacity: 0.55 }}> · snap {fmtN(r.snapshotUnitCost)}</span>}
                      {r.kind === 'customer_return' && r.impossibleDate && <span style={{ color: '#F87171' }}> · before sale {r.parentSaleDate}!</span>}
                    </div>
                    <div style={{ textAlign: 'right', color: r.qtyChange < 0 ? '#F87171' : r.qtyChange > 0 ? '#34D399' : '#9CA3AF' }}>{r.qtyChange > 0 ? '+' : ''}{r.qtyChange}</div>
                    <div style={{ textAlign: 'right', fontWeight: 700 }}>{fmtN(r.runningQty)}</div>
                    <div style={{ textAlign: 'right', opacity: 0.8 }}>{fmtN(r.unitCost)}</div>
                    <div style={{ textAlign: 'right', opacity: 0.8 }}>{fmtN(r.runningValue)}</div>
                  </React.Fragment>
                ))}
              </div>
              <div style={{ marginTop: 10, fontSize: 9.5, opacity: 0.5 }}>Read-only. Running qty includes stock adjustments, so closing matches the reconciliation replay. Sales show the perpetual WAC used for COGS and, in grey, the frozen snapshot recorded on the sale.</div>
            </div>
          </div>
        );
      })()}

      {/* ── Zero-cost override reason picker (part of the sale-save guard) ──── */}
      {zeroCostPrompt && (() => {
        const swZ = language === 'sw';
        const options = [
          { key: 'free_sample', label: swZ ? 'Sampuli ya bure' : 'Free sample' },
          { key: 'promotional_giveaway', label: swZ ? 'Zawadi ya matangazo' : 'Promotional giveaway' },
          { key: 'donated_stock', label: swZ ? 'Hisa iliyotolewa' : 'Donated stock' },
          { key: 'acquired_zero_cost', label: swZ ? 'Ilipatikana bila gharama' : 'Acquired at zero cost' },
          { key: 'internal_use', label: swZ ? 'Matumizi ya ndani' : 'Internal use' },
          { key: 'other', label: swZ ? 'Nyingine' : 'Other' },
        ];
        const commit = (key, text) => {
          zeroCostOverrideRef.current = true;
          zeroCostReasonRef.current = key;
          zeroCostReasonTextRef.current = text || null;
          setZeroCostPrompt(null);
          handleSaveSale();
        };
        const choose = (key) => {
          // "Other" must not be a blank escape route -require a short explanation.
          if (key === 'other') { setZeroCostPrompt(p => ({ ...p, otherMode: true, otherText: p.otherText || '' })); return; }
          commit(key);
        };
        return (
          <div onClick={() => setZeroCostPrompt(null)}
            style={{ position: 'fixed', inset: 0, zIndex: 200003, background: 'rgba(3,7,18,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div onClick={(e) => e.stopPropagation()}
              style={{ background: 'var(--bg-white)', color: 'var(--text-primary)', borderRadius: 14, padding: 18, width: 'min(420px, 94vw)', boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }}>
              <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 6 }}>{swZ ? 'Gharama ni sifuri' : 'Zero-cost sale -why?'}</div>
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 14, lineHeight: 1.4 }}>
                {swZ
                  ? `"${zeroCostPrompt.name}" ina gharama inayojulikana (~${fmt(zeroCostPrompt.basis)}). Ukirekodi kwa sifuri, chagua sababu -ili itofautishwe na kosa la gharama iliyokosekana.`
                  : `"${zeroCostPrompt.name}" has a known unit cost (~${fmt(zeroCostPrompt.basis)}). To record it at zero, pick a reason -so an intentional zero-cost sale isn't confused with a missing-cost error.`}
              </div>
              {zeroCostPrompt.otherMode ? (
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>
                    {swZ ? 'Eleza kwa ufupi sababu:' : 'Briefly explain the reason:'}
                  </label>
                  <textarea autoFocus value={zeroCostPrompt.otherText || ''}
                    onChange={(e) => setZeroCostPrompt(p => ({ ...p, otherText: e.target.value }))}
                    rows={3} placeholder={swZ ? 'k.m. bidhaa ya majaribio kwa mteja...' : 'e.g. trial unit given to a key client...'}
                    style={{ width: '100%', boxSizing: 'border-box', padding: 10, borderRadius: 10, border: '1px solid var(--border-color)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 13, resize: 'vertical' }} />
                  <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                    <button onClick={() => setZeroCostPrompt(p => ({ ...p, otherMode: false }))}
                      style={{ flex: 1, padding: '9px 0', borderRadius: 10, border: '1px solid var(--border-color)', background: 'var(--bg-tab)', color: 'var(--text-primary)', cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>
                      {swZ ? 'Rudi' : 'Back'}
                    </button>
                    <button disabled={!((zeroCostPrompt.otherText || '').trim().length >= 3)}
                      onClick={() => commit('other', (zeroCostPrompt.otherText || '').trim())}
                      style={{ flex: 2, padding: '9px 0', borderRadius: 10, border: 'none', background: (zeroCostPrompt.otherText || '').trim().length >= 3 ? '#F59E0B' : '#9CA3AF', color: 'var(--text-primary)', cursor: (zeroCostPrompt.otherText || '').trim().length >= 3 ? 'pointer' : 'not-allowed', fontWeight: 700, fontSize: 13 }}>
                      {swZ ? 'Thibitisha' : 'Confirm reason'}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {options.map((o) => (
                      <button key={o.key} onClick={() => choose(o.key)}
                        style={{ textAlign: 'left', padding: '10px 12px', borderRadius: 10, border: '1px solid var(--border-color)', background: 'var(--bg-tab)', color: 'var(--text-primary)', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                        {o.label}
                      </button>
                    ))}
                  </div>
                  <button onClick={() => setZeroCostPrompt(null)}
                    style={{ marginTop: 14, width: '100%', padding: '9px 0', borderRadius: 10, border: 'none', background: 'var(--text-secondary)', color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>
                    {swZ ? 'Ghairi -nitaweka gharama' : 'Cancel -I\'ll set the cost'}
                  </button>
                </>
              )}
            </div>
          </div>
        );
      })()}

      {/* ── Conflict log (dev-only) -behind faida-conflict-check ──────────── */}
      {(() => {
        let on = false;
        try { on = localStorage.getItem('faida-conflict-check') === '1'; } catch (e) {}
        if (!on) return null;
        const log = getConflictLog().slice(-12).reverse();
        void conflictTick; // re-render trigger
        return (
          <div style={{ position: 'fixed', top: 12, right: 12, zIndex: 200001, background: '#1A0B12', color: '#FBE7EE', padding: '12px 14px', borderRadius: 12, fontSize: 11, boxShadow: '0 10px 30px rgba(0,0,0,0.4)', width: 360, maxHeight: '70vh', overflowY: 'auto', fontFamily: 'ui-monospace, Menlo, monospace' }}>
            <div style={{ fontWeight: 800, marginBottom: 8, color: '#FB7185', letterSpacing: 0.4 }}>SAME-RECORD CONFLICTS ({getConflictLog().length})</div>
            {log.length === 0 && <div style={{ opacity: 0.6 }}>None detected this session. Concurrent edits to the SAME record resolve by newest updatedAt (last-write-wins).</div>}
            {log.map((c, i) => (
              <div key={i} style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 6, marginTop: 6 }}>
                <div><b>{c.collection}</b> #{String(c.recordId)} → winner: <b style={{ color: c.winner === 'local' ? '#34D399' : '#FBBF24' }}>{c.winner}</b></div>
                <div style={{ opacity: 0.75 }}>local {c.localUpdatedAt || '—'} · cloud {c.cloudUpdatedAt || '—'}</div>
                <div style={{ opacity: 0.6 }}>losing snapshot retained · {c.detectedAt}</div>
              </div>
            ))}
          </div>
        );
      })()}

      {/* OFFLINE BANNER */}
      {!isOnline && (
        <div style={{ position: 'fixed', bottom: 80, left: '50%', transform: 'translateX(-50%)', zIndex: 1300, background: '#1F2937', color: '#F9FAFB', padding: '10px 18px', borderRadius: 16, fontWeight: 700, fontSize: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2.5" strokeLinecap="round"><line x1="1" y1="1" x2="23" y2="23"/><path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55"/><path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39"/><path d="M10.71 5.05A16 16 0 0 1 22.56 9"/><path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88"/><path d="M8.53 16.11a6 6 0 0 1 6.95 0"/><line x1="12" y1="20" x2="12.01" y2="20"/></svg>
          {t.offlineMode || 'Offline -data saved locally'}
        </div>
      )}

      {/* TOP RIBBON - Dashboard Only */}
      {activeTab === 'dashboard' && (
        <header className="rb-ribbon">
          <div className="rb-greeting">
            <span className="rb-welcome">{t.welcome}</span>
            <span className="rb-biz-name" onClick={!(businessName || currentBusiness?.name) ? () => setActiveTab('setup') : undefined} style={!(businessName || currentBusiness?.name) ? { cursor: 'pointer', opacity: 0.75, fontWeight: 600, fontStyle: 'italic' } : undefined}>
              {businessName || currentBusiness?.name || (t.setBusinessName || 'Set your business name') + ' →'}
            </span>
          </div>
          <div className="rb-actions">
            {perms.editBusinessDetails && <span className="rb-link" onClick={() => setActiveTab('setup')}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#0A3D2E" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
              </svg>
              {t.businessDetailsLabel || 'Business Details'}
            </span>}
            <span className="rb-link rb-link-icon" onClick={() => setActiveTab('settings')} title={t.settings}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0A3D2E" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
              </svg>
            </span>
          </div>
        </header>
      )}
      
      {/* ── DESKTOP SIDEBAR ── */}
      <aside className="desktop-sidebar">
        {/* Brand */}
        <div className="sidebar-brand">
          <div className="sidebar-brand-logo">
            {businessLogo
              ? <img src={businessLogo} alt={`${businessName || currentBusiness?.name || 'Business'} logo`} />
              : ((businessName || currentBusiness?.name || 'F').charAt(0).toUpperCase())}
          </div>
          <div>
            <div className="sidebar-brand-name">{businessName || currentBusiness?.name || 'My Business'}</div>
            <div className="sidebar-brand-sub">Biashara Track</div>
          </div>
        </div>

        {/* Main nav */}
        <nav className="sidebar-nav" aria-label={t.mainNavigation || 'Main navigation'}>
          {!isTabHidden('dashboard') && perms.viewDashboard !== false && (
          <button className={`sidebar-nav-item${activeTab === 'dashboard' ? ' active' : ''}`} onClick={() => handleTabPress('dashboard')}>
            <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
            {t.dashboard}
          </button>
          )}

          {businessType !== 'service_only' && !isTabHidden('sales') && perms.viewSales && (
            <button className={`sidebar-nav-item${activeTab === 'sales' ? ' active' : ''}`} onClick={() => handleTabPress('sales')}>
              <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>
              {t.sales}
            </button>
          )}

          {businessType !== 'service_only' && !isTabHidden('purchases') && perms.viewPurchases && (
            <button className={`sidebar-nav-item${activeTab === 'purchases' ? ' active' : ''}`} onClick={() => handleTabPress('purchases')}>
              <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>
              {t.purchases}
            </button>
          )}

          {!isTabHidden('expenses') && perms.viewExpenses && (
          <button className={`sidebar-nav-item${activeTab === 'expenses' ? ' active' : ''}`} onClick={() => handleTabPress('expenses')}>
            <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
            {t.expenses}
          </button>
          )}

          {businessType !== 'service_only' && !isTabHidden('inventory') && perms.viewInventory && (
            <button className={`sidebar-nav-item${activeTab === 'inventory' ? ' active' : ''}`} onClick={() => handleTabPress('inventory')}>
              <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>
              {t.inventory}
            </button>
          )}

          {businessType !== 'products_only' && !isTabHidden('services') && perms.viewServices && (
            <button className={`sidebar-nav-item${activeTab === 'services' ? ' active' : ''}`} onClick={() => handleTabPress('services')}>
              <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="6" r="2.6"/><path d="M8.5 12.2a3.5 3.5 0 0 1 7 0"/><path d="M2 20c2.5-3 6-3 8.5-1.2 0.9 0.6 2.1 0.6 3 0C16 17 19.5 17 22 20"/></svg>
              {t.services || 'Services'}
            </button>
          )}

          {!isTabHidden('reports') && perms.viewReports && (
          <button className={`sidebar-nav-item${activeTab === 'reports' ? ' active' : ''}`} onClick={() => handleTabPress('reports')}>
            <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
            {language === 'sw' ? 'Ripoti' : 'Reports'}
          </button>
          )}
        </nav>

        {/* Bottom actions */}
        <div className="sidebar-bottom">
          <button className={`sidebar-bottom-item${activeTab === 'setup' ? ' active' : ''}`} onClick={() => setActiveTab('setup')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 3H8L6 7h12z"/></svg>
            {t.startHere}
          </button>
          <button className={`sidebar-bottom-item${activeTab === 'settings' ? ' active' : ''}`} onClick={() => setActiveTab('settings')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
            {t.settings}
          </button>
          <div className="sidebar-user">
            <div className="sidebar-user-email">{currentUser?.email}</div>
            <button className="sidebar-logout" onClick={handleLogout}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{width:14,height:14}}><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
              {t.logout || 'Log Out'}
            </button>
          </div>
        </div>
      </aside>

      <nav className={`mobile-bottom-nav ${businessType === 'service_only' ? 'nav-3' : businessType === 'products_only' ? 'nav-5' : 'nav-6'}`}>
        {/* Dashboard -home */}
        {!isTabHidden('dashboard') && perms.viewDashboard !== false && (
        <button onClick={() => handleTabPress('dashboard')} className={activeTab === 'dashboard' ? 'active' : ''}>
          <div className="nav-pill">
            <div className="nav-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                <polyline points="9 22 9 12 15 12 15 22"/>
              </svg>
            </div>
            <div className="nav-text">{t.dashboard}</div>
          </div>
        </button>
        )}
        {/* Sales -hidden for service-only businesses */}
        {businessType !== 'service_only' && !isTabHidden('sales') && perms.viewSales && (
        <button onClick={() => handleTabPress('sales')} className={activeTab === 'sales' ? 'active' : ''}>
          <div className="nav-pill">
            <div className="nav-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
                <polyline points="17 6 23 6 23 12"/>
              </svg>
            </div>
            <div className="nav-text">{t.sales}</div>
          </div>
        </button>
        )}
        {/* Purchases -hidden for service-only businesses */}
        {businessType !== 'service_only' && !isTabHidden('purchases') && perms.viewPurchases && (
        <button onClick={() => handleTabPress('purchases')} className={activeTab === 'purchases' ? 'active' : ''}>
          <div className="nav-pill">
            <div className="nav-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
              </svg>
            </div>
            <div className="nav-text">{t.purchases}</div>
          </div>
        </button>
        )}
        {/* Expenses -file-text */}
        {!isTabHidden('expenses') && perms.viewExpenses && (
        <button onClick={() => handleTabPress('expenses')} className={activeTab === 'expenses' ? 'active' : ''}>
          <div className="nav-pill">
            <div className="nav-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
                <polyline points="10 9 9 9 8 9"/>
              </svg>
            </div>
            <div className="nav-text">{t.expenses}</div>
          </div>
        </button>
        )}
        {/* Stock -hidden for service-only businesses */}
        {businessType !== 'service_only' && !isTabHidden('inventory') && perms.viewInventory && (
        <button onClick={() => handleTabPress('inventory')} className={activeTab === 'inventory' ? 'active' : ''}>
          <div className="nav-pill">
            <div className="nav-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
                <line x1="12" y1="22.08" x2="12" y2="12"/>
              </svg>
            </div>
            <div className="nav-text">{t.inventory}</div>
          </div>
        </button>
        )}
        {/* Services -hidden for products-only businesses */}
        {businessType !== 'products_only' && !isTabHidden('services') && perms.viewServices && (
        <button onClick={() => handleTabPress('services')} className={activeTab === 'services' ? 'active' : ''}>
          <div className="nav-pill">
            <div className="nav-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="6" r="2.6"/><path d="M8.5 12.2a3.5 3.5 0 0 1 7 0"/><path d="M2 20c2.5-3 6-3 8.5-1.2 0.9 0.6 2.1 0.6 3 0C16 17 19.5 17 22 20"/>
              </svg>
            </div>
            <div className="nav-text">{t.services || 'Services'}</div>
          </div>
        </button>
        )}
      </nav>
      
      <main className="main-content" id="main-content" role="main"><div className="content-scaler">
        {activeTab === 'dashboard' && (
          <div className="dashboard db-screen">
            {/* ── DRILLDOWN OVERLAY ─────────────────────────────────── */}
            {activeDrilldown && (
              <div style={{ position: 'fixed', inset: 0, background: 'var(--bg-tab)', zIndex: 600, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
                {/* Header -compact arrow + title (matches Product History style) */}
                <div style={{ display: 'flex', alignItems: 'center', padding: '12px 14px 8px', background: 'var(--bg-white)', borderBottom: '1px solid var(--border-default)', position: 'sticky', top: 0, zIndex: 10, gap: 10 }}>
                  <button onClick={() => {
                    setActiveDrilldown(null);
                    setShowWithdrawalForm(false);
                    // If user came from Reports tab, return them there
                    if (drilldownFromTab) {
                      setActiveTab(drilldownFromTab);
                      setDrilldownFromTab(null);
                    }
                  }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px 4px 0', display: 'flex', alignItems: 'center' }} aria-label={t.back}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--text-primary)" strokeWidth="2.5" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
                  </button>
                  <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: 'var(--text-primary)' }}>{drilldownTitle}</h3>
                </div>

                {/* Period tabs */}
                {!drilldownData.noFilter && (
                  <div style={{ background: 'var(--bg-white)', borderBottom: '1px solid var(--border-default)' }}>
                    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', padding: '10px 16px 8px' }}>
                      {['today', 'week', 'month', 'year', 'custom'].map(p => (
                        <button key={p} onClick={() => setDrilldownPeriod(p)} style={{ flex: 1, padding: '7px 0', borderRadius: 8, border: 'none', background: drilldownPeriod === p ? '#A3E635' : 'var(--bg-input)', color: drilldownPeriod === p ? '#0F172A' : 'var(--text-primary)', fontWeight: drilldownPeriod === p ? 700 : 500, fontSize: 12, cursor: 'pointer' }}>
                          {p === 'custom' ? (t.custom || 'Custom') : t[p]}
                        </button>
                      ))}
                    </div>
                    {drilldownPeriod === 'custom' && (
                      <div style={{ display: 'flex', gap: 10, padding: '0 16px 10px' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 3 }}>{t.startDate}</div>
                          <input type="date" value={drilldownCustomFrom} onChange={e => setDrilldownCustomFrom(e.target.value)}
                            onFocus={e => { try { e.currentTarget.showPicker(); } catch(x){} }}
                            style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border-default)', fontSize: 13, boxSizing: 'border-box', background: 'var(--bg-input)', color: 'var(--text-primary)' }} />
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 3 }}>{t.endDate}</div>
                          <input type="date" value={drilldownCustomTo} onChange={e => setDrilldownCustomTo(e.target.value)}
                            onFocus={e => { try { e.currentTarget.showPicker(); } catch(x){} }}
                            style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border-default)', fontSize: 13, boxSizing: 'border-box', background: 'var(--bg-input)', color: 'var(--text-primary)' }} />
                        </div>
                      </div>
                    )}
                    {(() => {
                      const fmtD = (s) => { if (!s) return ''; const p = s.split('-'); return `${p[2]}/${p[1]}/${p[0]}`; };
                      const monthNames = t.monthNames || ['January','February','March','April','May','June','July','August','September','October','November','December'];
                      const nowD = new Date();
                      let from = '', to = '', prefix = '';
                      if (drilldownPeriod === 'custom') { from = drilldownCustomFrom; to = drilldownCustomTo; }
                      else { const r = getReportDates(drilldownPeriod); if (r) { from = r.from; to = r.to; } }
                      if (drilldownPeriod === 'today') prefix = (t.today || 'Today');
                      else if (drilldownPeriod === 'week') prefix = (t.thisWeek || 'This week');
                      else if (drilldownPeriod === 'month') prefix = `${monthNames[nowD.getMonth()]} ${nowD.getFullYear()}`;
                      else if (drilldownPeriod === 'year') prefix = `${nowD.getFullYear()}`;
                      const rangeTxt = drilldownPeriod === 'today' ? fmtD(from) : `${fmtD(from)} – ${fmtD(to)}`;
                      if (!from) return null;
                      return <div style={{ padding: '0 16px 10px', fontSize: 12, color: 'var(--text-secondary)', fontWeight: 600 }}>{prefix ? `${prefix} · ${rangeTxt}` : rangeTxt}</div>;
                    })()}
                  </div>
                )}

                {/* Export buttons -compact */}
                <div style={{ display: 'flex', gap: 6, padding: '8px 14px', background: 'var(--bg-white)', borderBottom: '1px solid var(--border-default)' }}>
                  <button onClick={() => downloadPDF(drilldownData.headers || [], drilldownData.rows || [], drilldownTitle, `${activeDrilldown}-report.pdf`)} style={{ flex: 1, padding: '7px 10px', borderRadius: 8, background: '#EF4444', color: '#fff', border: 'none', fontWeight: 700, fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap' }}>⬇ PDF</button>
                  <button onClick={() => downloadCSV([(drilldownData.headers || []), ...(drilldownData.rows || [])], `${activeDrilldown}-report.csv`)} style={{ flex: 1, padding: '7px 10px', borderRadius: 8, background: '#10B981', color: '#fff', border: 'none', fontWeight: 700, fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap' }}>⬇ CSV</button>
                </div>

                <div style={{ padding: 16, flex: 1 }}>
                  {/* Sub-account picker for bank / mobilemoney drilldowns
                      (lets the user pick CRDB / NMB / specific accounts instead
                      of always seeing the aggregate). */}
                  {drilldownData.isBalance && (drilldownData.acct === 'bank' || drilldownData.acct === 'mobilemoney') && (() => {
                    const isBank = drilldownData.acct === 'bank';
                    const subs = (paymentAccounts || []).filter(a => a.type === drilldownData.acct);
                    if (subs.length === 0) return null;
                    const selected = isBank ? selectedBankAccount : selectedMobileMoneyAccount;
                    const setSel = isBank ? setSelectedBankAccount : setSelectedMobileMoneyAccount;
                    return (
                      <div style={{ marginBottom: 10 }}>
                        <label style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, marginBottom: 4 }}>
                          {isBank ? (language === 'sw' ? 'Akaunti ya Benki' : 'Bank Account') : (language === 'sw' ? 'Akaunti ya Pesa ya Simu' : 'Mobile Money Account')}
                        </label>
                        <select value={selected} onChange={e => setSel(e.target.value)}
                          style={{ width: '100%', padding: '9px 12px', borderRadius: 10, border: '1px solid var(--border-color)', background: 'var(--bg-white)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}>
                          <option value="all">{isBank
                            ? (language === 'sw' ? 'Akaunti Zote za Benki' : 'All Bank Accounts')
                            : (language === 'sw' ? 'Akaunti Zote za Simu' : 'All Mobile Money Accounts')}</option>
                          {subs.map((a, i) => (
                            <option key={a.id || i} value={i}>{isBank ? a.bankName : a.networkName}{a.accountName ? ` · ${a.accountName}` : ''}</option>
                          ))}
                        </select>
                      </div>
                    );
                  })()}

                  {/* Opening / Closing for balance screens */}
                  {drilldownData.isBalance && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', background: 'var(--bg-white)', borderRadius: 12, padding: '8px 14px', marginBottom: 14, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
                      <div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>{t.openingBalance}</div>
                        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>{fmt(drilldownData.openingBalance || 0)}</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>{t.closingBalance}</div>
                        <div style={{ fontSize: 16, fontWeight: 700, color: (drilldownData.closingBalance || 0) >= 0 ? '#059669' : '#DC2626' }}>{fmt(drilldownData.closingBalance || 0)}</div>
                      </div>
                    </div>
                  )}

                  {/* Filters for cashflow drilldowns (Total view especially) ─
                      Lets user narrow by account type / transaction type / free-text search */}
                  {drilldownData.isBalance && (
                    <div style={{ background: 'var(--bg-white)', border: '1px solid var(--border-color)', borderRadius: 10, padding: 10, marginBottom: 12, display: 'grid', gridTemplateColumns: drilldownData.acct === 'total' ? 'repeat(3, 1fr)' : 'repeat(2, 1fr)', gap: 8 }}>
                      {drilldownData.acct === 'total' && (
                        <div>
                          <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>{language === 'sw' ? 'Aina ya Akaunti' : 'Account Type'}</label>
                          <select value={drilldownFilterAcctType} onChange={e => setDrilldownFilterAcctType(e.target.value)}
                            style={{ width: '100%', padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border-color)', background: 'var(--bg-tab)', fontSize: 12, outline: 'none', boxSizing: 'border-box' }}>
                            <option value="all">{language === 'sw' ? 'Zote' : 'All'}</option>
                            <option value="cash">{language === 'sw' ? 'Taslimu' : 'Cash'}</option>
                            <option value="bank">{language === 'sw' ? 'Benki' : 'Bank'}</option>
                            <option value="mobilemoney">{language === 'sw' ? 'Pesa ya Simu' : 'Mobile Money'}</option>
                          </select>
                        </div>
                      )}
                      <div>
                        <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>{language === 'sw' ? 'Aina ya Muamala' : 'Transaction Type'}</label>
                        <select value={drilldownFilterSource} onChange={e => setDrilldownFilterSource(e.target.value)}
                          style={{ width: '100%', padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border-color)', background: 'var(--bg-tab)', fontSize: 12, outline: 'none', boxSizing: 'border-box' }}>
                          <option value="all">{language === 'sw' ? 'Zote' : 'All'}</option>
                          <option value="sale">{language === 'sw' ? 'Mauzo' : 'Sale'}</option>
                          <option value="purchase">{language === 'sw' ? 'Manunuzi' : 'Purchase'}</option>
                          <option value="expense">{language === 'sw' ? 'Gharama' : 'Expense'}</option>
                          <option value="manual">{language === 'sw' ? 'Mwingine' : 'Manual / Top-up'}</option>
                          <option value="return">{language === 'sw' ? 'Urudishaji' : 'Return'}</option>
                        </select>
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>{language === 'sw' ? 'Tafuta' : 'Search'}</label>
                        <input type="text" value={drilldownFilterSearch}
                          onChange={e => setDrilldownFilterSearch(e.target.value)}
                          placeholder={language === 'sw' ? 'Bidhaa, maelezo…' : 'Product, description…'}
                          style={{ width: '100%', padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border-color)', background: 'var(--bg-tab)', fontSize: 12, outline: 'none', boxSizing: 'border-box' }} />
                      </div>
                      {(drilldownFilterAcctType !== 'all' || drilldownFilterSource !== 'all' || drilldownFilterSearch) && (
                        <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end' }}>
                          <button onClick={() => { setDrilldownFilterAcctType('all'); setDrilldownFilterSource('all'); setDrilldownFilterSearch(''); }}
                            style={{ fontSize: 11, fontWeight: 700, color: '#DC2626', background: 'transparent', border: 'none', cursor: 'pointer', padding: '2px 4px' }}>
                            ✕ {language === 'sw' ? 'Futa vichujio' : 'Clear filters'}
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Summary row for list screens */}
                  {!drilldownData.isBalance && activeDrilldown !== 'stock' && activeDrilldown !== 'lowstock' && (drilldownData.items || []).length > 0 && (
                    <div style={{ background: 'var(--bg-white)', borderRadius: 8, padding: '6px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, border: '1px solid var(--border-color)' }}>
                      <span style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: 12 }}>{(drilldownData.items || []).length} {t.recordsLabel}</span>
                      <span style={{ fontWeight: 800, color: '#059669', fontSize: 13 }}>{fmt(drilldownData.total || 0)}</span>
                    </div>
                  )}

                  {/* Empty state */}
                  {(drilldownData.items || []).length === 0 && activeDrilldown !== 'stock' && activeDrilldown !== 'lowstock' && activeDrilldown !== 'receivables' && activeDrilldown !== 'payables' && (
                    <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)', fontSize: 14 }}>{t.noRecordsFound}</div>
                  )}

                  {/* Transaction rows -Sales, Products Only, Services Only, Total Revenue */}
                  {(activeDrilldown === 'sales' || activeDrilldown === 'products_only' || activeDrilldown === 'services_only' || activeDrilldown === 'total_revenue') && (drilldownData.items || []).map((s, i) => {
                    const itemName = s.productName || s.serviceName || s.name || (t.sale || 'Sale');
                    const clientName = s.customerName || s.clientName || t.walkIn || 'Walk-in';
                    const method = s.paymentMethod || 'cash';
                    return (
                      <div key={s.id || i} style={{ background: 'var(--bg-white)', borderRadius: 12, padding: '8px 14px', marginBottom: 10, border: '1.5px solid var(--border-default)', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                          <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)', flex: 1, marginRight: 8 }}>{s._isReturn ? '↩ ' : ''}{itemName}</span>
                          <span style={{ fontWeight: 800, fontSize: 15, color: s._isReturn ? '#DC2626' : '#059669', whiteSpace: 'nowrap' }}>{fmt(parseFloat(s.totalRevenue || 0))}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{clientName} · {method}</span>
                          <span style={{ fontSize: 12, fontWeight: 600, color: s._isReturn ? '#DC2626' : (s.isPaid === false ? '#EF4444' : '#059669') }}>{s._isReturn ? (language === 'sw' ? 'Marejesho' : 'Return') : (s.isPaid === false ? (t.credit || 'Credit') : (t.paid || 'Paid'))} · {s.date}</span>
                        </div>
                      </div>
                    );
                  })}

                  {/* Transaction rows -Purchases */}
                  {activeDrilldown === 'purchases' && (drilldownData.items || []).map((p, i) => {
                    const itemName = p.productName || p.name || (t.purchase || 'Purchase');
                    const supplierName = p.supplier || p.supplierName || '—';
                    return (
                      <div key={p.id || i} style={{ background: 'var(--bg-white)', borderRadius: 12, padding: '8px 14px', marginBottom: 10, border: '1.5px solid var(--border-default)', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                          <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)', flex: 1, marginRight: 8 }}>{p._isReturn ? '↩ ' : ''}{itemName}</span>
                          <span style={{ fontWeight: 800, fontSize: 15, color: p._isReturn ? '#059669' : '#DC2626', whiteSpace: 'nowrap' }}>{fmt(parseFloat(p.totalCost || 0))}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{supplierName} · {p.paymentMethod || 'cash'}</span>
                          <span style={{ fontSize: 12, fontWeight: 600, color: p._isReturn ? '#059669' : (p.isPaid === false ? '#EF4444' : '#059669') }}>{p._isReturn ? (language === 'sw' ? 'Marejesho' : 'Return') : (p.isPaid === false ? (t.credit || 'Credit') : (t.paid || 'Paid'))} · {p.date}</span>
                        </div>
                      </div>
                    );
                  })}

                  {/* Transaction rows -Expenses */}
                  {activeDrilldown === 'expenses' && (drilldownData.items || []).map((e, i) => (
                    <div key={e.id || i} style={{ background: 'var(--bg-white)', borderRadius: 12, padding: '8px 14px', marginBottom: 10, border: '1.5px solid var(--border-default)', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                        <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)', flex: 1, marginRight: 8 }}>{e.category || (t.expense || 'Expense')}</span>
                        <span style={{ fontWeight: 800, fontSize: 15, color: '#DC2626', whiteSpace: 'nowrap' }}>{fmt(parseFloat(e.amount || 0))}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{e.description || '—'}</span>
                        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{e.date}</span>
                      </div>
                    </div>
                  ))}

                  {/* ── ACCOUNT LEDGER TABLE (Cash / Bank / Mobile Money) ── */}
                  {drilldownData.isBalance && (() => {
                    const thS = { padding: '9px 10px', textAlign: 'left', fontWeight: 700, color: 'var(--text-secondary)', fontSize: 11, borderBottom: '2px solid var(--border-default)', whiteSpace: 'nowrap', background: 'var(--bg-input)', position: 'sticky', top: 0, zIndex: 1 };
                    const tdS = { padding: '9px 10px', fontSize: 12, verticalAlign: 'middle', color: 'var(--text-primary)' };
                    // Cashflow report rows are read-only -edits must happen in the originating
                    // module (Sales / Purchases / Expenses) to keep audit history clean.
                    const srcLabel = src => {
                      if (src === 'sale') return language === 'sw' ? 'Mauzo' : 'Sale';
                      if (src === 'purchase') return language === 'sw' ? 'Manunuzi' : 'Purchase';
                      if (src === 'expense') return language === 'sw' ? 'Gharama' : 'Expense';
                      return language === 'sw' ? 'Mwingine' : 'Manual';
                    };
                    // Compute running balance from the FULL list (so balance stays
                    // accurate across hidden rows), then filter the rows for display.
                    let runBal = drilldownData.openingBalance || 0;
                    const allRows = (drilldownData.items || []).map(item => {
                      runBal += item.type === 'credit' ? item.amount : -item.amount;
                      return { ...item, runningBalance: runBal };
                    });
                    // Resolve a row's account type (cash/bank/mobilemoney) for filtering.
                    const rowAcctType = (item) => {
                      // Try to read paymentMethod off the source record
                      const rec = item.sourceRecord || {};
                      if (item.source === 'manual') return rec.account || 'cash';
                      // For multi-payment records, item already represents the matching portion;
                      // we infer the type from the original record's primary payment method.
                      const pm = rec.paymentMethod || 'cash';
                      if (pm === 'bank') return 'bank';
                      if (pm === 'mobilemoney') return 'mobilemoney';
                      return 'cash';
                    };
                    const searchQ = (drilldownFilterSearch || '').trim().toLowerCase();
                    const filtersActive = drilldownFilterAcctType !== 'all' || drilldownFilterSource !== 'all' || !!searchQ;
                    const rows = allRows.filter(item => {
                      if (drilldownFilterAcctType !== 'all' && rowAcctType(item) !== drilldownFilterAcctType) return false;
                      if (drilldownFilterSource !== 'all') {
                        if (drilldownFilterSource === 'return' && !item.isReturnEntry) return false;
                        if (drilldownFilterSource !== 'return' && item.source !== drilldownFilterSource) return false;
                      }
                      if (searchQ) {
                        const hay = `${item.name || ''} ${(item.sourceRecord && item.sourceRecord.description) || ''}`.toLowerCase();
                        if (!hay.includes(searchQ)) return false;
                      }
                      return true;
                    });
                    const colProduct  = language === 'sw' ? 'Bidhaa/Huduma' : 'Product/Service';
                    const colGross    = language === 'sw' ? 'Kiasi cha Mwanzo' : 'Gross Amount';
                    const colReturns  = language === 'sw' ? 'Urudishaji' : 'Returns';
                    const colNet      = language === 'sw' ? 'Kiasi cha Mwisho' : 'Net Amount';
                    const colBalance  = language === 'sw' ? 'Salio' : 'Balance';
                    return (
                      <div style={{ overflowX: 'auto', marginTop: 4 }}>
                        <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%', minWidth: 680, tableLayout: 'auto' }}>
                          <thead>
                            <tr>
                              <th style={thS}>{t.date}</th>
                              <th style={thS}>{colProduct}</th>
                              <th style={thS}>{t.type || 'Type'}</th>
                              <th style={{ ...thS, textAlign: 'right' }}>{colGross}</th>
                              <th style={{ ...thS, textAlign: 'right' }}>{colReturns}</th>
                              <th style={{ ...thS, textAlign: 'right' }}>{colNet}</th>
                              <th style={{ ...thS, textAlign: 'right' }}>{colBalance}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {/* Opening balance row */}
                            <tr style={{ background: 'var(--bg-tab)', borderBottom: '1px solid var(--border-default)' }}>
                              <td colSpan={5} style={{ ...tdS, fontStyle: 'italic', color: 'var(--text-muted)', fontWeight: 600 }}>{t.openingBalance}</td>
                              <td style={{ ...tdS, textAlign: 'right' }}></td>
                              <td style={{ ...tdS, textAlign: 'right', fontWeight: 700, color: (drilldownData.openingBalance || 0) >= 0 ? '#059669' : '#DC2626' }}>{fmt(drilldownData.openingBalance || 0)}</td>
                            </tr>
                            {rows.length === 0 && (
                              <tr><td colSpan={7} style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text-muted)', fontSize: 13 }}>{t.noRecordsFound}</td></tr>
                            )}
                            {rows.map((item, i) => {
                              const retAmt = parseFloat(item.returnAmt || 0);
                              const grossAmt = parseFloat(item.amount || 0);
                              // Net: for credits (sales) gross minus returns; for debits just the gross (negative)
                              const netAmt = item.type === 'credit' ? grossAmt - retAmt : grossAmt;
                              const isRetEntry = item.isReturnEntry;
                              const rowBg = isRetEntry
                                ? '#FFF7ED'
                                : i % 2 === 0 ? 'var(--bg-white)' : 'var(--bg-tab)';
                              return (
                                <tr key={i} style={{ background: rowBg, borderBottom: '1px solid var(--border-default)' }}>
                                  <td style={{ ...tdS, whiteSpace: 'nowrap' }}>{item.date}</td>
                                  <td style={{ ...tdS, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {isRetEntry
                                      ? <span style={{ color: '#D97706', fontWeight: 600 }}>↩ {language === 'sw' ? 'Urudishaji' : 'Return'}</span>
                                      : (item.name || '—')
                                    }
                                  </td>
                                  <td style={{ ...tdS, whiteSpace: 'nowrap' }}>
                                    <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 6,
                                      background: isRetEntry ? '#FEF3C7' : item.type === 'credit' ? '#D1FAE5' : '#FEE2E2',
                                      color: isRetEntry ? '#92400E' : item.type === 'credit' ? '#065F46' : '#991B1B' }}>
                                      {isRetEntry ? (language === 'sw' ? 'Urudishaji' : 'Return') : srcLabel(item.source)}
                                    </span>
                                  </td>
                                  {/* Gross Amount */}
                                  <td style={{ ...tdS, textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap',
                                    color: item.type === 'credit' ? '#059669' : '#DC2626' }}>
                                    {item.type === 'credit' ? '+' : '−'}{fmt(grossAmt)}
                                  </td>
                                  {/* Returns column: negative for sales returns, blank for others */}
                                  <td style={{ ...tdS, textAlign: 'right', whiteSpace: 'nowrap',
                                    color: retAmt > 0 ? '#DC2626' : 'var(--text-muted)' }}>
                                    {retAmt > 0 ? `−${fmt(retAmt)}` : '—'}
                                  </td>
                                  {/* Net Amount */}
                                  <td style={{ ...tdS, textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap',
                                    color: item.type === 'credit' ? '#059669' : '#DC2626' }}>
                                    {item.type === 'credit' ? '+' : '−'}{fmt(netAmt)}
                                  </td>
                                  {/* Running Balance */}
                                  <td style={{ ...tdS, textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap',
                                    color: item.runningBalance >= 0 ? 'var(--text-primary)' : '#DC2626' }}>
                                    {fmt(item.runningBalance)}
                                  </td>
                                </tr>
                              );
                            })}
                            {/* Filtered subtotal -appears only when filters are active */}
                            {filtersActive && rows.length > 0 && (() => {
                              const inflow = rows.filter(r => r.type === 'credit').reduce((s, r) => s + parseFloat(r.amount || 0), 0);
                              const outflow = rows.filter(r => r.type !== 'credit').reduce((s, r) => s + parseFloat(r.amount || 0), 0);
                              const net = inflow - outflow;
                              return (
                                <tr style={{ background: '#FEF3C7', borderTop: '2px dashed #FCD34D' }}>
                                  <td colSpan={3} style={{ ...tdS, fontWeight: 700, color: '#92400E' }}>
                                    {language === 'sw' ? 'Jumla ndogo (yenye vichujio)' : 'Filtered subtotal'} · {rows.length} {language === 'sw' ? 'safu' : 'rows'}
                                  </td>
                                  <td style={{ ...tdS, textAlign: 'right', fontWeight: 700, color: '#059669' }}>+{fmt(inflow)}</td>
                                  <td></td>
                                  <td style={{ ...tdS, textAlign: 'right', fontWeight: 700, color: '#DC2626' }}>-{fmt(outflow)}</td>
                                  <td style={{ ...tdS, textAlign: 'right', fontWeight: 800, color: net >= 0 ? '#059669' : '#DC2626' }}>{net >= 0 ? '+' : ''}{fmt(net)}</td>
                                </tr>
                              );
                            })()}
                            {/* Closing balance row -always shows the un-filtered closing */}
                            {allRows.length > 0 && (
                              <tr style={{ background: 'var(--bg-white)', borderTop: '2px solid var(--border-default)' }}>
                                <td colSpan={5} style={{ ...tdS, fontWeight: 700, color: 'var(--text-primary)' }}>{t.closingBalance}{filtersActive ? ` (${language === 'sw' ? 'jumla yote' : 'all rows'})` : ''}</td>
                                <td></td>
                                <td style={{ ...tdS, textAlign: 'right', fontWeight: 800, fontSize: 13,
                                  color: (drilldownData.closingBalance || 0) >= 0 ? '#059669' : '#DC2626' }}>
                                  {fmt(drilldownData.closingBalance || 0)}
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    );
                  })()}

                  {/* ── RECEIVABLES: payment button + form + list ── */}
                  {activeDrilldown === 'receivables' && (
                    <>
                      {(drilldownData.items || []).length > 0 && (
                        <div style={{ marginBottom: 14 }}>
                          <button onClick={() => { setShowDebtPaymentForm(v => !v); setDebtPaymentData({ saleId: '', amount: '', paymentMethod: 'cash', date: new Date().toISOString().split('T')[0] }); }}
                            style={{ padding: '8px 14px', borderRadius: 10, background: '#16A34A', color: '#fff', border: 'none', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                            + {showDebtPaymentForm ? t.cancel : t.receiveDebtPayment}
                          </button>
                          {showDebtPaymentForm && (
                            <div style={{ background: 'var(--bg-white)', borderRadius: 14, padding: 18, marginTop: 10, boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
                              <h4 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{t.receiveDebtPayment}</h4>
                              <div className="form-group">
                                <label>{t.selectDebt}</label>
                                <select value={debtPaymentData.saleId}
                                  onChange={e => {
                                    const sel = (drilldownData.items || []).find(r => String(r.id) === e.target.value);
                                    setDebtPaymentData(p => ({ ...p, saleId: e.target.value, amount: sel ? sel.amount : '' }));
                                  }}>
                                  <option value="">{t.selectDebt}</option>
                                  {(drilldownData.items || []).map(r => (
                                    <option key={r.id} value={r.id}>{r.customerName} · {r.productName} ({fmt(r.amount)})</option>
                                  ))}
                                </select>
                              </div>
                              <div className="form-group">
                                <label>{t.amount}</label>
                                <input type="number" min="0" value={debtPaymentData.amount} readOnly
                                  style={{ background: 'var(--bg-input)' }}
                                  placeholder="0" />
                              </div>
                              <div className="form-group">
                                <label>{t.paymentMethod}</label>
                                <select value={debtPaymentData.paymentMethod} onChange={e => setDebtPaymentData(p => ({ ...p, paymentMethod: e.target.value }))}>
                                  {buildPaymentOptions(false).map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                  ))}
                                </select>
                              </div>
                              <div className="form-group">
                                <label>{t.date}</label>
                                <input type="date" value={debtPaymentData.date} onChange={e => setDebtPaymentData(p => ({ ...p, date: e.target.value }))} />
                              </div>
                              <button onClick={handleDebtPayment} disabled={!debtPaymentData.saleId}
                                style={{ width: '100%', padding: 13, borderRadius: 10, background: debtPaymentData.saleId ? '#16A34A' : '#D1D5DB', color: '#fff', border: 'none', fontWeight: 700, fontSize: 14, cursor: debtPaymentData.saleId ? 'pointer' : 'not-allowed', marginTop: 4 }}>
                                {t.markAsPaid}
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                      {(drilldownData.items || []).length === 0 && (
                        <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)', fontSize: 14 }}>{t.noRecordsFound}</div>
                      )}
                      {(drilldownData.items || []).length > 0 && (() => {
                        const thS = { padding: '9px 10px', textAlign: 'left', fontWeight: 700, color: 'var(--text-secondary)', fontSize: 11, borderBottom: '2px solid var(--border-default)', whiteSpace: 'nowrap', background: 'var(--bg-input)' };
                        const tdS = { padding: '9px 10px', fontSize: 12, verticalAlign: 'middle', color: 'var(--text-primary)' };
                        const editBtnS = { background: 'var(--bg-input)', color: 'var(--text-secondary)', border: 'none', borderRadius: 6, padding: '4px 7px', cursor: 'pointer', fontSize: 13, marginRight: 3 };
                        const delBtnS  = { background: '#FEE2E2', color: '#DC2626', border: 'none', borderRadius: 6, padding: '4px 7px', cursor: 'pointer', fontSize: 13 };
                        return (
                          <div style={{ overflowX: 'auto' }}>
                            <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%', minWidth: 420, tableLayout: 'auto' }}>
                              <thead>
                                <tr>
                                  <th style={thS}>{t.date}</th>
                                  <th style={thS}>{t.customer || 'Customer'}</th>
                                  <th style={thS}>{t.product || 'Product'}</th>
                                  <th style={{ ...thS, textAlign: 'right' }}>{t.amount || 'Amount'}</th>
                                  <th style={{ ...thS, textAlign: 'center' }}>{t.actions || 'Actions'}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(drilldownData.items || []).map((r, i) => (
                                  <tr key={r.id || i} style={{ background: i % 2 === 0 ? 'var(--bg-white)' : 'var(--bg-tab)', borderBottom: '1px solid var(--border-default)' }}>
                                    <td style={{ ...tdS, whiteSpace: 'nowrap' }}>{r.date}</td>
                                    <td style={{ ...tdS, maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600 }}>{r.customerName}</td>
                                    <td style={{ ...tdS, maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.productName}</td>
                                    <td style={{ ...tdS, textAlign: 'right', fontWeight: 700, color: '#92400E', whiteSpace: 'nowrap' }}>{fmt(r.amount)}</td>
                                    <td style={{ ...tdS, textAlign: 'center', whiteSpace: 'nowrap' }}>
                                      <button onClick={() => { const sale = sales.find(s => s.id === r.id); if (sale) setViewTxnModal({ open: true, kind: 'sale', record: sale }); }} style={{ background: 'var(--bg-input)', color: 'var(--text-secondary)', border: 'none', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 700 }} title={language === 'sw' ? 'Tazama' : 'View'}>{language === 'sw' ? 'Tazama' : 'View'}</button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        );
                      })()}
                    </>
                  )}

                  {/* ── PAYABLES: payment button + form + list ── */}
                  {activeDrilldown === 'payables' && (
                    <>
                      {(drilldownData.items || []).length > 0 && (
                        <div style={{ marginBottom: 14 }}>
                          <button onClick={() => { setShowLoanPaymentForm(v => !v); setLoanPaymentData({ purchaseId: '', amount: '', paymentMethod: 'cash', date: new Date().toISOString().split('T')[0] }); }}
                            style={{ padding: '8px 14px', borderRadius: 10, background: '#DC2626', color: '#fff', border: 'none', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                            + {showLoanPaymentForm ? t.cancel : t.payLoan}
                          </button>
                          {showLoanPaymentForm && (
                            <div style={{ background: 'var(--bg-white)', borderRadius: 14, padding: 18, marginTop: 10, boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
                              <h4 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{t.payLoan}</h4>
                              <div className="form-group">
                                <label>{t.selectLoan}</label>
                                <select value={loanPaymentData.purchaseId}
                                  onChange={e => {
                                    const sel = (drilldownData.items || []).find(p => String(p.id) === e.target.value);
                                    setLoanPaymentData(p => ({ ...p, purchaseId: e.target.value, amount: sel ? sel.amount : '' }));
                                  }}>
                                  <option value="">{t.selectLoan}</option>
                                  {(drilldownData.items || []).map(p => (
                                    <option key={p.id} value={p.id}>{p.supplier} · {p.productName} ({fmt(p.amount)})</option>
                                  ))}
                                </select>
                              </div>
                              <div className="form-group">
                                <label>{t.amount}</label>
                                <input type="number" min="0" value={loanPaymentData.amount} readOnly
                                  style={{ background: 'var(--bg-input)' }}
                                  placeholder="0" />
                              </div>
                              <div className="form-group">
                                <label>{t.paymentMethod}</label>
                                <select value={loanPaymentData.paymentMethod} onChange={e => setLoanPaymentData(p => ({ ...p, paymentMethod: e.target.value }))}>
                                  {buildPaymentOptions(false).map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                  ))}
                                </select>
                              </div>
                              <div className="form-group">
                                <label>{t.date}</label>
                                <input type="date" value={loanPaymentData.date} onChange={e => setLoanPaymentData(p => ({ ...p, date: e.target.value }))} />
                              </div>
                              <button onClick={handleLoanPayment} disabled={!loanPaymentData.purchaseId}
                                style={{ width: '100%', padding: 13, borderRadius: 10, background: loanPaymentData.purchaseId ? '#DC2626' : '#D1D5DB', color: '#fff', border: 'none', fontWeight: 700, fontSize: 14, cursor: loanPaymentData.purchaseId ? 'pointer' : 'not-allowed', marginTop: 4 }}>
                                {t.markAsPaid}
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                      {(drilldownData.items || []).length === 0 && (
                        <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)', fontSize: 14 }}>{t.noRecordsFound}</div>
                      )}
                      {(drilldownData.items || []).length > 0 && (() => {
                        const thS = { padding: '9px 10px', textAlign: 'left', fontWeight: 700, color: 'var(--text-secondary)', fontSize: 11, borderBottom: '2px solid var(--border-default)', whiteSpace: 'nowrap', background: 'var(--bg-input)' };
                        const tdS = { padding: '9px 10px', fontSize: 12, verticalAlign: 'middle', color: 'var(--text-primary)' };
                        const editBtnS = { background: '#E0E7FF', color: '#3730A3', border: 'none', borderRadius: 6, padding: '4px 7px', cursor: 'pointer', fontSize: 13, marginRight: 3 };
                        const delBtnS  = { background: '#FEE2E2', color: '#DC2626', border: 'none', borderRadius: 6, padding: '4px 7px', cursor: 'pointer', fontSize: 13 };
                        return (
                          <div style={{ overflowX: 'auto' }}>
                            <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%', minWidth: 420, tableLayout: 'auto' }}>
                              <thead>
                                <tr>
                                  <th style={thS}>{t.date}</th>
                                  <th style={thS}>{t.supplier || 'Supplier'}</th>
                                  <th style={thS}>{t.product || 'Product'}</th>
                                  <th style={{ ...thS, textAlign: 'right' }}>{t.amount || 'Amount'}</th>
                                  <th style={{ ...thS, textAlign: 'center' }}>{t.actions || 'Actions'}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(drilldownData.items || []).map((p, i) => (
                                  <tr key={p.id || i} style={{ background: i % 2 === 0 ? 'var(--bg-white)' : 'var(--bg-tab)', borderBottom: '1px solid var(--border-default)' }}>
                                    <td style={{ ...tdS, whiteSpace: 'nowrap' }}>{p.date}</td>
                                    <td style={{ ...tdS, maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600 }}>{p.supplier}</td>
                                    <td style={{ ...tdS, maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.productName}</td>
                                    <td style={{ ...tdS, textAlign: 'right', fontWeight: 700, color: '#DC2626', whiteSpace: 'nowrap' }}>{fmt(p.amount)}</td>
                                    <td style={{ ...tdS, textAlign: 'center', whiteSpace: 'nowrap' }}>
                                      <button onClick={() => { const pur = purchases.find(x => x.id === p.id); if (pur) setViewTxnModal({ open: true, kind: 'purchase', record: pur }); }} style={{ background: 'var(--bg-input)', color: 'var(--text-secondary)', border: 'none', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 700 }} title={language === 'sw' ? 'Tazama' : 'View'}>{language === 'sw' ? 'Tazama' : 'View'}</button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        );
                      })()}
                    </>
                  )}

                  {/* ── STOCK / LOW STOCK tables ── */}
                  {(activeDrilldown === 'stock' || activeDrilldown === 'lowstock') && (() => {
                    const rows = drilldownData.rows || [];
                    const headers = drilldownData.headers || [];
                    if (rows.length === 0) {
                      return (
                        <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)', fontSize: 14 }}>No items found</div>
                      );
                    }
                    const thS = { padding: '9px 10px', textAlign: 'left', fontWeight: 700, color: 'var(--text-secondary)', fontSize: 11, borderBottom: '2px solid var(--border-default)', whiteSpace: 'nowrap', background: 'var(--bg-input)' };
                    const tdS = { padding: '9px 10px', fontSize: 12, verticalAlign: 'middle', color: 'var(--text-primary)' };
                    const statusStyle = (status) => {
                      if (status === 'Out of Stock') return { background: '#FEE2E2', color: '#991B1B' };
                      if (status === 'Low')          return { background: '#FEF3C7', color: '#92400E' };
                      return { background: '#DCFCE7', color: '#065F46' };
                    };
                    return (
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%', minWidth: 420, tableLayout: 'auto' }}>
                          <thead>
                            <tr>
                              {headers.map((h, i) => (
                                <th key={i} style={{ ...thS, textAlign: (h === 'Quantity' || h === 'Unit Cost' || h === 'Value' || h === 'Alert Level') ? 'right' : (h === 'Status' ? 'center' : 'left') }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map((r, i) => (
                              <tr key={i} style={{ background: i % 2 === 0 ? 'var(--bg-white)' : 'var(--bg-tab)', borderBottom: '1px solid var(--border-default)' }}>
                                {r.map((cell, j) => {
                                  const h = headers[j];
                                  if (h === 'Status') {
                                    const s = statusStyle(cell);
                                    return (
                                      <td key={j} style={{ ...tdS, textAlign: 'center' }}>
                                        <span style={{ display: 'inline-block', padding: '3px 8px', borderRadius: 10, fontWeight: 700, fontSize: 11, ...s }}>{cell}</span>
                                      </td>
                                    );
                                  }
                                  const align = (h === 'Quantity' || h === 'Unit Cost' || h === 'Value' || h === 'Alert Level') ? 'right' : 'left';
                                  const weight = (h === 'Product Name') ? 600 : 500;
                                  return (
                                    <td key={j} style={{ ...tdS, textAlign: align, fontWeight: weight, whiteSpace: 'nowrap' }}>{cell}</td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  })()}

                  {/* Update Balance / Withdraw button (balance screens only,
                      not for 'total' since the aggregate isn't a single wallet) */}
                  {drilldownData.isBalance && drilldownData.acct !== 'total' && (
                    <div style={{ marginTop: 16 }}>
                      <button onClick={() => setShowWithdrawalForm(v => !v)} style={{ width: '100%', padding: 13, borderRadius: 10, background: 'var(--text-dark)', color: 'var(--bg-main)', border: 'none', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
                        {showWithdrawalForm ? t.cancel : `+ ${t.updateBalanceWithdraw}`}
                      </button>
                      {showWithdrawalForm && (
                        <div style={{ background: 'var(--bg-white)', borderRadius: 12, padding: 16, marginTop: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                          <h4 style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{t.manualTransaction}</h4>
                          <div className="form-group">
                            <label>{t.date}</label>
                            <input type="date" value={withdrawalFormData.date} onChange={e => setWithdrawalFormData(p => ({ ...p, date: e.target.value }))} />
                          </div>
                          <div className="form-group">
                            <label>{t.transactionTypeLabel}</label>
                            <select value={withdrawalFormData.type} onChange={e => setWithdrawalFormData(p => ({ ...p, type: e.target.value }))}>
                              <option value="debit">{t.withdrawOption}</option>
                              <option value="credit">{t.depositOption}</option>
                            </select>
                          </div>
                          <div className="form-group">
                            <label>{t.amount} (TSh)</label>
                            <input type="number" min="0" value={withdrawalFormData.amount} onChange={e => setWithdrawalFormData(p => ({ ...p, amount: e.target.value }))} placeholder={t.enterAmountPlaceholder} />
                          </div>
                          <div className="form-group">
                            <label>{t.description} ({t.contactOptional.split('(')[1]?.replace(')', '') || 'optional'})</label>
                            <input type="text" value={withdrawalFormData.description} onChange={e => setWithdrawalFormData(p => ({ ...p, description: e.target.value }))} placeholder={t.withdrawalDescPlaceholder} />
                          </div>
                          <button onClick={handleWithdrawalSave} style={{ width: '100%', padding: 11, borderRadius: 8, background: '#A3E635', color: '#0F172A', border: 'none', fontWeight: 700, fontSize: 14, cursor: 'pointer', marginTop: 8 }}>
                            {t.saveTransaction}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
            {/* ── END DRILLDOWN OVERLAY ────────────────────────────────── */}

            {(() => {
              // ── Real KPI computation from AppContext data ──
              // H5: all four dashboard KPI sets use the ONE shared helper (with
              // custom start/end when the user picks Custom).
              const periodSales     = filterByPeriod(sales, dashboardTimeFilter, dashboardCustomFrom, dashboardCustomTo).filter(s => s.status !== 'void');
              const periodPurchases = filterByPeriod(purchases, dashboardTimeFilter, dashboardCustomFrom, dashboardCustomTo).filter(p => p.status !== 'void');
              const periodExpenses  = filterByPeriod(expenses, dashboardTimeFilter, dashboardCustomFrom, dashboardCustomTo).filter(e => e.status !== 'void');
              const periodServiceTransactions = filterByPeriod(serviceTransactions, dashboardTimeFilter, dashboardCustomFrom, dashboardCustomTo).filter(st => st.status !== 'void');

              // ── Returns in this period (net figures, matching the reports) ──
              // The reports show revenue/purchases NET of returns; the dashboard
              // must too. A sales return lowers revenue by the refund AND puts
              // the goods back in stock (lowering COGS by their cost), so profit
              // falls by only the margin on the returned items.
              // Returns attributable to the transactions in THIS period, so the
              // boxes equal the reports (which net out returns of listed items).
              const _periodSaleIds = new Set(periodSales.map(s => String(s.id)));
              const _periodPurchaseIds = new Set(periodPurchases.map(p => String(p.id)));
              const periodSalesReturns = (salesReturns || []).filter(r => _periodSaleIds.has(String(r.originalSaleId)) && r.status !== 'void');
              const periodPurchaseReturns = (purchaseReturns || []).filter(r => _periodPurchaseIds.has(String(r.originalPurchaseId)) && r.status !== 'void');
              const salesReturnsRefund = periodSalesReturns.reduce((s, r) => s + parseFloat(r.refundAmt || r.totalRefund || 0), 0);
              const salesReturnsCost = periodSalesReturns.reduce((s, r) => s + returnGoodsCost(r), 0);
              const purchaseReturnsRefund = periodPurchaseReturns.reduce((s, r) => s + parseFloat(r.refundAmt || r.totalRefund || 0), 0);

              // Revenue/cost use each transaction's GRAND TOTAL (incl. taxes and
              // discount) -the exact figure the reports show per row.
              const kpiRevenue   = periodSales.reduce((s, x) => s + getSaleGrandTotal(x), 0) - salesReturnsRefund;
              const kpiPurchases = periodPurchases.reduce((s, x) => s + getPurchaseGrandTotal(x), 0) - purchaseReturnsRefund;
              const kpiExpenses  = periodExpenses.reduce((s, x) => s + parseFloat(x.amount || 0), 0);
              const kpiServiceRevenue = periodServiceTransactions.reduce((s, x) => s + (x.totalRevenue || 0), 0);
              // COGS = chronological replay (H4 source of truth), matching the
              // Business Statement. Returns are reversed at cost inside the replay,
              // so salesReturnsCost is NOT subtracted again (kept for other rows).
              const _kpiRange = getPeriodRange(dashboardTimeFilter, dashboardCustomFrom, dashboardCustomTo);
              const kpiCOGS = getReplayCOGS(_kpiRange.from, _kpiRange.to);
              void salesReturnsCost;
              // ── VAT is a pass-through, NOT income ──────────────────────────
              // Output VAT (charged on sales) is collected on behalf of TRA, so
              // it must be EXCLUDED from the revenue that feeds profit. Input
              // VAT (paid on purchases) is what you can reclaim. We report both
              // separately; net VAT payable = output − input.
              const kpiOutputVAT = periodSales.reduce((s, x) => s + getSaleTax(x), 0);
              const kpiInputVAT  = periodPurchases.reduce((s, x) => s + getPurchaseTax(x), 0);
              const kpiVATPayable = kpiOutputVAT - kpiInputVAT;
              // Revenue used for PROFIT excludes the VAT collected.
              const kpiRevenueExVAT = kpiRevenue - kpiOutputVAT;
              // Service COGS is intentionally NOT deducted -the app does not
              // capture cost-of-service per transaction, so service revenue is
              // treated as 100% gross margin. Materials/labour for services
              // flow through the general Expenses pool instead. This matches
              // how QuickBooks Self-Employed, FreshBooks, Wave, and Xero
              // handle service-only setups by default.
              const kpiProfit = businessType === 'service_only'
                ? kpiServiceRevenue - kpiExpenses
                : businessType === 'products_only'
                  ? kpiRevenueExVAT - kpiCOGS - kpiExpenses
                  : (kpiRevenueExVAT - kpiCOGS) + kpiServiceRevenue - kpiExpenses;

              // Paid vs credit/unpaid breakdowns for the selected period.
              // "Credit" = the amount STILL OWED (outstanding balance), not the
              // full value of a partly-paid sale.
              const salesCredit   = periodSales.reduce((s, x) => s + saleOutstanding(x), 0);
              const salesPaid     = periodSales.reduce((s, x) => s + (getSaleGrandTotal(x) - saleOutstanding(x)), 0);
              const purchUnpaid   = periodPurchases.filter(x => x.isPaid === false).reduce((s, x) => s + getPurchaseGrandTotal(x), 0);
              const purchPaid     = periodPurchases.filter(x => x.isPaid !== false).reduce((s, x) => s + getPurchaseGrandTotal(x), 0);
              const serviceSalesCredit = periodSales.filter(x => x.isService).reduce((s, x) => s + saleOutstanding(x), 0);
              const serviceSalesPaid = periodSales.filter(x => x.isService).reduce((s, x) => s + (getSaleGrandTotal(x) - saleOutstanding(x)), 0);

              // ── Top selling products by revenue -credit all items in multi-product orders ──
              // ACCOUNTING AGGREGATE: reads acctSales so a voided sale contributes
              // no revenue and no units. (Before read-query 024 voided rows never
              // reached the client, which masked this; they do now.)
              const topMap = {};
              acctSales.forEach(s => {
                const items = s.items && s.items.length > 0 ? s.items : [{ productName: s.productName, quantity: s.quantity, sellingPrice: s.sellingPrice }];
                const orderTotal = parseFloat(s.totalRevenue || 0);
                const itemSubtotal = items.reduce((sum, i) => sum + (parseFloat(i.quantity || 1) * parseFloat(i.sellingPrice || 0)), 0);
                items.forEach(item => {
                  const name = item.productName || s.productName;
                  if (!name) return;
                  if (!topMap[name]) topMap[name] = { sold: 0, revenue: 0 };
                  const qty = parseFloat(item.quantity || 1);
                  topMap[name].sold += qty;
                  // Apportion revenue proportionally if multi-item order
                  const itemRev = itemSubtotal > 0 ? (qty * parseFloat(item.sellingPrice || 0) / itemSubtotal) * orderTotal : 0;
                  topMap[name].revenue += itemRev;
                });
              });
              const dbTopProducts = Object.entries(topMap)
                .map(([name, d]) => ({ name, ...d }))
                .sort((a, b) => b.revenue - a.revenue)
                .slice(0, 5);

              // ── Stock status from products ──
              const totalStockItems = products.reduce((s, p) => s + (p.balance || p.currentStock || 0), 0);
              const lowStockCount   = products.filter(p => { const qty = (p.balance || p.currentStock || 0); const alert = parseInt(p.minStockAlert || p.lowStockLevel || 0) || 0; return alert > 0 && qty <= alert; }).length;

              // ── Recent activity from real data ──
              const firstProductName = (rec) => {
                if (Array.isArray(rec.items) && rec.items.length > 0) return rec.items[0].productName || '—';
                return rec.productName || '—';
              };
              const extraItemCount = (rec) => {
                if (Array.isArray(rec.items) && rec.items.length > 1) return rec.items.length - 1;
                return 0;
              };
              // HISTORY LIST: voided entries stay visible (audit trail) but carry
              // `voided` so the row renders struck through with a VOIDED badge.
              // This list drives no total -every dashboard figure above reads the
              // void-free period arrays.
              const _isVoid = (r) => r && r.status === 'void';
              // Take the most recent N BY DATE, not by array position. slice(-N)
              // assumed the arrays were oldest-first (local append order), but the
              // relational adapter returns them newest-first (ORDER BY date DESC),
              // so slice(-N) was silently picking the OLDEST rows and dropping the
              // newest transaction before the sort below ever ran.
              const _newest = (arr, n) => [...(arr || [])]
                .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0))
                .slice(0, n);
              const recentItems = [
                ..._newest(sales, 4).map(s => ({ id: s.id, type: 'sale', title: firstProductName(s), extra: extraItemCount(s), amount: parseFloat(s.totalRevenue || 0), date: s.date, voided: _isVoid(s) })),
                ..._newest(purchases, 3).map(p => ({ id: p.id, type: 'purchase', title: firstProductName(p), extra: extraItemCount(p), amount: parseFloat(p.totalCost || p.total || p.amount || 0), date: p.date, voided: _isVoid(p) })),
                ..._newest(expenses, 2).map(e => ({ id: e.id, type: 'expense', title: e.category, extra: 0, amount: parseFloat(e.amount || 0), date: e.date, voided: _isVoid(e) })),
                ..._newest(purchaseReturns, 2).map(r => ({ id: 'pr-' + r.id, type: 'purchaseReturn', title: firstProductName(r), extra: extraItemCount(r), amount: parseFloat(r.refundAmt || r.totalRefund || 0), date: r.date, voided: _isVoid(r) })),
                ..._newest(salesReturns, 2).map(r => ({ id: 'sr-' + r.id, type: 'saleReturn', title: firstProductName(r), extra: extraItemCount(r), amount: parseFloat(r.refundAmt || r.totalRefund || 0), date: r.date, voided: _isVoid(r) })),
              ].sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0)).slice(0, 7);

              const typeConfig = {
                sale:           { label: t.sales    || 'Sale',     color: '#059669', bg: '#ECFCCB', sign: '+' },
                expense:        { label: t.expenses || 'Expense',  color: '#EF4444', bg: '#FEE2E2', sign: '-' },
                purchase:       { label: t.purchases|| 'Purchase', color: '#F59E0B', bg: '#FEF3C7', sign: '-' },
                purchaseReturn: { label: language === 'sw' ? 'Rudisha Manunuzi' : 'Purch. Return', color: '#DC2626', bg: '#FEE2E2', sign: '+' },
                saleReturn:     { label: language === 'sw' ? 'Rudisha Mauzo' : 'Sale Return', color: '#DC2626', bg: '#FEE2E2', sign: '-' },
              };

              return (
                <div className="db-main">
                  {/* HEADER */}
                  <div className="db-header-row">
                    <div>
                      <h1 className="db-header">{t.businessOverview}</h1>
                      <div className="db-greeting">{getGreeting(t)} &middot; {getFormattedDate(t)}</div>
                    </div>
                  </div>

                  {/* GLOBAL TIME FILTER */}
                  <div className="db-time-filter">
                    {[
                      { key: 'today', label: t.today },
                      { key: 'week',  label: t.thisWeek  },
                      { key: 'month', label: t.thisMonth },
                      { key: 'year',  label: t.thisYear  },
                      { key: 'custom', label: t.custom }
                    ].map(f => (
                      <button
                        key={f.key}
                        className={`db-time-btn ${dashboardTimeFilter === f.key ? 'db-time-active' : ''}`}
                        onClick={() => {
                          setDashboardTimeFilter(f.key);
                          if (f.key === 'custom' && !dashboardCustomFrom && !dashboardCustomTo) {
                            // Pre-fill Custom with the shared month-to-date range.
                            const r = getPeriodRange('month');
                            setDashboardCustomFrom(r.from);
                            setDashboardCustomTo(r.to);
                          }
                        }}
                      >
                        {f.label}
                      </button>
                    ))}
                  </div>

                  {/* Period date label OR custom date pickers */}
                  {(() => {
                    if (dashboardTimeFilter === 'custom') {
                      return (
                        <div className="db-custom-date-card">
                          <div className="db-custom-date-row">
                            <div className="db-custom-date-col">
                              <span className="db-custom-date-label">{t.fromDate}</span>
                              <input type="date" className="db-custom-date-input" value={dashboardCustomFrom} onChange={e => setDashboardCustomFrom(e.target.value)} />
                            </div>
                            <div className="db-custom-date-col">
                              <span className="db-custom-date-label">{t.toDate}</span>
                              <input type="date" className="db-custom-date-input" value={dashboardCustomTo} onChange={e => setDashboardCustomTo(e.target.value)} />
                            </div>
                          </div>
                        </div>
                      );
                    }
                    // H5: the label is built from the SAME shared range as the
                    // data, so "Month"/"Year" show the actual to-date window
                    // (not a full calendar month/year that the numbers don't cover).
                    const _now = new Date();
                    const _fmtStr = s => { if (!s) return ''; const [y, m, d] = s.split('-'); return `${d}/${m}/${y}`; };
                    const _range = getPeriodRange(dashboardTimeFilter, dashboardCustomFrom, dashboardCustomTo);
                    let periodText = '';
                    if (dashboardTimeFilter === 'today') {
                      periodText = _fmtStr(_range.from);
                    } else if (dashboardTimeFilter === 'month') {
                      const _monthName = (t.monthNames || ['January','February','March','April','May','June','July','August','September','October','November','December'])[_now.getMonth()];
                      periodText = `${_monthName} ${_now.getFullYear()} (${_fmtStr(_range.from)} \u2013 ${_fmtStr(_range.to)})`;
                    } else if (dashboardTimeFilter === 'year') {
                      periodText = `${_now.getFullYear()} (${_fmtStr(_range.from)} \u2013 ${_fmtStr(_range.to)})`;
                    } else if (_range.from && _range.to) {
                      periodText = `${_fmtStr(_range.from)} \u2013 ${_fmtStr(_range.to)}`;
                    }
                    return periodText ? (
                      <div className="period-date-label">{periodText}</div>
                    ) : null;
                  })()}

                  {!isDataReady && (
                    <div role="status" aria-label={t.loading || 'Loading'}>
                      <div className="db-kpi-grid">
                        {[1,2,3,4].map(i => (
                          <div key={i} className="db-card db-kpi-card" style={{ minHeight: 90 }}>
                            <div className="skeleton skeleton-text-short" style={{ width: '60%', marginBottom: 8 }}></div>
                            <div className="skeleton skeleton-kpi"></div>
                          </div>
                        ))}
                      </div>
                      <div style={{ padding: '0 16px', marginTop: 16 }}>
                        <div className="skeleton skeleton-chart"></div>
                        <div className="skeleton skeleton-chart" style={{ height: 120 }}></div>
                      </div>
                    </div>
                  )}
                  <div className={`db-kpi-grid${businessType === 'products_only' ? ' db-kpi-grid--2col' : ''}`} style={!isDataReady ? { display: 'none' } : undefined}>

                    {/* Reusable inline icon set per category */}
                    {(() => null)()}
                    {/* ── PRODUCTS ONLY ─────────────────────────────── */}
                    {businessType === 'products_only' && (<>
                      <div className="db-card db-kpi-card db-kpi-sales" onClick={() => { setActiveDrilldown('sales'); setDrilldownPeriod(dashboardTimeFilter); }} style={{ cursor: 'pointer' }}>
                        <div className="db-kpi-icon"><svg viewBox="0 0 24 24" fill="none" strokeWidth="2.5" strokeLinecap="round"><polyline points="12 19 12 5"/><polyline points="5 12 12 5 19 12"/></svg></div>
                        <div className="db-kpi-label">{t.totalRevenue || 'Total Revenue'}</div>
                        <div className="db-kpi-value">{fmt(kpiRevenue)}</div>
                        <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>{t.tapToView} ›</div>
                      </div>
                      {!isItemHidden('dashboard', 'purchasesCard') && (
                      <div className="db-card db-kpi-card db-kpi-purchases" onClick={() => { setActiveDrilldown('purchases'); setDrilldownPeriod(dashboardTimeFilter); }} style={{ cursor: 'pointer' }}>
                        <div className="db-kpi-icon"><svg viewBox="0 0 24 24" fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg></div>
                        <div className="db-kpi-label">{t.productPurchases || 'Product Purchases (Stock)'}</div>
                        <div className="db-kpi-value">{fmt(kpiPurchases)}</div>
                        <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>{t.tapToView} ›</div>
                      </div>
                      )}
                      <div className="db-card db-kpi-card db-kpi-expenses" onClick={() => { setActiveDrilldown('expenses'); setDrilldownPeriod(dashboardTimeFilter); }} style={{ cursor: 'pointer' }}>
                        <div className="db-kpi-icon"><svg viewBox="0 0 24 24" fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="11" x2="12" y2="17"/><line x1="9" y1="14" x2="15" y2="14"/></svg></div>
                        <div className="db-kpi-label">{t.totalExpenses}</div>
                        <div className="db-kpi-value">{fmt(kpiExpenses)}</div>
                        <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>{t.tapToView} ›</div>
                      </div>
                      {!isItemHidden('dashboard', 'profitCard') && (
                      <div className={`db-card db-kpi-card ${kpiProfit >= 0 ? 'db-kpi-profit-pos' : 'db-kpi-profit-neg'}`} style={{ cursor: 'default' }}>
                        <div className="db-kpi-icon"><svg viewBox="0 0 24 24" fill="none" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="6" x2="12" y2="12"/><line x1="12" y1="12" x2="16" y2="14"/></svg></div>
                        <div className="db-kpi-label">{kpiProfit >= 0 ? t.profit : (t.loss || 'Loss')}</div>
                        <div className="db-kpi-value">
                          {kpiProfit < 0 ? '-' : ''}{fmt(Math.abs(kpiProfit))}
                        </div>
                        {(kpiOutputVAT > 0 || kpiInputVAT > 0) && (
                          <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>
                            {language === 'sw' ? 'VAT iliyokusanywa' : 'VAT collected'}: {fmt(kpiOutputVAT)}
                            {kpiInputVAT > 0 ? ` · ${language === 'sw' ? 'kulipa TRA' : 'net payable'}: ${fmt(kpiVATPayable)}` : ''}
                          </div>
                        )}
                      </div>
                      )}
                    </>)}

                    {/* ── SERVICES ONLY ─────────────────────────────── */}
                    {businessType === 'service_only' && (<>
                      <div className="db-card db-kpi-card db-kpi-service" onClick={() => { setActiveDrilldown('services_only'); setDrilldownPeriod(dashboardTimeFilter); }} style={{ cursor: 'pointer' }}>
                        <div className="db-kpi-icon"><svg viewBox="0 0 24 24" fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg></div>
                        <div className="db-kpi-label">{t.serviceRevenue || 'Service Revenue'}</div>
                        <div className="db-kpi-value">{fmt(kpiServiceRevenue)}</div>
                        <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>{t.tapToView} ›</div>
                      </div>
                      <div className="db-card db-kpi-card db-kpi-expenses" onClick={() => { setActiveDrilldown('expenses'); setDrilldownPeriod(dashboardTimeFilter); }} style={{ cursor: 'pointer' }}>
                        <div className="db-kpi-icon"><svg viewBox="0 0 24 24" fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="11" x2="12" y2="17"/><line x1="9" y1="14" x2="15" y2="14"/></svg></div>
                        <div className="db-kpi-label">{t.totalExpenses}</div>
                        <div className="db-kpi-value">{fmt(kpiExpenses)}</div>
                        <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>{t.tapToView} ›</div>
                      </div>
                      {!isItemHidden('dashboard', 'profitCard') && (
                      <div className={`db-card db-kpi-card ${kpiProfit >= 0 ? 'db-kpi-profit-pos' : 'db-kpi-profit-neg'}`} style={{ cursor: 'default' }}>
                        <div className="db-kpi-icon"><svg viewBox="0 0 24 24" fill="none" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="6" x2="12" y2="12"/><line x1="12" y1="12" x2="16" y2="14"/></svg></div>
                        <div className="db-kpi-label">{kpiProfit >= 0 ? t.profit : (t.loss || 'Loss')}</div>
                        <div className="db-kpi-value">
                          {kpiProfit < 0 ? '-' : ''}{fmt(Math.abs(kpiProfit))}
                        </div>
                        {(kpiOutputVAT > 0 || kpiInputVAT > 0) && (
                          <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>
                            {language === 'sw' ? 'VAT iliyokusanywa' : 'VAT collected'}: {fmt(kpiOutputVAT)}
                            {kpiInputVAT > 0 ? ` · ${language === 'sw' ? 'kulipa TRA' : 'net payable'}: ${fmt(kpiVATPayable)}` : ''}
                          </div>
                        )}
                      </div>
                      )}
                    </>)}

                    {/* ── PRODUCTS & SERVICES ───────────────────────── */}
                    {businessType === 'products_and_services' && (<>
                      <div className="db-card db-kpi-card db-kpi-sales" onClick={() => { setActiveDrilldown('products_only'); setDrilldownPeriod(dashboardTimeFilter); }} style={{ cursor: 'pointer' }}>
                        <div className="db-kpi-icon"><svg viewBox="0 0 24 24" fill="none" strokeWidth="2.5" strokeLinecap="round"><polyline points="12 19 12 5"/><polyline points="5 12 12 5 19 12"/></svg></div>
                        <div className="db-kpi-label">{t.productRevenue || 'Product Revenue'}</div>
                        <div className="db-kpi-value">{fmt(kpiRevenue)}</div>
                        <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>{t.tapToView} ›</div>
                      </div>
                      <div className="db-card db-kpi-card db-kpi-service" onClick={() => { setActiveDrilldown('services_only'); setDrilldownPeriod(dashboardTimeFilter); }} style={{ cursor: 'pointer' }}>
                        <div className="db-kpi-icon"><svg viewBox="0 0 24 24" fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg></div>
                        <div className="db-kpi-label">{t.serviceRevenue || 'Service Revenue'}</div>
                        <div className="db-kpi-value">{fmt(kpiServiceRevenue)}</div>
                        <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>{t.tapToView} ›</div>
                      </div>
                      <div className="db-card db-kpi-card db-kpi-total-revenue" onClick={() => { setActiveDrilldown('total_revenue'); setDrilldownPeriod(dashboardTimeFilter); }} style={{ cursor: 'pointer' }}>
                        <div className="db-kpi-icon"><svg viewBox="0 0 24 24" fill="none" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg></div>
                        <div className="db-kpi-label">{t.totalRevenue || 'Total Revenue'}</div>
                        <div className="db-kpi-value">{fmt(kpiRevenue + kpiServiceRevenue)}</div>
                        <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>{t.tapToView} ›</div>
                      </div>
                      {!isItemHidden('dashboard', 'purchasesCard') && (
                      <div className="db-card db-kpi-card db-kpi-purchases" onClick={() => { setActiveDrilldown('purchases'); setDrilldownPeriod(dashboardTimeFilter); }} style={{ cursor: 'pointer' }}>
                        <div className="db-kpi-icon"><svg viewBox="0 0 24 24" fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg></div>
                        <div className="db-kpi-label">{t.productPurchases || 'Product Purchases (Stock)'}</div>
                        <div className="db-kpi-value">{fmt(kpiPurchases)}</div>
                        <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>{t.tapToView} ›</div>
                      </div>
                      )}
                      <div className="db-card db-kpi-card db-kpi-expenses" onClick={() => { setActiveDrilldown('expenses'); setDrilldownPeriod(dashboardTimeFilter); }} style={{ cursor: 'pointer' }}>
                        <div className="db-kpi-icon"><svg viewBox="0 0 24 24" fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="11" x2="12" y2="17"/><line x1="9" y1="14" x2="15" y2="14"/></svg></div>
                        <div className="db-kpi-label">{t.totalExpenses}</div>
                        <div className="db-kpi-value">{fmt(kpiExpenses)}</div>
                        <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>{t.tapToView} ›</div>
                      </div>
                      {!isItemHidden('dashboard', 'profitCard') && (
                      <div className={`db-card db-kpi-card ${kpiProfit >= 0 ? 'db-kpi-profit-pos' : 'db-kpi-profit-neg'}`} style={{ cursor: 'default' }}>
                        <div className="db-kpi-icon"><svg viewBox="0 0 24 24" fill="none" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="6" x2="12" y2="12"/><line x1="12" y1="12" x2="16" y2="14"/></svg></div>
                        <div className="db-kpi-label">{kpiProfit >= 0 ? t.profit : (t.loss || 'Loss')}</div>
                        <div className="db-kpi-value">
                          {kpiProfit < 0 ? '-' : ''}{fmt(Math.abs(kpiProfit))}
                        </div>
                        {(kpiOutputVAT > 0 || kpiInputVAT > 0) && (
                          <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>
                            {language === 'sw' ? 'VAT iliyokusanywa' : 'VAT collected'}: {fmt(kpiOutputVAT)}
                            {kpiInputVAT > 0 ? ` · ${language === 'sw' ? 'kulipa TRA' : 'net payable'}: ${fmt(kpiVATPayable)}` : ''}
                          </div>
                        )}
                      </div>
                      )}
                    </>)}

                  </div>

                  {/* CASH POSITION -the whole section hides if every row is hidden */}
                  {(() => {
                    const hideCash = isItemHidden('dashboard', 'cashBalance');
                    const hideBank = isItemHidden('dashboard', 'bankBalance');
                    const hideMob  = isItemHidden('dashboard', 'mobileBalance');
                    const hideAll  = hideCash && hideBank && hideMob;
                    if (hideAll) return null;
                    return (
                  <div className="db-card">
                    <h3 className="db-card-title" style={{ marginBottom: 12 }}>{t.cashPosition}</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {[
                        {
                          key: 'cash',
                          title: t.cash,
                          icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="6" width="20" height="12" rx="2"/><circle cx="12" cy="12" r="2"/><path d="M6 12h.01M18 12h.01"/></svg>,
                          bg: 'var(--bg-card)',
                          layer: 'rgba(21,128,61,0.05)',
                          val: adjustedBalances.cash || 0,
                          color: (adjustedBalances.cash || 0) >= 0 ? 'var(--text-primary)' : '#DC2626'
                        },
                        {
                          key: 'bank',
                          title: t.bank,
                          icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="22" x2="21" y2="22"/><line x1="6" y1="18" x2="6" y2="11"/><line x1="10" y1="18" x2="10" y2="11"/><line x1="14" y1="18" x2="14" y2="11"/><line x1="18" y1="18" x2="18" y2="11"/><polygon points="12 2 20 7 4 7"/></svg>,
                          bg: 'var(--bg-card)',
                          layer: 'rgba(21,128,61,0.05)',
                          val: adjustedBalances.bank || 0,
                          color: (adjustedBalances.bank || 0) >= 0 ? 'var(--text-primary)' : '#DC2626'
                        },
                        {
                          key: 'mobilemoney',
                          title: t.mobileMoney,
                          icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="2" width="14" height="20" rx="2" ry="2"/><line x1="12" y1="18" x2="12.01" y2="18"/></svg>,
                          bg: 'var(--bg-card)',
                          layer: 'rgba(21,128,61,0.05)',
                          val: adjustedBalances.mobileMoney || 0,
                          color: (adjustedBalances.mobileMoney || 0) >= 0 ? 'var(--text-primary)' : '#DC2626'
                        }
                      ]
                      // Per-row privacy filter
                      .filter(c => !(c.key === 'cash' && hideCash) && !(c.key === 'bank' && hideBank) && !(c.key === 'mobilemoney' && hideMob))
                      .map(card => (
                        <div key={card.key} style={{ position: 'relative' }}>
                          <div style={{ position: 'absolute', inset: 0, borderRadius: 12, background: card.layer, transform: 'none' }} />
                          <div style={{ position: 'absolute', inset: 0, borderRadius: 12, background: card.layer, transform: 'none', opacity: 0.8 }} />
                          <div onClick={() => { setActiveDrilldown(card.key); setDrilldownPeriod(dashboardTimeFilter); }} className={`db-click-box db-balance-card db-balance-${card.key}`} style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderRadius: 12, background: card.bg, cursor: 'pointer' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--text-primary)' }}>
                              {card.icon}
                              <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 14 }}>{card.title}</span>
                            </div>
                            <span style={{ fontWeight: 700, fontSize: 15, color: card.color }}>
                              {card.val < 0 ? '-' : ''}{fmt(Math.abs(card.val))}
                            </span>
                          </div>
                        </div>
                      ))}
                      {/* Total Position row -hidden when cashflowReport is hidden */}
                      {!isItemHidden('dashboard', 'cashflowReport') && (() => {
                        const totalPosition = (adjustedBalances.cash || 0) + (adjustedBalances.bank || 0) + (adjustedBalances.mobileMoney || 0);
                        return (
                          <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 0', borderTop: '2px solid #6B7280', background: 'transparent' }}>
                            <span style={{ fontWeight: 700, color: totalPosition >= 0 ? 'var(--text-primary)' : '#DC2626', fontSize: 14 }}>{t.netCashflow || 'Total'}</span>
                            <span style={{ fontWeight: 800, fontSize: 16, color: totalPosition >= 0 ? 'var(--text-primary)' : '#DC2626' }}>
                              {totalPosition < 0 ? '-' : ''}{fmt(Math.abs(totalPosition))}
                            </span>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                    );
                  })()}

                  {/* DEBTS -mobile only; on desktop these live in the Reports tab */}
                  <div className="db-card db-debts-mobile-only">
                    <h3 className="db-card-title" style={{ marginBottom: 12 }}>{t.debts}</h3>
                    <div style={{ display: 'flex', gap: 10, marginBottom: totalReceivables > 0 || totalPayables > 0 ? 14 : 0 }}>
                      <div style={{ position: 'relative', flex: 1 }}>
                        <div style={{ position: 'absolute', inset: 0, borderRadius: 12, background: 'rgba(5,150,105,0.05)', transform: 'none' }} />
                        <div style={{ position: 'absolute', inset: 0, borderRadius: 12, background: 'rgba(5,150,105,0.03)', transform: 'none', opacity: 0.8 }} />
                        <div onClick={() => setActiveDrilldown('receivables')} className="db-click-box db-debt-card db-debt-recv" style={{ position: 'relative', zIndex: 1, padding: '9px 12px', borderRadius: 12, background: '#ECFCCB', border: '1px solid #A3E635', cursor: 'pointer' }}>
                          <div style={{ fontSize: 11, color: '#3F6212', fontWeight: 600, marginBottom: 4 }}>{t.receivablesLabel}</div>
                          <div style={{ fontWeight: 800, fontSize: 16, color: '#365314' }}>{fmt(totalReceivables)}</div>
                          <div style={{ fontSize: 11, color: '#365314', marginTop: 2 }}>{t.customersOweYou} ›</div>
                        </div>
                      </div>
                      <div style={{ position: 'relative', flex: 1 }}>
                        <div style={{ position: 'absolute', inset: 0, borderRadius: 12, background: 'rgba(5,150,105,0.05)', transform: 'none' }} />
                        <div style={{ position: 'absolute', inset: 0, borderRadius: 12, background: 'rgba(5,150,105,0.03)', transform: 'none', opacity: 0.8 }} />
                        <div onClick={() => setActiveDrilldown('payables')} className="db-click-box db-debt-card db-debt-pay" style={{ position: 'relative', zIndex: 1, padding: '9px 12px', borderRadius: 12, background: '#ECFCCB', border: '1px solid #A3E635', cursor: 'pointer' }}>
                          <div style={{ fontSize: 11, color: '#3F6212', fontWeight: 600, marginBottom: 4 }}>{t.payablesLabel}</div>
                          <div style={{ fontWeight: 800, fontSize: 16, color: '#365314' }}>{fmt(totalPayables)}</div>
                          <div style={{ fontSize: 11, color: '#365314', marginTop: 2 }}>{t.youOweSuppliers} ›</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* STOCK STATUS -hidden for service_only (no stock to track) */}
                  {businessType !== 'service_only' && (
                  <div className="db-card">
                    <h3 className="db-card-title" style={{ marginBottom: 12 }}>{t.stockStatus || 'Stock'}</h3>
                    <div className="db-stock-row">
                    <div style={{ position: 'relative', flex: 1 }}>
                      <div style={{ position: 'absolute', inset: 0, borderRadius: 16, background: 'rgba(5,150,105,0.06)', transform: 'none' }} />
                      <div style={{ position: 'absolute', inset: 0, borderRadius: 16, background: 'rgba(5,150,105,0.06)', transform: 'none', opacity: 0.85 }} />
                      <div className="db-card db-stock-card db-stock-items db-click-box" onClick={() => { setActiveDrilldown('stock'); }} style={{ position: 'relative', zIndex: 1, cursor: 'pointer' }}>
                        <div className="db-stock-icon db-stock-icon-ok"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg></div>
                        <div className="db-stock-info">
                          <div className="db-stock-num">{totalStockItems}</div>
                          <div className="db-stock-label">{t.itemsInStock}</div>
                        </div>
                      </div>
                    </div>
                    <div style={{ position: 'relative', flex: 1 }}>
                      <div style={{ position: 'absolute', inset: 0, borderRadius: 16, background: 'rgba(5,150,105,0.05)', transform: 'none' }} />
                      <div style={{ position: 'absolute', inset: 0, borderRadius: 16, background: 'rgba(5,150,105,0.03)', transform: 'none', opacity: 0.85 }} />
                      <div className="db-card db-stock-card db-stock-low db-click-box" onClick={() => { setActiveDrilldown('lowstock'); }} style={{ position: 'relative', zIndex: 1, cursor: 'pointer' }}>
                        <div className="db-stock-icon db-stock-icon-warn"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg></div>
                        <div className="db-stock-info">
                          <div className="db-stock-num" style={{ color: lowStockCount > 0 ? '#EF4444' : '#059669' }}>{lowStockCount}</div>
                          <div className="db-stock-label">{t.lowStock}</div>
                        </div>
                      </div>
                    </div>
                    </div>
                  </div>
                  )}

                  {/* TOP PRODUCTS -hidden for service_only */}
                  {businessType !== 'service_only' && (
                  <div className="db-card">
                    <h3 className="db-card-title">{t.topSellingProducts || 'Top Selling Products'}</h3>
                    {(dbTopProducts || []).length === 0 ? (
                      <div className="db-empty-state">{t.noSalesRecordedYet || 'No sales recorded yet'}</div>
                    ) : (dbTopProducts || []).map((p, i) => (
                      <div key={p.name} className="db-top-item">
                        <span className="db-top-rank">{i + 1}</span>
                        <div className="db-top-info">
                          <span className="db-top-name">{p.name}</span>
                          <span className="db-top-sold">{p.sold} {t.soldLabel || 'sold'}</span>
                        </div>
                        <span className="db-top-rev">{fmt(p.revenue)}</span>
                      </div>
                    ))}
                  </div>
                  )}

                  {/* REPORTS -mobile only (hidden on desktop, use Reports tab instead).
                      Hidden entirely when Reports tab is privacy-hidden. Individual section
                      buttons are also gated by their corresponding item toggles. */}
                  {!isTabHidden('reports') && (() => {
                    // Which of the 4 mobile Report section buttons should show
                    // -mirrors the desktop Reports tab item hides.
                    const showPrimary  = true; // Primary group has no item-level toggle
                    const showDebts    = true; // Debts have no item-level toggle
                    const showCashflow = !isItemHidden('reports', 'cashflowReports');
                    // Quick section contains P&L, WHT/VAT, Stock Valuation -hide the whole
                    // button only if ALL of those items are individually hidden.
                    const showQuick = !isItemHidden('reports', 'plReport')
                                   || !isItemHidden('reports', 'whtVat')
                                   || !isItemHidden('reports', 'stockValuation');
                    // If every category is hidden, don't render the outer card at all.
                    if (!(showPrimary || showDebts || showCashflow || showQuick)) return null;
                    return (
                    <div className="db-card db-quick-reports-mobile" style={{ paddingBottom: 4 }}>
                      {(() => {
                        const sectionBtnStyle = (accent) => ({
                          display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', borderRadius: 14, background: 'var(--bg-white)', border: '1px solid var(--border-light)', borderLeft: `4px solid ${accent}`, color: 'var(--text-primary)', fontWeight: 700, cursor: 'pointer', textAlign: 'left', width: '100%', boxSizing: 'border-box', boxShadow: '0 1px 3px rgba(15,23,42,0.04)', marginBottom: 8
                        });
                        const chevronRight = <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2.5" strokeLinecap="round" style={{ marginLeft: 'auto', flexShrink: 0 }}><polyline points="9 18 15 12 9 6"/></svg>;
                        const sectionMeta = {
                          primary:  { label: language === 'sw' ? 'Ripoti za Msingi' : 'Primary Reports',  desc: language === 'sw' ? 'Mauzo, Manunuzi, Matumizi, Stock' : 'Sales · Purchases · Expenses · Stock', accent: '#16A34A' },
                          debts:    { label: language === 'sw' ? 'Ripoti za Madeni'  : 'Debts Reports',    desc: language === 'sw' ? 'Wadaiwa na Wadeni' : 'Receivables · Payables', accent: '#F59E0B' },
                          cashflow: { label: language === 'sw' ? 'Mtiririko wa Fedha' : 'Cashflow Reports', desc: language === 'sw' ? 'Pesa, Benki, Simu, Jumla' : 'Cash · Bank · Mobile · Total', accent: '#0D9488' },
                          quick:    { label: language === 'sw' ? 'Ripoti za Haraka' : 'Quick Reports',     desc: language === 'sw' ? 'Taarifa za mteja, VAT, P&L na zaidi' : 'Customer · VAT · P&L & more', accent: '#A3E635' }
                        };
                        return (
                          <>
                            <h3 className="db-card-title" style={{ marginBottom: 12 }}>{language === 'sw' ? 'Ripoti' : 'Reports'}</h3>
                            {showPrimary && (
                            <button onClick={() => setMobileReportsSection('primary')} style={sectionBtnStyle(sectionMeta.primary.accent)}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{sectionMeta.primary.label}</div>
                                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{sectionMeta.primary.desc}</div>
                              </div>
                              {chevronRight}
                            </button>
                            )}
                            {showDebts && (
                            <button onClick={() => setMobileReportsSection('debts')} style={sectionBtnStyle(sectionMeta.debts.accent)}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{sectionMeta.debts.label}</div>
                                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{sectionMeta.debts.desc}</div>
                              </div>
                              {chevronRight}
                            </button>
                            )}
                            {showCashflow && (
                            <button onClick={() => setMobileReportsSection('cashflow')} style={sectionBtnStyle(sectionMeta.cashflow.accent)}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{sectionMeta.cashflow.label}</div>
                                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{sectionMeta.cashflow.desc}</div>
                              </div>
                              {chevronRight}
                            </button>
                            )}
                            {showQuick && (
                            <button onClick={() => setMobileReportsSection('quick')} style={sectionBtnStyle(sectionMeta.quick.accent)}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{sectionMeta.quick.label}</div>
                                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{sectionMeta.quick.desc}</div>
                              </div>
                              {chevronRight}
                            </button>
                            )}
                          </>
                        );
                      })()}
                    </div>
                    );
                  })()}

                  {/* RECENT ACTIVITY */}
                  <div className="db-card">
                    <h3 className="db-card-title">{t.recentActivity}</h3>
                    {recentItems.length === 0 ? (
                      <div className="db-empty-state">{t.noActivityRecordedYet || 'No activity recorded yet'}</div>
                    ) : recentItems.map(item => {
                      const cfg = typeConfig[item.type];
                      const isVoid = !!item.voided;
                      return (
                        <div key={item.id} className="db-activity-row" style={isVoid ? { opacity: 0.5, borderLeft: '3px solid #9CA3AF', paddingLeft: 8 } : undefined}>
                          <span className="db-activity-badge" style={{ background: isVoid ? '#F3F4F6' : cfg.bg, color: isVoid ? '#6B7280' : cfg.color }}>{cfg.label}</span>
                          <div className="db-activity-info">
                            <span className="db-activity-title" style={isVoid ? { textDecoration: 'line-through' } : undefined}>
                              {item.title}
                              {item.extra > 0 && (
                                <span style={{ fontSize: 10, fontWeight: 700, background: '#E0E7FF', color: '#3730A3', borderRadius: 4, padding: '2px 6px', marginLeft: 6, verticalAlign: 'middle' }}>+{item.extra}</span>
                              )}
                              {isVoid && (
                                <span style={{ fontSize: 9, fontWeight: 800, background: '#9CA3AF', color: '#fff', borderRadius: 4, padding: '2px 6px', marginLeft: 6, verticalAlign: 'middle', letterSpacing: 0.3, textDecoration: 'none', display: 'inline-block' }}>
                                  {language === 'sw' ? 'IMEBATILISHWA' : 'VOIDED'}
                                </span>
                              )}
                            </span>
                            <span className="db-activity-date">{item.date}</span>
                          </div>
                          <span className="db-activity-amount" style={{ color: isVoid ? '#6B7280' : cfg.color, textDecoration: isVoid ? 'line-through' : 'none' }}>{cfg.sign}{fmt(item.amount)}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* MOBILE REPORTS -FULL-SCREEN SECTION OVERLAY (drilled from dashboard) */}
        {mobileReportsSection && (() => {
          const sec = mobileReportsSection;
          const sectionTitle = {
            primary:  language === 'sw' ? 'Ripoti za Msingi' : 'Primary Reports',
            debts:    language === 'sw' ? 'Ripoti za Madeni'  : 'Debts Reports',
            cashflow: language === 'sw' ? 'Ripoti za Mtiririko wa Ki-Fedha' : 'Cashflow Reports',
            quick:    language === 'sw' ? 'Ripoti za Haraka' : 'Quick Reports'
          }[sec];
          // Each card: { onClick, bg, border, iconBg, stroke, title, sub, icon, hidden? }
          const cards = [];
          if (sec === 'primary') {
            if (businessType !== 'service_only') cards.push({
              onClick: () => { setMobileReportsSection(null); setActiveTab('sales'); setIsSalesReportOpen(true); },
              bg: '#F0FDF4', border: '#BBF7D0', iconBg: '#DCFCE7', stroke: '#166534', subColor: '#16A34A',
              title: language === 'sw' ? 'Ripoti ya Mauzo' : 'Sales Report',
              sub: language === 'sw' ? 'Takwimu za mauzo' : 'Sales performance & data',
              icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#166534" strokeWidth="2" strokeLinecap="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>
            });
            if (businessType !== 'products_only') cards.push({
              onClick: () => { setMobileReportsSection(null); setActiveTab('services'); setShowServiceReports(true); },
              bg: '#ECFDF5', border: '#A5F3FC', iconBg: '#CFFAFE', stroke: '#059669', subColor: '#0891B2',
              title: language === 'sw' ? 'Ripoti ya Huduma' : 'Services Report',
              sub: language === 'sw' ? 'Takwimu za huduma' : 'Services performance & data',
              icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
            });
            if (businessType !== 'service_only') cards.push({
              onClick: () => { setMobileReportsSection(null); setActiveTab('purchases'); setIsPurchasesReportOpen(true); },
              bg: '#FFF7ED', border: '#FED7AA', iconBg: '#FFEDD5', stroke: '#92400E', subColor: '#B45309',
              title: language === 'sw' ? 'Ripoti ya Manunuzi' : 'Purchases Report',
              sub: language === 'sw' ? 'Takwimu za manunuzi' : 'Purchases analysis & data',
              icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#92400E" strokeWidth="2" strokeLinecap="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>
            });
            cards.push({
              onClick: () => { setMobileReportsSection(null); setActiveTab('expenses'); setIsExpensesReportOpen(true); },
              bg: '#FFF1F2', border: '#FECDD3', iconBg: '#FFE4E6', stroke: '#BE123C', subColor: '#E11D48',
              title: language === 'sw' ? 'Ripoti ya Matumizi' : 'Expenses Report',
              sub: language === 'sw' ? 'Takwimu za matumizi' : 'Expenses tracking & data',
              icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#BE123C" strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
            });
            if (businessType !== 'service_only') cards.push({
              onClick: () => { setMobileReportsSection(null); setShowStockReport(true); },
              bg: '#F5F3FF', border: '#C7D2FE', iconBg: '#E0E7FF', stroke: '#3730A3', subColor: '#4F46E5',
              title: language === 'sw' ? 'Muhtasari wa Thamani ya Bidhaa' : 'Inventory Valuation Summary',
              sub: language === 'sw' ? 'Maelezo ya bidhaa za stock' : 'Current inventory details',
              icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3730A3" strokeWidth="2" strokeLinecap="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="9" y1="9" x2="9" y2="15"/><line x1="15" y1="9" x2="15" y2="15"/><line x1="9" y1="15" x2="15" y2="15"/><line x1="9" y1="9" x2="15" y2="9"/></svg>
            });
          } else if (sec === 'debts') {
            cards.push({
              onClick: () => { setMobileReportsSection(null); setDrilldownFromTab('reports'); setActiveTab('dashboard'); setActiveDrilldown('receivables'); setDrilldownPeriod(dashboardTimeFilter); },
              bg: '#FEFCE8', border: '#FDE68A', iconBg: '#FEF3C7', stroke: '#854D0E', subColor: '#A16207',
              title: language === 'sw' ? 'Ripoti ya Wateja Wanaodaiwa' : 'Accounts Receivable',
              sub: (language === 'sw' ? 'Wateja wanaokudai pesa' : 'Customers who owe you') + ' · ' + fmt(totalReceivables),
              icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#854D0E" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="6" x2="12" y2="12"/><line x1="12" y1="12" x2="16" y2="14"/></svg>
            });
            cards.push({
              onClick: () => { setMobileReportsSection(null); setDrilldownFromTab('reports'); setActiveTab('dashboard'); setActiveDrilldown('payables'); setDrilldownPeriod(dashboardTimeFilter); },
              bg: '#FFF1F2', border: '#FECDD3', iconBg: '#FFE4E6', stroke: '#BE123C', subColor: '#E11D48',
              title: language === 'sw' ? 'Ripoti ya Wauzaji Unaodai' : 'Accounts Payable',
              sub: (language === 'sw' ? 'Wauzaji unaowadai pesa' : 'Suppliers you owe') + ' · ' + fmt(totalPayables),
              icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#BE123C" strokeWidth="2" strokeLinecap="round"><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/><line x1="12" y1="22" x2="12" y2="12"/></svg>
            });
          } else if (sec === 'cashflow') {
            cards.push({
              onClick: () => { setMobileReportsSection(null); setDrilldownFromTab('reports'); setActiveTab('dashboard'); setActiveDrilldown('cash'); setDrilldownPeriod('year'); setSelectedBankAccount('all'); setSelectedMobileMoneyAccount('all'); },
              bg: '#F0FDF4', border: '#BBF7D0', iconBg: '#DCFCE7', stroke: '#166534', subColor: '#16A34A',
              title: language === 'sw' ? 'Ripoti ya Akaunti ya Pesa Taslimu' : 'Cash Account(s) Report',
              sub: language === 'sw' ? 'Maelezo ya pesa taslimu' : 'Cash transactions & details',
              icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#166534" strokeWidth="2" strokeLinecap="round"><rect x="2" y="6" width="20" height="12" rx="2"/><circle cx="12" cy="12" r="2"/><path d="M6 12h.01M18 12h.01"/></svg>
            });
            cards.push({
              onClick: () => { setMobileReportsSection(null); setDrilldownFromTab('reports'); setActiveTab('dashboard'); setActiveDrilldown('bank'); setDrilldownPeriod('year'); setSelectedBankAccount('all'); },
              bg: '#F1F5F9', border: '#BFDBFE', iconBg: '#E2E8F0', stroke: '#334155', subColor: '#1E3A8A',
              title: language === 'sw' ? 'Ripoti ya Akaunti za Benki' : 'Bank Account(s) Report',
              sub: language === 'sw' ? 'Maelezo ya benki' : 'Bank transactions & details',
              icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#334155" strokeWidth="2" strokeLinecap="round"><line x1="3" y1="22" x2="21" y2="22"/><line x1="6" y1="18" x2="6" y2="11"/><line x1="10" y1="18" x2="10" y2="11"/><line x1="14" y1="18" x2="14" y2="11"/><line x1="18" y1="18" x2="18" y2="11"/><polygon points="12 2 20 7 4 7"/></svg>
            });
            cards.push({
              onClick: () => { setMobileReportsSection(null); setDrilldownFromTab('reports'); setActiveTab('dashboard'); setActiveDrilldown('mobilemoney'); setDrilldownPeriod('year'); setSelectedMobileMoneyAccount('all'); },
              bg: '#F5F3FF', border: '#C7D2FE', iconBg: '#E0E7FF', stroke: '#6B21B6', subColor: '#4F46E5',
              title: language === 'sw' ? 'Ripoti ya Akaunti za Simu/Wakala' : 'Mobile Money Account(s) Report',
              sub: language === 'sw' ? 'Maelezo ya simu/wakala' : 'Mobile money transactions & details',
              icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6B21B6" strokeWidth="2" strokeLinecap="round"><rect x="5" y="2" width="14" height="20" rx="2" ry="2"/><line x1="12" y1="18" x2="12.01" y2="18"/></svg>
            });
            cards.push({
              onClick: () => { setMobileReportsSection(null); setDrilldownFromTab('reports'); setActiveTab('dashboard'); setActiveDrilldown('total'); setDrilldownPeriod('year'); },
              bg: '#F0FDF4', border: '#A7F3D0', iconBg: '#CCFBF1', stroke: '#0D9488', subColor: '#059669',
              title: language === 'sw' ? 'Ripoti ya Mtiririko wa Ki-Fedha' : 'Total Cashflow Report',
              sub: language === 'sw' ? 'Muhtasari wa jumla ya mtiririko' : 'Overall cashflow summary',
              icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0D9488" strokeWidth="2" strokeLinecap="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>
            });
          } else if (sec === 'quick') {
            cards.push({
              onClick: () => { setMobileReportsSection(null); setShowCustomerStatement(true); },
              bg: '#F1F5F9', border: '#BFDBFE', iconBg: '#E2E8F0', stroke: '#334155', subColor: '#475569',
              title: language === 'sw' ? 'Taarifa ya Mteja' : 'Customer Statement',
              sub: language === 'sw' ? 'Deni la kila mteja' : 'What each customer owes',
              icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#334155" strokeWidth="2" strokeLinecap="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
            });
            cards.push({
              onClick: () => { setMobileReportsSection(null); setShowAgingReport(true); },
              bg: '#FEF9C3', border: '#FDE68A', iconBg: '#FEF3C7', stroke: '#92400E', subColor: '#B45309',
              title: language === 'sw' ? 'Ripoti ya Umri wa Deni' : 'Aging Report',
              sub: language === 'sw' ? 'Madeni yaliyopita muda' : 'Overdue receivables breakdown',
              icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#92400E" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            });
            cards.push({
              onClick: () => { setMobileReportsSection(null); setShowDailySummary(true); },
              bg: '#F0FDF4', border: '#BBF7D0', iconBg: '#DCFCE7', stroke: '#166534', subColor: '#16A34A',
              title: language === 'sw' ? 'Muhtasari wa Siku' : 'Daily Summary',
              sub: language === 'sw' ? 'Shughuli za leo' : "Today's transactions",
              icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#166534" strokeWidth="2" strokeLinecap="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            });
            cards.push({
              onClick: () => { setMobileReportsSection(null); setShowPandL(true); },
              bg: '#F0FDF4', border: '#BBF7D0', iconBg: '#DCFCE7', stroke: '#166534', subColor: '#16A34A',
              title: language === 'sw' ? 'Faida na Hasara' : 'Profit & Loss',
              sub: language === 'sw' ? 'Mapato dhidi ya gharama' : 'Revenue vs costs overview',
              icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#166534" strokeWidth="2" strokeLinecap="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>
            });
            cards.push({
              onClick: () => { setMobileReportsSection(null); setShowWHTReport(true); },
              bg: '#FFF7ED', border: '#FED7AA', iconBg: '#FFEDD5', stroke: '#92400E', subColor: '#B45309',
              title: language === 'sw' ? 'Ripoti ya WHT' : 'WHT Report',
              sub: language === 'sw' ? 'Kodi ya kuzuia' : 'Withholding tax collected',
              icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#92400E" strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/></svg>
            });
            if (vatRegistered) cards.push({
              onClick: () => { setMobileReportsSection(null); setShowVATReport(true); },
              bg: '#F0FDF4', border: '#BBF7D0', iconBg: '#DCFCE7', stroke: '#166534', subColor: '#16A34A',
              title: language === 'sw' ? 'Ripoti ya VAT' : 'VAT Report',
              sub: language === 'sw' ? 'Kodi ya ongezeko la thamani' : 'Value added tax summary',
              icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#166534" strokeWidth="2" strokeLinecap="round"><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>
            });
            // Stock Valuation duplicate removed -the primary "Inventory
            // Valuation Summary" card already opens this report.
            if (businessType !== 'service_only') cards.push({
              onClick: () => { setMobileReportsSection(null); setDrilldownFromTab('reports'); setShowReturnsReport(true); },
              bg: '#FEF2F2', border: '#FECACA', iconBg: '#FEE2E2', stroke: '#DC2626', subColor: '#EF4444',
              title: language === 'sw' ? 'Ripoti ya Mauzo Yaliyorudishwa' : 'Returns Report',
              sub: language === 'sw' ? 'Bidhaa zilizorudishwa' : 'Sales returns breakdown',
              icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2" strokeLinecap="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.51"/></svg>
            });
            if (businessType !== 'service_only') cards.push({
              onClick: () => { setMobileReportsSection(null); setDrilldownFromTab('reports'); setShowPurchaseReturnsReport(true); },
              bg: '#FEF2F2', border: '#FECACA', iconBg: '#FEE2E2', stroke: '#DC2626', subColor: '#EF4444',
              title: language === 'sw' ? 'Manunuzi Yaliyorudishwa' : 'Purchase Returns',
              sub: language === 'sw' ? 'Manunuzi yaliyorudishwa' : 'Purchase returns breakdown',
              icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2" strokeLinecap="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.51"/></svg>
            });
          }
          return (
            <div style={{ position: 'fixed', inset: 0, background: '#F8FAFC', zIndex: 9000, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
              {/* Header */}
              <div style={{ position: 'sticky', top: 0, background: 'var(--bg-white)', borderBottom: '1px solid #E2E8F0', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12, zIndex: 1 }}>
                <button onClick={() => setMobileReportsSection(null)}
                  style={{ background: 'none', border: 'none', padding: 4, cursor: 'pointer', display: 'flex', alignItems: 'center' }} aria-label="Back">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--text-primary)" strokeWidth="2.5" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
                </button>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h2 style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>{sectionTitle}</h2>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{language === 'sw' ? 'Chagua ripoti' : 'Select a report to view'}</div>
                </div>
              </div>
              {/* Cards */}
              <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                {cards.map((c, i) => (
                  <button key={i} onClick={c.onClick}
                    style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 18px', borderRadius: 14, background: c.bg, border: `1px solid ${c.border}`, cursor: 'pointer', textAlign: 'left', width: '100%', boxSizing: 'border-box' }}>
                    <div style={{ width: 40, height: 40, borderRadius: 10, background: c.iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {c.icon}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: c.stroke }}>{c.title}</div>
                      <div style={{ fontSize: 12, color: c.subColor, marginTop: 2 }}>{c.sub}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          );
        })()}

        {activeTab === 'sales' && (
          <div className="sales mauzo-screen">
            {!isDataReady ? (
              <div role="status" aria-label={t.loading || 'Loading'} style={{ padding: 16 }}>
                <div className="skeleton skeleton-title"></div>
                <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
                  {[1,2,3].map(i => <div key={i} className="skeleton skeleton-btn" style={{ flex: 1, width: 'auto' }}></div>)}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
                  <div className="skeleton skeleton-summary-card"></div>
                  <div className="skeleton skeleton-summary-card"></div>
                </div>
                <div className="skeleton skeleton-chart"></div>
                <div className="skeleton skeleton-table-header"></div>
                {[1,2,3,4,5].map(i => <div key={i} className="skeleton skeleton-row"></div>)}
              </div>
            ) : null}
            {isDataReady && (() => {
              // Sales tab single-color palette -emerald family. Subtle lightness variation
              // gives bars visual texture without breaking the calm one-color discipline.
              const MZ_COLORS = Array.from({length: 200}, (_, i) => {
                const shades = ['#10B981', '#059669', '#34D399', '#047857', '#6EE7B7'];
                return shades[i % shades.length];
              });

              // Period-aware filtering via the ONE shared helper (H5).
              // TWO ARRAYS, ONE SOURCE -the display/accounting split.
              // _periodSalesAll is the full period slice (voided included).
              // filteredSales is the ACCOUNTING array: void is ALWAYS excluded,
              //   so every total/KPI/chart below is unchanged by the toggle.
              // displaySales is the HISTORY array: void is shown unless the user
              //   ticks "Hide voided", so a voided sale is never invisible.
              const _periodSalesAll = filterByPeriod(sales, salesTimeFilter, salesCustomFrom, salesCustomTo);
              const filteredSales = _periodSalesAll.filter(s => s.status !== 'void');
              const displaySales = _periodSalesAll.filter(s => !hideVoidedSales || s.status !== 'void');

              // NET of returns -grand totals + returns of the listed sales,
              // identical to the Sales Report.
              const _salesTabIds = new Set(filteredSales.map(s => String(s.id)));
              const _salesTabReturnsAmt = (salesReturns || []).filter(r => r.status !== 'void' && _salesTabIds.has(String(r.originalSaleId))).reduce((s, r) => s + parseFloat(r.refundAmt || r.totalRefund || 0), 0);
              const totalRevenue = filteredSales.reduce((s, x) => s + getSaleGrandTotal(x), 0) - _salesTabReturnsAmt;
              const totalUnits = filteredSales.reduce((s, x) => {
                if (Array.isArray(x.items) && x.items.length > 0) {
                  return s + x.items.reduce((qs, it) => qs + parseInt(it.quantity || 0), 0);
                }
                return s + parseInt(x.quantity || 0);
              }, 0);

              // Handles BOTH single-product sales (legacy: productName+quantity at top level)
              // AND multi-item sales (items[] array -one sale order, many products)
              const byProductMap = {};
              filteredSales.forEach(s => {
                if (Array.isArray(s.items) && s.items.length > 0) {
                  // Multi-item sale -count each line individually and apportion revenue
                  const orderTotal = parseFloat(s.totalRevenue || 0);
                  const itemSubtotal = s.items.reduce((sum, it) => sum + (parseFloat(it.quantity || 0) * parseFloat(it.sellingPrice || it.unitPrice || 0)), 0);
                  s.items.forEach(it => {
                    const name = it.productName || it.name;
                    if (!name) return;
                    if (!byProductMap[name]) byProductMap[name] = { name, revenue: 0, units: 0 };
                    const qty = parseFloat(it.quantity || 0);
                    const itemPrice = parseFloat(it.sellingPrice || it.unitPrice || 0);
                    const itemRev = itemSubtotal > 0 ? (qty * itemPrice / itemSubtotal) * orderTotal : (qty * itemPrice);
                    byProductMap[name].revenue += itemRev;
                    byProductMap[name].units += qty;
                  });
                } else if (s.productName) {
                  // Single-product legacy sale
                  if (!byProductMap[s.productName]) byProductMap[s.productName] = { name: s.productName, revenue: 0, units: 0 };
                  byProductMap[s.productName].revenue += parseFloat(s.totalRevenue || 0);
                  byProductMap[s.productName].units += parseInt(s.quantity || 0);
                }
              });
              const salesByProduct = Object.values(byProductMap).sort((a, b) => b.revenue - a.revenue);
              const maxBarRevenue = salesByProduct.length > 0 ? salesByProduct[0].revenue : 1;
              const topSelling = salesByProduct.slice(0, 5).map((item, i) => ({
                ...item, color: MZ_COLORS[i % MZ_COLORS.length],
                pct: totalRevenue > 0 ? Math.round(item.revenue / totalRevenue * 100) : 0
              }));
              const topSellingNames = new Set(topSelling.map(t => t.name));
              // Low selling: all inventory products sorted by units sold asc, excluding top sellers, max 5
              const lowSelling = (products || [])
                .map(p => ({
                  name: p.productName,
                  revenue: byProductMap[p.productName]?.revenue || 0,
                  units: byProductMap[p.productName]?.units || 0
                }))
                .filter(item => !topSellingNames.has(item.name))
                .sort((a, b) => a.units - b.units || a.revenue - b.revenue)
                .slice(0, 5);
              // Recent sales filtered by selected period. HISTORY list -uses the
              // display array so a voided sale stays visible (marked VOIDED).
              const recentSalesList = [...displaySales].sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0)).slice(0, 5);

              // ── Annual section data (filtered by salesAnnualYear) ─────────
              const annualFilteredSales = sales.filter(s => {
                const d = new Date(s.date); return !isNaN(d) && d.getFullYear() === salesAnnualYear && s.status !== 'void';
              });
              const annualByProductMap = {};
              annualFilteredSales.forEach(s => {
                if (!annualByProductMap[s.productName]) annualByProductMap[s.productName] = { name: s.productName, revenue: 0, units: 0 };
                annualByProductMap[s.productName].revenue += parseFloat(s.totalRevenue || 0);
                annualByProductMap[s.productName].units += parseInt(s.quantity || 0);
              });
              const annualRevTotal = annualFilteredSales.reduce((s, x) => s + getSaleGrandTotal(x), 0);
              const annualSalesByProduct = Object.values(annualByProductMap).sort((a, b) => b.revenue - a.revenue);
              const annualTopSelling = annualSalesByProduct.slice(0, 5).map((item, i) => ({
                ...item, color: MZ_COLORS[i % MZ_COLORS.length],
                pct: annualRevTotal > 0 ? Math.round(item.revenue / annualRevTotal * 100) : 0
              }));
              const annualTopNames = new Set(annualTopSelling.map(x => x.name));
              const annualLowSelling = (products || [])
                .map(p => ({ name: p.productName, revenue: annualByProductMap[p.productName]?.revenue || 0, units: annualByProductMap[p.productName]?.units || 0 }))
                .filter(item => !annualTopNames.has(item.name))
                .sort((a, b) => a.units - b.units || a.revenue - b.revenue)
                .slice(0, 5);
              // All products performance -descending by annual revenue (include sold items not in master list)
              const _productNames = new Set((products || []).map(p => p.productName));
              const allProductsPerf = [
                ...(products || []).map(p => ({ name: p.productName, revenue: annualByProductMap[p.productName]?.revenue || 0, units: annualByProductMap[p.productName]?.units || 0 })),
                ...Object.values(annualByProductMap).filter(item => !_productNames.has(item.name))
              ].sort((a, b) => b.revenue - a.revenue);
              const maxPerfRevenue = allProductsPerf.length > 0 ? (allProductsPerf[0].revenue || 1) : 1;
              // Bottom 5 by revenue (lowest earning, excluding top 5)
              const annualLowByRevenue = allProductsPerf.slice().reverse().slice(0, 5);

              // Period display label
              const _now = new Date();
              const _pad = n => String(n).padStart(2, '0');
              const _fmt = d => `${_pad(d.getDate())}/${_pad(d.getMonth()+1)}/${d.getFullYear()}`;
              let periodLabel = '';
              if (salesTimeFilter === 'today') {
                periodLabel = _fmt(_now);
              } else if (salesTimeFilter === 'week') {
                const ws = new Date(_now); ws.setDate(_now.getDate() - 7);
                periodLabel = `${_fmt(ws)} \u2013 ${_fmt(_now)}`;
              } else if (salesTimeFilter === 'month') {
                const _slMn = (t.monthNames || ['January','February','March','April','May','June','July','August','September','October','November','December'])[_now.getMonth()];
                periodLabel = `${_slMn} ${_now.getFullYear()}`;
              } else if (salesTimeFilter === 'year') {
                periodLabel = String(_now.getFullYear());
              } else if (salesTimeFilter === 'custom' && salesCustomFrom && salesCustomTo) {
                periodLabel = `${salesCustomFrom} \u2013 ${salesCustomTo}`;
              }

              // Annual performance (monthly breakdown for selected year)
              const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
              const annualData = MONTHS_SHORT.map((label, m) => ({
                label,
                rev: sales.filter(s => { const d = new Date(s.date); return d.getFullYear() === salesAnnualYear && d.getMonth() === m && s.status !== 'void'; })
                         .reduce((sum, x) => sum + parseFloat(x.totalRevenue || 0), 0)
              }));
              const annualTotal = annualData.reduce((s, d) => s + d.rev, 0);
              const maxAnnualRev = Math.max(...annualData.map(d => d.rev), 1);

              // Year selector options
              const rawYears = [...new Set(sales.map(s => new Date(s.date).getFullYear()).filter(y => !isNaN(y)))].sort((a, b) => b - a);
              const _sNow = new Date().getFullYear();
              const _sFuture = Array.from({ length: 5 }, (_, i) => _sNow + i + 1);
              const availableYears = [...new Set([..._sFuture, salesAnnualYear, ...rawYears])].sort((a, b) => b - a);

              return (
                <>
                  {/* SELL MODAL (POS) */}
                  {showSalesForm && (
                    <div className="popup-overlay">
                      <div className="popup-form mz-modal" style={{ maxWidth: 420, width: '100%', borderRadius: 16, padding: 24 }}>
                        <h3 className="mz-modal-title" style={{ marginBottom: 14, fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>{t.newSale}</h3>
                        {(() => {
                          const isService = saleItemKind === 'service';
                          const noProducts = (products || []).length === 0;
                          const noServices = (services || []).length === 0;
                          const blockedProducts = !isService && noProducts;
                          const blockedServices = isService && noServices;
                          if (blockedProducts) return <div style={{ textAlign: 'center', padding: '16px 0', color: 'var(--text-muted)' }}>{t.pleaseRegisterProducts}</div>;
                          // removed blocked-services gate -allow recording even before setup
                          return (
                          <>
                            {/* ── CART: added items (inline-editable qty + price) ── */}
                              {saleLineItems.length > 0 && (
                                <div style={{ marginBottom: 12, borderRadius: 12, overflow: 'hidden', border: '1px solid var(--border-color)' }}>
                                  {/* Header row */}
                                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 60px 18px 90px 90px 28px', gap: 6, padding: '6px 12px', background: '#F3F4F6', fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4, alignItems: 'center' }}>
                                    <div>{language === 'sw' ? 'Bidhaa' : 'Item'}</div>
                                    <div style={{ textAlign: 'right' }}>{language === 'sw' ? 'Idadi' : 'Qty'}</div>
                                    <div />
                                    <div style={{ textAlign: 'right' }}>{language === 'sw' ? 'Bei' : 'Price'}</div>
                                    <div style={{ textAlign: 'right' }}>{language === 'sw' ? 'Jumla' : 'Total'}</div>
                                    <div />
                                  </div>
                                  {saleLineItems.map((item, idx) => {
                                    const isService = item.type === 'service';
                                    const qty = isService ? 1 : parseFloat(item.quantity || 0);
                                    const price = parseFloat(item.sellingPrice || 0);
                                    const lineTotal = qty * price;
                                    return (
                                      <div key={item.id} style={{ display: 'grid', gridTemplateColumns: '1fr 60px 18px 90px 90px 28px', gap: 6, padding: '8px 12px', background: idx % 2 === 0 ? 'var(--bg-tab)' : 'var(--bg-card)', borderTop: '1px solid var(--border-color)', alignItems: 'center' }}>
                                        <div style={{ minWidth: 0 }}>
                                          <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.productName}</div>
                                          {isService && <div style={{ fontSize: 10, color: 'var(--text-secondary)' }}>{language === 'sw' ? 'huduma' : 'service'}</div>}
                                        </div>
                                        {isService ? (
                                          <div style={{ textAlign: 'right', fontSize: 13, color: 'var(--text-secondary)' }}>1</div>
                                        ) : (
                                          <input type="number" min="0" step="any" value={item.quantity || ''}
                                            onChange={e => {
                                              const v = e.target.value;
                                              setSaleLineItems(prev => prev.map(i => i.id === item.id ? { ...i, quantity: v } : i));
                                            }}
                                            style={{ width: '100%', padding: '5px 6px', borderRadius: 6, border: '1px solid var(--border-color)', background: 'var(--bg-white)', fontSize: 12, textAlign: 'right', outline: 'none', boxSizing: 'border-box' }} />
                                        )}
                                        <div style={{ textAlign: 'center', fontSize: 11, color: 'var(--text-secondary)' }}>×</div>
                                        <input type="number" min="0" step="any" value={item.sellingPrice || ''}
                                          onChange={e => {
                                            const v = e.target.value;
                                            setSaleLineItems(prev => prev.map(i => i.id === item.id ? { ...i, sellingPrice: v } : i));
                                          }}
                                          style={{ width: '100%', padding: '5px 6px', borderRadius: 6, border: '1px solid var(--border-color)', background: 'var(--bg-white)', fontSize: 12, textAlign: 'right', outline: 'none', boxSizing: 'border-box' }} />
                                        <div style={{ textAlign: 'right', fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{fmt(lineTotal)}</div>
                                        <button onClick={() => setSaleLineItems(prev => prev.filter(i => i.id !== item.id))}
                                          style={{ width: 24, height: 24, borderRadius: 6, background: '#FEE2E2', color: '#DC2626', border: 'none', fontWeight: 700, fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                                      </div>
                                    );
                                  })}
                                  <div style={{ padding: '8px 12px', background: '#F0FDF4', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid var(--border-color)' }}>
                                    <span style={{ fontSize: 12, fontWeight: 600, color: '#065F46' }}>{language === 'sw' ? 'Jumla ya bidhaa' : 'Items subtotal'}</span>
                                    <span style={{ fontSize: 14, fontWeight: 800, color: '#059669' }}>
                                      {fmt(saleLineItems.reduce((s, item) => s + (item.type === 'service' ? 1 : parseFloat(item.quantity || 0)) * parseFloat(item.sellingPrice || 0), 0))}
                                    </span>
                                  </div>
                                </div>
                              )}

                              {/* ── ADD ITEM ROW ── */}
                              <div style={{ background: 'var(--bg-tab)', borderRadius: 12, padding: 12, marginBottom: 16 }}>
                                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                  {language === 'sw' ? 'Ongeza bidhaa' : 'Add item'}
                                </div>
                                {/* Item select */}
                                <select value={saleLineDraft.productName} onChange={e => {
                                  const v = e.target.value;
                                  const prod = (products || []).find(p => p.productName === v);
                                  const wacCost = getProductWAC(v, salesFormData.date || new Date().toISOString().split('T')[0]);
                                  // C2/H6: capture the STABLE productId so this line tracks the
                                  // product through renames; name stays for display only.
                                  setSaleLineDraft(p => ({ ...p, productName: v, productId: prod ? prod.id : undefined, cost: wacCost > 0 ? wacCost.toFixed(2) : (prod?.costPrice ? String(prod.costPrice) : ''), sellingPrice: '' }));
                                }} style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: 'none', background: 'var(--bg-card)', fontSize: 14, boxSizing: 'border-box', outline: 'none', marginBottom: 8 }}>
                                  <option value="">{t.selectProduct}</option>
                                  {notArchived(products).map(p => <option key={p.id} value={p.productName}>{p.productName} (stock: {p.balance ?? p.openingStockQty ?? 0})</option>)}
                                </select>
                                {/* Stock warning */}
                                {saleLineDraft.productName && (() => {
                                  const _p = (products || []).find(p => p.productName === saleLineDraft.productName);
                                  const _s = _p ? (_p.balance ?? _p.currentStock ?? _p.openingStockQty ?? 0) : null;
                                  return _s !== null ? <div style={{ fontSize: 12, fontWeight: 600, color: _s === 0 ? '#DC2626' : '#16A34A', marginBottom: 6 }}>{_s === 0 ? 'Out of stock' : `Available: ${_s}`}</div> : null;
                                })()}
                                <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                                  <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>{t.quantity || 'Qty'}</div>
                                    <input type="number" inputMode="numeric" min="1" placeholder="0" value={saleLineDraft.quantity}
                                      onChange={e => setSaleLineDraft(p => ({ ...p, quantity: e.target.value }))}
                                      style={{ width: '100%', padding: '10px 8px', borderRadius: 10, border: 'none', background: 'var(--bg-card)', fontSize: 14, boxSizing: 'border-box', outline: 'none' }} />
                                  </div>
                                  <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>{t.sellingPrice || 'Selling Price'}</div>
                                    <input type="number" inputMode="decimal" min="0" placeholder="0" value={saleLineDraft.sellingPrice}
                                      onChange={e => setSaleLineDraft(p => ({ ...p, sellingPrice: e.target.value }))}
                                      style={{ width: '100%', padding: '10px 8px', borderRadius: 10, border: 'none', background: 'var(--bg-card)', fontSize: 14, boxSizing: 'border-box', outline: 'none' }} />
                                  </div>
                                </div>
                                {/* Auto-calc line total preview */}
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', background: '#F0FAF5', borderRadius: 8, marginBottom: 8 }}>
                                  <span style={{ fontSize: 12, fontWeight: 600, color: '#0F6E56' }}>{t.totalCost || 'Total'}</span>
                                  <span style={{ fontSize: 14, fontWeight: 800, color: '#0A3D2E' }}>
                                    {fmt((parseFloat(saleLineDraft.quantity) || 0) * (parseFloat(saleLineDraft.sellingPrice) || 0))}
                                  </span>
                                </div>
                                <button type="button" onClick={() => {
                                  const d = saleLineDraft;
                                  if (!d.productName || !d.sellingPrice || !d.quantity) return;
                                  // Block overselling as soon as the line is added (early feedback).
                                  // C2/H6: match by stable productId (lineMatchesProduct).
                                  if (d.type !== 'service') {
                                    const prod = (products || []).find(p => lineMatchesProduct(d, p));
                                    if (prod) {
                                      const alreadyInCart = (saleLineItems || []).filter(i => i.type !== 'service' && lineMatchesProduct(i, prod)).reduce((s, i) => s + parseFloat(i.quantity || 0), 0);
                                      const oldSale = editSaleId ? (sales || []).find(s => s.id === editSaleId) : null;
                                      const oldItems = oldSale ? (Array.isArray(oldSale.items) && oldSale.items.length > 0 ? oldSale.items : [{ productName: oldSale.productName, productId: oldSale.productId, quantity: oldSale.quantity, type: oldSale.isService ? 'service' : 'product' }]) : [];
                                      const oldQty = oldItems.filter(i => i.type !== 'service' && lineMatchesProduct(i, prod)).reduce((s, i) => s + parseFloat(i.quantity || 0), 0);
                                      const available = (prod.balance ?? prod.currentStock ?? prod.openingStockQty ?? 0) + oldQty;
                                      const requested = alreadyInCart + parseFloat(d.quantity || 0);
                                      if (requested > available + 0.0001) {
                                        const swS = language === 'sw';
                                        showAlert(swS ? `Hisa haitoshi kwa "${prod.productName}".\nInapatikana: ${available}\nUnataka: ${requested}\n\nPunguza idadi au ongeza hisa kwanza.` : `Not enough stock for "${prod.productName}".\nAvailable: ${available}\nRequested: ${requested}\n\nReduce the quantity or add stock first.`, { tone: 'error', title: swS ? 'Hisa haitoshi' : 'Insufficient stock' });
                                        return;
                                      }
                                    }
                                  }
                                  // Inject WAC as the cost at add-time, AS OF the sale's date
                                  // (a future-dated purchase must not inflate this sale's cost).
                                  const wac = wacForProduct(d.productName, salesFormData.date || new Date().toISOString().split('T')[0]);
                                  d.cost = wac > 0 ? wac.toFixed(2) : '0';
                                  setSaleLineItems(prev => [...prev, { ...d, id: Date.now() }]);
                                  setSaleLineDraft(p => ({ ...p, productName: '', quantity: '', sellingPrice: '', cost: '' }));
                                }} style={{ width: '100%', padding: '10px 0', borderRadius: 10, background: '#111827', color: '#A3E635', border: 'none', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
                                  + {language === 'sw' ? 'Ongeza bidhaa' : 'Add item'}
                                </button>
                              </div>

                            {/* Customer Details -New/Walk-in vs Registered (proforma template) */}
                            <div style={{ background: 'var(--bg-input)', borderRadius: 14, padding: '14px 14px 10px', marginBottom: 16 }}>
                              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                {t.customerDetails || 'Customer Details'}
                                {saleSaveMode === 'paid' && salesCustomerType === 'walkin' && <span style={{ fontWeight: 400, fontSize: 10, color: 'var(--text-muted)', marginLeft: 6, textTransform: 'none', letterSpacing: 0 }}>(optional for paid sales)</span>}
                              </div>
                              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                                {[
                                  { val: 'walkin', label: language === 'sw' ? 'Mpya / Wa Kupita' : 'New / Walk-in' },
                                  { val: 'registered', label: language === 'sw' ? 'Aliyesajiliwa' : 'Registered' },
                                ].map(opt => (
                                  <button key={opt.val} type="button"
                                    onClick={() => {
                                      setSalesCustomerType(opt.val);
                                      setSalesFormData(p => ({ ...p, customerName: '', customerPhone: '', customerEmail: '', customerAddress: '', customerTIN: '', customerVRN: '' }));
                                    }}
                                    style={{ flex: 1, padding: '9px 0', borderRadius: 10, border: 'none', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                                      background: salesCustomerType === opt.val ? '#334155' : '#fff',
                                      color: salesCustomerType === opt.val ? '#fff' : '#6B7280' }}>
                                    {opt.label}
                                  </button>
                                ))}
                              </div>
                              {salesCustomerType === 'registered' ? (
                                <div>
                                  <label style={{ display: 'block', marginBottom: 5, color: 'var(--text-primary)', fontWeight: 600, fontSize: 12 }}>{t.selectCustomer || 'Select customer'}</label>
                                  <select value={salesFormData.customerName || ''}
                                    onChange={e => {
                                      const selectedName = e.target.value;
                                      const cust = (customers || []).find(c => c.name === selectedName);
                                      setSalesFormData(p => ({
                                        ...p,
                                        customerName: selectedName,
                                        customerPhone: cust?.phone || '',
                                        customerEmail: cust?.email || '',
                                        customerAddress: cust?.address || '',
                                        customerTIN: cust?.tin || '',
                                        customerVRN: cust?.vrn || '',
                                      }));
                                    }}
                                    style={{ width: '100%', padding: 10, borderRadius: 10, border: 'none', background: 'var(--bg-white)', fontSize: 13, boxSizing: 'border-box', outline: 'none' }}>
                                    <option value="">{t.selectCustomer || 'Select customer...'}</option>
                                    {(customers || []).map(c => (
                                      <option key={c.id} value={c.name}>{c.name}{c.phone ? ` (${c.phone})` : ''}</option>
                                    ))}
                                  </select>
                                  {salesFormData.customerName && (
                                    <div style={{ marginTop: 10, padding: 10, background: 'rgba(255,255,255,0.6)', borderRadius: 10, fontSize: 12, color: 'var(--text-secondary)' }}>
                                      {salesFormData.customerPhone && <div>📞 {salesFormData.customerPhone}</div>}
                                      {salesFormData.customerEmail && <div>✉ {salesFormData.customerEmail}</div>}
                                      {salesFormData.customerAddress && <div>📍 {salesFormData.customerAddress}</div>}
                                      {salesFormData.customerTIN && <div>TIN: {salesFormData.customerTIN}</div>}
                                      {salesFormData.customerVRN && <div>VRN: {salesFormData.customerVRN}</div>}
                                      {!salesFormData.customerPhone && !salesFormData.customerEmail && !salesFormData.customerAddress && !salesFormData.customerTIN && !salesFormData.customerVRN && (
                                        <div style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>{language === 'sw' ? 'Hakuna maelezo ya ziada' : 'No additional details on file'}</div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <>
                                  <div style={{ marginBottom: 10 }}>
                                    <label style={{ display: 'block', marginBottom: 5, color: 'var(--text-primary)', fontWeight: 600, fontSize: 12 }}>{t.customerName}</label>
                                    <input type="text" name="customerName" value={salesFormData.customerName} onChange={handleSalesInputChange}
                                      placeholder={saleSaveMode === 'paid' ? 'Customer name (leave blank for Walk-in)' : (t.customerName || 'Customer name')}
                                      style={{ width: '100%', padding: 10, borderRadius: 10, border: 'none', background: 'var(--bg-white)', fontSize: 13, boxSizing: 'border-box', outline: 'none' }} />
                                  </div>
                                  <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
                                    <div style={{ flex: 1 }}>
                                      <label style={{ display: 'block', marginBottom: 5, color: 'var(--text-primary)', fontWeight: 600, fontSize: 12 }}>{t.phone} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
                                      <input type="tel" value={salesFormData.customerPhone || ''}
                                        onChange={e => setSalesFormData(p => ({ ...p, customerPhone: e.target.value }))}
                                        placeholder="+255..."
                                        style={{ width: '100%', padding: 10, borderRadius: 10, border: 'none', background: 'var(--bg-white)', fontSize: 13, boxSizing: 'border-box', outline: 'none' }} />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                      <label style={{ display: 'block', marginBottom: 5, color: 'var(--text-primary)', fontWeight: 600, fontSize: 12 }}>{language === 'sw' ? 'Barua pepe' : 'Email'} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
                                      <input type="email" value={salesFormData.customerEmail || ''}
                                        onChange={e => setSalesFormData(p => ({ ...p, customerEmail: e.target.value }))}
                                        placeholder="email@example.com"
                                        style={{ width: '100%', padding: 10, borderRadius: 10, border: 'none', background: 'var(--bg-white)', fontSize: 13, boxSizing: 'border-box', outline: 'none' }} />
                                    </div>
                                  </div>
                                  <div style={{ marginBottom: 10 }}>
                                    <label style={{ display: 'block', marginBottom: 5, color: 'var(--text-primary)', fontWeight: 600, fontSize: 12 }}>{language === 'sw' ? 'Anwani' : 'Address'} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
                                    <input type="text" value={salesFormData.customerAddress || ''}
                                      onChange={e => setSalesFormData(p => ({ ...p, customerAddress: e.target.value }))}
                                      placeholder="Street, City"
                                      style={{ width: '100%', padding: 10, borderRadius: 10, border: 'none', background: 'var(--bg-white)', fontSize: 13, boxSizing: 'border-box', outline: 'none' }} />
                                  </div>
                                  <div style={{ display: 'flex', gap: 10 }}>
                                    <div style={{ flex: 1 }}>
                                      <label style={{ display: 'block', marginBottom: 5, color: 'var(--text-primary)', fontWeight: 600, fontSize: 12 }}>TIN <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
                                      <input type="text" value={salesFormData.customerTIN || ''}
                                        onChange={e => setSalesFormData(p => ({ ...p, customerTIN: e.target.value }))}
                                        placeholder="123-456-789"
                                        style={{ width: '100%', padding: 10, borderRadius: 10, border: 'none', background: 'var(--bg-white)', fontSize: 13, boxSizing: 'border-box', outline: 'none' }} />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                      <label style={{ display: 'block', marginBottom: 5, color: 'var(--text-primary)', fontWeight: 600, fontSize: 12 }}>VRN <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
                                      <input type="text" value={salesFormData.customerVRN || ''}
                                        onChange={e => setSalesFormData(p => ({ ...p, customerVRN: e.target.value }))}
                                        placeholder="40-001234-X"
                                        style={{ width: '100%', padding: 10, borderRadius: 10, border: 'none', background: 'var(--bg-white)', fontSize: 13, boxSizing: 'border-box', outline: 'none' }} />
                                    </div>
                                  </div>
                                </>
                              )}
                            </div>
                            {/* Date */}
                            <div style={{ marginBottom: 16 }}>
                              <label style={{ display: 'block', marginBottom: 6, color: 'var(--text-primary)', fontWeight: 600, fontSize: 14 }}>{t.date}</label>
                              <input type="date" name="date" value={salesFormData.date} onChange={handleSalesInputChange}
                                style={{ width: '100%', padding: 14, borderRadius: 12, border: 'none', background: 'var(--bg-tab)', fontSize: 15, boxSizing: 'border-box', outline: 'none' }} />
                            </div>
                            {/* Discount */}
                            <div style={{ marginBottom: 16 }}>
                              <label style={{ display: 'block', marginBottom: 6, color: 'var(--text-primary)', fontWeight: 600, fontSize: 14 }}>
                                {language === 'sw' ? 'Punguzo (Discount)' : 'Punguzo (Discount)'} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>({t.optional || 'optional'})</span>
                              </label>
                              <input type="number" min="0" inputMode="numeric" placeholder="0"
                                value={salesFormData.discount}
                                onChange={e => setSalesFormData(p => ({ ...p, discount: e.target.value }))}
                                style={{ width: '100%', padding: '9px 12px', borderRadius: 12, border: 'none', background: 'var(--bg-tab)', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
                            </div>

                            {/* EFD Receipt Number -hidden when generating invoice (no sale made yet) */}
                            {!saleFromInvoiceManagement && <div style={{ marginBottom: 16 }}>
                              <label style={{ display: 'block', marginBottom: 6, color: 'var(--text-primary)', fontWeight: 600, fontSize: 14 }}>EFD Receipt No. <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
                              <input type="text" placeholder="e.g. EFD-0000123"
                                value={salesFormData.efdReceiptNo}
                                onChange={e => setSalesFormData(p => ({ ...p, efdReceiptNo: e.target.value }))}
                                style={{ width: '100%', padding: '9px 12px', borderRadius: 12, border: 'none', background: 'var(--bg-tab)', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
                            </div>}

                            {/* Taxes -only for VAT-registered businesses */}
                            {vatRegistered && (<div style={{ marginBottom: 16 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                <label style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: 14 }}>Taxes <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>({t.optional || 'optional'})</span></label>
                                <button type="button" onClick={addSaleTax}
                                  style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', background: 'var(--bg-input)', border: 'none', borderRadius: 8, padding: '4px 10px', cursor: 'pointer' }}>
                                  {t.addTax || '+ Add Tax'}
                                </button>
                              </div>
                              {(salesFormData.taxes || []).map((tax, idx) => (
                                <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                                  <select value={tax.type} onChange={e => updateSaleTax(idx, 'type', e.target.value)}
                                    style={{ flex: 1, padding: '12px 12px', borderRadius: 12, border: '1px solid var(--border-color)', background: '#fff', fontSize: 14, outline: 'none', minWidth: 0, cursor: 'pointer' }}>
                                    <option value="vat">{t.vatOption || 'VAT (Value Added Tax)'}</option>
                                    <option value="withholding">{t.withholdingOption || 'Withholding Tax'}</option>
                                  </select>
                                  <input type="number" min="0" inputMode="numeric" placeholder="Rate %"
                                    value={tax.rate}
                                    onChange={e => updateSaleTax(idx, 'rate', e.target.value)}
                                    style={{ width: 70, padding: '12px 10px', borderRadius: 12, border: '1px solid var(--border-color)', background: '#fff', fontSize: 14, outline: 'none', textAlign: 'right', flexShrink: 0, boxSizing: 'border-box' }} />
                                  <button type="button" onClick={() => removeSaleTax(idx)}
                                    style={{ width: 36, height: 46, borderRadius: 10, border: 'none', background: '#FEE2E2', color: '#DC2626', cursor: 'pointer', fontSize: 20, fontWeight: 700, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                                </div>
                              ))}
                              {(salesFormData.taxes || []).length === 0 && (
                                <div style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>{t.noTaxesAdded || 'No taxes added. Click "Add Tax" to include VAT 18% or custom withholding tax.'}</div>
                              )}
                            </div>)}

                            {/* Total Changed banner -edit mode only */}
                            {editSaleId && (() => {
                              const orig = (sales || []).find(s => s.id === editSaleId);
                              if (!orig) return null;
                              const origTotal = parseFloat(orig.totalRevenue || 0);
                              const newTotal = getSaleTotalForPayments();
                              const delta = newTotal - origTotal;
                              if (Math.abs(delta) < 0.005) return null;
                              const isDown = delta < 0;
                              const bg = isDown ? '#FEF3C7' : '#E2E8F0';
                              const border = isDown ? '#FCD34D' : '#93C5FD';
                              const fg = isDown ? '#92400E' : '#334155';
                              const sub = isDown ? '#78350F' : '#1E3A8A';
                              return (
                                <div style={{ marginBottom: 12, padding: '12px 14px', borderRadius: 12, background: bg, border: `1px solid ${border}` }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                                    <span style={{ fontSize: 12, fontWeight: 700, color: fg, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                                      ⚠ {language === 'sw' ? 'Jumla imebadilika' : 'Total has changed'}
                                    </span>
                                    <span style={{ fontSize: 13, fontWeight: 800, color: fg }}>
                                      {isDown ? '−' : '+'}{fmt(Math.abs(delta))}
                                    </span>
                                  </div>
                                  <div style={{ fontSize: 12, color: sub, lineHeight: 1.4 }}>
                                    {language === 'sw' ? 'Awali' : 'Was'}: <strong>{fmt(origTotal)}</strong>{' → '}
                                    {language === 'sw' ? 'Sasa' : 'Now'}: <strong>{fmt(newTotal)}</strong>
                                    <br/>
                                    {language === 'sw'
                                      ? 'Hakikisha malipo hapa chini yanalingana na jumla mpya. Punguza Mkopo kwanza, kisha pesa kutoka akaunti.'
                                      : 'Adjust the payments below to match the new total. Tip: reduce Credit first, then cash/bank/mobile.'}
                                  </div>
                                </div>
                              );
                            })()}

                            {/* Total Display */}
                            {(() => {
                              const displayTotal = getSaleTotalForPayments();
                              return displayTotal > 0 ? (
                                <div style={{ marginBottom: 16, padding: '10px 14px', borderRadius: 14, background: 'var(--bg-tab)', border: '1.5px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <span style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-primary)' }}>{language === 'sw' ? 'Jumla' : 'Total'}</span>
                                  <span style={{ fontWeight: 800, fontSize: 18, color: '#059669' }}>{fmt(displayTotal)}</span>
                                </div>
                              ) : null;
                            })()}
                            {/* Split Payment -hidden for invoice (unpaid) mode */}
                            {saleSaveMode !== 'invoice' && (() => {
                              const saleTotal = getSaleTotalForPayments();
                              // Credit = customer owes you -NOT money received
                              const paidAmt = (salePayments || []).filter(p => p.type !== 'credit').reduce((s, p) => s + parseFloat(p.amount || 0), 0);
                              const creditAmt = (salePayments || []).filter(p => p.type === 'credit').reduce((s, p) => s + parseFloat(p.amount || 0), 0);
                              const coverage = paidAmt + creditAmt;
                              const remaining = Math.max(0, saleTotal - coverage);
                              const isCreditOnly = creditAmt > 0 && paidAmt === 0;
                              const isPartialCredit = creditAmt > 0 && paidAmt > 0;
                              const payOpts = buildPaymentOptions(true);
                              return (
                                <div style={{ marginBottom: 24 }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                    <label style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: 14 }}>{t.paymentMethod}</label>
                                    {(salePayments || []).length < (payOpts || []).length && saleTotal > 0 && (
                                      <button type="button" onClick={addSalePayment}
                                        style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', background: 'var(--bg-input)', border: 'none', borderRadius: 8, padding: '4px 10px', cursor: 'pointer' }}>
                                        + {t.add || 'Add'}
                                      </button>
                                    )}
                                  </div>
                                  {(salePayments || []).map((pay, idx) => {
                                    // Max for this row = saleTotal minus the sum of all other rows
                                    const otherRowsTotal = (salePayments || []).reduce((s, p, i) => i !== idx ? s + parseFloat(p.amount || 0) : s, 0);
                                    const maxForRow = Math.max(0, saleTotal - otherRowsTotal);
                                    // Uniqueness rule: exclude specific accounts already chosen by other rows
                                    // (a row keyed by accountKey -so CRDB ≠ NMB even though both are "bank")
                                    const currentKey = pay.accountKey || pay.type;
                                    const usedByOthers = new Set((salePayments || []).map((p, i) => i !== idx ? (p.accountKey || p.type) : null).filter(Boolean));
                                    const availableOpts = (payOpts || []).filter(o => !usedByOthers.has(o.value) || o.value === currentKey);
                                    return (
                                    <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                                      <select value={currentKey} onChange={e => handleSalePaymentTypeChange(idx, e.target.value)}
                                        style={{ flex: 1, padding: '12px 10px', borderRadius: 12, border: 'none', background: 'var(--bg-tab)', fontSize: 14, outline: 'none', minWidth: 0 }}>
                                        {availableOpts.map(o => <option key={o.value + o.label} value={o.value}>{o.label}</option>)}
                                      </select>
                                      <input type="number" min="0" max={maxForRow} inputMode="numeric" placeholder="0"
                                        value={pay.amount}
                                        onChange={e => {
                                          const raw = parseFloat(e.target.value);
                                          const clamped = isNaN(raw) ? '' : String(Math.min(raw, maxForRow));
                                          handleSalePaymentAmountChange(idx, clamped);
                                        }}
                                        style={{ width: 100, padding: '12px 10px', borderRadius: 12, border: 'none', background: 'var(--bg-tab)', fontSize: 14, outline: 'none', textAlign: 'right', flexShrink: 0, boxSizing: 'border-box' }} />
                                      {(salePayments || []).length > 1 && (
                                        <button type="button" onClick={() => removeSalePayment(idx)}
                                          style={{ width: 36, height: 46, borderRadius: 10, border: 'none', background: '#FEE2E2', color: '#DC2626', cursor: 'pointer', fontSize: 20, fontWeight: 700, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                                      )}
                                    </div>
                                    );
                                  })}
                                  {saleTotal > 0 && (() => {
                                    if (remaining > 0.01) {
                                      return (
                                        <div style={{ borderRadius: 10, padding: '8px 12px', marginTop: 4, background: '#FEF3C7', color: '#92400E', fontSize: 13, fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                          <span>{language === 'sw' ? 'Hayajakamilika' : 'Incomplete'}</span>
                                          <span>{fmt(remaining)} {language === 'sw' ? 'bakaza' : 'remaining'}</span>
                                        </div>
                                      );
                                    }
                                    if (isCreditOnly) {
                                      return (
                                        <div style={{ borderRadius: 10, padding: '8px 12px', marginTop: 4, background: '#FEF3C7', color: '#92400E', fontSize: 13, fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                          <span>{language === 'sw' ? 'Haijalipwa (Mkopo)' : 'Unpaid (Credit)'}</span>
                                          <span>{fmt(creditAmt)} {language === 'sw' ? 'bakaza' : 'remaining'}</span>
                                        </div>
                                      );
                                    }
                                    if (isPartialCredit) {
                                      return (
                                        <div style={{ borderRadius: 10, padding: '8px 12px', marginTop: 4, background: '#FEF3C7', color: '#92400E', fontSize: 13, fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                          <span>{language === 'sw' ? 'Sehemu kwa Mkopo' : 'Partly on Credit'}</span>
                                          <span>{fmt(creditAmt)} {language === 'sw' ? 'kwa mkopo' : 'on credit'}</span>
                                        </div>
                                      );
                                    }
                                    return (
                                      <div style={{ borderRadius: 10, padding: '8px 12px', marginTop: 4, background: '#D1FAE5', color: '#065F46', fontSize: 13, fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span>{t.paid || 'Paid'}</span>
                                      </div>
                                    );
                                  })()}
                                </div>
                              );
                            })()}
                          </>
                          );
                        })()}
                        {/* Document Type toggle removed -sales are always receipts (paid). Use Invoice Management for pending invoices. */}

                        {/* Footer Sections (Terms, Additional Details, etc.) */}
                        <div style={{ background: 'var(--bg-tab)', borderRadius: 12, padding: '12px', marginBottom: 16 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                            <label style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: 13 }}>
                              {language === 'sw' ? 'Sehemu za Chini' : 'Footer Sections'} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>({t.optional || 'optional'})</span>
                            </label>
                            {saleFooterSections.length < 2 && (
                              <button type="button" onClick={() => setSaleFooterSections(prev => [...prev, { label: 'Additional Details', content: '' }])}
                                style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', background: 'var(--bg-input)', border: 'none', borderRadius: 8, padding: '4px 10px', cursor: 'pointer' }}>
                                + {language === 'sw' ? 'Ongeza' : 'Add section'}
                              </button>
                            )}
                          </div>
                          <p style={{ fontSize: 11, color: 'var(--text-secondary)', margin: '0 0 8px' }}>
                            {language === 'sw' ? '2 sehemu = kando kwa kando kwenye PDF.' : '2 sections appear side by side on the PDF.'}
                          </p>
                          {saleFooterSections.map((sec, si) => (
                            <div key={si} style={{ marginBottom: si < saleFooterSections.length - 1 ? 10 : 0 }}>
                              <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 5 }}>
                                <select value={sec.label}
                                  onChange={e => setSaleFooterSections(prev => prev.map((s, i) => i === si ? { ...s, label: e.target.value } : s))}
                                  style={{ flex: 1, padding: '7px 8px', borderRadius: 8, border: 'none', background: 'var(--bg-card)', fontSize: 12, outline: 'none' }}>
                                  <option>Terms &amp; Conditions</option>
                                  <option>Additional Details</option>
                                  <option>Notes</option>
                                  <option>Delivery Terms</option>
                                  <option>Warranty</option>
                                  <option>Custom</option>
                                </select>
                                {sec.label === 'Custom' && (
                                  <input type="text" placeholder="Section title" value={sec.customLabel || ''}
                                    onChange={e => setSaleFooterSections(prev => prev.map((s, i) => i === si ? { ...s, customLabel: e.target.value } : s))}
                                    style={{ flex: 1, padding: '7px 8px', borderRadius: 8, border: 'none', background: 'var(--bg-card)', fontSize: 12, outline: 'none' }} />
                                )}
                                {saleFooterSections.length > 1 && (
                                  <button type="button" onClick={() => setSaleFooterSections(prev => prev.filter((_, i) => i !== si))}
                                    style={{ width: 26, height: 26, borderRadius: 6, background: '#FEE2E2', color: '#DC2626', border: 'none', fontWeight: 700, fontSize: 15, cursor: 'pointer', flexShrink: 0 }}>×</button>
                                )}
                              </div>
                              <textarea rows={2} value={sec.content}
                                onChange={e => setSaleFooterSections(prev => prev.map((s, i) => i === si ? { ...s, content: e.target.value } : s))}
                                placeholder={language === 'sw' ? 'Andika hapa...' : 'One point per line...'}
                                style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: 'none', background: 'var(--bg-card)', fontSize: 12, color: 'var(--text-primary)', boxSizing: 'border-box', outline: 'none', resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }} />
                            </div>
                          ))}
                        </div>

                        {/* Issue invoice toggle -only relevant for fully-paid sales.
                            Credit / partial sales always issue an invoice (proof of debt). */}
                        {(() => {
                          // Compute payments composition from current form state
                          const _allPmts = (salePayments || []).filter(p => p.amount && parseFloat(p.amount) > 0);
                          const _hasCredit = _allPmts.some(p => p.type === 'credit');
                          const _totalCart = saleLineItems.reduce((s, it) => {
                            const q = it.type === 'service' ? 1 : parseFloat(it.quantity || 0);
                            return s + q * parseFloat(it.sellingPrice || 0);
                          }, 0);
                          const _discPct = parseFloat(salesFormData.discount || 0);
                          const _afterDisc = _totalCart * (1 - _discPct / 100);
                          const _withTax = _afterDisc + (salesFormData.taxes || []).reduce((s, tx) => s + _afterDisc * (parseFloat(tx.rate) || 0) / 100, 0);
                          const _paidNonCredit = _allPmts.filter(p => p.type !== 'credit').reduce((s, p) => s + parseFloat(p.amount || 0), 0);
                          const _fullyPaid = !_hasCredit && _paidNonCredit >= _withTax - 0.005 && _withTax > 0;
                          // Auto-default: ON when there's a named customer, OFF for blank walk-in
                          const _hasCustomer = (salesFormData.customerName || '').trim().length > 0;
                          // Only show the toggle when the sale will be paid in full AND not in invoice/credit mode
                          if (!_fullyPaid || saleSaveMode === 'invoice' || saleFromInvoiceManagement) return null;
                          return (
                            <div style={{ background: 'var(--bg-tab)', border: '1px solid var(--border-color)', borderRadius: 12, padding: '12px 14px', marginBottom: 12 }}>
                              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                                <input type="checkbox"
                                  checked={!!salesIssueInvoice}
                                  onChange={e => { salesIssueInvoiceTouched.current = true; setSalesIssueInvoice(e.target.checked); }}
                                  style={{ width: 18, height: 18, accentColor: '#334155', cursor: 'pointer', flexShrink: 0 }} />
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text-primary)' }}>
                                    {language === 'sw' ? 'Toa ankara rasmi kwa mauzo haya' : 'Issue formal invoice for this sale'}
                                  </div>
                                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2, lineHeight: 1.3 }}>
                                    {language === 'sw'
                                      ? 'Itazalisha INV-XXXX na kuwekwa kwenye orodha ya Ankara Zilizotolewa. Unaweza kuwasha baadaye kupitia Ripoti → Hariri.'
                                      : 'Auto-generates INV-XXXX, adds to Issued Invoices, and prompts PDF download. You can also enable later via Reports → Edit.'}
                                  </div>
                                </div>
                              </label>
                            </div>
                          );
                        })()}

                        <div className="form-buttons">
                          {saleLineItems.length === 0 && (
                            <p style={{ color: '#DC2626', fontSize: 13, fontWeight: 600, margin: '0 0 10px', textAlign: 'center' }}>
                              ⚠ {language === 'sw' ? 'Ongeza angalau bidhaa moja' : 'Add at least one item to the order'}
                            </p>
                          )}
                          <button className="save-btn" onClick={handleSaveSale} disabled={saleLineItems.length === 0 && !(saleLineDraft.productName && saleLineDraft.quantity && saleLineDraft.sellingPrice)}>
                            {saleSaveMode === 'invoice' ? (language === 'sw' ? 'Toa Invoice' : 'Issue Invoice') : t.save}
                          </button>
                          <button className="cancel-btn" onClick={() => { setShowSalesForm(false); resetSaleForm(); }}>{t.cancel}</button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* PROFORMA INVOICE FORM -moved to global level so it works from Services tab too */}

                  {/* INVOICE / RECEIPT PREVIEW MODAL */}
                  {showInvoicePreview && lastSavedSale && (
                    <div className="popup-overlay" style={{ zIndex: 1000 }}>
                      <div className="popup-form" style={{ maxWidth: 420, width: '100%', borderRadius: 16, padding: 28, textAlign: 'center', maxHeight: '90vh', overflowY: 'auto' }}>
                        {/* Icon -green check for receipt, blue document for invoice */}
                        <div style={{ width: 60, height: 60, borderRadius: '50%', background: lastSavedSale.isPaid === false ? '#F1F5F9' : '#F0FDF4', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                          {lastSavedSale.isPaid === false
                            ? <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#334155" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                            : <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                          }
                        </div>
                        <h3 style={{ fontSize: 20, fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 4px' }}>
                          {lastSavedSale.isPaid === false
                            ? (language === 'sw' ? 'Invoice Imetolewa!' : 'Invoice Issued!')
                            : t.saleRecorded}
                        </h3>
                        <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 20px' }}>
                          {lastSavedSale.isPaid === false
                            ? (language === 'sw' ? 'Tuma invoice kwa mteja. Malipo yatachelewa.' : 'Send this invoice to your customer. Payment is pending.')
                            : t.invoiceReady}
                        </p>

                        {/* Badge: Invoice vs Receipt */}
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 14px', borderRadius: 20, marginBottom: 14,
                          background: lastSavedSale.isPaid === false ? '#F1F5F9' : '#D1FAE5',
                          color: lastSavedSale.isPaid === false ? '#334155' : '#065F46',
                          fontSize: 12, fontWeight: 700 }}>
                          {lastSavedSale.isPaid === false ? 'INVOICE' : 'RECEIPT'} · {lastSavedSale.invoiceNumber}
                        </div>

                        {/* Preview card */}
                        <div style={{ background: 'var(--bg-tab)', borderRadius: 14, padding: '16px 20px', marginBottom: 14, textAlign: 'left', border: '1px solid var(--border-color)' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                            <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--text-primary)' }}>{businessName}</span>
                            <span style={{ fontSize: 11, fontWeight: 700, color: lastSavedSale.isPaid === false ? '#334155' : '#059669', background: lastSavedSale.isPaid === false ? '#F1F5F9' : '#F0FDF4', padding: '3px 10px', borderRadius: 8 }}>{lastSavedSale.invoiceNumber}</span>
                          </div>
                          <div style={{ fontSize: 12, color: 'var(--text-muted)', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 0' }}>
                            <span style={{ color: 'var(--text-muted)' }}>{t.date}:</span><span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{lastSavedSale.date}</span>
                            <span style={{ color: 'var(--text-muted)' }}>{t.billTo}:</span><span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{lastSavedSale.customerName}</span>
                            <span style={{ color: 'var(--text-muted)' }}>{t.selectProduct}:</span><span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{lastSavedSale.productName}</span>
                            <span style={{ color: 'var(--text-muted)' }}>{t.quantity}:</span><span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{lastSavedSale.quantity}</span>
                            <span style={{ color: 'var(--text-muted)' }}>{t.paymentMethod}:</span>
                            <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>
                              {lastSavedSale.payments && lastSavedSale.payments.length > 1
                                ? (lastSavedSale.payments || []).map((p, i) => (
                                    <span key={i} style={{ display: 'block', textTransform: 'capitalize' }}>
                                      {p.type}: {fmt(parseFloat(p.amount || 0))}
                                    </span>
                                  ))
                                : <span style={{ textTransform: 'capitalize' }}>{lastSavedSale.paymentMethod}</span>
                              }
                            </span>
                            <span style={{ color: 'var(--text-muted)' }}>{t.status}:</span>
                            {(() => {
                              const _pmts = lastSavedSale.payments || [];
                              const _nonCredit = _pmts.filter(p => p.type !== 'credit').reduce((s, p) => s + parseFloat(p.amount || 0), 0);
                              const _credit = _pmts.filter(p => p.type === 'credit').reduce((s, p) => s + parseFloat(p.amount || 0), 0);
                              const _total = parseFloat(lastSavedSale.totalRevenue || 0);
                              const _fullyPaid = lastSavedSale.isPaid === true || (_credit === 0 && _nonCredit >= _total - 0.005);
                              const _partial = !_fullyPaid && _nonCredit > 0;
                              const label = _fullyPaid
                                ? (language === 'sw' ? 'Imelipwa' : 'Paid')
                                : _partial
                                  ? (language === 'sw' ? 'Imelipwa Kiasi' : 'Partially Paid')
                                  : (language === 'sw' ? 'Haijalipiwa' : 'Unpaid');
                              const color = _fullyPaid ? '#059669' : _partial ? '#D97706' : '#DC2626';
                              return <span style={{ fontWeight: 700, color }}>{label}</span>;
                            })()}
                          </div>
                          {lastSavedSale.invoiceNotes && (
                            <div style={{ marginTop: 10, padding: '8px 10px', borderRadius: 8, background: '#FAFAFA', border: '1px solid var(--border-color)', fontSize: 12, color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                              {lastSavedSale.invoiceNotes}
                            </div>
                          )}
                          <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>Total</span>
                            <span style={{ fontSize: 16, fontWeight: 800, color: '#059669' }}>{fmt(lastSavedSale.totalRevenue)}</span>
                          </div>
                        </div>

                        {/* Download button */}
                        <button onClick={() => generateInvoicePDF(lastSavedSale, getBizInfo(), paymentAccounts)}
                          style={{ width: '100%', padding: '11px 0', borderRadius: 14, background: '#111827', color: '#A3E635', border: 'none', fontWeight: 700, fontSize: 15, cursor: 'pointer', marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                          {lastSavedSale.isPaid === false ? (language === 'sw' ? 'Pakua Invoice (PDF)' : 'Download Invoice (PDF)') : t.downloadInvoice}
                        </button>
                        <button onClick={() => { setShowInvoicePreview(false); setLastSavedSale(null); }}
                          style={{ width: '100%', padding: '12px 0', borderRadius: 14, background: 'var(--bg-tab)', color: 'var(--text-primary)', border: 'none', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
                          {t.done}
                        </button>
                      </div>
                    </div>
                  )}


                  {/* SALES REPORT SCREEN */}
                  {isSalesReportOpen && (() => {
                    const reportSales = getFilteredSales()
                      .filter(s => !salesReportProductFilter || s.productName === salesReportProductFilter)
                      .filter(s => !salesReportPaymentFilter || s.paymentMethod === salesReportPaymentFilter)
                      .filter(s => !salesReportCustomerFilter || s.customerName === salesReportCustomerFilter)
                      .filter(s => !hideVoidedSales || s.status !== 'void');
                    const getSaleReturns = (saleId) => (salesReturns || []).filter(r => r.status !== 'void' && String(r.originalSaleId) === String(saleId));
                    // Accounting totals ALWAYS exclude void (display rows are
                    // toggle-controlled, but totals/exports are not).
                    const acctReportSales = reportSales.filter(s => s.status !== 'void');
                    const reportTotal = acctReportSales.reduce((sum, s) => sum + getSaleGrandTotal(s), 0);
                    // Credit Sales = amount STILL OWED across these sales (each
                    // sale's outstanding balance), not the full value of any
                    // partly-paid sale, and net of returns that cut the credit.
                    const creditTotal = acctReportSales.reduce((sum, s) => sum + saleOutstanding(s), 0);
                    // Returns = returns of the sales actually listed, so the
                    // RETURNS summary matches the returns shown in the table.
                    const totalReturnsAmt = acctReportSales.reduce((s, sale) =>
                      s + getSaleReturns(sale.id).reduce((a, r) => a + parseFloat(r.refundAmt || 0), 0), 0);
                    const srH = ['Date','Product','Qty','Selling Price','Recorded Unit Cost','Accounting Unit Cost','Recorded Total Cost','Accounting Total Cost','Cost Adjustment','Correction Status','Total Revenue','Profit','Customer','Payment','Status','Invoice No.'];
                    // Accounting cost is drawn from the SAME chronological replay the
                    // P&L uses (replaySaleUnitCosts → productTimeline), keyed per sale
                    // line, NOT a separate per-row WAC -so the export is deterministic
                    // and consistent with the P&L ordering. Falls back to as-of WAC
                    // only for a line the replay didn't attribute (e.g. legacy).
                    const _saleCostMap = replaySaleUnitCosts({ products, purchases: acctPurchases, sales: acctSales, salesReturns: acctSalesReturns, purchaseReturns: acctPurchaseReturns });
                    const _nkey = (s, item) => `${String(s.id)}::${normProductName(item.productName || s.productName)}`;
                    const lineRecon = (item, s) => {
                      const qty = parseFloat(item.quantity != null ? item.quantity : s.quantity) || 0;
                      const isService = item.type === 'service' || s.isService;
                      const recUnit = parseFloat(item.cost != null ? item.cost : (s.cost || 0)) || 0;
                      const mapped = _saleCostMap[_nkey(s, item)];
                      const accUnit = isService ? recUnit : (mapped != null ? mapped : (getProductWAC(item.productName || s.productName, s.date) || recUnit));
                      const recTot = recUnit * qty, accTot = accUnit * qty, adj = accTot - recTot;
                      let st;
                      if (item.costCorrectedAt || s.costCorrectedAt) st = 'Corrected';
                      else if (!isService && recUnit <= 0 && accUnit > 0) st = 'Zero-cost (uncorrected)';
                      else if (!isService && Math.abs(adj) > 1) st = 'WAC variation';
                      else st = 'OK';
                      return { recUnit, accUnit, recTot, accTot, adj, st };
                    };
                    // Footer reconciliation: the sum of sale-row accounting costs is
                    // GROSS COGS (before customer-return reversals). Netting the
                    // return reversals yields the P&L COGS exactly, so the export
                    // reconciles rather than sitting ~2.4M above the P&L.
                    const srFooter = () => {
                      let gross = 0, reversals = 0;
                      // ACCOUNTING array -a voided sale must contribute no COGS to
                      // the reconciliation footer even when Show voided is on.
                      acctReportSales.forEach(s => {
                        const items = s.items && s.items.length > 0 ? s.items : [{ productName: s.productName, quantity: s.quantity, cost: s.cost, type: s.isService ? 'service' : 'product' }];
                        items.forEach(item => { if (item.type === 'service' || s.isService) return; gross += lineRecon(item, s).accTot; });
                        getSaleReturns(s.id).forEach(r => {
                          const rItems = r.items && r.items.length > 0 ? r.items : [{ productName: r.productName, qty: r.qty, unitCost: r.unitCost, productId: r.productId }];
                          rItems.forEach(ri => {
                            const q = parseFloat(ri.qty != null ? ri.qty : ri.quantity) || 0;
                            if (q <= 0) return;
                            const snap = ri.unitCost != null && ri.unitCost !== '' ? parseFloat(ri.unitCost) : null;
                            const acc = snap != null ? snap : (_saleCostMap[_nkey(s, ri)] != null ? _saleCostMap[_nkey(s, ri)] : getProductWAC(ri.productName || s.productName, r.date));
                            reversals += q * (acc || 0);
                          });
                        });
                      });
                      const net = gross - reversals;
                      const mk = (label, val) => { const row = Array(srH.length).fill(''); row[1] = label; row[7] = val.toFixed(2); return row; };
                      return [Array(srH.length).fill(''), mk('Sales accounting cost (gross)', gross), mk('Less customer-return COGS reversals', -reversals), mk('Net P&L COGS', net)];
                    };
                    const srR = () => { const rows = []; reportSales.forEach(s => { const pmts=(s.payments||[]).filter(p=>p.amount&&parseFloat(p.amount)>0 && p.type !== 'credit'); const totalPaid=pmts.reduce((a,p)=>a+parseFloat(p.amount||0),0); const isPartial=s.isPaid===false&&totalPaid>0; const payLabel=s.isPaid===false&&totalPaid===0?'Credit':isPartial?'Partial Pmt':'Paid in Full'; const status=s.isPaid===false?'Unpaid':'Paid'; const items = s.items && s.items.length > 0 ? s.items : [{ productName: s.productName, quantity: s.quantity, sellingPrice: s.sellingPrice, cost: s.cost }]; items.forEach((item, idx) => { const rc = lineRecon(item, s); rows.push([ s.date, item.productName || s.productName || '—', item.quantity || s.quantity || '', item.sellingPrice || s.sellingPrice || '', rc.recUnit.toFixed(2), rc.accUnit.toFixed(2), rc.recTot.toFixed(2), rc.accTot.toFixed(2), rc.adj.toFixed(2), rc.st, idx === 0 ? parseFloat(s.totalRevenue||0).toFixed(2) : '', idx === 0 ? grossProfitExVAT(s).toFixed(2) : '', s.customerName||'—', idx === 0 ? payLabel : '', idx === 0 ? status : '', s.invoiceNumber || '' ]); }); }); return rows; };
                    const fromD = salesDateFilters.fromDate ? new Date(salesDateFilters.fromDate).toLocaleDateString() : '—';
                    const toD = salesDateFilters.toDate ? new Date(salesDateFilters.toDate).toLocaleDateString() : '—';
                    return (
                      <div style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'var(--bg-tab)', display: 'flex', flexDirection: 'column' }}>
                        {/* ── HEADER: back + title + download buttons ── */}
                        <div style={{ background: 'var(--bg-white)', borderBottom: '1px solid var(--border-color)', padding: '14px 16px 10px', flexShrink: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
                            <button onClick={() => { setIsSalesReportOpen(false); if (drilldownFromTab) { setActiveTab(drilldownFromTab); setDrilldownFromTab(null); } }} style={{ background: 'none', border: 'none', padding: '4px 10px 4px 0', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#111827" strokeWidth="2.5" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
                            </button>
                            <h2 style={{ flex: 1, fontSize: 18, fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>{t.salesReports}</h2>
                          </div>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button onClick={() => downloadPDF(srH, [...srR(), ...srFooter()], 'Sales Report', 'sales-report.pdf')} style={{ flex: 1, padding: '9px 4px', borderRadius: 10, background: '#EF4444', color: '#fff', border: 'none', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>{t.downloadPDF}</button>
                            <button onClick={() => downloadCSV([srH, ...srR(), ...srFooter()], 'sales-report.csv')} style={{ flex: 1, padding: '9px 4px', borderRadius: 10, background: '#059669', color: '#fff', border: 'none', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>{t.downloadCSV}</button>
                            <button onClick={() => {
                              const ordersHeader = ['Date','Invoice','Customer','Item Count','Total Qty','Subtotal','Discount %','Tax','Grand Total','Profit','Payment','Status'];
                              const ordersRows = reportSales.map(s => {
                                const items = Array.isArray(s.items) && s.items.length > 0 ? s.items : [{ productName: s.productName, quantity: s.quantity, sellingPrice: s.sellingPrice }];
                                const qtyT = items.reduce((sum, it) => sum + (it.type === 'service' ? 1 : parseFloat(it.quantity || 0)), 0);
                                const sub = items.reduce((sum, it) => { const q = it.type === 'service' ? 1 : parseFloat(it.quantity || 0); return sum + q * parseFloat(it.sellingPrice || 0); }, 0);
                                const discPct = parseFloat(s.discount || 0);
                                const grand = getSaleGrandTotal(s);
                                const taxAmt = Math.max(0, grand - sub * (1 - discPct / 100));
                                return [s.date, s.invoiceNumber || '—', s.customerName || 'Walk-in', items.length, qtyT, sub.toFixed(2), discPct, taxAmt.toFixed(2), grand.toFixed(2), grossProfitExVAT(s).toFixed(2), s.paymentMethod || 'cash', s.isPaid === false ? 'Unpaid' : 'Paid'];
                              });
                              const itemsHeader = ['Date','Invoice','Customer','Product','Qty','Unit Price','Line Total','Cost','Order Grand Total','Payment','Status'];
                              const itemsRows = reportSales.flatMap(s => {
                                const items = Array.isArray(s.items) && s.items.length > 0 ? s.items : [{ productName: s.productName, quantity: s.quantity, sellingPrice: s.sellingPrice, cost: s.cost }];
                                const grand = getSaleGrandTotal(s);
                                return items.map(it => {
                                  const qty = it.type === 'service' ? 1 : parseFloat(it.quantity || 0);
                                  const price = parseFloat(it.sellingPrice || 0);
                                  return [s.date, s.invoiceNumber || '—', s.customerName || 'Walk-in', it.productName || '—', qty, price.toFixed(2), (qty * price).toFixed(2), parseFloat(it.cost || 0).toFixed(2), grand.toFixed(2), s.paymentMethod || 'cash', s.isPaid === false ? 'Unpaid' : 'Paid'];
                                });
                              });
                              downloadExcelMultiSheet([
                                { name: 'Orders', rows: [ordersHeader, ...ordersRows] },
                                { name: 'Line Items', rows: [itemsHeader, ...itemsRows] },
                              ], 'sales-report.xlsx');
                            }} style={{ flex: 1, padding: '9px 4px', borderRadius: 10, background: '#2563EB', color: '#fff', border: 'none', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>{t.downloadExcel}</button>
                          </div>
                        </div>
                        {/* ── PERIOD SELECTOR (matches Sales tab style) ── */}
                        <div style={{ background: 'var(--bg-white)', padding: '12px 16px 8px', flexShrink: 0 }}>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {[{key:'today',label:t.today},{key:'week',label:t.week},{key:'month',label:t.month},{key:'year',label:t.year || t.thisYear || 'Year'},{key:'custom',label:t.custom}].map(({key,label}) => (
                              <button key={key} onClick={() => { setSalesReportPeriod(key); const d = getReportDates(key); if (d) setSalesDateFilters({ fromDate: d.from, toDate: d.to }); }}
                                style={{ flex: 1, padding: '10px 6px', borderRadius: 16, fontSize: 12, fontWeight: salesReportPeriod === key ? 700 : 600, cursor: 'pointer', border: 'none', whiteSpace: 'nowrap',
                                  background: salesReportPeriod === key ? 'var(--bg-tab-active)' : 'var(--bg-tab-inactive)',
                                  color: salesReportPeriod === key ? 'var(--text-tab-active)' : 'var(--text-tab-inactive)' }}>
                                {label}
                              </button>
                            ))}
                          </div>
                          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>{fromD} - {toD}</div>
                        </div>
                        {/* ── CUSTOM DATE PICKERS ── */}
                        {salesReportPeriod === 'custom' && (
                          <div style={{ background: 'var(--bg-white)', padding: '0 16px 12px', borderBottom: '1px solid var(--border-color)', display: 'flex', gap: 10, flexShrink: 0 }}>
                            <div style={{ flex: 1 }}>
                              <label style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>{t.startDate}</label>
                              <input type="date" value={salesDateFilters.fromDate} onFocus={e => { try { e.currentTarget.showPicker(); } catch(x){} }} onChange={e => setSalesDateFilters(f => ({...f, fromDate: e.target.value}))} style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid var(--border-color)', background: 'var(--bg-tab)', fontSize: 14, boxSizing: 'border-box' }} />
                            </div>
                            <div style={{ flex: 1 }}>
                              <label style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>{t.endDate}</label>
                              <input type="date" value={salesDateFilters.toDate} onFocus={e => { try { e.currentTarget.showPicker(); } catch(x){} }} onChange={e => setSalesDateFilters(f => ({...f, toDate: e.target.value}))} style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid var(--border-color)', background: 'var(--bg-tab)', fontSize: 14, boxSizing: 'border-box' }} />
                            </div>
                          </div>
                        )}
                        {/* ── FILTERS stacked vertically ── */}
                        <div style={{ background: 'var(--bg-white)', padding: '10px 16px 12px', borderBottom: '1px solid var(--border-color)', flexShrink: 0 }}>
                          <select value={salesReportProductFilter} onChange={e => setSalesReportProductFilter(e.target.value)} style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: 'none', background: 'var(--bg-tab)', fontSize: 14, marginBottom: 8, boxSizing: 'border-box' }}>
                            <option value="">{t.filterByProduct}</option>
                            {[...new Set(sales.map(s => s.productName))].map(n => <option key={n} value={n}>{n}</option>)}
                          </select>
                          <select value={salesReportPaymentFilter} onChange={e => setSalesReportPaymentFilter(e.target.value)} style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: 'none', background: 'var(--bg-tab)', fontSize: 14, marginBottom: 8, boxSizing: 'border-box' }}>
                            <option value="">{t.filterByPayment}</option>
                            {buildPaymentOptions(true).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </select>
                          <select value={salesReportCustomerFilter} onChange={e => setSalesReportCustomerFilter(e.target.value)} style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: 'none', background: 'var(--bg-tab)', fontSize: 14, boxSizing: 'border-box' }}>
                            <option value="">{t.filterByCustomer}</option>
                            {[...new Set(sales.map(s => s.customerName).filter(Boolean))].map(n => <option key={n} value={n}>{n}</option>)}
                          </select>
                        </div>
                        {/* ── SUMMARY CARD ── */}
                        <div style={{ background: 'var(--bg-white)', padding: '10px 16px', borderBottom: '1px solid var(--border-color)', display: 'flex', gap: 10, flexShrink: 0, flexWrap: 'wrap' }}>
                          <div className="pastel-card pastel-card--sage">
                            <div className="pc-label">{t.totalSales}</div>
                            <div className="pc-value">{fmt(reportTotal)}</div>
                          </div>
                          {totalReturnsAmt > 0 && (
                            <div className="pastel-card pastel-card--rose">
                              <div className="pc-label">↩ {language === 'sw' ? 'Zilizorudishwa' : 'Returns'}</div>
                              <div className="pc-value">-{fmt(totalReturnsAmt)}</div>
                            </div>
                          )}
                          {totalReturnsAmt > 0 && (
                            <div className="pastel-card pastel-card--mint">
                              <div className="pc-label">{language === 'sw' ? 'Halisi' : 'Net Sales'}</div>
                              <div className="pc-value">{fmt(reportTotal - totalReturnsAmt)}</div>
                            </div>
                          )}
                          {creditTotal > 0 && (
                            <div className="pastel-card pastel-card--amber">
                              <div className="pc-label">{t.creditSales}</div>
                              <div className="pc-value">{fmt(creditTotal)}</div>
                            </div>
                          )}
                        </div>
                        {/* ── TRANSACTION TABLE ── */}
                        {(() => {
                          const thS = { padding: '8px 12px', textAlign: 'left', fontWeight: 700, color: 'var(--text-primary)', fontSize: 12, borderBottom: '2px solid #D1D5DB', whiteSpace: 'nowrap' };
                          const tdS = { padding: '8px 12px', color: 'var(--text-primary)', fontSize: 12, verticalAlign: 'middle', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
                          const editBtnS = { background: 'var(--bg-input)', color: 'var(--text-secondary)', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 13, marginRight: 3 };
                          const delBtnS  = { background: '#FEE2E2', color: '#DC2626', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 13 };
                          const moreBtnS = { fontSize: 10, fontWeight: 700, background: '#E0E7FF', color: '#3730A3', border: 'none', borderRadius: 4, padding: '2px 5px', marginLeft: 4, cursor: 'pointer' };
                          const getSaleProductCell = (rec) => {
                            const items = rec.items;
                            if (items && items.length > 0) {
                              const label = items.map(i => `${i.productName || '—'}${items.length > 1 ? ` ×${i.quantity || 1}` : ''}`).join(', ');
                              return <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180, title: label }}>{label}</span>;
                            }
                            return <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 110 }}>{rec.productName || '—'}</span>;
                          };
                          return (
                            <div style={{ flex: 1, overflowY: 'auto', overflowX: 'auto', WebkitOverflowScrolling: 'touch', padding: '0 12px' }}>
                              {reportSales.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--text-muted)', fontSize: 14 }}>{t.noDataForPeriod}</div>
                              ) : (<>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 4px' }}>
                                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{language === 'sw' ? 'Bofya kitufe cha View kuona maelezo kamili.' : 'Click the View button to view full transaction details.'}</div>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--text-muted)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                    <input type="checkbox" checked={hideVoidedSales} onChange={e => setHideVoidedSales(e.target.checked)} style={{ accentColor: '#92400E' }} />
                                    {language === 'sw' ? 'Ficha zilizobatilishwa' : 'Hide voided'}
                                  </label>
                                </div>
                                <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%', tableLayout: 'auto' }}>

                                  <thead>
                                    <tr style={{ background: 'var(--bg-tab)', position: 'sticky', top: 0, zIndex: 1 }}>
                                      <th style={{ ...thS, fontSize: 10 }}>Date</th>
                                      <th style={{ ...thS, fontSize: 10 }}>Cust.</th>
                                      <th style={{ ...thS, fontSize: 10 }}>Product</th>
                                      <th style={{ ...thS, textAlign: 'right', fontSize: 10 }}>Qty</th>
                                      <th style={{ ...thS, textAlign: 'right', fontSize: 10 }}>Amt</th>
                                      <th style={{ ...thS, textAlign: 'center', fontSize: 10 }}>Pay</th>
                                      <th style={{ ...thS, textAlign: 'center', fontSize: 10, color: '#DC2626' }}>↩Ret</th>
                                      <th style={{ ...thS, textAlign: 'center', fontSize: 10 }}>Act</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {reportSales.map((sale, i) => {
                                      const sRets = getSaleReturns(sale.id);
                                      const hasReturn = sRets.length > 0;
                                      const retQty = sRets.reduce((s, r) => s + parseFloat(r.qty || 0), 0);
                                      const retAmt = sRets.reduce((s, r) => s + parseFloat(r.refundAmt || 0), 0);
                                      const shortDate = sale.date ? sale.date.slice(2).replace(/-/g, '/') : '—';
                                      return (
                                        <tr key={sale.id} style={{ background: hasReturn ? (theme === 'dark' ? '#3A1D1D' : '#FFF5F5') : (i % 2 === 0 ? 'var(--bg-white)' : 'var(--bg-tab)'), borderBottom: '1px solid var(--border-color)', borderLeft: hasReturn ? '3px solid #EF4444' : sale.status === 'void' ? '3px solid #9CA3AF' : '3px solid transparent', opacity: sale.status === 'void' ? 0.5 : 1, textDecoration: sale.status === 'void' ? 'line-through' : 'none' }}>
                                          <td style={{ ...tdS }}>{shortDate}{sale.status === 'void' && <span style={{ marginLeft: 4, fontSize: 8, fontWeight: 800, padding: '1px 4px', borderRadius: 3, background: '#9CA3AF', color: '#fff', verticalAlign: 'middle', textDecoration: 'none', display: 'inline-block' }}>VOIDED</span>}</td>
                                          <td style={{ ...tdS }}>{sale.customerName || 'Walk-in'}</td>
                                          <td style={{ ...tdS }}>{getSaleProductCell(sale)}</td>
                                          <td style={{ ...tdS, textAlign: 'right' }}>{sale.items && sale.items.length > 1 ? sale.items.reduce((s, it) => s + parseFloat(it.quantity || 0), 0) : (sale.quantity || '—')}</td>
                                          <td style={{ ...tdS, textAlign: 'right', fontWeight: 700, color: '#059669' }}>{fmt(getSaleGrandTotal(sale))}</td>
                                          <td style={{ ...tdS, textAlign: 'center' }}>
                                            {(() => {
                                              // Credit doesn't count as money received -only cash/bank/mobilemoney
                                              const pmts = (sale.payments || []).filter(p => p.amount && parseFloat(p.amount) > 0 && p.type !== 'credit');
                                              const totalPaid = pmts.reduce((s, p) => s + parseFloat(p.amount || 0), 0);
                                              const isPartial = sale.isPaid === false && totalPaid > 0;
                                              const isUnpaid = sale.isPaid === false && totalPaid === 0;
                                              const isMulti = sale.isPaid !== false && pmts.length > 1;
                                              const label = isUnpaid ? 'Credit' : isPartial ? 'Partial Pmt' : 'Paid in Full';
                                              const bg = isUnpaid ? '#FEF3C7' : isPartial ? '#E0E7FF' : '#D1FAE5';
                                              const color = isUnpaid ? '#92400E' : isPartial ? '#3730A3' : '#065F46';
                                              return <span style={{ fontSize: 9, fontWeight: 600, padding: '2px 4px', borderRadius: 4, textTransform: 'capitalize', background: bg, color }}>{label}</span>;
                                            })()}
                                          </td>
                                          <td style={{ ...tdS, textAlign: 'center', fontSize: 10 }}>
                                            {hasReturn ? (
                                              <div>
                                                <div style={{ fontSize: 10, fontWeight: 700, color: '#DC2626' }}>↩{retQty}</div>
                                                <div style={{ fontSize: 9, color: '#DC2626' }}>-{fmt(retAmt)}</div>
                                              </div>
                                            ) : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                                          </td>
                                          <td style={{ ...tdS, textAlign: 'center', whiteSpace: 'nowrap' }}>
                                            <button onClick={() => setViewTxnModal({ open: true, kind: 'sale', record: sale })} style={{ background: 'var(--bg-input)', color: 'var(--text-secondary)', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 13, marginRight: 3 }} title={language === 'sw' ? 'Tazama' : 'View'}>View</button>
                                            <button onClick={() => handleEditSale(sale)} style={editBtnS}>✎️</button>
                                            {sale.status !== 'void' && <button onClick={() => handleVoidSale(sale.id)} style={{ ...delBtnS, background: '#FEF3C7', color: '#92400E' }} title={language === 'sw' ? 'Batilisha' : 'Void'}>⊘</button>}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                                </>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })()}

                  {/* === MAIN MAUZO DASHBOARD === */}
                  {!isSalesReportOpen && (
                    <div style={{ padding: 16, background: 'var(--bg-tab)', minHeight: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}>

                      {/* HEADER */}
                      <h1 style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 12px' }}>{t.sales} ({language === 'sw' ? 'Bidhaa' : 'Products'})</h1>

                      {/* TOP ACTION BUTTONS -neon for Record, emerald for Invoices */}
                      <div style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
                        {perms.createSale && <button onClick={() => setShowSalesForm(true)}
                          style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, height: 42, borderRadius: 26, background: '#A3E635', color: '#18222F', border: 'none', fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'pointer', boxShadow: '0 4px 14px rgba(163,230,53,0.35)' }}>
                          + {t.recordSale}
                        </button>}
                        <button onClick={() => setIsSalesReportOpen(true)}
                          style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, height: 42, borderRadius: 26, background: '#ECFDF5', color: '#065F46', border: '1.5px solid #A7F3D0', fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'pointer', boxShadow: '0 1px 3px rgba(15,23,42,0.04)' }}>
                          {t.viewSalesReports}
                        </button>
                        <button onClick={() => { setInvoiceMgmtContext('sales'); setShowInvoiceManagement(true); }}
                          style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, height: 42, borderRadius: 26, background: '#047857', color: '#FFFFFF', border: 'none', fontWeight: 700, fontSize: 11.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'pointer', boxShadow: '0 4px 14px rgba(4,120,87,0.35)', textAlign: 'center', lineHeight: 1.2 }}>
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
                          {t.invoiceManagementProducts || 'Invoice Management'}
                        </button>
                      </div>
                      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
                        <button onClick={() => setShowReturnsModal(true)}
                          style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, height: 44, borderRadius: 22, background: '#FEF3C7', color: '#92400E', border: '1.5px solid #FCD34D', fontWeight: 600, fontSize: 11.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'pointer' }}>
                          ↩ {t.salesReturns || 'Sales Returns'}
                        </button>
                      </div>

                      {/* ═══════════════════════════════════════════════════════════
                           SECTION 1 -SALES OVERVIEW (Option A: white card + green header band)
                      ═══════════════════════════════════════════════════════════ */}
                      <div style={{ background: 'var(--bg-white)', borderRadius: 16, overflow: 'hidden', marginBottom: 14, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', border: '0.5px solid var(--border-color)' }}>
                        {/* Section heading */}
                        <div style={{ padding: '12px 14px 4px' }}>
                          <h3 className="db-card-title" style={{ fontSize: 15, marginBottom: 0 }}>{t.salesOverview}</h3>
                        </div>

                        {/* Section 1 content */}
                        <div style={{ padding: 12 }}>

                          {/* PERIOD SELECTOR */}
                          <div style={{ background: 'var(--bg-tab)', borderRadius: 16, padding: 4, display: 'flex', gap: 2, marginBottom: 8 }}>
                            {[
                              { key: 'today',  label: t.today },
                              { key: 'week',   label: t.thisWeek },
                              { key: 'month',  label: t.thisMonth },
                              { key: 'year',   label: t.thisYear },
                              { key: 'custom', label: t.custom },
                            ].map(f => (
                              <button key={f.key} onClick={() => {
                                setSalesTimeFilter(f.key);
                                if (f.key === 'custom' && !salesCustomFrom && !salesCustomTo) {
                                  const n = new Date(); const y = n.getFullYear(); const m = String(n.getMonth()+1).padStart(2,'0');
                                  setSalesCustomFrom(`${y}-${m}-01`);
                                  setSalesCustomTo(`${y}-${m}-${String(new Date(y, n.getMonth()+1, 0).getDate()).padStart(2,'0')}`);
                                }
                              }}
                                style={{ flex: 1, padding: '10px 6px', borderRadius: 16, fontSize: 12, fontWeight: salesTimeFilter === f.key ? 700 : 600, cursor: 'pointer', border: 'none', whiteSpace: 'nowrap',
                                  background: salesTimeFilter === f.key ? 'var(--bg-tab-active)' : 'var(--bg-tab-inactive)',
                                  color: salesTimeFilter === f.key ? 'var(--text-tab-active)' : 'var(--text-tab-inactive)' }}>
                                {f.label}
                              </button>
                            ))}
                          </div>

                          {/* PERIOD DISPLAY LABEL OR CUSTOM DATE RANGE */}
                          {salesTimeFilter === 'custom' ? (
                            <div className="db-custom-date-card">
                                  <div className="db-custom-date-row">
                                <div className="db-custom-date-col">
                                  <span className="db-custom-date-label">{t.fromDate}</span>
                                  <input type="date" className="db-custom-date-input" value={salesCustomFrom} onChange={e => setSalesCustomFrom(e.target.value)} />
                                </div>
                                <div className="db-custom-date-col">
                                  <span className="db-custom-date-label">{t.toDate}</span>
                                  <input type="date" className="db-custom-date-input" value={salesCustomTo} onChange={e => setSalesCustomTo(e.target.value)} />
                                </div>
                              </div>
                            </div>
                          ) : periodLabel ? (
                            <div className="period-date-label">{periodLabel}</div>
                          ) : <div style={{ marginBottom: 8 }} />}

                          {/* SALES SUMMARY CARDS -match Purchases tab sizing/feel */}
                          <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'stretch' }}>
                            <div style={{ flex: 1, minWidth: 0, position: 'relative', display: 'flex' }}>
                              <div style={{ position: 'relative', zIndex: 2, background: 'linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)', borderRadius: 18, padding: '16px 14px', border: '1px solid #A7F3D0', boxShadow: '0 1px 4px rgba(5,150,105,0.10)', flex: 1, display: 'flex', flexDirection: 'column' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                                  <div style={{ width: 32, height: 32, borderRadius: 10, background: 'rgba(5,150,105,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" strokeLinecap="round"><polyline points="12 19 12 5"/><polyline points="5 12 12 5 19 12"/></svg>
                                  </div>
                                  <div style={{ fontSize: 11, fontWeight: 700, color: '#18222F', textTransform: 'uppercase', letterSpacing: 0.5 }}>{t.totalSales}</div>
                                </div>
                                <div style={{ fontSize: 'clamp(15px, 5.5vw, 22px)', fontWeight: 900, color: '#059669', marginBottom: 2, flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{fmt(totalRevenue)}</div>
                                <div style={{ fontSize: 10, fontWeight: 600, color: '#18222F', opacity: 0.7 }}>{periodLabel}</div>
                              </div>
                            </div>
                            <div style={{ flex: 1, minWidth: 0, position: 'relative', display: 'flex' }}>
                              <div style={{ position: 'relative', zIndex: 2, background: 'linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)', borderRadius: 18, padding: '16px 14px', border: '1px solid #A7F3D0', boxShadow: '0 1px 4px rgba(5,150,105,0.10)', flex: 1, display: 'flex', flexDirection: 'column' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                                  <div style={{ width: 32, height: 32, borderRadius: 10, background: 'rgba(5,150,105,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" strokeLinecap="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                                  </div>
                                  <div style={{ fontSize: 11, fontWeight: 700, color: '#18222F', textTransform: 'uppercase', letterSpacing: 0.5 }}>{t.salesCount}</div>
                                </div>
                                <div style={{ fontSize: 'clamp(15px, 5.5vw, 22px)', fontWeight: 900, color: '#059669', marginBottom: 2, flex: 1, whiteSpace: 'nowrap' }}>{filteredSales.length}</div>
                                <div style={{ fontSize: 10, fontWeight: 600, color: '#18222F', opacity: 0.7 }}>{periodLabel}</div>
                              </div>
                            </div>
                          </div>

                          {/* SALES BY PRODUCT */}
                          <div style={{ background: 'var(--bg-tab)', borderRadius: 14, padding: 14, marginBottom: 12 }}>
                            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 14 }}>{t.salesByProduct}</div>
                            {salesByProduct.length === 0 ? (
                              <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 13, padding: '16px 0' }}>{t.noDataForPeriod}</div>
                            ) : (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                {salesByProduct.slice(0, 5).map((item, i) => (
                                  <div key={item.name} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <span style={{ width: 80, fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 0 }}>{item.name}</span>
                                    <div style={{ flex: 1, height: 22, background: 'var(--bg-white)', borderRadius: 6, overflow: 'hidden' }}>
                                      <div style={{ height: '100%', width: `${maxBarRevenue > 0 ? (item.revenue / maxBarRevenue * 100) : 0}%`, background: MZ_COLORS[i % MZ_COLORS.length], borderRadius: 6, transition: 'width 0.6s ease' }}></div>
                                    </div>
                                    <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', minWidth: 36, textAlign: 'right' }}>{fmt(item.revenue)}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          {/* RECENT SALES */}
                          <div style={{ background: 'var(--bg-tab)', borderRadius: 14, padding: 14 }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, gap: 8, flexWrap: 'wrap' }}>
                              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{t.recentSales}</div>
                              {/* DISPLAY-ONLY toggle. Never affects any total above. */}
                              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', cursor: 'pointer' }}>
                                <input type="checkbox" checked={hideVoidedSales} onChange={e => setHideVoidedSales(e.target.checked)} style={{ accentColor: '#92400E' }} />
                                {language === 'sw' ? 'Ficha zilizobatilishwa' : 'Hide voided'}
                              </label>
                            </div>
                            {recentSalesList.length === 0 ? (
                              <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 13, padding: '16px 0' }}>{t.noDataForPeriod}</div>
                            ) : recentSalesList.map((sale, i) => {
                              const saleReturns = (salesReturns || []).filter(r => String(r.originalSaleId) === String(sale.id));
                              const returnedQty = saleReturns.reduce((s, r) => s + (parseFloat(r.qty) || 0), 0);
                              const returnedAmt = saleReturns.reduce((s, r) => s + (parseFloat(r.refundAmt) || 0), 0);
                              const isVoid = sale.status === 'void';
                              return (
                                <div key={sale.id} style={{ paddingBottom: i < recentSalesList.length - 1 ? 12 : 0, marginBottom: i < recentSalesList.length - 1 ? 12 : 0, borderBottom: i < recentSalesList.length - 1 ? '1px solid var(--border-color)' : 'none', opacity: isVoid ? 0.5 : 1, borderLeft: isVoid ? '3px solid #9CA3AF' : 'none', paddingLeft: isVoid ? 8 : 0 }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 3, textDecoration: isVoid ? 'line-through' : 'none' }}>
                                        {sale.items && sale.items.length > 0
                                          ? sale.items.map(it => it.productName || '—').join(', ')
                                          : sale.productName || '—'}
                                      </div>
                                      <div style={{ fontSize: 11, color: 'var(--text-muted)', textDecoration: isVoid ? 'line-through' : 'none' }}>{sale.customerName || t.walkIn} &middot; {sale.date}</div>
                                      {sale.invoiceNumber && <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 1 }}>{sale.invoiceNumber}</div>}
                                      {isVoid && (
                                        <div style={{ marginTop: 4, display: 'inline-block', fontSize: 9, fontWeight: 800, padding: '2px 6px', borderRadius: 4, background: '#9CA3AF', color: '#fff', letterSpacing: 0.3 }}>
                                          {language === 'sw' ? 'IMEBATILISHWA' : 'VOIDED'}
                                        </div>
                                      )}
                                      {isVoid && sale._voidUnsynced && (
                                        <div style={{ marginTop: 4, fontSize: 9, fontWeight: 700, color: '#B45309' }}>
                                          {language === 'sw' ? 'HAIJASAWAZISHWA NA SEVA' : 'NOT SYNCED TO SERVER'}
                                        </div>
                                      )}
                                    </div>
                                    <div style={{ marginLeft: 8, flexShrink: 0, textAlign: 'right' }}>
                                      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', textDecoration: isVoid ? 'line-through' : 'none' }}>{fmt(getSaleGrandTotal(sale))}</div>
                                      {returnedAmt > 0 && !isVoid && <div style={{ fontSize: 11, fontWeight: 600, color: '#DC2626' }}>-{fmt(returnedAmt)}</div>}
                                    </div>
                                  </div>
                                  {returnedQty > 0 && (
                                    <div style={{ marginTop: 5, display: 'inline-flex', alignItems: 'center', gap: 4, background: '#FEE2E2', borderRadius: 6, padding: '3px 8px' }}>
                                      <span style={{ fontSize: 11 }}>↩</span>
                                      <span style={{ fontSize: 11, fontWeight: 700, color: '#DC2626' }}>{returnedQty} {t.units || 'units'} {language === 'sw' ? 'zilirudishwa' : 'returned'}</span>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>

                        </div>
                      </div>{/* END SECTION 1 */}

                      {/* ═══════════════════════════════════════════════════════════
                           SECTION 2 -ANNUAL PERFORMANCE (Option A: white card + navy header band)
                      ═══════════════════════════════════════════════════════════ */}
                      <div style={{ background: 'var(--bg-white)', borderRadius: 16, overflow: 'hidden', marginBottom: 16, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', border: '0.5px solid var(--border-color)' }}>
                        {/* Section heading */}
                        <div style={{ padding: '12px 14px 4px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div>
                            <h3 className="db-card-title" style={{ fontSize: 15, marginBottom: 0 }}>{t.annualPerformance}</h3>
                          </div>
                          {/* Year selector */}
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3 }}>
                            <div style={{ fontSize: 8, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8 }}>{t.selectYear}</div>
                            <select value={salesAnnualYear} onChange={e => setSalesAnnualYear(parseInt(e.target.value))}
                              style={{ padding: '4px 10px', borderRadius: 8, border: '2px solid var(--border-color)', background: 'var(--bg-input)', fontSize: 14, fontWeight: 900, color: 'var(--text-primary)', cursor: 'pointer', outline: 'none', minWidth: 80, textAlign: 'center' }}>
                              {availableYears.map(y => <option key={y} value={y} style={{ background: 'var(--bg-card)', color: 'var(--text-primary)' }}>{y}</option>)}
                            </select>
                          </div>
                        </div>

                        {/* Section 2 content */}
                        <div style={{ padding: 12 }}>

                          {/* Annual total summary pill */}
                          <div style={{ background: theme === 'dark' ? 'rgba(22,163,74,0.15)' : 'rgba(22,163,74,0.07)', borderRadius: 12, padding: '10px 14px', marginBottom: 14, borderLeft: '4px solid #16A34A', borderTop: `1px solid rgba(22,163,74,0.2)`, borderRight: `1px solid rgba(22,163,74,0.2)`, borderBottom: `1px solid rgba(22,163,74,0.2)` }}>
                            <div style={{ fontSize: 11, color: theme === 'dark' ? '#86EFAC' : '#15803D', fontWeight: 700, marginBottom: 2 }}>{t.annualTotal} · {salesAnnualYear}</div>
                            <div style={{ fontSize: 22, fontWeight: 800, color: theme === 'dark' ? '#4ADE80' : '#16A34A' }}>{fmt(annualTotal)}</div>
                          </div>

                          {/* TREND CHART -original bezier design preserved */}
                          <div style={{ background: 'var(--bg-tab)', borderRadius: 14, padding: 14, marginBottom: 12 }}>
                            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 10 }}>Trend / {t.salesTrend}</div>
                            {(() => {
                              const W = 320, H = 180, PX = 8, PY = 28;
                              const pts = annualData.map((d, i) => ({
                                x: PX + i * (W - PX * 2) / 11,
                                y: H - PY - (maxAnnualRev > 0 ? (d.rev / maxAnnualRev) * (H - PY * 2) : 0)
                              }));
                              const bez = (p) => { let d = `M${p[0].x.toFixed(1)},${p[0].y.toFixed(1)}`; for (let i = 0; i < p.length-1; i++) { const a=p[Math.max(i-1,0)],b=p[i],c=p[i+1],e=p[Math.min(i+2,p.length-1)]; d+=` C${(b.x+(c.x-a.x)/4).toFixed(1)},${(b.y+(c.y-a.y)/4).toFixed(1)} ${(c.x-(e.x-b.x)/4).toFixed(1)},${(c.y-(e.y-b.y)/4).toFixed(1)} ${c.x.toFixed(1)},${c.y.toFixed(1)}`; } return d; };
                              const linePath = bez(pts);
                              const areaPath = `${linePath} L${pts[pts.length-1].x.toFixed(1)},${(H-PY).toFixed(1)} L${pts[0].x.toFixed(1)},${(H-PY).toFixed(1)} Z`;
                              const hi = salesChartHover;
                              const tipX = hi !== null ? Math.max(40, Math.min(pts[hi].x, W - 40)) : 0;
                              return (
                                <div style={{ overflow: 'visible' }}>
                                  <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 190, display: 'block', overflow: 'visible' }} preserveAspectRatio="none" onMouseLeave={() => setSalesChartHover(null)}>
                                    <defs>
                                      <linearGradient id="salesAreaGrad2" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#16A34A" stopOpacity="0.45" />
                                        <stop offset="100%" stopColor="#16A34A" stopOpacity="0.04" />
                                      </linearGradient>
                                    </defs>
                                    {[0.33,0.66].map((f,i) => <line key={i} x1={PX} y1={(PY+f*(H-PY*2)).toFixed(1)} x2={W-PX} y2={(PY+f*(H-PY*2)).toFixed(1)} stroke="var(--border-color)" strokeWidth="1" />)}
                                    <path d={areaPath} fill="url(#salesAreaGrad2)" />
                                    <path d={linePath} fill="none" stroke="#16A34A" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
                                    {pts.map((p, i) => { const x0 = i === 0 ? 0 : (pts[i-1].x+p.x)/2; const x1 = i === pts.length-1 ? W : (p.x+pts[i+1].x)/2; return <rect key={i} x={x0.toFixed(1)} y={0} width={(x1-x0).toFixed(1)} height={H} fill="transparent" style={{ cursor: 'crosshair' }} onMouseEnter={() => setSalesChartHover(i)} />; })}
                                    {hi !== null && (() => {
                                      const aboveFits = pts[hi].y - 26 >= PY;
                                      const tipRectY = aboveFits ? pts[hi].y - 26 : pts[hi].y + 8;
                                      const tipTextY = aboveFits ? pts[hi].y - 13 : pts[hi].y + 21;
                                      const tipRectX = Math.max(2, Math.min(tipX - 38, W - 78));
                                      const tipTextX = tipRectX + 38;
                                      return (
                                        <>
                                          <line x1={pts[hi].x.toFixed(1)} y1={PY} x2={pts[hi].x.toFixed(1)} y2={H-PY} stroke="#9CA3AF" strokeWidth="1" strokeDasharray="3,3" />
                                          <circle cx={pts[hi].x.toFixed(1)} cy={pts[hi].y.toFixed(1)} r="4.5" fill="#16A34A" stroke="#fff" strokeWidth="2" />
                                          <rect x={tipRectX.toFixed(1)} y={tipRectY.toFixed(1)} width="64" height="14" rx="4" fill="#166534" fillOpacity="0.85" />
                                          <text x={(tipRectX + 32).toFixed(1)} y={(parseFloat(tipRectY) + 10).toFixed(1)} textAnchor="middle" fill="#fff" fontSize="7" fontWeight="400">{fmt(annualData[hi].rev)}</text>
                                        </>
                                      );
                                    })()}
                                  </svg>
                                  <div style={{ display: 'flex', marginTop: 4 }}>
                                    {annualData.map((d, i) => (
                                      <div key={i} style={{ flex: 1, textAlign: 'center' }}>
                                        <span style={{ display: 'inline-block', background: hi === i ? 'var(--bg-white)' : 'var(--bg-tab)', borderRadius: 5, padding: '2px 3px', fontSize: 8, color: 'var(--text-primary)', fontWeight: 700 }}>{d.label}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              );
                            })()}
                          </div>

                          {/* TOP 5 FAST SELLERS */}
                          <div style={{ background: 'var(--bg-tab)', borderRadius: 14, padding: 14, marginBottom: 12 }}>
                            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 14 }}>★ {t.topSellingProducts}</div>
                            {annualTopSelling.length === 0 ? (
                              <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 13, padding: '16px 0' }}>{t.noDataForPeriod}</div>
                            ) : annualTopSelling.map((item, i) => (
                              <div key={item.name} style={{ display: 'flex', alignItems: 'center', gap: 10, paddingBottom: i < annualTopSelling.length - 1 ? 12 : 0, marginBottom: i < annualTopSelling.length - 1 ? 12 : 0, borderBottom: i < annualTopSelling.length - 1 ? '1px solid var(--border-color)' : 'none' }}>
                                <div style={{ width: 28, height: 28, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0,
                                  background: i === 0 ? '#FCD34D' : i === 1 ? 'var(--border-default)' : i === 2 ? '#FED7AA' : 'var(--bg-tab)', color: i === 0 || i === 2 ? '#7C2D12' : 'var(--text-primary)' }}>{i + 1}</div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</div>
                                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{item.units} {t.units}</div>
                                </div>
                                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                  <div style={{ fontSize: 13, fontWeight: 700, color: '#059669' }}>{fmt(item.revenue)}</div>
                                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{item.pct}%</div>
                                </div>
                              </div>
                            ))}
                          </div>

                          {/* BOTTOM 5 LOW UNIT SELLERS */}
                          <div style={{ background: 'var(--bg-tab)', borderRadius: 14, padding: 14, marginBottom: 12 }}>
                            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>↘ {t.lowSellingProducts}</div>
                            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 12 }}>{t.lowestSellersRevenueSub || ''}</div>
                            {annualLowSelling.length === 0 ? (
                              <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 13, padding: '16px 0' }}>{t.noDataForPeriod}</div>
                            ) : annualLowSelling.map((item, i) => (
                              <div key={item.name} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: i < annualLowSelling.length - 1 ? 12 : 0, marginBottom: i < annualLowSelling.length - 1 ? 12 : 0, borderBottom: i < annualLowSelling.length - 1 ? '1px solid var(--border-color)' : 'none' }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</div>
                                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{item.units} {t.units}</div>
                                </div>
                                <div style={{ fontSize: 13, fontWeight: 700, color: '#EF4444', marginLeft: 12, flexShrink: 0 }}>{item.revenue > 0 ? fmt(item.revenue) : '—'}</div>
                              </div>
                            ))}
                          </div>

                          {/* OVERALL PRODUCT PERFORMANCE */}
                          <div style={{ background: 'var(--bg-tab)', borderRadius: 14, padding: 14 }}>
                            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>{t.overallProductPerformance}</div>
                            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 14 }}>{t.overallProductSub} · {salesAnnualYear}</div>
                            {allProductsPerf.length === 0 ? (
                              <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 13, padding: '16px 0' }}>{t.noDataForPeriod}</div>
                            ) : (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                {allProductsPerf.map((item, i) => (
                                  <div key={item.name} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <span style={{ width: 80, fontSize: 11, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 0 }}>{item.name}</span>
                                    <div style={{ flex: 1, height: 20, background: 'var(--bg-white)', borderRadius: 5, overflow: 'hidden' }}>
                                      <div style={{ height: '100%', width: `${maxPerfRevenue > 0 ? (item.revenue / maxPerfRevenue * 100) : 0}%`, background: MZ_COLORS[i % MZ_COLORS.length], borderRadius: 5, transition: 'width 0.6s ease', minWidth: item.revenue > 0 ? 4 : 0 }}></div>
                                    </div>
                                    <span style={{ fontSize: 12, fontWeight: 700, color: item.revenue > 0 ? 'var(--text-primary)' : '#9CA3AF', minWidth: 40, textAlign: 'right' }}>{item.revenue > 0 ? fmt(item.revenue) : '—'}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                        </div>
                      </div>{/* END SECTION 2 */}

                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}

        {/* INVOICE MANAGEMENT SCREEN */}
        {showInvoiceManagement && (
          <div className="popup-overlay" style={{ zIndex: 1200 }}>
            <div className="popup-form" style={{ borderRadius: 16, padding: 24, maxHeight: '90vh', overflowY: 'auto' }}>
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <h3 style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>{t.invoiceManagement || 'Invoice Management'}</h3>
                <button onClick={() => { setShowInvoiceManagement(false); setInvoiceManagementSection(null); }}
                  style={{ background: 'none', border: 'none', fontSize: 24, color: 'var(--text-muted)', cursor: 'pointer', lineHeight: 1, padding: 0 }}>×</button>
              </div>

              {/* Main menu */}
              {!invoiceManagementSection && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

                  {/* ── PRE-SALE DOCUMENTS -shown in both contexts; lists are filtered by kind ── */}
                  <div style={{ border: '1px solid #BFDBFE', borderRadius: 14, overflow: 'hidden' }}>
                    <div style={{ background: 'var(--bg-input)', padding: '10px 14px', borderBottom: '1px solid #BFDBFE' }}>
                      <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text-secondary)' }}>
                        {invoiceMgmtContext === 'services' ? 'Pre-Service Documents' : 'Pre-Sale Documents'}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 1 }}>
                        {invoiceMgmtContext === 'services'
                          ? 'No service recorded until payment is received'
                          : 'No sale recorded until payment is received'}
                      </div>
                    </div>
                    {/* Proforma Invoices */}
                    <button onClick={() => setShowProformaListScreen(true)}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 14px', background: '#fff', border: 'none', borderBottom: '1px solid var(--border-default)', cursor: 'pointer', width: '100%', textAlign: 'left' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 30, height: 30, borderRadius: 8, background: 'var(--bg-input)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>Proforma Invoices</div>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Quotes &amp; estimates</div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#CBD5E1" strokeWidth="2.5" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>
                      </div>
                    </button>
                    {/* Manual Invoices */}
                    <button onClick={() => setShowManualInvoiceListScreen(true)}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 14px', background: '#fff', border: 'none', borderBottom: '1px solid var(--border-default)', cursor: 'pointer', width: '100%', textAlign: 'left' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 30, height: 30, borderRadius: 8, background: 'var(--bg-input)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>Manual Invoices</div>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Awaiting payment</div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#CBD5E1" strokeWidth="2.5" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>
                      </div>
                    </button>
                    {/* Create buttons */}
                    <div style={{ display: 'flex', gap: 8, padding: '10px 14px', background: '#F8FAFF' }}>
                      <button onClick={() => { setProformaFooterSections([{ label: 'Terms & Conditions', content: '' }]); setProformaValidityDays(7); setShowProformaForm(true); }}
                        style={{ flex: 1, padding: '7px', borderRadius: 8, background: 'var(--bg-input)', color: 'var(--text-secondary)', border: 'none', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                        + Proforma
                      </button>
                      <button onClick={() => { setShowInvoiceManagement(false); setInvoiceManagementSection(null); setActiveTab(invoiceMgmtContext === 'services' ? 'services' : 'sales'); if (invoiceMgmtContext === 'services') { setShowServiceTransactionForm(true); } else { setSaleSaveMode('invoice'); setSaleFromInvoiceManagement(true); setEditingManualInvoiceId(null); setShowSalesForm(true); } }}
                        style={{ flex: 1, padding: '7px', borderRadius: 8, background: 'var(--bg-input)', color: 'var(--text-secondary)', border: 'none', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                        + Invoice
                      </button>
                    </div>
                  </div>

                  {/* ── ISSUED INVOICES -context-aware ── */}
                  <div style={{ border: '1px solid #BBF7D0', borderRadius: 14, overflow: 'hidden' }}>
                    <div style={{ background: '#F0FDF4', padding: '10px 14px', borderBottom: '1px solid #BBF7D0' }}>
                      <div style={{ fontWeight: 700, fontSize: 13, color: '#065F46' }}>
                        {invoiceMgmtContext === 'services'
                          ? (language === 'sw' ? 'Ankara za Huduma' : 'Service Invoices')
                          : (language === 'sw' ? 'Ankara za Mauzo' : 'Sales Invoices')}
                      </div>
                      <div style={{ fontSize: 11, color: '#059669', marginTop: 1 }}>
                        {invoiceMgmtContext === 'services'
                          ? (language === 'sw' ? 'Huduma zilizotolewa -kwenye ripoti' : 'Confirmed services -recorded in reports')
                          : (language === 'sw' ? 'Mauzo yaliyothibitishwa -kwenye ripoti' : 'Confirmed sales -recorded in reports')}
                      </div>
                    </div>
                    <button onClick={() => setShowIssuedListScreen(true)}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 14px', background: '#fff', border: 'none', cursor: 'pointer', width: '100%', textAlign: 'left' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 30, height: 30, borderRadius: 8, background: '#F0FDF4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><polyline points="9 15 12 18 15 12"/></svg>
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>
                            {invoiceMgmtContext === 'services'
                              ? (language === 'sw' ? 'Ankara Zote za Huduma' : 'All Service Invoices')
                              : (language === 'sw' ? 'Ankara Zote za Mauzo' : 'All Sales Invoices')}
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                            {invoiceMgmtContext === 'services'
                              ? (language === 'sw' ? 'Huduma zilizorekodiwa na ankara' : 'Service records with SRV-XXXX numbers')
                              : (language === 'sw' ? 'Mauzo + ankara za mwongozo' : 'Record Sale + converted manual invoices')}
                          </div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#CBD5E1" strokeWidth="2.5" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>
                      </div>
                    </button>
                  </div>

                </div>
              )}

              {/* Proforma Invoices List -filtered by context kind */}
              {invoiceManagementSection === 'proforma' && (() => {
                const filteredProformas = (proformaInvoices || []).filter(p => {
                  const k = p.kind || (((p.items || []).some(it => it.type === 'service' || it.serviceName)) ? 'service' : 'sale');
                  return invoiceMgmtContext === 'services' ? k === 'service' : k !== 'service';
                });
                return (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                    <button onClick={() => setInvoiceManagementSection(null)}
                      style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 4 }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                    </button>
                    <h4 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>
                      {invoiceMgmtContext === 'services'
                        ? (language === 'sw' ? 'Proforma za Huduma' : 'Service Proformas')
                        : (t.proformaInvoices || 'Proforma Invoices')}
                    </h4>
                  </div>

                  {filteredProformas.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-muted)' }}>
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#D1D5DB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 12 }}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
                      <div style={{ fontSize: 14, fontWeight: 600 }}>{t.noProformaInvoices || 'No proforma invoices yet'}</div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {filteredProformas.map(proforma => (
                        <div key={proforma.id} style={{ background: 'var(--bg-card)', borderRadius: 12, padding: 14, border: '1px solid var(--border-color)' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                            <div>
                              <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)' }}>{proforma.proformaNumber}</div>
                              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{proforma.customerName}</div>
                            </div>
                            <div style={{ display: 'flex', gap: 6 }}>
                              <button onClick={() => setViewingProforma(proforma)}
                                style={{ padding: '6px 10px', borderRadius: 8, background: '#ECFDF5', color: '#065F46', border: 'none', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                                {t.view || 'View'}
                              </button>
                              <button onClick={() => { setEditingProformaId(proforma.id); setProformaData(proforma); setProformaCustomerType(proforma.customerType || 'walkin'); setProformaValidityDays(proforma.validityDays || 7); setProformaFooterSections(proforma.footerSections && proforma.footerSections.length > 0 ? proforma.footerSections : [{ label: 'Terms & Conditions', content: proforma.terms != null ? proforma.terms : '' }]); setShowProformaForm(true); }}
                                style={{ padding: '6px 10px', borderRadius: 8, background: 'var(--bg-input)', color: 'var(--text-secondary)', border: 'none', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                                {t.edit || 'Edit'}
                              </button>
                              <button onClick={() => showConfirm(
                                language === 'sw' ? 'Una uhakika unataka kufuta proforma hii? Hatua hii haiwezi kutenduliwa.' : 'Are you sure you want to permanently delete this proforma invoice? This cannot be undone.',
                                () => setProformaInvoices(prev => softDeleteById(prev, proforma.id)),
                                { tone: 'error', title: language === 'sw' ? 'Futa Proforma' : 'Delete Proforma' }
                              )}
                                style={{ padding: '6px 10px', borderRadius: 8, background: '#FEE2E2', color: '#DC2626', border: 'none', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                                {t.delete || 'Delete'}
                              </button>
                            </div>
                          </div>
                          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                            <div>{proforma.date}</div>
                            <div>{(proforma.items || []).length} {t.items || 'items'}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
                );
              })()}

              {/* Issued Invoices List */}
              {invoiceManagementSection === 'issued' && (() => {
                // Context-aware source: services context reads from serviceTransactions; sales context reads sales (excl. pure-service records)
                const rawList = invoiceMgmtContext === 'services'
                  ? (serviceTransactions || []).filter(st => st.invoiceNumber)
                  : (sales || []).filter(s => s.invoiceNumber).filter(s => {
                      // Exclude records that are pure-service (legacy data where services accidentally landed in sales)
                      if (s.isService === true) return false;
                      const itms = s.items || [];
                      if (itms.length > 0 && itms.every(it => it.type === 'service')) return false;
                      return true;
                    });
                return (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                    <button onClick={() => setInvoiceManagementSection(null)}
                      style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 4 }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                    </button>
                    <h4 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>
                      {invoiceMgmtContext === 'services'
                        ? (language === 'sw' ? 'Ankara za Huduma' : 'Service Invoices')
                        : (t.issuedInvoices || 'Issued Invoices')}
                    </h4>
                  </div>

                  {rawList.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-muted)' }}>
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#D1D5DB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 12 }}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
                      <div style={{ fontSize: 14, fontWeight: 600 }}>{t.noIssuedInvoices || 'No issued invoices yet'}</div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {rawList.map(invoice => (
                        <div key={invoice.id} style={{ background: 'var(--bg-card)', borderRadius: 12, padding: 14, border: '1px solid var(--border-color)' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                            <div>
                              <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)' }}>{invoice.invoiceNumber}</div>
                              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{invoice.customerName}</div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              <div style={{ fontWeight: 700, fontSize: 14, color: '#059669' }}>{fmt(invoice.totalRevenue)}</div>
                              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{invoice.date}</div>
                            </div>
                          </div>
                          <button onClick={() => {
                            // Services: map first item to legacy fields so PDF picks them up
                            const payload = invoiceMgmtContext === 'services'
                              ? { ...invoice, productName: invoice.serviceName, sellingPrice: invoice.price }
                              : invoice;
                            generateInvoicePDF(payload, getBizInfo(), paymentAccounts);
                          }}
                            style={{ width: '100%', padding: '8px 12px', borderRadius: 8, background: '#F0FDF4', color: '#059669', border: '1.5px solid #A7F3D0', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                            {t.download || 'Download PDF'}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </>
                );
              })()}
            </div>
          </div>
        )}

        {/* PROFORMA INVOICE LIST SCREEN -filtered by context kind */}
        {showProformaListScreen && (() => {
          const byKind = (proformaInvoices || []).filter(p => {
            const k = p.kind || (((p.items || []).some(it => it.type === 'service' || it.serviceName)) ? 'service' : 'sale');
            return invoiceMgmtContext === 'services' ? k === 'service' : k !== 'service';
          });
          const filtered = byKind.filter(p =>
            (p.proformaNumber || '').toLowerCase().includes(proformaSearchQuery.toLowerCase()) ||
            (p.customerName || '').toLowerCase().includes(proformaSearchQuery.toLowerCase())
          );
          const sorted = [...filtered].sort((a, b) => {
            const dateA = new Date(a.date || 0);
            const dateB = new Date(b.date || 0);
            const totalA = (a.items || []).reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
            const totalB = (b.items || []).reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
            if (proformaSortOption === 'dateDesc') return dateB - dateA;
            if (proformaSortOption === 'dateAsc') return dateA - dateB;
            if (proformaSortOption === 'amountDesc') return totalB - totalA;
            if (proformaSortOption === 'amountAsc') return totalA - totalB;
            return 0;
          });
          return (
            <div style={{ position: 'fixed', inset: 0, zIndex: 99999, background: 'var(--bg-tab)', display: 'flex', flexDirection: 'column' }}>
              <div style={{ background: '#fff', padding: '16px 20px', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <button onClick={() => setShowProformaListScreen(false)}
                    style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 4 }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                  </button>
                  <span style={{ fontWeight: 700, fontSize: 18, color: 'var(--text-primary)' }}>{t.proformaInvoices || 'Proforma Invoices'}</span>
                </div>
                <div style={{ fontSize: 14, color: 'var(--text-muted)' }}>{sorted.length} {t.invoices || 'invoices'}</div>
              </div>
              <div style={{ padding: '14px', maxWidth: 800, margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>
                {/* Add New */}
                <button onClick={() => { setEditingProformaId(null); resetProformaData(); setProformaFooterSections([{ label: 'Terms & Conditions', content: '' }]); setProformaValidityDays(7); setShowProformaListScreen(false); setShowProformaForm(true); }}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', padding: '11px 14px', borderRadius: 12, background: '#10B981', color: '#fff', border: 'none', fontSize: 14, fontWeight: 700, cursor: 'pointer', marginBottom: 14, boxShadow: '0 2px 6px rgba(16,185,129,0.3)' }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  {language === 'sw' ? 'Ongeza Proforma Mpya' : 'Add New Proforma'}
                </button>
                {/* Search and Sort */}
                <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
                  <input
                    type="text"
                    placeholder={t.search || 'Search by invoice number or customer name'}
                    value={proformaSearchQuery}
                    onChange={e => setProformaSearchQuery(e.target.value)}
                    style={{ flex: 1, padding: '8px 14px', borderRadius: 12, border: '1px solid var(--border-color)', background: '#fff', fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
                  />
                  <select
                    value={proformaSortOption}
                    onChange={e => setProformaSortOption(e.target.value)}
                    style={{ padding: '8px 14px', borderRadius: 12, border: '1px solid var(--border-color)', background: '#fff', fontSize: 14, outline: 'none', cursor: 'pointer' }}
                  >
                    <option value="dateDesc">{t.newestFirst || 'Newest first'}</option>
                    <option value="dateAsc">{t.oldestFirst || 'Oldest first'}</option>
                    <option value="amountDesc">{t.highestAmount || 'Highest amount'}</option>
                    <option value="amountAsc">{t.lowestAmount || 'Lowest amount'}</option>
                  </select>
                </div>
                {/* List */}
                {sorted.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-muted)' }}>
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#D1D5DB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 16 }}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
                    <div style={{ fontSize: 16, fontWeight: 600 }}>{proformaSearchQuery ? t.noResults || 'No results' : t.noProformaInvoices || 'No proforma invoices yet'}</div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {sorted.map(proforma => {
                      const total = (proforma.items || []).reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
                      return (
                        <div key={proforma.id} style={{ background: '#fff', borderRadius: 12, padding: '12px 14px', border: '1px solid var(--border-color)', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                            <div style={{ minWidth: 0, flex: 1, marginRight: 8 }}>
                              <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)', marginBottom: 2 }}>{proforma.proformaNumber}</div>
                              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{proforma.customerName}</div>
                              {proforma.customerAddress ? <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>{proforma.customerAddress}</div> : null}
                            </div>
                            <div style={{ textAlign: 'right', flexShrink: 0 }}>
                              <div style={{ fontWeight: 800, fontSize: 16, color: '#059669' }}>{fmt(total)}</div>
                              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>{proforma.date}</div>
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: 5 }}>
                            <button onClick={() => setViewingProforma(proforma)}
                              style={{ flex: 1, padding: '5px 4px', borderRadius: 6, background: '#ECFDF5', color: '#065F46', border: 'none', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                              {t.view || 'View'}
                            </button>
                            <button onClick={() => { setEditingProformaId(proforma.id); setProformaData(proforma); setProformaCustomerType(proforma.customerType || 'walkin'); setProformaValidityDays(proforma.validityDays || 7); setProformaFooterSections(proforma.footerSections && proforma.footerSections.length > 0 ? proforma.footerSections : [{ label: 'Terms & Conditions', content: proforma.terms != null ? proforma.terms : '' }]); setShowProformaForm(true); setShowProformaListScreen(false); }}
                              style={{ flex: 1, padding: '5px 4px', borderRadius: 6, background: '#F3F4F6', color: 'var(--text-secondary)', border: 'none', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                              {t.edit || 'Edit'}
                            </button>
                            <button onClick={() => { generateProformaPDF(proforma, getBizInfo(), paymentAccounts); }}
                              style={{ flex: 1, padding: '5px 4px', borderRadius: 6, background: '#F3F4F6', color: 'var(--text-secondary)', border: 'none', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                              {t.download || 'Download'}
                            </button>
                            <button onClick={() => showConfirm(
                              language === 'sw' ? 'Una uhakika unataka kufuta proforma hii? Hatua hii haiwezi kutenduliwa.' : 'Are you sure you want to permanently delete this proforma invoice? This cannot be undone.',
                              () => { const updated = softDeleteById(proformaInvoicesRaw, proforma.id); setProformaInvoices(updated); localStorage.setItem(getUserKey('proformaInvoices'), JSON.stringify(updated)); },
                              { tone: 'error', title: language === 'sw' ? 'Futa Proforma' : 'Delete Proforma' }
                            )}
                              style={{ flex: 1, padding: '5px 4px', borderRadius: 6, background: '#FEF2F2', color: '#DC2626', border: 'none', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                              {t.delete || 'Delete'}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* PROFORMA VIEW MODAL -read-only preview of a single proforma */}
        {viewingProforma && (() => {
          const pv = viewingProforma;
          const sw = language === 'sw';
          const items = pv.items || [];
          const subtotal = items.reduce((s, it) => s + (parseFloat(it.total || '') || parseFloat(it.quantity || 0) * parseFloat(it.unitPrice || 0)), 0);
          const discPct = parseFloat(pv.discount || 0);
          const discAmt = subtotal * (discPct / 100);
          const afterDisc = Math.max(0, subtotal - discAmt);
          const taxes = pv.taxes || [];
          let totalTax = 0;
          taxes.forEach(tx => { totalTax += afterDisc * (parseFloat(tx.rate) || 0) / 100; });
          const grandTotal = afterDisc + totalTax;
          const today = new Date(); today.setHours(0,0,0,0);
          const expDate = pv.expiryDate ? new Date(pv.expiryDate) : null;
          const isActive = !expDate || expDate >= today;
          return (
            <div onClick={() => setViewingProforma(null)}
              style={{ position: 'fixed', inset: 0, zIndex: 100000, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
              <div onClick={e => e.stopPropagation()}
                style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 720, maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
                {/* Header */}
                <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border-default)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#1A4B3A', borderRadius: '14px 14px 0 0' }}>
                  <div>
                    <div style={{ fontSize: 11, color: '#A7D5C0', fontWeight: 700, letterSpacing: 0.5 }}>{sw ? 'PROFORMA' : 'PROFORMA'}</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: '#fff', marginTop: 2 }}>{pv.proformaNumber || '—'}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {!isActive && (
                      <span style={{ padding: '4px 10px', borderRadius: 12, background: '#FEE2E2', color: '#991B1B', fontWeight: 700, fontSize: 11 }}>
                        {sw ? 'IMEISHA' : 'EXPIRED'}
                      </span>
                    )}
                    <button onClick={() => setViewingProforma(null)}
                      style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', width: 30, height: 30, borderRadius: 15, fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                  </div>
                </div>

                {/* Scrollable body -tight */}
                <div style={{ overflow: 'auto', padding: '12px 14px', flex: 1 }}>
                  {/* Customer + dates row */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 8, marginBottom: 10, paddingBottom: 8, borderBottom: '1px solid var(--border-default)' }}>
                    <div>
                      <div style={{ fontSize: 9, fontWeight: 700, color: '#1A4B3A', textTransform: 'uppercase', marginBottom: 2, letterSpacing: 0.3 }}>{sw ? 'Mteja' : 'Bill To'}</div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.2 }}>{pv.customerName || '—'}</div>
                      {pv.customerPhone && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1, lineHeight: 1.2 }}>{pv.customerPhone}</div>}
                      {pv.customerEmail && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1, lineHeight: 1.2 }}>{pv.customerEmail}</div>}
                      {pv.customerAddress && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1, lineHeight: 1.2 }}>{pv.customerAddress}</div>}
                      {pv.customerTIN && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1, lineHeight: 1.2 }}>TIN: {pv.customerTIN}</div>}
                      {pv.customerVRN && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1, lineHeight: 1.2 }}>VRN: {pv.customerVRN}</div>}
                    </div>
                    <div>
                      <div style={{ fontSize: 9, fontWeight: 700, color: '#1A4B3A', textTransform: 'uppercase', marginBottom: 2, letterSpacing: 0.3 }}>{sw ? 'Tarehe' : 'Issue Date'}</div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.2 }}>{pv.date || '—'}</div>
                      <div style={{ fontSize: 9, fontWeight: 700, color: '#1A4B3A', textTransform: 'uppercase', marginTop: 4, marginBottom: 2, letterSpacing: 0.3 }}>{sw ? 'Uhalali' : 'Validity'}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-primary)', fontWeight: 600, lineHeight: 1.2 }}>{pv.validityDays || 30} {sw ? 'siku' : 'days'}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 9, fontWeight: 700, color: '#1A4B3A', textTransform: 'uppercase', marginBottom: 2, letterSpacing: 0.3 }}>{sw ? 'Inakwisha' : 'Valid Until'}</div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.2 }}>{pv.expiryDate || '—'}</div>
                    </div>
                  </div>

                  {/* Items table */}
                  <div style={{ border: '1px solid var(--border-default)', borderRadius: 6, overflow: 'hidden', marginBottom: 10 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '28px 1fr 50px 80px 90px', gap: 0, background: '#1A4B3A', color: '#fff', fontSize: 9, fontWeight: 700, textTransform: 'uppercase', padding: '6px 10px', letterSpacing: 0.3 }}>
                      <div>#</div>
                      <div>{sw ? 'Bidhaa' : 'Description'}</div>
                      <div style={{ textAlign: 'right' }}>{sw ? 'Idadi' : 'Qty'}</div>
                      <div style={{ textAlign: 'right' }}>{sw ? 'Bei' : 'Unit Price'}</div>
                      <div style={{ textAlign: 'right' }}>{sw ? 'Jumla' : 'Line Total'}</div>
                    </div>
                    {items.length === 0 ? (
                      <div style={{ padding: 12, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>{sw ? 'Hakuna vipengele' : 'No items'}</div>
                    ) : items.map((it, i) => {
                      const lineTotal = parseFloat(it.total || '') || (parseFloat(it.quantity || 0) * parseFloat(it.unitPrice || 0));
                      return (
                        <div key={i} style={{ display: 'grid', gridTemplateColumns: '28px 1fr 50px 80px 90px', gap: 0, padding: '5px 10px', borderTop: '1px solid #F3F4F6', fontSize: 12, color: 'var(--text-primary)', background: i % 2 === 0 ? 'var(--bg-card)' : 'var(--bg-main)' }}>
                          <div style={{ color: 'var(--text-muted)' }}>{i + 1}</div>
                          <div style={{ wordBreak: 'break-word' }}>{it.productName || '—'}</div>
                          <div style={{ textAlign: 'right' }}>{it.quantity || 0}</div>
                          <div style={{ textAlign: 'right' }}>{fmt(it.unitPrice)}</div>
                          <div style={{ textAlign: 'right', fontWeight: 700 }}>{fmt(lineTotal)}</div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Totals */}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                    <div style={{ minWidth: 220, fontSize: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                        <span style={{ color: 'var(--text-muted)' }}>{sw ? 'Jumla Ndogo' : 'Subtotal'}</span>
                        <span style={{ color: 'var(--text-primary)' }}>{fmt(subtotal)}</span>
                      </div>
                      {discAmt > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                          <span style={{ color: 'var(--text-muted)' }}>{sw ? 'Punguzo' : 'Discount'} ({discPct}%)</span>
                          <span style={{ color: '#DC2626' }}>-{fmt(discAmt)}</span>
                        </div>
                      )}
                      {taxes.map((tx, i) => {
                        const rate = parseFloat(tx.rate) || 0;
                        const amt = afterDisc * rate / 100;
                        const lbl = tx.type === 'vat' ? `VAT (${rate}%)` : tx.type === 'withholding' ? `WHT (${rate}%)` : `Tax (${rate}%)`;
                        return (
                          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                            <span style={{ color: 'var(--text-muted)' }}>{lbl}</span>
                            <span style={{ color: 'var(--text-primary)' }}>{fmt(amt)}</span>
                          </div>
                        );
                      })}
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', marginTop: 4, background: '#1A4B3A', color: '#fff', borderRadius: 5, fontWeight: 800, fontSize: 14 }}>
                        <span>{sw ? 'JUMLA' : 'TOTAL'}</span>
                        <span>{fmt(grandTotal)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Footer sections (terms etc.) */}
                  {(pv.footerSections || []).filter(s => s.content && s.content.trim()).map((sec, i) => {
                    const lbl = (sec.label === 'Custom' && sec.customLabel) ? sec.customLabel : (sec.label || 'Section');
                    return (
                      <div key={i} style={{ marginTop: 8, padding: '8px 10px', background: 'var(--bg-tab)', borderRadius: 6, borderLeft: '3px solid #1A4B3A' }}>
                        <div style={{ fontSize: 9, fontWeight: 700, color: '#1A4B3A', textTransform: 'uppercase', marginBottom: 3, letterSpacing: 0.3 }}>{lbl}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-secondary)', whiteSpace: 'pre-wrap', lineHeight: 1.35 }}>{sec.content}</div>
                      </div>
                    );
                  })}
                </div>

                {/* Footer actions */}
                <div style={{ padding: '12px 18px', borderTop: '1px solid var(--border-default)', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button onClick={() => setViewingProforma(null)}
                    style={{ padding: '8px 16px', borderRadius: 8, background: '#F3F4F6', color: 'var(--text-secondary)', border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                    {sw ? 'Funga' : 'Close'}
                  </button>
                  <button onClick={() => { generateProformaPDF(pv, getBizInfo(), paymentAccounts); }}
                    style={{ padding: '8px 16px', borderRadius: 8, background: '#1A4B3A', color: '#fff', border: 'none', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                    {sw ? 'Pakua PDF' : 'Download PDF'}
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* ISSUED INVOICE LIST SCREEN -context-aware */}
        {showIssuedListScreen && (() => {
          // Source records depend on which tab opened Invoice Management
          const sourceRecords = invoiceMgmtContext === 'services'
            ? (serviceTransactions || []).filter(st => st.invoiceNumber)
            : (sales || []).filter(s => s.invoiceNumber).filter(s => {
                if (s.isService === true) return false;
                const itms = s.items || [];
                if (itms.length > 0 && itms.every(it => it.type === 'service')) return false;
                return true;
              });
          const filtered = sourceRecords.filter(i =>
            (i.invoiceNumber || '').toLowerCase().includes(issuedSearchQuery.toLowerCase()) ||
            (i.customerName || '').toLowerCase().includes(issuedSearchQuery.toLowerCase())
          );
          const sorted = [...filtered].sort((a, b) => {
            const dateA = new Date(a.date || 0);
            const dateB = new Date(b.date || 0);
            const totalA = parseFloat(a.totalRevenue || 0);
            const totalB = parseFloat(b.totalRevenue || 0);
            if (issuedSortOption === 'dateDesc') return dateB - dateA;
            if (issuedSortOption === 'dateAsc') return dateA - dateB;
            if (issuedSortOption === 'amountDesc') return totalB - totalA;
            if (issuedSortOption === 'amountAsc') return totalA - totalB;
            return 0;
          });
          return (
            <div style={{ position: 'fixed', inset: 0, zIndex: 99999, background: 'var(--bg-tab)', display: 'flex', flexDirection: 'column' }}>
              <div style={{ background: '#fff', padding: '16px 20px', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <button onClick={() => setShowIssuedListScreen(false)}
                    style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 4 }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                  </button>
                  <span style={{ fontWeight: 700, fontSize: 18, color: 'var(--text-primary)' }}>
                    {invoiceMgmtContext === 'services'
                      ? (language === 'sw' ? 'Ankara za Huduma' : 'Service Invoices')
                      : (t.issuedInvoices || 'Issued Invoices')}
                  </span>
                </div>
                <div style={{ fontSize: 14, color: 'var(--text-muted)' }}>{sorted.length} {t.invoices || 'invoices'}</div>
              </div>
              <div style={{ padding: '14px', maxWidth: 800, margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>
                {/* Add New -opens the Sales OR Service form depending on context */}
                <button onClick={() => {
                  setShowIssuedListScreen(false); setShowInvoiceManagement(false); setInvoiceManagementSection(null);
                  if (invoiceMgmtContext === 'services') {
                    setActiveTab('services');
                    setShowServiceTransactionForm(true);
                  } else {
                    setActiveTab('sales'); setSaleSaveMode('paid'); setSaleFromInvoiceManagement(true); setEditingManualInvoiceId(null); setShowSalesForm(true);
                  }
                }}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', padding: '11px 14px', borderRadius: 12, background: '#10B981', color: '#fff', border: 'none', fontSize: 14, fontWeight: 700, cursor: 'pointer', marginBottom: 14, boxShadow: '0 2px 6px rgba(16,185,129,0.3)' }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  {invoiceMgmtContext === 'services'
                    ? (language === 'sw' ? 'Rekodi Huduma Mpya' : 'Record New Service')
                    : (language === 'sw' ? 'Ongeza Ankara Mpya' : 'Add New Invoice')}
                </button>
                {/* Search and Sort */}
                <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
                  <input
                    type="text"
                    placeholder={t.search || 'Search by invoice number or customer name'}
                    value={issuedSearchQuery}
                    onChange={e => setIssuedSearchQuery(e.target.value)}
                    style={{ flex: 1, padding: '8px 14px', borderRadius: 12, border: '1px solid var(--border-color)', background: '#fff', fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
                  />
                  <select
                    value={issuedSortOption}
                    onChange={e => setIssuedSortOption(e.target.value)}
                    style={{ padding: '8px 14px', borderRadius: 12, border: '1px solid var(--border-color)', background: '#fff', fontSize: 14, outline: 'none', cursor: 'pointer' }}
                  >
                    <option value="dateDesc">{t.newestFirst || 'Newest first'}</option>
                    <option value="dateAsc">{t.oldestFirst || 'Oldest first'}</option>
                    <option value="amountDesc">{t.highestAmount || 'Highest amount'}</option>
                    <option value="amountAsc">{t.lowestAmount || 'Lowest amount'}</option>
                  </select>
                </div>
                {/* List */}
                {sorted.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-muted)' }}>
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#D1D5DB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 16 }}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
                    <div style={{ fontSize: 16, fontWeight: 600 }}>{issuedSearchQuery ? t.noResults || 'No results' : t.noIssuedInvoices || 'No issued invoices yet'}</div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {sorted.map(invoice => (
                      <div key={invoice.id} style={{ background: '#fff', borderRadius: 12, padding: '12px 14px', border: '1px solid var(--border-color)', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                          <div style={{ minWidth: 0, flex: 1, marginRight: 8 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2, flexWrap: 'wrap' }}>
                              <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)' }}>{invoice.invoiceNumber}</div>
                              {(() => {
                                // Credit doesn't count as actual money received
                                const paid = (invoice.payments || []).filter(p => p.type !== 'credit').reduce((s, p) => s + parseFloat(p.amount || 0), 0);
                                const total = parseFloat(invoice.totalRevenue || 0);
                                const isPaidFull = invoice.isPaid === true && paid >= total;
                                const isPartial = !isPaidFull && paid > 0;
                                return (
                                  <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 20, whiteSpace: 'nowrap',
                                    background: '#F3F4F6',
                                    color: isPaidFull ? '#065F46' : isPartial ? '#3730A3' : '#9B1C1C' }}>
                                    {isPaidFull ? 'Paid in Full' : isPartial ? 'Partially Paid' : 'Unpaid'}
                                  </span>
                                );
                              })()}
                            </div>
                            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{invoice.customerName}</div>
                          </div>
                          <div style={{ textAlign: 'right', flexShrink: 0 }}>
                            <div style={{ fontWeight: 800, fontSize: 16, color: '#059669' }}>{fmt(invoice.totalRevenue)}</div>
                            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>{invoice.date}</div>
                          </div>
                        </div>
                        {/* Record Payment button for unpaid/partial (sales context only -services use full edit flow) */}
                        {invoiceMgmtContext !== 'services' && invoice.isPaid !== true && (() => {
                          const paid = (invoice.payments || []).reduce((s, p) => s + parseFloat(p.amount || 0), 0);
                          const total = parseFloat(invoice.totalRevenue || 0);
                          if (paid >= total && total > 0) return null;
                          const sale = (sales || []).find(s => s.id === invoice.id);
                          if (!sale) return null;
                          return (
                            <button onClick={() => { setPartialPaySale(sale); payAmtRef.current = ''; setPayDate(''); setPayRows([{ id: 1, type: 'cash', amountRef: { current: '' } }]); setShowPartialPayModal(true); }}
                              style={{ width: '100%', padding: '5px 10px', borderRadius: 6, background: 'var(--border-default)', color: '#DC2626', border: 'none', borderBottom: '2px solid #D1D5DB', fontSize: 11, fontWeight: 700, cursor: 'pointer', marginBottom: 5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, boxSizing: 'border-box' }}>
                              Record Payment · {fmt(total - paid)} remaining
                            </button>
                          );
                        })()}
                        <div style={{ display: 'flex', gap: 5 }}>
                          <button onClick={() => setInvoicePreviewModal({ open: true, invoice })}
                            style={{ flex: 1, padding: '5px 4px', borderRadius: 6, background: '#F3F4F6', color: 'var(--text-secondary)', border: 'none', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                            View
                          </button>
                          <button onClick={() => {
                            if (invoiceMgmtContext === 'services') {
                              handleServiceTransactionEdit(invoice);
                              setShowIssuedListScreen(false); setShowInvoiceManagement(false); setInvoiceManagementSection(null);
                              setActiveTab('services');
                            } else {
                              const sale = (sales || []).find(s => s.id === invoice.id);
                              if (sale) { setActiveTab('sales'); handleEditSale(sale); setShowIssuedListScreen(false); setShowInvoiceManagement(false); setInvoiceManagementSection(null); }
                            }
                          }}
                            style={{ flex: 1, padding: '5px 4px', borderRadius: 6, background: '#F3F4F6', color: 'var(--text-secondary)', border: 'none', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                            Edit
                          </button>
                          <button onClick={() => {
                            const payload = invoiceMgmtContext === 'services'
                              ? { ...invoice, productName: invoice.serviceName, sellingPrice: invoice.price }
                              : invoice;
                            generateInvoicePDF(payload, getBizInfo(), paymentAccounts);
                          }}
                            style={{ flex: 1, padding: '5px 4px', borderRadius: 6, background: '#F3F4F6', color: 'var(--text-secondary)', border: 'none', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                            Download
                          </button>
                          <button onClick={() => showConfirm(
                            language === 'sw' ? 'Una uhakika unataka kufuta invoice hii na mauzo yake yote? Hatua hii haiwezi kutenduliwa.' : 'Are you sure you want to permanently delete this invoice and its sale transaction? This cannot be undone.',
                            () => {
                              // C3: soft-delete (tombstone) via every delete path,
                              // including this invoice-management one, so a sale/
                              // service deleted here also cannot resurrect. Persistence
                              // is handled by the state effects (which write the raw,
                              // tombstoned arrays).
                              if (invoiceMgmtContext === 'services') {
                                setServiceTransactions(prev => softDeleteById(prev, invoice.id));
                              } else {
                                setSales(prev => softDeleteById(prev, invoice.id));
                                setIssuedInvoices(prev => softDeleteById(prev, invoice.id));
                              }
                            }, { tone: 'error', title: language === 'sw' ? 'Futa Invoice' : 'Delete Invoice' })}
                            style={{ flex: 1, padding: '5px 4px', borderRadius: 6, background: '#F3F4F6', color: '#DC2626', border: 'none', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* MANUAL INVOICE LIST SCREEN */}
        {showManualInvoiceListScreen && (() => {
          const byKind = (manualInvoices || []).filter(i => {
            const k = i.kind || (((i.items || []).some(it => it.type === 'service' || it.serviceName)) ? 'service' : 'sale');
            return invoiceMgmtContext === 'services' ? k === 'service' : k !== 'service';
          });
          const filtered = byKind.filter(i =>
            (i.invoiceNumber || '').toLowerCase().includes(manualInvoiceSearchQuery.toLowerCase()) ||
            (i.customerName || '').toLowerCase().includes(manualInvoiceSearchQuery.toLowerCase())
          );
          const sorted = [...filtered].sort((a, b) => {
            const dA = new Date(a.date || 0), dB = new Date(b.date || 0);
            const tA = parseFloat(a.totalRevenue || 0), tB = parseFloat(b.totalRevenue || 0);
            if (manualInvoiceSortOption === 'dateAsc') return dA - dB;
            if (manualInvoiceSortOption === 'amountDesc') return tB - tA;
            if (manualInvoiceSortOption === 'amountAsc') return tA - tB;
            return dB - dA;
          });
          return (
            <div style={{ position: 'fixed', inset: 0, zIndex: 99999, background: 'var(--bg-tab)', display: 'flex', flexDirection: 'column' }}>
              <div style={{ background: '#fff', padding: '16px 20px', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <button onClick={() => setShowManualInvoiceListScreen(false)}
                    style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 4 }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                  </button>
                  <span style={{ fontWeight: 700, fontSize: 18, color: 'var(--text-primary)' }}>Manual Invoices</span>
                </div>
                <div style={{ fontSize: 14, color: 'var(--text-muted)' }}>{sorted.length} invoices</div>
              </div>
              <div style={{ padding: '14px', maxWidth: 800, margin: '0 auto', width: '100%', boxSizing: 'border-box', overflowY: 'auto', flex: 1 }}>
                {/* Add New -opens the dedicated proforma-style Manual Invoice form */}
                <button onClick={() => { setEditingManualInvoiceId(null); resetManualInvoiceData(); setShowManualInvoiceListScreen(false); setShowManualInvoiceForm(true); }}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', padding: '11px 14px', borderRadius: 12, background: '#10B981', color: '#fff', border: 'none', fontSize: 14, fontWeight: 700, cursor: 'pointer', marginBottom: 14, boxShadow: '0 2px 6px rgba(16,185,129,0.3)' }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  {language === 'sw' ? 'Ongeza Ankara Mpya' : 'Add New Manual Invoice'}
                </button>
                <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                  <input type="text" placeholder="Search by invoice number or customer"
                    value={manualInvoiceSearchQuery} onChange={e => setManualInvoiceSearchQuery(e.target.value)}
                    style={{ flex: 1, padding: '8px 14px', borderRadius: 12, border: '1px solid var(--border-color)', background: '#fff', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
                  <select value={manualInvoiceSortOption} onChange={e => setManualInvoiceSortOption(e.target.value)}
                    style={{ padding: '8px 14px', borderRadius: 12, border: '1px solid var(--border-color)', background: '#fff', fontSize: 14, outline: 'none', cursor: 'pointer' }}>
                    <option value="dateDesc">Newest first</option>
                    <option value="dateAsc">Oldest first</option>
                    <option value="amountDesc">Highest amount</option>
                    <option value="amountAsc">Lowest amount</option>
                  </select>
                </div>
                {sorted.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-muted)' }}>
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#D1D5DB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 16 }}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                    <div style={{ fontSize: 16, fontWeight: 600 }}>{manualInvoiceSearchQuery ? 'No results' : 'No manual invoices yet'}</div>
                    <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 6 }}>Create one from Invoice Management</div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {sorted.map(invoice => {
                      const paid = (invoice.payments || []).reduce((s, p) => s + parseFloat(p.amount || 0), 0);
                      const total = parseFloat(invoice.totalRevenue || 0);
                      const isPartial = paid > 0 && paid < total;
                      return (
                        <div key={invoice.id} style={{ background: '#fff', borderRadius: 12, padding: '12px 14px', border: '1px solid var(--border-color)', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                            <div style={{ minWidth: 0, flex: 1, marginRight: 8 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2, flexWrap: 'wrap' }}>
                                <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)' }}>{invoice.invoiceNumber}</div>
                                <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 20, background: '#F3F4F6',
                                  color: isPartial ? '#9B1C1C' : '#9B1C1C' }}>
                                  {isPartial ? 'Partially Paid' : 'Unpaid'}
                                </span>
                              </div>
                              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{invoice.customerName}</div>
                            </div>
                            <div style={{ textAlign: 'right', flexShrink: 0 }}>
                              <div style={{ fontWeight: 800, fontSize: 16, color: '#059669' }}>{fmt(total)}</div>
                              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>{invoice.date}</div>
                            </div>
                          </div>
                          {/* Record Payment button */}
                          <button onClick={() => { setPartialPaySale(invoice); payAmtRef.current = ''; setPayDate(''); setPayRows([{ id: 1, type: 'cash', amountRef: { current: '' } }]); setShowPartialPayModal(true); }}
                            style={{ width: '100%', padding: '5px 10px', borderRadius: 6, background: 'var(--border-default)', color: '#DC2626', border: 'none', borderBottom: '2px solid #D1D5DB', fontSize: 11, fontWeight: 700, cursor: 'pointer', marginBottom: 5, boxSizing: 'border-box' }}>
                            Record Payment · {fmt(total - paid)} remaining
                          </button>
                          <div style={{ display: 'flex', gap: 5 }}>
                            <button onClick={() => setInvoicePreviewModal({ open: true, invoice })}
                              style={{ flex: 1, padding: '5px 4px', borderRadius: 6, background: '#F3F4F6', color: 'var(--text-secondary)', border: 'none', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>View</button>
                            <button onClick={() => {
                              setEditingManualInvoiceId(invoice.id);
                              // Hydrate the dedicated Manual Invoice form
                              const cd = invoice.customerDetails || {};
                              setManualInvoiceData({
                                customerName: invoice.customerName || '',
                                customerPhone: cd.phone || '',
                                customerEmail: cd.email || '',
                                customerAddress: cd.address || '',
                                customerTIN: cd.tin || '',
                                customerVRN: cd.vrn || '',
                                date: invoice.date || new Date().toISOString().split('T')[0],
                                discount: invoice.discount || '',
                                taxes: invoice.taxes || [],
                                items: (invoice.items && invoice.items.length > 0
                                  ? invoice.items.map(it => ({ productName: it.productName, quantity: it.quantity, unitPrice: it.sellingPrice, total: String((parseFloat(it.quantity)||0) * (parseFloat(it.sellingPrice)||0)) }))
                                  : [{ productName: invoice.productName || '', quantity: invoice.quantity || '', unitPrice: invoice.sellingPrice || '', total: '' }]),
                              });
                              setManualInvoiceCustomerType(invoice.customerType || (invoice.customerName ? 'walkin' : 'walkin'));
                              setManualInvoiceFooterSections(invoice.footerSections && invoice.footerSections.length > 0 ? invoice.footerSections : [{ label: 'Terms & Conditions', content: '' }]);
                              setShowManualInvoiceListScreen(false);
                              setShowManualInvoiceForm(true);
                            }}
                              style={{ flex: 1, padding: '5px 4px', borderRadius: 6, background: '#F3F4F6', color: 'var(--text-secondary)', border: 'none', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>Edit</button>
                            <button onClick={() => generateInvoicePDF(invoice, getBizInfo(), paymentAccounts)}
                              style={{ flex: 1, padding: '5px 4px', borderRadius: 6, background: '#F3F4F6', color: 'var(--text-secondary)', border: 'none', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>Download</button>
                            <button onClick={() => showConfirm(
                              language === 'sw' ? 'Una uhakika unataka kufuta invoice hii? Hatua hii haiwezi kutenduliwa.' : 'Are you sure you want to permanently delete this invoice? This cannot be undone.',
                              () => setManualInvoices(prev => softDeleteById(prev, invoice.id)),
                              { tone: 'error', title: language === 'sw' ? 'Futa Invoice' : 'Delete Invoice' }
                            )}
                              style={{ flex: 1, padding: '5px 4px', borderRadius: 6, background: '#F3F4F6', color: '#DC2626', border: 'none', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>Delete</button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* EXPENSE CATEGORY HISTORY MODAL */}
        {expenseHistoryCategory && (() => {
          const catName = expenseHistoryCategory;
          const catExpenses = (expenses || []).filter(e => e.category === catName).sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
          const total = catExpenses.reduce((s, e) => s + parseFloat(e.amount || 0), 0);
          const sw = language === 'sw';
          return (
            <div className="popup-overlay" style={{ zIndex: 100200 }} onClick={() => setExpenseHistoryCategory(null)}>
              <div className="popup-form" style={{ maxWidth: 540, width: '94%', borderRadius: 18, padding: 0, overflow: 'hidden', textAlign: 'left', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
                <div style={{ background: '#FEE2E2', padding: '14px 18px', borderBottom: '1px solid var(--border-color)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#991B1B', textTransform: 'uppercase', letterSpacing: 0.5 }}>{sw ? 'Kategoria ya Matumizi' : 'Expense Category'}</div>
                      <div style={{ fontSize: 18, fontWeight: 800, color: '#111827', marginTop: 2 }}>{catName}</div>
                      <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>{catExpenses.length} {sw ? 'matumizi' : 'transactions'} · {fmt(total)}</div>
                    </div>
                    <button onClick={() => setExpenseHistoryCategory(null)} style={{ background: 'rgba(0,0,0,0.06)', border: 'none', width: 30, height: 30, borderRadius: 999, cursor: 'pointer', fontSize: 18, color: '#111827' }}>×</button>
                  </div>
                </div>
                <div style={{ padding: '14px 18px' }}>
                  {catExpenses.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 24, color: 'var(--text-muted)', fontSize: 13 }}>{sw ? 'Hakuna matumizi katika kategoria hii' : 'No expenses in this category'}</div>
                  ) : (
                    <div style={{ border: '1px solid var(--border-color)', borderRadius: 10, overflow: 'hidden' }}>
                      {catExpenses.map((e, ix) => (
                        <div key={e.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '10px 12px', background: ix % 2 === 0 ? 'var(--bg-card)' : 'var(--bg-main)', borderBottom: ix < catExpenses.length - 1 ? '1px solid #F3F4F6' : 'none' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.description || '—'}</div>
                            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{e.date} · {e.paymentMethod || 'cash'}{e.isPaid === false ? ` · ${sw ? 'Haijalipwa' : 'Unpaid'}` : ''}</div>
                          </div>
                          <div style={{ fontWeight: 700, fontSize: 13, color: '#DC2626', whiteSpace: 'nowrap', marginLeft: 8 }}>{fmt(e.amount)}</div>
                        </div>
                      ))}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: '#FEE2E2', borderTop: '2px solid #FECACA' }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#991B1B' }}>{sw ? 'Jumla' : 'Total'}</span>
                        <span style={{ fontSize: 16, fontWeight: 800, color: '#DC2626' }}>{fmt(total)}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

        {/* SERVICE HISTORY MODAL */}
        {serviceHistoryName && (() => {
          const sName = serviceHistoryName;
          const sTxns = (serviceTransactions || []).filter(s => s.serviceName === sName).sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
          const totalRev = sTxns.reduce((s, t) => s + parseFloat(t.totalRevenue || 0), 0);
          const totalProfit = periodProfitExVAT(sTxns); // ex-output-VAT (VAT is a TRA liability, not profit)
          const sw = language === 'sw';
          return (
            <div className="popup-overlay" style={{ zIndex: 100200 }} onClick={() => setServiceHistoryName(null)}>
              <div className="popup-form" style={{ maxWidth: 540, width: '94%', borderRadius: 18, padding: 0, overflow: 'hidden', textAlign: 'left', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
                <div style={{ background: '#CFFAFE', padding: '14px 18px', borderBottom: '1px solid var(--border-color)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#059669', textTransform: 'uppercase', letterSpacing: 0.5 }}>{sw ? 'Huduma' : 'Service'}</div>
                      <div style={{ fontSize: 18, fontWeight: 800, color: '#111827', marginTop: 2 }}>{sName}</div>
                      <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>{sTxns.length} {sw ? 'shughuli' : 'transactions'} · {fmt(totalRev)} {sw ? 'mapato' : 'revenue'}</div>
                    </div>
                    <button onClick={() => setServiceHistoryName(null)} style={{ background: 'rgba(0,0,0,0.06)', border: 'none', width: 30, height: 30, borderRadius: 999, cursor: 'pointer', fontSize: 18, color: '#111827' }}>×</button>
                  </div>
                </div>
                <div style={{ padding: '14px 18px' }}>
                  {sTxns.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 24, color: 'var(--text-muted)', fontSize: 13 }}>{sw ? 'Hakuna shughuli za huduma hii' : 'No transactions for this service'}</div>
                  ) : (
                    <div style={{ border: '1px solid var(--border-color)', borderRadius: 10, overflow: 'hidden' }}>
                      {sTxns.map((tx, ix) => (
                        <div key={tx.id || ix} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '10px 12px', background: ix % 2 === 0 ? 'var(--bg-card)' : 'var(--bg-main)', borderBottom: ix < sTxns.length - 1 ? '1px solid #F3F4F6' : 'none' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tx.customerName || (sw ? 'Mteja' : 'Walk-in')}</div>
                            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{tx.date} · {sw ? 'Idadi' : 'Qty'}: {tx.quantity} · {tx.paymentMethod || 'cash'}</div>
                          </div>
                          <div style={{ textAlign: 'right', marginLeft: 8 }}>
                            <div style={{ fontWeight: 700, fontSize: 13, color: '#0891B2', whiteSpace: 'nowrap' }}>{fmt(tx.totalRevenue || 0)}</div>
                            <div style={{ fontSize: 10, color: grossProfitExVAT(tx) >= 0 ? '#10B981' : '#EF4444', marginTop: 1 }}>{grossProfitExVAT(tx) >= 0 ? '+' : ''}{fmt(grossProfitExVAT(tx))} {sw ? 'faida' : 'profit'}</div>
                          </div>
                        </div>
                      ))}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: '#CFFAFE', borderTop: '2px solid #A5F3FC' }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#059669' }}>{sw ? 'Jumla ya mapato' : 'Total revenue'}</span>
                        <span style={{ fontSize: 16, fontWeight: 800, color: '#0891B2' }}>{fmt(totalRev)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: '#CFFAFE', borderTop: '1px solid #A5F3FC' }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#059669' }}>{sw ? 'Jumla ya faida' : 'Total profit'}</span>
                        <span style={{ fontSize: 14, fontWeight: 700, color: totalProfit >= 0 ? '#059669' : '#DC2626' }}>{totalProfit >= 0 ? '+' : ''}{fmt(totalProfit)}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

        {/* FULL TRANSACTION DETAIL MODAL */}
        {viewTxnModal.open && viewTxnModal.record && (() => {
          const rec = viewTxnModal.record;
          const isPurchase = viewTxnModal.kind === 'purchase';
          // Normalize service / product key aliases -service items may store { serviceName, price }
          const _normItem = it => ({
            ...it,
            productName: it.productName || it.serviceName || it.name || '—',
            sellingPrice: it.sellingPrice != null && it.sellingPrice !== ''
              ? it.sellingPrice
              : (it.price != null ? it.price : 0),
            quantity: it.quantity || 1,
          });
          const items = Array.isArray(rec.items) && rec.items.length > 0
            ? rec.items.map(_normItem)
            : [_normItem({ productName: rec.productName || rec.serviceName, quantity: rec.quantity, unitPrice: rec.unitPrice ?? rec.sellingPrice ?? rec.price, totalCost: rec.totalCost ?? (parseFloat(rec.quantity || 0) * parseFloat(rec.sellingPrice || rec.price || 0)), sellingPrice: rec.sellingPrice ?? rec.price, cost: rec.cost })];
          const subTotal = items.reduce((s, it) => {
            if (isPurchase) return s + parseFloat(it.totalCost || 0);
            const q = parseFloat(it.quantity || 0);
            return s + q * parseFloat(it.sellingPrice || 0);
          }, 0);
          const discPct = parseFloat(rec.discount || 0);
          const discountAmt = subTotal * (discPct / 100);
          const afterDisc = Math.max(0, subTotal - discountAmt);
          const taxRows = (rec.taxes || []).map(tax => ({ name: (tax.type === 'vat' ? 'VAT' : tax.type === 'withholding' ? 'WHT' : (tax.type || 'Tax').toUpperCase()) + ` ${tax.rate}%`, amount: afterDisc * (parseFloat(tax.rate || 0) / 100) }));
          const grandTotal = isPurchase ? (parseFloat(rec.totalCost) || (afterDisc + taxRows.reduce((s, r) => s + r.amount, 0))) : (parseFloat(rec.totalRevenue) || (afterDisc + taxRows.reduce((s, r) => s + r.amount, 0)));
          // Lookup matching returns for this transaction
          const allReturnsList = isPurchase ? (purchaseReturns || []) : (salesReturns || []);
          const recReturnsList = allReturnsList.filter(r => String(r.originalPurchaseId || r.originalSaleId) === String(rec.id));
          const totalReturnedAmt = recReturnsList.reduce((s, r) => s + parseFloat(r.refundAmt || r.totalRefund || 0), 0);
          const netAmount = Math.max(0, grandTotal - totalReturnedAmt);
          const hasReturns = recReturnsList.length > 0;
          const payments = (rec.payments || []).filter(p => p.amount && parseFloat(p.amount) > 0);
          const paidNonCredit = payments.filter(p => p.type !== 'credit').reduce((s, p) => s + parseFloat(p.amount), 0);
          const credit = payments.filter(p => p.type === 'credit').reduce((s, p) => s + parseFloat(p.amount), 0);
          // Adjust credit by returns proportionally -returns reduce the outstanding balance
          // Strategy: returns first cancel out credit (debt forgiven), then refund paid amounts
          const adjustedCredit = Math.max(0, credit - totalReturnedAmt);
          const returnsAppliedToCredit = Math.min(credit, totalReturnedAmt);
          const returnsAppliedToPaid = Math.max(0, totalReturnedAmt - credit);
          const adjustedPaid = Math.max(0, paidNonCredit - returnsAppliedToPaid);
          return (
            <div className="popup-overlay" style={{ zIndex: 100200 }} onClick={() => setViewTxnModal({ open: false, kind: null, record: null })}>
              <div className="popup-form" style={{ maxWidth: 540, width: '94%', borderRadius: 18, padding: 0, overflow: 'hidden', textAlign: 'left', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
                <div style={{ background: isPurchase ? '#E0E7FF' : '#DCFCE7', padding: '14px 18px', borderBottom: '1px solid var(--border-color)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: isPurchase ? '#3730A3' : '#166534', textTransform: 'uppercase', letterSpacing: 0.5 }}>{isPurchase ? (language === 'sw' ? 'Manunuzi' : 'Purchase') : (language === 'sw' ? 'Mauzo' : 'Sale')}</div>
                      <div style={{ fontSize: 16, fontWeight: 800, color: '#111827', marginTop: 2 }}>{isPurchase ? (rec.supplier || '—') : (rec.customerName || 'Walk-in')}</div>
                      <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>{rec.date}{rec.invoiceNumber ? ` · ${rec.invoiceNumber}` : ''}</div>
                    </div>
                    <button onClick={() => setViewTxnModal({ open: false, kind: null, record: null })} style={{ background: 'rgba(0,0,0,0.06)', border: 'none', width: 30, height: 30, borderRadius: 999, cursor: 'pointer', fontSize: 18, color: '#111827', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                  </div>
                </div>
                <div style={{ padding: '14px 18px' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>{language === 'sw' ? 'Bidhaa' : 'Items'} ({items.length})</div>
                  <div style={{ border: '1px solid var(--border-color)', borderRadius: 10, overflow: 'hidden', marginBottom: 14 }}>
                    {items.map((it, ix) => {
                      const qty = parseFloat(it.quantity || 0);
                      const unit = isPurchase ? parseFloat(it.unitPrice ?? it.unitCost ?? (qty > 0 ? parseFloat(it.totalCost || 0) / qty : 0)) : parseFloat(it.sellingPrice || 0);
                      const lineTotal = isPurchase ? parseFloat(it.totalCost || 0) : qty * unit;
                      return (
                        <div key={ix} style={{ display: 'flex', alignItems: 'center', padding: '10px 12px', background: ix % 2 === 0 ? 'var(--bg-card)' : 'var(--bg-main)', borderBottom: ix < items.length - 1 ? '1px solid #F3F4F6' : 'none' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.productName || '—'}</div>
                            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{qty} × {fmt(unit)}</div>
                          </div>
                          <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text-primary)', whiteSpace: 'nowrap', marginLeft: 8 }}>{fmt(lineTotal)}</div>
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ border: '1px solid var(--border-color)', borderRadius: 10, padding: '10px 12px', background: 'var(--bg-tab)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 4 }}>
                      <span>{language === 'sw' ? 'Jumla ndogo' : 'Subtotal'}</span>
                      <span style={{ fontWeight: 600 }}>{fmt(subTotal)}</span>
                    </div>
                    {discPct > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13, color: '#DC2626', marginBottom: 4 }}>
                        <span>{language === 'sw' ? 'Punguzo' : 'Discount'} ({discPct}%)</span>
                        <span style={{ fontWeight: 600 }}>−{fmt(discountAmt)}</span>
                      </div>
                    )}
                    {taxRows.map((tr, ix) => (
                      <div key={ix} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 4 }}>
                        <span>{tr.name}</span>
                        <span style={{ fontWeight: 600 }}>{fmt(tr.amount)}</span>
                      </div>
                    ))}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 15, fontWeight: 800, color: '#0A3D2E', borderTop: '1px solid var(--border-default)', paddingTop: 8, marginTop: 6 }}>
                      <span>{language === 'sw' ? 'Jumla Kuu' : 'Grand Total'}</span>
                      <span>{fmt(grandTotal)}</span>
                    </div>
                  </div>

                  {/* RETURNS -look up matching returns and show breakdown */}
                  {hasReturns && (() => {
                    const totalReturnQty = recReturnsList.reduce((s, r) => {
                      if (Array.isArray(r.items) && r.items.length > 0) {
                        return s + r.items.reduce((qs, it) => qs + parseFloat(it.qty || 0), 0);
                      }
                      return s + parseFloat(r.qty || 0);
                    }, 0);
                    return (
                      <div style={{ marginTop: 14, border: '1px solid #FECACA', borderRadius: 10, overflow: 'hidden' }}>
                        <div style={{ padding: '8px 12px', background: '#FEE2E2', fontSize: 12, fontWeight: 700, color: '#991B1B', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                          ↩ {language === 'sw' ? 'Marudisho' : 'Returns'} ({totalReturnQty} {language === 'sw' ? 'vipande' : 'units'})
                        </div>
                        {recReturnsList.map((r, rx) => {
                          const rItems = Array.isArray(r.items) && r.items.length > 0
                            ? r.items
                            : [{ productName: r.productName, qty: parseFloat(r.qty || 0), lineRefund: parseFloat(r.refundAmt || 0) }];
                          return (
                            <div key={rx} style={{ padding: '8px 12px', borderTop: rx > 0 ? '1px solid #FECACA' : 'none', background: '#FFFBFB' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                                <span style={{ fontSize: 11, color: '#991B1B', fontWeight: 600 }}>{r.date}{r.reason ? ` · ${r.reason}` : ''}</span>
                                <span style={{ fontSize: 13, fontWeight: 700, color: '#DC2626' }}>−{fmt(parseFloat(r.refundAmt || r.totalRefund || 0))}</span>
                              </div>
                              {rItems.map((ri, ix) => (
                                <div key={ix} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, color: 'var(--text-muted)', paddingLeft: 8 }}>
                                  <span>└ {ri.productName || '—'} × {ri.qty}</span>
                                  <span>{fmt(parseFloat(ri.lineRefund || (ri.qty * (ri.unitPrice || 0))))}</span>
                                </div>
                              ))}
                            </div>
                          );
                        })}
                        <div style={{ padding: '10px 12px', background: 'var(--bg-tab)', borderTop: '2px solid #FECACA', display: 'flex', flexDirection: 'column', gap: 6 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13, color: 'var(--text-secondary)' }}>
                            <span>{language === 'sw' ? 'Jumla Kuu (Awali)' : 'Grand Total (original)'}</span>
                            <span>{fmt(grandTotal)}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13, color: '#DC2626' }}>
                            <span>{language === 'sw' ? 'Marudisho' : 'Less: Returns'}</span>
                            <span>−{fmt(totalReturnedAmt)}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 15, fontWeight: 800, color: '#0A3D2E', borderTop: '1px solid var(--border-default)', paddingTop: 6 }}>
                            <span>{language === 'sw' ? 'Salio Halisi' : 'Net Amount'}</span>
                            <span>{fmt(netAmount)}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  <div style={{ marginTop: 14 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>{language === 'sw' ? 'Malipo' : 'Payment'}</div>
                    {payments.length === 0 ? (
                      <div style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic' }}>{language === 'sw' ? 'Hakuna malipo yaliyorekodiwa' : 'No payment recorded'}</div>
                    ) : payments.map((p, ix) => {
                      const origAmt = parseFloat(p.amount);
                      // After returns: credit reduced first, then paid amounts
                      let adjustedAmt = origAmt;
                      if (hasReturns) {
                        if (p.type === 'credit') {
                          adjustedAmt = Math.max(0, origAmt - returnsAppliedToCredit);
                        } else {
                          // Apportion the paid-back amount across non-credit payment rows
                          const totalPaidRows = paidNonCredit || 1;
                          const share = origAmt / totalPaidRows;
                          adjustedAmt = Math.max(0, origAmt - returnsAppliedToPaid * share);
                        }
                      }
                      const changed = hasReturns && Math.round(adjustedAmt * 100) !== Math.round(origAmt * 100);
                      return (
                        <div key={ix} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', fontSize: 13, color: 'var(--text-secondary)' }}>
                          <span style={{ textTransform: 'capitalize' }}>{p.type === 'mobilemoney' ? 'Mobile Money' : p.type === 'credit' ? (language === 'sw' ? 'Mkopo' : 'Credit (Loan)') : p.type}</span>
                          <span style={{ fontWeight: 700, color: p.type === 'credit' ? '#DC2626' : '#059669', display: 'inline-flex', alignItems: 'baseline', gap: 6 }}>
                            {changed && <span style={{ fontSize: 11, color: 'var(--text-muted)', textDecoration: 'line-through', fontWeight: 500 }}>{fmt(origAmt)}</span>}
                            <span>{fmt(adjustedAmt)}</span>
                          </span>
                        </div>
                      );
                    })}
                    {hasReturns && (paidNonCredit > 0 || credit > 0) && (
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4, fontStyle: 'italic' }}>
                        {language === 'sw' ? '↩ Marudisho yamepunguza salio' : '↩ Returns have reduced the balance'}
                      </div>
                    )}
                    {adjustedCredit > 0 && (
                      <div style={{ marginTop: 6, padding: '6px 10px', borderRadius: 8, background: '#FEF3C7', color: '#92400E', fontSize: 12, fontWeight: 600 }}>
                        ⚠ {language === 'sw' ? 'Kwenye Mkopo (haijalipwa)' : 'On Credit (Unpaid)'}: {fmt(adjustedCredit)}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* ITEMS DETAIL MODAL */}
        {detailItemsModal.open && (
          <div className="popup-overlay" style={{ zIndex: 100100 }} onClick={() => setDetailItemsModal({ open: false, items: [], title: '' })}>
            <div className="popup-form" style={{ maxWidth: 400, width: '94%', borderRadius: 18, padding: 0, overflow: 'hidden', textAlign: 'left' }}
              onClick={e => e.stopPropagation()}>
              <div style={{ background: 'var(--bg-tab)', padding: '14px 18px', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)' }}>{detailItemsModal.title}</span>
                <button onClick={() => setDetailItemsModal({ open: false, items: [], title: '' })}
                  style={{ background: 'none', border: 'none', fontSize: 20, color: 'var(--text-muted)', cursor: 'pointer', lineHeight: 1, padding: 0 }}>×</button>
              </div>
              <div style={{ overflowY: 'auto', overflowX: 'auto', maxHeight: 340, WebkitOverflowScrolling: 'touch' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#FAFAFA' }}>
                      <th style={{ padding: '9px 14px', textAlign: 'left', fontWeight: 700, fontSize: 11, color: 'var(--text-primary)', borderBottom: '1px solid var(--border-color)' }}>{t.product || 'Product'}</th>
                      <th style={{ padding: '9px 10px', textAlign: 'right', fontWeight: 700, fontSize: 11, color: 'var(--text-primary)', borderBottom: '1px solid var(--border-color)' }}>{t.qty || 'Qty'}</th>
                      <th style={{ padding: '9px 14px', textAlign: 'right', fontWeight: 700, fontSize: 11, color: 'var(--text-primary)', borderBottom: '1px solid var(--border-color)' }}>{t.amount || 'Amount'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(detailItemsModal.items || []).map((item, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid var(--border-color)' }}>
                        <td style={{ padding: '10px 14px', color: 'var(--text-primary)', fontWeight: 500 }}>{item.productName || item.name || '—'}</td>
                        <td style={{ padding: '10px 10px', textAlign: 'right', color: 'var(--text-primary)' }}>{item.quantity || '—'}</td>
                        <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 700, color: '#059669' }}>
                          {item.total != null ? fmt(item.total) : (item.unitPrice != null ? fmt(parseFloat(item.quantity || 1) * parseFloat(item.unitPrice || 0)) : (item.sellingPrice != null ? fmt(parseFloat(item.quantity || 1) * parseFloat(item.sellingPrice || 0)) : '—'))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ padding: '12px 18px', borderTop: '1px solid var(--border-color)', textAlign: 'right' }}>
                <button onClick={() => setDetailItemsModal({ open: false, items: [], title: '' })}
                  style={{ background: '#111827', color: '#fff', border: 'none', borderRadius: 10, padding: '9px 22px', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                  {t.close}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── INVOICE PREVIEW MODAL ── */}
        {invoicePreviewModal.open && invoicePreviewModal.invoice && (() => {
          const inv = invoicePreviewModal.invoice;
          const biz = getBizInfo();
          const items = inv.items && inv.items.length > 0
            ? inv.items
            : inv.productName
              ? [{ productName: inv.productName, quantity: inv.quantity, sellingPrice: inv.sellingPrice }]
              : [];
          const subtotal = items.reduce((s, i) => s + parseFloat(i.quantity || 1) * parseFloat(i.sellingPrice || i.unitPrice || 0), 0);
          const discAmt = parseFloat(inv.discountAmount || 0) || (subtotal * parseFloat(inv.discount || 0) / 100);
          const afterDisc = subtotal - discAmt;
          const taxAmt = (inv.taxes || []).reduce((s, tx) => s + afterDisc * parseFloat(tx.rate || 0) / 100, 0);
          const total = parseFloat(inv.totalRevenue || afterDisc + taxAmt);
          const isPaidStatus = inv.isPaid !== false;
          return (
            <div className="popup-overlay" style={{ zIndex: 100200 }} onClick={() => { setInvoicePreviewModal({ open: false, invoice: null }); setEditingPaymentIdx(null); }}>
              <div style={{ background: '#fff', borderRadius: 16, width: '94%', maxWidth: 520, maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}
                onClick={e => e.stopPropagation()}>
                {/* Modal header */}
                <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border-default)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
                  <span style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-primary)' }}>{inv.invoiceNumber}</span>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <button onClick={() => generateInvoicePDF(inv, biz, paymentAccounts)}
                      style={{ background: '#F0FDF4', color: '#059669', border: 'none', borderRadius: 8, padding: '6px 12px', fontWeight: 700, fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                      Download
                    </button>
                    <button onClick={() => { setInvoicePreviewModal({ open: false, invoice: null }); setEditingPaymentIdx(null); }}
                      style={{ background: 'none', border: 'none', fontSize: 22, color: 'var(--text-muted)', cursor: 'pointer', lineHeight: 1, padding: 0 }}>×</button>
                  </div>
                </div>
                {/* Invoice body */}
                <div style={{ padding: '20px 22px' }}>
                  {/* Business info */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
                    <div>
                      {biz.logo && <img src={biz.logo} alt={`${biz.name || 'Business'} logo`} style={{ height: 48, marginBottom: 6, display: 'block', borderRadius: 6 }} />}
                      <div style={{ fontWeight: 800, fontSize: 16, color: 'var(--text-primary)' }}>{biz.name || 'My Business'}</div>
                      {biz.address && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{biz.address}</div>}
                      {biz.tin && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>TIN: {biz.tin}</div>}
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontWeight: 800, fontSize: 18, color: '#1A4B3A' }}>INVOICE</div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-secondary)', marginTop: 2 }}>{inv.invoiceNumber}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{inv.date}</div>
                      <span style={{ display: 'inline-block', marginTop: 6, fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20,
                        background: isPaidStatus ? '#D1FAE5' : '#FEF3C7',
                        color: isPaidStatus ? '#065F46' : '#92400E' }}>
                        {isPaidStatus ? 'Paid' : 'Unpaid'}
                      </span>
                    </div>
                  </div>
                  {/* Bill to */}
                  {inv.customerName && (
                    <div style={{ marginBottom: 16, padding: '10px 14px', background: 'var(--bg-tab)', borderRadius: 10 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>Bill To</div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)' }}>{inv.customerName}</div>
                      {inv.customerPhone && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{inv.customerPhone}</div>}
                      {inv.customerAddress && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{inv.customerAddress}</div>}
                    </div>
                  )}
                  {/* Items table */}
                  <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16, fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: '#1A4B3A' }}>
                        <th style={{ padding: '8px 10px', textAlign: 'left', color: '#fff', fontWeight: 700, fontSize: 11, borderRadius: '6px 0 0 0' }}>Item</th>
                        <th style={{ padding: '8px 8px', textAlign: 'right', color: '#fff', fontWeight: 700, fontSize: 11 }}>Qty</th>
                        <th style={{ padding: '8px 8px', textAlign: 'right', color: '#fff', fontWeight: 700, fontSize: 11 }}>Unit Price</th>
                        <th style={{ padding: '8px 10px', textAlign: 'right', color: '#fff', fontWeight: 700, fontSize: 11, borderRadius: '0 6px 0 0' }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, idx) => {
                        const qty = parseFloat(item.quantity || 1);
                        const price = parseFloat(item.sellingPrice || item.unitPrice || 0);
                        const lineTotal = item.total != null ? parseFloat(item.total) : qty * price;
                        return (
                          <tr key={idx} style={{ background: idx % 2 === 0 ? '#F8FAFC' : '#fff', borderBottom: '1px solid var(--border-default)' }}>
                            <td style={{ padding: '9px 10px', color: 'var(--text-primary)', fontWeight: 500 }}>{item.productName || item.name || '—'}</td>
                            <td style={{ padding: '9px 8px', textAlign: 'right', color: 'var(--text-secondary)' }}>{item.type === 'service' ? '—' : qty}</td>
                            <td style={{ padding: '9px 8px', textAlign: 'right', color: 'var(--text-secondary)' }}>{fmt(price)}</td>
                            <td style={{ padding: '9px 10px', textAlign: 'right', fontWeight: 700, color: '#059669' }}>{fmt(lineTotal)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {/* Totals */}
                  <div style={{ marginLeft: 'auto', width: '60%', fontSize: 13 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', color: 'var(--text-muted)' }}>
                      <span>Subtotal</span><span>{fmt(subtotal)}</span>
                    </div>
                    {discAmt > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', color: '#DC2626' }}>
                        <span>Discount</span><span>-{fmt(discAmt)}</span>
                      </div>
                    )}
                    {(inv.taxes || []).map((tx, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', color: 'var(--text-muted)' }}>
                        <span>{tx.name || 'Tax'} ({tx.rate}%)</span><span>{fmt(afterDisc * parseFloat(tx.rate || 0) / 100)}</span>
                      </div>
                    ))}
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 10px', marginTop: 6, background: '#1A4B3A', borderRadius: 8, color: '#fff', fontWeight: 800, fontSize: 15 }}>
                      <span>Total</span><span>{fmt(total)}</span>
                    </div>
                  </div>
                  {/* Payment history -editable */}
                  {(inv.payments || []).filter(p => p.amount).length > 0 && (
                    <div style={{ marginTop: 16, borderTop: '1px solid var(--border-default)', paddingTop: 12 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>Payments Received</div>
                      {(inv.payments || []).filter(p => p.amount).map((p, i) => (
                        <div key={i} style={{ background: 'var(--bg-tab)', borderRadius: 8, padding: '8px 10px', marginBottom: 6 }}>
                          {editingPaymentIdx === i ? (
                            // ── Edit mode ──
                            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                              <input type="number" defaultValue={p.amount}
                                onChange={e => { editingPaymentAmtRef.current = e.target.value; }}
                                style={{ flex: 1, padding: '5px 8px', borderRadius: 6, border: '1px solid #D1D5DB', fontSize: 13, outline: 'none' }} />
                              <button onClick={() => {
                                const newAmt = parseFloat(editingPaymentAmtRef.current);
                                if (!newAmt || newAmt <= 0) return;
                                const oldAmt = parseFloat(p.amount);
                                const diff = newAmt - oldAmt;
                                // Update payment on sale/issued invoice
                                const updatePayments = (payments) => payments.map((pm, idx) => idx === i ? { ...pm, amount: newAmt } : pm);
                                setSales(prev => prev.map(s => s.id === inv.id ? { ...s, payments: updatePayments(s.payments || []), isPaid: updatePayments(s.payments || []).reduce((sum, pm) => sum + parseFloat(pm.amount || 0), 0) >= total } : s));
                                setIssuedInvoices(prev => prev.map(ii => ii.id === inv.id ? { ...ii, payments: updatePayments(ii.payments || []), isPaid: updatePayments(ii.payments || []).reduce((sum, pm) => sum + parseFloat(pm.amount || 0), 0) >= total } : ii));
                                setManualInvoices(prev => prev.map(mi => mi.id === inv.id ? { ...mi, payments: updatePayments(mi.payments || []), isPaid: updatePayments(mi.payments || []).reduce((sum, pm) => sum + parseFloat(pm.amount || 0), 0) >= total } : mi));
                                // Cashflow adjustment for the difference
                                if (diff !== 0 && p.type !== 'credit') {
                                  setManualTransactions(prev => [...prev, {
                                    id: Date.now() + Math.random(),
                                    type: diff > 0 ? 'credit' : 'debit',
                                    account: p.type || 'cash',
                                    amount: Math.abs(diff),
                                    date: p.date || new Date().toISOString().split('T')[0],
                                    systemGenerated: true, bridgeType: 'payment_adjustment',
                                    description: `Payment adjustment ${inv.invoiceNumber}${inv.customerName ? ' · ' + inv.customerName : ''}`,
                                  }]);
                                }
                                // Refresh modal invoice data
                                const updated = { ...inv, payments: updatePayments(inv.payments || []) };
                                setInvoicePreviewModal({ open: true, invoice: updated });
                                setEditingPaymentIdx(null);
                              }}
                                style={{ padding: '5px 10px', borderRadius: 6, background: '#059669', color: '#fff', border: 'none', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>Save</button>
                              <button onClick={() => setEditingPaymentIdx(null)}
                                style={{ padding: '5px 10px', borderRadius: 6, background: '#F3F4F6', color: 'var(--text-secondary)', border: 'none', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                            </div>
                          ) : (
                            // ── View mode ──
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div>
                                <div style={{ fontWeight: 600, fontSize: 13, color: '#059669' }}>{fmt(p.amount)}</div>
                                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>{p.date || '—'} · {p.type || 'cash'}</div>
                              </div>
                              <div style={{ display: 'flex', gap: 5 }}>
                                <button onClick={() => { editingPaymentAmtRef.current = String(p.amount); setEditingPaymentIdx(i); }}
                                  style={{ padding: '3px 10px', borderRadius: 6, background: '#fff', border: '0.5px solid #D1D5DB', color: 'var(--text-secondary)', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>Edit</button>
                                <button onClick={() => showConfirm(
                                  language === 'sw' ? 'Una uhakika unataka kufuta malipo haya? Pesa itarejeshwa kwenye akaunti. Hatua hii haiwezi kutenduliwa.' : 'Are you sure you want to permanently delete this payment entry? The cashflow will be reversed. This cannot be undone.',
                                  () => {
                                  const newPayments = (inv.payments || []).filter((_, idx) => idx !== i);
                                  const newTotal = newPayments.reduce((s, pm) => s + parseFloat(pm.amount || 0), 0);
                                  const newIsPaid = newTotal >= total;
                                  setSales(prev => prev.map(s => s.id === inv.id ? { ...s, payments: newPayments, isPaid: newIsPaid } : s));
                                  setIssuedInvoices(prev => prev.map(ii => ii.id === inv.id ? { ...ii, payments: newPayments, isPaid: newIsPaid } : ii));
                                  setManualInvoices(prev => prev.map(mi => mi.id === inv.id ? { ...mi, payments: newPayments, isPaid: newIsPaid } : mi));
                                  if (p.type !== 'credit') {
                                    setManualTransactions(prev => [...prev, {
                                      id: Date.now() + Math.random(),
                                      type: 'debit',
                                      account: p.type || 'cash',
                                      amount: parseFloat(p.amount),
                                      date: p.date || new Date().toISOString().split('T')[0],
                                      systemGenerated: true, bridgeType: 'payment_deleted',
                                      description: `Payment deleted ${inv.invoiceNumber}${inv.customerName ? ' · ' + inv.customerName : ''}`,
                                    }]);
                                  }
                                  const updated = { ...inv, payments: newPayments, isPaid: newIsPaid };
                                  setInvoicePreviewModal({ open: true, invoice: updated });
                                }, { tone: 'error', title: language === 'sw' ? 'Futa Malipo' : 'Delete Payment' })}
                                  style={{ padding: '3px 10px', borderRadius: 6, background: '#FEF2F2', color: '#DC2626', border: 'none', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>Delete</button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                      {(() => {
                        const paid = (inv.payments || []).reduce((s, p) => s + parseFloat(p.amount || 0), 0);
                        const bal = total - paid;
                        return bal > 0.01 ? (
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '6px 0', fontWeight: 700, color: '#DC2626' }}>
                            <span>Balance due</span><span>{fmt(bal)}</span>
                          </div>
                        ) : null;
                      })()}
                    </div>
                  )}
                  {/* Terms/notes */}
                  {inv.notes && <div style={{ marginTop: 16, fontSize: 12, color: 'var(--text-muted)', borderTop: '1px solid var(--border-default)', paddingTop: 12 }}>{inv.notes}</div>}
                </div>
              </div>
            </div>
          );
        })()}

        {/* ── PROFORMA INVOICE FORM -global so it works from Sales AND Services tabs ── */}
        {showProformaForm && (
          <div className="popup-overlay" style={{ zIndex: 1300 }}>
            <div className="popup-form" style={{ borderRadius: 16, padding: 20, maxHeight: '90vh', overflowY: 'auto' }}>
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div>
                  <h3 style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>{t.proformaInvoice}</h3>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{t.notATaxInvoice}</div>
                </div>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', background: 'var(--bg-input)', padding: '4px 12px', borderRadius: 8 }}>
                  PROF-{String(proformaCounter + 1).padStart(4, '0')}
                </div>
              </div>

              {/* Customer Details -New/Walk-in vs Registered */}
              <div style={{ background: 'var(--bg-input)', borderRadius: 14, padding: '14px 14px 10px', marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>{t.customerDetails}</div>
                {/* Toggle */}
                <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                  {[
                    { val: 'walkin', label: language === 'sw' ? 'Mpya / Wa Kupita' : 'New / Walk-in' },
                    { val: 'registered', label: language === 'sw' ? 'Aliyesajiliwa' : 'Registered' },
                  ].map(opt => (
                    <button key={opt.val} type="button"
                      onClick={() => {
                        setProformaCustomerType(opt.val);
                        if (opt.val === 'walkin') {
                          setProformaData(p => ({ ...p, customerName: '', customerPhone: '', customerAddress: '', customerEmail: '', customerTIN: '', customerVRN: '' }));
                        } else {
                          // when switching to registered, clear so user re-picks
                          setProformaData(p => ({ ...p, customerName: '', customerPhone: '', customerAddress: '', customerEmail: '', customerTIN: '', customerVRN: '' }));
                        }
                      }}
                      style={{ flex: 1, padding: '9px 0', borderRadius: 10, border: 'none', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                        background: proformaCustomerType === opt.val ? '#334155' : '#fff',
                        color: proformaCustomerType === opt.val ? '#fff' : '#6B7280' }}>
                      {opt.label}
                    </button>
                  ))}
                </div>

                {proformaCustomerType === 'registered' ? (
                  <div>
                    <label style={{ display: 'block', marginBottom: 5, color: 'var(--text-primary)', fontWeight: 600, fontSize: 12 }}>{t.selectCustomer || 'Select customer'}</label>
                    <select value={proformaData.customerName || ''}
                      onChange={e => {
                        const selectedName = e.target.value;
                        const cust = (customers || []).find(c => c.name === selectedName);
                        setProformaData(p => ({
                          ...p,
                          customerName: selectedName,
                          customerPhone: cust?.phone || '',
                          customerAddress: cust?.address || '',
                          customerEmail: cust?.email || '',
                          customerTIN: cust?.tin || '',
                          customerVRN: cust?.vrn || '',
                        }));
                      }}
                      style={{ width: '100%', padding: 10, borderRadius: 10, border: 'none', background: 'var(--bg-white)', fontSize: 13, boxSizing: 'border-box', outline: 'none' }}>
                      <option value="">{t.selectCustomer || 'Select customer...'}</option>
                      {(customers || []).map(c => (
                        <option key={c.id} value={c.name}>{c.name}{c.phone ? ` (${c.phone})` : ''}</option>
                      ))}
                    </select>
                    {proformaData.customerName && (
                      <div style={{ marginTop: 10, padding: 10, background: 'rgba(255,255,255,0.6)', borderRadius: 10, fontSize: 12, color: 'var(--text-secondary)' }}>
                        {proformaData.customerPhone && <div>📞 {proformaData.customerPhone}</div>}
                        {proformaData.customerEmail && <div>✉ {proformaData.customerEmail}</div>}
                        {proformaData.customerAddress && <div>📍 {proformaData.customerAddress}</div>}
                        {proformaData.customerTIN && <div>TIN: {proformaData.customerTIN}</div>}
                        {proformaData.customerVRN && <div>VRN: {proformaData.customerVRN}</div>}
                        {!proformaData.customerPhone && !proformaData.customerEmail && !proformaData.customerAddress && !proformaData.customerTIN && !proformaData.customerVRN && (
                          <div style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>{language === 'sw' ? 'Hakuna maelezo ya ziada' : 'No additional details on file'}</div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <div style={{ marginBottom: 10 }}>
                      <label style={{ display: 'block', marginBottom: 5, color: 'var(--text-primary)', fontWeight: 600, fontSize: 12 }}>{t.customerName}</label>
                      <input type="text" value={proformaData.customerName}
                        onChange={e => setProformaData(p => ({ ...p, customerName: e.target.value }))}
                        placeholder={t.customerName}
                        style={{ width: '100%', padding: 10, borderRadius: 10, border: 'none', background: 'var(--bg-white)', fontSize: 13, boxSizing: 'border-box', outline: 'none' }} />
                    </div>
                    <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', marginBottom: 5, color: 'var(--text-primary)', fontWeight: 600, fontSize: 12 }}>{t.phone} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
                        <input type="tel" value={proformaData.customerPhone}
                          onChange={e => setProformaData(p => ({ ...p, customerPhone: e.target.value }))}
                          placeholder="+255..."
                          style={{ width: '100%', padding: 10, borderRadius: 10, border: 'none', background: 'var(--bg-white)', fontSize: 13, boxSizing: 'border-box', outline: 'none' }} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', marginBottom: 5, color: 'var(--text-primary)', fontWeight: 600, fontSize: 12 }}>{language === 'sw' ? 'Barua pepe' : 'Email'} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
                        <input type="email" value={proformaData.customerEmail || ''}
                          onChange={e => setProformaData(p => ({ ...p, customerEmail: e.target.value }))}
                          placeholder="email@example.com"
                          style={{ width: '100%', padding: 10, borderRadius: 10, border: 'none', background: 'var(--bg-white)', fontSize: 13, boxSizing: 'border-box', outline: 'none' }} />
                      </div>
                    </div>
                    <div style={{ marginBottom: 10 }}>
                      <label style={{ display: 'block', marginBottom: 5, color: 'var(--text-primary)', fontWeight: 600, fontSize: 12 }}>{language === 'sw' ? 'Anwani' : 'Address'} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
                      <input type="text" value={proformaData.customerAddress}
                        onChange={e => setProformaData(p => ({ ...p, customerAddress: e.target.value }))}
                        placeholder="Street, City"
                        style={{ width: '100%', padding: 10, borderRadius: 10, border: 'none', background: 'var(--bg-white)', fontSize: 13, boxSizing: 'border-box', outline: 'none' }} />
                    </div>
                    <div style={{ display: 'flex', gap: 10 }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', marginBottom: 5, color: 'var(--text-primary)', fontWeight: 600, fontSize: 12 }}>TIN <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
                        <input type="text" value={proformaData.customerTIN || ''}
                          onChange={e => setProformaData(p => ({ ...p, customerTIN: e.target.value }))}
                          placeholder="123-456-789"
                          style={{ width: '100%', padding: 10, borderRadius: 10, border: 'none', background: 'var(--bg-white)', fontSize: 13, boxSizing: 'border-box', outline: 'none' }} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', marginBottom: 5, color: 'var(--text-primary)', fontWeight: 600, fontSize: 12 }}>VRN <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
                        <input type="text" value={proformaData.customerVRN || ''}
                          onChange={e => setProformaData(p => ({ ...p, customerVRN: e.target.value }))}
                          placeholder="40-001234-X"
                          style={{ width: '100%', padding: 10, borderRadius: 10, border: 'none', background: 'var(--bg-white)', fontSize: 13, boxSizing: 'border-box', outline: 'none' }} />
                      </div>
                    </div>
                  </>
                )}
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', marginBottom: 6, color: 'var(--text-primary)', fontWeight: 600, fontSize: 13 }}>{t.date}</label>
                <input type="date" value={proformaData.date}
                  onChange={e => setProformaData(p => ({ ...p, date: e.target.value }))}
                  style={{ width: '100%', padding: 12, borderRadius: 12, border: 'none', background: 'var(--bg-tab)', fontSize: 14, boxSizing: 'border-box', outline: 'none' }} />
              </div>

              {/* Items */}
              <div style={{ marginBottom: 6 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <label style={{ color: 'var(--text-primary)', fontWeight: 700, fontSize: 13 }}>{t.selectProduct}</label>
                  <button type="button" onClick={addProformaItem}
                    style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', background: 'var(--bg-input)', border: 'none', borderRadius: 8, padding: '4px 12px', cursor: 'pointer' }}>
                    + {t.addItem}
                  </button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 40px 1fr 1fr 26px', gap: 4, marginBottom: 4 }}>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600 }}>{t.selectProduct}</div>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600 }}>{t.quantity}</div>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600 }}>{t.unitPrice}</div>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600 }}>{t.lineTotal}</div>
                  <div />
                </div>
                {(proformaData.items || []).map((item, idx) => {
                  // Look up current stock for selected product
                  const _selProd = item.productName ? (products || []).find(p => p.productName === item.productName) : null;
                  const _stockQty = _selProd ? (_selProd.balance ?? _selProd.currentStock ?? _selProd.openingStockQty ?? 0) : null;
                  const _qtyEntered = parseFloat(item.quantity || 0);
                  const _stockShort = _stockQty !== null && _qtyEntered > _stockQty;
                  return (
                  <div key={idx} style={{ marginBottom: 8 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 40px 1fr 1fr 26px', gap: 4, alignItems: 'center' }}>
                      <select value={item.productName}
                        onChange={e => handleProformaItemChange(idx, 'productName', e.target.value)}
                        style={{ padding: '8px 6px', borderRadius: 10, border: 'none', background: 'var(--bg-tab)', fontSize: 12, boxSizing: 'border-box', outline: 'none', width: '100%' }}>
                        <option value="">— select product —</option>
                        {(products || []).map(p => (
                          <option key={p.id} value={p.productName}>{p.productName}</option>
                        ))}
                      </select>
                      <input type="number" min="0" inputMode="numeric" placeholder="0" value={item.quantity}
                        onChange={e => handleProformaItemChange(idx, 'quantity', e.target.value)}
                        style={{ padding: '8px 6px', borderRadius: 10, border: item.productName && !item.quantity ? '1.5px solid #EF4444' : (_stockShort ? '1.5px solid #EF4444' : 'none'), background: 'var(--bg-tab)', fontSize: 12, width: '100%', boxSizing: 'border-box', outline: 'none', textAlign: 'center' }} />
                      <input type="number" min="0" inputMode="numeric" placeholder="0" value={item.unitPrice}
                        onChange={e => handleProformaItemChange(idx, 'unitPrice', e.target.value)}
                        style={{ padding: '8px 6px', borderRadius: 10, border: item.productName && !item.unitPrice ? '1.5px solid #EF4444' : 'none', background: 'var(--bg-tab)', fontSize: 12, width: '100%', boxSizing: 'border-box', outline: 'none', textAlign: 'right' }} />
                      <input type="number" min="0" inputMode="numeric" placeholder="0"
                        value={item.total}
                        onChange={e => handleProformaItemChange(idx, 'total', e.target.value)}
                        readOnly={!!(item.unitPrice && item.quantity)}
                        style={{ padding: '8px 6px', borderRadius: 10, border: 'none', fontSize: 12, width: '100%', boxSizing: 'border-box', outline: 'none', textAlign: 'right',
                          background: (item.unitPrice && item.quantity) ? '#DCFCE7' : '#FFF7ED',
                          color: (item.unitPrice && item.quantity) ? '#166534' : '#92400E',
                          fontWeight: 600, cursor: (item.unitPrice && item.quantity) ? 'default' : 'text' }} />
                      <button type="button" onClick={() => removeProformaItem(idx)} disabled={(proformaData.items || []).length === 1}
                        style={{ height: 32, width: 28, borderRadius: 8, border: 'none', background: (proformaData.items || []).length === 1 ? '#F3F4F6' : '#FEE2E2', color: (proformaData.items || []).length === 1 ? '#D1D5DB' : '#DC2626', cursor: (proformaData.items || []).length === 1 ? 'default' : 'pointer', fontSize: 16, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        ×
                      </button>
                    </div>
                    {/* Stock availability hint for selected product */}
                    {item.productName && _stockQty !== null && (
                      <div style={{ fontSize: 11, fontWeight: 600, marginTop: 3, paddingLeft: 4, color: _stockQty === 0 ? '#DC2626' : _stockShort ? '#DC2626' : '#059669' }}>
                        {_stockQty === 0
                          ? (language === 'sw' ? 'Stock imekwisha' : 'Out of stock')
                          : _stockShort
                            ? (language === 'sw' ? `⚠ Stock: ${_stockQty} tu (umeomba ${_qtyEntered})` : `⚠ In stock: ${_stockQty} only (you requested ${_qtyEntered})`)
                            : (language === 'sw' ? `Stock: ${_stockQty}` : `Available: ${_stockQty}`)}
                      </div>
                    )}
                  </div>
                  );
                })}
                <div style={{ fontSize: 10, color: 'var(--text-muted)', padding: '4px 0 2px', fontStyle: 'italic' }}>
                  Total auto-fills when Qty + Unit Price are set, or enter manually.
                </div>
              </div>

              {/* Discount */}
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', marginBottom: 6, color: 'var(--text-primary)', fontWeight: 600, fontSize: 13 }}>{t.discount} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
                <input type="number" inputMode="numeric" min="0" max="100" placeholder="0"
                  value={proformaData.discount}
                  onChange={e => setProformaData(p => ({ ...p, discount: e.target.value }))}
                  style={{ width: '100%', padding: 12, borderRadius: 12, border: 'none', background: 'var(--bg-tab)', fontSize: 14, boxSizing: 'border-box', outline: 'none' }} />
              </div>

              {/* Taxes -only for VAT-registered businesses */}
              {vatRegistered && (<div style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <label style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: 13 }}>Taxes <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>({t.optional || 'optional'})</span></label>
                  <button type="button" onClick={addTax}
                    style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', background: 'var(--bg-input)', border: 'none', borderRadius: 8, padding: '4px 10px', cursor: 'pointer' }}>
                    {t.addTax || '+ Add Tax'}
                  </button>
                </div>
                {(proformaData.taxes || []).map((tax, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                    <select value={tax.type} onChange={e => updateTax(idx, 'type', e.target.value)}
                      style={{ flex: 1, padding: '10px 12px', borderRadius: 12, border: '1px solid var(--border-color)', background: '#fff', fontSize: 13, outline: 'none', minWidth: 0, cursor: 'pointer' }}>
                      <option value="vat">{t.vatOption || 'VAT (Value Added Tax)'}</option>
                      <option value="withholding">{t.withholdingOption || 'Withholding Tax'}</option>
                    </select>
                    <input type="number" min="0" inputMode="numeric" placeholder="Rate %"
                      value={tax.rate}
                      onChange={e => updateTax(idx, 'rate', e.target.value)}
                      style={{ width: 70, padding: '10px 8px', borderRadius: 12, border: '1px solid var(--border-color)', background: '#fff', fontSize: 13, outline: 'none', textAlign: 'right', flexShrink: 0, boxSizing: 'border-box' }} />
                    <button type="button" onClick={() => removeTax(idx)}
                      style={{ width: 34, height: 42, borderRadius: 10, border: 'none', background: '#FEE2E2', color: '#DC2626', cursor: 'pointer', fontSize: 20, fontWeight: 700, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                  </div>
                ))}
                {(proformaData.taxes || []).length === 0 && (
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic' }}>{t.noTaxesAdded || 'No taxes added. Click "Add Tax" to include VAT 18% or custom withholding tax.'}</div>
                )}
              </div>)}

              {/* Live totals summary -placed AFTER Discount + Taxes inputs so the user sees the impact below as they edit */}
              {(() => {
                const sub = (proformaData.items || []).reduce((s, it) => s + (parseFloat(it.total || '') || parseFloat(it.quantity || 0) * parseFloat(it.unitPrice || 0)), 0);
                const disc = parseFloat(proformaData.discount || 0);
                const discAmt = sub * disc / 100;
                const afterDisc = Math.max(0, sub - discAmt);
                const taxRows = (proformaData.taxes || []).map(tax => ({
                  name: (tax.type === 'vat' ? 'VAT' : tax.type === 'withholding' ? 'WHT' : (tax.type || 'Tax').toUpperCase()) + ` ${tax.rate}%`,
                  amount: afterDisc * (parseFloat(tax.rate || 0) / 100),
                  isNeg: tax.type === 'withholding',
                }));
                const taxNet = taxRows.reduce((s, r) => s + (r.isNeg ? -r.amount : r.amount), 0);
                const grand = afterDisc + taxNet;
                return sub > 0 ? (
                  <div style={{ background: '#F0F9FF', borderRadius: 12, padding: '8px 14px', marginBottom: 14 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--text-primary)', marginBottom: (disc > 0 || taxRows.length > 0) ? 4 : 0 }}>
                      <span>{t.subtotal}</span><span style={{ fontWeight: 600 }}>{fmt(sub)}</span>
                    </div>
                    {disc > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#DC2626', marginBottom: 4 }}>
                        <span>{t.discountAmount} ({disc}%)</span><span style={{ fontWeight: 600 }}>- {fmt(discAmt)}</span>
                      </div>
                    )}
                    {taxRows.map((tr, ix) => (
                      <div key={ix} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: tr.isNeg ? '#DC2626' : 'var(--text-primary)', marginBottom: 4 }}>
                        <span>{tr.name}</span>
                        <span style={{ fontWeight: 600 }}>{tr.isNeg ? '- ' : ''}{fmt(tr.amount)}</span>
                      </div>
                    ))}
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, fontWeight: 800, color: 'var(--text-secondary)', borderTop: '1px solid #BFDBFE', paddingTop: 8, marginTop: 4 }}>
                      <span>{t.grandTotal}</span><span>{fmt(grand)}</span>
                    </div>
                  </div>
                ) : null;
              })()}

              {/* Validity Period */}
              <div style={{ background: '#FFF7ED', borderRadius: 14, padding: '14px 14px 10px', marginBottom: 14, border: '1px solid #FED7AA' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#C2410C', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {language === 'sw' ? 'Muda wa Kutumika' : 'Validity Period'}
                </div>
                <label style={{ display: 'block', marginBottom: 8, color: 'var(--text-primary)', fontWeight: 600, fontSize: 12 }}>
                  {language === 'sw' ? 'Proforma hii inatumika kwa:' : 'This proforma is valid for:'}
                </label>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                  {[7, 14, 30].map(d => (
                    <button key={d} type="button" onClick={() => setProformaValidityDays(d)}
                      style={{ padding: '6px 14px', borderRadius: 20, border: 'none', fontWeight: 600, fontSize: 12, cursor: 'pointer',
                        background: proformaValidityDays === d ? '#C2410C' : 'var(--bg-tab)',
                        color: proformaValidityDays === d ? '#fff' : 'var(--text-primary)' }}>
                      {d} {language === 'sw' ? 'siku' : 'days'}
                    </button>
                  ))}
                  <button type="button" onClick={() => setProformaValidityDays('')}
                    style={{ padding: '6px 14px', borderRadius: 20, border: 'none', fontWeight: 600, fontSize: 12, cursor: 'pointer',
                      background: ![7,14,30].includes(proformaValidityDays) ? '#C2410C' : 'var(--bg-tab)',
                      color: ![7,14,30].includes(proformaValidityDays) ? '#fff' : 'var(--text-primary)' }}>
                    {language === 'sw' ? 'Maalum' : 'Custom'}
                  </button>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <input type="number" min="1" value={proformaValidityDays}
                    onChange={e => setProformaValidityDays(parseInt(e.target.value) || 1)}
                    style={{ width: 70, padding: '8px 10px', borderRadius: 10, border: '1px solid #FED7AA', background: '#fff', fontSize: 14, outline: 'none', fontWeight: 700, textAlign: 'center' }} />
                  <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{language === 'sw' ? 'siku kutoka tarehe ya kutolewa' : 'days from date of issue'}</span>
                </div>
                {proformaData.date && proformaValidityDays > 0 && (() => {
                  const exp = new Date(proformaData.date);
                  exp.setDate(exp.getDate() + parseInt(proformaValidityDays));
                  return <p style={{ fontSize: 11, color: '#C2410C', fontWeight: 600, margin: 0 }}>
                    {language === 'sw' ? 'Inaisha:' : 'Expires:'} {exp.toDateString()}
                  </p>;
                })()}
              </div>

              {/* Footer Sections -editable per proforma, up to 2 shown side by side */}
              <div style={{ background: 'var(--bg-tab)', borderRadius: 14, padding: '14px', marginBottom: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {language === 'sw' ? 'Sehemu za Chini' : 'Footer Sections'}
                  </div>
                  {proformaFooterSections.length < 2 && (
                    <button type="button"
                      onClick={() => setProformaFooterSections(prev => [...prev, { label: 'Additional Details', content: '' }])}
                      style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', background: 'var(--bg-input)', border: 'none', borderRadius: 8, padding: '4px 10px', cursor: 'pointer' }}>
                      + {language === 'sw' ? 'Ongeza sehemu' : 'Add section'}
                    </button>
                  )}
                </div>
                <p style={{ fontSize: 11, color: 'var(--text-secondary)', margin: '0 0 10px' }}>
                  {language === 'sw' ? '1 sehemu = upana kamili. 2 sehemu = kando kwa kando kama malipo.' : '1 section = full width. 2 sections = side by side like payment columns.'}
                </p>
                {proformaFooterSections.map((sec, si) => (
                  <div key={si} style={{ marginBottom: si < proformaFooterSections.length - 1 ? 12 : 0 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                      <select value={sec.label}
                        onChange={e => setProformaFooterSections(prev => prev.map((s, i) => i === si ? { ...s, label: e.target.value } : s))}
                        style={{ flex: 1, padding: '7px 10px', borderRadius: 8, border: 'none', background: 'var(--bg-card)', fontSize: 13, outline: 'none' }}>
                        <option>Terms &amp; Conditions</option>
                        <option>Additional Details</option>
                        <option>Notes</option>
                        <option>Delivery Terms</option>
                        <option>Warranty</option>
                        <option>Custom</option>
                      </select>
                      {sec.label === 'Custom' && (
                        <input type="text" placeholder="Section title" value={sec.customLabel || ''}
                          onChange={e => setProformaFooterSections(prev => prev.map((s, i) => i === si ? { ...s, customLabel: e.target.value } : s))}
                          style={{ flex: 1, padding: '7px 10px', borderRadius: 8, border: 'none', background: 'var(--bg-card)', fontSize: 13, outline: 'none' }} />
                      )}
                      {proformaFooterSections.length > 1 && (
                        <button type="button" onClick={() => setProformaFooterSections(prev => prev.filter((_, i) => i !== si))}
                          style={{ width: 28, height: 28, borderRadius: 8, background: '#FEE2E2', color: '#DC2626', border: 'none', fontWeight: 700, fontSize: 16, cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                      )}
                    </div>
                    <textarea rows={3} value={sec.content}
                      onChange={e => setProformaFooterSections(prev => prev.map((s, i) => i === si ? { ...s, content: e.target.value } : s))}
                      placeholder={language === 'sw' ? 'Andika hapa...' : 'Write here, one point per line...'}
                      style={{ width: '100%', padding: '9px 12px', borderRadius: 10, border: '1px solid var(--border-color)', background: 'var(--bg-card)', fontSize: 13, color: 'var(--text-primary)', boxSizing: 'border-box', outline: 'none', resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }} />
                  </div>
                ))}
              </div>

              {/* Action buttons */}
              <button onClick={handleGenerateProforma}
                style={{ width: '100%', padding: '14px 0', borderRadius: 14, background: '#334155', color: '#fff', border: 'none', fontWeight: 700, fontSize: 15, cursor: 'pointer', marginBottom: 10 }}>
                {language === 'sw' ? 'Hifadhi' : 'Save'}
              </button>
              <button onClick={() => { setShowProformaForm(false); resetProformaData(); }}
                style={{ width: '100%', padding: '12px 0', borderRadius: 14, background: 'var(--bg-tab)', color: 'var(--text-primary)', border: 'none', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
                {t.cancel}
              </button>
            </div>
          </div>
        )}

        {/* ── DEDICATED MANUAL INVOICE FORM (proforma-styled) ───────── */}
        {showManualInvoiceForm && (
          <div className="popup-overlay" style={{ zIndex: 1300 }}>
            <div className="popup-form" style={{ borderRadius: 16, padding: 20, maxHeight: '90vh', overflowY: 'auto' }}>
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div>
                  <h3 style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>{language === 'sw' ? 'Ankara ya Mkono' : 'Manual Invoice'}</h3>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{language === 'sw' ? 'Itasubiri malipo' : 'Awaiting payment -credit invoice'}</div>
                </div>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', background: 'var(--bg-input)', padding: '4px 12px', borderRadius: 8 }}>
                  {editingManualInvoiceId
                    ? ((manualInvoices || []).find(m => m.id === editingManualInvoiceId)?.invoiceNumber || `INV-${String(invoiceCounter + 1).padStart(4, '0')}`)
                    : `INV-${String(invoiceCounter + 1).padStart(4, '0')}`}
                </div>
              </div>

              {/* Customer Details -New/Walk-in vs Registered (same template as proforma) */}
              <div style={{ background: 'var(--bg-input)', borderRadius: 14, padding: '14px 14px 10px', marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>{t.customerDetails || 'Customer Details'}</div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                  {[
                    { val: 'walkin', label: language === 'sw' ? 'Mpya / Wa Kupita' : 'New / Walk-in' },
                    { val: 'registered', label: language === 'sw' ? 'Aliyesajiliwa' : 'Registered' },
                  ].map(opt => (
                    <button key={opt.val} type="button"
                      onClick={() => {
                        setManualInvoiceCustomerType(opt.val);
                        setManualInvoiceData(p => ({ ...p, customerName: '', customerPhone: '', customerEmail: '', customerAddress: '', customerTIN: '', customerVRN: '' }));
                      }}
                      style={{ flex: 1, padding: '9px 0', borderRadius: 10, border: 'none', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                        background: manualInvoiceCustomerType === opt.val ? '#334155' : '#fff',
                        color: manualInvoiceCustomerType === opt.val ? '#fff' : '#6B7280' }}>
                      {opt.label}
                    </button>
                  ))}
                </div>
                {manualInvoiceCustomerType === 'registered' ? (
                  <div>
                    <label style={{ display: 'block', marginBottom: 5, color: 'var(--text-primary)', fontWeight: 600, fontSize: 12 }}>{t.selectCustomer || 'Select customer'}</label>
                    <select value={manualInvoiceData.customerName || ''}
                      onChange={e => {
                        const sel = e.target.value;
                        const cust = (customers || []).find(c => c.name === sel);
                        setManualInvoiceData(p => ({ ...p, customerName: sel, customerPhone: cust?.phone || '', customerEmail: cust?.email || '', customerAddress: cust?.address || '', customerTIN: cust?.tin || '', customerVRN: cust?.vrn || '' }));
                      }}
                      style={{ width: '100%', padding: 10, borderRadius: 10, border: 'none', background: 'var(--bg-white)', fontSize: 13, boxSizing: 'border-box', outline: 'none' }}>
                      <option value="">{t.selectCustomer || 'Select customer...'}</option>
                      {(customers || []).map(c => (<option key={c.id} value={c.name}>{c.name}{c.phone ? ` (${c.phone})` : ''}</option>))}
                    </select>
                    {manualInvoiceData.customerName && (
                      <div style={{ marginTop: 10, padding: 10, background: 'rgba(255,255,255,0.6)', borderRadius: 10, fontSize: 12, color: 'var(--text-secondary)' }}>
                        {manualInvoiceData.customerPhone && <div>📞 {manualInvoiceData.customerPhone}</div>}
                        {manualInvoiceData.customerEmail && <div>✉ {manualInvoiceData.customerEmail}</div>}
                        {manualInvoiceData.customerAddress && <div>📍 {manualInvoiceData.customerAddress}</div>}
                        {manualInvoiceData.customerTIN && <div>TIN: {manualInvoiceData.customerTIN}</div>}
                        {manualInvoiceData.customerVRN && <div>VRN: {manualInvoiceData.customerVRN}</div>}
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <div style={{ marginBottom: 10 }}>
                      <label style={{ display: 'block', marginBottom: 5, color: 'var(--text-primary)', fontWeight: 600, fontSize: 12 }}>{t.customerName}</label>
                      <input type="text" value={manualInvoiceData.customerName}
                        onChange={e => setManualInvoiceData(p => ({ ...p, customerName: e.target.value }))}
                        placeholder={t.customerName}
                        style={{ width: '100%', padding: 10, borderRadius: 10, border: 'none', background: 'var(--bg-white)', fontSize: 13, boxSizing: 'border-box', outline: 'none' }} />
                    </div>
                    <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', marginBottom: 5, color: 'var(--text-primary)', fontWeight: 600, fontSize: 12 }}>{t.phone} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
                        <input type="tel" value={manualInvoiceData.customerPhone}
                          onChange={e => setManualInvoiceData(p => ({ ...p, customerPhone: e.target.value }))}
                          placeholder="+255..."
                          style={{ width: '100%', padding: 10, borderRadius: 10, border: 'none', background: 'var(--bg-white)', fontSize: 13, boxSizing: 'border-box', outline: 'none' }} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', marginBottom: 5, color: 'var(--text-primary)', fontWeight: 600, fontSize: 12 }}>{language === 'sw' ? 'Barua pepe' : 'Email'} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
                        <input type="email" value={manualInvoiceData.customerEmail || ''}
                          onChange={e => setManualInvoiceData(p => ({ ...p, customerEmail: e.target.value }))}
                          placeholder="email@example.com"
                          style={{ width: '100%', padding: 10, borderRadius: 10, border: 'none', background: 'var(--bg-white)', fontSize: 13, boxSizing: 'border-box', outline: 'none' }} />
                      </div>
                    </div>
                    <div style={{ marginBottom: 10 }}>
                      <label style={{ display: 'block', marginBottom: 5, color: 'var(--text-primary)', fontWeight: 600, fontSize: 12 }}>{language === 'sw' ? 'Anwani' : 'Address'} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
                      <input type="text" value={manualInvoiceData.customerAddress}
                        onChange={e => setManualInvoiceData(p => ({ ...p, customerAddress: e.target.value }))}
                        placeholder="Street, City"
                        style={{ width: '100%', padding: 10, borderRadius: 10, border: 'none', background: 'var(--bg-white)', fontSize: 13, boxSizing: 'border-box', outline: 'none' }} />
                    </div>
                    <div style={{ display: 'flex', gap: 10 }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', marginBottom: 5, color: 'var(--text-primary)', fontWeight: 600, fontSize: 12 }}>TIN <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
                        <input type="text" value={manualInvoiceData.customerTIN || ''}
                          onChange={e => setManualInvoiceData(p => ({ ...p, customerTIN: e.target.value }))}
                          placeholder="123-456-789"
                          style={{ width: '100%', padding: 10, borderRadius: 10, border: 'none', background: 'var(--bg-white)', fontSize: 13, boxSizing: 'border-box', outline: 'none' }} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', marginBottom: 5, color: 'var(--text-primary)', fontWeight: 600, fontSize: 12 }}>VRN <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
                        <input type="text" value={manualInvoiceData.customerVRN || ''}
                          onChange={e => setManualInvoiceData(p => ({ ...p, customerVRN: e.target.value }))}
                          placeholder="40-001234-X"
                          style={{ width: '100%', padding: 10, borderRadius: 10, border: 'none', background: 'var(--bg-white)', fontSize: 13, boxSizing: 'border-box', outline: 'none' }} />
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Date */}
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', marginBottom: 6, color: 'var(--text-primary)', fontWeight: 600, fontSize: 13 }}>{t.date}</label>
                <input type="date" value={manualInvoiceData.date}
                  onChange={e => setManualInvoiceData(p => ({ ...p, date: e.target.value }))}
                  style={{ width: '100%', padding: 12, borderRadius: 12, border: 'none', background: 'var(--bg-tab)', fontSize: 14, boxSizing: 'border-box', outline: 'none' }} />
              </div>

              {/* Items */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <label style={{ color: 'var(--text-primary)', fontWeight: 700, fontSize: 13 }}>{t.selectProduct}</label>
                  <button type="button" onClick={addManualInvoiceItem}
                    style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', background: 'var(--bg-input)', border: 'none', borderRadius: 8, padding: '4px 12px', cursor: 'pointer' }}>
                    + {t.addItem}
                  </button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 40px 1fr 1fr 26px', gap: 4, marginBottom: 4 }}>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600 }}>{t.selectProduct}</div>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600 }}>{t.quantity}</div>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600 }}>{t.unitPrice}</div>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600 }}>{t.lineTotal}</div>
                  <div />
                </div>
                {(manualInvoiceData.items || []).map((item, idx) => (
                  <div key={idx} style={{ display: 'grid', gridTemplateColumns: '2fr 40px 1fr 1fr 26px', gap: 4, marginBottom: 6, alignItems: 'center' }}>
                    <select value={item.productName}
                      onChange={e => handleManualInvoiceItemChange(idx, 'productName', e.target.value)}
                      style={{ padding: '8px 8px', borderRadius: 8, border: '1px solid var(--border-color)', background: '#fff', fontSize: 12, outline: 'none', minWidth: 0 }}>
                      <option value="">— {t.selectProduct} —</option>
                      {(products || []).map(p => (<option key={p.id} value={p.productName}>{p.productName}</option>))}
                    </select>
                    <input type="number" min="0" inputMode="numeric" value={item.quantity}
                      onChange={e => handleManualInvoiceItemChange(idx, 'quantity', e.target.value)}
                      placeholder="0"
                      style={{ padding: '8px 6px', borderRadius: 8, border: '1px solid var(--border-color)', background: '#fff', fontSize: 12, outline: 'none', textAlign: 'center', minWidth: 0 }} />
                    <input type="number" min="0" inputMode="decimal" step="0.01" value={item.unitPrice}
                      onChange={e => handleManualInvoiceItemChange(idx, 'unitPrice', e.target.value)}
                      placeholder="0"
                      style={{ padding: '8px 6px', borderRadius: 8, border: '1px solid var(--border-color)', background: '#fff', fontSize: 12, outline: 'none', textAlign: 'right', minWidth: 0 }} />
                    <input type="text" readOnly value={item.total ? fmt(item.total) : ''} placeholder="—"
                      style={{ padding: '8px 6px', borderRadius: 8, border: '1px solid var(--border-color)', background: 'var(--bg-tab)', fontSize: 12, outline: 'none', textAlign: 'right', minWidth: 0, color: 'var(--text-secondary)' }} />
                    {(manualInvoiceData.items || []).length > 1 ? (
                      <button type="button" onClick={() => removeManualInvoiceItem(idx)}
                        style={{ background: '#FEE2E2', color: '#DC2626', border: 'none', borderRadius: 6, padding: 4, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>×</button>
                    ) : <div />}
                  </div>
                ))}
              </div>

              {/* Discount */}
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', marginBottom: 6, color: 'var(--text-primary)', fontWeight: 600, fontSize: 13 }}>{language === 'sw' ? 'Punguzo (%)' : 'Discount (%)'} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
                <input type="number" min="0" max="100" inputMode="numeric" value={manualInvoiceData.discount}
                  onChange={e => setManualInvoiceData(p => ({ ...p, discount: e.target.value }))}
                  placeholder="0"
                  style={{ width: '100%', padding: 12, borderRadius: 12, border: 'none', background: 'var(--bg-tab)', fontSize: 14, boxSizing: 'border-box', outline: 'none' }} />
              </div>

              {/* Taxes -only for VAT-registered businesses */}
              {vatRegistered && (<div style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <label style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: 13 }}>{language === 'sw' ? 'Kodi' : 'Taxes'} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
                  <button type="button" onClick={addManualInvoiceTax}
                    style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', background: 'var(--bg-input)', border: 'none', borderRadius: 8, padding: '4px 10px', cursor: 'pointer' }}>
                    {t.addTax || '+ Add Tax'}
                  </button>
                </div>
                {(manualInvoiceData.taxes || []).map((tax, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
                    <select value={tax.type} onChange={e => updateManualInvoiceTax(idx, 'type', e.target.value)}
                      style={{ flex: 1, padding: '8px', borderRadius: 8, border: '1px solid var(--border-color)', background: '#fff', fontSize: 13 }}>
                      <option value="vat">VAT</option>
                      <option value="withholding">Withholding</option>
                    </select>
                    <input type="number" min="0" value={tax.rate} onChange={e => updateManualInvoiceTax(idx, 'rate', e.target.value)} placeholder="Rate %"
                      style={{ width: 80, padding: '8px', borderRadius: 8, border: '1px solid var(--border-color)', background: '#fff', fontSize: 13, textAlign: 'right' }} />
                    <button type="button" onClick={() => removeManualInvoiceTax(idx)}
                      style={{ background: '#FEE2E2', color: '#DC2626', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>×</button>
                  </div>
                ))}
              </div>)}

              {/* Totals preview */}
              {(() => {
                const sub = (manualInvoiceData.items || []).reduce((s, it) => s + (parseFloat(it.quantity) || 0) * (parseFloat(it.unitPrice) || 0), 0);
                const dp = parseFloat(manualInvoiceData.discount || 0);
                const da = sub * (dp / 100);
                const aft = Math.max(0, sub - da);
                const tt = (manualInvoiceData.taxes || []).reduce((s, tx) => s + aft * (parseFloat(tx.rate) || 0) / 100, 0);
                const gt = aft + tt;
                return (
                  <div style={{ background: '#F3F4F6', padding: '10px 14px', borderRadius: 10, marginBottom: 14, fontSize: 13 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', color: 'var(--text-muted)' }}><span>{language === 'sw' ? 'Jumla Ndogo' : 'Subtotal'}</span><span>{fmt(sub)}</span></div>
                    {da > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', color: '#DC2626' }}><span>{language === 'sw' ? 'Punguzo' : 'Discount'} ({dp}%)</span><span>-{fmt(da)}</span></div>}
                    {(manualInvoiceData.taxes || []).map((tx, i) => {
                      const r = parseFloat(tx.rate) || 0;
                      const a = aft * r / 100;
                      const lbl = tx.type === 'vat' ? `VAT (${r}%)` : tx.type === 'withholding' ? `WHT (${r}%)` : `Tax (${r}%)`;
                      return <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', color: 'var(--text-muted)' }}><span>{lbl}</span><span>{fmt(a)}</span></div>;
                    })}
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 8px', marginTop: 4, background: '#334155', color: '#fff', borderRadius: 6, fontWeight: 800 }}>
                      <span>{language === 'sw' ? 'JUMLA' : 'TOTAL'}</span><span>{fmt(gt)}</span>
                    </div>
                  </div>
                );
              })()}

              {/* Footer Sections (T&Cs etc.) */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <label style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: 13 }}>{language === 'sw' ? 'Vipengele vya Mwisho' : 'Footer Sections'} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
                  <button type="button" onClick={() => setManualInvoiceFooterSections(prev => [...prev, { label: 'Custom', customLabel: '', content: '' }])}
                    style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', background: 'var(--bg-input)', border: 'none', borderRadius: 8, padding: '4px 10px', cursor: 'pointer' }}>+ Add</button>
                </div>
                {(manualInvoiceFooterSections || []).map((sec, idx) => (
                  <div key={idx} style={{ marginBottom: 10, padding: 10, background: 'var(--bg-tab)', borderRadius: 8, border: '1px solid var(--border-color)' }}>
                    <div style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
                      <input type="text" value={sec.label || ''} onChange={e => setManualInvoiceFooterSections(prev => prev.map((s, i) => i === idx ? { ...s, label: e.target.value } : s))}
                        placeholder="Label (e.g., Terms & Conditions)"
                        style={{ flex: 1, padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border-color)', background: '#fff', fontSize: 12 }} />
                      <button type="button" onClick={() => setManualInvoiceFooterSections(prev => prev.filter((_, i) => i !== idx))}
                        style={{ background: '#FEE2E2', color: '#DC2626', border: 'none', borderRadius: 6, padding: '4px 8px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>×</button>
                    </div>
                    <textarea value={sec.content || ''} onChange={e => setManualInvoiceFooterSections(prev => prev.map((s, i) => i === idx ? { ...s, content: e.target.value } : s))}
                      placeholder="Content (one item per line)"
                      rows={2} style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border-color)', background: '#fff', fontSize: 12, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }} />
                  </div>
                ))}
              </div>

              {/* Action buttons */}
              <button onClick={handleSaveManualInvoice}
                style={{ width: '100%', padding: '14px 0', borderRadius: 14, background: '#334155', color: '#fff', border: 'none', fontWeight: 700, fontSize: 15, cursor: 'pointer', marginBottom: 10 }}>
                {language === 'sw' ? 'Hifadhi Ankara' : 'Save Invoice'}
              </button>
              <button onClick={() => { setShowManualInvoiceForm(false); resetManualInvoiceData(); setEditingManualInvoiceId(null); }}
                style={{ width: '100%', padding: '12px 0', borderRadius: 14, background: 'var(--bg-tab)', color: 'var(--text-primary)', border: 'none', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
                {t.cancel}
              </button>
            </div>
          </div>
        )}

        {/* ── PROFORMA SAVED SUCCESS SCREEN ─────────────────────────── */}
        {savedProformaRecord && (
          <div className="popup-overlay" style={{ zIndex: 1400 }}>
            <div className="popup-form" style={{ borderRadius: 20, padding: 28, maxWidth: 380, width: '100%', textAlign: 'center' }}>
              <div style={{ width: 60, height: 60, borderRadius: '50%', background: '#D1FAE5', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              </div>
              <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 6px' }}>
                {language === 'sw' ? 'Imehifadhiwa!' : 'Proforma Saved!'}
              </h3>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 6px' }}>
                {savedProformaRecord.proformaNumber}
              </p>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 24px' }}>
                {savedProformaRecord.customerName || 'Walk-in'}
                {savedProformaRecord.expiryDate ? ` · ${language === 'sw' ? 'Inaisha' : 'Expires'}: ${savedProformaRecord.expiryDate}` : ''}
              </p>
              <button
                onClick={() => { generateProformaPDF(savedProformaRecord, getBizInfo(), paymentAccounts); }}
                style={{ width: '100%', padding: '13px 0', borderRadius: 14, background: '#334155', color: '#fff', border: 'none', fontWeight: 700, fontSize: 15, cursor: 'pointer', marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                {language === 'sw' ? 'Pakua PDF' : 'Download PDF'}
              </button>
              <button
                onClick={() => setSavedProformaRecord(null)}
                style={{ width: '100%', padding: '12px 0', borderRadius: 14, background: 'var(--bg-tab)', color: 'var(--text-primary)', border: 'none', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
                {language === 'sw' ? 'Funga' : 'Close'}
              </button>
            </div>
          </div>
        )}

        {/* ── RETURNS REPORT SCREEN ──────────────────────────────────── */}
        {showReturnsReport && (() => {
          const filteredReturns = (salesReturns || []).filter(r => {
            if (returnsReportFrom && r.date < returnsReportFrom) return false;
            if (returnsReportTo && r.date > returnsReportTo) return false;
            return true;
          }).sort((a, b) => new Date(b.date) - new Date(a.date));
          const totalRefund = filteredReturns.reduce((s, r) => s + parseFloat(r.refundAmt || 0), 0);
          const totalQty = filteredReturns.reduce((s, r) => s + parseFloat(r.qty || 0), 0);
          const sw = language === 'sw';
          return (
            <div style={{ position: 'fixed', inset: 0, zIndex: 1500, background: 'var(--bg-tab)', display: 'flex', flexDirection: 'column' }}>
              {/* Header */}
              <div style={{ background: 'var(--bg-white)', borderBottom: '1px solid var(--border-color)', padding: '14px 16px 10px', flexShrink: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                  <button onClick={() => { setShowReturnsReport(false); if (drilldownFromTab) { setActiveTab(drilldownFromTab); setDrilldownFromTab(null); } }} style={{ background: 'none', border: 'none', padding: '4px 10px 4px 0', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--text-primary)" strokeWidth="2.5" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
                  </button>
                  <h2 style={{ flex: 1, fontSize: 18, fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>
                    {sw ? 'Ripoti ya Mauzo Yaliyorudishwa' : 'Returns Report'}
                  </h2>
                  <button onClick={() => setShowReturnsModal(true)} style={{ fontSize: 12, fontWeight: 700, background: '#FEE2E2', color: '#DC2626', border: 'none', borderRadius: 10, padding: '7px 12px', cursor: 'pointer' }}>
                    + {sw ? 'Rekodi Urudishaji' : 'Record Return'}
                  </button>
                </div>
                {/* Period preset buttons + date range pickers */}
                {periodPresetBar(returnsReportFrom, returnsReportTo, (f, t) => { setReturnsReportFrom(f); setReturnsReportTo(t); })}
                <div style={{ display: 'flex', gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>{sw ? 'Tarehe ya Kuanzia' : 'From Date'}</label>
                    <input type="date" value={returnsReportFrom} onChange={e => setReturnsReportFrom(e.target.value)}
                      style={{ width: '100%', padding: '9px 12px', borderRadius: 10, border: '1px solid var(--border-color)', background: 'var(--bg-tab)', fontSize: 13, boxSizing: 'border-box' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>{sw ? 'Tarehe ya Mwisho' : 'To Date'}</label>
                    <input type="date" value={returnsReportTo} onChange={e => setReturnsReportTo(e.target.value)}
                      style={{ width: '100%', padding: '9px 12px', borderRadius: 10, border: '1px solid var(--border-color)', background: 'var(--bg-tab)', fontSize: 13, boxSizing: 'border-box' }} />
                  </div>
                </div>
              </div>
              {/* Summary cards */}
              <div style={{ background: 'var(--bg-white)', borderBottom: '1px solid var(--border-color)', padding: '10px 16px', display: 'flex', gap: 10, flexShrink: 0 }}>
                <div style={{ flex: 1, background: '#FEE2E2', borderRadius: 12, padding: '10px 14px' }}>
                  <div style={{ fontSize: 11, color: '#991B1B', fontWeight: 600 }}>{sw ? 'Idadi ya Urudishaji' : 'Total Returns'}</div>
                  <div style={{ fontSize: 20, fontWeight: 900, color: '#DC2626' }}>{filteredReturns.length}</div>
                </div>
                <div style={{ flex: 1, background: '#FEF3C7', borderRadius: 12, padding: '10px 14px' }}>
                  <div style={{ fontSize: 11, color: '#92400E', fontWeight: 600 }}>{sw ? 'Bidhaa Zilizorudishwa' : 'Units Returned'}</div>
                  <div style={{ fontSize: 20, fontWeight: 900, color: '#92400E' }}>{totalQty}</div>
                </div>
                <div style={{ flex: 1, background: 'var(--bg-input)', borderRadius: 12, padding: '10px 14px' }}>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 600 }}>{sw ? 'Jumla ya Marejesho' : 'Total Refunded'}</div>
                  <div style={{ fontSize: 18, fontWeight: 900, color: '#DC2626' }}>-{fmt(totalRefund)}</div>
                </div>
              </div>
              {/* List */}
              <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
                {filteredReturns.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-muted)' }}>
                    <div style={{ fontSize: 40, marginBottom: 12 }}>↩</div>
                    <div style={{ fontSize: 15, fontWeight: 600 }}>{sw ? 'Hakuna mauzo yaliyorudishwa' : 'No returns found'}</div>
                    <div style={{ fontSize: 13, marginTop: 4 }}>{sw ? 'Badilisha kipindi cha tarehe' : 'Try a different date range'}</div>
                  </div>
                ) : filteredReturns.map((r, i) => {
                  const origSale = sales.find(s => String(s.id) === String(r.originalSaleId));
                  // Resolve the specific sub-account label (e.g. "CRDB -Hesabu
                  // Solutions") so the user can verify which account was
                  // actually selected when the refund was recorded.
                  const refundLabel = (() => {
                    const key = r.refundAccountKey || r.refundMethod || 'cash';
                    if (key === 'cash') return sw ? '$ Taslimu' : '$ Cash';
                    if (key === 'bank') return sw ? 'Benki' : 'Bank';
                    if (key === 'mobilemoney') return sw ? 'Wakala/Simu' : 'Mobile Money';
                    if (key === 'credit') return sw ? '⚠ Mkopo' : '⚠ Credit';
                    // Composite 'bank|<id>' or 'mobilemoney|<id>' → find the sub
                    const [t, id] = String(key).split('|');
                    const acct = (paymentAccounts || []).find(a => String(a.id) === id);
                    if (acct) {
                      const brand = acct.bankName || acct.networkName || '';
                      const icon = t === 'bank' ? '' : '';
                      return `${icon} ${brand}${acct.accountName ? ` · ${acct.accountName}` : ''}`;
                    }
                    return key;
                  })();
                  // Helper used by both Delete and Edit -reverses every side-effect of this return.
                  const reverseReturn = () => {
                    const cashOut = parseFloat(r.cashRefundAmt != null ? r.cashRefundAmt : (r.refundAmt || r.totalRefund || 0)) || 0;
                    if (cashOut > 0) {
                      // TOMBSTONE (not physically remove) any legacy bridge for this
                      // return, so the correction is traceable and sync-safe.
                      setManualTransactions(prev => (prev || []).map(mt => {
                        const looksLikeReturn = mt.type === 'debit'
                          && mt.date === r.date
                          && Math.abs(parseFloat(mt.amount || 0) - cashOut) < 0.01
                          && (mt.description || '').includes(r.invoiceNumber || String(r.originalSaleId));
                        return looksLikeReturn ? tombstone(mt) : mt;
                      }));
                    }
                    const itemsList = Array.isArray(r.items) && r.items.length > 0
                      ? r.items
                      : (r.productName ? [{ productName: r.productName, qty: parseFloat(r.qty || 0), type: 'product' }] : []);
                    setProducts(prev => prev.map(p => {
                      const matched = itemsList.find(it => it.type !== 'service' && lineMatchesProduct(it, p));
                      if (!matched) return p;
                      // Signed (no clamp), matched by productId -reversing a return
                      // removes the units it had put back.
                      const newBal = (p.balance ?? p.currentStock ?? p.openingStockQty ?? 0) - parseFloat(matched.qty || 0);
                      return { ...p, balance: newBal, currentStock: newBal, updatedAt: new Date().toISOString() };
                    }));
                    const credRev = parseFloat(r.creditReductionAmt || 0) || 0;
                    if (credRev > 0 && r.originalSaleId) {
                      setSales(prev => prev.map(s => {
                        if (String(s.id) !== String(r.originalSaleId)) return s;
                        return {
                          ...s,
                          creditReducedByReturns: Math.max(0, parseFloat(s.creditReducedByReturns || 0) - credRev),
                          isPaid: false,
                        };
                      }));
                    }
                    setSalesReturns(prev => softDeleteById(prev, r.id));
                  };
                  const handleVoidReturn = () => {
                    if (r.status === 'void') return;
                    showConfirm(
                      sw
                        ? 'Batilisha urudishaji huu? Athari za kifedha zitatenduliwa lakini kumbukumbu itabaki ionekane kwa ukaguzi.'
                        : 'Void this return? Financial effects will be reversed but the record stays visible for your audit trail.',
                      async () => {
                        setSalesReturns(prev => prev.map(sr => String(sr.id) === String(r.id)
                          ? { ...sr, status: 'void', voidedAt: new Date().toISOString(), updatedAt: new Date().toISOString() }
                          : sr));
                        flushVoidToStorage('salesReturns', r.id);
                        registerVoid(currentUser?.uid, 'salesReturns', r.id, r?._rid);
                        const outcome = await syncVoidToServer({
                          label: 'salesReturn',
                          record: r,
                          localId: r.id,
                          call: (rid) => voidSalesReturn(businessId, rid, currentUser.accessToken),
                        });
                        if (!outcome.ok) { showAlert(voidFailureMessage('salesReturn', outcome, sw), { tone: 'error' }); return; }
                        showAlert(sw ? 'Urudishaji umebatilishwa.' : 'Return voided.', { tone: 'success' });
                      },
                      { tone: 'error', title: sw ? 'Batilisha Urudishaji' : 'Void Return' }
                    );
                  };
                  // Edit Return -reverses this return then re-opens the Record
                  // Return modal pre-populated with its existing values so the
                  // user can adjust qty / reason / refund account and save fresh.
                  const handleEditReturn = () => {
                    showConfirm(
                      sw
                        ? 'Kuhariri urudishaji huu kutarudisha hali kabla yake, kisha kufungua fomu ya kurudisha tena ili uweze kufanya mabadiliko. Endelea?'
                        : 'Editing this return will reverse it first, then re-open the Record Return form pre-filled with its values so you can adjust and re-save. Continue?',
                      () => {
                        // Capture values BEFORE reversing (state will be cleared)
                        const saleIdForEdit = r.originalSaleId;
                        const reasonForEdit = r.reason || '';
                        const refundForEdit = r.refundAccountKey || r.refundMethod || 'cash';
                        const dateForEdit = r.date || new Date().toISOString().split('T')[0];
                        const itemsForEdit = Array.isArray(r.items) && r.items.length > 0 ? r.items : null;
                        reverseReturn();
                        // Open the Record Return modal with pre-filled state
                        setReturnSaleId(String(saleIdForEdit));
                        setReturnReason(reasonForEdit);
                        setReturnRefundMethod(refundForEdit);
                        setReturnDate(dateForEdit);
                        // Map returned items to sale-item indices for the per-item qty inputs
                        const targetSale = (sales || []).find(s => String(s.id) === String(saleIdForEdit));
                        if (targetSale && itemsForEdit) {
                          const saleItemsList = Array.isArray(targetSale.items) && targetSale.items.length > 0
                            ? targetSale.items
                            : [{ productName: targetSale.productName }];
                          const qtyMap = {};
                          itemsForEdit.forEach(retIt => {
                            const idx = saleItemsList.findIndex(si => (si.productName || '') === (retIt.productName || ''));
                            if (idx >= 0) qtyMap[idx] = String(retIt.qty || '');
                          });
                          setSalesReturnItemQtys(qtyMap);
                        } else {
                          setSalesReturnItemQtys({ 0: String(r.qty || '') });
                        }
                        setShowReturnsReport(false);
                        setShowReturnsModal(true);
                      },
                      { title: sw ? 'Hariri Urudishaji' : 'Edit Return' }
                    );
                  };
                  return (
                    <div key={r.id} style={{ background: 'var(--bg-white)', borderRadius: 16, padding: '18px 20px', marginBottom: 14, border: '1px solid var(--border-color)', borderLeft: '5px solid #EF4444', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                        <div>
                          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.3 }}>{r.productName}{Array.isArray(r.items) && r.items.length > 1 ? ` +${r.items.length - 1}` : ''}</div>
                          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>{r.customerName || 'Walk-in'} · {r.date}</div>
                          {r.invoiceNumber && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{r.invoiceNumber}</div>}
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: 20, fontWeight: 800, color: '#DC2626' }}>-{fmt(r.refundAmt)}</div>
                          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>↩ {r.qty} {sw ? 'vipande' : 'units'}</div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
                        {r.reason && (
                          <span style={{ fontSize: 12, background: '#FEF3C7', color: '#92400E', borderRadius: 8, padding: '4px 10px', fontWeight: 600 }}>
                            {sw ? 'Sababu' : 'Reason'}: {r.reason}
                          </span>
                        )}
                        {origSale && (
                          <span style={{ fontSize: 12, background: '#F0FDF4', color: '#065F46', borderRadius: 8, padding: '4px 10px', fontWeight: 600 }}>
                            {sw ? 'Mauzo ya Asili' : 'Original'}: {fmt(origSale.totalRevenue)}
                          </span>
                        )}
                        <span style={{ fontSize: 12, background: 'var(--bg-input)', color: 'var(--text-secondary)', borderRadius: 8, padding: '4px 10px', fontWeight: 600 }}>
                          ↩ {refundLabel}
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: 10 }}>
                        <button onClick={handleEditReturn}
                          style={{ padding: '8px 14px', borderRadius: 8, background: 'var(--bg-input)', color: 'var(--text-secondary)', border: 'none', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                          ✎ {sw ? 'Hariri Urudishaji' : 'Edit Return'}
                        </button>
                        <button onClick={handleVoidReturn}
                          style={{ padding: '8px 14px', borderRadius: 8, background: r.status === 'void' ? 'var(--bg-tab)' : '#FEF3C7', color: r.status === 'void' ? 'var(--text-muted)' : '#92400E', border: 'none', fontSize: 13, fontWeight: 700, cursor: r.status === 'void' ? 'not-allowed' : 'pointer', opacity: r.status === 'void' ? 0.5 : 1 }}
                          disabled={r.status === 'void'}>
                          {r.status === 'void' ? (sw ? 'Imebatilishwa' : 'VOIDED') : (sw ? 'Batilisha' : 'Void')}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* ── PURCHASE RETURNS REPORT MODAL ───────────────────────────── */}
        {showPurchaseReturnsReport && (() => {
          const sw = language === 'sw';
          const filteredPurchRets = (purchaseReturns || []).filter(r => {
            if (purchaseReturnsReportFrom && r.date < purchaseReturnsReportFrom) return false;
            if (purchaseReturnsReportTo && r.date > purchaseReturnsReportTo) return false;
            return true;
          }).sort((a, b) => new Date(b.date) - new Date(a.date));
          const totalPurchRetRefund = filteredPurchRets.reduce((s, r) => s + parseFloat(r.refundAmt || 0), 0);
          const totalPurchRetQty = filteredPurchRets.reduce((s, r) => s + parseFloat(r.qty || 0), 0);
          return (
            <div style={{ position: 'fixed', inset: 0, zIndex: 1500, background: 'var(--bg-tab)', display: 'flex', flexDirection: 'column' }}>
              {/* Header */}
              <div style={{ background: 'var(--bg-white)', borderBottom: '1px solid var(--border-color)', padding: '14px 16px 10px', flexShrink: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                  <button onClick={() => { setShowPurchaseReturnsReport(false); if (drilldownFromTab) { setActiveTab(drilldownFromTab); setDrilldownFromTab(null); } }} style={{ background: 'none', border: 'none', padding: '4px 10px 4px 0', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--text-primary)" strokeWidth="2.5" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
                  </button>
                  <h2 style={{ flex: 1, fontSize: 18, fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>
                    {sw ? 'Ripoti ya Manunuzi Yaliyorudishwa' : 'Purchase Returns Report'}
                  </h2>
                </div>
                {/* Period preset buttons + date range pickers */}
                {periodPresetBar(purchaseReturnsReportFrom, purchaseReturnsReportTo, (f, t) => { setPurchaseReturnsReportFrom(f); setPurchaseReturnsReportTo(t); })}
                <div style={{ display: 'flex', gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>{sw ? 'Tarehe ya Kuanzia' : 'From Date'}</label>
                    <input type="date" value={purchaseReturnsReportFrom} onChange={e => setPurchaseReturnsReportFrom(e.target.value)}
                      style={{ width: '100%', padding: '9px 12px', borderRadius: 10, border: '1px solid var(--border-color)', background: 'var(--bg-tab)', fontSize: 13, boxSizing: 'border-box' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>{sw ? 'Tarehe ya Mwisho' : 'To Date'}</label>
                    <input type="date" value={purchaseReturnsReportTo} onChange={e => setPurchaseReturnsReportTo(e.target.value)}
                      style={{ width: '100%', padding: '9px 12px', borderRadius: 10, border: '1px solid var(--border-color)', background: 'var(--bg-tab)', fontSize: 13, boxSizing: 'border-box' }} />
                  </div>
                </div>
              </div>
              {/* Summary cards */}
              <div style={{ background: 'var(--bg-white)', borderBottom: '1px solid var(--border-color)', padding: '10px 16px', display: 'flex', gap: 10, flexShrink: 0 }}>
                <div style={{ flex: 1, background: '#FEE2E2', borderRadius: 12, padding: '10px 14px' }}>
                  <div style={{ fontSize: 11, color: '#991B1B', fontWeight: 600 }}>{sw ? 'Idadi ya Urudishaji' : 'Total Returns'}</div>
                  <div style={{ fontSize: 20, fontWeight: 900, color: '#DC2626' }}>{filteredPurchRets.length}</div>
                </div>
                <div style={{ flex: 1, background: '#FEF3C7', borderRadius: 12, padding: '10px 14px' }}>
                  <div style={{ fontSize: 11, color: '#92400E', fontWeight: 600 }}>{sw ? 'Bidhaa Zilizorudishwa' : 'Units Returned'}</div>
                  <div style={{ fontSize: 20, fontWeight: 900, color: '#92400E' }}>{totalPurchRetQty}</div>
                </div>
                <div style={{ flex: 1, background: '#FEE2E2', borderRadius: 12, padding: '10px 14px' }}>
                  <div style={{ fontSize: 11, color: '#991B1B', fontWeight: 600 }}>{sw ? 'Jumla ya Marejesho' : 'Total Refunded'}</div>
                  <div style={{ fontSize: 18, fontWeight: 900, color: '#DC2626' }}>+{fmt(totalPurchRetRefund)}</div>
                </div>
              </div>
              {/* List */}
              <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
                {filteredPurchRets.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-muted)' }}>
                    <div style={{ fontSize: 40, marginBottom: 12 }}>↩</div>
                    <div style={{ fontSize: 15, fontWeight: 600 }}>{sw ? 'Hakuna manunuzi yaliyorudishwa' : 'No purchase returns found'}</div>
                    <div style={{ fontSize: 13, marginTop: 4 }}>{sw ? 'Badilisha kipindi cha tarehe' : 'Try a different date range'}</div>
                  </div>
                ) : filteredPurchRets.map((r, i) => {
                  const origPurchase = (purchases || []).find(p => String(p.id) === String(r.originalPurchaseId));
                  return (
                    <div key={r.id} style={{ background: 'var(--bg-white)', borderRadius: 14, padding: 14, marginBottom: 10, border: '1px solid var(--border-color)', borderLeft: '4px solid #EF4444' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                        <div>
                          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{r.productName}</div>
                          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{r.supplier || '—'} · {r.date}</div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: 16, fontWeight: 800, color: '#DC2626' }}>+{fmt(r.refundAmt)}</div>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>↩ {r.qty} {sw ? 'vipande' : 'units'}</div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {r.reason && (
                          <span style={{ fontSize: 11, background: '#FEF3C7', color: '#92400E', borderRadius: 6, padding: '3px 8px', fontWeight: 600 }}>
                            {sw ? 'Sababu' : 'Reason'}: {r.reason}
                          </span>
                        )}
                        {origPurchase && (
                          <span style={{ fontSize: 11, background: '#F0FDF4', color: '#065F46', borderRadius: 6, padding: '3px 8px', fontWeight: 600 }}>
                            {sw ? 'Ununuzi wa Asili' : 'Original'}: {fmt(origPurchase.totalCost)}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* ── SALES RETURN MODAL ──────────────────────────────────────── */}
        {showReturnsModal && (() => {
          const selectedSale = sales.find(s => String(s.id) === String(returnSaleId));
          const sw = language === 'sw';

          // Build the list of items in this sale (multi-item aware, with legacy fallback)
          const saleItems = selectedSale
            ? (Array.isArray(selectedSale.items) && selectedSale.items.length > 0
                ? selectedSale.items
                : [{ productName: selectedSale.productName, quantity: selectedSale.quantity, sellingPrice: selectedSale.sellingPrice, type: selectedSale.isService ? 'service' : 'product' }])
            : [];

          // Sum prior returns per product so we cap each line at the original qty
          const alreadyReturnedBySaleProduct = {};
          (salesReturns || []).forEach(r => {
            if (String(r.originalSaleId) !== String(selectedSale?.id)) return;
            if (Array.isArray(r.items) && r.items.length > 0) {
              r.items.forEach(ri => {
                alreadyReturnedBySaleProduct[ri.productName] = (alreadyReturnedBySaleProduct[ri.productName] || 0) + parseFloat(ri.qty || 0);
              });
            } else if (r.productName) {
              alreadyReturnedBySaleProduct[r.productName] = (alreadyReturnedBySaleProduct[r.productName] || 0) + parseFloat(r.qty || 0);
            }
          });

          // Compute total refund across all per-item entries
          let totalRefund = 0;
          saleItems.forEach((it, idx) => {
            const q = parseFloat(salesReturnItemQtys[idx] || 0);
            const isService = it.type === 'service';
            totalRefund += (isService ? 1 : q) * parseFloat(it.sellingPrice || 0);
          });

          // When a sale is selected, build payment breakdown for display + default refund method
          const salePayments = selectedSale && Array.isArray(selectedSale.payments) && selectedSale.payments.length > 0
            ? selectedSale.payments
            : selectedSale ? [{ type: selectedSale.paymentMethod || 'cash', amount: selectedSale.totalRevenue }] : [];

          // ── Credit-aware split ──────────────────────────────────────────
          // How much of this sale was actually paid in cash/bank/mobile vs how
          // much is still on credit (customer owes us)?
          const cashPaidOnSale = (salePayments || [])
            .filter(p => p.type !== 'credit')
            .reduce((s, p) => s + parseFloat(p.amount || 0), 0);
          const creditOnSale = Math.max(0, parseFloat(selectedSale?.totalRevenue || 0) - cashPaidOnSale);
          // Already-reduced credit from prior returns on this same sale
          const alreadyReducedCredit = (salesReturns || [])
            .filter(r => String(r.originalSaleId) === String(selectedSale?.id))
            .reduce((s, r) => s + (parseFloat(r.creditReductionAmt || 0) || 0), 0);
          const remainingCredit = Math.max(0, creditOnSale - alreadyReducedCredit);
          // Already-refunded cash from prior returns on this same sale
          const alreadyRefundedCash = (salesReturns || [])
            .filter(r => String(r.originalSaleId) === String(selectedSale?.id))
            .reduce((s, r) => s + (parseFloat(r.cashRefundAmt != null ? r.cashRefundAmt : 0) || 0), 0);
          const remainingCashRefundCap = Math.max(0, cashPaidOnSale - alreadyRefundedCash);

          // Split the current refund: reduce credit first (since the customer
          // owes us -they didn't actually pay that portion), THEN refund cash.
          // This way returning credit-only sales NEVER touches a wallet.
          const creditReductionDue = Math.min(totalRefund, remainingCredit);
          const cashRefundDue = Math.max(0, totalRefund - creditReductionDue);
          const cashRefundExceedsPaid = cashRefundDue > remainingCashRefundCap + 0.005;

          const payMethodLabel = (m) => {
            if (m === 'bank') return sw ? 'Benki' : 'Bank';
            if (m === 'mobilemoney') return sw ? 'Wakala/Simu' : 'Mobile Money';
            return sw ? '$ Taslimu' : '$ Cash';
          };

          const handleSaleSelect = (saleId) => {
            setReturnSaleId(saleId);
            setSalesReturnItemQtys({}); // reset per-item qtys when sale changes
            const s = sales.find(sale => String(sale.id) === String(saleId));
            if (s) {
              const primaryMethod = (Array.isArray(s.payments) && s.payments.length > 0)
                ? (s.payments[0].type || 'cash')
                : (s.paymentMethod || 'cash');
              setReturnRefundMethod(primaryMethod);
            }
          };

          const handleSaveReturn = () => {
            if (!selectedSale) return;
            // A voided sale is cancelled -no return can be recorded against it.
            if (selectedSale.status === 'void') {
              showAlert(sw
                ? 'Mauzo haya yamebatilishwa. Hauwezi kurekodi urudishaji kwa mauzo yaliyobatilishwa.'
                : 'This sale has been voided. A return cannot be recorded against a voided sale.', { tone: 'error' });
              return;
            }
            // Collect per-item returns with qty > 0
            const returnItems = [];
            saleItems.forEach((it, idx) => {
              const q = parseFloat(salesReturnItemQtys[idx] || 0);
              if (q <= 0) return;
              const unit = parseFloat(it.sellingPrice || 0);
              const isService = it.type === 'service';
              const effectiveQty = isService ? 1 : q;
              // Resolve the product's relational UUID for movement persistence.
              // saleItems come from selectedSale.items which may carry productId.
              // If not, try to match against the products catalog by name.
              const resolvedProdId = it.productId || (products.find(p => lineMatchesProduct(it, p))?.id) || null;
              returnItems.push({
                productName: it.productName || '—',
                productId: resolvedProdId,
                qty: effectiveQty,
                unitPrice: unit,
                lineRefund: effectiveQty * unit,
                // Snapshot the ORIGINAL sale's per-unit cost so return COGS
                // reverses the true cost of sale, not a later WAC.
                unitCost: it.type === 'service' ? 0 : parseFloat(it.cost || 0),
                type: it.type || 'product',
              });
            });
            if (returnItems.length === 0) {
              showAlert(sw ? 'Weka idadi ya kurudisha kwa angalau bidhaa moja.' : 'Enter a return quantity for at least one item.', { tone: 'warn' });
              return;
            }
            const refundAmt = returnItems.reduce((s, r) => s + r.lineRefund, 0);
            // Recompute the split here (closure values may be slightly stale)
            const creditReduction = Math.min(refundAmt, remainingCredit);
            const cashRefund = Math.max(0, refundAmt - creditReduction);
            // Guard: never refund more cash than was actually paid on this sale
            if (cashRefund > remainingCashRefundCap + 0.005) {
              showAlert(
                sw
                  ? `Kiasi cha cash kinacho rudishwa (${fmt(cashRefund)}) kinazidi kile kilicholipwa kwa hela (${fmt(remainingCashRefundCap)}). Punguza idadi inayorudishwa.`
                  : `Cash refund (${fmt(cashRefund)}) would exceed what was actually paid in cash (${fmt(remainingCashRefundCap)}). Reduce the return quantity.`,
                { tone: 'error' }
              );
              return;
            }
            // Validate the return date -can't be before the sale's date or in the future
            const finalReturnDate = returnDate || new Date().toISOString().split('T')[0];
            const todayStr = new Date().toISOString().split('T')[0];
            if (finalReturnDate > todayStr) {
              showAlert(sw ? 'Tarehe ya kurudisha haiwezi kuwa siku za mbeleni.' : 'Return date cannot be in the future.', { tone: 'error' });
              return;
            }
            if (selectedSale.date && finalReturnDate < selectedSale.date) {
              showAlert(
                sw
                  ? `Tarehe ya kurudisha haiwezi kuwa kabla ya tarehe ya mauzo (${selectedSale.date}).`
                  : `Return date cannot be before the original sale date (${selectedSale.date}).`,
                { tone: 'error' }
              );
              return;
            }
            const retBaseType = baseType(returnRefundMethod);
            const ret = {
              id: Date.now(),
              originalSaleId: selectedSale.id,
              invoiceNumber: selectedSale.invoiceNumber,
              customerName: selectedSale.customerName,
              // Backward-compat single-item fields (first item)
              productName: returnItems[0].productName,
              qty: returnItems[0].qty,
              // Multi-item array
              items: returnItems,
              refundAmt,
              totalRefund: refundAmt,
              cashRefundAmt: cashRefund,           // money actually leaving a wallet
              creditReductionAmt: creditReduction, // amount taken off the customer's outstanding credit
              reason: returnReason,
              date: finalReturnDate,
              refundMethod: cashRefund > 0 ? retBaseType : 'credit',
              refundAccountKey: cashRefund > 0 ? returnRefundMethod : 'credit',
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            };
            setSalesReturns(prev => [...prev, ret]);
            // MIGRATION-ONLY dual-write: persist to relational DB
            if (businessId && currentUser?.accessToken) {
              persistSalesReturn(businessId, { ...ret, originalSaleRid: selectedSale._rid || null }, currentUser.accessToken).then(r => {
                const rid = r?.data?.sales_return_id;
                if (rid) setSalesReturns(prev => prev.map(sr => sr.id === ret.id ? { ...sr, _rid: rid, _rv: 1 } : sr));
              }).catch((err) =>
                console.warn('[MIGRATION-ONLY] sales return persist failed (non-blocking):', err.message));
            }
            // Restore stock per item (skip services), matched by productId.
            setProducts(prev => prev.map(p => {
              const matching = returnItems.find(ri => ri.type !== 'service' && lineMatchesProduct(ri, p));
              if (!matching) return p;
              const q = parseFloat(matching.qty || 0);
              return { ...p, balance: (p.balance ?? p.currentStock ?? p.openingStockQty ?? 0) + q, currentStock: (p.currentStock ?? p.openingStockQty ?? 0) + q };
            }));
            // Reduce the original sale's receivable by the credit-reduction amount
            if (creditReduction > 0) {
              setSales(prev => prev.map(s => {
                if (String(s.id) !== String(selectedSale.id)) return s;
                const prevReduced = parseFloat(s.creditReducedByReturns || 0);
                const newReduced = prevReduced + creditReduction;
                // Mark fully paid if credit is now fully consumed by returns
                const newCreditOutstanding = Math.max(0, creditOnSale - newReduced);
                const fullyClear = newCreditOutstanding < 0.005;
                return { ...s, creditReducedByReturns: newReduced, isPaid: fullyClear ? true : s.isPaid };
              }));
            }
            // Debit wallet ONLY for the cash portion -credit-only returns never touch a wallet
            if (cashRefund > 0) {
              const itemsLabel = returnItems.length === 1 ? returnItems[0].productName : `${returnItems.length} ${sw ? 'bidhaa' : 'items'}`;
              setManualTransactions(prev => [...prev, {
                id: Date.now() + 1,
                date: finalReturnDate,
                account: retBaseType,
                accountKey: returnRefundMethod,
                type: 'debit',
                amount: cashRefund,
                systemGenerated: true, bridgeType: 'sales_return',
                description: `${sw ? 'Bidhaa iliyorudishwa' : 'Sales Return'} ${itemsLabel} (${selectedSale.invoiceNumber || selectedSale.id})`
              }]);
            }
            // Show a confirming toast that explains exactly what happened
            const msg = (cashRefund > 0 && creditReduction > 0)
              ? (sw
                  ? `Urudishaji umehifadhiwa: ${fmt(cashRefund)} kurejeshwa kwa mkoba, ${fmt(creditReduction)} kuondolewa kwenye deni la mteja.`
                  : `Return saved: ${fmt(cashRefund)} refunded to wallet, ${fmt(creditReduction)} taken off customer's outstanding credit.`)
              : (cashRefund > 0
                  ? (sw ? `Urudishaji umehifadhiwa: ${fmt(cashRefund)} kurejeshwa kwa mkoba.` : `Return saved: ${fmt(cashRefund)} refunded to wallet.`)
                  : (sw ? `Urudishaji umehifadhiwa: ${fmt(creditReduction)} kuondolewa kwenye deni la mteja (hakuna fedha imehamishwa).` : `Return saved: ${fmt(creditReduction)} taken off customer's outstanding credit (no money moved).`));
            showAlert(msg, { tone: 'success' });
            setReturnSaleId('');
            setReturnQty('');
            setSalesReturnItemQtys({});
            setReturnReason('');
            setReturnRefundMethod('cash');
            setReturnDate(new Date().toISOString().split('T')[0]);
            setShowReturnsModal(false);
          };

          return (
            <div className="popup-overlay" style={{ zIndex: 1600 }}>
              <div className="popup-form mz-modal" style={{ maxWidth: 420, width: '100%', borderRadius: 16, padding: 24 }}>
                <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 20 }}>
                  {sw ? 'Rekodi Urudishaji' : 'Record Return / Refund'}
                </h3>

                {/* Select sale */}
                <div style={{ marginBottom: 14 }}>
                  <label style={{ display: 'block', fontWeight: 600, fontSize: 14, color: 'var(--text-primary)', marginBottom: 8 }}>
                    {sw ? 'Chagua Mauzo' : 'Select Sale'}
                  </label>
                  {/* SECTION 1 -Filter by Date */}
                  <div style={{ background: 'var(--bg-tab)', borderRadius: 10, padding: 10, marginBottom: 8 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>
                      {sw ? 'Chuja kwa Tarehe' : 'Filter by date'}
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <input type="date" value={returnSaleDate}
                        onChange={e => setReturnSaleDate(e.target.value)}
                        onFocus={e => { try { e.currentTarget.showPicker(); } catch(x){} }}
                        style={{ flex: 1, padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border-color)', background: 'var(--bg-white)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                      {returnSaleDate && (
                        <button type="button" onClick={() => setReturnSaleDate('')} style={{ width: 28, height: 28, borderRadius: 999, background: '#F3F4F6', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--text-muted)' }}>×</button>
                      )}
                    </div>
                  </div>
                  {/* SECTION 2 -Filter by Product or Customer */}
                  <div style={{ background: 'var(--bg-tab)', borderRadius: 10, padding: 10, marginBottom: 8 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>
                      {sw ? 'Chuja kwa Bidhaa au Mteja' : 'Filter by product or customer'}
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <select value={returnSaleProduct} onChange={e => setReturnSaleProduct(e.target.value)}
                        style={{ flex: 1, padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border-color)', background: 'var(--bg-white)', fontSize: 13, outline: 'none', minWidth: 0 }}>
                        <option value="">{sw ? 'Bidhaa zote' : 'All products'}</option>
                        {[...new Set((sales || []).filter(s => !s.isService).flatMap(s => Array.isArray(s.items) && s.items.length > 0 ? s.items.map(it => it.productName) : [s.productName]).filter(Boolean))].sort().map(name => (
                          <option key={name} value={name}>{name}</option>
                        ))}
                      </select>
                      <select value={returnSaleCustomer} onChange={e => setReturnSaleCustomer(e.target.value)}
                        style={{ flex: 1, padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border-color)', background: 'var(--bg-white)', fontSize: 13, outline: 'none', minWidth: 0 }}>
                        <option value="">{sw ? 'Wateja wote' : 'All customers'}</option>
                        {[...new Set((sales || []).filter(s => !s.isService).map(s => s.customerName).filter(Boolean))].sort().map(name => (
                          <option key={name} value={name}>{name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  {(() => {
                    // A VOIDED sale cannot be the parent of a NEW return -it is
                    // cancelled, so there is nothing to return against.
                    const sorted = [...(sales || []).filter(s => !s.isService && s.status !== 'void')].sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
                    const matchesFilter = (s) => {
                      if (returnSaleDate && s.date !== returnSaleDate) return false;
                      if (returnSaleProduct) {
                        const inItems = Array.isArray(s.items) && s.items.some(it => it.productName === returnSaleProduct);
                        const inTop = s.productName === returnSaleProduct;
                        if (!inItems && !inTop) return false;
                      }
                      if (returnSaleCustomer && s.customerName !== returnSaleCustomer) return false;
                      return true;
                    };
                    const matched = sorted.filter(matchesFilter);
                    const CAP = 50;
                    const visible = matched.slice(0, CAP);
                    return (
                      <>
                        <select value={returnSaleId} onChange={e => handleSaleSelect(e.target.value)}
                          style={{ width: '100%', padding: '9px 12px', borderRadius: 12, border: 'none', background: 'var(--bg-tab)', fontSize: 14, outline: 'none', boxSizing: 'border-box' }}>
                          <option value="">
                            {matched.length === 0
                              ? (sw ? '— hakuna mauzo yanayolingana —' : '— no matching sales —')
                              : (sw ? `— chagua mauzo (${matched.length}) —` : `— choose a sale (${matched.length} found) —`)}
                          </option>
                          {visible.map(s => {
                            const summary = Array.isArray(s.items) && s.items.length > 0
                              ? `${s.items[0].productName || '—'}${s.items.length > 1 ? ` +${s.items.length - 1}` : ''}`
                              : (s.productName || '—');
                            return (
                              <option key={s.id} value={s.id}>{s.invoiceNumber || s.id} · {s.date} · {summary} ({s.customerName || 'Walk-in'})</option>
                            );
                          })}
                        </select>
                        {matched.length > CAP && (
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                            {sw ? `Onyesha ${CAP} ya hivi karibuni kati ya ${matched.length}. Tumia vichujio kupunguza.` : `Showing ${CAP} most recent of ${matched.length}. Use filters to narrow down.`}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>

                {/* Original sale summary + payment breakdown */}
                {selectedSale && (
                  <div style={{ background: 'var(--bg-tab)', borderRadius: 12, padding: '10px 14px', marginBottom: 14 }}>
                    <div style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 600, marginBottom: 6 }}>
                      {sw ? 'Muhtasari wa Mauzo' : 'Original Sale'}: {saleItems.length} {saleItems.length === 1 ? (sw ? 'kipengele' : 'item') : (sw ? 'vipengele' : 'items')} · {sw ? 'Jumla' : 'Total'} {fmt(selectedSale.totalRevenue)}
                    </div>
                    {/* Payment breakdown */}
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 2 }}>{sw ? 'Ilipolipwa:' : 'Paid via:'}</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {salePayments.map((pmt, i) => (
                        <span key={i} style={{ fontSize: 11, fontWeight: 700, background: 'var(--bg-white)', borderRadius: 6, padding: '3px 8px', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}>
                          {payMethodLabel(pmt.type || pmt.method || 'cash')}{pmt.amount ? ` ${fmt(pmt.amount)}` : ''}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Return Date -defaults to today; not before sale date, not in the future */}
                {selectedSale && (
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ display: 'block', fontWeight: 600, fontSize: 14, color: 'var(--text-primary)', marginBottom: 6 }}>
                      {sw ? 'Tarehe ya Kurudisha' : 'Return Date'}
                    </label>
                    <input type="date"
                      value={returnDate}
                      min={selectedSale.date || undefined}
                      max={new Date().toISOString().split('T')[0]}
                      onChange={e => setReturnDate(e.target.value)}
                      style={{ width: '100%', padding: '9px 12px', borderRadius: 12, border: 'none', background: 'var(--bg-tab)', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                      {sw
                        ? `Tarehe halisi ya kurudisha (kuanzia tarehe ya mauzo ${selectedSale.date || ''} mpaka leo)`
                        : `When the return actually happened (between the original sale date ${selectedSale.date || ''} and today)`}
                    </div>
                  </div>
                )}

                {/* Per-item return qty (multi-item aware) */}
                {selectedSale && (
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ display: 'block', fontWeight: 600, fontSize: 14, color: 'var(--text-primary)', marginBottom: 6 }}>
                      {sw ? 'Idadi Inayorudishwa kwa Kila Bidhaa' : 'Return Qty per Item'}
                    </label>
                    <div style={{ background: 'var(--bg-tab)', borderRadius: 10, border: '1px solid var(--border-color)', overflow: 'hidden' }}>
                      {/* Header */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 70px 90px', gap: 6, padding: '8px 10px', background: '#F3F4F6', fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                        <div>{sw ? 'Bidhaa' : 'Product'}</div>
                        <div style={{ textAlign: 'right' }}>{sw ? 'Rudisha' : 'Return'}</div>
                        <div style={{ textAlign: 'right' }}>{sw ? 'Rejesho' : 'Refund'}</div>
                      </div>
                      {saleItems.map((it, idx) => {
                        const isService = it.type === 'service';
                        const origQty = isService ? 1 : parseFloat(it.quantity || 0);
                        const returnedBefore = alreadyReturnedBySaleProduct[it.productName] || 0;
                        const maxReturn = Math.max(0, origQty - returnedBefore);
                        const unitPrice = parseFloat(it.sellingPrice || 0);
                        const myQty = parseFloat(salesReturnItemQtys[idx] || 0);
                        const lineRefund = (isService ? (myQty > 0 ? 1 : 0) : myQty) * unitPrice;
                        return (
                          <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 70px 90px', gap: 6, padding: '10px 10px', borderTop: idx > 0 ? '1px solid #F3F4F6' : 'none', alignItems: 'center', opacity: maxReturn <= 0 ? 0.55 : 1 }}>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.productName || '—'}</div>
                              <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 1 }}>
                                {sw ? 'Asili' : 'Orig'}: {origQty} @ {fmt(unitPrice)}{isService ? ` · ${sw ? 'huduma' : 'service'}` : ''}
                                {returnedBefore > 0 && ` • ${returnedBefore} ${sw ? 'tayari' : 'returned'}`}
                                {maxReturn > 0 && ` • ${sw ? 'Kiwango' : 'Max'}: ${maxReturn}`}
                              </div>
                            </div>
                            {isService ? (
                              <button type="button"
                                onClick={() => setSalesReturnItemQtys(prev => ({ ...prev, [idx]: prev[idx] ? 0 : 1 }))}
                                disabled={maxReturn <= 0}
                                style={{ padding: '7px 6px', borderRadius: 8, border: '1px solid var(--border-color)', background: myQty > 0 ? '#1A4B3A' : 'var(--bg-white)', color: myQty > 0 ? '#fff' : '#111827', fontSize: 12, fontWeight: 700, cursor: maxReturn > 0 ? 'pointer' : 'not-allowed' }}>
                                {myQty > 0 ? 'Added' : (sw ? 'Chagua' : 'Pick')}
                              </button>
                            ) : (
                              <input type="number" min="0" max={maxReturn} step="any"
                                value={salesReturnItemQtys[idx] || ''}
                                onChange={e => {
                                  const v = e.target.value;
                                  const num = parseFloat(v);
                                  const capped = !isNaN(num) && num > maxReturn ? String(maxReturn) : v;
                                  setSalesReturnItemQtys(prev => ({ ...prev, [idx]: capped }));
                                }}
                                disabled={maxReturn <= 0}
                                placeholder="0"
                                style={{ width: '100%', padding: '7px 8px', borderRadius: 8, border: '1px solid var(--border-color)', background: maxReturn <= 0 ? '#F3F4F6' : 'var(--bg-white)', fontSize: 13, textAlign: 'right', outline: 'none', boxSizing: 'border-box' }} />
                            )}
                            <div style={{ textAlign: 'right', fontWeight: 700, fontSize: 13, color: lineRefund > 0 ? '#DC2626' : '#9CA3AF', whiteSpace: 'nowrap' }}>{fmt(lineRefund)}</div>
                          </div>
                        );
                      })}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: '#FEF2F2', borderTop: '2px solid #FECACA' }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#991B1B' }}>{sw ? 'Jumla ya Rejesho' : 'Total Refund'}</span>
                        <span style={{ fontSize: 16, fontWeight: 800, color: '#DC2626' }}>{fmt(totalRefund)}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Reason */}
                <div style={{ marginBottom: 14 }}>
                  <label style={{ display: 'block', fontWeight: 600, fontSize: 14, color: 'var(--text-primary)', marginBottom: 6 }}>
                    {sw ? 'Sababu' : 'Reason'}
                  </label>
                  <input type="text" placeholder={sw ? 'mfano: Kasoro, Bidhaa mbaya…' : 'e.g. Defective, Wrong item…'} value={returnReason} onChange={e => setReturnReason(e.target.value)}
                    style={{ width: '100%', padding: '9px 12px', borderRadius: 12, border: 'none', background: 'var(--bg-tab)', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
                </div>

                {/* Refund flow -credit-aware: split between wallet refund (cash portion)
                    and receivable reduction (credit portion). */}
                {selectedSale && totalRefund > 0 && (() => {
                  const refundOptions = buildPaymentOptions(false);
                  const selOption = refundOptions.find(o => o.value === returnRefundMethod) || refundOptions[0];
                  const selBalance = selOption ? getWalletBalance(selOption.value) : 0;
                  const newBalance = selBalance - cashRefundDue;
                  const insufficient = cashRefundDue > 0 && newBalance < 0;
                  const isCreditOnly = cashRefundDue === 0 && creditReductionDue > 0;
                  return (
                    <>
                      {/* Split breakdown -always show when there's anything to refund */}
                      <div style={{ background: '#F3F4F6', borderRadius: 12, padding: '10px 14px', marginBottom: 14, border: '1px solid var(--border-default)' }}>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>
                          {sw ? 'Mgawanyo wa Marejesho' : 'How this refund will be applied'}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {/* Sale composition recap */}
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-muted)' }}>
                            <span>{sw ? 'Mauzo yalilipwa kwa fedha' : 'Cash actually paid on sale'}</span>
                            <span style={{ fontFamily: 'monospace' }}>{fmt(cashPaidOnSale)}{alreadyRefundedCash > 0 ? ` − ${fmt(alreadyRefundedCash)}` : ''}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-muted)' }}>
                            <span>{sw ? 'Bado kwenye mkopo' : 'Still owed (credit)'}</span>
                            <span style={{ fontFamily: 'monospace' }}>{fmt(remainingCredit)}</span>
                          </div>
                          <div style={{ borderTop: '1px solid var(--border-default)', marginTop: 4, paddingTop: 6 }}>
                            {creditReductionDue > 0 && (
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 700, color: '#92400E' }}>
                                <span>↩ {sw ? 'Punguza deni la mteja' : 'Reduce customer credit'}</span>
                                <span style={{ fontFamily: 'monospace' }}>-{fmt(creditReductionDue)}</span>
                              </div>
                            )}
                            {cashRefundDue > 0 && (
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 700, color: '#DC2626', marginTop: creditReductionDue > 0 ? 4 : 0 }}>
                                <span>↩ {sw ? 'Rejesha kwa mkoba' : 'Refund to wallet'}</span>
                                <span style={{ fontFamily: 'monospace' }}>-{fmt(cashRefundDue)}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Wallet picker -only when there's actually cash to refund */}
                      {cashRefundDue > 0 && (
                        <div style={{ marginBottom: 14 }}>
                          <label style={{ display: 'block', fontWeight: 600, fontSize: 14, color: 'var(--text-primary)', marginBottom: 6 }}>
                            {sw ? `Rejesha ${fmt(cashRefundDue)} Kutoka` : `Refund ${fmt(cashRefundDue)} from Account`}
                          </label>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 220, overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: 10, padding: 6 }}>
                            {refundOptions.map(opt => {
                              const bal = getWalletBalance(opt.value);
                              const selected = returnRefundMethod === opt.value;
                              const wouldGoNegative = cashRefundDue > 0 && (bal - cashRefundDue) < 0;
                              return (
                                <button key={opt.value} type="button"
                                  onClick={() => setReturnRefundMethod(opt.value)}
                                  style={{
                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                    padding: '9px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
                                    background: selected ? '#1A4B3A' : 'var(--bg-tab)',
                                    color: selected ? '#fff' : 'var(--text-primary)',
                                    textAlign: 'left',
                                  }}>
                                  <span style={{ fontSize: 13, fontWeight: 600, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{opt.label}</span>
                                  <span style={{ fontSize: 12, fontWeight: 700, color: selected ? '#A7D5C0' : (wouldGoNegative ? '#DC2626' : '#059669'), marginLeft: 10, fontFamily: 'monospace' }}>
                                    {fmt(bal)}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                          {insufficient && (
                            <div style={{ fontSize: 11, color: '#DC2626', marginTop: 6, fontWeight: 700 }}>
                              ⚠ {sw ? 'Salio halitoshi kwenye akaunti hii' : 'Insufficient balance in this account'}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Credit-only note (no wallet picker shown) */}
                      {isCreditOnly && (
                        <div style={{ background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 12, padding: '12px 14px', marginBottom: 14 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: '#92400E', marginBottom: 4 }}>
                            {sw ? 'Hakuna fedha itahamishwa' : 'No money will move'}
                          </div>
                          <div style={{ fontSize: 11, color: '#92400E', lineHeight: 1.4 }}>
                            {sw
                              ? `Mauzo haya yalikuwa kwa mkopo -mteja hajalipa fedha bado. Kuhifadhi urudishaji huku kutapunguza deni la mteja kwa ${fmt(creditReductionDue)} tu, hakuna mkoba utaguswa.`
                              : `This sale was on credit -the customer hasn't actually paid yet. Saving this return will only reduce their outstanding balance by ${fmt(creditReductionDue)}; no wallet will be touched.`}
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}

                {cashRefundExceedsPaid && (
                  <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '8px 12px', marginBottom: 10, color: '#B91C1C', fontSize: 12, fontWeight: 600 }}>
                    {sw
                      ? `Cash refund inazidi kile kilicholipwa kwa hela (${fmt(remainingCashRefundCap)}). Punguza idadi.`
                      : `Cash refund exceeds what was paid in cash (${fmt(remainingCashRefundCap)}). Reduce the quantity.`}
                  </div>
                )}
                <div className="form-buttons">
                  <button className="save-btn" onClick={handleSaveReturn} disabled={!selectedSale || totalRefund <= 0 || cashRefundExceedsPaid}>
                    {sw ? `Hifadhi${totalRefund > 0 ? ` (${fmt(totalRefund)})` : ''}` : `Save Return${totalRefund > 0 ? ` (${fmt(totalRefund)})` : ''}`}
                  </button>
                  <button className="cancel-btn" onClick={() => { setShowReturnsModal(false); setReturnSaleId(''); setReturnQty(''); setSalesReturnItemQtys({}); setReturnReason(''); setReturnRefundMethod('cash'); setReturnDate(new Date().toISOString().split('T')[0]); }}>
                    {sw ? 'Ghairi' : 'Cancel'}
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* ── STOCK ADJUSTMENT MODAL ──────────────────────────────────── */}
        {showStockAdjModal && (
          <div className="popup-overlay" style={{ zIndex: 1600 }}>
            <div className="popup-form mz-modal" style={{ maxWidth: 420, width: '100%', borderRadius: 16, padding: 24 }}>
              <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 20 }}>{t.stockAdjustment || 'Stock Adjustment'}</h3>
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontWeight: 600, fontSize: 14, color: 'var(--text-primary)', marginBottom: 6 }}>Product</label>
                <select value={stockAdjFormData.productId} onChange={e => setStockAdjFormData(p => ({ ...p, productId: e.target.value }))}
                  style={{ width: '100%', padding: '9px 12px', borderRadius: 12, border: 'none', background: 'var(--bg-tab)', fontSize: 14, outline: 'none', boxSizing: 'border-box' }}>
                  <option value="">— select product —</option>
                  {(products || []).map(prod => (
                    <option key={prod.id} value={prod.id}>{prod.productName} (Stock: {prod.balance ?? prod.currentStock ?? prod.openingStockQty ?? 0})</option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
                {[{v:'increase',l:'+ Increase'},{v:'decrease',l:'− Decrease'}].map(o => (
                  <button key={o.v} type="button" onClick={() => setStockAdjFormData(p => ({ ...p, direction: o.v }))}
                    style={{ flex: 1, padding: '12px 0', borderRadius: 12, border: 'none', fontSize: 14, fontWeight: 600, cursor: 'pointer',
                      background: stockAdjFormData.direction === o.v ? (o.v === 'increase' ? '#D1FAE5' : '#FEE2E2') : 'var(--bg-tab)',
                      color: stockAdjFormData.direction === o.v ? (o.v === 'increase' ? '#065F46' : '#991B1B') : 'var(--text-secondary)' }}>{o.l}</button>
                ))}
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontWeight: 600, fontSize: 14, color: 'var(--text-primary)', marginBottom: 6 }}>Quantity</label>
                <input type="number" min="1" value={stockAdjFormData.qty} onChange={e => setStockAdjFormData(p => ({ ...p, qty: e.target.value }))}
                  style={{ width: '100%', padding: '9px 12px', borderRadius: 12, border: 'none', background: 'var(--bg-tab)', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontWeight: 600, fontSize: 14, color: 'var(--text-primary)', marginBottom: 6 }}>Reason</label>
                <select value={stockAdjFormData.reason} onChange={e => setStockAdjFormData(p => ({ ...p, reason: e.target.value }))}
                  style={{ width: '100%', padding: '9px 12px', borderRadius: 12, border: 'none', background: 'var(--bg-tab)', fontSize: 14, outline: 'none', boxSizing: 'border-box' }}>
                  <option value="damaged">Damaged / Expired</option>
                  <option value="stockcount">Stock Count Correction</option>
                  <option value="returned">Customer Return (received back)</option>
                  <option value="loss">Loss / Theft</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontWeight: 600, fontSize: 14, color: 'var(--text-primary)', marginBottom: 6 }}>Notes (optional)</label>
                <input type="text" placeholder="Additional details…" value={stockAdjFormData.notes} onChange={e => setStockAdjFormData(p => ({ ...p, notes: e.target.value }))}
                  style={{ width: '100%', padding: '9px 12px', borderRadius: 12, border: 'none', background: 'var(--bg-tab)', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div className="form-buttons">
                <button className="save-btn" onClick={() => {
                  const prod = (products || []).find(p => String(p.id) === String(stockAdjFormData.productId));
                  if (!prod || !stockAdjFormData.qty) return;
                  const qty = parseFloat(stockAdjFormData.qty) || 0;
                  const sign = stockAdjFormData.direction === 'increase' ? 1 : -1;
                  const oldStock = prod.balance ?? prod.currentStock ?? prod.openingStockQty ?? 0;
                  const newStock = Math.max(0, oldStock + sign * qty);
                  const actualDelta = Math.abs(newStock - oldStock); // clamped-safe qty
                  setProducts(prev => prev.map(p => String(p.id) === String(stockAdjFormData.productId) ? { ...p, balance: newStock, currentStock: newStock, updatedAt: new Date().toISOString() } : p));
                  setStockAdjHistory(prev => [...prev, { id: Date.now(), productId: prod.id, productName: prod.productName, direction: stockAdjFormData.direction, qty, reason: stockAdjFormData.reason, notes: stockAdjFormData.notes, date: stockAdjFormData.date, oldStock, newStock, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() }]);
                  // ── Cost a shrinkage write-off to profit ──────────────────
                  // A decrease for damage/loss/theft/correction removes real
                  // inventory value. Book it as a NON-CASH "Stock write-off"
                  // expense (qty × WAC) so profit reflects the loss. No wallet
                  // is touched (paymentMethod 'none'). 'returned' is an increase
                  // reason and never written off here.
                  const sw2 = language === 'sw';
                  if (sign === -1 && stockAdjFormData.reason !== 'returned' && actualDelta > 0) {
                    const wac = getProductWAC(prod.productName);
                    const unitCost = wac > 0 ? wac : (parseFloat(prod.costPrice || 0) || 0);
                    const writeoffValue = actualDelta * unitCost;
                    if (writeoffValue > 0) {
                      const reasonLabelMap = sw2
                        ? { damaged: 'Imeharibika/Imeisha', loss: 'Hasara/Wizi', stockcount: 'Marekebisho ya Hesabu', other: 'Nyingine' }
                        : { damaged: 'Damaged / Expired', loss: 'Loss / Theft', stockcount: 'Stock Count Correction', other: 'Other' };
                      const rLabel = reasonLabelMap[stockAdjFormData.reason] || stockAdjFormData.reason;
                      const writeoffExp = {
                        id: Date.now() + 1,
                        category: sw2 ? 'Hasara ya Bidhaa' : 'Stock Write-off',
                        description: `${rLabel} ${prod.productName} × ${actualDelta}`,
                        amount: writeoffValue,
                        date: stockAdjFormData.date,
                        paymentMethod: 'none',   // non-cash: value leaves inventory, not a wallet
                        isPaid: true,
                        isStockWriteoff: true,
                      };
                      setExpenses(prev => [...prev, writeoffExp]);
                      // MIGRATION-ONLY dual-write: persist stock write-off expense
                      if (businessId && currentUser?.accessToken) {
                        persistExpense(businessId, writeoffExp, currentUser.accessToken, paymentAccounts).then(r => {
                          const rid = r?.data?.expense_id;
                          if (rid) setExpenses(prev => prev.map(e => e.id === writeoffExp.id ? { ...e, _rid: rid, _rv: 1 } : e));
                        }).catch(err => console.warn('[dual-write] stockWriteoff:', err.message));
                      }
                    }
                  }
                  setStockAdjFormData({ productId: '', qty: '', reason: 'damaged', direction: 'decrease', notes: '', date: new Date().toISOString().split('T')[0] });
                  setShowStockAdjModal(false);
                }} disabled={!stockAdjFormData.productId || !stockAdjFormData.qty}>Save Adjustment</button>
                <button className="cancel-btn" onClick={() => setShowStockAdjModal(false)}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* ── PARTIAL PAYMENT MODAL ────────────────────────────────────── */}
        {showPartialPayModal && partialPaySale && (() => {
          const totalOwed = parseFloat(partialPaySale.totalRevenue || 0);
          const alreadyPaid = (partialPaySale.payments || []).filter(p => p.amount).reduce((s, p) => s + parseFloat(p.amount || 0), 0);
          const remaining = Math.max(0, totalOwed - alreadyPaid);
          const payOpts = buildPaymentOptions(false);
          const handleSavePartialPay = () => {
            const newEntries = payRows.map(r => ({
              type: r.type,
              amount: parseFloat(r.amountRef.current || 0),
              date: payDate || new Date().toISOString().split('T')[0],
            })).filter(r => r.amount > 0);
            if (newEntries.length === 0) return;
            const totalNew = newEntries.reduce((s, r) => s + r.amount, 0);
            const newPayments = [...(partialPaySale.payments || []), ...newEntries];
            const newTotal = newPayments.reduce((s, p) => s + parseFloat(p.amount || 0), 0);
            const isPaid = newTotal >= totalOwed;
            const isManual = partialPaySale.isManualInvoice === true;

            if (isManual) {
              // ── Manual invoice payment ─────────────────────────────────
              if (isPaid) {
                // Fully paid → convert to sale, move to Sales Invoices
                const newSale = {
                  id: partialPaySale.id,
                  invoiceNumber: partialPaySale.invoiceNumber,
                  customerName: partialPaySale.customerName,
                  customerPhone: partialPaySale.customerPhone || '',
                  customerAddress: partialPaySale.customerAddress || '',
                  date: partialPaySale.date,
                  items: partialPaySale.items || [],
                  productName: (partialPaySale.items || [])[0]?.productName || '',
                  quantity: (partialPaySale.items || [])[0]?.quantity || 1,
                  sellingPrice: (partialPaySale.items || [])[0]?.sellingPrice || 0,
                  cost: (partialPaySale.items || [])[0]?.cost || 0,
                  totalRevenue: partialPaySale.totalRevenue,
                  profit: partialPaySale.totalRevenue,
                  taxes: partialPaySale.taxes || [],
                  discount: partialPaySale.discount || 0,
                  payments: newPayments,
                  paymentMethod: newEntries[0]?.type || 'cash',
                  isPaid: true,
                  paidDate: payDate || new Date().toISOString().split('T')[0],
                  convertedFromManual: true,
                  createdAt: partialPaySale.createdAt,
                };
                setSales(prev => [...prev, newSale]);
                setIssuedInvoices(prev => [...prev, {
                  id: newSale.id,
                  invoiceNumber: newSale.invoiceNumber,
                  customerName: newSale.customerName,
                  date: newSale.date,
                  totalRevenue: newSale.totalRevenue,
                  isPaid: true,
                  payments: newPayments,
                  items: newSale.items,
                  convertedFromManual: true,
                }]);
                // MIGRATION-ONLY: convert invoice → sale in relational DB
                const invoiceRid = resolveRid(partialPaySale);
                if (invoiceRid && businessId && currentUser?.accessToken) {
                  bridgeConvertInvoiceToSale(businessId, invoiceRid, newSale, currentUser.accessToken, paymentAccounts)
                    .then(r => {
                      const saleRid = r?.data?.sale_id;
                      if (saleRid) setSales(prev => prev.map(s => s.id === newSale.id ? { ...s, _rid: saleRid, _rv: 1 } : s));
                    })
                    .catch(err => console.warn('[MIGRATION-ONLY] invoice→sale conversion failed:', err.message));
                }
                setManualInvoices(prev => softDeleteById(prev, partialPaySale.id));
              } else {
                // Partially paid -stay in manual invoices list
                setManualInvoices(prev => prev.map(i => i.id === partialPaySale.id ? { ...i, payments: newPayments, isPaid: false } : i));
              }
            } else {
              // ── Regular sale invoice payment ───────────────────────────
              setSales(prev => prev.map(s => s.id === partialPaySale.id ? { ...s, payments: newPayments, isPaid } : s));
              setIssuedInvoices(prev => prev.map(i => i.id === partialPaySale.id ? { ...i, payments: newPayments, isPaid } : i));
            }

            // MIGRATION-ONLY: record payments in relational DB
            if (businessId && currentUser?.accessToken) {
              const rid = resolveRid(partialPaySale);
              if (rid) {
                newEntries.forEach(entry => {
                  if (isManual) {
                    bridgeRecordInvoiceDeposit(businessId, rid, entry, currentUser.accessToken, paymentAccounts)
                      .catch(err => console.warn('[MIGRATION-ONLY] invoice deposit failed:', err.message));
                  } else {
                    bridgeRecordSalePayment(businessId, rid, entry, currentUser.accessToken, paymentAccounts)
                      .catch(err => console.warn('[MIGRATION-ONLY] sale payment failed:', err.message));
                  }
                });
              }
            }

            // Record each payment into cashflow
            newEntries.forEach(entry => {
              if (entry.type !== 'credit') {
                setManualTransactions(prev => [...prev, {
                  id: Date.now() + Math.random(),
                  type: 'credit',
                  account: entry.type,
                  amount: entry.amount,
                  date: entry.date,
                  systemGenerated: true, bridgeType: 'debt_collection',
                  description: `Payment received ${partialPaySale.invoiceNumber}${partialPaySale.customerName ? ' · ' + partialPaySale.customerName : ''}`,
                }]);
              }
            });
            setPayRows([{ id: 1, type: 'cash', amountRef: { current: '' } }]);
            setShowPartialPayModal(false);
            setSuccessMessage(
              isPaid && isManual
                ? `${partialPaySale.invoiceNumber} paid in full - converted to Sales Invoice`
                : isPaid
                  ? `Payment recorded ${partialPaySale.invoiceNumber} marked as Paid in Full`
                  : `Payment of ${fmt(totalNew)} recorded for ${partialPaySale.invoiceNumber}`
            );
            setTimeout(() => setSuccessMessage(''), 4000);
          };
          return (
            <div className="popup-overlay" style={{ zIndex: 100001 }}>
              <div className="popup-form mz-modal" style={{ maxWidth: 420, width: '100%', borderRadius: 16, padding: 24 }}>
                <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>Record Payment</h3>
                <div style={{ background: 'var(--bg-tab)', borderRadius: 12, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: 'var(--text-secondary)' }}>
                  <div>Invoice: <strong>{partialPaySale.invoiceNumber}</strong></div>
                  <div>Total: <strong>{fmt(totalOwed)}</strong> &nbsp;|&nbsp; Paid: <strong>{fmt(alreadyPaid)}</strong> &nbsp;|&nbsp; Balance: <strong style={{ color: '#DC2626' }}>{fmt(remaining)}</strong></div>
                </div>
                {/* Payment Date */}
                <div style={{ marginBottom: 14 }}>
                  <label style={{ display: 'block', fontWeight: 600, fontSize: 14, color: 'var(--text-primary)', marginBottom: 6 }}>Payment Date</label>
                  <input type="date" value={payDate || new Date().toISOString().split('T')[0]} onChange={e => setPayDate(e.target.value)}
                    style={{ width: '100%', padding: '9px 12px', borderRadius: 12, border: 'none', background: 'var(--bg-tab)', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
                </div>
                {/* Payment rows */}
                <div style={{ marginBottom: 6 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <label style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-primary)' }}>Payment Method &amp; Amount</label>
                    {payRows.length < 3 && (
                      <button type="button" onClick={() => setPayRows(prev => [...prev, { id: Date.now(), type: payOpts[0]?.value || 'cash', amountRef: { current: '' } }])}
                        style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', background: 'var(--bg-input)', border: 'none', borderRadius: 8, padding: '4px 10px', cursor: 'pointer' }}>+ Add</button>
                    )}
                  </div>
                  {payRows.map((row, idx) => (
                    <div key={row.id} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                      <select value={row.type}
                        onChange={e => setPayRows(prev => prev.map((r, i) => i === idx ? { ...r, type: e.target.value } : r))}
                        style={{ flex: 1, padding: '9px 10px', borderRadius: 12, border: 'none', background: 'var(--bg-tab)', fontSize: 13, outline: 'none' }}>
                        {payOpts.map(o => <option key={o.value + o.label} value={o.value}>{o.label}</option>)}
                      </select>
                      <input type="number" min="0" placeholder="Amount" defaultValue=""
                        ref={el => { if (el) row.amountRef.current = el.value; }}
                        onChange={e => { row.amountRef.current = e.target.value; }}
                        style={{ width: 110, padding: '9px 10px', borderRadius: 12, border: 'none', background: 'var(--bg-tab)', fontSize: 14, outline: 'none', fontWeight: 600, boxSizing: 'border-box' }} />
                      {payRows.length > 1 && (
                        <button type="button" onClick={() => setPayRows(prev => prev.filter((_, i) => i !== idx))}
                          style={{ width: 32, height: 38, borderRadius: 8, border: 'none', background: '#FEE2E2', color: '#DC2626', fontSize: 18, fontWeight: 700, cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                      )}
                    </div>
                  ))}
                </div>
                <div className="form-buttons" style={{ marginTop: 16 }}>
                  <button className="save-btn" onClick={handleSavePartialPay}>Record Payment</button>
                  <button className="cancel-btn" onClick={() => setShowPartialPayModal(false)}>Cancel</button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* ── PURCHASE RETURN MODAL ───────────────────────────────────── */}
        {showPurchaseReturnModal && (() => {
          const selectedPurchase = purchases.find(p => String(p.id) === String(retPurchaseId));
          const sw = language === 'sw';
          // Normalised item list (handles both legacy single-item and multi-item records)
          const purchaseItems = selectedPurchase ? (
            Array.isArray(selectedPurchase.items) && selectedPurchase.items.length > 0
              ? selectedPurchase.items
              : [{ productName: selectedPurchase.productName, quantity: selectedPurchase.quantity, unitPrice: selectedPurchase.unitPrice || selectedPurchase.buyingPrice || selectedPurchase.unitCost || selectedPurchase.costPrice || 0, totalCost: selectedPurchase.totalCost }]
          ) : [];
          // Already-returned qty per product
          const alreadyReturnedByProduct = {};
          (purchaseReturns || []).filter(r => r.status !== 'void' && String(r.originalPurchaseId) === String(retPurchaseId)).forEach(r => {
            if (Array.isArray(r.items) && r.items.length > 0) {
              r.items.forEach(ri => { alreadyReturnedByProduct[ri.productName] = (alreadyReturnedByProduct[ri.productName] || 0) + parseFloat(ri.qty || 0); });
            } else if (r.productName) {
              alreadyReturnedByProduct[r.productName] = (alreadyReturnedByProduct[r.productName] || 0) + parseFloat(r.qty || 0);
            }
          });
          // Helper: get unit price for an item
          const unitPriceFor = (it) => parseFloat(it.unitPrice ?? it.unitCost ?? (parseFloat(it.quantity || 0) > 0 ? parseFloat(it.totalCost || 0) / parseFloat(it.quantity) : 0));
          // Running total refund
          let totalRefund = 0;
          purchaseItems.forEach((it, idx) => {
            const q = parseFloat(retItemQtys[idx] || 0);
            totalRefund += q * unitPriceFor(it);
          });

          const handleSavePurchaseReturn = () => {
            if (!selectedPurchase) return;
            // A voided purchase is cancelled -no return can be recorded against it.
            if (selectedPurchase.status === 'void') {
              showAlert(language === 'sw'
                ? 'Manunuzi haya yamebatilishwa. Hauwezi kurekodi urudishaji kwa manunuzi yaliyobatilishwa.'
                : 'This purchase has been voided. A return cannot be recorded against a voided purchase.', { tone: 'error' });
              return;
            }
            const returnItems = [];
            purchaseItems.forEach((it, idx) => {
              const qty = parseFloat(retItemQtys[idx] || 0);
              if (qty <= 0) return;
              const unitPrice = unitPriceFor(it);
              // Resolve the product's relational UUID for movement persistence.
              // purchaseItems come from selectedPurchase.items which may carry productId.
              // If not, try to match against the products catalog by name.
              const resolvedProductId = it.productId || (products.find(p => lineMatchesProduct(it, p))?.id) || null;
              returnItems.push({ productName: it.productName || '—', productId: resolvedProductId, qty, unitPrice, unitCost: unitPriceFor(it), lineRefund: qty * unitPrice });
            });
            if (returnItems.length === 0) {
              showAlert(sw ? 'Weka idadi ya kurudisha kwa angalau bidhaa moja.' : 'Enter a return quantity for at least one item.', { tone: 'warn' });
              return;
            }
            const totalRefundAmt = returnItems.reduce((s, r) => s + r.lineRefund, 0);
            // Validate the return date -can't be before the purchase date or in the future
            const finalRetDate = retDate || new Date().toISOString().split('T')[0];
            const todayStr = new Date().toISOString().split('T')[0];
            if (finalRetDate > todayStr) {
              showAlert(sw ? 'Tarehe ya kurudisha haiwezi kuwa siku za mbeleni.' : 'Return date cannot be in the future.', { tone: 'error' });
              return;
            }
            if (selectedPurchase.date && finalRetDate < selectedPurchase.date) {
              showAlert(
                sw
                  ? `Tarehe ya kurudisha haiwezi kuwa kabla ya tarehe ya ununuzi (${selectedPurchase.date}).`
                  : `Return date cannot be before the original purchase date (${selectedPurchase.date}).`,
                { tone: 'error' }
              );
              return;
            }
            // Save return record -store both broad type and composite accountKey
            const retBaseT = baseType(retRefundMethod);
            // ── Explicit cash / payable split (mirrors sales returns) ──────
            // Supplier-credit refund ('credit') reduces what we still OWE the
            // supplier (payable), up to the amount still outstanding -no money
            // moves. A wallet refund is cash coming back IN. This split lets
            // payables and (later) the payment ledger read clean fields instead
            // of inferring intent from the refund method.
            const purchaseTotal = getPurchaseGrandTotal(selectedPurchase);
            const purchaseCashPaid = Array.isArray(selectedPurchase.payments)
              ? selectedPurchase.payments.filter(x => x.type !== 'credit').reduce((a, x) => a + parseFloat(x.amount || 0), 0)
              : (selectedPurchase.isPaid === false ? 0 : purchaseTotal);
            const alreadyReducedPayable = (purchaseReturns || [])
              .filter(r => String(r.originalPurchaseId) === String(selectedPurchase.id))
              .reduce((a, r) => a + parseFloat(r.payableReductionAmt || 0), 0);
            const remainingPayable = Math.max(0, purchaseTotal - purchaseCashPaid - alreadyReducedPayable);
            const payableReductionAmt = retBaseT === 'credit' ? Math.min(totalRefundAmt, remainingPayable) : 0;
            const cashRefundAmt = retBaseT === 'credit' ? 0 : totalRefundAmt;
            const newPurchaseReturn = {
              id: Date.now(),
              originalPurchaseId: selectedPurchase.id,
              supplier: selectedPurchase.supplier,
              items: returnItems,
              totalRefund: totalRefundAmt,
              refundAmt: totalRefundAmt, // backward-compat
              cashRefundAmt,             // money actually coming back into a wallet
              payableReductionAmt,       // amount knocked off what we owe the supplier
              reason: retReason,
              restock: retRestock,
              refundMethod: retBaseT,            // legacy broad type
              refundAccountKey: retRefundMethod, // composite (e.g. 'bank|abc123' or 'credit')
              date: finalRetDate,
              productName: returnItems[0].productName, // backward-compat
              qty: returnItems[0].qty, // backward-compat
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            };
            setPurchaseReturns(prev => [...prev, newPurchaseReturn]);
            // MIGRATION-ONLY dual-write: persist to relational DB
            if (businessId && currentUser?.accessToken) {
              persistPurchaseReturn(businessId, { ...newPurchaseReturn, originalPurchaseRid: selectedPurchase._rid || null }, currentUser.accessToken).then(r => {
                const rid = r?.data?.purchase_return_id;
                if (rid) setPurchaseReturns(prev => prev.map(pr => pr.id === newPurchaseReturn.id ? { ...pr, _rid: rid, _rv: 1 } : pr));
              }).catch((err) =>
                console.warn('[MIGRATION-ONLY] purchase return persist failed (non-blocking):', err.message));
            }
            // Reduce stock for items physically returned to supplier
            if (retRestock) {
              setProducts(prev => prev.map(p => {
                const matching = returnItems.find(ri => lineMatchesProduct(ri, p));
                if (!matching) return p;
                const q = parseFloat(matching.qty || 0);
                // Signed (no clamp) -surface discrepancies rather than hide them.
                return { ...p, balance: (p.balance ?? p.currentStock ?? p.openingStockQty ?? 0) - q, currentStock: (p.currentStock ?? p.openingStockQty ?? 0) - q };
              }));
            }
            // Supplier-credit refund: reduce the payable on the original purchase
            // (and mark it paid if the credit fully clears what we owed).
            if (payableReductionAmt > 0) {
              setPurchases(prev => prev.map(pu => {
                if (String(pu.id) !== String(selectedPurchase.id)) return pu;
                const prevReduced = parseFloat(pu.payableReducedByReturns || 0);
                const newReduced = prevReduced + payableReductionAmt;
                const stillOwed = Math.max(0, purchaseTotal - purchaseCashPaid - newReduced);
                return { ...pu, payableReducedByReturns: newReduced, isPaid: stillOwed < 0.005 ? true : pu.isPaid };
              }));
            }
            // Credit our chosen wallet ONLY for the cash portion -a supplier-
            // credit refund moves no money (handled above via the payable).
            if (cashRefundAmt > 0) {
              setManualTransactions(prev => [...prev, {
                id: Date.now() + 1,
                type: 'credit',
                account: retBaseT,
                accountKey: retRefundMethod,
                amount: cashRefundAmt,
                date: finalRetDate,
                systemGenerated: true, bridgeType: 'purchase_return',
                description: `${sw ? 'Rudisha Manunuzi' : 'Purchase Return'}: ${selectedPurchase.supplier || '—'}`,
              }]);
            }
            setRetPurchaseId('');
            setRetItemQtys({});
            setRetQty('');
            setRetReason('');
            setRetSearchQuery('');
            setRetFilterDate('');
            setRetFilterProduct('');
            setRetFilterSupplier('');
            setRetRestock(true);
            setRetRefundMethod('cash');
            setRetDate(new Date().toISOString().split('T')[0]);
            setShowPurchaseReturnModal(false);
            showToast(sw ? `Rudisho la ${fmt(totalRefundAmt)} limehifadhiwa` : `Purchase return of ${fmt(totalRefundAmt)} saved`);
          };
          return (
            <div className="popup-overlay" style={{ zIndex: 1600 }}>
              <div className="popup-form mz-modal" style={{ maxWidth: 420, width: '100%', borderRadius: 16, padding: 24 }}>
                <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 20 }}>
                  {language === 'sw' ? '↩ Rudisha Manunuzi' : '↩ Purchase Return'}
                </h3>
                <div style={{ marginBottom: 14 }}>
                  <label style={{ display: 'block', fontWeight: 600, fontSize: 14, color: 'var(--text-primary)', marginBottom: 8 }}>
                    {language === 'sw' ? 'Chagua Ununuzi' : 'Select Purchase'}
                  </label>
                  {/* SECTION 1 -Filter by Date */}
                  <div style={{ background: 'var(--bg-tab)', borderRadius: 10, padding: 10, marginBottom: 8 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>
                      {language === 'sw' ? 'Chuja kwa Tarehe' : 'Filter by date'}
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <input type="date" value={retFilterDate}
                        onChange={e => setRetFilterDate(e.target.value)}
                        onFocus={e => { try { e.currentTarget.showPicker(); } catch(x){} }}
                        style={{ flex: 1, padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border-color)', background: 'var(--bg-white)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                      {retFilterDate && (
                        <button type="button" onClick={() => setRetFilterDate('')} style={{ width: 28, height: 28, borderRadius: 999, background: '#F3F4F6', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--text-muted)' }}>×</button>
                      )}
                    </div>
                  </div>
                  {/* SECTION 2 -Filter by Product or Supplier */}
                  <div style={{ background: 'var(--bg-tab)', borderRadius: 10, padding: 10, marginBottom: 8 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>
                      {language === 'sw' ? 'Chuja kwa Bidhaa au Muuzaji' : 'Filter by product or supplier'}
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <select value={retFilterProduct} onChange={e => setRetFilterProduct(e.target.value)}
                        style={{ flex: 1, padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border-color)', background: 'var(--bg-white)', fontSize: 13, outline: 'none', minWidth: 0 }}>
                        <option value="">{language === 'sw' ? 'Bidhaa zote' : 'All products'}</option>
                        {[...new Set((purchases || []).flatMap(p => Array.isArray(p.items) && p.items.length > 0 ? p.items.map(it => it.productName) : [p.productName]).filter(Boolean))].sort().map(name => (
                          <option key={name} value={name}>{name}</option>
                        ))}
                      </select>
                      <select value={retFilterSupplier} onChange={e => setRetFilterSupplier(e.target.value)}
                        style={{ flex: 1, padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border-color)', background: 'var(--bg-white)', fontSize: 13, outline: 'none', minWidth: 0 }}>
                        <option value="">{language === 'sw' ? 'Wauzaji wote' : 'All suppliers'}</option>
                        {[...new Set((purchases || []).map(p => p.supplier).filter(Boolean))].sort().map(name => (
                          <option key={name} value={name}>{name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  {/* Matching select dropdown */}
                  {(() => {
                    // A VOIDED purchase cannot be the parent of a NEW return.
                    const sorted = [...(purchases || []).filter(p => p.status !== 'void')].sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
                    const matches = (p) => {
                      if (retFilterDate && p.date !== retFilterDate) return false;
                      if (retFilterProduct) {
                        const inItems = Array.isArray(p.items) && p.items.some(it => it.productName === retFilterProduct);
                        const inTop = p.productName === retFilterProduct;
                        if (!inItems && !inTop) return false;
                      }
                      if (retFilterSupplier && p.supplier !== retFilterSupplier) return false;
                      return true;
                    };
                    const matched = sorted.filter(matches);
                    const CAP = 50;
                    const visible = matched.slice(0, CAP);
                    return (
                      <>
                        <select value={retPurchaseId} onChange={e => setRetPurchaseId(e.target.value)}
                          style={{ width: '100%', padding: '9px 12px', borderRadius: 12, border: 'none', background: 'var(--bg-tab)', fontSize: 14, outline: 'none', boxSizing: 'border-box' }}>
                          <option value="">
                            {matched.length === 0
                              ? (language === 'sw' ? '— hakuna ununuzi unaolingana —' : '— no matching purchases —')
                              : (language === 'sw' ? `— chagua ununuzi (${matched.length}) —` : `— choose a purchase (${matched.length} found) —`)}
                          </option>
                          {visible.map(p => {
                            const itemSummary = Array.isArray(p.items) && p.items.length > 0
                              ? `${p.items[0].productName || '—'}${p.items.length > 1 ? ` +${p.items.length - 1}` : ''}`
                              : (p.productName || '—');
                            return (
                              <option key={p.id} value={p.id}>{p.date} · {itemSummary} ({p.supplier || (language === 'sw' ? 'Bila muuzaji' : 'No supplier')}) · {fmt(getPurchaseGrandTotal(p))}</option>
                            );
                          })}
                        </select>
                        {matched.length > CAP && (
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                            {language === 'sw'
                              ? `Onyesha ${CAP} ya hivi karibuni kati ya ${matched.length}. Tumia vichujio kupunguza.`
                              : `Showing ${CAP} most recent of ${matched.length}. Use filters to narrow down.`}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
                {selectedPurchase && (
                  <>
                  {/* ITEMS CHECKLIST */}
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ display: 'block', fontWeight: 600, fontSize: 14, color: 'var(--text-primary)', marginBottom: 8 }}>
                      {sw ? 'Bidhaa za Kurudisha' : 'Items to Return'}
                    </label>
                    <div style={{ border: '1px solid var(--border-color)', borderRadius: 12, overflow: 'hidden' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 70px 90px', gap: 6, padding: '8px 10px', background: 'var(--bg-tab)', fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.3 }}>
                        <div>{sw ? 'Bidhaa' : 'Product'}</div>
                        <div style={{ textAlign: 'right' }}>{sw ? 'Rudisha' : 'Return'}</div>
                        <div style={{ textAlign: 'right' }}>{sw ? 'Rejesho' : 'Refund'}</div>
                      </div>
                      {purchaseItems.map((it, idx) => {
                        const origQty = parseFloat(it.quantity || 0);
                        const returnedBefore = alreadyReturnedByProduct[it.productName] || 0;
                        const maxReturn = Math.max(0, origQty - returnedBefore);
                        const unitPrice = unitPriceFor(it);
                        const myQty = parseFloat(retItemQtys[idx] || 0);
                        const lineRefund = myQty * unitPrice;
                        return (
                          <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 70px 90px', gap: 6, padding: '10px 10px', borderTop: idx > 0 ? '1px solid #F3F4F6' : 'none', alignItems: 'center', opacity: maxReturn <= 0 ? 0.55 : 1 }}>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.productName || '—'}</div>
                              <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 1 }}>
                                {sw ? 'Asili' : 'Orig'}: {origQty} @ {fmt(unitPrice)}
                                {returnedBefore > 0 && ` • ${returnedBefore} ${sw ? 'tayari' : 'returned'}`}
                                {maxReturn > 0 && ` • ${sw ? 'Kiwango' : 'Max'}: ${maxReturn}`}
                              </div>
                            </div>
                            <input type="number" min="0" max={maxReturn} step="any"
                              value={retItemQtys[idx] || ''}
                              onChange={e => {
                                const v = e.target.value;
                                const num = parseFloat(v);
                                const capped = !isNaN(num) && num > maxReturn ? String(maxReturn) : v;
                                setRetItemQtys(prev => ({ ...prev, [idx]: capped }));
                              }}
                              disabled={maxReturn <= 0}
                              placeholder="0"
                              style={{ width: '100%', padding: '7px 8px', borderRadius: 8, border: '1px solid var(--border-color)', background: maxReturn <= 0 ? '#F3F4F6' : 'var(--bg-white)', fontSize: 13, textAlign: 'right', outline: 'none', boxSizing: 'border-box' }} />
                            <div style={{ textAlign: 'right', fontWeight: 700, fontSize: 13, color: lineRefund > 0 ? '#3730A3' : '#9CA3AF', whiteSpace: 'nowrap' }}>{fmt(lineRefund)}</div>
                          </div>
                        );
                      })}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: '#F5F3FF', borderTop: '2px solid #C7D2FE' }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#3730A3' }}>{sw ? 'Jumla ya Rejesho' : 'Total Refund'}</span>
                        <span style={{ fontSize: 16, fontWeight: 800, color: '#3730A3' }}>{fmt(totalRefund)}</span>
                      </div>
                    </div>
                  </div>

                  {/* REFUND METHOD -per-account picker with live balance */}
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ display: 'block', fontWeight: 600, fontSize: 14, color: 'var(--text-primary)', marginBottom: 6 }}>
                      {sw ? 'Rejesho la fedha kupitia' : 'Refund credited to'}
                    </label>
                    {(() => {
                      const purRetOptions = buildPaymentOptions(false);
                      // Add supplier-credit option at the end
                      const allOptions = [
                        ...purRetOptions,
                        { value: 'credit', type: 'credit', label: sw ? '⚠ Mkopo wa Muuzaji (punguza deni)' : '⚠ Supplier Credit (reduce payables)' },
                      ];
                      const selOpt = allOptions.find(o => o.value === retRefundMethod) || allOptions[0];
                      const isSupplierCredit = selOpt?.type === 'credit';
                      const curBal = !isSupplierCredit ? getWalletBalance(selOpt.value) : 0;
                      const newBal = curBal + totalRefund;
                      return (
                        <>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 240, overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: 10, padding: 6 }}>
                            {allOptions.map(opt => {
                              const selected = retRefundMethod === opt.value;
                              const isCreditOpt = opt.type === 'credit';
                              const bal = !isCreditOpt ? getWalletBalance(opt.value) : null;
                              return (
                                <button key={opt.value} type="button"
                                  onClick={() => setRetRefundMethod(opt.value)}
                                  style={{
                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                    padding: '9px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
                                    background: selected ? '#1A4B3A' : 'var(--bg-tab)',
                                    color: selected ? '#fff' : 'var(--text-primary)',
                                    textAlign: 'left',
                                  }}>
                                  <span style={{ fontSize: 13, fontWeight: 600, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{opt.label}</span>
                                  {!isCreditOpt && (
                                    <span style={{ fontSize: 12, fontWeight: 700, color: selected ? '#A7D5C0' : '#059669', marginLeft: 10, fontFamily: 'monospace' }}>{fmt(bal)}</span>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                          {totalRefund > 0 && !isSupplierCredit && (
                            <div style={{ background: '#D1FAE5', borderRadius: 12, padding: '12px 14px', marginTop: 10 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                                <span style={{ fontSize: 12, color: '#065F46', fontWeight: 600 }}>{sw ? 'Kiasi cha Kupokea' : 'Refund Received'}</span>
                                <span style={{ fontSize: 20, fontWeight: 900, color: '#059669' }}>+{fmt(totalRefund)}</span>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', paddingTop: 6, borderTop: '1px solid rgba(5,150,105,0.2)' }}>
                                <div>
                                  <div style={{ fontSize: 11, color: '#065F46', fontWeight: 600 }}>{sw ? 'Salio Sasa' : 'Current Balance'}</div>
                                  <div style={{ fontSize: 13, color: 'var(--text-secondary)', fontWeight: 700, fontFamily: 'monospace' }}>{fmt(curBal)}</div>
                                  <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 1 }}>{selOpt?.label}</div>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                  <div style={{ fontSize: 11, color: '#065F46', fontWeight: 600 }}>{sw ? 'Baada ya Kupokea' : 'After Refund'}</div>
                                  <div style={{ fontSize: 14, color: '#059669', fontWeight: 800, fontFamily: 'monospace' }}>{fmt(newBal)}</div>
                                </div>
                              </div>
                            </div>
                          )}
                          {totalRefund > 0 && isSupplierCredit && (
                            <div style={{ background: '#FEF3C7', borderRadius: 12, padding: '12px 14px', marginTop: 10, border: '1px solid #FCD34D' }}>
                              <div style={{ fontSize: 12, color: '#92400E', fontWeight: 700, marginBottom: 4 }}>
                                {sw ? 'Hakuna fedha itahamishwa' : 'No money will move'}
                              </div>
                              <div style={{ fontSize: 11, color: '#92400E', lineHeight: 1.4 }}>
                                {sw
                                  ? `Deni lako kwa ${selectedPurchase?.supplier || 'muuzaji'} litapungua kwa ${fmt(totalRefund)}.`
                                  : `Your outstanding balance with ${selectedPurchase?.supplier || 'the supplier'} will be reduced by ${fmt(totalRefund)}.`}
                              </div>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>

                  {/* Return Date -defaults to today; not before purchase date, not in the future */}
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ display: 'block', fontWeight: 600, fontSize: 14, color: 'var(--text-primary)', marginBottom: 6 }}>
                      {sw ? 'Tarehe ya Kurudisha' : 'Return Date'}
                    </label>
                    <input type="date"
                      value={retDate}
                      min={selectedPurchase?.date || undefined}
                      max={new Date().toISOString().split('T')[0]}
                      onChange={e => setRetDate(e.target.value)}
                      style={{ width: '100%', padding: '9px 12px', borderRadius: 12, border: 'none', background: 'var(--bg-tab)', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                      {sw
                        ? `Tarehe halisi ya kurudisha (kuanzia tarehe ya ununuzi ${selectedPurchase?.date || ''} mpaka leo)`
                        : `When the return actually happened (between the original purchase date ${selectedPurchase?.date || ''} and today)`}
                    </div>
                  </div>

                  {/* RESTOCK */}
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 14, cursor: 'pointer', padding: '10px 12px', background: 'var(--bg-tab)', borderRadius: 10 }}>
                    <input type="checkbox" checked={retRestock} onChange={e => setRetRestock(e.target.checked)} style={{ marginTop: 2 }} />
                    <span style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.4 }}>
                      <strong>{sw ? 'Bidhaa zimerudishwa kwa muuzaji' : 'Items physically returned to supplier'}</strong>
                      <span style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{sw ? 'Stock zetu zinapungua. Acha bila ya tiki ikiwa bidhaa zimebaki nasi.' : 'Reduces our stock. Uncheck if goods stay with us (e.g. defective kept).'}</span>
                    </span>
                  </label>

                  {/* REASON */}
                  <div style={{ marginBottom: 20 }}>
                    <label style={{ display: 'block', fontWeight: 600, fontSize: 14, color: 'var(--text-primary)', marginBottom: 6 }}>
                      {sw ? 'Sababu' : 'Reason'}
                    </label>
                    <input type="text"
                      placeholder={sw ? 'mf. Bidhaa mbaya, Bidhaa si sahihi, Ziada…' : 'e.g. Defective, Wrong item, Overstock…'}
                      value={retReason} onChange={e => setRetReason(e.target.value)}
                      style={{ width: '100%', padding: '9px 12px', borderRadius: 12, border: 'none', background: 'var(--bg-tab)', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
                  </div>
                  </>
                )}
                <div className="form-buttons">
                  <button className="save-btn" onClick={handleSavePurchaseReturn} disabled={!selectedPurchase || totalRefund <= 0}>
                    {sw ? `Hifadhi Kurudisha${totalRefund > 0 ? ` (${fmt(totalRefund)})` : ''}` : `Save Return${totalRefund > 0 ? ` (${fmt(totalRefund)})` : ''}`}
                  </button>
                  <button className="cancel-btn" onClick={() => { setRetPurchaseId(''); setRetItemQtys({}); setRetQty(''); setRetReason(''); setRetSearchQuery(''); setRetFilterDate(''); setRetFilterProduct(''); setRetFilterSupplier(''); setRetRestock(true); setRetRefundMethod('cash'); setRetDate(new Date().toISOString().split('T')[0]); setShowPurchaseReturnModal(false); }}>
                    {sw ? 'Ghairi' : 'Cancel'}
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* ── RECURRING EXPENSES MODAL ────────────────────────────────── */}
        {showRecurringModal && (
          <div className="popup-overlay" style={{ zIndex: 1700, alignItems: 'flex-start', paddingTop: 40 }}>
            <div className="popup-form mz-modal" style={{ maxWidth: 460, width: '100%', borderRadius: 16, padding: 24, maxHeight: '85vh', overflowY: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>↻ Recurring Expenses</h3>
                <button onClick={() => setShowRecurringModal(false)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: 'var(--text-secondary)' }}>×</button>
              </div>
              {/* Add / Edit Form */}
              <div style={{ background: 'var(--bg-tab)', borderRadius: 14, padding: 16, marginBottom: 16 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)', marginBottom: 12 }}>{recurringEditId ? 'Edit Recurring Expense' : 'Add Recurring Expense'}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <input type="text" placeholder="Expense name (e.g. Rent, Electricity)" value={recurringForm.name}
                    onChange={e => setRecurringForm(p => ({ ...p, name: e.target.value }))}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: 'none', background: 'var(--bg-white)', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
                  <div style={{ display: 'flex', gap: 8 }}>
                    <select value={recurringForm.category} onChange={e => setRecurringForm(p => ({ ...p, category: e.target.value }))}
                      style={{ flex: 1, padding: '10px 12px', borderRadius: 10, border: 'none', background: 'var(--bg-white)', fontSize: 14, outline: 'none' }}>
                      <option value="">Category</option>
                      {(expenseCategories || []).map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                    </select>
                    <input type="number" placeholder="Amount" value={recurringForm.amount}
                      onChange={e => setRecurringForm(p => ({ ...p, amount: e.target.value }))}
                      style={{ width: 110, padding: '10px 12px', borderRadius: 10, border: 'none', background: 'var(--bg-white)', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <select value={recurringForm.frequency} onChange={e => setRecurringForm(p => ({ ...p, frequency: e.target.value }))}
                      style={{ flex: 1, padding: '10px 12px', borderRadius: 10, border: 'none', background: 'var(--bg-white)', fontSize: 14, outline: 'none' }}>
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="yearly">Yearly</option>
                    </select>
                    <input type="date" value={recurringForm.nextDueDate} onChange={e => setRecurringForm(p => ({ ...p, nextDueDate: e.target.value }))}
                      style={{ flex: 1, padding: '10px 8px', borderRadius: 10, border: 'none', background: 'var(--bg-white)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                  </div>
                  <button onClick={() => {
                    if (!recurringForm.name || !recurringForm.amount) return;
                    if (recurringEditId) {
                      setRecurringExpenses(prev => prev.map(r => r.id === recurringEditId ? { ...r, ...recurringForm } : r));
                      setRecurringEditId(null);
                    } else {
                      setRecurringExpenses(prev => [...prev, { ...recurringForm, id: Date.now(), active: true }]);
                    }
                    setRecurringForm({ name: '', category: '', amount: '', frequency: 'monthly', paymentMethod: 'cash', nextDueDate: new Date().toISOString().split('T')[0] });
                  }} style={{ padding: '11px', borderRadius: 10, background: '#111827', color: '#A3E635', border: 'none', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
                    {recurringEditId ? 'Save Changes' : '+ Add'}
                  </button>
                </div>
              </div>
              {/* List */}
              {(recurringExpenses || []).length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--text-secondary)', fontSize: 14 }}>No recurring expenses set up yet.</div>
              ) : (recurringExpenses || []).map(r => (
                <div key={r.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 12px', background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border-color)', marginBottom: 8 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-primary)' }}>{r.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                      {r.frequency} · {fmt(r.amount)} · Next: {r.nextDueDate}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button onClick={() => {
                      // Record as actual expense
                      const newExp = { id: Date.now(), description: r.name, category: r.category || 'Other', amount: r.amount, date: new Date().toISOString().split('T')[0], paymentMethod: r.paymentMethod || 'cash', isPaid: true };
                      setExpenses(prev => [...prev, newExp]);
                      // MIGRATION-ONLY dual-write: persist recurring expense
                      if (businessId && currentUser?.accessToken) {
                        persistExpense(businessId, newExp, currentUser.accessToken, paymentAccounts).then(r => {
                          const rid = r?.data?.expense_id;
                          if (rid) setExpenses(prev => prev.map(e => e.id === newExp.id ? { ...e, _rid: rid, _rv: 1 } : e));
                        }).catch(err => console.warn('[dual-write] recurringExpense:', err.message));
                      }
                      // Update next due date
                      const nextDate = r.nextDueDate ? new Date(r.nextDueDate) : new Date();
                      if (isNaN(nextDate.getTime())) nextDate.setTime(Date.now());
                      if (r.frequency === 'daily') nextDate.setDate(nextDate.getDate() + 1);
                      else if (r.frequency === 'weekly') nextDate.setDate(nextDate.getDate() + 7);
                      else if (r.frequency === 'monthly') nextDate.setMonth(nextDate.getMonth() + 1);
                      else if (r.frequency === 'quarterly') nextDate.setMonth(nextDate.getMonth() + 3);
                      else if (r.frequency === 'yearly') nextDate.setFullYear(nextDate.getFullYear() + 1);
                      setRecurringExpenses(prev => prev.map(x => x.id === r.id ? { ...x, nextDueDate: nextDate.toISOString().split('T')[0] } : x));
                    }} style={{ padding: '5px 10px', borderRadius: 8, background: '#D1FAE5', color: '#065F46', border: 'none', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Pay</button>
                    <button onClick={() => { setRecurringForm({ name: r.name, category: r.category || '', amount: r.amount, frequency: r.frequency, paymentMethod: r.paymentMethod || 'cash', nextDueDate: r.nextDueDate }); setRecurringEditId(r.id); }}
                      style={{ padding: '5px 10px', borderRadius: 8, background: 'var(--bg-input)', color: 'var(--text-secondary)', border: 'none', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>✎️</button>
                    <button onClick={() => setRecurringExpenses(prev => prev.filter(x => x.id !== r.id))}
                      style={{ padding: '5px 10px', borderRadius: 8, background: '#FEE2E2', color: '#DC2626', border: 'none', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>×</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── CUSTOMER STATEMENT MODAL ────────────────────────────────── */}
        {showCustomerStatement && (() => {
          const custSales = (sales || []).filter(s =>
            s.status !== 'void' &&
            (stmtCustId ? (s.customerName === stmtCustId) : true) &&
            (!stmtFrom || s.date >= stmtFrom) && (!stmtTo || s.date <= stmtTo)
          );
          const totalBilled = custSales.reduce((s, x) => s + getSaleGrandTotal(x), 0);
          // Use the authoritative saleOutstanding() (same as dashboard receivables)
          // so partial payments and return-credits are correctly reflected.
          const totalOwed = custSales.reduce((s, x) => s + saleOutstanding(x), 0);
          const totalPaid = totalBilled - totalOwed;
          const custList = [...new Set((sales || []).map(s => s.customerName).filter(Boolean))];
          return (
            <div style={{ position: 'fixed', inset: 0, zIndex: 1500, background: 'var(--bg-tab)', display: 'flex', flexDirection: 'column' }}>
              <div style={{ background: 'var(--bg-white)', borderBottom: '1px solid var(--border-color)', padding: '14px 16px 10px', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
                <button onClick={() => setShowCustomerStatement(false)} style={{ background: 'none', border: 'none', padding: '4px 8px 4px 0', cursor: 'pointer', display: 'flex', alignItems: 'center' }} aria-label={t.back || 'Back'}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--text-primary)" strokeWidth="2.5" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
                </button>
                <h2 style={{ flex: 1, fontSize: 18, fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>{t.customerStatement || 'Customer Statement'}</h2>
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: 20, maxWidth: 1200, width: '100%', margin: '0 auto', boxSizing: 'border-box' }}>
                {/* Filter + KPI strip */}
                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(280px, 1fr) minmax(440px, 1.7fr)', gap: 14, marginBottom: 16 }}>
                  <div style={{ background: 'var(--bg-white)', borderRadius: 14, padding: 14, border: '1px solid var(--border-color)', boxShadow: '0 1px 2px rgba(0,0,0,0.03)' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 10 }}>{language === 'sw' ? 'Vichujio' : 'Filters'}</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <div>
                        <label style={{ display: 'block', fontWeight: 600, fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>{t.customer || 'Customer'}</label>
                        <select value={stmtCustId} onChange={e => setStmtCustId(e.target.value)}
                          style={{ width: '100%', padding: '9px 10px', borderRadius: 8, border: '1px solid var(--border-color)', background: 'var(--bg-white)', fontSize: 13, outline: 'none' }}>
                          <option value="">{t.allCustomersOption || 'All Customers'}</option>
                          {custList.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        <div>
                          <label style={{ display: 'block', fontWeight: 600, fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>{t.fromLabel || 'From'}</label>
                          <input type="date" value={stmtFrom} onChange={e => setStmtFrom(e.target.value)}
                            style={{ width: '100%', padding: '8px 6px', borderRadius: 8, border: '1px solid var(--border-color)', background: 'var(--bg-white)', fontSize: 12, outline: 'none', boxSizing: 'border-box' }} />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontWeight: 600, fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>{t.toLabel || 'To'}</label>
                          <input type="date" value={stmtTo} onChange={e => setStmtTo(e.target.value)}
                            style={{ width: '100%', padding: '8px 6px', borderRadius: 8, border: '1px solid var(--border-color)', background: 'var(--bg-white)', fontSize: 12, outline: 'none', boxSizing: 'border-box' }} />
                        </div>
                      </div>
                      {typeof periodPresetBar === 'function' && periodPresetBar(stmtFrom, stmtTo, (a, b) => { setStmtFrom(a); setStmtTo(b); })}
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                    {[
                      { label: t.totalBilled || 'Total Billed', val: fmt(totalBilled), color: 'var(--text-secondary)', bg: 'linear-gradient(135deg, #F1F5F9 0%, #E2E8F0 100%)', accent: '#475569', icon: '', sub: `${custSales.length} ${language === 'sw' ? 'ankara' : 'invoices'}` },
                      { label: t.totalPaid || 'Total Paid', val: fmt(totalPaid), color: '#065F46', bg: 'linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)', accent: '#10B981', icon: '', sub: `${totalBilled > 0 ? Math.round((totalPaid / totalBilled) * 100) : 0}% ${language === 'sw' ? 'imekusanywa' : 'collected'}` },
                      { label: t.balanceOwed || 'Balance Owed', val: fmt(totalOwed), color: '#991B1B', bg: 'linear-gradient(135deg, #FEF2F2 0%, #FEE2E2 100%)', accent: '#EF4444', icon: '', sub: `${totalBilled > 0 ? Math.round((totalOwed / totalBilled) * 100) : 0}% ${language === 'sw' ? 'inadaiwa' : 'outstanding'}` }
                    ].map(c => (
                      <div key={c.label} style={{ background: c.bg, borderRadius: 14, padding: '14px 14px 16px', border: `1px solid ${c.accent}22`, position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 110 }}>
                        <div style={{ position: 'absolute', top: 10, right: 12, fontSize: 18, opacity: 0.55 }}>{c.icon}</div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: c.color, textTransform: 'uppercase', letterSpacing: 0.4, opacity: 0.9 }}>{c.label}</div>
                        <div style={{ fontSize: 22, fontWeight: 800, color: c.color, lineHeight: 1.1, marginTop: 8 }}>{c.val}</div>
                        <div style={{ marginTop: 6, fontSize: 11, color: c.color, opacity: 0.7, fontWeight: 600 }}>{c.sub}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Main grid: transactions + side breakdown */}
                <div style={{ display: 'grid', gridTemplateColumns: stmtCustId ? '1fr' : 'minmax(0, 1.7fr) minmax(260px, 1fr)', gap: 14 }}>
                  <div style={{ background: 'var(--bg-white)', borderRadius: 14, border: '1px solid var(--border-color)', overflow: 'hidden', boxShadow: '0 1px 2px rgba(0,0,0,0.03)' }}>
                    <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--bg-tab)' }}>
                      <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-primary)' }}>
                        {stmtCustId ? stmtCustId : (language === 'sw' ? 'Miamala Yote' : 'All Transactions')}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 600 }}>{custSales.length} {language === 'sw' ? 'rekodi' : 'records'}</div>
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                          <tr style={{ background: '#111827' }}>
                            <th style={{ padding: '11px 14px', color: '#A3E635', fontWeight: 700, textAlign: 'left', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, width: 110 }}>{t.date || 'Date'}</th>
                            <th style={{ padding: '11px 10px', color: '#A3E635', fontWeight: 700, textAlign: 'left', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, width: 110 }}>{t.invoice || 'Invoice'}</th>
                            <th style={{ padding: '11px 10px', color: '#A3E635', fontWeight: 700, textAlign: 'left', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>{t.item || 'Item'}</th>
                            <th style={{ padding: '11px 14px', color: '#A3E635', fontWeight: 700, textAlign: 'right', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, width: 140 }}>{t.amount || 'Amount'}</th>
                            <th style={{ padding: '11px 14px', color: '#A3E635', fontWeight: 700, textAlign: 'center', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, width: 100 }}>{t.status || 'Status'}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {custSales.length === 0 ? (
                            <tr><td colSpan={5} style={{ padding: '40px 14px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>{t.noTransactionsFound || 'No transactions found'}</td></tr>
                          ) : custSales.sort((a, b) => new Date(a.date || 0) - new Date(b.date || 0)).map((s, i) => (
                            <tr key={s.id} style={{ background: i % 2 === 0 ? 'var(--bg-white)' : 'var(--bg-tab)', borderBottom: '1px solid var(--border-color)', transition: 'background 0.15s' }}
                                onMouseEnter={e => { e.currentTarget.style.background = '#F0FDF4'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = i % 2 === 0 ? 'var(--bg-white)' : 'var(--bg-tab)'; }}>
                              <td style={{ padding: '11px 14px', whiteSpace: 'nowrap', color: 'var(--text-primary)' }}>{s.date}</td>
                              <td style={{ padding: '11px 10px', fontSize: 11, color: 'var(--text-muted)', fontFamily: 'ui-monospace, monospace' }}>{s.invoiceNumber || '—'}</td>
                              <td style={{ padding: '11px 10px', color: 'var(--text-primary)' }}>{s.productName}{!stmtCustId && s.customerName ? <span style={{ fontSize: 11, color: 'var(--text-secondary)', display: 'block', marginTop: 2 }}>{s.customerName}</span> : null}</td>
                              <td style={{ padding: '11px 14px', fontWeight: 700, color: '#059669', textAlign: 'right' }}>{fmt(getSaleGrandTotal(s))}</td>
                              <td style={{ padding: '11px 14px', textAlign: 'center' }}>
                                {(() => {
                                  const _pmts = s.payments || [];
                                  const _nc = _pmts.filter(p => p.type !== 'credit').reduce((a, p) => a + parseFloat(p.amount || 0), 0);
                                  const _cr = _pmts.filter(p => p.type === 'credit').reduce((a, p) => a + parseFloat(p.amount || 0), 0);
                                  const _tot = parseFloat(s.totalRevenue || 0);
                                  const _full = s.isPaid === true || (_cr === 0 && _nc >= _tot - 0.005);
                                  const _part = !_full && _nc > 0;
                                  const lbl = _full ? (t.paidLabel || 'Paid') : _part ? (language === 'sw' ? 'Imelipwa Kiasi' : 'Partial') : (t.unpaidLabel || 'Unpaid');
                                  const bg = _full ? '#D1FAE5' : _part ? '#FEF3C7' : '#FEE2E2';
                                  const fg = _full ? '#065F46' : _part ? '#92400E' : '#991B1B';
                                  return <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 999, background: bg, color: fg, letterSpacing: 0.3 }}>{lbl}</span>;
                                })()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        {custSales.length > 0 && (
                          <tfoot>
                            <tr style={{ background: 'var(--bg-tab)', borderTop: '2px solid var(--border-color)' }}>
                              <td colSpan={3} style={{ padding: '12px 14px', fontWeight: 800, color: 'var(--text-primary)', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.4 }}>{language === 'sw' ? 'Jumla' : 'Total'}</td>
                              <td style={{ padding: '12px 14px', textAlign: 'right', fontWeight: 800, color: '#059669', fontSize: 14 }}>{fmt(totalBilled)}</td>
                              <td></td>
                            </tr>
                          </tfoot>
                        )}
                      </table>
                    </div>
                  </div>

                  {!stmtCustId && (() => {
                    const byCust = {};
                    (sales || []).filter(s => (!stmtFrom || s.date >= stmtFrom) && (!stmtTo || s.date <= stmtTo)).forEach(s => {
                      const k = s.customerName || (language === 'sw' ? 'Bila Jina' : 'Walk-in');
                      if (!byCust[k]) byCust[k] = { billed: 0, paid: 0, owed: 0, count: 0 };
                      const tot = parseFloat(s.totalRevenue || 0);
                      const pmts = s.payments || [];
                      const nc = pmts.filter(p => p.type !== 'credit').reduce((a, p) => a + parseFloat(p.amount || 0), 0);
                      const isFull = s.isPaid === true || nc >= tot - 0.005;
                      byCust[k].billed += tot;
                      byCust[k].paid += isFull ? tot : nc;
                      byCust[k].owed += isFull ? 0 : (tot - nc);
                      byCust[k].count += 1;
                    });
                    const top = Object.entries(byCust).sort((a, b) => b[1].owed - a[1].owed).slice(0, 8);
                    const maxOwed = Math.max(...top.map(([, v]) => v.owed), 1);
                    const hasOwed = top.some(([, v]) => v.owed > 0);
                    return (
                      <div style={{ background: 'var(--bg-white)', borderRadius: 14, border: '1px solid var(--border-color)', padding: 16, boxShadow: '0 1px 2px rgba(0,0,0,0.03)', height: 'fit-content' }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 4 }}>{language === 'sw' ? 'Wadaiwa Wakuu' : 'Top Outstanding'}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12 }}>{language === 'sw' ? 'Kwa wateja' : 'By customer'}</div>
                        {!hasOwed ? (
                          <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text-secondary)', fontSize: 12 }}>{language === 'sw' ? 'Hakuna deni 🎉' : 'No outstanding balances 🎉'}</div>
                        ) : top.filter(([, v]) => v.owed > 0).map(([name, v]) => (
                          <div key={name} onClick={() => setStmtCustId(name)} style={{ cursor: 'pointer', padding: '8px 10px', borderRadius: 8, transition: 'background 0.15s', margin: '0 -10px 4px' }}
                            onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-tab)'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
                              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160 }}>{name}</div>
                              <div style={{ fontSize: 12, fontWeight: 800, color: '#991B1B' }}>{fmt(v.owed)}</div>
                            </div>
                            <div style={{ height: 5, background: '#FEE2E2', borderRadius: 999, overflow: 'hidden' }}>
                              <div style={{ height: '100%', width: `${(v.owed / maxOwed) * 100}%`, background: 'linear-gradient(90deg, #EF4444, #DC2626)', borderRadius: 999 }} />
                            </div>
                            <div style={{ fontSize: 10, color: 'var(--text-secondary)', marginTop: 4 }}>{v.count} {language === 'sw' ? 'ankara' : 'invoices'} · {fmt(v.billed)} {language === 'sw' ? 'jumla' : 'billed'}</div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          );
        })()}

        {/* ── AGING REPORT MODAL ──────────────────────────────────────── */}
        {showAgingReport && (() => {
          const today = new Date();
          const unpaid = (sales || []).filter(s => s.isPaid === false);
          const bucket = (s) => { if (!s.date) return '90+ days'; const d = Math.floor((today - new Date(s.date)) / 86400000); if (isNaN(d) || d > 90) return '90+ days'; if (d <= 30) return '0–30 days'; if (d <= 60) return '31–60 days'; return '61–90 days'; };
          const buckets = ['0–30 days', '31–60 days', '61–90 days', '90+ days'];
          const grouped = buckets.map(b => ({ label: b, items: unpaid.filter(s => bucket(s) === b) }));
          return (
            <div style={{ position: 'fixed', inset: 0, zIndex: 1500, background: 'var(--bg-tab)', display: 'flex', flexDirection: 'column' }}>
              <div style={{ background: 'var(--bg-white)', borderBottom: '1px solid var(--border-color)', padding: '14px 16px 10px', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
                <button onClick={() => setShowAgingReport(false)} style={{ background: 'none', border: 'none', padding: '4px 8px 4px 0', cursor: 'pointer', display: 'flex', alignItems: 'center' }} aria-label={t.back || 'Back'}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--text-primary)" strokeWidth="2.5" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
                </button>
                <h2 style={{ flex: 1, fontSize: 18, fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>{t.receivablesAgingReport || 'Receivables Aging Report'}</h2>
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: 20, maxWidth: 900, width: '100%', margin: '0 auto', boxSizing: 'border-box' }}>
                <div style={{ background: '#FEF3C7', borderRadius: 12, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#92400E' }}>
                  {t.totalOutstanding || 'Total Outstanding'}: <strong>{fmt(unpaid.reduce((s, x) => s + parseFloat(x.totalRevenue || 0), 0))}</strong> · {unpaid.length} {unpaid.length !== 1 ? (t.acrossInvoicesPlural || 'invoices') : (t.acrossInvoices || 'invoice')}
                </div>
                {grouped.map(g => g.items.length > 0 && (
                  <div key={g.label} style={{ marginBottom: 16 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text-primary)', marginBottom: 8, display: 'flex', justifyContent: 'space-between' }}>
                      <span>◰ {g.label}</span>
                      <span style={{ color: '#DC2626' }}>{fmt(g.items.reduce((s, x) => s + parseFloat(x.totalRevenue || 0), 0))}</span>
                    </div>
                    <div style={{ borderRadius: 10, overflow: 'hidden', border: '1px solid var(--border-color)' }}>
                      {g.items.map((s, i) => (
                        <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: i % 2 === 0 ? 'var(--bg-white)' : 'var(--bg-tab)', borderBottom: i < g.items.length - 1 ? '1px solid var(--border-color)' : 'none' }}>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>{s.customerName || 'Walk-in'}</div>
                            <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{s.productName} · {s.date}</div>
                          </div>
                          <div style={{ fontWeight: 700, color: '#DC2626', fontSize: 14 }}>{fmt(getSaleGrandTotal(s))}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                {unpaid.length === 0 && <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-secondary)' }}>{t.noOutstandingInvoices || '★ No outstanding invoices!'}</div>}
              </div>
            </div>
          );
        })()}

        {/* ── DAILY SUMMARY MODAL ─────────────────────────────────────── */}
        {showDailySummary && (() => {
          const daySales = (sales || []).filter(s => s.date === dailySummaryDate);
          const dayPurchases = (purchases || []).filter(p => p.date === dailySummaryDate);
          const dayExpenses = (expenses || []).filter(e => e.date === dailySummaryDate);
          const dayRevenue = daySales.reduce((s, x) => s + getSaleGrandTotal(x), 0);
          const dayPurchCost = dayPurchases.reduce((s, x) => s + getPurchaseGrandTotal(x), 0);
          const dayExpCost = dayExpenses.reduce((s, x) => s + parseFloat(x.amount || 0), 0);
          const dayCOGS = getReplayCOGS(dailySummaryDate, dailySummaryDate); // replay COGS for the single day (H4 source of truth)
          const dayProfit = dayRevenue - dayCOGS - dayExpCost;
          const cashIn = daySales.filter(s => (s.paymentMethod === 'cash' || (!s.payments || s.payments.length === 0)) && s.isPaid !== false).reduce((s, x) => s + parseFloat(x.totalRevenue || 0), 0);
          return (
            <div style={{ position: 'fixed', inset: 0, zIndex: 1500, background: 'var(--bg-tab)', display: 'flex', flexDirection: 'column' }}>
              <div style={{ background: 'var(--bg-white)', borderBottom: '1px solid var(--border-color)', padding: '14px 16px 10px', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
                <button onClick={() => setShowDailySummary(false)} style={{ background: 'none', border: 'none', padding: '4px 8px 4px 0', cursor: 'pointer', display: 'flex', alignItems: 'center' }} aria-label={t.back || 'Back'}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--text-primary)" strokeWidth="2.5" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
                </button>
                <h2 style={{ flex: 1, fontSize: 18, fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>{t.dailySummary || 'Daily Summary'}</h2>
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: 20, maxWidth: 900, width: '100%', margin: '0 auto', boxSizing: 'border-box' }}>
                <input type="date" value={dailySummaryDate} onChange={e => setDailySummaryDate(e.target.value)}
                  style={{ width: '100%', padding: '9px 12px', borderRadius: 12, border: 'none', background: 'var(--bg-tab)', fontSize: 14, marginBottom: 16, outline: 'none', boxSizing: 'border-box', fontWeight: 600 }} />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
                  {[
                    { label: t.salesRevenue || 'Sales Revenue', val: fmt(dayRevenue), color: '#065F46', bg: '#D1FAE5', icon: '$' },
                    { label: t.grossProfit || 'Gross Profit', val: fmt(dayProfit), color: dayProfit >= 0 ? '#334155' : '#991B1B', bg: dayProfit >= 0 ? '#F1F5F9' : '#FEE2E2', icon: '↗' },
                    { label: t.purchases || 'Purchases', val: fmt(dayPurchCost), color: '#4F46E5', bg: '#F5F3FF', icon: '▣' },
                    { label: t.expenses || 'Expenses', val: fmt(dayExpCost), color: '#DC2626', bg: '#FEF2F2', icon: '↓' },
                  ].map(c => (
                    <div key={c.label} style={{ background: c.bg, borderRadius: 12, padding: '9px 12px' }}>
                      <div style={{ fontSize: 12, color: c.color, fontWeight: 600, marginBottom: 4 }}>{c.icon} {c.label}</div>
                      <div style={{ fontSize: 18, fontWeight: 800, color: c.color }}>{c.val}</div>
                    </div>
                  ))}
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 8 }}>{t.transactionsLabel || 'Transactions'} ({daySales.length} {t.sales || 'sales'} · {dayPurchases.length} {t.purchases || 'purchases'} · {dayExpenses.length} {t.expenses || 'expenses'})</div>
                {[...daySales.map(s => ({ ...s, _type: 'sale' })), ...dayPurchases.map(p => ({ ...p, _type: 'purchase' })), ...dayExpenses.map(e => ({ ...e, _type: 'expense' }))].map((tx, i) => (
                  <div key={tx.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 12px', background: i % 2 === 0 ? 'var(--bg-tab)' : 'var(--bg-white)', borderRadius: 8, marginBottom: 4 }}>
                    <div>
                      <span style={{ fontSize: 12, fontWeight: 700, padding: '1px 7px', borderRadius: 6, marginRight: 6, background: tx._type === 'sale' ? '#D1FAE5' : tx._type === 'purchase' ? '#F5F3FF' : '#FEE2E2', color: tx._type === 'sale' ? '#065F46' : tx._type === 'purchase' ? '#3730A3' : '#991B1B' }}>
                        {tx._type === 'sale' ? (t.saleLabel || 'Sale') : tx._type === 'purchase' ? (t.buyLabel || 'Buy') : (t.expLabel || 'Exp')}
                      </span>
                      <span style={{ fontSize: 13, color: 'var(--text-primary)' }}>{tx.productName || tx.description || '—'}</span>
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 700, color: tx._type === 'sale' ? '#059669' : '#DC2626' }}>
                      {tx._type === 'sale' ? '+' : '-'}{fmt(tx.totalRevenue || tx.totalCost || tx.amount)}
                    </span>
                  </div>
                ))}
                {daySales.length === 0 && dayPurchases.length === 0 && dayExpenses.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--text-secondary)', fontSize: 14 }}>{t.noTransactionsDate || 'No transactions for this date.'}</div>
                )}
              </div>
            </div>
          );
        })()}


        {/* ── WHT REPORT MODAL ────────────────────────────────────────── */}
        {showWHTReport && (() => {
          const whtSales = (sales || []).filter(s => {
            if (s.status === 'void') return false;
            const hasTax = (s.taxes || []).some(tx => tx.type === 'withholding');
            return hasTax && (!whtFrom || s.date >= whtFrom) && (!whtTo || s.date <= whtTo);
          });
          const whtPurchases = (purchases || []).filter(p => {
            if (p.status === 'void') return false;
            const hasTax = (p.taxes || []).some(tx => tx.type === 'withholding');
            return hasTax && (!whtFrom || p.date >= whtFrom) && (!whtTo || p.date <= whtTo);
          });
          // WHT from the pre-tax base (same correct basis as VAT), not the
          // tax-inclusive grand total.
          const totalWHTFromSales = whtSales.reduce((sum, s) => sum + saleTaxOfType(s, 'withholding'), 0);
          const totalWHTFromPurchases = whtPurchases.reduce((sum, p) => sum + purchaseTaxOfType(p, 'withholding'), 0);
          const activeList = whtTab === 'sales' ? whtSales : whtPurchases;
          const activeTotal = whtTab === 'sales' ? totalWHTFromSales : totalWHTFromPurchases;
          return (
            <div style={{ position: 'fixed', inset: 0, zIndex: 1500, background: 'var(--bg-tab)', display: 'flex', flexDirection: 'column' }}>
              <div style={{ background: 'var(--bg-white)', borderBottom: '1px solid var(--border-color)', padding: '14px 16px 10px', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
                <button onClick={() => setShowWHTReport(false)} style={{ background: 'none', border: 'none', padding: '4px 8px 4px 0', cursor: 'pointer', display: 'flex', alignItems: 'center' }} aria-label={t.back || 'Back'}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--text-primary)" strokeWidth="2.5" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
                </button>
                <h2 style={{ flex: 1, fontSize: 18, fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>{t.whtReportFull || 'WHT Report (Withholding Tax)'}</h2>
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: 20, maxWidth: 900, width: '100%', margin: '0 auto', boxSizing: 'border-box' }}>
                <div>

                </div>
                {/* Tabs */}
                <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                  {[{ key: 'sales', label: t.whtFromSales || 'From Sales' }, { key: 'purchases', label: t.whtFromPurchases || 'From Purchases' }].map(tab => (
                    <button key={tab.key} onClick={() => setWhtTab(tab.key)}
                      style={{ flex: 1, padding: '8px 0', borderRadius: 10, border: 'none', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                        background: whtTab === tab.key ? '#D97706' : 'var(--bg-tab)',
                        color: whtTab === tab.key ? '#fff' : 'var(--text-secondary)' }}>
                      {tab.label}
                    </button>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', marginBottom: 5 }}>{t.fromLabel || 'From'}</label>
                    <input type="date" value={whtFrom} onChange={e => setWhtFrom(e.target.value)}
                      style={{ width: '100%', padding: '10px 8px', borderRadius: 10, border: 'none', background: 'var(--bg-tab)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', marginBottom: 5 }}>{t.toLabel || 'To'}</label>
                    <input type="date" value={whtTo} onChange={e => setWhtTo(e.target.value)}
                      style={{ width: '100%', padding: '10px 8px', borderRadius: 10, border: 'none', background: 'var(--bg-tab)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                  </div>
                </div>
                <div style={{ background: '#FFF7ED', borderRadius: 12, padding: '8px 14px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#92400E' }}>
                    {whtTab === 'sales' ? (t.totalWHTCollected || 'Total WHT Collected') : (t.totalWHTCharged || 'Total WHT Paid')}
                  </span>
                  <span style={{ fontSize: 20, fontWeight: 800, color: '#D97706' }}>{fmt(activeTotal)}</span>
                </div>
                <div style={{ borderRadius: 12, overflowX: 'auto', overflowY: 'hidden', border: '1px solid var(--border-color)' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: '#111827' }}>
                        {whtTab === 'sales'
                          ? [t.date || 'Date', t.customer || 'Customer', t.invoice || 'Invoice', t.saleAmt || 'Sale Amt', t.whtRate || 'WHT Rate', t.whtAmt || 'WHT Amt'].map(h => (
                              <th key={h} style={{ padding: '8px 10px', color: '#A3E635', fontWeight: 700, textAlign: 'right', fontSize: 11 }}>{h}</th>
                            ))
                          : [t.date || 'Date', t.product || 'Product', t.supplier || 'Supplier', t.purchaseAmt || 'Purchase Amt', t.whtRate || 'WHT Rate', t.whtAmt || 'WHT Amt'].map(h => (
                              <th key={h} style={{ padding: '8px 10px', color: '#A3E635', fontWeight: 700, textAlign: 'right', fontSize: 11 }}>{h}</th>
                            ))
                        }
                      </tr>
                    </thead>
                    <tbody>
                      {activeList.length === 0 ? (
                        <tr><td colSpan={6} style={{ padding: 20, textAlign: 'center', color: 'var(--text-secondary)' }}>{t.noWHTTransactions || 'No WHT transactions in this period'}</td></tr>
                      ) : whtTab === 'sales' ? activeList.map((s, i) => {
                        const rate = (s.taxes || []).filter(tx => tx.type === 'withholding').reduce((r, tx) => r + parseFloat(tx.rate || 0), 0);
                        const amt = saleTaxOfType(s, 'withholding');
                        return (
                          <tr key={s.id} style={{ background: i % 2 === 0 ? 'var(--bg-white)' : 'var(--bg-tab)', borderBottom: '1px solid var(--border-color)' }}>
                            <td style={{ padding: '8px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>{s.date}</td>
                            <td style={{ padding: '8px 10px', textAlign: 'right', maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.customerName || (t.walkIn || 'Walk-in')}</td>
                            <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: 11, color: 'var(--text-muted)' }}>{s.invoiceNumber || '—'}</td>
                            <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600 }}>{fmt(getSaleGrandTotal(s))}</td>
                            <td style={{ padding: '8px 10px', textAlign: 'right' }}>{rate}%</td>
                            <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, color: '#D97706' }}>{fmt(amt)}</td>
                          </tr>
                        );
                      }) : activeList.map((p, i) => {
                        const rate = (p.taxes || []).filter(tx => tx.type === 'withholding').reduce((r, tx) => r + parseFloat(tx.rate || 0), 0);
                        const amt = purchaseTaxOfType(p, 'withholding');
                        return (
                          <tr key={p.id} style={{ background: i % 2 === 0 ? 'var(--bg-white)' : 'var(--bg-tab)', borderBottom: '1px solid var(--border-color)' }}>
                            <td style={{ padding: '8px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>{p.date}</td>
                            <td style={{ padding: '8px 10px', textAlign: 'right', maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.productName || '—'}</td>
                            <td style={{ padding: '8px 10px', textAlign: 'right', maxWidth: 70, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.supplier || '—'}</td>
                            <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600 }}>{fmt(getPurchaseGrandTotal(p))}</td>
                            <td style={{ padding: '8px 10px', textAlign: 'right' }}>{rate}%</td>
                            <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, color: '#D97706' }}>{fmt(amt)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          );
        })()}

        {/* ── VAT REPORT MODAL ────────────────────────────────────────── */}
        {showVATReport && (() => {
          const vatSales = (sales || []).filter(s => {
            if (s.status === 'void') return false;
            const hasTax = (s.taxes || []).some(tx => tx.type === 'vat');
            return hasTax && (!vatFrom || s.date >= vatFrom) && (!vatTo || s.date <= vatTo);
          });
          const vatPurchases = (purchases || []).filter(p => {
            if (p.status === 'void') return false;
            const hasTax = (p.taxes || []).some(tx => tx.type === 'vat');
            return hasTax && (!vatFrom || p.date >= vatFrom) && (!vatTo || p.date <= vatTo);
          });
          // Output/Input VAT from the tax BASE (not the VAT-inclusive total) via
          // the same helper the Business Statement uses -so the two reports agree.
          const totalVATFromSales = vatSales.reduce((sum, s) => sum + getSaleTax(s), 0);
          const totalVATFromPurchases = vatPurchases.reduce((sum, p) => sum + getPurchaseTax(p), 0);
          const activeList = vatTab === 'sales' ? vatSales : vatPurchases;
          const activeTotal = vatTab === 'sales' ? totalVATFromSales : totalVATFromPurchases;
          return (
            <div style={{ position: 'fixed', inset: 0, zIndex: 1500, background: 'var(--bg-tab)', display: 'flex', flexDirection: 'column' }}>
              <div style={{ background: 'var(--bg-white)', borderBottom: '1px solid var(--border-color)', padding: '14px 16px 10px', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
                <button onClick={() => setShowVATReport(false)} style={{ background: 'none', border: 'none', padding: '4px 8px 4px 0', cursor: 'pointer', display: 'flex', alignItems: 'center' }} aria-label={t.back || 'Back'}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--text-primary)" strokeWidth="2.5" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
                </button>
                <h2 style={{ flex: 1, fontSize: 18, fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>{t.vatReportFull || 'VAT Report (Value Added Tax)'}</h2>
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: 20, maxWidth: 900, width: '100%', margin: '0 auto', boxSizing: 'border-box' }}>
                {/* Tabs */}
                <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                  {[{ key: 'sales', label: t.vatFromSales || 'VAT Collected (Sales)' }, { key: 'purchases', label: t.vatFromPurchases || 'VAT Paid (Purchases)' }].map(tab => (
                    <button key={tab.key} onClick={() => setVatTab(tab.key)}
                      style={{ flex: 1, padding: '8px 0', borderRadius: 10, border: 'none', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                        background: vatTab === tab.key ? '#059669' : 'var(--bg-tab)',
                        color: vatTab === tab.key ? '#fff' : 'var(--text-secondary)' }}>
                      {tab.label}
                    </button>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', marginBottom: 5 }}>{t.fromLabel || 'From'}</label>
                    <input type="date" value={vatFrom} onChange={e => setVatFrom(e.target.value)}
                      style={{ width: '100%', padding: '10px 8px', borderRadius: 10, border: 'none', background: 'var(--bg-tab)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', marginBottom: 5 }}>{t.toLabel || 'To'}</label>
                    <input type="date" value={vatTo} onChange={e => setVatTo(e.target.value)}
                      style={{ width: '100%', padding: '10px 8px', borderRadius: 10, border: 'none', background: 'var(--bg-tab)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                  <div style={{ flex: 1, background: '#F0FDF4', borderRadius: 12, padding: '10px 14px' }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#065F46', marginBottom: 2 }}>{t.vatFromSales || 'Collected (Sales)'}</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: '#059669' }}>{fmt(totalVATFromSales)}</div>
                  </div>
                  <div style={{ flex: 1, background: '#FEF3C7', borderRadius: 12, padding: '10px 14px' }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#92400E', marginBottom: 2 }}>{t.vatFromPurchases || 'Paid (Purchases)'}</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: '#D97706' }}>{fmt(totalVATFromPurchases)}</div>
                  </div>
                </div>
                <div style={{ marginBottom: 14, padding: '10px 14px', borderRadius: 12, background: totalVATFromSales >= totalVATFromPurchases ? '#DCFCE7' : '#FEE2E2', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: totalVATFromSales >= totalVATFromPurchases ? '#065F46' : '#DC2626' }}>
                    {language === 'sw' ? 'VAT ya Kulipa Serikalini' : 'Net VAT Payable to Gov'}
                  </span>
                  <span style={{ fontSize: 18, fontWeight: 800, color: totalVATFromSales >= totalVATFromPurchases ? '#059669' : '#DC2626' }}>
                    {fmt(Math.abs(totalVATFromSales - totalVATFromPurchases))}
                    {totalVATFromPurchases > totalVATFromSales ? ` (${language === 'sw' ? 'Refund' : 'Refund'})` : ''}
                  </span>
                </div>
                <div style={{ borderRadius: 12, overflowX: 'auto', overflowY: 'hidden', border: '1px solid var(--border-color)' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: '#111827' }}>
                        {vatTab === 'sales'
                          ? [t.date || 'Date', t.customer || 'Customer', t.saleAmt || 'Sale Amt', t.vatRate || 'VAT Rate', t.vatAmt || 'VAT Amt'].map(h => (
                              <th key={h} style={{ padding: '8px 10px', color: '#A3E635', fontWeight: 700, textAlign: 'right', fontSize: 11 }}>{h}</th>
                            ))
                          : [t.date || 'Date', t.product || 'Product', t.purchaseAmt || 'Purchase Amt', t.vatRate || 'VAT Rate', t.vatAmt || 'VAT Amt'].map(h => (
                              <th key={h} style={{ padding: '8px 10px', color: '#A3E635', fontWeight: 700, textAlign: 'right', fontSize: 11 }}>{h}</th>
                            ))
                        }
                      </tr>
                    </thead>
                    <tbody>
                      {activeList.length === 0 ? (
                        <tr><td colSpan={5} style={{ padding: 20, textAlign: 'center', color: 'var(--text-secondary)' }}>{t.noVATTransactions || 'No VAT transactions in this period'}</td></tr>
                      ) : vatTab === 'sales' ? activeList.map((s, i) => {
                        const rate = (s.taxes || []).filter(tx => tx.type === 'vat').reduce((r, tx) => r + parseFloat(tx.rate || 0), 0);
                        const amt = getSaleTax(s);
                        return (
                          <tr key={s.id} style={{ background: i % 2 === 0 ? 'var(--bg-white)' : 'var(--bg-tab)', borderBottom: '1px solid var(--border-color)' }}>
                            <td style={{ padding: '8px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>{s.date}</td>
                            <td style={{ padding: '8px 10px', textAlign: 'right', maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.customerName || (t.walkIn || 'Walk-in')}</td>
                            <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600 }}>{fmt(getSaleGrandTotal(s))}</td>
                            <td style={{ padding: '8px 10px', textAlign: 'right' }}>{rate}%</td>
                            <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, color: '#059669' }}>{fmt(amt)}</td>
                          </tr>
                        );
                      }) : activeList.map((p, i) => {
                        const rate = (p.taxes || []).filter(tx => tx.type === 'vat').reduce((r, tx) => r + parseFloat(tx.rate || 0), 0);
                        const amt = getPurchaseTax(p);
                        return (
                          <tr key={p.id} style={{ background: i % 2 === 0 ? 'var(--bg-white)' : 'var(--bg-tab)', borderBottom: '1px solid var(--border-color)' }}>
                            <td style={{ padding: '8px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>{p.date}</td>
                            <td style={{ padding: '8px 10px', textAlign: 'right', maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.productName || '—'}</td>
                            <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600 }}>{fmt(getPurchaseGrandTotal(p))}</td>
                            <td style={{ padding: '8px 10px', textAlign: 'right' }}>{rate}%</td>
                            <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, color: '#D97706' }}>{fmt(amt)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          );
        })()}

        {/* ── PROFIT & LOSS REPORT ── */}
        {showPandL && (() => {
          const now = new Date();
          // Compute date range from plPeriod
          // H5: month/year use the shared range helper (to-date); quarter is
          // statement-specific; custom honours the picker.
          let fromDate, toDate;
          if (plPeriod === 'quarter') {
            const q = Math.floor(now.getMonth()/3);
            fromDate = `${now.getFullYear()}-${String(q*3+1).padStart(2,'0')}-01`;
            toDate = getPeriodRange('today').to;
          } else if (plPeriod === 'custom') {
            fromDate = plFrom;
            toDate = plTo;
          } else {
            const _r = getPeriodRange(plPeriod); // month | year → { from, to }
            fromDate = _r.from;
            toDate = _r.to;
          }

          const inRange = (date) => inPeriod(date, { from: fromDate, to: toDate });

          // Revenue -NET of returns of the sales in this period (matches the
          // dashboard and the tab reports).
          const filteredSales = (sales || []).filter(s => s.status !== 'void' && inRange(s.date));
          const _stmtSaleIds = new Set(filteredSales.map(s => String(s.id)));
          const _stmtReturns = (salesReturns || []).filter(r => r.status !== 'void' && _stmtSaleIds.has(String(r.originalSaleId)));
          const _stmtReturnsRefund = _stmtReturns.reduce((s, r) => s + parseFloat(r.refundAmt || r.totalRefund || 0), 0);
          const _stmtReturnsCost = _stmtReturns.reduce((s, r) => s + returnGoodsCost(r), 0);
          const productSalesRev = filteredSales.filter(s => !s.isService).reduce((s, x) => s + getSaleGrandTotal(x), 0) - _stmtReturnsRefund;
          const serviceRev = filteredSales.filter(s => s.isService).reduce((s, x) => s + getSaleGrandTotal(x), 0);
          const serviceTransRev = (serviceTransactions || []).filter(s => inRange(s.date) && s.status !== 'void').reduce((s, x) => s + parseFloat(x.totalRevenue||x.price||0), 0);
          const totalRevenue = productSalesRev + serviceRev + serviceTransRev;

          // VAT is collected FOR TRA, not income -exclude output VAT from the
          // revenue that feeds profit; report VAT separately below.
          const _stmtOutputVAT = filteredSales.reduce((s, x) => s + getSaleTax(x), 0);
          const _stmtInputVAT  = (purchases || []).filter(p => p.status !== 'void' && inRange(p.date)).reduce((s, x) => s + getPurchaseTax(x), 0);
          const _stmtVATPayable = _stmtOutputVAT - _stmtInputVAT;
          const totalRevenueExVAT = totalRevenue - _stmtOutputVAT;

          // COGS -chronological replay is the source of truth (H4): every unit
          // costed at the perpetual WAC as of its sale date, NOT the frozen
          // per-sale snapshot (which can be stale or, for mis-recorded sales,
          // zero). Customer returns are reversed at cost inside the replay, so we
          // do NOT subtract _stmtReturnsCost again. (_stmtReturnsCost is retained
          // only for the returns line display.)
          const cogs = getReplayCOGS(fromDate, toDate);
          void _stmtReturnsCost;

          // Gross Profit (on ex-VAT revenue)
          const grossProfit = totalRevenueExVAT - cogs;

          // Expenses by category
          const filteredExpenses = (expenses || []).filter(e => e.status !== 'void' && inRange(e.date));
          const expByCategory = {};
          filteredExpenses.forEach(e => {
            const cat = e.category || (language === 'sw' ? 'Nyingine' : 'Other');
            expByCategory[cat] = (expByCategory[cat] || 0) + parseFloat(e.amount||0);
          });
          const totalExpenses = filteredExpenses.reduce((s, e) => s + parseFloat(e.amount||0), 0);

          // Net Profit
          const netProfit = grossProfit - totalExpenses;

          const fmtN = (n) => {
            const abs = Math.abs(n);
            const formatted = `${currency} ${abs.toLocaleString(undefined, {minimumFractionDigits:0, maximumFractionDigits:0})}`;
            return n < 0 ? `(${formatted})` : formatted;
          };

          const Row = ({label, value, bold, indent, highlight, separator}) => (
            separator
              ? <div style={{ borderBottom: '2px solid var(--border-color)', margin: '4px 0' }} />
              : <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding: bold ? '10px 0' : '6px 0', borderBottom: highlight ? '2px solid #059669' : 'none' }}>
                  <span style={{ fontSize: bold ? 15 : 13, fontWeight: bold ? 800 : 500, color: 'var(--text-primary)', paddingLeft: indent ? 16 : 0 }}>{label}</span>
                  <span style={{ fontSize: bold ? 15 : 13, fontWeight: bold ? 800 : 600, color: value < 0 ? '#DC2626' : (highlight ? '#059669' : 'var(--text-primary)'), fontVariantNumeric: 'tabular-nums' }}>{fmtN(value)}</span>
                </div>
          );

          const periodLabel = plPeriod === 'month' ? `${now.toLocaleString('default',{month:'long'})} ${now.getFullYear()}`
            : plPeriod === 'quarter' ? `Q${Math.floor(now.getMonth()/3)+1} ${now.getFullYear()}`
            : plPeriod === 'year' ? `${now.getFullYear()}`
            : `${plFrom} – ${plTo}`;

          return (
            <div className="popup-overlay" style={{ zIndex:1700, alignItems:'flex-start', paddingTop:40 }}>
              <div className="popup-form" style={{ maxWidth:520, width:'100%', borderRadius:20, padding:0, overflow:'hidden', maxHeight:'90vh', display:'flex', flexDirection:'column' }}>
                {/* Header */}
                <div style={{ background:'#111827', padding:'20px 24px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <div>
                    <div style={{ color:'#A3E635', fontSize:11, fontWeight:700, letterSpacing:1, marginBottom:3 }}>{t.profitAndLoss || 'PROFIT & LOSS STATEMENT'}</div>
                    <div style={{ color:'#FFFFFF', fontSize:18, fontWeight:800 }}>{businessName || 'Your Business'}</div>
                    <div style={{ color:'rgba(255,255,255,0.6)', fontSize:12, marginTop:2 }}>{periodLabel}</div>
                  </div>
                  <button onClick={() => setShowPandL(false)} style={{ background:'rgba(255,255,255,0.1)', border:'none', borderRadius:10, padding:'8px 14px', color:'#fff', fontSize:13, cursor:'pointer', fontWeight:600 }}>×</button>
                </div>

                {/* Period selector */}
                <div style={{ background:'var(--bg-tab)', padding:'12px 20px', display:'flex', gap:8, overflowX:'auto', borderBottom:'1px solid var(--border-color)' }}>
                  {[['month', language==='sw'?'Mwezi':'Month'],['quarter',language==='sw'?'Robo':'Quarter'],['year',language==='sw'?'Mwaka':'Year'],['custom',language==='sw'?'Maalum':'Custom']].map(([k,l])=>(
                    <button key={k} onClick={()=>setPlPeriod(k)}
                      style={{ padding:'7px 16px', borderRadius:20, border:'none', fontWeight:700, fontSize:12, cursor:'pointer', whiteSpace:'nowrap',
                        background: plPeriod===k ? '#111827' : 'var(--bg-card)', color: plPeriod===k ? '#A3E635' : 'var(--text-secondary)' }}>
                      {l}
                    </button>
                  ))}
                  {plPeriod === 'custom' && (
                    <>
                      <input type="date" value={plFrom} onChange={e=>setPlFrom(e.target.value)}
                        style={{ padding:'6px 8px', borderRadius:10, border:'1px solid var(--border-color)', background:'var(--bg-card)', fontSize:12, outline:'none' }} />
                      <input type="date" value={plTo} onChange={e=>setPlTo(e.target.value)}
                        style={{ padding:'6px 8px', borderRadius:10, border:'1px solid var(--border-color)', background:'var(--bg-card)', fontSize:12, outline:'none' }} />
                    </>
                  )}
                </div>

                {/* P&L body */}
                <div style={{ overflowY:'auto', flex:1, padding:'20px 24px' }}>
                  {/* Revenue */}
                  <div style={{ fontSize:11, fontWeight:800, color:'#6B7280', textTransform:'uppercase', letterSpacing:1, marginBottom:6 }}>{language==='sw'?'MAPATO':'REVENUE'}</div>
                  {businessType !== 'service_only' && <Row label={language==='sw'?'Mauzo ya Bidhaa':'Product Sales'} value={productSalesRev} indent />}
                  {businessType !== 'products_only' && <Row label={language==='sw'?'Mapato ya Huduma':'Service Revenue'} value={serviceRev + serviceTransRev} indent />}
                  <Row separator />
                  <Row label={language==='sw'?'Jumla ya Mapato':'Total Revenue'} value={totalRevenue} bold />
                  {_stmtOutputVAT > 0 && <>
                    <Row label={language==='sw'?'Ondoa: VAT iliyokusanywa (ya TRA)':'Less: VAT collected (for TRA)'} value={-_stmtOutputVAT} indent />
                    <Row label={language==='sw'?'Mapato Halisi (bila VAT)':'Net Revenue (ex-VAT)'} value={totalRevenueExVAT} bold />
                  </>}

                  {/* COGS */}
                  {businessType !== 'service_only' && <>
                    <div style={{ marginTop:16, fontSize:11, fontWeight:800, color:'#6B7280', textTransform:'uppercase', letterSpacing:1, marginBottom:6 }}>{language==='sw'?'GHARAMA ZA BIDHAA':'COST OF GOODS SOLD'}</div>
                    <Row label={language==='sw'?'Manunuzi ya Bidhaa':'Purchases / Stock Cost'} value={cogs} indent />
                    <Row separator />
                    <Row label={language==='sw'?'Faida Ghafi':'Gross Profit'} value={grossProfit} bold />
                  </>}

                  {/* Expenses */}
                  <div style={{ marginTop:16, fontSize:11, fontWeight:800, color:'#6B7280', textTransform:'uppercase', letterSpacing:1, marginBottom:6 }}>{language==='sw'?'MATUMIZI':'OPERATING EXPENSES'}</div>
                  {Object.entries(expByCategory).sort((a,b)=>b[1]-a[1]).map(([cat,amt])=>(
                    <Row key={cat} label={cat} value={amt} indent />
                  ))}
                  {Object.keys(expByCategory).length === 0 && <div style={{fontSize:13,color:'var(--text-secondary)',paddingLeft:16,paddingBottom:6}}>{language==='sw'?'Hakuna matumizi':'No expenses recorded'}</div>}
                  <Row separator />
                  <Row label={language==='sw'?'Jumla ya Matumizi':'Total Expenses'} value={totalExpenses} bold />

                  {/* Net Profit */}
                  <div style={{ marginTop:16, background: netProfit >= 0 ? '#DCFCE7' : '#FEE2E2', borderRadius:14, padding:'16px 18px' }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                      <div>
                        <div style={{ fontSize:11, fontWeight:800, color: netProfit>=0?'#065F46':'#991B1B', letterSpacing:1, marginBottom:4 }}>{language==='sw'?'FAIDA / HASARA HALISI':'NET PROFIT / LOSS'}</div>
                        <div style={{ fontSize:24, fontWeight:900, color: netProfit>=0?'#059669':'#DC2626' }}>{fmtN(netProfit)}</div>
                      </div>
                      <div style={{ fontSize:36 }}>{netProfit>=0?'↗':'↘'}</div>
                    </div>
                    {totalRevenueExVAT > 0 && <div style={{ fontSize:12, color: netProfit>=0?'#065F46':'#991B1B', marginTop:8 }}>
                      {language==='sw'?'Kiwango cha faida:':'Profit margin:'} {((netProfit/totalRevenueExVAT)*100).toFixed(1)}%
                    </div>}
                  </div>
                  {(_stmtOutputVAT > 0 || _stmtInputVAT > 0) && (
                    <div style={{ marginTop:12, background:'var(--bg-tab)', borderRadius:12, padding:'12px 16px', fontSize:12, color:'var(--text-secondary)' }}>
                      <div style={{ fontWeight:800, color:'var(--text-primary)', marginBottom:6, letterSpacing:0.5 }}>{language==='sw'?'MUHTASARI WA VAT':'VAT SUMMARY'}</div>
                      <div style={{ display:'flex', justifyContent:'space-between' }}><span>{language==='sw'?'VAT ya mauzo (output)':'Output VAT (on sales)'}</span><span>{fmtN(_stmtOutputVAT)}</span></div>
                      <div style={{ display:'flex', justifyContent:'space-between' }}><span>{language==='sw'?'VAT ya manunuzi (input)':'Input VAT (on purchases)'}</span><span>{fmtN(_stmtInputVAT)}</span></div>
                      <div style={{ display:'flex', justifyContent:'space-between', fontWeight:800, color:'var(--text-primary)', marginTop:4 }}><span>{language==='sw'?'VAT ya kulipa TRA':'Net VAT payable to TRA'}</span><span>{fmtN(_stmtVATPayable)}</span></div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

        {activeTab === 'purchases' && (
          <div className="purchases mn-screen">
            {!isDataReady ? (
              <div role="status" aria-label={t.loading || 'Loading'} style={{ padding: 16 }}>
                <div className="skeleton skeleton-title"></div>
                <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
                  {[1,2,3].map(i => <div key={i} className="skeleton skeleton-btn" style={{ flex: 1, width: 'auto' }}></div>)}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
                  <div className="skeleton skeleton-summary-card"></div>
                  <div className="skeleton skeleton-summary-card"></div>
                </div>
                <div className="skeleton skeleton-chart"></div>
                <div className="skeleton skeleton-table-header"></div>
                {[1,2,3,4,5].map(i => <div key={i} className="skeleton skeleton-row"></div>)}
              </div>
            ) : null}
            {isDataReady && (() => {
              // Purchases tab single-color palette -indigo / slate family (operations, not danger)
              const JEWEL_TONES = ['#6366F1','#4F46E5','#818CF8','#4338CA','#A5B4FC','#3730A3','#C7D2FE','#312E81','#E0E7FF','#1E1B4B'];
              const allPurchaseProductsSorted = [...new Set((purchases || []).map(p => p.productName).filter(Boolean))].sort();
              const productColorMap = {};
              allPurchaseProductsSorted.forEach((name, i) => { productColorMap[name] = JEWEL_TONES[i % JEWEL_TONES.length]; });
              const getProductColor = (name) => productColorMap[name] || JEWEL_TONES[0];

              // H5: shared period helper (with custom start/end).
              const filteredPurchases = filterByPeriod(purchases, purchasesTimeFilter, purchasesCustomFrom, purchasesCustomTo).filter(p => p.status !== 'void');

              // NET of purchase returns -grand totals + returns of the listed
              // purchases, identical to the Purchase Report.
              const _purchTabIds = new Set(filteredPurchases.map(p => String(p.id)));
              const _purchTabReturnsAmt = (purchaseReturns || []).filter(r => r.status !== 'void' && _purchTabIds.has(String(r.originalPurchaseId))).reduce((s, r) => s + parseFloat(r.refundAmt || r.totalRefund || 0), 0);
              const totalCostAmt = filteredPurchases.reduce((s, x) => s + getPurchaseGrandTotal(x), 0) - _purchTabReturnsAmt;
              const totalItemsAmt = filteredPurchases.reduce((s, x) => {
                if (Array.isArray(x.items) && x.items.length > 0) {
                  return s + x.items.reduce((qs, it) => qs + parseInt(it.quantity || 0), 0);
                }
                return s + parseInt(x.quantity || 0);
              }, 0);
              const ordersCount = filteredPurchases.length;

              // By-product breakdown (for bar chart + top/low)
              // Handles BOTH single-product purchases (legacy: productName+quantity at top level)
              // AND multi-item purchases (items[] array -one purchase order, many products)
              const byProductMap = {};
              filteredPurchases.forEach(p => {
                if (Array.isArray(p.items) && p.items.length > 0) {
                  // Multi-item purchase -count each line individually
                  p.items.forEach(it => {
                    const name = it.productName || it.name;
                    if (!name) return;
                    if (!byProductMap[name]) byProductMap[name] = { name, cost: 0, qty: 0 };
                    // Per-item cost: prefer explicit totalCost, else qty * unitCost/price
                    const itemCost = parseFloat(it.totalCost) || (parseFloat(it.quantity || 0) * parseFloat(it.unitCost || it.cost || it.unitPrice || 0));
                    byProductMap[name].cost += itemCost || 0;
                    byProductMap[name].qty += parseInt(it.quantity || 0);
                  });
                } else if (p.productName) {
                  // Single-product legacy purchase
                  if (!byProductMap[p.productName]) byProductMap[p.productName] = { name: p.productName, cost: 0, qty: 0 };
                  byProductMap[p.productName].cost += parseFloat(p.totalCost || 0);
                  byProductMap[p.productName].qty += parseInt(p.quantity || 0);
                }
              });
              const mnByProduct = Object.values(byProductMap).sort((a, b) => b.cost - a.cost);
              const maxBarCost = mnByProduct.length > 0 ? mnByProduct[0].cost : 1;
              // All products perf (includes 0-purchase products) -for the bar chart
              const allProductsPurchasePerf = (products || [])
                .map(p => ({ name: p.productName || p.name || '—', cost: byProductMap[p.productName || p.name]?.cost || 0, qty: byProductMap[p.productName || p.name]?.qty || 0 }))
                .sort((a, b) => b.cost - a.cost);
              const maxAllPurchaseCost = allProductsPurchasePerf.length > 0 ? (allProductsPurchasePerf[0].cost || 1) : 1;
              const mnTopPurchased = mnByProduct.slice(0, 5).map((item) => ({
                ...item, color: getProductColor(item.name),
                pct: totalCostAmt > 0 ? Math.round(item.cost / totalCostAmt * 100) : 0
              }));
              const mnTopPurchasedNames = new Set(mnTopPurchased.map(t => t.name));
              const mnLowPurchased = [...mnByProduct]
                .sort((a, b) => a.cost - b.cost)
                .filter(item => !mnTopPurchasedNames.has(item.name))
                .slice(0, 4);

              // HISTORY list -voided purchases stay visible (marked VOIDED)
              // unless the user ticks "Hide voided". Display only: every total on
              // this tab reads filteredPurchases, which always excludes void.
              const mnRecent = [...purchases]
                .filter(p => !hideVoidedPurchases || p.status !== 'void')
                .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0)).slice(0, 5);

              // Period display label
              const _pnow = new Date();
              const _ppad = n => String(n).padStart(2, '0');
              const _pfmt = d => `${_ppad(d.getDate())}/${_ppad(d.getMonth()+1)}/${d.getFullYear()}`;
              let purchasesPeriodLabel = '';
              if (purchasesTimeFilter === 'today') {
                purchasesPeriodLabel = _pfmt(_pnow);
              } else if (purchasesTimeFilter === 'week') {
                const ws = new Date(_pnow); ws.setDate(_pnow.getDate() - 7);
                purchasesPeriodLabel = `${_pfmt(ws)} \u2013 ${_pfmt(_pnow)}`;
              } else if (purchasesTimeFilter === 'month') {
                const _puMn = (t.monthNames || ['January','February','March','April','May','June','July','August','September','October','November','December'])[_pnow.getMonth()];
                purchasesPeriodLabel = `${_puMn} ${_pnow.getFullYear()}`;
              } else if (purchasesTimeFilter === 'year') {
                purchasesPeriodLabel = String(_pnow.getFullYear());
              } else if (purchasesTimeFilter === 'custom' && purchasesCustomFrom && purchasesCustomTo) {
                purchasesPeriodLabel = `${purchasesCustomFrom} \u2013 ${purchasesCustomTo}`;
              }

              // Annual performance (monthly breakdown for selected year)
              const MN_MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
              const mnAnnualData = MN_MONTHS.map((label, m) => ({
                label,
                cost: (purchases || []).filter(p => { const d = new Date(p.date || 0); return !isNaN(d.getTime()) && d.getFullYear() === purchasesAnnualYear && d.getMonth() === m && p.status !== 'void'; })
                              .reduce((sum, x) => sum + getPurchaseGrandTotal(x), 0)
              }));
              const mnAnnualTotal = mnAnnualData.reduce((s, d) => s + d.cost, 0);
              const mnMaxAnnualCost = Math.max(...mnAnnualData.map(d => d.cost), 1);

              // Year selector options
              const mnRawYears = [...new Set(purchases.map(p => new Date(p.date).getFullYear()).filter(y => !isNaN(y)))].sort((a, b) => b - a);
              const _mnNow = new Date().getFullYear();
              const _mnFuture = Array.from({ length: 5 }, (_, i) => _mnNow + i + 1);
              const mnAvailableYears = [...new Set([..._mnFuture, purchasesAnnualYear, ...mnRawYears])].sort((a, b) => b - a);

              return (
                <>
                  {/* ADD PURCHASE MODAL */}
                  {showPurchaseForm && (
                    <div className="popup-overlay">
                      <div className="popup-form mz-modal" style={{ maxWidth: 420, width: '100%', borderRadius: 16, padding: 24 }}>
                        <h3 className="mz-modal-title" style={{ marginBottom: 14, fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>{t.addPurchase}</h3>
                        {(products || []).length === 0
                          ? <div style={{ textAlign: 'center', padding: '16px 0', color: 'var(--text-muted)' }}>{t.pleaseRegisterProducts}</div>
                          : (<>

                          {/* ── PURCHASE CART: added items (inline-editable qty + cost) ── */}
                          {purchaseLineItems.length > 0 && (
                            <div style={{ marginBottom: 12, borderRadius: 12, overflow: 'hidden', border: '1px solid var(--border-color)' }}>
                              {/* Header row */}
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 60px 18px 90px 90px 28px', gap: 6, padding: '6px 12px', background: '#F3F4F6', fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4, alignItems: 'center' }}>
                                <div>{language === 'sw' ? 'Bidhaa' : 'Item'}</div>
                                <div style={{ textAlign: 'right' }}>{language === 'sw' ? 'Idadi' : 'Qty'}</div>
                                <div />
                                <div style={{ textAlign: 'right' }}>{language === 'sw' ? 'Bei' : 'Cost'}</div>
                                <div style={{ textAlign: 'right' }}>{language === 'sw' ? 'Jumla' : 'Total'}</div>
                                <div />
                              </div>
                              {purchaseLineItems.map((item, idx) => {
                                const qty = parseFloat(item.quantity || 0);
                                const unit = parseFloat(item.unitPrice || 0);
                                const lineTotal = qty * unit;
                                return (
                                  <div key={item.id} style={{ display: 'grid', gridTemplateColumns: '1fr 60px 18px 90px 90px 28px', gap: 6, padding: '8px 12px', background: idx % 2 === 0 ? 'var(--bg-tab)' : 'var(--bg-card)', borderTop: '1px solid var(--border-color)', alignItems: 'center' }}>
                                    <div style={{ minWidth: 0 }}>
                                      <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.productName}</div>
                                    </div>
                                    <input type="number" min="0" step="any" value={item.quantity || ''}
                                      onChange={e => {
                                        const v = e.target.value;
                                        setPurchaseLineItems(prev => prev.map(i => i.id === item.id
                                          ? { ...i, quantity: v, totalCost: String((parseFloat(v) || 0) * (parseFloat(i.unitPrice) || 0)) }
                                          : i));
                                      }}
                                      style={{ width: '100%', padding: '5px 6px', borderRadius: 6, border: '1px solid var(--border-color)', background: 'var(--bg-white)', fontSize: 12, textAlign: 'right', outline: 'none', boxSizing: 'border-box' }} />
                                    <div style={{ textAlign: 'center', fontSize: 11, color: 'var(--text-secondary)' }}>×</div>
                                    <input type="number" min="0" step="any" value={item.unitPrice || ''}
                                      onChange={e => {
                                        const v = e.target.value;
                                        setPurchaseLineItems(prev => prev.map(i => i.id === item.id
                                          ? { ...i, unitPrice: v, totalCost: String((parseFloat(i.quantity) || 0) * (parseFloat(v) || 0)) }
                                          : i));
                                      }}
                                      style={{ width: '100%', padding: '5px 6px', borderRadius: 6, border: '1px solid var(--border-color)', background: 'var(--bg-white)', fontSize: 12, textAlign: 'right', outline: 'none', boxSizing: 'border-box' }} />
                                    <div style={{ textAlign: 'right', fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{fmt(lineTotal)}</div>
                                    <button onClick={() => setPurchaseLineItems(prev => prev.filter(i => i.id !== item.id))}
                                      style={{ width: 24, height: 24, borderRadius: 6, background: '#FEE2E2', color: '#DC2626', border: 'none', fontWeight: 700, fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                                  </div>
                                );
                              })}
                              <div style={{ padding: '8px 12px', background: 'var(--bg-input)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid var(--border-color)' }}>
                                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>{language === 'sw' ? 'Jumla' : 'Order total'}</span>
                                <span style={{ fontSize: 14, fontWeight: 800, color: 'var(--text-secondary)' }}>
                                  {fmt(purchaseLineItems.reduce((s, i) => s + (parseFloat(i.quantity) || 0) * (parseFloat(i.unitPrice) || 0), 0))}
                                </span>
                              </div>
                            </div>
                          )}

                          {/* ── ADD ITEM ROW ── */}
                          <div style={{ background: 'var(--bg-tab)', borderRadius: 12, padding: 12, marginBottom: 16 }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              {purchaseLineItems.length === 0 ? (language === 'sw' ? 'Ongeza bidhaa' : 'Add item') : (language === 'sw' ? 'Ongeza bidhaa nyingine' : 'Add another item')}
                            </div>
                            <select value={purchaseLineDraft.productName} onChange={e => {
                              const v = e.target.value;
                              const _prod = (products || []).find(p => p.productName === v);
                              // C2/H6: capture the stable productId (display name aside).
                              setPurchaseLineDraft(p => ({ ...p, productName: v, productId: _prod ? _prod.id : undefined, unitPrice: '', quantity: '', totalCost: '' }));
                            }} style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: 'none', background: 'var(--bg-card)', fontSize: 14, boxSizing: 'border-box', outline: 'none', marginBottom: 8 }}>
                              <option value="">{t.selectProduct}</option>
                              {notArchived(products).map(p => <option key={p.id} value={p.productName}>{p.productName}</option>)}
                            </select>
                            <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                              <div style={{ width: '28%' }}>
                                <label style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 600, display: 'block', marginBottom: 4 }}>{t.qty || 'Qty'}</label>
                                <input type="number" inputMode="numeric" min="1" placeholder="0" value={purchaseLineDraft.quantity}
                                  onChange={e => {
                                    const qty = e.target.value;
                                    const up = parseFloat(purchaseLineDraft.unitPrice) || 0;
                                    setPurchaseLineDraft(p => ({ ...p, quantity: qty, totalCost: up && qty ? String(up * parseFloat(qty)) : p.totalCost }));
                                  }}
                                  style={{ width: '100%', padding: '10px 8px', borderRadius: 10, border: 'none', background: 'var(--bg-card)', fontSize: 14, boxSizing: 'border-box', outline: 'none' }} />
                              </div>
                              <div style={{ flex: 1 }}>
                                <label style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 600, display: 'block', marginBottom: 4 }}>{t.unitPrice || 'Unit Price'}</label>
                                <input type="number" inputMode="decimal" min="0" placeholder="0" value={purchaseLineDraft.unitPrice}
                                  onChange={e => {
                                    const up = e.target.value;
                                    const qty = parseFloat(purchaseLineDraft.quantity) || 0;
                                    setPurchaseLineDraft(p => ({ ...p, unitPrice: up, totalCost: up && qty ? String(parseFloat(up) * qty) : p.totalCost }));
                                  }}
                                  style={{ width: '100%', padding: '10px 8px', borderRadius: 10, border: 'none', background: 'var(--bg-card)', fontSize: 14, boxSizing: 'border-box', outline: 'none' }} />
                              </div>
                              <div style={{ flex: 1 }}>
                                <label style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 600, display: 'block', marginBottom: 4 }}>{t.totalCost || 'Total Cost'}</label>
                                <input type="number" inputMode="decimal" min="0" placeholder="0" value={purchaseLineDraft.totalCost}
                                  onChange={e => setPurchaseLineDraft(p => ({ ...p, totalCost: e.target.value }))}
                                  style={{ width: '100%', padding: '10px 8px', borderRadius: 10, border: 'none', background: 'var(--bg-card)', fontSize: 14, boxSizing: 'border-box', outline: 'none' }} />
                              </div>
                            </div>
                            <button type="button" onClick={() => {
                              const d = purchaseLineDraft;
                              if (!d.productName || !d.quantity || !d.totalCost) return;
                              setPurchaseLineItems(prev => [...prev, { ...d, id: Date.now() }]);
                              setPurchaseLineDraft({ productName: '', quantity: '', unitPrice: '', totalCost: '' });
                            }} style={{ width: '100%', padding: '10px 0', borderRadius: 10, background: '#111827', color: '#A3E635', border: 'none', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
                              + {language === 'sw' ? 'Ongeza kwenye orodha' : 'Add to order'}
                            </button>
                          </div>

                          {/* Seller (Muuzaji) Toggle -OPTIONAL, mirrors Customer Type */}
                          <div style={{ marginBottom: 16 }}>
                            <label style={{ display: 'block', marginBottom: 8, color: 'var(--text-primary)', fontWeight: 600, fontSize: 14 }}>
                              {language === 'sw' ? 'Muuzaji' : 'Seller'}
                              <span style={{ fontWeight: 400, fontSize: 12, color: 'var(--text-muted)', marginLeft: 6 }}>({t.optional || 'optional'})</span>
                            </label>
                            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                              {['registered', 'other'].map(type => (
                                <button key={type} type="button"
                                  onClick={() => { setPurchaseSupplierType(type); setPurchaseFormData(p => ({ ...p, supplier: '' })); }}
                                  style={{ flex: 1, padding: '10px 0', borderRadius: 10, border: 'none', fontSize: 14, fontWeight: 600, cursor: 'pointer',
                                    background: purchaseSupplierType === type ? '#A3E635' : '#F3F4F6',
                                    color: purchaseSupplierType === type ? '#111827' : '#6B7280' }}>
                                  {type === 'registered' ? t.registeredSupplier : t.otherSupplier}
                                </button>
                              ))}
                            </div>
                            {purchaseSupplierType === 'registered' ? (
                              <select name="supplier" value={purchaseFormData.supplier} onChange={handlePurchaseInputChange}
                                style={{ width: '100%', padding: 14, borderRadius: 12, border: 'none', background: 'var(--bg-tab)', fontSize: 15, boxSizing: 'border-box', outline: 'none' }}>
                                <option value="">{language === 'sw' ? '— Chagua muuzaji —' : '— Select seller —'}</option>
                                {(suppliers || []).map(s => (
                                  <option key={s.id} value={s.name}>{s.name}{s.contact ? ` (${s.contact})` : ''}</option>
                                ))}
                              </select>
                            ) : (
                              <input type="text" name="supplier" value={purchaseFormData.supplier} onChange={handlePurchaseInputChange}
                                placeholder={t.enterSupplierName}
                                style={{ width: '100%', padding: 14, borderRadius: 12, border: 'none', background: 'var(--bg-tab)', fontSize: 15, boxSizing: 'border-box', outline: 'none' }} />
                            )}
                          </div>

                          {/* Date */}
                          <div style={{ marginBottom: 16 }}>
                            <label style={{ display: 'block', marginBottom: 6, color: 'var(--text-primary)', fontWeight: 600, fontSize: 14 }}>{t.date}</label>
                            <input type="date" name="date" value={purchaseFormData.date} onChange={handlePurchaseInputChange}
                              style={{ width: '100%', padding: 14, borderRadius: 12, border: 'none', background: 'var(--bg-tab)', fontSize: 15, boxSizing: 'border-box', outline: 'none' }} />
                          </div>

                          {/* EFD Receipt Number -optional */}
                          <div style={{ marginBottom: 16 }}>
                            <label style={{ display: 'block', marginBottom: 6, color: 'var(--text-primary)', fontWeight: 600, fontSize: 14 }}>
                              EFD Receipt No. <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>({language === 'sw' ? 'Hiari' : 'optional'})</span>
                            </label>
                            <input type="text" name="efdReceiptNo" placeholder="e.g. EFD-0000123"
                              value={purchaseFormData.efdReceiptNo} onChange={handlePurchaseInputChange}
                              style={{ width: '100%', padding: '9px 12px', borderRadius: 12, border: 'none', background: 'var(--bg-tab)', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
                          </div>

                          {/* Discount */}
                          <div style={{ marginBottom: 16 }}>
                            <label style={{ display: 'block', marginBottom: 6, color: 'var(--text-primary)', fontWeight: 600, fontSize: 14 }}>
                              {language === 'sw' ? 'Punguzo (Discount)' : 'Punguzo (Discount)'} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>({t.optional || 'optional'})</span>
                            </label>
                            <input type="number" min="0" max="100" inputMode="numeric" placeholder="0"
                              value={purchaseFormData.discount}
                              onChange={e => setPurchaseFormData(p => ({ ...p, discount: e.target.value }))}
                              style={{ width: '100%', padding: '9px 12px', borderRadius: 12, border: 'none', background: 'var(--bg-tab)', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
                            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>{language === 'sw' ? 'Asilimia ya punguzo (%)' : 'Discount percentage (%)'}</div>
                          </div>

                          {/* Taxes -only for VAT-registered businesses */}
                          {vatRegistered && (<div style={{ marginBottom: 16 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                              <label style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: 14 }}>
                                {language === 'sw' ? 'Kodi' : 'Taxes'} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>({t.optional || 'optional'})</span>
                              </label>
                              <button type="button" onClick={addPurchaseTax}
                                style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', background: 'var(--bg-input)', border: 'none', borderRadius: 8, padding: '4px 10px', cursor: 'pointer' }}>
                                {t.addTax || '+ Add Tax'}
                              </button>
                            </div>
                            {(purchaseFormData.taxes || []).map((tax, idx) => (
                              <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                                <select value={tax.type} onChange={e => updatePurchaseTax(idx, 'type', e.target.value)}
                                  style={{ flex: 1, padding: '12px 12px', borderRadius: 12, border: '1px solid var(--border-color)', background: 'var(--bg-white)', fontSize: 14, outline: 'none', minWidth: 0, cursor: 'pointer' }}>
                                  <option value="vat">{t.vatOption || 'VAT (Value Added Tax)'}</option>
                                  <option value="withholding">{t.withholdingOption || 'Withholding Tax'}</option>
                                </select>
                                <input type="number" min="0" inputMode="numeric" placeholder="Rate %"
                                  value={tax.rate} onChange={e => updatePurchaseTax(idx, 'rate', e.target.value)}
                                  style={{ width: 70, padding: '12px 10px', borderRadius: 12, border: '1px solid var(--border-color)', background: 'var(--bg-white)', fontSize: 14, outline: 'none', textAlign: 'right', flexShrink: 0, boxSizing: 'border-box' }} />
                                <button type="button" onClick={() => removePurchaseTax(idx)}
                                  style={{ width: 36, height: 46, borderRadius: 10, border: 'none', background: '#FEE2E2', color: '#DC2626', cursor: 'pointer', fontSize: 20, fontWeight: 700, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                              </div>
                            ))}
                            {(purchaseFormData.taxes || []).length === 0 && (
                              <div style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>{t.noTaxesAdded || 'No taxes added. Click "Add Tax" to include VAT 18% or withholding tax.'}</div>
                            )}
                          </div>)}

                          {/* Total Display */}
                          {(() => {
                            const displayTotal = getPurchaseTotalForPayments();
                            return displayTotal > 0 ? (
                              <div style={{ marginBottom: 16, padding: '10px 14px', borderRadius: 14, background: 'var(--bg-tab)', border: '1.5px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-primary)' }}>{language === 'sw' ? 'Jumla' : 'Total'}</span>
                                <span style={{ fontWeight: 800, fontSize: 18, color: '#4F46E5' }}>{fmt(displayTotal)}</span>
                              </div>
                            ) : null;
                          })()}

                          {/* Payment -split payment rows like sales form */}
                          {(() => {
                            const purchTotal = getPurchaseTotalForPayments();
                            // Credit doesn't count as money paid -it's a deferred obligation
                            const paidAmt = (purchasePayments || []).filter(p => p.type !== 'credit').reduce((s, p) => s + parseFloat(p.amount || 0), 0);
                            const creditAmt = (purchasePayments || []).filter(p => p.type === 'credit').reduce((s, p) => s + parseFloat(p.amount || 0), 0);
                            const coverage = paidAmt + creditAmt;
                            const remaining = Math.max(0, purchTotal - coverage);
                            const isCreditOnly = creditAmt > 0 && paidAmt === 0;
                            const isPartialCredit = creditAmt > 0 && paidAmt > 0;
                            const payOpts = buildPaymentOptions(true);
                            return (
                              <div style={{ marginBottom: 24 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                  <label style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: 14 }}>{t.paymentMethod}</label>
                                  {(purchasePayments || []).length < (payOpts || []).length && purchTotal > 0 && (
                                    <button type="button" onClick={addPurchasePayment}
                                      style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', background: 'var(--bg-input)', border: 'none', borderRadius: 8, padding: '4px 10px', cursor: 'pointer' }}>
                                      + {t.add || 'Add'}
                                    </button>
                                  )}
                                </div>
                                {(purchasePayments || []).map((pay, idx) => {
                                  const otherRowsTotal = (purchasePayments || []).reduce((s, p, i) => i !== idx ? s + parseFloat(p.amount || 0) : s, 0);
                                  const maxForRow = Math.max(0, purchTotal - otherRowsTotal);
                                  // Balance of the SPECIFIC selected account (bank|id), not the whole
                                  // category -read from the ledger via getWalletBalance.
                                  const walletBal = getWalletBalance(pay.accountKey || pay.type);
                                  const effectiveMax = pay.type === 'credit' ? maxForRow : Math.min(maxForRow, Math.max(0, walletBal));
                                  const enteredAmt = parseFloat(pay.amount || 0);
                                  // Uniqueness rule: exclude specific accounts already chosen by other rows
                                  const currentKey = pay.accountKey || pay.type;
                                  const usedByOthers = new Set((purchasePayments || []).map((p, i) => i !== idx ? (p.accountKey || p.type) : null).filter(Boolean));
                                  const availableOpts = (payOpts || []).filter(o => !usedByOthers.has(o.value) || o.value === currentKey);
                                  return (
                                    <div key={idx} style={{ marginBottom: 10 }}>
                                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                        <select value={currentKey} onChange={e => handlePurchasePaymentTypeChange(idx, e.target.value)}
                                          style={{ flex: 1, padding: '12px 10px', borderRadius: 12, border: 'none', background: 'var(--bg-tab)', fontSize: 14, outline: 'none', minWidth: 0 }}>
                                          {availableOpts.map(o => <option key={o.value + o.label} value={o.value}>{o.label}</option>)}
                                        </select>
                                        <input type="number" min="0" inputMode="numeric" placeholder="0"
                                          value={pay.amount}
                                          onChange={e => handlePurchasePaymentAmountChange(idx, e.target.value)}
                                          style={{ width: 100, padding: '12px 10px', borderRadius: 12, border: 'none', background: 'var(--bg-tab)', fontSize: 14, outline: 'none', textAlign: 'right', flexShrink: 0, boxSizing: 'border-box' }} />
                                        {(purchasePayments || []).length > 1 && (
                                          <button type="button" onClick={() => removePurchasePayment(idx)}
                                            style={{ width: 36, height: 46, borderRadius: 10, border: 'none', background: '#FEE2E2', color: '#DC2626', cursor: 'pointer', fontSize: 20, fontWeight: 700, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                                        )}
                                      </div>
                                      {pay.type !== 'credit' && (
                                        <div style={{ fontSize: 12, fontWeight: 600, marginTop: 3, paddingLeft: 4, color: enteredAmt > walletBal ? '#DC2626' : '#059669' }}>
                                          {language === 'sw' ? 'Salio' : 'Balance'}: {fmt(walletBal)}{enteredAmt > walletBal ? ` (${language === 'sw' ? 'Haitoshi' : 'Insufficient'})` : ''}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                                {purchTotal > 0 && (() => {
                                  // Determine status: missing coverage > unpaid (credit only) > partial > fully paid
                                  if (remaining > 0.01) {
                                    return (
                                      <div style={{ borderRadius: 10, padding: '8px 12px', marginTop: 4, background: '#FEF3C7', color: '#92400E', fontSize: 13, fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span>{language === 'sw' ? 'Hayajakamilika' : 'Incomplete'}</span>
                                        <span>{fmt(remaining)} {language === 'sw' ? 'bakaza' : 'remaining'}</span>
                                      </div>
                                    );
                                  }
                                  if (isCreditOnly) {
                                    return (
                                      <div style={{ borderRadius: 10, padding: '8px 12px', marginTop: 4, background: '#FEF3C7', color: '#92400E', fontSize: 13, fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span>{language === 'sw' ? 'Kwenye Mkopo' : 'On Credit (Unpaid)'}</span>
                                        <span>{fmt(creditAmt)} {language === 'sw' ? 'kwa mkopo' : 'owed'}</span>
                                      </div>
                                    );
                                  }
                                  if (isPartialCredit) {
                                    return (
                                      <div style={{ borderRadius: 10, padding: '8px 12px', marginTop: 4, background: '#FEF3C7', color: '#92400E', fontSize: 13, fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span>{language === 'sw' ? 'Sehemu kwa Mkopo' : 'Partly on Credit'}</span>
                                        <span>{fmt(creditAmt)} {language === 'sw' ? 'kwa mkopo' : 'on credit'}</span>
                                      </div>
                                    );
                                  }
                                  return (
                                    <div style={{ borderRadius: 10, padding: '8px 12px', marginTop: 4, background: '#D1FAE5', color: '#065F46', fontSize: 13, fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                      <span>{t.paid || 'Paid'}</span>
                                    </div>
                                  );
                                })()}
                              </div>
                            );
                          })()}
                        </>)}
                        <div className="form-buttons">
                          {purchaseLineItems.length === 0 && (
                            <p style={{ color: '#DC2626', fontSize: 13, fontWeight: 600, margin: '0 0 10px', textAlign: 'center' }}>
                              ⚠ {language === 'sw' ? 'Ongeza angalau bidhaa moja' : 'Add at least one item to the order'}
                            </p>
                          )}
                          <button className="save-btn" onClick={handleSavePurchase} disabled={purchaseLineItems.length === 0 && !(purchaseLineDraft.productName && purchaseLineDraft.quantity && purchaseLineDraft.totalCost)}>{t.save}</button>
                          <button className="cancel-btn" onClick={() => { setShowPurchaseForm(false); resetPurchaseForm(); }}>{t.cancel}</button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* PURCHASE REPORT SCREEN */}
                  {isPurchasesReportOpen && (() => {
                    const filteredPurchases = getReportFilteredPurchases().filter(p => !hideVoidedPurchases || p.status !== 'void');
                    const getPurchaseReturnsForId = (pid) => (purchaseReturns || []).filter(r => r.status !== 'void' && String(r.originalPurchaseId) === String(pid));
                    // Accounting totals ALWAYS exclude void (display rows are
                    // toggle-controlled, but totals/exports are not).
                    const acctFilteredPurchases = filteredPurchases.filter(p => p.status !== 'void');
                    const purchasesTotal = acctFilteredPurchases.reduce((sum, p) => sum + getPurchaseGrandTotal(p), 0);
                    // Returns = the returns of the purchases actually listed, so
                    // the RETURNS summary always matches the Returns column in
                    // the table (no "phantom" returns with no row).
                    const totalPurchaseReturnsAmt = acctFilteredPurchases.reduce((s, p) =>
                      s + getPurchaseReturnsForId(p.id).reduce((a, r) => a + parseFloat(r.refundAmt || 0), 0), 0);
                    const netPurchases = purchasesTotal - totalPurchaseReturnsAmt;
                    const prH = ['Date','Product','Qty','Unit Cost','Total Cost','Supplier','Payment','Status','Returns Qty','Returns Amount'];
                    const prR = () => {
                      const rows = [];
                      filteredPurchases.forEach(p => {
                        const pRets = getPurchaseReturnsForId(p.id);
                        const retQty = pRets.reduce((s, r) => s + parseFloat(r.qty || 0), 0);
                        const retAmt = pRets.reduce((s, r) => s + parseFloat(r.refundAmt || 0), 0);
                        // Multi-item: expand each item into its own row
                        if (Array.isArray(p.items) && p.items.length > 0) {
                          p.items.forEach((it, idx) => {
                            const isFirst = idx === 0;
                            const itemUnit = it.unitPrice ?? it.unitCost ?? (it.quantity > 0 ? (parseFloat(it.totalCost || 0) / parseFloat(it.quantity || 1)) : '');
                            rows.push([
                              isFirst ? p.date : '',
                              it.productName || '—',
                              it.quantity || 0,
                              itemUnit !== '' ? parseFloat(itemUnit).toFixed(2) : '',
                              parseFloat(it.totalCost || 0).toFixed(2),
                              isFirst ? (p.supplier || '—') : '',
                              isFirst ? (p.paymentMethod || 'cash') : '',
                              isFirst ? (p.isPaid === false ? 'Unpaid' : 'Paid') : '',
                              isFirst ? (retQty || '—') : '',
                              isFirst ? (retAmt || '—') : '',
                            ]);
                          });
                        } else {
                          rows.push([p.date, p.productName, p.quantity, p.costPrice ?? p.unitCost ?? '', p.totalCost, p.supplier||'—', p.paymentMethod||'cash', p.isPaid===false?'Unpaid':'Paid', retQty || '—', retAmt || '—']);
                        }
                      });
                      return rows;
                    };
                    const fromD = reportDateFilters.fromDate ? new Date(reportDateFilters.fromDate).toLocaleDateString() : '—';
                    const toD = reportDateFilters.toDate ? new Date(reportDateFilters.toDate).toLocaleDateString() : '—';
                    return (
                      <div style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'var(--bg-tab)', display: 'flex', flexDirection: 'column' }}>
                        {/* ── HEADER ── */}
                        <div style={{ background: 'var(--bg-white)', borderBottom: '1px solid var(--border-color)', padding: '14px 16px 10px', flexShrink: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
                            <button onClick={() => { setIsPurchasesReportOpen(false); if (drilldownFromTab) { setActiveTab(drilldownFromTab); setDrilldownFromTab(null); } }} style={{ background: 'none', border: 'none', padding: '4px 10px 4px 0', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#111827" strokeWidth="2.5" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
                            </button>
                            <h2 style={{ flex: 1, fontSize: 18, fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>{t.purchaseReports}</h2>
                          </div>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button onClick={() => downloadPDF(prH, prR(), 'Purchases Report', 'purchases-report.pdf')} style={{ flex: 1, padding: '9px 4px', borderRadius: 10, background: '#EF4444', color: '#fff', border: 'none', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>{t.downloadPDF}</button>
                            <button onClick={() => downloadCSV([prH, ...prR()], 'purchases-report.csv')} style={{ flex: 1, padding: '9px 4px', borderRadius: 10, background: '#059669', color: '#fff', border: 'none', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>{t.downloadCSV}</button>
                            <button onClick={() => {
                              // Two-sheet export: order-level summary + line-item detail (best practice)
                              const ordersHeader = ['Date','Supplier','Item Count','Total Qty','Subtotal','Discount %','Tax','Grand Total','Payment','Status','Returns Qty','Returns Amount'];
                              const ordersRows = filteredPurchases.map(p => {
                                const pRets = getPurchaseReturnsForId(p.id);
                                const retQty = pRets.reduce((s, r) => s + parseFloat(r.qty || 0), 0);
                                const retAmt = pRets.reduce((s, r) => s + parseFloat(r.refundAmt || 0), 0);
                                const items = Array.isArray(p.items) && p.items.length > 0 ? p.items : [{ productName: p.productName, quantity: p.quantity, unitPrice: p.unitPrice, totalCost: p.totalCost }];
                                const sub = items.reduce((s, it) => s + parseFloat(it.totalCost || 0), 0);
                                const qtyT = items.reduce((s, it) => s + parseFloat(it.quantity || 0), 0);
                                const discPct = parseFloat(p.discount || 0);
                                const grand = getPurchaseGrandTotal(p);
                                const taxAmt = Math.max(0, grand - sub * (1 - discPct / 100));
                                return [p.date, p.supplier || '—', items.length, qtyT, sub.toFixed(2), discPct, taxAmt.toFixed(2), grand.toFixed(2), p.paymentMethod || 'cash', p.isPaid === false ? 'Unpaid' : 'Paid', retQty || '', retAmt || ''];
                              });
                              const itemsHeader = ['Date','Supplier','Product','Qty','Unit Cost','Line Total','Order Grand Total','Payment','Status'];
                              const itemsRows = filteredPurchases.flatMap(p => {
                                const items = Array.isArray(p.items) && p.items.length > 0 ? p.items : [{ productName: p.productName, quantity: p.quantity, unitPrice: p.unitPrice, totalCost: p.totalCost }];
                                const grand = getPurchaseGrandTotal(p);
                                return items.map(it => {
                                  const qty = parseFloat(it.quantity || 0);
                                  const unit = parseFloat(it.unitPrice ?? it.unitCost ?? (qty > 0 ? parseFloat(it.totalCost || 0) / qty : 0));
                                  return [p.date, p.supplier || '—', it.productName || '—', qty, unit.toFixed(2), parseFloat(it.totalCost || 0).toFixed(2), grand.toFixed(2), p.paymentMethod || 'cash', p.isPaid === false ? 'Unpaid' : 'Paid'];
                                });
                              });
                              downloadExcelMultiSheet([
                                { name: 'Orders', rows: [ordersHeader, ...ordersRows] },
                                { name: 'Line Items', rows: [itemsHeader, ...itemsRows] },
                              ], 'purchases-report.xlsx');
                            }} style={{ flex: 1, padding: '9px 4px', borderRadius: 10, background: '#2563EB', color: '#fff', border: 'none', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>{t.downloadExcel}</button>
                          </div>
                        </div>
                        {/* ── PERIOD SELECTOR ── */}
                        <div style={{ background: 'var(--bg-white)', padding: '12px 16px 8px', flexShrink: 0 }}>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {[{key:'today',label:t.today},{key:'week',label:t.week},{key:'month',label:t.month},{key:'year',label:t.year || t.thisYear || 'Year'},{key:'custom',label:t.custom}].map(({key,label}) => (
                              <button key={key} onClick={() => { setPurchasesReportPeriod(key); const d = getReportDates(key); if (d) setReportDateFilters({ fromDate: d.from, toDate: d.to }); }}
                                style={{ flex: 1, padding: '10px 6px', borderRadius: 16, fontSize: 12, fontWeight: purchasesReportPeriod === key ? 700 : 600, cursor: 'pointer', border: 'none', whiteSpace: 'nowrap',
                                  background: purchasesReportPeriod === key ? 'var(--bg-tab-active)' : 'var(--bg-tab-inactive)',
                                  color: purchasesReportPeriod === key ? 'var(--text-tab-active)' : 'var(--text-tab-inactive)' }}>
                                {label}
                              </button>
                            ))}
                          </div>
                          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>{fromD} - {toD}</div>
                        </div>
                        {/* ── CUSTOM DATE PICKERS ── */}
                        {purchasesReportPeriod === 'custom' && (
                          <div style={{ background: 'var(--bg-white)', padding: '0 16px 12px', borderBottom: '1px solid var(--border-color)', display: 'flex', gap: 10, flexShrink: 0 }}>
                            <div style={{ flex: 1 }}>
                              <label style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>{t.startDate}</label>
                              <input type="date" value={reportDateFilters.fromDate} onFocus={e => { try { e.currentTarget.showPicker(); } catch(x){} }} onChange={e => setReportDateFilters(f => ({...f, fromDate: e.target.value}))} style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid var(--border-color)', background: 'var(--bg-tab)', fontSize: 14, boxSizing: 'border-box' }} />
                            </div>
                            <div style={{ flex: 1 }}>
                              <label style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>{t.endDate}</label>
                              <input type="date" value={reportDateFilters.toDate} onFocus={e => { try { e.currentTarget.showPicker(); } catch(x){} }} onChange={e => setReportDateFilters(f => ({...f, toDate: e.target.value}))} style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid var(--border-color)', background: 'var(--bg-tab)', fontSize: 14, boxSizing: 'border-box' }} />
                            </div>
                          </div>
                        )}
                        {/* ── PRODUCT FILTER ── */}
                        <div style={{ background: 'var(--bg-white)', padding: '8px 16px 10px', borderBottom: '1px solid var(--border-color)', flexShrink: 0 }}>
                          <label style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600, display: 'block', marginBottom: 4 }}>{language === 'sw' ? 'Chuja kwa Bidhaa' : 'Filter by Product'}</label>
                          <select value={purchasesReportProduct} onChange={e => setPurchasesReportProduct(e.target.value)}
                            style={{ width: '100%', padding: '9px 12px', borderRadius: 10, border: '1px solid var(--border-color)', background: 'var(--bg-tab)', fontSize: 14, color: 'var(--text-primary)', outline: 'none' }}>
                            <option value="all">{language === 'sw' ? 'Bidhaa Zote' : 'All Products'}</option>
                            {[...new Set((purchases || []).map(p => p.productName).filter(Boolean))].sort().map(name => (
                              <option key={name} value={name}>{name}</option>
                            ))}
                          </select>
                        </div>
                        {/* ── SUMMARY CARD ── */}
                        <div style={{ background: 'var(--bg-white)', padding: '10px 16px', borderBottom: '1px solid var(--border-color)', flexShrink: 0 }}>
                          <div style={{ display: 'flex', gap: 10 }}>
                            <div className="pastel-card pastel-card--slate">
                              <div className="pc-label">{t.totalPurchases}</div>
                              <div className="pc-value">{fmt(purchasesTotal)}</div>
                            </div>
                            {totalPurchaseReturnsAmt > 0 && (
                              <div className="pastel-card pastel-card--rose">
                                <div className="pc-label">↩ {language === 'sw' ? 'Zilizorudishwa' : 'Returns'}</div>
                                <div className="pc-value">+{fmt(totalPurchaseReturnsAmt)}</div>
                              </div>
                            )}
                            {totalPurchaseReturnsAmt > 0 && (
                              <div className="pastel-card pastel-card--slate">
                                <div className="pc-label">{language === 'sw' ? 'Jumla Halisi' : 'Net Cost'}</div>
                                <div className="pc-value">{fmt(netPurchases)}</div>
                              </div>
                            )}
                          </div>
                        </div>
                        {/* ── TRANSACTION TABLE ── */}
                        {(() => {
                          const thS = { padding: '9px 10px', textAlign: 'left', fontWeight: 700, color: 'var(--text-primary)', fontSize: 11, borderBottom: '2px solid #D1D5DB', whiteSpace: 'nowrap', letterSpacing: '0.03em' };
                          const tdS = { padding: '9px 10px', color: 'var(--text-primary)', fontSize: 12, verticalAlign: 'middle' };
                          const editBtnS = { background: '#E0E7FF', color: '#3730A3', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 13, marginRight: 3 };
                          const delBtnS  = { background: '#FEE2E2', color: '#DC2626', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 13 };
                          const viewBtnS = { background: 'var(--bg-input)', color: 'var(--text-secondary)', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 13, marginRight: 3 };
                          const moreBtnS = { fontSize: 10, fontWeight: 700, background: '#E0E7FF', color: '#3730A3', border: 'none', borderRadius: 4, padding: '2px 5px', marginLeft: 4, cursor: 'pointer' };
                          const getPurchaseProductCell = (rec) => {
                            const items = rec.items;
                            if (items && items.length > 0) {
                              const first = items[0].productName || '—';
                              return items.length > 1 ? (
                                <span>
                                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block', maxWidth: 80, verticalAlign: 'middle' }}>{first}</span>
                                  <button style={moreBtnS} onClick={() => setDetailItemsModal({ open: true, items, title: rec.supplier || 'Items' })}>+{items.length - 1}</button>
                                </span>
                              ) : <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 110 }}>{first}</span>;
                            }
                            return <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 110 }}>{rec.productName || '—'}</span>;
                          };
                          return (
                            <div style={{ flex: 1, overflowY: 'auto', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                              {filteredPurchases.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--text-muted)', fontSize: 14 }}>{t.noDataForPeriod}</div>
                              ) : (
                                <>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 16px', fontSize: 12, color: 'var(--text-muted)', background: '#F0F9FF', borderBottom: '1px solid #E2E8F0' }}>
                                  <span>{language === 'sw' ? 'Bofya safu yoyote au kitufe cha View kuona maelezo kamili.' : 'Click any row or the View button to view full transaction details.'}</span>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                    <input type="checkbox" checked={hideVoidedPurchases} onChange={e => setHideVoidedPurchases(e.target.checked)} style={{ accentColor: '#92400E' }} />
                                    {language === 'sw' ? 'Ficha zilizobatilishwa' : 'Hide voided'}
                                  </label>
                                </div>
                                <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%', minWidth: 600, tableLayout: 'auto' }}>
                                  <thead>
                                    <tr style={{ background: 'var(--bg-tab)', position: 'sticky', top: 0, zIndex: 1 }}>
                                      <th style={thS}>{t.date}</th>
                                      <th style={thS}>{t.supplier || 'Supplier'}</th>
                                      <th style={thS}>{t.product || 'Product'}</th>
                                      <th style={{ ...thS, textAlign: 'right' }}>{t.qty || 'Qty'}</th>
                                      <th style={{ ...thS, textAlign: 'right' }}>{t.amount || 'Amount'}</th>
                                      <th style={thS}>{t.paymentMethod}</th>
                                      <th style={{ ...thS, textAlign: 'center', color: '#DC2626' }}>↩ {language === 'sw' ? 'Zilizorudishwa' : 'Returns'}</th>
                                      <th style={{ ...thS, textAlign: 'center' }}>{t.actions || 'Actions'}</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {filteredPurchases.map((purchase, i) => {
                                      const pRets = getPurchaseReturnsForId(purchase.id);
                                      const hasReturn = pRets.length > 0;
                                      const retQty = pRets.reduce((s, r) => s + parseFloat(r.qty || 0), 0);
                                      const retAmt = pRets.reduce((s, r) => s + parseFloat(r.refundAmt || 0), 0);
                                      const items = Array.isArray(purchase.items) && purchase.items.length > 0
                                        ? purchase.items
                                        : [{ productName: purchase.productName, quantity: purchase.quantity, unitPrice: purchase.unitPrice, totalCost: purchase.totalCost }];
                                      const isMulti = items.length > 1;
                                      const totalQty = items.reduce((s, it) => s + parseFloat(it.quantity || 0), 0);
                                      const firstItem = items[0];
                                      const productLabel = isMulti
                                        ? (<span><span style={{ verticalAlign: 'middle' }}>{firstItem.productName || '—'}</span><span style={{ fontSize: 10, fontWeight: 700, background: '#E0E7FF', color: '#3730A3', borderRadius: 4, padding: '2px 6px', marginLeft: 6, verticalAlign: 'middle' }}>+{items.length - 1}</span></span>)
                                        : (firstItem.productName || '—');
                                      return (
                                        <tr key={purchase.id} onClick={() => setViewTxnModal({ open: true, kind: 'purchase', record: purchase })} style={{ background: hasReturn ? (theme === 'dark' ? '#3A1D1D' : '#FFF5F5') : (i % 2 === 0 ? 'var(--bg-white)' : 'var(--bg-tab)'), borderBottom: '1px solid var(--border-color)', borderLeft: hasReturn ? '3px solid #EF4444' : purchase.status === 'void' ? '3px solid #9CA3AF' : '3px solid transparent', cursor: 'pointer', opacity: purchase.status === 'void' ? 0.5 : 1, textDecoration: purchase.status === 'void' ? 'line-through' : 'none' }}>
                                          <td style={{ ...tdS, whiteSpace: 'nowrap' }}>{purchase.date}{purchase.status === 'void' && <span style={{ marginLeft: 4, fontSize: 8, fontWeight: 800, padding: '1px 4px', borderRadius: 3, background: '#9CA3AF', color: '#fff', verticalAlign: 'middle', textDecoration: 'none', display: 'inline-block' }}>VOIDED</span>}</td>
                                          <td style={{ ...tdS, maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{purchase.supplier || '—'}</td>
                                          <td style={{ ...tdS, maxWidth: 160 }}>{productLabel}</td>
                                          <td style={{ ...tdS, textAlign: 'right' }}>{isMulti ? totalQty : (firstItem.quantity || '—')}</td>
                                          <td style={{ ...tdS, textAlign: 'right', fontWeight: 700, color: '#4F46E5', whiteSpace: 'nowrap' }}>{fmt(getPurchaseGrandTotal(purchase))}</td>
                                          <td style={{ ...tdS, whiteSpace: 'nowrap' }}>
                                            <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 6, textTransform: 'capitalize', background: purchase.isPaid === false ? '#FEF3C7' : '#E0E7FF', color: purchase.isPaid === false ? '#92400E' : '#3730A3' }}>{purchase.paymentMethod || 'cash'}</span>
                                          </td>
                                          <td style={{ ...tdS, textAlign: 'center' }}>{hasReturn ? (<div><div style={{ fontSize: 11, fontWeight: 700, color: '#DC2626' }}>↩ {retQty} {t.units || 'units'}</div><div style={{ fontSize: 10, color: '#DC2626' }}>+{fmt(retAmt)}</div></div>) : <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                                          <td style={{ ...tdS, textAlign: 'center', whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                                            <button onClick={() => setViewTxnModal({ open: true, kind: 'purchase', record: purchase })} style={viewBtnS} title={language === 'sw' ? 'Tazama' : 'View'}>View</button>
                                            <button onClick={() => handleEditPurchase(purchase)} style={editBtnS}>✎️</button>
                                            {purchase.status !== 'void' && <button onClick={() => handleVoidPurchase(purchase.id)} style={{ ...delBtnS, background: '#FEF3C7', color: '#92400E' }} title={language === 'sw' ? 'Batilisha' : 'Void'}>⊘</button>}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                                </>
                              )}
                            </div>
                          );
                        })()}

                      </div>
                    );
                  })()}

                  {/* === MAIN MANUNUZI DASHBOARD === */}
                  {!isPurchasesReportOpen && (
                    <div style={{ padding: 16, background: 'var(--bg-tab)', minHeight: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}>

                      {/* HEADER */}
                      <h1 style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 12px' }}>{t.purchases} ({language === 'sw' ? 'Bidhaa' : 'Products'})</h1>

                      {/* TOP ACTION BUTTONS -neon for Record, emerald for Invoices */}
                      <div style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
                        {perms.createPurchase && <button onClick={() => setShowPurchaseForm(true)}
                          style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, height: 42, borderRadius: 26, background: '#A3E635', color: '#18222F', border: 'none', fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'pointer', boxShadow: '0 4px 14px rgba(163,230,53,0.35)' }}>
                          + {t.recordPurchase}
                        </button>}
                        <button onClick={() => setIsPurchasesReportOpen(true)}
                          style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, height: 42, borderRadius: 26, background: '#ECFDF5', color: '#065F46', border: '1.5px solid #A7F3D0', fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'pointer', boxShadow: '0 1px 3px rgba(15,23,42,0.04)' }}>
                          {t.viewPurchasesReports}
                        </button>
                      </div>
                      <div style={{ marginBottom: 16 }}>
                        <button onClick={() => setShowPurchaseReturnModal(true)}
                          style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, height: 44, borderRadius: 22, background: '#ECFDF5', color: '#065F46', border: '1.5px solid #A7F3D0', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                          ↩ {t.purchaseReturns || 'Purchase Returns'}
                        </button>
                      </div>

                      {/* ══════════════════════════════════════════════════════════════
                           SECTION 1 -PURCHASES OVERVIEW
                      ══════════════════════════════════════════════════════════════ */}
                      <div style={{ background: 'var(--bg-white)', borderRadius: 16, overflow: 'hidden', marginBottom: 14, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', border: '0.5px solid var(--border-color)' }}>
                        <div style={{ padding: '12px 14px 4px' }}>
                          <h3 className="db-card-title" style={{ fontSize: 15, marginBottom: 0 }}>
                            {language === 'sw' ? 'Muhtasari wa Manunuzi' : 'Purchases Overview'}
                          </h3>
                        </div>
                        <div style={{ padding: 12 }}>

                      {/* PERIOD SELECTOR -pill container */}
                      <div style={{ background: 'var(--bg-tab)', borderRadius: 16, padding: 4, display: 'flex', gap: 2, marginBottom: 8 }}>
                        {[
                          { key: 'today',  label: t.today },
                          { key: 'week',   label: t.thisWeek },
                          { key: 'month',  label: t.thisMonth },
                          { key: 'year',   label: t.thisYear },
                          { key: 'custom', label: t.custom },
                        ].map(f => (
                          <button key={f.key} onClick={() => {
                            setPurchasesTimeFilter(f.key);
                            if (f.key === 'custom' && !purchasesCustomFrom && !purchasesCustomTo) {
                              const n = new Date(); const y = n.getFullYear(); const m = String(n.getMonth()+1).padStart(2,'0');
                              setPurchasesCustomFrom(`${y}-${m}-01`);
                              setPurchasesCustomTo(`${y}-${m}-${String(new Date(y, n.getMonth()+1, 0).getDate()).padStart(2,'0')}`);
                            }
                          }}
                            style={{ flex: 1, padding: '10px 6px', borderRadius: 16, fontSize: 12, fontWeight: purchasesTimeFilter === f.key ? 700 : 600, cursor: 'pointer', border: 'none', whiteSpace: 'nowrap',
                              background: purchasesTimeFilter === f.key ? 'var(--bg-tab-active)' : 'var(--bg-tab-inactive)',
                              color: purchasesTimeFilter === f.key ? 'var(--text-tab-active)' : 'var(--text-tab-inactive)',
                              boxShadow: 'none' }}>
                            {f.label}
                          </button>
                        ))}
                      </div>

                      {/* PERIOD DISPLAY LABEL OR CUSTOM DATE RANGE */}
                      {purchasesTimeFilter === 'custom' ? (
                        <div className="db-custom-date-card">
                          <div className="db-custom-date-row">
                            <div className="db-custom-date-col">
                              <span className="db-custom-date-label">{t.fromDate}</span>
                              <input type="date" className="db-custom-date-input" value={purchasesCustomFrom} onChange={e => setPurchasesCustomFrom(e.target.value)} />
                            </div>
                            <div className="db-custom-date-col">
                              <span className="db-custom-date-label">{t.toDate}</span>
                              <input type="date" className="db-custom-date-input" value={purchasesCustomTo} onChange={e => setPurchasesCustomTo(e.target.value)} />
                            </div>
                          </div>
                        </div>
                      ) : purchasesPeriodLabel ? (
                        <div className="period-date-label">{purchasesPeriodLabel}</div>
                      ) : <div style={{ marginBottom: 8 }} />}

                      {/* SUMMARY CARDS */}
                      {/* PURCHASES SUMMARY CARDS -triple layered */}
                      <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                        {/* Total Purchases card -triple layered */}
                        <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
                          <div style={{ position: 'absolute', inset: 0, transform: 'none', pointerEvents: 'none', borderRadius: 18, background: 'rgba(124,58,237,0.08)', zIndex: 0 }}></div>
                          <div style={{ position: 'absolute', inset: 0, transform: 'none', pointerEvents: 'none', borderRadius: 18, background: 'rgba(124,58,237,0.15)', zIndex: 1 }}></div>
                          <div style={{ position: 'relative', zIndex: 2, background: theme === 'dark' ? 'linear-gradient(135deg, #312E81 0%, #3730A3 100%)' : 'linear-gradient(135deg, #F3F0FF 0%, #E0E7FF 100%)', borderRadius: 18, padding: '16px 14px', border: theme === 'dark' ? '1px solid #6366F1' : '1px solid #C7D2FE' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                              <div style={{ width: 32, height: 32, borderRadius: 10, background: theme === 'dark' ? 'rgba(139,92,246,0.2)' : 'rgba(124,58,237,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={theme === 'dark' ? '#818CF8' : '#4F46E5'} strokeWidth="2.5" strokeLinecap="round"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                              </div>
                              <div style={{ fontSize: 11, fontWeight: 700, color: theme === 'dark' ? '#FFFFFF' : '#3730A3', textTransform: 'uppercase', letterSpacing: 0.5 }}>{t.totalPurchases}</div>
                            </div>
                            <div style={{ fontSize: 'clamp(15px, 5.5vw, 22px)', fontWeight: 900, color: theme === 'dark' ? '#FFFFFF' : '#4F46E5', marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{fmt(totalCostAmt)}</div>
                            <div style={{ fontSize: 10, fontWeight: 600, color: theme === 'dark' ? '#FFFFFF' : '#6D28D9', opacity: 0.8 }}>{purchasesPeriodLabel}</div>
                          </div>
                        </div>
                        {/* Transaction Count card -triple layered */}
                        <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
                          <div style={{ position: 'absolute', inset: 0, transform: 'none', pointerEvents: 'none', borderRadius: 18, background: 'rgba(124,58,237,0.08)', zIndex: 0 }}></div>
                          <div style={{ position: 'absolute', inset: 0, transform: 'none', pointerEvents: 'none', borderRadius: 18, background: 'rgba(124,58,237,0.15)', zIndex: 1 }}></div>
                          <div style={{ position: 'relative', zIndex: 2, background: theme === 'dark' ? 'linear-gradient(135deg, #312E81 0%, #3730A3 100%)' : 'linear-gradient(135deg, #F3F0FF 0%, #E0E7FF 100%)', borderRadius: 18, padding: '16px 14px', border: theme === 'dark' ? '1px solid #6366F1' : '1px solid #C7D2FE' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                              <div style={{ width: 32, height: 32, borderRadius: 10, background: theme === 'dark' ? 'rgba(139,92,246,0.2)' : 'rgba(124,58,237,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={theme === 'dark' ? '#818CF8' : '#4F46E5'} strokeWidth="2.5" strokeLinecap="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                              </div>
                              <div style={{ fontSize: 11, fontWeight: 700, color: theme === 'dark' ? '#FFFFFF' : '#3730A3', textTransform: 'uppercase', letterSpacing: 0.5 }}>{t.purchasesCount}</div>
                            </div>
                            <div style={{ fontSize: 'clamp(15px, 5.5vw, 22px)', fontWeight: 900, color: theme === 'dark' ? '#FFFFFF' : '#4F46E5', marginBottom: 2, whiteSpace: 'nowrap' }}>{ordersCount}</div>
                            <div style={{ fontSize: 10, fontWeight: 600, color: theme === 'dark' ? '#FFFFFF' : '#6D28D9', opacity: 0.8 }}>{purchasesPeriodLabel}</div>
                          </div>
                        </div>
                      </div>

                      {/* PURCHASES BY PRODUCT -TOP 5 BAR CHART */}
                      <div style={{ background: 'var(--bg-tab)', borderRadius: 14, padding: 14, marginBottom: 16 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 14 }}>{t.purchasesByProduct}</div>
                        {mnByProduct.length === 0 ? (
                          <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 13, padding: '16px 0' }}>{t.noDataForPeriod}</div>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {mnByProduct.slice(0, 5).map((item) => (
                              <div key={item.name} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <span style={{ width: 80, fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 0 }}>{item.name}</span>
                                <div style={{ flex: 1, height: 22, background: 'var(--bg-white)', borderRadius: 6, overflow: 'hidden' }}>
                                  <div style={{ height: '100%', width: `${maxBarCost > 0 ? (item.cost / maxBarCost * 100) : 0}%`, background: getProductColor(item.name), borderRadius: 6, transition: 'width 0.6s ease' }} />
                                </div>
                                <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', minWidth: 36, textAlign: 'right' }}>{fmt(item.cost)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* RECENT PURCHASES -last 5 */}
                      <div style={{ background: 'var(--bg-white)', borderRadius: 16, padding: 16, marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, gap: 8, flexWrap: 'wrap' }}>
                          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{t.recentPurchases}</div>
                          {/* DISPLAY-ONLY toggle. Never affects any total above. */}
                          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', cursor: 'pointer' }}>
                            <input type="checkbox" checked={hideVoidedPurchases} onChange={e => setHideVoidedPurchases(e.target.checked)} style={{ accentColor: '#92400E' }} />
                            {language === 'sw' ? 'Ficha zilizobatilishwa' : 'Hide voided'}
                          </label>
                        </div>
                        {mnRecent.length === 0 ? (
                          <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 13, padding: '24px 0' }}>{t.noDataForPeriod}</div>
                        ) : mnRecent.map((p, i) => {
                          const pRets = (purchaseReturns || []).filter(r => r.status !== 'void' && String(r.originalPurchaseId) === String(p.id));
                          const retQtyTotal = pRets.reduce((s, r) => s + (parseFloat(r.qty) || 0), 0);
                          const retAmtTotal = pRets.reduce((s, r) => s + (parseFloat(r.refundAmt) || 0), 0);
                          const isVoid = p.status === 'void';
                          return (
                            <div key={p.id} style={{ paddingBottom: i < mnRecent.length - 1 ? 12 : 0, marginBottom: i < mnRecent.length - 1 ? 12 : 0, borderBottom: i < mnRecent.length - 1 ? '1px solid #F3F4F6' : 'none', opacity: isVoid ? 0.5 : 1, borderLeft: isVoid ? '3px solid #9CA3AF' : 'none', paddingLeft: isVoid ? 8 : 0 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 3, textDecoration: isVoid ? 'line-through' : 'none' }}>
                                    {Array.isArray(p.items) && p.items.length > 0
                                      ? p.items.map(it => it.productName || '—').join(', ')
                                      : (p.productName || '—')}
                                  </div>
                                  <div style={{ fontSize: 11, color: 'var(--text-muted)', textDecoration: isVoid ? 'line-through' : 'none' }}>{p.supplier || '—'} &middot; {t.qty}: {Array.isArray(p.items) && p.items.length > 0 ? p.items.reduce((s, it) => s + parseFloat(it.quantity || 0), 0) : (p.quantity || 0)} &middot; {p.date}</div>
                                  {isVoid && (
                                    <div style={{ marginTop: 4, display: 'inline-block', fontSize: 9, fontWeight: 800, padding: '2px 6px', borderRadius: 4, background: '#9CA3AF', color: '#fff', letterSpacing: 0.3 }}>
                                      {language === 'sw' ? 'IMEBATILISHWA' : 'VOIDED'}
                                    </div>
                                  )}
                                  {isVoid && p._voidUnsynced && (
                                    <div style={{ marginTop: 4, fontSize: 9, fontWeight: 700, color: '#B45309' }}>
                                      {language === 'sw' ? 'HAIJASAWAZISHWA NA SEVA' : 'NOT SYNCED TO SERVER'}
                                    </div>
                                  )}
                                </div>
                                <div style={{ marginLeft: 12, textAlign: 'right', flexShrink: 0 }}>
                                  <div style={{ fontSize: 14, fontWeight: 700, color: p.isPaid === false ? '#EF4444' : 'var(--text-primary)' }}>{fmt(getPurchaseGrandTotal(p))}</div>
                                  {retAmtTotal > 0 && <div style={{ fontSize: 11, fontWeight: 600, color: '#DC2626', marginTop: 1 }}>-{fmt(retAmtTotal)}</div>}
                                  {p.isPaid === false && <div style={{ fontSize: 10, color: '#EF4444', fontWeight: 600, marginTop: 2 }}>{t.unpaid}</div>}
                                </div>
                              </div>
                              {retQtyTotal > 0 && (
                                <div style={{ marginTop: 5, display: 'inline-flex', alignItems: 'center', gap: 4, background: '#FEE2E2', borderRadius: 6, padding: '3px 8px' }}>
                                  <span style={{ fontSize: 11 }}>↩</span>
                                  <span style={{ fontSize: 11, fontWeight: 700, color: '#DC2626' }}>{retQtyTotal} {t.units || 'units'} {language === 'sw' ? 'zilirudishwa' : 'returned'}</span>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>

                        </div>
                      </div>{/* END SECTION 1 */}

                      {/* ══════════════════════════════════════════════════════════════
                           SECTION 2 -PERFORMANCE & ANALYSIS
                      ══════════════════════════════════════════════════════════════ */}
                      <div style={{ background: 'var(--bg-white)', borderRadius: 16, overflow: 'hidden', marginBottom: 14, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', border: '0.5px solid var(--border-color)' }}>
                        <div style={{ padding: 12 }}>

                      {/* ANNUAL PERFORMANCE */}
                      <div style={{ background: 'var(--bg-white)', borderRadius: 16, overflow: 'hidden', marginBottom: 16, border: '0.5px solid var(--border-color)' }}>
                        {/* Section heading */}
                        <div style={{ padding: '12px 14px 4px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <h3 className="db-card-title" style={{ fontSize: 15, marginBottom: 0 }}>{t.annualPerformance}</h3>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3 }}>
                            <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8 }}>{t.selectYear}</div>
                            <select value={purchasesAnnualYear} onChange={e => setPurchasesAnnualYear(parseInt(e.target.value))}
                              style={{ padding: '4px 10px', borderRadius: 8, border: '2px solid var(--border-color)', background: 'var(--bg-input)', fontSize: 13, fontWeight: 900, color: 'var(--text-primary)', cursor: 'pointer', outline: 'none', minWidth: 80, textAlign: 'center' }}>
                              {mnAvailableYears.map(y => <option key={y} value={y} style={{ background: 'var(--bg-card)', color: 'var(--text-primary)' }}>{y}</option>)}
                            </select>
                          </div>
                        </div>
                        <div style={{ padding: 12 }}>

                        {/* Annual total */}
                        <div style={{ background: theme === 'dark' ? 'rgba(110,231,183,0.12)' : 'rgba(110,231,183,0.1)', borderRadius: 12, padding: '10px 14px', marginBottom: 14, borderLeft: '4px solid #6EE7B7', borderTop: '1px solid rgba(110,231,183,0.3)', borderRight: '1px solid rgba(110,231,183,0.3)', borderBottom: '1px solid rgba(110,231,183,0.3)' }}>
                          <div style={{ fontSize: 11, color: theme === 'dark' ? '#6EE7B7' : '#059669', fontWeight: 700, marginBottom: 2 }}>{t.totalPurchases} · {purchasesAnnualYear}</div>
                          <div style={{ fontSize: 22, fontWeight: 800, color: theme === 'dark' ? '#34D399' : '#064E3B' }}>{fmt(mnAnnualTotal)}</div>
                        </div>

                        {/* PURCHASE TREND AREA CHART */}
                        <div style={{ background: 'var(--bg-tab)', borderRadius: 14, padding: 14 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 10 }}>{t.purchaseTrend}</div>
                        {(() => {
                          const W = 320, H = 180, PX = 8, PY = 28;
                          const pts = mnAnnualData.map((d, i) => ({
                            x: PX + i * (W - PX * 2) / 11,
                            y: H - PY - (mnMaxAnnualCost > 0 ? (d.cost / mnMaxAnnualCost) * (H - PY * 2) : 0)
                          }));
                          const bez = (p) => { let d = `M${p[0].x.toFixed(1)},${p[0].y.toFixed(1)}`; for (let i = 0; i < p.length-1; i++) { const a=p[Math.max(i-1,0)],b=p[i],c=p[i+1],e=p[Math.min(i+2,p.length-1)]; d+=` C${(b.x+(c.x-a.x)/4).toFixed(1)},${(b.y+(c.y-a.y)/4).toFixed(1)} ${(c.x-(e.x-b.x)/4).toFixed(1)},${(c.y-(e.y-b.y)/4).toFixed(1)} ${c.x.toFixed(1)},${c.y.toFixed(1)}`; } return d; };
                          const linePath = bez(pts);
                          const areaPath = `${linePath} L${pts[pts.length-1].x.toFixed(1)},${(H-PY).toFixed(1)} L${pts[0].x.toFixed(1)},${(H-PY).toFixed(1)} Z`;
                          const hi = purchasesChartHover;
                          return (
                            <div style={{ overflow: 'visible' }}>
                              <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 190, display: 'block', overflow: 'visible' }} preserveAspectRatio="none" onMouseLeave={() => setPurchasesChartHover(null)}>
                                <defs>
                                  <linearGradient id="mnAreaGrad" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#6EE7B7" stopOpacity="0.55" />
                                    <stop offset="100%" stopColor="#6EE7B7" stopOpacity="0.04" />
                                  </linearGradient>
                                </defs>
                                {[0.33,0.66].map((f,i) => <line key={i} x1={PX} y1={(PY+f*(H-PY*2)).toFixed(1)} x2={W-PX} y2={(PY+f*(H-PY*2)).toFixed(1)} stroke="var(--border-color)" strokeWidth="1" />)}
                                <path d={areaPath} fill="url(#mnAreaGrad)" />
                                <path d={linePath} fill="none" stroke="#10B981" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
                                {pts.map((p, i) => { const x0 = i === 0 ? 0 : (pts[i-1].x+p.x)/2; const x1 = i === pts.length-1 ? W : (p.x+pts[i+1].x)/2; return <rect key={i} x={x0.toFixed(1)} y={0} width={(x1-x0).toFixed(1)} height={H} fill="transparent" style={{ cursor: 'crosshair' }} onMouseEnter={() => setPurchasesChartHover(i)} />; })}
                                {hi !== null && (() => {
                                  const aboveFits = pts[hi].y - 26 >= PY;
                                  const tipRectY = aboveFits ? pts[hi].y - 26 : pts[hi].y + 8;
                                  const tipRectX = Math.max(2, Math.min(pts[hi].x - 38, W - 78));
                                  return (
                                    <>
                                      <line x1={pts[hi].x.toFixed(1)} y1={PY} x2={pts[hi].x.toFixed(1)} y2={H-PY} stroke="#9CA3AF" strokeWidth="1" strokeDasharray="3,3" />
                                      <circle cx={pts[hi].x.toFixed(1)} cy={pts[hi].y.toFixed(1)} r="4.5" fill="#10B981" stroke="#fff" strokeWidth="2" />
                                      <rect x={tipRectX.toFixed(1)} y={tipRectY.toFixed(1)} width="64" height="14" rx="4" fill="#064E3B" fillOpacity="0.9" />
                                      <text x={(parseFloat(tipRectX) + 32).toFixed(1)} y={(parseFloat(tipRectY) + 10).toFixed(1)} textAnchor="middle" fill="#fff" fontSize="7" fontWeight="400">{fmt(mnAnnualData[hi].cost)}</text>
                                    </>
                                  );
                                })()}
                              </svg>
                              <div style={{ display: 'flex', marginTop: 4 }}>
                                {mnAnnualData.map((d, i) => (
                                  <div key={i} style={{ flex: 1, textAlign: 'center' }}>
                                    <span style={{ display: 'inline-block', background: hi === i ? 'var(--bg-white)' : 'var(--bg-tab)', borderRadius: 5, padding: '2px 3px', fontSize: 8, color: 'var(--text-primary)', fontWeight: 700 }}>{d.label}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })()}
                        </div>{/* end chart sub-card */}
                        </div>{/* end content */}
                      </div>

                      {/* TOP PURCHASED PRODUCTS */}
                      <div style={{ background: 'var(--bg-tab)', borderRadius: 16, padding: 16, marginBottom: 16 }}>
                        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 14 }}>{t.topPurchasedProducts}</div>
                        {mnTopPurchased.length === 0 ? (
                          <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 13, padding: '24px 0' }}>{t.noDataForPeriod}</div>
                        ) : mnTopPurchased.map((item, i) => (
                          <div key={item.name} style={{ display: 'flex', alignItems: 'center', gap: 10, paddingBottom: i < mnTopPurchased.length - 1 ? 12 : 0, marginBottom: i < mnTopPurchased.length - 1 ? 12 : 0, borderBottom: i < mnTopPurchased.length - 1 ? '1px solid #F3F4F6' : 'none' }}>
                            <div style={{ width: 28, height: 28, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0,
                              background: i === 0 ? '#FCD34D' : i === 1 ? 'var(--bg-input)' : i === 2 ? '#FED7AA' : 'var(--bg-tab)', color: i === 0 || i === 2 ? '#7C2D12' : 'var(--text-primary)' }}>{i + 1}</div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</div>
                              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{item.qty} {t.units}</div>
                            </div>
                            <div style={{ textAlign: 'right', flexShrink: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: 700, color: '#4F46E5' }}>{fmt(item.cost)}</div>
                              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{item.pct}%</div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* LEAST PURCHASED PRODUCTS */}
                      <div style={{ background: 'var(--bg-tab)', borderRadius: 16, padding: 16, marginBottom: 16 }}>
                        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 14 }}>↘ {t.lowPurchasedProducts}</div>
                        {mnLowPurchased.length === 0 ? (
                          <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 13, padding: '24px 0' }}>{t.noDataForPeriod}</div>
                        ) : mnLowPurchased.map((item, i) => (
                          <div key={item.name} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: i < mnLowPurchased.length - 1 ? 12 : 0, marginBottom: i < mnLowPurchased.length - 1 ? 12 : 0, borderBottom: i < mnLowPurchased.length - 1 ? '1px solid #F3F4F6' : 'none' }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</div>
                              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{item.qty} {t.units}</div>
                            </div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: '#EF4444', marginLeft: 12, flexShrink: 0 }}>{fmt(item.cost)}</div>
                          </div>
                        ))}
                      </div>

                      {/* ANNUAL PRODUCT-WISE BREAKDOWN -after Bidhaa Zilizounuliwa Kidogo */}
                      {(() => {
                        // Purchases tab single-color palette -indigo / slate family (operations, not danger)
                        const ANNUAL_COLORS = Array.from({length: 200}, (_, i) => {
                          const shades = ['#6366F1', '#4F46E5', '#818CF8', '#4338CA', '#A5B4FC'];
                          return shades[i % shades.length];
                        });
                        const annualPurchasesMap = (purchases || [])
                          .filter(p => { const d = new Date(p.date || 0); return !isNaN(d.getTime()) && d.getFullYear() === purchasesAnnualYear && p.status !== 'void'; })
                          .reduce((acc, p) => {
                            const name = p.productName || '—';
                            if (!acc[name]) acc[name] = { cost: 0, qty: 0 };
                            acc[name].cost += parseFloat(p.totalCost || 0);
                            acc[name].qty  += parseInt(p.quantity || 0);
                            return acc;
                          }, {});
                        const annualByProduct = Object.entries(annualPurchasesMap)
                          .map(([name, val]) => ({ name, cost: val.cost, qty: val.qty }))
                          .filter(item => item.cost > 0)
                          .sort((a, b) => b.cost - a.cost);
                        if (annualByProduct.length === 0) return null;
                        const maxCost = annualByProduct[0].cost || 1;
                        return (
                          <div style={{ background: 'var(--bg-tab)', borderRadius: 16, padding: 16, marginBottom: 16 }}>
                            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>
                              {language === 'sw' ? `Manunuzi kwa Bidhaa · ${purchasesAnnualYear}` : `Purchases by Product · ${purchasesAnnualYear}`}
                            </div>
                            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 14 }}>
                              {language === 'sw' ? 'Bidhaa zilizounuliwa' : 'Purchased products'} · {purchasesAnnualYear}
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                              {annualByProduct.map((item, i) => {
                                const color = ANNUAL_COLORS[i % ANNUAL_COLORS.length];
                                const barPct = maxCost > 0 ? (item.cost / maxCost * 100) : 0;
                                return (
                                  <div key={item.name} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <span style={{ width: 80, fontSize: 11, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 0 }}>{item.name}</span>
                                    <div style={{ flex: 1, height: 20, background: 'var(--bg-white)', borderRadius: 5, overflow: 'hidden' }}>
                                      <div style={{ height: '100%', width: `${barPct}%`, background: color, borderRadius: 5, transition: 'width 0.6s ease', minWidth: item.cost > 0 ? 4 : 0 }} />
                                    </div>
                                    <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', minWidth: 40, textAlign: 'right' }}>
                                      {fmt(item.cost)}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })()}

                        </div>
                      </div>{/* END SECTION 2 */}

                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}

        {activeTab === 'expenses' && (
          <div className="expenses ex-screen">
            {!isDataReady ? (
              <div role="status" aria-label={t.loading || 'Loading'} style={{ padding: 16 }}>
                <div className="skeleton skeleton-title"></div>
                <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
                  {[1,2,3].map(i => <div key={i} className="skeleton skeleton-btn" style={{ flex: 1, width: 'auto' }}></div>)}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
                  <div className="skeleton skeleton-summary-card"></div>
                  <div className="skeleton skeleton-summary-card"></div>
                </div>
                <div className="skeleton skeleton-chart"></div>
                <div className="skeleton skeleton-table-header"></div>
                {[1,2,3,4,5].map(i => <div key={i} className="skeleton skeleton-row"></div>)}
              </div>
            ) : null}
            {isDataReady && (() => {
              // Expenses tab -amber family only (costs, not warnings)
              const SUNSET = ['#F59E0B','#D97706','#FBBF24','#B45309','#FCD34D','#92400E','#FDE68A','#78350F','#EAB308','#451A03'];
              const allExpCatsSorted = [...new Set((expenses || []).map(e => e.category).filter(Boolean))].sort();
              const catColorMap = {};
              allExpCatsSorted.forEach((cat, i) => { catColorMap[cat] = SUNSET[i % SUNSET.length]; });
              const getCatColor = (cat) => catColorMap[cat] || SUNSET[0];

              // H5: shared period helper (with custom start/end).
              const filteredExpenses = filterByPeriod(expenses, expensesTimeFilter, expensesCustomFrom, expensesCustomTo).filter(e => e.status !== 'void');

              const totalExpAmt = filteredExpenses.reduce((s, x) => s + parseFloat(x.amount || 0), 0);
              const expCount = filteredExpenses.length;

              // Category breakdown
              const byCatMap = {};
              filteredExpenses.forEach(e => {
                const cat = e.category || 'Other';
                if (!byCatMap[cat]) byCatMap[cat] = { name: cat, amount: 0 };
                byCatMap[cat].amount += parseFloat(e.amount || 0);
              });
              const exByCategory = Object.values(byCatMap).sort((a, b) => b.amount - a.amount).map((item) => ({
                ...item, color: getCatColor(item.name),
                pct: totalExpAmt > 0 ? Math.round(item.amount / totalExpAmt * 100) : 0
              }));
              const exMaxCat = exByCategory.length > 0 ? exByCategory[0].amount : 1;

              // HISTORY list -voided expenses stay visible (marked VOIDED) unless
              // the user ticks "Hide voided". Display only: every total on this
              // tab reads filteredExpenses, which always excludes void.
              const exRecent = [...expenses]
                .filter(e => !hideVoidedExpenses || e.status !== 'void')
                .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0)).slice(0, 5);

              // Period display label
              const _enow = new Date();
              const _epad = n => String(n).padStart(2, '0');
              const _efmt = d => `${_epad(d.getDate())}/${_epad(d.getMonth()+1)}/${d.getFullYear()}`;
              let expensesPeriodLabel = '';
              if (expensesTimeFilter === 'today') {
                expensesPeriodLabel = _efmt(_enow);
              } else if (expensesTimeFilter === 'week') {
                const ws = new Date(_enow); ws.setDate(_enow.getDate() - 7);
                expensesPeriodLabel = `${_efmt(ws)} \u2013 ${_efmt(_enow)}`;
              } else if (expensesTimeFilter === 'month') {
                const _exMn = (t.monthNames || ['January','February','March','April','May','June','July','August','September','October','November','December'])[_enow.getMonth()];
                expensesPeriodLabel = `${_exMn} ${_enow.getFullYear()}`;
              } else if (expensesTimeFilter === 'year') {
                expensesPeriodLabel = String(_enow.getFullYear());
              } else if (expensesTimeFilter === 'custom' && expensesCustomFrom && expensesCustomTo) {
                expensesPeriodLabel = `${expensesCustomFrom} \u2013 ${expensesCustomTo}`;
              }

              // Annual performance (monthly breakdown)
              const EX_MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
              const exAnnualData = EX_MONTHS.map((label, m) => ({
                label,
                amount: (expenses || []).filter(e => { const d = new Date(e.date || 0); return !isNaN(d.getTime()) && d.getFullYear() === expensesAnnualYear && d.getMonth() === m && e.status !== 'void'; })
                                .reduce((sum, x) => sum + parseFloat(x.amount || 0), 0)
              }));
              const exAnnualTotal = exAnnualData.reduce((s, d) => s + d.amount, 0);
              const exMaxAnnual = Math.max(...exAnnualData.map(d => d.amount), 1);

              // Year selector options
              const exRawYears = [...new Set(expenses.map(e => new Date(e.date).getFullYear()).filter(y => !isNaN(y)))].sort((a, b) => b - a);
              const _exNow = new Date().getFullYear();
              const _exFuture = Array.from({ length: 5 }, (_, i) => _exNow + i + 1);
              const exAvailableYears = [...new Set([..._exFuture, expensesAnnualYear, ...exRawYears])].sort((a, b) => b - a);

              return (
                <>
                  {/* ADD EXPENSE MODAL */}
                  {showExpenseForm && (
                    <div className="popup-overlay">
                      <div className="popup-form mz-modal" style={{ maxWidth: 420, width: '100%', borderRadius: 16, padding: 24 }}>
                        <h3 className="mz-modal-title" style={{ marginBottom: 14, fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>{t.addExpense}</h3>

                        {expenseCategories.length === 0 ? (
                          <div style={{ padding: '16px 14px', borderRadius: 12, background: '#FEF3C7', color: '#92400E', fontSize: 14, fontWeight: 600, marginBottom: 14, textAlign: 'center' }}>
                            {t.pleaseAddExpenseCategories}
                          </div>
                        ) : (<>

                          {/* Category */}
                          <div style={{ marginBottom: 16 }}>
                            <label style={{ display: 'block', marginBottom: 6, color: 'var(--text-primary)', fontWeight: 600, fontSize: 14 }}>{t.selectCategory}</label>
                            <select name="category" value={expenseFormData.category} onChange={handleExpenseInputChange}
                              style={{ width: '100%', padding: 14, borderRadius: 12, border: 'none', background: 'var(--bg-tab)', fontSize: 15, boxSizing: 'border-box', outline: 'none' }}>
                              <option value="">{t.selectCategory}</option>
                              {expenseCategories.map(c => (
                                <option key={c.id} value={c.name}>{c.name}</option>
                              ))}
                            </select>
                          </div>

                          {/* Amount */}
                          <div style={{ marginBottom: 16 }}>
                            <label style={{ display: 'block', marginBottom: 6, color: 'var(--text-primary)', fontWeight: 600, fontSize: 14 }}>{t.amount}</label>
                            <input type="number" min="0" inputMode="numeric" name="amount" value={expenseFormData.amount} onChange={handleExpenseInputChange} placeholder="0" autoComplete="off"
                              style={{ width: '100%', padding: 14, borderRadius: 12, border: 'none', background: 'var(--bg-tab)', fontSize: 15, boxSizing: 'border-box', outline: 'none' }} />
                          </div>

                          {/* Description (optional) */}
                          <div style={{ marginBottom: 16 }}>
                            <label style={{ display: 'block', marginBottom: 6, color: 'var(--text-primary)', fontWeight: 600, fontSize: 14 }}>{t.notesOptional}</label>
                            <input type="text" name="description" value={expenseFormData.description} onChange={handleExpenseInputChange} placeholder={t.notesOptional}
                              style={{ width: '100%', padding: 14, borderRadius: 12, border: 'none', background: 'var(--bg-tab)', fontSize: 15, boxSizing: 'border-box', outline: 'none' }} />
                          </div>

                          {/* Date */}
                          <div style={{ marginBottom: 16 }}>
                            <label style={{ display: 'block', marginBottom: 6, color: 'var(--text-primary)', fontWeight: 600, fontSize: 14 }}>{t.date}</label>
                            <input type="date" name="date" value={expenseFormData.date} onChange={handleExpenseInputChange}
                              style={{ width: '100%', padding: 14, borderRadius: 12, border: 'none', background: 'var(--bg-tab)', fontSize: 15, boxSizing: 'border-box', outline: 'none' }} />
                          </div>

                          {/* Total Display */}
                          {parseFloat(expenseFormData.amount) > 0 && (
                            <div style={{ marginBottom: 16, padding: '10px 14px', borderRadius: 14, background: 'var(--bg-tab)', border: '1.5px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-primary)' }}>{language === 'sw' ? 'Jumla' : 'Total'}</span>
                              <span style={{ fontWeight: 800, fontSize: 18, color: '#DC2626' }}>{fmt(parseFloat(expenseFormData.amount))}</span>
                            </div>
                          )}

                          {/* Split Payment rows */}
                          {(() => {
                            const expTotal = parseFloat(expenseFormData.amount || 0);
                            const amountPaid = (expensePayments || []).reduce((s, p) => s + parseFloat(p.amount || 0), 0);
                            const remaining = Math.max(0, expTotal - amountPaid);
                            const payOpts = buildPaymentOptions(true);
                            return (
                              <div style={{ marginBottom: 24 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                  <div>
                                    <label style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: 14 }}>Amount Paid</label>
                                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>Method on left · Amount on right</div>
                                  </div>
                                  {(expensePayments || []).length < (payOpts || []).length && expTotal > 0 && (
                                    <button type="button" onClick={addExpensePayment}
                                      style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', background: 'var(--bg-input)', border: 'none', borderRadius: 8, padding: '4px 10px', cursor: 'pointer' }}>
                                      + {t.add || 'Add'}
                                    </button>
                                  )}
                                </div>
                                {(expensePayments || []).map((pay, idx) => {
                                  const otherRowsTotal = (expensePayments || []).reduce((s, p, i) => i !== idx ? s + parseFloat(p.amount || 0) : s, 0);
                                  const maxForRow = Math.max(0, expTotal - otherRowsTotal);
                                  // Balance of the SPECIFIC selected account, not the whole category.
                                  const walletBal = getWalletBalance(pay.accountKey || pay.type);
                                  const effectiveMax = pay.type === 'credit' ? maxForRow : Math.min(maxForRow, Math.max(0, walletBal));
                                  const enteredAmt = parseFloat(pay.amount || 0);
                                  // Uniqueness rule: exclude specific accounts already chosen by other rows
                                  const currentKey = pay.accountKey || pay.type;
                                  const usedByOthers = new Set((expensePayments || []).map((p, i) => i !== idx ? (p.accountKey || p.type) : null).filter(Boolean));
                                  const availableOpts = (payOpts || []).filter(o => !usedByOthers.has(o.value) || o.value === currentKey);
                                  return (
                                    <div key={idx} style={{ marginBottom: 10 }}>
                                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                        <select value={currentKey} onChange={e => handleExpensePaymentTypeChange(idx, e.target.value)}
                                          style={{ flex: 1, padding: '12px 10px', borderRadius: 12, border: 'none', background: 'var(--bg-tab)', fontSize: 14, outline: 'none', minWidth: 0 }}>
                                          {availableOpts.map(o => <option key={o.value + o.label} value={o.value}>{o.label}</option>)}
                                        </select>
                                        <input type="number" min="0" inputMode="numeric" placeholder="0"
                                          value={pay.amount}
                                          onChange={e => handleExpensePaymentAmountChange(idx, e.target.value)}
                                          style={{ width: 100, padding: '12px 10px', borderRadius: 12, border: 'none', background: 'var(--bg-tab)', fontSize: 14, outline: 'none', textAlign: 'right', flexShrink: 0, boxSizing: 'border-box' }} />
                                        {(expensePayments || []).length > 1 && (
                                          <button type="button" onClick={() => removeExpensePayment(idx)}
                                            style={{ width: 36, height: 46, borderRadius: 10, border: 'none', background: '#FEE2E2', color: '#DC2626', cursor: 'pointer', fontSize: 20, fontWeight: 700, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                                        )}
                                      </div>
                                      {pay.type !== 'credit' && (
                                        <div style={{ fontSize: 12, fontWeight: 600, marginTop: 3, paddingLeft: 4, color: enteredAmt > walletBal ? '#DC2626' : '#059669' }}>
                                          {language === 'sw' ? 'Salio' : 'Balance'}: {fmt(walletBal)}{enteredAmt > walletBal ? ` (${language === 'sw' ? 'Haitoshi' : 'Insufficient'})` : ''}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                                {expTotal > 0 && (
                                  <div style={{ borderRadius: 10, padding: '8px 12px', marginTop: 4,
                                    background: remaining > 0.01 ? '#FEF3C7' : '#D1FAE5',
                                    color: remaining > 0.01 ? '#92400E' : '#065F46',
                                    fontSize: 13, fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span>{remaining > 0.01 ? `${t.unpaid}` : `${t.paid}`}</span>
                                    {remaining > 0.01 && <span>{fmt(remaining)} remaining</span>}
                                  </div>
                                )}
                              </div>
                            );
                          })()}

                        </>)}

                        <div className="form-buttons">
                          <button className="save-btn" onClick={handleSaveExpense} disabled={expenseCategories.length === 0}>{t.save}</button>
                          <button className="cancel-btn" onClick={() => { setShowExpenseForm(false); setExpensePayments([{ type: 'cash', amount: '' }]); setExpenseFormData({ category: '', amount: '', description: '', date: new Date().toISOString().split('T')[0], paymentMethod: 'cash' }); }}>{t.cancel}</button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* EXPENSE REPORT SCREEN */}
                  {isExpensesReportOpen && (() => {
                    const filteredExpenses = getFilteredExpenses().filter(e => !hideVoidedExpenses || e.status !== 'void');
                    // Accounting total ALWAYS excludes void (display is toggle-controlled).
                    const acctFilteredExpenses = filteredExpenses.filter(e => e.status !== 'void');
                    const expensesTotal = acctFilteredExpenses.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);
                    const erH = ['Date','Category','Amount','Description'];
                    const erR = () => filteredExpenses.map(e => [e.date, e.category, parseFloat(e.amount||0).toFixed(2), e.description||'']);
                    const fromD = expenseStartDate ? new Date(expenseStartDate).toLocaleDateString() : '—';
                    const toD = expenseEndDate ? new Date(expenseEndDate).toLocaleDateString() : '—';
                    return (
                      <div style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'var(--bg-tab)', display: 'flex', flexDirection: 'column' }}>
                        {/* ── HEADER ── */}
                        <div style={{ background: 'var(--bg-white)', borderBottom: '1px solid var(--border-color)', padding: '14px 16px 10px', flexShrink: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
                            <button onClick={() => { setIsExpensesReportOpen(false); if (drilldownFromTab) { setActiveTab(drilldownFromTab); setDrilldownFromTab(null); } }} style={{ background: 'none', border: 'none', padding: '4px 10px 4px 0', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#111827" strokeWidth="2.5" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
                            </button>
                            <h2 style={{ flex: 1, fontSize: 18, fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>{t.expenseReports}</h2>
                          </div>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button onClick={() => downloadPDF(erH, erR(), 'Expenses Report', 'expenses-report.pdf')} style={{ flex: 1, padding: '9px 4px', borderRadius: 10, background: '#EF4444', color: '#fff', border: 'none', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>{t.downloadPDF}</button>
                            <button onClick={() => downloadCSV([erH, ...erR()], 'expenses-report.csv')} style={{ flex: 1, padding: '9px 4px', borderRadius: 10, background: '#059669', color: '#fff', border: 'none', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>{t.downloadCSV}</button>
                            <button onClick={() => downloadExcel([erH, ...erR()], 'expenses-report.xlsx')} style={{ flex: 1, padding: '9px 4px', borderRadius: 10, background: '#2563EB', color: '#fff', border: 'none', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>{t.downloadExcel}</button>
                          </div>
                        </div>
                        {/* ── PERIOD SELECTOR ── */}
                        <div style={{ background: 'var(--bg-white)', padding: '12px 16px 8px', flexShrink: 0 }}>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {[{key:'today',label:t.today},{key:'week',label:t.week},{key:'month',label:t.month},{key:'year',label:t.year || t.thisYear || 'Year'},{key:'custom',label:t.custom}].map(({key,label}) => (
                              <button key={key} onClick={() => { setExpensesReportPeriod(key); const d = getReportDates(key); if (d) { setExpenseStartDate(d.from); setExpenseEndDate(d.to); } }}
                                style={{ flex: 1, padding: '10px 6px', borderRadius: 16, fontSize: 12, fontWeight: expensesReportPeriod === key ? 700 : 600, cursor: 'pointer', border: 'none', whiteSpace: 'nowrap',
                                  background: expensesReportPeriod === key ? 'var(--bg-tab-active)' : 'var(--bg-tab-inactive)',
                                  color: expensesReportPeriod === key ? 'var(--text-tab-active)' : 'var(--text-tab-inactive)' }}>
                                {label}
                              </button>
                            ))}
                          </div>
                          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>{fromD} - {toD}</div>
                        </div>
                        {/* ── CUSTOM DATE PICKERS ── */}
                        {expensesReportPeriod === 'custom' && (
                          <div style={{ background: 'var(--bg-white)', padding: '0 16px 12px', borderBottom: '1px solid var(--border-color)', display: 'flex', gap: 10, flexShrink: 0 }}>
                            <div style={{ flex: 1 }}>
                              <label style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>{t.startDate}</label>
                              <input type="date" value={expenseStartDate} onFocus={e => { try { e.currentTarget.showPicker(); } catch(x){} }} onChange={e => setExpenseStartDate(e.target.value)} style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid var(--border-color)', background: 'var(--bg-tab)', fontSize: 14, boxSizing: 'border-box' }} />
                            </div>
                            <div style={{ flex: 1 }}>
                              <label style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>{t.endDate}</label>
                              <input type="date" value={expenseEndDate} onFocus={e => { try { e.currentTarget.showPicker(); } catch(x){} }} onChange={e => setExpenseEndDate(e.target.value)} style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid var(--border-color)', background: 'var(--bg-tab)', fontSize: 14, boxSizing: 'border-box' }} />
                            </div>
                          </div>
                        )}
                        {/* ── CATEGORY FILTER ── */}
                        <div style={{ background: 'var(--bg-white)', padding: '8px 16px 10px', borderBottom: '1px solid var(--border-color)', flexShrink: 0 }}>
                          <label style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600, display: 'block', marginBottom: 4 }}>{language === 'sw' ? 'Chuja kwa Aina' : 'Filter by Category'}</label>
                          <select value={expensesReportCategory} onChange={e => setExpensesReportCategory(e.target.value)}
                            style={{ width: '100%', padding: '9px 12px', borderRadius: 10, border: '1px solid var(--border-color)', background: 'var(--bg-tab)', fontSize: 14, color: 'var(--text-primary)', outline: 'none' }}>
                            <option value="all">{language === 'sw' ? 'Aina Zote' : 'All Categories'}</option>
                            {[...new Set((expenses || []).map(e => e.category).filter(Boolean))].sort().map(cat => (
                              <option key={cat} value={cat}>{cat}</option>
                            ))}
                          </select>
                        </div>
                        {/* ── SUMMARY CARD ── */}
                        <div style={{ background: 'var(--bg-white)', padding: '10px 16px', borderBottom: '1px solid var(--border-color)', flexShrink: 0 }}>
                          <div className="pastel-card pastel-card--amber">
                            <div className="pc-label">{t.totalExpenses}</div>
                            <div className="pc-value">{fmt(expensesTotal)}</div>
                          </div>
                        </div>
                        {/* ── TRANSACTION TABLE ── */}
                        {(() => {
                          const thS = { padding: '9px 10px', textAlign: 'left', fontWeight: 700, color: 'var(--text-primary)', fontSize: 11, borderBottom: '2px solid #D1D5DB', whiteSpace: 'nowrap', letterSpacing: '0.03em' };
                          const tdS = { padding: '9px 10px', color: 'var(--text-primary)', fontSize: 12, verticalAlign: 'middle' };
                          const editBtnS = { background: '#FEE2E2', color: '#991B1B', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 13, marginRight: 3 };
                          const delBtnS  = { background: '#FEE2E2', color: '#DC2626', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 13 };
                          return (
                            <div style={{ flex: 1, overflowY: 'auto', overflowX: 'auto' }}>
                              {filteredExpenses.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--text-muted)', fontSize: 14 }}>{t.noDataForPeriod}</div>
                              ) : (<>
                                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', padding: '8px 4px' }}>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--text-muted)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                    <input type="checkbox" checked={hideVoidedExpenses} onChange={e => setHideVoidedExpenses(e.target.checked)} style={{ accentColor: '#92400E' }} />
                                    {language === 'sw' ? 'Ficha zilizobatilishwa' : 'Hide voided'}
                                  </label>
                                </div>
                                <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%', minWidth: 380, tableLayout: 'auto' }}>
                                  <thead>
                                    <tr style={{ background: 'var(--bg-tab)', position: 'sticky', top: 0, zIndex: 1 }}>
                                      <th style={thS}>{t.date}</th>
                                      <th style={thS}>{t.category || 'Category'}</th>
                                      <th style={thS}>{t.description || 'Description'}</th>
                                      <th style={{ ...thS, textAlign: 'right' }}>{t.amount || 'Amount'}</th>
                                      <th style={{ ...thS, textAlign: 'center' }}>{t.actions || 'Actions'}</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {filteredExpenses.map((expense, i) => (
                                      <tr key={expense.id} style={{ background: i % 2 === 0 ? 'var(--bg-white)' : 'var(--bg-tab)', borderBottom: '1px solid var(--border-color)', opacity: expense.status === 'void' ? 0.5 : 1, textDecoration: expense.status === 'void' ? 'line-through' : 'none', borderLeft: expense.status === 'void' ? '3px solid #9CA3AF' : '3px solid transparent' }}>
                                        <td style={{ ...tdS, whiteSpace: 'nowrap' }}>{expense.date}{expense.status === 'void' && <span style={{ marginLeft: 4, fontSize: 8, fontWeight: 800, padding: '1px 4px', borderRadius: 3, background: '#9CA3AF', color: '#fff', verticalAlign: 'middle', textDecoration: 'none', display: 'inline-block' }}>VOIDED</span>}</td>
                                        <td style={{ ...tdS, whiteSpace: 'nowrap' }}>
                                          <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 6, background: '#FEE2E2', color: '#991B1B' }}>{expense.category}</span>
                                        </td>
                                        <td style={{ ...tdS, maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: expense.description && expense.description !== expense.category ? '#111827' : '#9CA3AF' }}>
                                          {expense.description && expense.description !== expense.category ? expense.description : '—'}
                                        </td>
                                        <td style={{ ...tdS, textAlign: 'right', fontWeight: 700, color: '#DC2626', whiteSpace: 'nowrap' }}>{fmt(parseFloat(expense.amount) || 0)}</td>
                                        <td style={{ ...tdS, textAlign: 'center', whiteSpace: 'nowrap' }}>
                                          <button onClick={() => handleEditExpense(expense)} style={editBtnS}>✎️</button>
                                          {expense.status !== 'void' && <button onClick={() => handleVoidExpense(expense.id)} style={{ ...delBtnS, background: '#FEF3C7', color: '#92400E' }} title={language === 'sw' ? 'Batilisha' : 'Void'}>⊘</button>}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </>)}
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })()}

                  {/* === MAIN EXPENSES DASHBOARD === */}
                  {!isExpensesReportOpen && (
                    <div style={{ padding: 16, background: 'var(--bg-tab)', minHeight: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}>

                      {/* HEADING */}
                      <h1 style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 12px' }}>{t.expenses}</h1>

                      {/* TOP ACTION BUTTONS -neon for Record */}
                      <div style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
                        {perms.createExpense && <button onClick={() => setShowExpenseForm(true)}
                          style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, height: 42, borderRadius: 26, background: '#A3E635', color: '#18222F', border: 'none', fontWeight: 700, fontSize: 12, cursor: 'pointer', boxShadow: '0 4px 14px rgba(163,230,53,0.35)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          + {t.recordExpense}
                        </button>}
                        <button onClick={() => setIsExpensesReportOpen(true)}
                          style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, height: 42, borderRadius: 26, background: '#ECFDF5', color: '#065F46', border: '1.5px solid #A7F3D0', fontWeight: 600, fontSize: 12, cursor: 'pointer', boxShadow: '0 1px 3px rgba(15,23,42,0.04)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {t.viewExpenseReports}
                        </button>
                      </div>

                      {/* ═══ SECTION 1 -Expenses Overview ═══ */}
                      <div style={{ background: 'var(--bg-white)', borderRadius: 16, overflow: 'hidden', marginBottom: 14, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', border: '0.5px solid var(--border-color)' }}>
                        {/* Section heading */}
                        <div style={{ padding: '12px 14px 4px' }}>
                          <h3 className="db-card-title" style={{ fontSize: 15, marginBottom: 0 }}>
                            {language === 'sw' ? 'Muhtasari wa Gharama' : 'Expenses Overview'}
                          </h3>
                        </div>
                        <div style={{ padding: 12 }}>

                      {/* PERIOD SELECTOR -pill container */}
                      <div style={{ background: 'var(--bg-tab)', borderRadius: 16, padding: 4, display: 'flex', gap: 2, marginBottom: 8 }}>
                        {[
                          { key: 'today',  label: t.today },
                          { key: 'week',   label: t.thisWeek },
                          { key: 'month',  label: t.thisMonth },
                          { key: 'year',   label: t.thisYear },
                          { key: 'custom', label: t.custom },
                        ].map(f => (
                          <button key={f.key} onClick={() => {
                            setExpensesTimeFilter(f.key);
                            if (f.key === 'custom' && !expensesCustomFrom && !expensesCustomTo) {
                              const n = new Date(); const y = n.getFullYear(); const m = String(n.getMonth()+1).padStart(2,'0');
                              setExpensesCustomFrom(`${y}-${m}-01`);
                              setExpensesCustomTo(`${y}-${m}-${String(new Date(y, n.getMonth()+1, 0).getDate()).padStart(2,'0')}`);
                            }
                          }}
                            style={{ flex: 1, padding: '10px 6px', borderRadius: 16, fontSize: 12, fontWeight: expensesTimeFilter === f.key ? 700 : 600, cursor: 'pointer', border: 'none', whiteSpace: 'nowrap',
                              background: expensesTimeFilter === f.key ? 'var(--bg-tab-active)' : 'var(--bg-tab-inactive)',
                              color: expensesTimeFilter === f.key ? 'var(--text-tab-active)' : 'var(--text-tab-inactive)',
                              boxShadow: 'none' }}>
                            {f.label}
                          </button>
                        ))}
                      </div>

                      {/* PERIOD DISPLAY LABEL OR CUSTOM DATE RANGE */}
                      {expensesTimeFilter === 'custom' ? (
                        <div className="db-custom-date-card">
                          <div className="db-custom-date-row">
                            <div className="db-custom-date-col">
                              <span className="db-custom-date-label">{t.fromDate}</span>
                              <input type="date" className="db-custom-date-input" value={expensesCustomFrom} onChange={e => setExpensesCustomFrom(e.target.value)} />
                            </div>
                            <div className="db-custom-date-col">
                              <span className="db-custom-date-label">{t.toDate}</span>
                              <input type="date" className="db-custom-date-input" value={expensesCustomTo} onChange={e => setExpensesCustomTo(e.target.value)} />
                            </div>
                          </div>
                        </div>
                      ) : expensesPeriodLabel ? (
                        <div className="period-date-label">{expensesPeriodLabel}</div>
                      ) : <div style={{ marginBottom: 8 }} />}

                      {/* TOTAL EXPENSE HIGHLIGHT CARD -hidden by privacy Expenses.totalCard */}
                      {!isItemHidden('expenses', 'totalCard') && (
                      <div style={{ position: 'relative', marginBottom: 16 }}>
                        <div style={{ position: 'absolute', inset: 0, transform: 'none', pointerEvents: 'none', borderRadius: 18, background: 'rgba(245,158,11,0.10)', zIndex: 0 }}></div>
                        <div style={{ position: 'absolute', inset: 0, transform: 'none', pointerEvents: 'none', borderRadius: 18, background: 'rgba(245,158,11,0.18)', zIndex: 1 }}></div>
                        <div style={{ position: 'relative', zIndex: 2, background: theme === 'dark' ? 'linear-gradient(135deg, #451A03 0%, #78350F 100%)' : 'linear-gradient(180deg, #FFF8E8 0%, #FFE5A8 100%)', borderRadius: 18, padding: '16px 14px', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                              <div style={{ width: 32, height: 32, borderRadius: 10, background: theme === 'dark' ? 'rgba(245,158,11,0.2)' : 'rgba(245,158,11,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={theme === 'dark' ? '#FCD34D' : '#92400E'} strokeWidth="2.5" strokeLinecap="round"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                              </div>
                              <div style={{ fontSize: 11, fontWeight: 700, color: theme === 'dark' ? '#FDE68A' : '#18222F', textTransform: 'uppercase', letterSpacing: 0.5 }}>{t.totalExpenses}</div>
                            </div>
                            <div style={{ fontSize: 22, fontWeight: 900, color: theme === 'dark' ? '#FEF3C7' : '#4A3711', marginBottom: 2 }}>{fmt(totalExpAmt)}</div>
                            <div style={{ fontSize: 10, fontWeight: 600, color: theme === 'dark' ? '#FDE68A' : '#18222F', opacity: 0.7 }}>{expCount} {t.noOfExpenses.toLowerCase()}</div>
                          </div>
                          <div style={{ width: 44, height: 44, borderRadius: 12, background: theme === 'dark' ? 'rgba(245,158,11,0.2)' : 'rgba(245,158,11,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0, color: '#92400E' }}>$</div>
                        </div>
                      </div>
                      )}

                      {/* EXPENSES BY CATEGORY -HORIZONTAL BAR CHART (Stock tab style) */}
                      <div style={{ background: 'var(--bg-white)', borderRadius: 16, padding: 16, marginBottom: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                          <div style={{ width: 3, height: 16, borderRadius: 2, background: '#FACC15', flexShrink: 0 }} />
                          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{t.expensesByCategory}</div>
                        </div>
                        {exByCategory.length === 0 ? (
                          <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 13, padding: '24px 0' }}>{t.noDataForPeriod}</div>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {exByCategory.slice(0, 5).map((item) => (
                              <div key={item.name} onClick={() => setExpenseHistoryCategory(item.name)} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '4px 6px', borderRadius: 8, transition: 'background 0.15s' }} onMouseEnter={e => e.currentTarget.style.background = '#F9FAFB'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'} title={language === 'sw' ? 'Bofya kuona historia' : 'Click to view history'}>
                                <span style={{ width: 90, fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 0 }}>{item.name}</span>
                                <div style={{ flex: 1, height: 22, background: 'var(--bg-input, #F3F4F6)', borderRadius: 6, overflow: 'hidden' }}>
                                  <div style={{ height: '100%', width: `${exMaxCat > 0 ? (item.amount / exMaxCat * 100) : 0}%`, background: item.color, borderRadius: 6, transition: 'width 0.6s ease' }}></div>
                                </div>
                                <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', minWidth: 60, textAlign: 'right', flexShrink: 0 }}>{fmt(item.amount)}</span>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2.5" strokeLinecap="round" style={{ flexShrink: 0 }}><polyline points="9 18 15 12 9 6"/></svg>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* RECENT EXPENSES -last 5 */}
                      <div style={{ background: 'var(--bg-white)', borderRadius: 16, padding: 16, marginBottom: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
                          <div style={{ width: 3, height: 16, borderRadius: 2, background: '#FACC15', flexShrink: 0 }} />
                          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', flex: 1 }}>{t.recentExpenses}</div>
                          {/* DISPLAY-ONLY toggle. Never affects any total above. */}
                          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', cursor: 'pointer' }}>
                            <input type="checkbox" checked={hideVoidedExpenses} onChange={e => setHideVoidedExpenses(e.target.checked)} style={{ accentColor: '#92400E' }} />
                            {language === 'sw' ? 'Ficha zilizobatilishwa' : 'Hide voided'}
                          </label>
                        </div>
                        {exRecent.length === 0 ? (
                          <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 13, padding: '24px 0' }}>{t.noDataForPeriod}</div>
                        ) : exRecent.map((exp, i) => {
                          const isVoid = exp.status === 'void';
                          return (
                          <div key={exp.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: i < exRecent.length - 1 ? 12 : 0, marginBottom: i < exRecent.length - 1 ? 12 : 0, borderBottom: i < exRecent.length - 1 ? '1px solid #F3F4F6' : 'none', opacity: isVoid ? 0.5 : 1, borderLeft: isVoid ? '3px solid #9CA3AF' : 'none', paddingLeft: isVoid ? 8 : 0 }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: isVoid ? 'line-through' : 'none' }}>
                                {exp.description || exp.category}
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                <span style={{ fontSize: 10, background: '#FEF9C3', color: '#854D0E', borderRadius: 4, padding: '2px 7px', fontWeight: 600, border: '1px solid #FDE68A' }}>{exp.category}</span>
                                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{exp.date}</span>
                                {isVoid && (
                                  <span style={{ fontSize: 9, fontWeight: 800, padding: '2px 6px', borderRadius: 4, background: '#9CA3AF', color: '#fff', letterSpacing: 0.3 }}>
                                    {language === 'sw' ? 'IMEBATILISHWA' : 'VOIDED'}
                                  </span>
                                )}
                                {isVoid && exp._voidUnsynced && (
                                  <span style={{ fontSize: 9, fontWeight: 700, color: '#B45309' }}>
                                    {language === 'sw' ? 'HAIJASAWAZISHWA' : 'NOT SYNCED'}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div style={{ fontSize: 14, fontWeight: 700, color: '#92400E', marginLeft: 12, flexShrink: 0, textDecoration: isVoid ? 'line-through' : 'none' }}>{fmt(exp.amount)}</div>
                          </div>
                          );
                        })}
                      </div>

                        </div>{/* end Section 1 content padding */}
                      </div>{/* END SECTION 1 */}

                      {/* ═══ SECTION 2 -Annual Performance ═══ */}
                      <div style={{ background: 'var(--bg-white)', borderRadius: 16, overflow: 'hidden', marginBottom: 16, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', border: '0.5px solid var(--border-color)' }}>
                        {/* Section heading */}
                        <div style={{ padding: '12px 14px 4px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <h3 className="db-card-title" style={{ fontSize: 15, marginBottom: 0 }}>{t.annualPerformance}</h3>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3 }}>
                            <div style={{ fontSize: 8, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8 }}>{t.selectYear}</div>
                            <select value={expensesAnnualYear} onChange={e => setExpensesAnnualYear(parseInt(e.target.value))}
                              style={{ padding: '4px 10px', borderRadius: 8, border: '2px solid var(--border-color)', background: 'var(--bg-input)', fontSize: 14, fontWeight: 900, color: 'var(--text-primary)', cursor: 'pointer', outline: 'none', minWidth: 80, textAlign: 'center' }}>
                              {exAvailableYears.map(y => <option key={y} value={y} style={{ background: 'var(--bg-card)', color: 'var(--text-primary)' }}>{y}</option>)}
                            </select>
                          </div>
                        </div>
                        <div style={{ padding: 12 }}>

                        {/* Annual total -amber palette */}
                        <div style={{ background: 'rgba(245,158,11,0.08)', borderRadius: 12, padding: '10px 14px', marginBottom: 14, borderLeft: '4px solid #F59E0B' }}>
                          <div style={{ fontSize: 11, color: '#92400E', fontWeight: 700, marginBottom: 2 }}>{t.totalExpenses} · {expensesAnnualYear}</div>
                          <div style={{ fontSize: 22, fontWeight: 800, color: '#92400E' }}>{fmt(exAnnualTotal)}</div>
                        </div>

                        {/* EXPENSE TREND AREA CHART */}
                        <div style={{ background: 'var(--bg-tab)', borderRadius: 14, padding: 14 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 10 }}>{t.expenseTrend}</div>
                        {(() => {
                          const W = 320, H = 180, PX = 8, PY = 28;
                          const pts = exAnnualData.map((d, i) => ({
                            x: PX + i * (W - PX * 2) / 11,
                            y: H - PY - (exMaxAnnual > 0 ? (d.amount / exMaxAnnual) * (H - PY * 2) : 0)
                          }));
                          const bez = (p) => { let d = `M${p[0].x.toFixed(1)},${p[0].y.toFixed(1)}`; for (let i = 0; i < p.length-1; i++) { const a=p[Math.max(i-1,0)],b=p[i],c=p[i+1],e=p[Math.min(i+2,p.length-1)]; d+=` C${(b.x+(c.x-a.x)/4).toFixed(1)},${(b.y+(c.y-a.y)/4).toFixed(1)} ${(c.x-(e.x-b.x)/4).toFixed(1)},${(c.y-(e.y-b.y)/4).toFixed(1)} ${c.x.toFixed(1)},${c.y.toFixed(1)}`; } return d; };
                          const linePath = bez(pts);
                          const areaPath = `${linePath} L${pts[pts.length-1].x.toFixed(1)},${(H-PY).toFixed(1)} L${pts[0].x.toFixed(1)},${(H-PY).toFixed(1)} Z`;
                          const hi = expensesChartHover;
                          return (
                            <div style={{ overflow: 'visible' }}>
                              <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 190, display: 'block', overflow: 'visible' }} preserveAspectRatio="none" onMouseLeave={() => setExpensesChartHover(null)}>
                                <defs>
                                  <linearGradient id="exAreaGrad" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#F59E0B" stopOpacity="0.35" />
                                    <stop offset="100%" stopColor="#F59E0B" stopOpacity="0.03" />
                                  </linearGradient>
                                </defs>
                                {[0.33,0.66].map((f,i) => <line key={i} x1={PX} y1={(PY+f*(H-PY*2)).toFixed(1)} x2={W-PX} y2={(PY+f*(H-PY*2)).toFixed(1)} stroke="var(--border-color)" strokeWidth="1" />)}
                                <path d={areaPath} fill="url(#exAreaGrad)" />
                                <path d={linePath} fill="none" stroke="#F59E0B" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
                                {pts.map((p, i) => { const x0 = i === 0 ? 0 : (pts[i-1].x+p.x)/2; const x1 = i === pts.length-1 ? W : (p.x+pts[i+1].x)/2; return <rect key={i} x={x0.toFixed(1)} y={0} width={(x1-x0).toFixed(1)} height={H} fill="transparent" style={{ cursor: 'crosshair' }} onMouseEnter={() => setExpensesChartHover(i)} />; })}
                                {hi !== null && (() => {
                                  const aboveFits = pts[hi].y - 26 >= PY;
                                  const tipRectY = aboveFits ? pts[hi].y - 26 : pts[hi].y + 8;
                                  const tipRectX = Math.max(2, Math.min(pts[hi].x - 38, W - 78));
                                  return (
                                    <>
                                      <line x1={pts[hi].x.toFixed(1)} y1={PY} x2={pts[hi].x.toFixed(1)} y2={H-PY} stroke="#9CA3AF" strokeWidth="1" strokeDasharray="3,3" />
                                      <circle cx={pts[hi].x.toFixed(1)} cy={pts[hi].y.toFixed(1)} r="4.5" fill="#F59E0B" stroke="#fff" strokeWidth="2" />
                                      <rect x={tipRectX.toFixed(1)} y={tipRectY.toFixed(1)} width="64" height="14" rx="4" fill="#92400E" fillOpacity="0.9" />
                                      <text x={(parseFloat(tipRectX) + 32).toFixed(1)} y={(parseFloat(tipRectY) + 10).toFixed(1)} textAnchor="middle" fill="#fff" fontSize="7" fontWeight="400">{fmt(exAnnualData[hi].amount)}</text>
                                    </>
                                  );
                                })()}
                              </svg>
                              <div style={{ display: 'flex', marginTop: 4 }}>
                                {exAnnualData.map((d, i) => (
                                  <div key={i} style={{ flex: 1, textAlign: 'center' }}>
                                    <span style={{ display: 'inline-block', background: hi === i ? 'var(--bg-white)' : 'var(--bg-tab)', borderRadius: 5, padding: '2px 3px', fontSize: 8, color: 'var(--text-primary)', fontWeight: 700 }}>{d.label}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })()}
                        </div>{/* end chart sub-card */}

                        {/* ANNUAL CATEGORY BREAKDOWN */}
                        {(() => {
                          const annualExpenses = (expenses || []).filter(e => {
                            const d = new Date(e.date || 0);
                            return !isNaN(d.getTime()) && d.getFullYear() === expensesAnnualYear && e.status !== 'void';
                          });
                          const annCatMap = {};
                          annualExpenses.forEach(e => {
                            const cat = e.category || 'Other';
                            if (!annCatMap[cat]) annCatMap[cat] = { name: cat, amount: 0 };
                            annCatMap[cat].amount += parseFloat(e.amount || 0);
                          });
                          const annCatList = Object.values(annCatMap).sort((a, b) => b.amount - a.amount);
                          const annCatMax = annCatList.length > 0 ? annCatList[0].amount : 1;
                          return (
                            <div style={{ background: 'var(--bg-white)', borderRadius: 16, padding: 16, marginTop: 14, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
                              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>
                                {language === 'sw' ? `Matumizi kwa Aina · ${expensesAnnualYear}` : `Expenses by Category · ${expensesAnnualYear}`}
                              </div>
                              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 16 }}>
                                {language === 'sw' ? `Aina za matumizi · ${expensesAnnualYear}` : `Expense categories · ${expensesAnnualYear}`}
                              </div>
                              {annCatList.length === 0 ? (
                                <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 13, padding: '24px 0' }}>{t.noDataForPeriod}</div>
                              ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                  {annCatList.slice(0, 7).map((item) => {
                                    const barColor = getCatColor(item.name);
                                    return (
                                      <div key={item.name} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                        <span style={{ width: 90, fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 0 }}>{item.name}</span>
                                        <div style={{ flex: 1, height: 22, background: 'var(--bg-input, #F3F4F6)', borderRadius: 6, overflow: 'hidden' }}>
                                          <div style={{ height: '100%', width: `${annCatMax > 0 ? (item.amount / annCatMax * 100) : 0}%`, background: barColor, borderRadius: 6, transition: 'width 0.6s ease' }} />
                                        </div>
                                        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', minWidth: 60, textAlign: 'right', flexShrink: 0 }}>{fmt(item.amount)}</span>
                                      </div>
                                    );
                                  })}
                                  {annCatList.length > 7 && (
                                    <div style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'center', paddingTop: 4 }}>
                                      +{annCatList.length - 7} {language === 'sw' ? 'aina zaidi' : 'more categories'}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })()}

                        </div>{/* end Section 2 content padding */}
                      </div>{/* END SECTION 2 */}

                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}

        {activeTab === 'inventory' && (
          <div className="inventory st-screen">
            {!isDataReady ? (
              <div role="status" aria-label={t.loading || 'Loading'} style={{ padding: 16 }}>
                <div className="skeleton skeleton-title"></div>
                <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
                  <div className="skeleton skeleton-btn" style={{ flex: 1, width: 'auto' }}></div>
                  <div className="skeleton skeleton-btn" style={{ flex: 1, width: 'auto' }}></div>
                </div>
                <div className="skeleton skeleton-table-header"></div>
                {[1,2,3,4,5,6].map(i => <div key={i} className="skeleton skeleton-row"></div>)}
              </div>
            ) : stockHistoryProduct ? (() => {
              // ── PRODUCT HISTORY SCREEN ──
              const phName = stockHistoryProduct;
              const phProduct = (products || []).find(p => p.productName === phName);
              const phQty = parseInt(phProduct?.balance || phProduct?.openingStockQty || 0);

              // Period filtering -H5: shared range (to-date presets, rolling week).
              const _phRange = getPeriodRange(stockHistoryFilter, stockHistoryFrom, stockHistoryTo);
              const _phMk = (s, end) => {
                if (!s) return end ? new Date(2999, 11, 31, 23, 59, 59, 999) : new Date(1900, 0, 1);
                const [y, m, d] = s.split('-').map(Number);
                return end ? new Date(y, m - 1, d, 23, 59, 59, 999) : new Date(y, m - 1, d);
              };
              let _phFrom = _phMk(_phRange.from, false);
              let _phTo = _phMk(_phRange.to, true);
              const _phInP = d => inPeriod(d, _phRange);

              // Period label
              const _phNow = new Date(); // (restored -used by the label/preset below)
              const _phPad = n => String(n).padStart(2, '0');
              const _phFmt = d => `${_phPad(d.getDate())}/${_phPad(d.getMonth()+1)}/${d.getFullYear()}`;
              let _phPeriodText = '';
              if (stockHistoryFilter === 'today') _phPeriodText = _phFmt(_phNow);
              else if (stockHistoryFilter === 'week') _phPeriodText = `${_phFmt(_phFrom)} \u2013 ${_phFmt(_phTo)}`;
              else if (stockHistoryFilter === 'month') {
                const mn = (t.monthNames || ['January','February','March','April','May','June','July','August','September','October','November','December'])[_phNow.getMonth()];
                _phPeriodText = `${mn} ${_phNow.getFullYear()}`;
              } else if (stockHistoryFilter === 'year') _phPeriodText = String(_phNow.getFullYear());

              // Helper: extract qty for a specific product from a record (handles single + multi-item)
              // Name comparison is forgiving (case + whitespace-insensitive)
              const normPN = (s) => (s || '').trim().toLowerCase().replace(/\s+/g, ' ');
              const qtyForProduct = (rec, name) => {
                const tgt = normPN(name);
                if (Array.isArray(rec.items) && rec.items.length > 0) {
                  return rec.items
                    .filter(it => normPN(it.productName || it.name) === tgt)
                    .reduce((s, it) => s + parseInt(it.quantity || 0), 0);
                }
                return normPN(rec.productName) === tgt ? parseInt(rec.quantity || 0) : 0;
              };
              const recordMatchesProduct = (rec, name) => {
                const tgt = normPN(name);
                if (Array.isArray(rec.items) && rec.items.length > 0) {
                  return rec.items.some(it => normPN(it.productName || it.name) === tgt);
                }
                return normPN(rec.productName) === tgt;
              };

              // Helper: extract qty for a specific product from a return record
              // Purchase returns: goods returned TO supplier -reduce stock (qtyOut) when restocked
              // Sales returns: goods returned BY customer -increase stock (qtyIn) when restocked
              const returnQtyForProduct = (rec, name) => {
                const tgt = normPN(name);
                if (Array.isArray(rec.items) && rec.items.length > 0) {
                  return rec.items
                    .filter(it => normPN(it.productName || it.name) === tgt)
                    .reduce((s, it) => s + parseInt(it.qty || it.quantity || 0), 0);
                }
                return normPN(rec.productName) === tgt ? parseInt(rec.qty || rec.quantity || 0) : 0;
              };
              const returnMatchesProduct = (rec, name) => {
                const tgt = normPN(name);
                if (Array.isArray(rec.items) && rec.items.length > 0) {
                  return rec.items.some(it => normPN(it.productName || it.name) === tgt);
                }
                return normPN(rec.productName) === tgt;
              };

              // Build transactions -purchases, sales, and returns
              const phPurchases = (purchases || []).filter(p => recordMatchesProduct(p, phName) && _phInP(p.date)).map(p => ({
                date: p.date, type: t.purchase || 'Purchase', qtyIn: qtyForProduct(p, phName), qtyOut: 0, ref: p.supplier || '—', sortDate: new Date(p.date || 0)
              }));
              const phSales = (sales || []).filter(s => recordMatchesProduct(s, phName) && _phInP(s.date)).map(s => ({
                date: s.date, type: t.sale || 'Sale', qtyIn: 0, qtyOut: qtyForProduct(s, phName), ref: s.customerName || '—', sortDate: new Date(s.date || 0)
              }));
              // Purchase returns -qty leaves stock when restocked (goods sent back to supplier)
              const phPurchaseReturns = (purchaseReturns || []).filter(r => returnMatchesProduct(r, phName) && _phInP(r.date) && r.restock !== false).map(r => ({
                date: r.date, type: language === 'sw' ? 'Rud. Manunuzi' : 'Purch. Return', qtyIn: 0, qtyOut: returnQtyForProduct(r, phName), ref: r.supplier || r.reason || '—', sortDate: new Date(r.date || 0), isReturn: true
              }));
              // Sales returns -qty back into stock (customer returned goods)
              const phSalesReturns = (salesReturns || []).filter(r => returnMatchesProduct(r, phName) && _phInP(r.date)).map(r => ({
                date: r.date, type: language === 'sw' ? 'Rud. Mauzo' : 'Sale Return', qtyIn: returnQtyForProduct(r, phName), qtyOut: 0, ref: r.customerName || r.reason || '—', sortDate: new Date(r.date || 0), isReturn: true
              }));
              const phAll = [...phPurchases, ...phSales, ...phPurchaseReturns, ...phSalesReturns].sort((a, b) => a.sortDate - b.sortDate);

              // Running balance -replay ALL movements before the period to get opening balance
              const openingQty = parseInt(phProduct?.openingStockQty || 0);
              let runBal = openingQty;
              (purchases || []).filter(p => recordMatchesProduct(p, phName) && new Date(p.date || 0) < _phFrom).forEach(p => { runBal += qtyForProduct(p, phName); });
              (sales || []).filter(s => recordMatchesProduct(s, phName) && new Date(s.date || 0) < _phFrom).forEach(s => { runBal -= qtyForProduct(s, phName); });
              (purchaseReturns || []).filter(r => returnMatchesProduct(r, phName) && new Date(r.date || 0) < _phFrom && r.restock !== false).forEach(r => { runBal -= returnQtyForProduct(r, phName); });
              (salesReturns || []).filter(r => returnMatchesProduct(r, phName) && new Date(r.date || 0) < _phFrom).forEach(r => { runBal += returnQtyForProduct(r, phName); });
              const startBal = runBal;
              phAll.forEach(tx => { runBal += tx.qtyIn - tx.qtyOut; tx.balance = runBal; });

              // Totals -match the FILTERED period so the cards agree with the list below
              const totalPurchased = phPurchases.reduce((s, p) => s + p.qtyIn, 0);
              const totalSold = phSales.reduce((s, sale) => s + sale.qtyOut, 0);
              const totalPurchaseReturned = phPurchaseReturns.reduce((s, r) => s + r.qtyOut, 0);
              const totalSalesReturned = phSalesReturns.reduce((s, r) => s + r.qtyIn, 0);

              return (
                <div style={{ minHeight: '100vh', background: 'var(--bg-main)', paddingBottom: 80 }}>
                  {/* Header */}
                  <div style={{ display: 'flex', alignItems: 'center', padding: '20px 20px 8px', gap: 10 }}>
                    <button onClick={() => setStockHistoryProduct(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px 4px 0', display: 'flex', alignItems: 'center' }}>
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--text-primary)" strokeWidth="2.5" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
                    </button>
                    <h1 style={{ fontSize: 20, fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>{t.productHistory || 'Product History'}</h1>
                  </div>

                  <div style={{ padding: '0 16px' }}>
                    {/* Product Summary Card */}
                    <div style={{ background: 'var(--bg-card)', borderRadius: 16, padding: '16px 18px', marginBottom: 12, boxShadow: 'var(--shadow-card)' }}>
                      <div style={{ fontWeight: 800, fontSize: 17, color: 'var(--text-primary)', marginBottom: 10 }}>{phName}</div>
                      <div style={{ display: 'flex', gap: 10 }}>
                        <div style={{ flex: 1, background: 'var(--bg-input, #F3F4F6)', borderRadius: 12, padding: '10px 12px', textAlign: 'center' }}>
                          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 2 }}>{t.currentStock || 'Current Stock'}</div>
                          <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--text-primary)' }}>{phQty}</div>
                        </div>
                        <div style={{ flex: 1, background: 'var(--bg-input, #F3F4F6)', borderRadius: 12, padding: '10px 12px', textAlign: 'center' }}>
                          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 2 }}>{t.totalPurchased || 'Total Purchased'}</div>
                          <div style={{ fontSize: 20, fontWeight: 800, color: '#059669' }}>+{totalPurchased}</div>
                        </div>
                        <div style={{ flex: 1, background: 'var(--bg-input, #F3F4F6)', borderRadius: 12, padding: '10px 12px', textAlign: 'center' }}>
                          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 2 }}>{t.totalSold || 'Total Sold'}</div>
                          <div style={{ fontSize: 20, fontWeight: 800, color: '#EF4444' }}>-{totalSold}</div>
                        </div>
                      </div>
                    </div>

                    {/* Period Selector */}
                    <div className="db-time-filter">
                      {[
                        { key: 'today', label: t.today },
                        { key: 'week',  label: t.thisWeek },
                        { key: 'month', label: t.thisMonth },
                        { key: 'year',  label: t.thisYear },
                        { key: 'custom', label: t.custom }
                      ].map(f => (
                        <button key={f.key}
                          className={`db-time-btn ${stockHistoryFilter === f.key ? 'db-time-active' : ''}`}
                          onClick={() => {
                            setStockHistoryFilter(f.key);
                            if (f.key === 'custom' && !stockHistoryFrom && !stockHistoryTo) {
                              const y = _phNow.getFullYear(), m = String(_phNow.getMonth()+1).padStart(2,'0');
                              const ld = new Date(y, _phNow.getMonth()+1, 0).getDate();
                              setStockHistoryFrom(`${y}-${m}-01`);
                              setStockHistoryTo(`${y}-${m}-${String(ld).padStart(2,'0')}`);
                            }
                          }}>
                          {f.label}
                        </button>
                      ))}
                    </div>

                    {/* Custom date range or period label */}
                    {stockHistoryFilter === 'custom' ? (
                      <div className="db-custom-date-card">
                        <div className="db-custom-date-row">
                          <div className="db-custom-date-col">
                            <span className="db-custom-date-label">{t.fromDate}</span>
                            <input type="date" className="db-custom-date-input" value={stockHistoryFrom} onChange={e => setStockHistoryFrom(e.target.value)} onKeyDown={e => e.preventDefault()} />
                          </div>
                          <div className="db-custom-date-col">
                            <span className="db-custom-date-label">{t.toDate}</span>
                            <input type="date" className="db-custom-date-input" value={stockHistoryTo} onChange={e => setStockHistoryTo(e.target.value)} onKeyDown={e => e.preventDefault()} />
                          </div>
                        </div>
                      </div>
                    ) : _phPeriodText ? (
                      <div className="period-date-label">{_phPeriodText}</div>
                    ) : null}

                    {/* Opening Balance row */}
                    <div style={{ background: 'var(--bg-card)', borderRadius: 14, padding: '10px 14px', marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: 'var(--shadow-card)' }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' }}>{t.openingBalance || 'Opening Balance'}</span>
                      <span style={{ fontSize: 15, fontWeight: 800, color: 'var(--text-primary)' }}>{startBal}</span>
                    </div>

                    {/* Transaction Table */}
                    <div style={{ background: 'var(--bg-card)', borderRadius: 16, boxShadow: 'var(--shadow-card)', overflow: 'hidden' }}>
                      {phAll.length === 0 ? (
                        <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 14 }}>{t.noHistoryAvailable || 'No history available'}</div>
                      ) : (
                        <div style={{ overflowX: 'auto' }}>
                          <table className="st-table" style={{ minWidth: 500 }}>
                            <thead>
                              <tr>
                                <th>{t.date}</th>
                                <th>{t.type || 'Type'}</th>
                                <th style={{ color: '#059669' }}>+{t.inLabel || 'In'}</th>
                                <th style={{ color: '#EF4444' }}>-{t.outLabel || 'Out'}</th>
                                <th>{t.balance || 'Balance'}</th>
                                <th>{t.reference || 'Reference'}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {phAll.map((tx, i) => {
                                // Color badge by transaction type
                                const isReturn = tx.isReturn;
                                const isIn = tx.qtyIn > 0;
                                const badgeBg = isReturn
                                  ? (theme === 'dark' ? '#3B1A24' : '#FFE4E6')
                                  : isIn
                                    ? (theme === 'dark' ? '#1A3A1A' : '#ECFCCB')
                                    : (theme === 'dark' ? '#3B1A1A' : '#FEE2E2');
                                const badgeColor = isReturn ? '#BE123C' : (isIn ? '#059669' : '#EF4444');
                                return (
                                  <tr key={i}>
                                    <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{tx.date}</td>
                                    <td><span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 8, fontSize: 11, fontWeight: 700, background: badgeBg, color: badgeColor }}>{isReturn ? '↩ ' : ''}{tx.type}</span></td>
                                    <td style={{ fontWeight: 700, color: '#059669' }}>{tx.qtyIn > 0 ? `+${tx.qtyIn}` : ''}</td>
                                    <td style={{ fontWeight: 700, color: '#EF4444' }}>{tx.qtyOut > 0 ? `-${tx.qtyOut}` : ''}</td>
                                    <td style={{ fontWeight: 800, color: 'var(--text-primary)' }}>{tx.balance}</td>
                                    <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{tx.ref}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })() : (() => {
              const stProducts = (products || []).map(p => {
                const qty = parseInt(p.balance || p.openingStockQty || 0);
                const wacUnit = getProductWAC(p.productName);
                const unitCost = wacUnit > 0 ? wacUnit : (parseFloat(p.costPrice ?? p.cost ?? p.unitCost ?? p.buyingPrice ?? p.purchasePrice ?? 0) || 0);
                return { name: p.productName, qty, unitCost, min: parseInt(p.minStockAlert || 0) };
              });
              const stLowStock = stProducts.filter(p => p.qty <= p.min);

              // Period-filtered sales for Top Performing chart -H5: same shared
              // range as every other surface (to-date presets, rolling week).
              const _stRange = getPeriodRange(stockTimeFilter, stockCustomFrom, stockCustomTo);
              const _stMk = (s, end) => {
                if (!s) return end ? new Date(2999, 11, 31, 23, 59, 59, 999) : new Date(1900, 0, 1);
                const [y, m, d] = s.split('-').map(Number);
                return end ? new Date(y, m - 1, d, 23, 59, 59, 999) : new Date(y, m - 1, d);
              };
              let _stFrom = _stMk(_stRange.from, false);
              let _stTo = _stMk(_stRange.to, true);
              const _stInP = d => inPeriod(d, _stRange);
              const filteredSales = sales.filter(s => s.status !== 'void' && _stInP(s.date));
              const addedInPeriod = purchases.filter(p => p.status !== 'void' && _stInP(p.date)).reduce((s, p) => {
                if (Array.isArray(p.items) && p.items.length > 0) {
                  return s + p.items.reduce((qs, it) => qs + parseInt(it.quantity || 0), 0);
                }
                return s + parseInt(p.quantity || 0);
              }, 0);
              const soldInPeriod = filteredSales.reduce((s, sale) => {
                if (Array.isArray(sale.items) && sale.items.length > 0) {
                  return s + sale.items.reduce((qs, it) => qs + parseInt(it.quantity || 0), 0);
                }
                return s + parseInt(sale.quantity || 0);
              }, 0);
              const _stPeriodLabel = stockTimeFilter === 'week' ? (t.thisWeek || 'This Week') : stockTimeFilter === 'month' ? (t.thisMonth || 'This Month') : stockTimeFilter === 'year' ? (t.thisYear || 'This Year') : (t.custom || 'Custom');
              // Period label
              const _stNow = new Date(); // (restored -used by the label/preset below)
              const _stPad = n => String(n).padStart(2, '0');
              const _stFmt = d => `${_stPad(d.getDate())}/${_stPad(d.getMonth()+1)}/${d.getFullYear()}`;
              let _stPeriodText = '';
              if (stockTimeFilter === 'today') { _stPeriodText = _stFmt(_stNow); }
              else if (stockTimeFilter === 'week') { _stPeriodText = `${_stFmt(_stFrom)} \u2013 ${_stFmt(_stTo)}`; }
              else if (stockTimeFilter === 'month') {
                const _mn = (t.monthNames || ['January','February','March','April','May','June','July','August','September','October','November','December'])[_stNow.getMonth()];
                _stPeriodText = `${_mn} ${_stNow.getFullYear()}`;
              } else if (stockTimeFilter === 'year') { _stPeriodText = String(_stNow.getFullYear()); }

              return (
                <div style={{ padding: 16, background: 'var(--bg-tab)', minHeight: '100%', boxSizing: 'border-box', overflowX: 'hidden', paddingBottom: 80 }}>

                  {/* HEADING */}
                  <h1 style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 12px' }}>{t.inventory}</h1>

                  {/* STOCK ADJUSTMENT + STOCK REPORT BUTTONS -outline pills with icons */}
                  <div style={{ marginBottom: 14, display: 'flex', gap: 10 }}>
                    <button onClick={() => setShowStockAdjModal(true)}
                      style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, height: 44, borderRadius: 26, background: '#ECFDF5', color: '#065F46', border: '1.5px solid #A7F3D0', fontWeight: 700, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap', boxShadow: '0 1px 3px rgba(15,23,42,0.04)' }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>
                      {t.stockAdjustment || 'Stock Adjustment'}
                    </button>
                    <button onClick={() => setShowStockReport(true)}
                      style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, height: 44, borderRadius: 26, background: '#EEF2FF', color: '#3730A3', border: '1.5px solid #C7D2FE', fontWeight: 700, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap', boxShadow: '0 1px 3px rgba(15,23,42,0.04)' }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#4F46E5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>
                      {language === 'sw' ? 'Muhtasari wa Thamani ya Bidhaa' : 'Inventory Valuation Summary'}
                    </button>
                  </div>

                  {/* ═══ SECTION 1 -Stock Overview ═══ */}
                  <div style={{ background: 'var(--bg-white)', borderRadius: 16, overflow: 'hidden', marginBottom: 14, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', border: '0.5px solid var(--border-color)' }}>
                    {/* Section heading */}
                    <div style={{ padding: '12px 14px 4px' }}>
                      <h3 className="db-card-title" style={{ fontSize: 15, marginBottom: 0 }}>
                        {language === 'sw' ? 'Muhtasari wa Stock' : 'Stock Overview'}
                      </h3>
                    </div>
                    <div style={{ padding: 12 }}>

                  {/* PERIOD SELECTOR */}
                  <div className="db-time-filter">
                    {[
                      { key: 'today', label: t.today },
                      { key: 'week',  label: t.thisWeek },
                      { key: 'month', label: t.thisMonth },
                      { key: 'year',  label: t.thisYear },
                      { key: 'custom', label: t.custom }
                    ].map(f => (
                      <button key={f.key}
                        className={`db-time-btn ${stockTimeFilter === f.key ? 'db-time-active' : ''}`}
                        onClick={() => {
                          setStockTimeFilter(f.key);
                          if (f.key === 'custom' && !stockCustomFrom && !stockCustomTo) {
                            const y = _stNow.getFullYear(), m = String(_stNow.getMonth()+1).padStart(2,'0');
                            const ld = new Date(y, _stNow.getMonth()+1, 0).getDate();
                            setStockCustomFrom(`${y}-${m}-01`);
                            setStockCustomTo(`${y}-${m}-${String(ld).padStart(2,'0')}`);
                          }
                        }}>
                        {f.label}
                      </button>
                    ))}
                  </div>

                  {/* Custom date range or period label */}
                  {stockTimeFilter === 'custom' ? (
                    <div className="db-custom-date-card">
                      <div className="db-custom-date-row">
                        <div className="db-custom-date-col">
                          <span className="db-custom-date-label">{t.fromDate}</span>
                          <input type="date" className="db-custom-date-input" value={stockCustomFrom} onChange={e => setStockCustomFrom(e.target.value)} onKeyDown={e => e.preventDefault()} />
                        </div>
                        <div className="db-custom-date-col">
                          <span className="db-custom-date-label">{t.toDate}</span>
                          <input type="date" className="db-custom-date-input" value={stockCustomTo} onChange={e => setStockCustomTo(e.target.value)} onKeyDown={e => e.preventDefault()} />
                        </div>
                      </div>
                    </div>
                  ) : _stPeriodText ? (
                    <div className="period-date-label">{_stPeriodText}</div>
                  ) : null}

                  {/* STOCK MOVEMENT SUMMARY */}
                  <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                    {/* Stock In card -sage green, charcoal labels */}
                    <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
                      <div style={{ position: 'absolute', inset: 0, transform: 'none', pointerEvents: 'none', borderRadius: 18, background: 'rgba(5,150,105,0.08)', zIndex: 0 }}></div>
                      <div style={{ position: 'absolute', inset: 0, transform: 'none', pointerEvents: 'none', borderRadius: 18, background: 'rgba(5,150,105,0.15)', zIndex: 1 }}></div>
                      <div style={{ position: 'relative', zIndex: 2, background: 'linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)', borderRadius: 18, padding: '16px 14px', border: '1px solid #A7F3D0' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                          <div style={{ width: 32, height: 32, borderRadius: 10, background: 'rgba(5,150,105,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" strokeLinecap="round"><polyline points="12 19 12 5"/><polyline points="5 12 12 5 19 12"/></svg>
                          </div>
                          <div style={{ fontSize: 11, fontWeight: 700, color: '#18222F', textTransform: 'uppercase', letterSpacing: 0.5 }}>{t.stockIn || 'Stock In'}</div>
                        </div>
                        <div style={{ fontSize: 22, fontWeight: 900, color: '#059669', marginBottom: 2 }}>+{addedInPeriod}</div>
                        <div style={{ fontSize: 10, fontWeight: 600, color: '#18222F', opacity: 0.7 }}>{_stPeriodLabel}</div>
                      </div>
                    </div>
                    {/* Stock Out card -soft rose (stock leaving inventory) */}
                    <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
                      <div style={{ position: 'absolute', inset: 0, transform: 'none', pointerEvents: 'none', borderRadius: 18, background: 'rgba(220,38,38,0.06)', zIndex: 0 }}></div>
                      <div style={{ position: 'absolute', inset: 0, transform: 'none', pointerEvents: 'none', borderRadius: 18, background: 'rgba(220,38,38,0.12)', zIndex: 1 }}></div>
                      <div style={{ position: 'relative', zIndex: 2, background: 'linear-gradient(135deg, #FFF1F2 0%, #FBD5D8 100%)', borderRadius: 18, padding: '16px 14px', border: '1px solid #FECACA' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                          <div style={{ width: 32, height: 32, borderRadius: 10, background: 'rgba(220,38,38,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2.5" strokeLinecap="round"><polyline points="12 5 12 19"/><polyline points="19 12 12 19 5 12"/></svg>
                          </div>
                          <div style={{ fontSize: 11, fontWeight: 700, color: '#18222F', textTransform: 'uppercase', letterSpacing: 0.5 }}>{t.stockOut || 'Stock Out'}</div>
                        </div>
                        <div style={{ fontSize: 22, fontWeight: 900, color: '#DC2626', marginBottom: 2 }}>-{soldInPeriod}</div>
                        <div style={{ fontSize: 10, fontWeight: 600, color: '#18222F', opacity: 0.7 }}>{_stPeriodLabel}</div>
                      </div>
                    </div>
                  </div>

                  {/* PRODUCTS NEEDING RESTOCK / GOOD-CONDITION BANNER */}
                  {stLowStock.length === 0 ? (
                    <div style={{ position: 'relative', background: 'var(--bg-white)', border: '1px solid var(--border-color)', borderRadius: 14, padding: '14px 18px 14px 22px', marginBottom: 4, overflow: 'hidden', boxShadow: '0 1px 3px rgba(15,23,42,0.04)' }}>
                      <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 5, background: '#FACC15', borderRadius: '14px 0 0 14px' }} />
                      <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 8, paddingBottom: 6, display: 'inline-block', borderBottom: '2px solid #A3E635' }}>{language === 'sw' ? 'Bidhaa Zenye Stock Nzuri' : 'Products Needing Restock'}</div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#16A34A' }}>{language === 'sw' ? 'Bidhaa zote ziko katika hali nzuri' : 'All products are in good condition'}</div>
                    </div>
                  ) : (
                    <div style={{ background: 'linear-gradient(135deg, #FFF8E8 0%, #FFE5A8 100%)', border: '1px solid #FCD34D', borderRadius: 14, padding: '14px 16px', marginBottom: 4 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                        <div style={{ width: 36, height: 36, borderRadius: 999, background: 'rgba(245,158,11,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#92400E" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 800, color: '#18222F' }}>{t.stockAlerts}</div>
                      </div>
                      {stLowStock.map(item => (
                        <div key={item.name} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: 'rgba(255,255,255,0.6)', borderRadius: 10, marginBottom: 6 }}>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: '#18222F' }}>{item.name}</div>
                            <div style={{ fontSize: 11, color: '#18222F', opacity: 0.65 }}>{t.minLabel}: {item.min}</div>
                          </div>
                          <span style={{ fontSize: 12, fontWeight: 700, color: '#DC2626' }}>{item.qty} {t.leftLabel}</span>
                        </div>
                      ))}
                    </div>
                  )}

                    </div>{/* end Section 1 content padding */}
                  </div>{/* END SECTION 1 */}

                  {/* ═══ SECTION 2 -Product List ═══ */}
                  <div style={{ background: 'var(--bg-white)', borderRadius: 16, overflow: 'hidden', marginBottom: 14, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', border: '0.5px solid var(--border-color)' }}>
                    {/* Section heading */}
                    <div style={{ padding: '12px 14px 4px' }}>
                      <h3 className="db-card-title" style={{ fontSize: 15, marginBottom: 0 }}>
                        {language === 'sw' ? 'Orodha ya Bidhaa' : 'Product List'}
                      </h3>
                    </div>
                    <div style={{ padding: 12 }}>

                  {/* CURRENT STOCK LIST -clean rows matching inspo */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {(stProducts || []).length === 0 ? (
                      <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13, background: 'var(--bg-tab)', borderRadius: 12 }}>{t.noDataForPeriod}</div>
                    ) : (stProducts || []).map(p => {
                      const isLow = p.qty <= p.min;
                      const openHistory = () => { setStockHistoryProduct(p.name); setStockHistoryFilter('month'); setStockHistoryFrom(''); setStockHistoryTo(''); };
                      return (
                        <div key={p.name} onClick={openHistory} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: 'var(--bg-white)', border: '1px solid var(--border-color)', borderRadius: 14, cursor: 'pointer', boxShadow: '0 1px 3px rgba(15,23,42,0.04)' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                              {language === 'sw' ? 'Idadi' : 'Qty'} <strong style={{ color: isLow ? '#DC2626' : '#059669' }}>{p.qty}</strong>
                              <span style={{ margin: '0 6px', color: 'var(--text-secondary)' }}>·</span>
                              <span style={{ color: 'var(--text-primary)' }}>{fmt(p.qty * p.unitCost)}</span>
                            </div>
                          </div>
                          <span style={{ fontSize: 11, fontWeight: 700, padding: '4px 12px', borderRadius: 999, background: isLow ? '#FEE2E2' : '#ECFDF5', color: isLow ? '#991B1B' : '#065F46', border: `1px solid ${isLow ? '#FECACA' : '#A7F3D0'}`, flexShrink: 0 }}>
                            {isLow ? t.stockLow : t.inStock}
                          </span>
                          <button onClick={(e) => { e.stopPropagation(); openHistory(); }} style={{ background: '#064E3B', color: '#FFFFFF', border: 'none', padding: '7px 14px', borderRadius: 999, fontSize: 11, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0, boxShadow: '0 2px 6px rgba(6,78,59,0.25)' }}>
                            {language === 'sw' ? 'Tazama ›' : 'View ›'}
                          </button>
                        </div>
                      );
                    })}
                  </div>

                    </div>{/* end Section 2 content padding */}
                  </div>{/* END SECTION 2 */}

                </div>
              );
            })()}
          </div>
        )}

        {activeTab === 'services' && (
          <div className="services-screen" style={{ background: 'var(--bg-tab)', minHeight: '100%', boxSizing: 'border-box', overflowX: 'hidden', paddingBottom: 80 }}>
            {!isDataReady ? (
              <div role="status" aria-label={t.loading || 'Loading'} style={{ padding: 16 }}>
                <div className="skeleton skeleton-title"></div>
                <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
                  {[1,2,3].map(i => <div key={i} className="skeleton skeleton-btn" style={{ flex: 1, width: 'auto' }}></div>)}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
                  <div className="skeleton skeleton-summary-card"></div>
                  <div className="skeleton skeleton-summary-card"></div>
                </div>
                <div className="skeleton skeleton-chart"></div>
                <div className="skeleton skeleton-table-header"></div>
                {[1,2,3,4,5].map(i => <div key={i} className="skeleton skeleton-row"></div>)}
              </div>
            ) : null}
            {isDataReady && (() => {
              const now = new Date();
              // ── Current period bounds ─────────────────────────────────
              // Period start → TODAY (never into the future), identical to the
              // dashboard, drilldowns and reports so every screen agrees.
              const _todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
              const _todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
              let from, to;
              if (serviceTimeFilter === 'today') {
                from = _todayStart; to = _todayEnd;
              } else if (serviceTimeFilter === 'week') {
                from = new Date(_todayStart); from.setDate(from.getDate() - 6); to = _todayEnd;
              } else if (serviceTimeFilter === 'month') {
                from = new Date(now.getFullYear(), now.getMonth(), 1); to = _todayEnd;
              } else if (serviceTimeFilter === 'year') {
                from = new Date(now.getFullYear(), 0, 1); to = _todayEnd;
              } else {
                from = serviceCustomFrom ? new Date(serviceCustomFrom) : new Date('1900-01-01');
                to = serviceCustomTo ? new Date(serviceCustomTo + 'T23:59:59') : new Date('2100-12-31');
              }
              const inPeriod = d => { const dt = new Date(d); return dt >= from && dt <= to; };
              // TWO ARRAYS, ONE SOURCE -same display/accounting split as the
              // Sales, Purchases and Expenses tabs.
              // filteredTx  = ACCOUNTING: void ALWAYS excluded, so every total,
              //               KPI and chart below is unchanged by the toggle.
              // displayTx   = HISTORY: void shown unless the user ticks
              //               "Hide voided", so a voided service is never invisible.
              const _periodTxAll = (serviceTransactions || []).filter(st => inPeriod(st.date));
              const filteredTx = _periodTxAll.filter(st => st.status !== 'void');
              const displayTx = _periodTxAll.filter(st => !hideVoidedServices || st.status !== 'void');

              // ── Previous period bounds (for trend) ─────────────────────
              let prevFrom, prevTo;
              if (serviceTimeFilter === 'today') {
                prevFrom = new Date(from); prevFrom.setDate(prevFrom.getDate() - 1);
                prevTo = new Date(to); prevTo.setDate(prevTo.getDate() - 1);
              } else if (serviceTimeFilter === 'week') {
                prevFrom = new Date(from); prevFrom.setDate(prevFrom.getDate() - 7);
                prevTo = new Date(to); prevTo.setDate(prevTo.getDate() - 7);
              } else if (serviceTimeFilter === 'month') {
                prevFrom = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                prevTo = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
              } else if (serviceTimeFilter === 'year') {
                prevFrom = new Date(now.getFullYear() - 1, 0, 1);
                prevTo = new Date(now.getFullYear() - 1, 11, 31, 23, 59, 59);
              }
              const inPrev = d => prevFrom && prevTo ? (new Date(d) >= prevFrom && new Date(d) <= prevTo) : false;
              const prevTx = (serviceTransactions || []).filter(st => inPrev(st.date) && st.status !== 'void');

              // ── Metrics ───────────────────────────────────────────────
              const periodRevenue = filteredTx.reduce((s, st) => s + (st.totalRevenue || 0), 0);
              const prevRevenue = prevTx.reduce((s, st) => s + (st.totalRevenue || 0), 0);
              const revTrend = prevRevenue > 0 ? ((periodRevenue - prevRevenue) / prevRevenue) * 100 : null;

              const uniqueClients = (() => {
                const named = filteredTx.filter(st => st.customerName && st.customerName.trim());
                if (named.length > 0) return new Set(named.map(st => st.customerName.trim().toLowerCase())).size;
                return filteredTx.length;
              })();
              const prevClients = (() => {
                const named = prevTx.filter(st => st.customerName && st.customerName.trim());
                if (named.length > 0) return new Set(named.map(st => st.customerName.trim().toLowerCase())).size;
                return prevTx.length;
              })();
              const clientTrend = prevClients > 0 ? ((uniqueClients - prevClients) / prevClients) * 100 : null;

              const avgPerJob = filteredTx.length > 0 ? periodRevenue / filteredTx.length : 0;
              const prevAvg = prevTx.length > 0 ? prevRevenue / prevTx.length : 0;
              const avgTrend = prevAvg > 0 ? ((avgPerJob - prevAvg) / prevAvg) * 100 : null;

              // ── Top services by revenue & profit ─────────────────────
              const svcRevenueMap = {};
              const svcProfitMap = {};
              const svcCountMap = {};
              filteredTx.forEach(st => {
                const n = st.serviceName;
                if (!svcRevenueMap[n]) { svcRevenueMap[n] = 0; svcProfitMap[n] = 0; svcCountMap[n] = 0; }
                svcRevenueMap[n] += st.totalRevenue || 0;
                svcProfitMap[n] += st.profit || 0;
                svcCountMap[n] += 1;
              });
              const topByRevenue = Object.entries(svcRevenueMap).sort((a, b) => b[1] - a[1]).slice(0, 5);
              const topByProfit = Object.entries(svcProfitMap).sort((a, b) => b[1] - a[1]).slice(0, 5);

              // ── Top clients ──────────────────────────────────────────
              const clientMap = {};
              filteredTx.filter(st => st.customerName && st.customerName.trim()).forEach(st => {
                const key = st.customerName.trim();
                if (!clientMap[key]) clientMap[key] = { name: key, revenue: 0, visits: 0, services: {} };
                clientMap[key].revenue += st.totalRevenue || 0;
                clientMap[key].visits += 1;
                clientMap[key].services[st.serviceName] = (clientMap[key].services[st.serviceName] || 0) + 1;
              });
              const topClients = Object.values(clientMap).sort((a, b) => b.visits - a.visits).slice(0, 5);
              const hasClientData = topClients.length > 0;

              const periodLabel = serviceTimeFilter === 'today' ? (t.yesterday || 'yesterday') : serviceTimeFilter === 'week' ? (t.lastWeek || 'last week') : serviceTimeFilter === 'month' ? (t.lastMonth || 'last month') : serviceTimeFilter === 'year' ? (t.lastYear || 'last year') : null;
              const currentPeriodName = serviceTimeFilter === 'today' ? (t.today || 'Today') : serviceTimeFilter === 'week' ? (t.thisWeek || 'This Week') : serviceTimeFilter === 'month' ? (t.thisMonth || 'This Month') : serviceTimeFilter === 'year' ? (t.thisYear || 'This Year') : (t.custom || 'Custom');

              return (
                <>
                  {/* ═══ REPORT FULL SCREEN (matches Sales/Purchases/Expenses pattern) ═══ */}
                  {showServiceReports && (() => {
                    // Apply user filters on top of the FULL period slice
                    // (_periodTxAll, voided included). Deriving this from
                    // filteredTx would strip voided rows before the toggle below
                    // ever ran, so "Show voided" could never reveal them.
                    // Accounting totals below still read acctReportTx, which
                    // always excludes void -so no figure is affected.
                    const reportTx = _periodTxAll
                      .filter(st => !serviceReportServiceFilter || st.serviceName === serviceReportServiceFilter)
                      .filter(st => !serviceReportPaymentFilter || st.paymentMethod === serviceReportPaymentFilter || (st.payments || []).some(p => (p.accountKey || p.type) === serviceReportPaymentFilter))
                      .filter(st => !serviceReportCustomerFilter || st.customerName === serviceReportCustomerFilter);
                    // Accounting totals ALWAYS exclude void.
                    const acctReportTx = reportTx.filter(st => st.status !== 'void');
                    const reportTotal = acctReportTx.reduce((s, x) => s + parseFloat(x.totalRevenue || 0), 0);
                    // Credit = amount still owed per service transaction (total
                    // minus real payments), not the full value of a partial.
                    const _svcOutstanding = (x) => {
                      if (x.isPaid !== false) return 0;
                      const total = parseFloat(x.totalRevenue || 0);
                      const cashPaid = Array.isArray(x.payments) ? x.payments.filter(p => p.type !== 'credit').reduce((a, p) => a + parseFloat(p.amount || 0), 0) : 0;
                      return Math.max(0, total - cashPaid);
                    };
                    const creditTotal = acctReportTx.reduce((s, x) => s + _svcOutstanding(x), 0);
                    const reportProfit = periodProfitExVAT(acctReportTx); // ex-output-VAT
                    const sortedTx = [...reportTx].sort((a, b) => new Date(b.date) - new Date(a.date)).filter(st => !hideVoidedServices || st.status !== 'void');

                    // Download formats
                    const srvH = ['Date','Service','Qty','Selling Price','Cost','Total Revenue','Profit','Customer','Payment','Status','Invoice No.'];
                    const srvR = () => {
                      const rows = [];
                      sortedTx.forEach(st => {
                        const pmts = (st.payments || []).filter(p => p.amount && parseFloat(p.amount) > 0 && p.type !== 'credit');
                        const totalPaid = pmts.reduce((a, p) => a + parseFloat(p.amount || 0), 0);
                        const isPartial = st.isPaid === false && totalPaid > 0;
                        const payLabel = st.isPaid === false && totalPaid === 0 ? 'Credit' : isPartial ? 'Partial Pmt' : 'Paid in Full';
                        const status = st.isPaid === false ? 'Unpaid' : 'Paid';
                        const items = st.items && st.items.length > 0 ? st.items : [{ serviceName: st.serviceName, productName: st.serviceName, quantity: st.quantity, price: st.price, sellingPrice: st.price, cost: st.cost }];
                        items.forEach((item, idx) => {
                          rows.push([
                            st.date,
                            item.serviceName || item.productName || st.serviceName || '—',
                            item.quantity || st.quantity || '',
                            item.price || item.sellingPrice || st.price || '',
                            item.cost || st.cost || '',
                            idx === 0 ? parseFloat(st.totalRevenue || 0).toFixed(2) : '',
                            idx === 0 ? grossProfitExVAT(st).toFixed(2) : '',
                            st.customerName || '—',
                            idx === 0 ? payLabel : '',
                            idx === 0 ? status : '',
                            st.invoiceNumber || ''
                          ]);
                        });
                      });
                      return rows;
                    };
                    const fromD = serviceCustomFrom ? new Date(serviceCustomFrom).toLocaleDateString() : (from ? from.toLocaleDateString() : '—');
                    const toD = serviceCustomTo ? new Date(serviceCustomTo).toLocaleDateString() : (to ? to.toLocaleDateString() : '—');
                    return (
                      <div style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'var(--bg-tab)', display: 'flex', flexDirection: 'column' }}>
                        {/* ── HEADER: back + title + download buttons ── */}
                        <div style={{ background: 'var(--bg-white)', borderBottom: '1px solid var(--border-color)', padding: '14px 16px 10px', flexShrink: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
                            <button onClick={() => setShowServiceReports(false)} style={{ background: 'none', border: 'none', padding: '4px 10px 4px 0', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#111827" strokeWidth="2.5" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
                            </button>
                            <h2 style={{ flex: 1, fontSize: 18, fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>{language === 'sw' ? 'Ripoti za Huduma' : 'Services Report'}</h2>
                          </div>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button onClick={() => downloadPDF(srvH, srvR(), 'Services Report', 'services-report.pdf')} style={{ flex: 1, padding: '9px 4px', borderRadius: 10, background: '#EF4444', color: '#fff', border: 'none', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>{t.downloadPDF || 'Download PDF'}</button>
                            <button onClick={() => downloadCSV([srvH, ...srvR()], 'services-report.csv')} style={{ flex: 1, padding: '9px 4px', borderRadius: 10, background: '#059669', color: '#fff', border: 'none', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>{t.downloadCSV || 'Download CSV'}</button>
                            <button onClick={() => {
                              const ordersHeader = ['Date','Invoice','Customer','Item Count','Total Qty','Subtotal','Discount %','Tax','Grand Total','Profit','Payment','Status'];
                              const ordersRows = sortedTx.map(st => {
                                const items = Array.isArray(st.items) && st.items.length > 0 ? st.items : [{ serviceName: st.serviceName, quantity: st.quantity, price: st.price }];
                                const qtyT = items.reduce((sum, it) => sum + parseFloat(it.quantity || 0), 0);
                                const sub = items.reduce((sum, it) => sum + parseFloat(it.quantity || 0) * parseFloat(it.price || it.sellingPrice || 0), 0);
                                const discPct = parseFloat(st.discount || 0);
                                const grand = parseFloat(st.totalRevenue || 0);
                                const taxAmt = Math.max(0, grand - sub * (1 - discPct / 100));
                                return [st.date, st.invoiceNumber || '—', st.customerName || 'Walk-in', items.length, qtyT, sub.toFixed(2), discPct, taxAmt.toFixed(2), grand.toFixed(2), grossProfitExVAT(st).toFixed(2), st.paymentMethod || 'cash', st.isPaid === false ? 'Unpaid' : 'Paid'];
                              });
                              const itemsHeader = ['Date','Invoice','Customer','Service','Qty','Unit Price','Line Total','Cost','Order Grand Total','Payment','Status'];
                              const itemsRows = sortedTx.flatMap(st => {
                                const items = Array.isArray(st.items) && st.items.length > 0 ? st.items : [{ serviceName: st.serviceName, quantity: st.quantity, price: st.price, cost: st.cost }];
                                const grand = parseFloat(st.totalRevenue || 0);
                                return items.map(it => {
                                  const qty = parseFloat(it.quantity || 0);
                                  const price = parseFloat(it.price || it.sellingPrice || 0);
                                  return [st.date, st.invoiceNumber || '—', st.customerName || 'Walk-in', it.serviceName || it.productName || '—', qty, price.toFixed(2), (qty * price).toFixed(2), parseFloat(it.cost || 0).toFixed(2), grand.toFixed(2), st.paymentMethod || 'cash', st.isPaid === false ? 'Unpaid' : 'Paid'];
                                });
                              });
                              downloadExcelMultiSheet([
                                { name: 'Orders', rows: [ordersHeader, ...ordersRows] },
                                { name: 'Line Items', rows: [itemsHeader, ...itemsRows] },
                              ], 'services-report.xlsx');
                            }} style={{ flex: 1, padding: '9px 4px', borderRadius: 10, background: '#2563EB', color: '#fff', border: 'none', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>{t.downloadExcel || 'Download Excel'}</button>
                          </div>
                        </div>
                        {/* ── PERIOD SELECTOR (matches Sales tab style) ── */}
                        <div style={{ background: 'var(--bg-white)', padding: '12px 16px 8px', flexShrink: 0 }}>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {[{key:'today',label:t.today},{key:'week',label:t.week},{key:'month',label:t.month},{key:'year',label:t.year || t.thisYear || 'Year'},{key:'custom',label:t.custom}].map(({key,label}) => (
                              <button key={key} onClick={() => {
                                setServiceTimeFilter(key);
                                if (key !== 'custom') {
                                  // H5: fill the date inputs with the shared to-date range.
                                  const r = getPeriodRange(key);
                                  if (r.from && r.to) { setServiceCustomFrom(r.from); setServiceCustomTo(r.to); }
                                }
                              }}
                                style={{ flex: 1, padding: '10px 6px', borderRadius: 16, fontSize: 12, fontWeight: serviceTimeFilter === key ? 700 : 600, cursor: 'pointer', border: 'none', whiteSpace: 'nowrap',
                                  background: serviceTimeFilter === key ? 'var(--bg-tab-active)' : 'var(--bg-tab-inactive)',
                                  color: serviceTimeFilter === key ? 'var(--text-tab-active)' : 'var(--text-tab-inactive)' }}>
                                {label}
                              </button>
                            ))}
                          </div>
                          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>{fromD} - {toD}</div>
                        </div>
                        {/* ── CUSTOM DATE PICKERS ── */}
                        {serviceTimeFilter === 'custom' && (
                          <div style={{ background: 'var(--bg-white)', padding: '0 16px 12px', borderBottom: '1px solid var(--border-color)', display: 'flex', gap: 10, flexShrink: 0 }}>
                            <div style={{ flex: 1 }}>
                              <label style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>{t.startDate || 'From'}</label>
                              <input type="date" value={serviceCustomFrom} onFocus={e => { try { e.currentTarget.showPicker(); } catch(x){} }} onChange={e => setServiceCustomFrom(e.target.value)} style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid var(--border-color)', background: 'var(--bg-tab)', fontSize: 14, boxSizing: 'border-box' }} />
                            </div>
                            <div style={{ flex: 1 }}>
                              <label style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>{t.endDate || 'To'}</label>
                              <input type="date" value={serviceCustomTo} onFocus={e => { try { e.currentTarget.showPicker(); } catch(x){} }} onChange={e => setServiceCustomTo(e.target.value)} style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid var(--border-color)', background: 'var(--bg-tab)', fontSize: 14, boxSizing: 'border-box' }} />
                            </div>
                          </div>
                        )}
                        {/* ── FILTERS stacked vertically ── */}
                        <div style={{ background: 'var(--bg-white)', padding: '10px 16px 12px', borderBottom: '1px solid var(--border-color)', flexShrink: 0 }}>
                          <select value={serviceReportServiceFilter} onChange={e => setServiceReportServiceFilter(e.target.value)} style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: 'none', background: 'var(--bg-tab)', fontSize: 14, marginBottom: 8, boxSizing: 'border-box' }}>
                            <option value="">{language === 'sw' ? 'Chuja kwa huduma' : 'Filter by service'}</option>
                            {[...new Set((serviceTransactions || []).map(st => st.serviceName).filter(Boolean))].map(n => <option key={n} value={n}>{n}</option>)}
                          </select>
                          <select value={serviceReportPaymentFilter} onChange={e => setServiceReportPaymentFilter(e.target.value)} style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: 'none', background: 'var(--bg-tab)', fontSize: 14, marginBottom: 8, boxSizing: 'border-box' }}>
                            <option value="">{t.filterByPayment || 'Filter by payment'}</option>
                            {buildPaymentOptions(true).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </select>
                          <select value={serviceReportCustomerFilter} onChange={e => setServiceReportCustomerFilter(e.target.value)} style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: 'none', background: 'var(--bg-tab)', fontSize: 14, boxSizing: 'border-box' }}>
                            <option value="">{t.filterByCustomer || 'Filter by customer'}</option>
                            {[...new Set((serviceTransactions || []).map(st => st.customerName).filter(Boolean))].map(n => <option key={n} value={n}>{n}</option>)}
                          </select>
                        </div>
                        {/* ── SUMMARY CARD ── */}
                        <div style={{ background: 'var(--bg-white)', padding: '10px 16px', borderBottom: '1px solid var(--border-color)', display: 'flex', gap: 10, flexShrink: 0, flexWrap: 'wrap' }}>
                          <div className="pastel-card pastel-card--sage">
                            <div className="pc-label">{language === 'sw' ? 'Mapato' : 'Total Revenue'}</div>
                            <div className="pc-value">{fmt(reportTotal)}</div>
                          </div>
                          {!isItemHidden('services', 'profitColumn') && (
                          <div className="pastel-card pastel-card--mint">
                            <div className="pc-label">{language === 'sw' ? 'Faida' : 'Profit'}</div>
                            <div className="pc-value">{fmt(reportProfit)}</div>
                          </div>
                          )}
                          {creditTotal > 0 && (
                            <div className="pastel-card pastel-card--amber">
                              <div className="pc-label">{t.creditSales || 'Credit'}</div>
                              <div className="pc-value">{fmt(creditTotal)}</div>
                            </div>
                          )}
                        </div>
                        {/* ── TRANSACTION TABLE ── */}
                        {(() => {
                          const thS = { padding: '8px 12px', textAlign: 'left', fontWeight: 700, color: 'var(--text-primary)', fontSize: 12, borderBottom: '2px solid #D1D5DB', whiteSpace: 'nowrap' };
                          const tdS = { padding: '8px 12px', color: 'var(--text-primary)', fontSize: 12, verticalAlign: 'middle', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
                          const editBtnS = { background: 'var(--bg-input)', color: 'var(--text-secondary)', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 13, marginRight: 3 };
                          const delBtnS  = { background: '#FEE2E2', color: '#DC2626', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 13 };
                          const getServiceCell = (rec) => {
                            const items = rec.items;
                            if (items && items.length > 0) {
                              const label = items.map(i => `${i.serviceName || i.productName || '—'}${items.length > 1 ? ` ×${i.quantity || 1}` : ''}`).join(', ');
                              return <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180 }}>{label}</span>;
                            }
                            return <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 110 }}>{rec.serviceName || '—'}</span>;
                          };
                          return (
                            <div style={{ flex: 1, overflowY: 'auto', overflowX: 'auto', WebkitOverflowScrolling: 'touch', padding: '0 12px' }}>
                              {sortedTx.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--text-muted)', fontSize: 14 }}>{t.noDataForPeriod || 'No data for this period'}</div>
                              ) : (<>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 4px' }}>
                                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{language === 'sw' ? 'Bofya kitufe cha View kuona maelezo kamili.' : 'Click the View button to view full transaction details.'}</div>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--text-muted)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                    <input type="checkbox" checked={hideVoidedServices} onChange={e => setHideVoidedServices(e.target.checked)} style={{ accentColor: '#92400E' }} />
                                    {language === 'sw' ? 'Ficha zilizobatilishwa' : 'Hide voided'}
                                  </label>
                                </div>
                                <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%', tableLayout: 'auto' }}>
                                  <thead>
                                    <tr style={{ background: 'var(--bg-tab)', position: 'sticky', top: 0, zIndex: 1 }}>
                                      <th style={{ ...thS, fontSize: 10 }}>Date</th>
                                      <th style={{ ...thS, fontSize: 10 }}>Cust.</th>
                                      <th style={{ ...thS, fontSize: 10 }}>Service</th>
                                      <th style={{ ...thS, textAlign: 'right', fontSize: 10 }}>Qty</th>
                                      <th style={{ ...thS, textAlign: 'right', fontSize: 10 }}>Amt</th>
                                      <th style={{ ...thS, textAlign: 'center', fontSize: 10 }}>Pay</th>
                                      <th style={{ ...thS, textAlign: 'center', fontSize: 10 }}>Act</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {sortedTx.map((st, i) => {
                                      const shortDate = st.date ? st.date.slice(2).replace(/-/g, '/') : '—';
                                      const totalQty = st.items && st.items.length > 1
                                        ? st.items.reduce((s, it) => s + parseFloat(it.quantity || 0), 0)
                                        : (st.quantity || '—');
                                      return (
                                        <tr key={st.id} style={{ background: i % 2 === 0 ? 'var(--bg-white)' : 'var(--bg-tab)', borderBottom: '1px solid var(--border-color)', opacity: st.status === 'void' ? 0.5 : 1, textDecoration: st.status === 'void' ? 'line-through' : 'none', borderLeft: st.status === 'void' ? '3px solid #9CA3AF' : '3px solid transparent' }}>
                                          <td style={{ ...tdS }}>{shortDate}{st.status === 'void' && <span style={{ marginLeft: 4, fontSize: 8, fontWeight: 800, padding: '1px 4px', borderRadius: 3, background: '#9CA3AF', color: '#fff', verticalAlign: 'middle', textDecoration: 'none', display: 'inline-block' }}>VOIDED</span>}</td>
                                          <td style={{ ...tdS }}>{st.customerName || 'Walk-in'}</td>
                                          <td style={{ ...tdS }}>{getServiceCell(st)}</td>
                                          <td style={{ ...tdS, textAlign: 'right' }}>{totalQty}</td>
                                          <td style={{ ...tdS, textAlign: 'right', fontWeight: 700, color: '#059669' }}>{fmt(st.totalRevenue || 0)}</td>
                                          <td style={{ ...tdS, textAlign: 'center' }}>
                                            {(() => {
                                              const pmts = (st.payments || []).filter(p => p.amount && parseFloat(p.amount) > 0 && p.type !== 'credit');
                                              const totalPaid = pmts.reduce((s, p) => s + parseFloat(p.amount || 0), 0);
                                              const isPartial = st.isPaid === false && totalPaid > 0;
                                              const isUnpaid = st.isPaid === false && totalPaid === 0;
                                              const label = isUnpaid ? 'Credit' : isPartial ? 'Partial Pmt' : 'Paid in Full';
                                              const bg = isUnpaid ? '#FEF3C7' : isPartial ? '#E0E7FF' : '#D1FAE5';
                                              const color = isUnpaid ? '#92400E' : isPartial ? '#3730A3' : '#065F46';
                                              return <span style={{ fontSize: 9, fontWeight: 600, padding: '2px 4px', borderRadius: 4, background: bg, color }}>{label}</span>;
                                            })()}
                                          </td>
                                          <td style={{ ...tdS, textAlign: 'center', whiteSpace: 'nowrap' }}>
                                            <button onClick={() => setViewTxnModal({ open: true, kind: 'service', record: st })} style={{ background: 'var(--bg-input)', color: 'var(--text-secondary)', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 13, marginRight: 3 }} title={language === 'sw' ? 'Tazama' : 'View'}>View</button>
                                            <button onClick={() => handleServiceTransactionEdit(st)} style={editBtnS}>✎️</button>
                                            {st.status !== 'void' && <button onClick={() => handleVoidServiceTransaction(st.id)} style={{ ...delBtnS, background: '#FEF3C7', color: '#92400E' }} title={language === 'sw' ? 'Batilisha' : 'Void'}>⊘</button>}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </>)}
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })()}

                  {/* ═══ MAIN VIEW ═══ */}
                  {!showServiceReports && (
                    <div style={{ padding: 16 }}>

                      {/* HEADER */}
                      <h1 style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 12px' }}>{t.services || 'Services'}</h1>

                      {/* TOP ACTION BUTTONS -neon for Record, emerald for Invoices */}
                      <div style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
                        <button onClick={() => setShowServiceTransactionForm(true)}
                          style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, height: 42, borderRadius: 26, background: '#A3E635', color: '#18222F', border: 'none', fontWeight: 700, fontSize: 12, cursor: 'pointer', boxShadow: '0 4px 14px rgba(163,230,53,0.35)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          + {t.recordService || 'Record Service'}
                        </button>
                        <button onClick={() => setShowServiceReports(true)}
                          style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, height: 42, borderRadius: 26, background: '#ECFDF5', color: '#065F46', border: '1.5px solid #A7F3D0', fontWeight: 600, fontSize: 12, cursor: 'pointer', boxShadow: '0 1px 3px rgba(15,23,42,0.04)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {t.salesReports ? t.salesReports.replace(/Mauzo|Sales/i, t.services || 'Services') : 'Reports'}
                        </button>
                        <button onClick={() => { setInvoiceMgmtContext('services'); setShowInvoiceManagement(true); }}
                          style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, height: 42, borderRadius: 26, background: '#047857', color: '#FFFFFF', border: 'none', fontWeight: 700, fontSize: 12, cursor: 'pointer', boxShadow: '0 4px 14px rgba(4,120,87,0.35)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/></svg>
                          {t.invoices || 'Invoices'}
                        </button>
                      </div>

                      {/* ═══ SECTION 1 -Services Overview ═══ */}
                      <div style={{ background: 'var(--bg-white)', borderRadius: 16, overflow: 'hidden', marginBottom: 14, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', border: '0.5px solid var(--border-color)' }}>
                        {/* Section heading */}
                        <div style={{ padding: '12px 14px 4px' }}>
                          <h3 className="db-card-title" style={{ fontSize: 15, marginBottom: 0 }}>
                            {language === 'sw' ? 'Muhtasari wa Huduma' : 'Services Overview'}
                          </h3>
                        </div>
                        <div style={{ padding: 12 }}>

                      {/* PERIOD SELECTOR -pill container */}
                      <div style={{ background: 'var(--bg-tab)', borderRadius: 16, padding: 4, display: 'flex', gap: 2, marginBottom: 8 }}>
                        {[
                          { key: 'today', label: t.today },
                          { key: 'week', label: t.thisWeek },
                          { key: 'month', label: t.thisMonth },
                          { key: 'year', label: t.thisYear },
                          { key: 'custom', label: t.custom }
                        ].map(f => (
                          <button key={f.key} onClick={() => {
                            setServiceTimeFilter(f.key);
                            if (f.key === 'custom' && !serviceCustomFrom && !serviceCustomTo) {
                              const n = new Date(); const y = n.getFullYear(); const m = String(n.getMonth()+1).padStart(2,'0');
                              setServiceCustomFrom(`${y}-${m}-01`);
                              setServiceCustomTo(`${y}-${m}-${String(new Date(y, n.getMonth()+1, 0).getDate()).padStart(2,'0')}`);
                            }
                          }}
                            style={{ flex: 1, padding: '10px 6px', borderRadius: 16, fontSize: 12, fontWeight: serviceTimeFilter === f.key ? 700 : 600, cursor: 'pointer', border: 'none', whiteSpace: 'nowrap',
                              background: serviceTimeFilter === f.key ? 'var(--bg-tab-active)' : 'var(--bg-tab-inactive)',
                              color: serviceTimeFilter === f.key ? 'var(--text-tab-active)' : 'var(--text-tab-inactive)',
                              boxShadow: 'none' }}>
                            {f.label}
                          </button>
                        ))}
                      </div>

                      {/* PERIOD DISPLAY LABEL OR CUSTOM DATE RANGE */}
                      {serviceTimeFilter === 'custom' ? (
                        <div className="db-custom-date-card">
                          <div className="db-custom-date-row">
                            <div className="db-custom-date-col">
                              <span className="db-custom-date-label">{t.fromDate}</span>
                              <input type="date" className="db-custom-date-input" value={serviceCustomFrom} onChange={e => setServiceCustomFrom(e.target.value)} />
                            </div>
                            <div className="db-custom-date-col">
                              <span className="db-custom-date-label">{t.toDate}</span>
                              <input type="date" className="db-custom-date-input" value={serviceCustomTo} onChange={e => setServiceCustomTo(e.target.value)} />
                            </div>
                          </div>
                        </div>
                      ) : (() => {
                        const _n = new Date(); const _p = n => String(n).padStart(2,'0'); const _d = d => `${_p(d.getDate())}/${_p(d.getMonth()+1)}/${d.getFullYear()}`;
                        let lbl = '';
                        if (serviceTimeFilter === 'today') lbl = _d(_n);
                        else if (serviceTimeFilter === 'week') { const ws = new Date(_n); ws.setDate(_n.getDate()-6); lbl = `${_d(ws)} – ${_d(_n)}`; }
                        else if (serviceTimeFilter === 'month') { const mn = (t.monthNames||['January','February','March','April','May','June','July','August','September','October','November','December'])[_n.getMonth()]; const ms = new Date(_n.getFullYear(), _n.getMonth(), 1); lbl = `${mn} ${_n.getFullYear()} · ${_d(ms)} – ${_d(_n)}`; }
                        else if (serviceTimeFilter === 'year') { const ys = new Date(_n.getFullYear(), 0, 1); lbl = `${_n.getFullYear()} · ${_d(ys)} – ${_d(_n)}`; }
                        return lbl ? <div className="period-date-label">{lbl}</div> : <div style={{ marginBottom: 8 }} />;
                      })()}

                      {/* 2 SUMMARY CARDS -equal-height matching Sales tab pattern */}
                      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'stretch' }}>
                    {/* Revenue card */}
                    <div style={{ flex: 1, minWidth: 0, position: 'relative', display: 'flex' }}>
                      <div style={{ position: 'absolute', inset: 0, transform: 'none', pointerEvents: 'none', borderRadius: 18, background: 'rgba(5,150,105,0.08)', zIndex: 0 }}></div>
                      <div style={{ position: 'absolute', inset: 0, transform: 'none', pointerEvents: 'none', borderRadius: 18, background: 'rgba(5,150,105,0.15)', zIndex: 1 }}></div>
                      <div style={{ position: 'relative', zIndex: 2, background: 'linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)', borderRadius: 18, padding: '16px 14px', border: '1px solid #A7F3D0', flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                          <div style={{ width: 32, height: 32, borderRadius: 10, background: 'rgba(5,150,105,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" strokeLinecap="round"><polyline points="12 19 12 5"/><polyline points="5 12 12 5 19 12"/></svg>
                          </div>
                          <div style={{ fontSize: 11, fontWeight: 700, color: '#18222F', textTransform: 'uppercase', letterSpacing: 0.5 }}>{t.revenue || 'Revenue'}</div>
                        </div>
                        <div style={{ fontSize: 'clamp(15px, 5.5vw, 22px)', fontWeight: 900, color: '#059669', marginBottom: 2, flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{fmt(periodRevenue)}</div>
                        <div style={{ fontSize: 10, fontWeight: 600, color: '#18222F', opacity: 0.7 }}>
                          {revTrend === null ? currentPeriodName : `${revTrend >= 0 ? '▲' : '▼'} ${Math.abs(revTrend).toFixed(0)}% vs ${periodLabel}`}
                        </div>
                      </div>
                    </div>
                    {/* No. of Transactions card */}
                    <div style={{ flex: 1, minWidth: 0, position: 'relative', display: 'flex' }}>
                      <div style={{ position: 'absolute', inset: 0, transform: 'none', pointerEvents: 'none', borderRadius: 18, background: 'rgba(5,150,105,0.08)', zIndex: 0 }}></div>
                      <div style={{ position: 'absolute', inset: 0, transform: 'none', pointerEvents: 'none', borderRadius: 18, background: 'rgba(5,150,105,0.15)', zIndex: 1 }}></div>
                      <div style={{ position: 'relative', zIndex: 2, background: 'linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)', borderRadius: 18, padding: '16px 14px', border: '1px solid #A7F3D0', flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                          <div style={{ width: 32, height: 32, borderRadius: 10, background: 'rgba(5,150,105,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" strokeLinecap="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                          </div>
                          <div style={{ fontSize: 11, fontWeight: 700, color: '#18222F', textTransform: 'uppercase', letterSpacing: 0.5 }}>{language === 'sw' ? 'Idadi ya Miamala' : 'No. of Transactions'}</div>
                        </div>
                        <div style={{ fontSize: 'clamp(15px, 5.5vw, 22px)', fontWeight: 900, color: '#059669', marginBottom: 2, flex: 1, whiteSpace: 'nowrap' }}>{filteredTx.length}</div>
                        <div style={{ fontSize: 10, fontWeight: 600, color: '#18222F', opacity: 0.7 }}>{currentPeriodName}</div>
                      </div>
                    </div>
                  </div>

                  {/* INCOME BY SERVICE -horizontal bar chart (like Sales by Product) */}
                  <div style={{ background: 'var(--bg-white)', borderRadius: 16, padding: 16, marginBottom: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 16 }}>{t.incomeByService || 'Income by Service'}</div>
                    {topByRevenue.length === 0 ? (
                      <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 13, padding: '24px 0' }}>{t.noDataForPeriod || 'No data for this period'}</div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {topByRevenue.map(([name, rev], i) => {
                          const maxRev = topByRevenue[0][1];
                          const pct = maxRev > 0 ? (rev / maxRev) * 100 : 0;
                          // Services tab single-color palette -emerald family (revenue/income)
                          const SVC_COLORS = ['#10B981','#059669','#34D399','#047857','#6EE7B7','#065F46'];
                          const barColor = SVC_COLORS[i % SVC_COLORS.length];
                          return (
                            <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <span style={{ width: 90, fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 0 }}>{name}</span>
                              <div style={{ flex: 1, height: 22, background: 'var(--bg-input, #F3F4F6)', borderRadius: 6, overflow: 'hidden', position: 'relative' }}>
                                <div style={{ height: '100%', width: `${pct}%`, background: barColor, borderRadius: 6, transition: 'width 0.6s ease' }}></div>
                              </div>
                              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', minWidth: 60, textAlign: 'right', flexShrink: 0 }}>{fmt(rev)}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* TOP-SELLING SERVICES -numbered list matching Sales tab Top Products */}
                  <div style={{ background: 'var(--bg-white)', borderRadius: 16, padding: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: 14 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 14 }}>★ {language === 'sw' ? 'Huduma Zinazoongoza' : 'Top-Selling Services'}</div>
                    {topByRevenue.length === 0 ? (
                      <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 13, padding: '16px 0' }}>{t.recordServicesToSeeRankings || 'Record services to see rankings'}</div>
                    ) : (() => {
                      const totalSvcRev = topByRevenue.reduce((s, [, v]) => s + v, 0);
                      return topByRevenue.map(([name, rev], i) => {
                        const units = svcCountMap[name] || 0;
                        const pct = totalSvcRev > 0 ? Math.round((rev / totalSvcRev) * 100) : 0;
                        return (
                          <div key={name} onClick={() => setServiceHistoryName(name)} style={{ display: 'flex', alignItems: 'center', gap: 10, paddingBottom: i < topByRevenue.length - 1 ? 12 : 0, marginBottom: i < topByRevenue.length - 1 ? 12 : 0, borderBottom: i < topByRevenue.length - 1 ? '1px solid var(--border-color)' : 'none', cursor: 'pointer' }}>
                            <div style={{ width: 28, height: 28, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0,
                              background: i === 0 ? '#FCD34D' : i === 1 ? 'var(--border-default)' : i === 2 ? '#FED7AA' : 'var(--bg-tab)', color: i === 0 || i === 2 ? '#7C2D12' : 'var(--text-primary)' }}>{i + 1}</div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
                              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{units} {language === 'sw' ? 'mara' : 'units'}</div>
                            </div>
                            <div style={{ textAlign: 'right', flexShrink: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: 700, color: '#059669' }}>{fmt(rev)}</div>
                              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{pct}%</div>
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>

                  {/* CUSTOMER INSIGHTS -always visible */}
                  <div style={{ background: 'var(--bg-white)', borderRadius: 16, padding: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: 14 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>{t.clientInsights || 'Client Insights'}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>{t.whoComesBackMost || 'Who comes back most & what they book'}</div>
                    {!hasClientData ? (
                      <div style={{ background: '#F5F3FF', borderRadius: 12, padding: '10px 14px', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                        <span style={{ fontSize: 20 }}>◆</span>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: '#3730A3', marginBottom: 3 }}>{t.startTrackingClients || 'Start tracking your clients'}</div>
                          <div style={{ fontSize: 12, color: '#4F46E5', lineHeight: 1.5 }}>{t.startTrackingClientsDesc || "When you record a service, add the client's name. This section will show you who visits most, how much they spend, and which services they prefer."}</div>
                        </div>
                      </div>
                    ) : (
                      topClients.map((cl, idx) => {
                        const favService = Object.entries(cl.services).sort((a,b) => b[1]-a[1])[0];
                        return (
                          <div key={cl.name} style={{ display: 'flex', alignItems: 'center', gap: 10, paddingBottom: idx < topClients.length - 1 ? 12 : 0, marginBottom: idx < topClients.length - 1 ? 12 : 0, borderBottom: idx < topClients.length - 1 ? '1px solid #F3F4F6' : 'none' }}>
                            <div style={{ width: 38, height: 38, borderRadius: 12, background: '#F5F3FF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 15, color: '#4F46E5', flexShrink: 0 }}>
                              {cl.name.charAt(0).toUpperCase()}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cl.name}</div>
                              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>
                                {cl.visits} {cl.visits === 1 ? (t.visitSingular || 'visit') : (t.visitPlural || 'visits')}
                                {favService ? <span style={{ color: '#4F46E5' }}> · {t.prefers || 'prefers'} {favService[0]}</span> : null}
                              </div>
                            </div>
                            <div style={{ textAlign: 'right', flexShrink: 0 }}>
                              <div style={{ fontWeight: 700, fontSize: 13, color: '#10B981' }}>{fmt(cl.revenue)}</div>
                              {cl.visits >= 2 && <div style={{ fontSize: 10, color: '#10B981', fontWeight: 600 }}>↩ {t.repeatClient || 'Repeat'}</div>}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>

                  {/* RECENT TRANSACTIONS */}
                  <div style={{ background: 'var(--bg-white)', borderRadius: 16, padding: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, gap: 8, flexWrap: 'wrap' }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{t.recentActivity || 'Recent Transactions'}</div>
                      {/* DISPLAY-ONLY toggle. Never affects any total above. */}
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', cursor: 'pointer' }}>
                        <input type="checkbox" checked={hideVoidedServices} onChange={e => setHideVoidedServices(e.target.checked)} style={{ accentColor: '#92400E' }} />
                        {language === 'sw' ? 'Ficha zilizobatilishwa' : 'Hide voided'}
                      </label>
                    </div>
                    {displayTx.length === 0 ? (
                      <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 13, padding: '24px 0' }}>{t.noDataForPeriod || 'No data for this period'}</div>
                    ) : (
                      [...displayTx].sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0)).map((st, idx) => {
                        const isVoid = st.status === 'void';
                        return (
                          <div key={st.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: idx < displayTx.length - 1 ? 12 : 0, marginBottom: idx < displayTx.length - 1 ? 12 : 0, borderBottom: idx < displayTx.length - 1 ? '1px solid #F3F4F6' : 'none', opacity: isVoid ? 0.5 : 1, borderLeft: isVoid ? '3px solid #9CA3AF' : 'none', paddingLeft: isVoid ? 8 : 0 }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: 14, marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: isVoid ? 'line-through' : 'none' }}>{st.serviceName}</div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                <span style={{ color: 'var(--text-muted)', fontSize: 12, textDecoration: isVoid ? 'line-through' : 'none' }}>{st.date}</span>
                                {isVoid && (
                                  <span style={{ fontSize: 9, fontWeight: 800, padding: '2px 6px', borderRadius: 4, background: '#9CA3AF', color: '#fff', letterSpacing: 0.3 }}>
                                    {language === 'sw' ? 'IMEBATILISHWA' : 'VOIDED'}
                                  </span>
                                )}
                                {isVoid && st._voidUnsynced && (
                                  <span style={{ fontSize: 9, fontWeight: 700, color: '#B45309' }}>
                                    {language === 'sw' ? 'HAIJASAWAZISHWA' : 'NOT SYNCED'}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div style={{ fontWeight: 700, color: isVoid ? 'var(--text-muted)' : '#10B981', fontSize: 14, marginLeft: 10, flexShrink: 0, textDecoration: isVoid ? 'line-through' : 'none' }}>{fmt(st.totalRevenue)}</div>
                          </div>
                        );
                      })
                    )}
                  </div>

                        </div>
                      </div>{/* END SECTION 1 -Services Overview */}

                      {/* ═══ SECTION 2 -Annual Performance (Huduma) ═══ */}
                      {(() => {
                        // Build consistent color map for services (alphabetical → teal palette)
                        const SVC_ANNUAL_COLORS = ['#10B981','#6366F1','#10B981','#F59E0B','#6366F1','#94A3B8','#0EA5E9','#14B8A6','#F97316','#84CC16'];
                        const allSvcNamesSorted = [...new Set((serviceTransactions || []).map(st => st.serviceName).filter(Boolean))].sort();
                        const svcColorMap = {};
                        allSvcNamesSorted.forEach((name, i) => { svcColorMap[name] = SVC_ANNUAL_COLORS[i % SVC_ANNUAL_COLORS.length]; });
                        const getSvcColor = (name) => svcColorMap[name] || SVC_ANNUAL_COLORS[0];

                        // Year selector options
                        const svcRawYears = [...new Set((serviceTransactions || []).map(st => new Date(st.date || 0).getFullYear()).filter(y => !isNaN(y) && y > 2000))].sort((a, b) => b - a);
                        const _svNow = new Date().getFullYear();
                        const _svFuture = Array.from({ length: 3 }, (_, i) => _svNow + i + 1);
                        const svcAvailableYears = [...new Set([..._svFuture, serviceAnnualYear, ...svcRawYears])].sort((a, b) => b - a);

                        // Monthly revenue data for selected year
                        const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                        const svcAnnualData = MONTHS_SHORT.map((label, m) => ({
                          label,
                          rev: (serviceTransactions || []).filter(st => {
                            const d = new Date(st.date || 0);
                            return d.getFullYear() === serviceAnnualYear && d.getMonth() === m && st.status !== 'void';
                          }).reduce((sum, st) => sum + parseFloat(st.totalRevenue || 0), 0)
                        }));
                        const svcAnnualTotal = svcAnnualData.reduce((s, d) => s + d.rev, 0);
                        const svcMaxMonthRev = Math.max(...svcAnnualData.map(d => d.rev), 1);

                        // Income by service for selected year
                        const svcByNameMap = {};
                        (serviceTransactions || []).filter(st => {
                          const d = new Date(st.date || 0);
                          return d.getFullYear() === serviceAnnualYear && st.status !== 'void';
                        }).forEach(st => {
                          const name = st.serviceName || 'Unknown';
                          if (!svcByNameMap[name]) svcByNameMap[name] = 0;
                          svcByNameMap[name] += parseFloat(st.totalRevenue || 0);
                        });
                        const svcByNameList = Object.entries(svcByNameMap).sort((a, b) => b[1] - a[1]);
                        const svcMaxRevenue = svcByNameList.length > 0 ? svcByNameList[0][1] : 1;

                        return (
                          <div style={{ background: 'var(--bg-white)', borderRadius: 16, overflow: 'hidden', marginBottom: 16, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', border: '0.5px solid var(--border-color)' }}>
                            {/* Section heading */}
                            <div style={{ padding: '12px 14px 4px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <div>
                                <h3 className="db-card-title" style={{ fontSize: 15, marginBottom: 0 }}>
                                  {language === 'sw' ? 'Utendaji wa Mwaka' : 'Annual Performance'}
                                </h3>
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3 }}>
                                <div style={{ fontSize: 8, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8 }}>{t.selectYear || 'Year'}</div>
                                <select value={serviceAnnualYear} onChange={e => setServiceAnnualYear(parseInt(e.target.value))}
                                  style={{ padding: '4px 10px', borderRadius: 8, border: '2px solid var(--border-color)', background: 'var(--bg-input)', fontSize: 14, fontWeight: 900, color: 'var(--text-primary)', cursor: 'pointer', outline: 'none', minWidth: 80, textAlign: 'center' }}>
                                  {svcAvailableYears.map(y => <option key={y} value={y} style={{ background: 'var(--bg-card)', color: 'var(--text-primary)' }}>{y}</option>)}
                                </select>
                              </div>
                            </div>

                            <div style={{ padding: 12 }}>
                              {/* Annual total card -teal */}
                              <div style={{ background: theme === 'dark' ? 'rgba(6,182,212,0.12)' : 'rgba(6,182,212,0.07)', borderRadius: 12, padding: '10px 14px', marginBottom: 14, borderLeft: '4px solid #10B981', borderTop: '1px solid rgba(6,182,212,0.2)', borderRight: '1px solid rgba(6,182,212,0.2)', borderBottom: '1px solid rgba(6,182,212,0.2)' }}>
                                <div style={{ fontSize: 11, color: theme === 'dark' ? '#67E8F9' : '#059669', fontWeight: 700, marginBottom: 2 }}>
                                  {language === 'sw' ? 'Jumla ya Mapato' : 'Total Revenue'} · {serviceAnnualYear}
                                </div>
                                <div style={{ fontSize: 22, fontWeight: 800, color: theme === 'dark' ? '#22D3EE' : '#0891B2' }}>{fmt(svcAnnualTotal)}</div>
                              </div>

                              {/* Monthly trend SVG area chart */}
                              <div style={{ background: 'var(--bg-tab)', borderRadius: 14, padding: 14, marginBottom: 12 }}>
                                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 10 }}>
                                  {language === 'sw' ? 'Mwenendo wa Mwaka' : 'Annual Trend'}
                                </div>
                                {(() => {
                                  const W = 320, H = 180, PX = 8, PY = 28;
                                  const pts = svcAnnualData.map((d, i) => ({
                                    x: PX + i * (W - PX * 2) / 11,
                                    y: H - PY - (svcMaxMonthRev > 0 ? (d.rev / svcMaxMonthRev) * (H - PY * 2) : 0)
                                  }));
                                  const bez = (p) => { let d = `M${p[0].x.toFixed(1)},${p[0].y.toFixed(1)}`; for (let i = 0; i < p.length-1; i++) { const a=p[Math.max(i-1,0)],b=p[i],c=p[i+1],e=p[Math.min(i+2,p.length-1)]; d+=` C${(b.x+(c.x-a.x)/4).toFixed(1)},${(b.y+(c.y-a.y)/4).toFixed(1)} ${(c.x-(e.x-b.x)/4).toFixed(1)},${(c.y-(e.y-b.y)/4).toFixed(1)} ${c.x.toFixed(1)},${c.y.toFixed(1)}`; } return d; };
                                  const linePath = bez(pts);
                                  const areaPath = `${linePath} L${pts[pts.length-1].x.toFixed(1)},${(H-PY).toFixed(1)} L${pts[0].x.toFixed(1)},${(H-PY).toFixed(1)} Z`;
                                  const hi = serviceChartHover;
                                  return (
                                    <div style={{ overflow: 'visible' }}>
                                      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 190, display: 'block', overflow: 'visible' }} preserveAspectRatio="none" onMouseLeave={() => setServiceChartHover(null)}>
                                        <defs>
                                          <linearGradient id="svcAreaGrad" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="#10B981" stopOpacity="0.45" />
                                            <stop offset="100%" stopColor="#10B981" stopOpacity="0.04" />
                                          </linearGradient>
                                        </defs>
                                        {[0.33,0.66].map((f,i) => <line key={i} x1={PX} y1={(PY+f*(H-PY*2)).toFixed(1)} x2={W-PX} y2={(PY+f*(H-PY*2)).toFixed(1)} stroke="var(--border-color)" strokeWidth="1" />)}
                                        <path d={areaPath} fill="url(#svcAreaGrad)" />
                                        <path d={linePath} fill="none" stroke="#10B981" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
                                        {pts.map((p, i) => { const x0 = i === 0 ? 0 : (pts[i-1].x+p.x)/2; const x1 = i === pts.length-1 ? W : (p.x+pts[i+1].x)/2; return <rect key={i} x={x0.toFixed(1)} y={0} width={(x1-x0).toFixed(1)} height={H} fill="transparent" style={{ cursor: 'crosshair' }} onMouseEnter={() => setServiceChartHover(i)} />; })}
                                        {hi !== null && (() => {
                                          const aboveFits = pts[hi].y - 26 >= PY;
                                          const tipRectY = aboveFits ? pts[hi].y - 26 : pts[hi].y + 8;
                                          const tipTextY = aboveFits ? pts[hi].y - 13 : pts[hi].y + 21;
                                          const tipRectX = Math.max(2, Math.min(pts[hi].x - 38, W - 78));
                                          const tipTextX = tipRectX + 38;
                                          return (
                                            <>
                                              <line x1={pts[hi].x.toFixed(1)} y1={PY} x2={pts[hi].x.toFixed(1)} y2={H-PY} stroke="#9CA3AF" strokeWidth="1" strokeDasharray="3,3" />
                                              <circle cx={pts[hi].x.toFixed(1)} cy={pts[hi].y.toFixed(1)} r="4.5" fill="#10B981" stroke="#fff" strokeWidth="2" />
                                              <rect x={tipRectX.toFixed(1)} y={tipRectY.toFixed(1)} width="64" height="14" rx="4" fill="#059669" fillOpacity="0.85" />
                                              <text x={(parseFloat(tipRectX) + 32).toFixed(1)} y={(parseFloat(tipRectY) + 10).toFixed(1)} textAnchor="middle" fill="#fff" fontSize="7" fontWeight="400">{fmt(svcAnnualData[hi].rev)}</text>
                                            </>
                                          );
                                        })()}
                                      </svg>
                                      <div style={{ display: 'flex', marginTop: 4 }}>
                                        {svcAnnualData.map((d, i) => (
                                          <div key={i} style={{ flex: 1, textAlign: 'center' }}>
                                            <span style={{ display: 'inline-block', background: hi === i ? 'var(--bg-white)' : 'var(--bg-tab)', borderRadius: 5, padding: '2px 3px', fontSize: 8, color: 'var(--text-primary)', fontWeight: 700 }}>{d.label}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  );
                                })()}
                              </div>

                              {/* Income by Service horizontal bar chart */}
                              <div style={{ background: 'var(--bg-white)', borderRadius: 16, padding: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
                                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>
                                  {language === 'sw' ? `Mapato kwa Huduma · ${serviceAnnualYear}` : `Income by Service · ${serviceAnnualYear}`}
                                </div>
                                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 16 }}>
                                  {language === 'sw' ? `Huduma zote · ${serviceAnnualYear}` : `All services · ${serviceAnnualYear}`}
                                </div>
                                {svcByNameList.length === 0 ? (
                                  <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 13, padding: '24px 0' }}>{t.noDataForPeriod || 'No data for this period'}</div>
                                ) : (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                    {svcByNameList.slice(0, 7).map(([name, rev]) => {
                                      const barColor = getSvcColor(name);
                                      return (
                                        <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                          <span style={{ width: 90, fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 0 }}>{name}</span>
                                          <div style={{ flex: 1, height: 22, background: 'var(--bg-input, #F3F4F6)', borderRadius: 6, overflow: 'hidden' }}>
                                            <div style={{ height: '100%', width: `${svcMaxRevenue > 0 ? (rev / svcMaxRevenue * 100) : 0}%`, background: barColor, borderRadius: 6, transition: 'width 0.6s ease' }} />
                                          </div>
                                          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', minWidth: 60, textAlign: 'right', flexShrink: 0 }}>{fmt(rev)}</span>
                                        </div>
                                      );
                                    })}
                                    {svcByNameList.length > 7 && (
                                      <div style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'center', paddingTop: 4 }}>
                                        +{svcByNameList.length - 7} {language === 'sw' ? 'huduma zaidi' : 'more services'}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                      {/* END SECTION 2 -Annual Performance (Huduma) */}

                    </div>
                  )}
                </>
              );
            })()}

            {/* RECORD SERVICE FORM MODAL */}
            {showServiceTransactionForm && (
              <div className="popup-overlay" style={{ zIndex: 1100 }}>
                <div className="popup-form mz-modal" style={{ maxWidth: 480, width: '100%', borderRadius: 16, padding: 24, maxHeight: '90vh', overflowY: 'auto' }}>
                  {/* Header */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                    <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: 'var(--text-primary)' }}>
                      {serviceTransactionEditId ? (t.editService || 'Edit Service') : (t.recordService || 'Record Service')}
                    </h3>
                    <button onClick={() => { setShowServiceTransactionForm(false); resetServiceForm(); }}
                      style={{ width: 32, height: 32, borderRadius: 50, background: 'var(--bg-tab)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                  </div>
                  <form onSubmit={handleServiceTransactionSubmit}>
                    {/* ── CART: added services (inline-editable qty + price) ── */}
                    {serviceLineItems.length > 0 && (
                      <div style={{ marginBottom: 12, borderRadius: 12, overflow: 'hidden', border: '1px solid var(--border-color)' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 60px 18px 90px 90px 28px', gap: 6, padding: '6px 12px', background: '#F3F4F6', fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4, alignItems: 'center' }}>
                          <div>{language === 'sw' ? 'Huduma' : 'Service'}</div>
                          <div style={{ textAlign: 'right' }}>{language === 'sw' ? 'Idadi' : 'Qty'}</div>
                          <div />
                          <div style={{ textAlign: 'right' }}>{language === 'sw' ? 'Bei' : 'Price'}</div>
                          <div style={{ textAlign: 'right' }}>{language === 'sw' ? 'Jumla' : 'Total'}</div>
                          <div />
                        </div>
                        {serviceLineItems.map((item, idx) => {
                          const qty = parseFloat(item.quantity || 0);
                          const price = parseFloat(item.price || 0);
                          const lineTotal = qty * price;
                          return (
                            <div key={item.id} style={{ display: 'grid', gridTemplateColumns: '1fr 60px 18px 90px 90px 28px', gap: 6, padding: '8px 12px', background: idx % 2 === 0 ? 'var(--bg-tab)' : 'var(--bg-card)', borderTop: '1px solid var(--border-color)', alignItems: 'center' }}>
                              <div style={{ minWidth: 0 }}>
                                <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.serviceName}</div>
                                <div style={{ fontSize: 10, color: 'var(--text-secondary)' }}>{language === 'sw' ? 'huduma' : 'service'}</div>
                              </div>
                              <input type="number" min="0" step="any" value={item.quantity || ''}
                                onChange={e => setServiceLineItems(prev => prev.map(i => i.id === item.id ? { ...i, quantity: e.target.value } : i))}
                                style={{ width: '100%', padding: '5px 6px', borderRadius: 6, border: '1px solid var(--border-color)', background: 'var(--bg-white)', fontSize: 12, textAlign: 'right', outline: 'none', boxSizing: 'border-box' }} />
                              <div style={{ textAlign: 'center', fontSize: 11, color: 'var(--text-secondary)' }}>×</div>
                              <input type="number" min="0" step="any" value={item.price || ''}
                                onChange={e => setServiceLineItems(prev => prev.map(i => i.id === item.id ? { ...i, price: e.target.value } : i))}
                                style={{ width: '100%', padding: '5px 6px', borderRadius: 6, border: '1px solid var(--border-color)', background: 'var(--bg-white)', fontSize: 12, textAlign: 'right', outline: 'none', boxSizing: 'border-box' }} />
                              <div style={{ textAlign: 'right', fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{fmt(lineTotal)}</div>
                              <button type="button" onClick={() => setServiceLineItems(prev => prev.filter(i => i.id !== item.id))}
                                style={{ width: 24, height: 24, borderRadius: 6, background: '#FEE2E2', color: '#DC2626', border: 'none', fontWeight: 700, fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                            </div>
                          );
                        })}
                        <div style={{ padding: '8px 12px', background: '#F0FDF4', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid var(--border-color)' }}>
                          <span style={{ fontSize: 12, fontWeight: 600, color: '#065F46' }}>{language === 'sw' ? 'Jumla ya huduma' : 'Services subtotal'}</span>
                          <span style={{ fontSize: 14, fontWeight: 800, color: '#059669' }}>
                            {fmt(serviceLineItems.reduce((s, item) => s + parseFloat(item.quantity || 0) * parseFloat(item.price || 0), 0))}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* ── ADD SERVICE ROW ── */}
                    <div style={{ background: 'var(--bg-tab)', borderRadius: 12, padding: 12, marginBottom: 16 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        {language === 'sw' ? 'Ongeza huduma' : 'Add service'}
                      </div>
                      {/* Service select OR free-text */}
                      {(services || []).length > 0 ? (
                        <select value={serviceLineDraft.serviceId}
                          onChange={e => {
                            const selId = e.target.value;
                            const selSvc = (services || []).find(s => String(s.id) === String(selId));
                            setServiceLineDraft(p => ({
                              ...p,
                              serviceId: selId,
                              serviceName: selSvc ? selSvc.name : '',
                              price: selSvc ? String(selSvc.defaultPrice ?? '') : '',
                              cost: selSvc ? String(selSvc.defaultCost ?? '') : ''
                            }));
                          }}
                          style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: 'none', background: 'var(--bg-card)', fontSize: 14, boxSizing: 'border-box', outline: 'none', marginBottom: 8 }}>
                          <option value="">{t.selectService || 'Select service...'}</option>
                          {(services || []).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                      ) : null}
                      {/* Free-text fallback */}
                      <input type="text" value={serviceLineDraft.serviceName && !serviceLineDraft.serviceId ? serviceLineDraft.serviceName : (serviceLineDraft.serviceId ? '' : (serviceLineDraft.serviceName || ''))}
                        onChange={e => setServiceLineDraft(p => ({ ...p, serviceName: e.target.value, serviceId: '' }))}
                        placeholder={(services || []).length > 0 ? (language === 'sw' ? 'Au andika jina la huduma...' : 'Or type service name...') : (language === 'sw' ? 'k.m. Ushauri, Ukarabati...' : 'e.g. Consultation, Repair...')}
                        style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: 'none', background: 'var(--bg-card)', fontSize: 14, boxSizing: 'border-box', outline: 'none', marginBottom: 8 }} />
                      <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>{language === 'sw' ? 'Idadi / Saa' : 'Qty / Hours'}</div>
                          <input type="number" inputMode="decimal" min="0.01" step="0.01" placeholder="1" value={serviceLineDraft.quantity}
                            onChange={e => setServiceLineDraft(p => ({ ...p, quantity: e.target.value }))}
                            style={{ width: '100%', padding: '10px 8px', borderRadius: 10, border: 'none', background: 'var(--bg-card)', fontSize: 14, boxSizing: 'border-box', outline: 'none' }} />
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>{language === 'sw' ? 'Bei kwa huduma' : 'Selling Price per Service'}</div>
                          <input type="number" inputMode="decimal" min="0" placeholder="0" value={serviceLineDraft.price}
                            onChange={e => setServiceLineDraft(p => ({ ...p, price: e.target.value }))}
                            style={{ width: '100%', padding: '10px 8px', borderRadius: 10, border: 'none', background: 'var(--bg-card)', fontSize: 14, boxSizing: 'border-box', outline: 'none' }} />
                        </div>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', background: '#F0FAF5', borderRadius: 8, marginBottom: 8 }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#0F6E56' }}>{language === 'sw' ? 'Jumla ya mstari' : 'Line total'}</span>
                        <span style={{ fontSize: 14, fontWeight: 800, color: '#0A3D2E' }}>
                          {fmt((parseFloat(serviceLineDraft.quantity) || 0) * (parseFloat(serviceLineDraft.price) || 0))}
                        </span>
                      </div>
                      <button type="button" onClick={() => {
                        const d = serviceLineDraft;
                        if (!d.serviceName || !d.price || !d.quantity) return;
                        // Pull cost from the master service record if present
                        if (!d.cost && d.serviceId) {
                          const svc = (services || []).find(s => String(s.id) === String(d.serviceId));
                          if (svc?.defaultCost) d.cost = String(svc.defaultCost);
                        }
                        setServiceLineItems(prev => [...prev, { ...d, id: Date.now() }]);
                        setServiceLineDraft({ serviceId: '', serviceName: '', quantity: '', price: '', cost: '' });
                      }} style={{ width: '100%', padding: '10px 0', borderRadius: 10, background: '#111827', color: '#A3E635', border: 'none', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
                        + {language === 'sw' ? 'Ongeza huduma' : 'Add service'}
                      </button>
                    </div>

                    {/* ── Customer Details (rich, mirrors product form) ── */}
                    <div style={{ background: 'var(--bg-input)', borderRadius: 14, padding: '14px 14px 10px', marginBottom: 16 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        {t.customerDetails || 'Customer Details'}
                      </div>
                      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                        {[
                          { val: 'walkin', label: language === 'sw' ? 'Mpya / Wa Kupita' : 'New / Walk-in' },
                          { val: 'registered', label: language === 'sw' ? 'Aliyesajiliwa' : 'Registered' },
                        ].map(opt => (
                          <button key={opt.val} type="button"
                            onClick={() => {
                              setServiceCustomerType(opt.val);
                              setServiceTransactionFormData(p => ({ ...p, customerName: '', customerPhone: '', customerEmail: '', customerAddress: '', customerTIN: '', customerVRN: '' }));
                            }}
                            style={{ flex: 1, padding: '9px 0', borderRadius: 10, border: 'none', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                              background: serviceCustomerType === opt.val ? '#334155' : '#fff',
                              color: serviceCustomerType === opt.val ? '#fff' : '#6B7280' }}>
                            {opt.label}
                          </button>
                        ))}
                      </div>
                      {serviceCustomerType === 'registered' ? (
                        <div>
                          <label style={{ display: 'block', marginBottom: 5, color: 'var(--text-primary)', fontWeight: 600, fontSize: 12 }}>{t.selectCustomer || 'Select customer'}</label>
                          <select value={serviceTransactionFormData.customerName || ''}
                            onChange={e => {
                              const selectedName = e.target.value;
                              const cust = (customers || []).find(c => c.name === selectedName);
                              setServiceTransactionFormData(p => ({
                                ...p,
                                customerName: selectedName,
                                customerPhone: cust?.phone || '',
                                customerEmail: cust?.email || '',
                                customerAddress: cust?.address || '',
                                customerTIN: cust?.tin || '',
                                customerVRN: cust?.vrn || '',
                              }));
                            }}
                            style={{ width: '100%', padding: 10, borderRadius: 10, border: 'none', background: 'var(--bg-white)', fontSize: 13, boxSizing: 'border-box', outline: 'none' }}>
                            <option value="">{t.selectCustomer || 'Select customer...'}</option>
                            {(customers || []).map(c => (
                              <option key={c.id} value={c.name}>{c.name}{c.phone ? ` (${c.phone})` : ''}</option>
                            ))}
                          </select>
                          {serviceTransactionFormData.customerName && (
                            <div style={{ marginTop: 10, padding: 10, background: 'rgba(255,255,255,0.6)', borderRadius: 10, fontSize: 12, color: 'var(--text-secondary)' }}>
                              {serviceTransactionFormData.customerPhone && <div>📞 {serviceTransactionFormData.customerPhone}</div>}
                              {serviceTransactionFormData.customerEmail && <div>✉ {serviceTransactionFormData.customerEmail}</div>}
                              {serviceTransactionFormData.customerAddress && <div>📍 {serviceTransactionFormData.customerAddress}</div>}
                              {serviceTransactionFormData.customerTIN && <div>TIN: {serviceTransactionFormData.customerTIN}</div>}
                              {serviceTransactionFormData.customerVRN && <div>VRN: {serviceTransactionFormData.customerVRN}</div>}
                              {!serviceTransactionFormData.customerPhone && !serviceTransactionFormData.customerEmail && !serviceTransactionFormData.customerAddress && !serviceTransactionFormData.customerTIN && !serviceTransactionFormData.customerVRN && (
                                <div style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>{language === 'sw' ? 'Hakuna maelezo ya ziada' : 'No additional details on file'}</div>
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        <>
                          <div style={{ marginBottom: 10 }}>
                            <label style={{ display: 'block', marginBottom: 5, color: 'var(--text-primary)', fontWeight: 600, fontSize: 12 }}>{t.customerName || 'Customer Name'}</label>
                            <input type="text" value={serviceTransactionFormData.customerName}
                              onChange={e => setServiceTransactionFormData(p => ({ ...p, customerName: e.target.value }))}
                              placeholder={language === 'sw' ? 'Jina la mteja (au acha wazi kwa Walk-in)' : 'Customer name (leave blank for Walk-in)'}
                              style={{ width: '100%', padding: 10, borderRadius: 10, border: 'none', background: 'var(--bg-white)', fontSize: 13, boxSizing: 'border-box', outline: 'none' }} />
                          </div>
                          <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
                            <div style={{ flex: 1 }}>
                              <label style={{ display: 'block', marginBottom: 5, color: 'var(--text-primary)', fontWeight: 600, fontSize: 12 }}>{t.phone || 'Phone'} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
                              <input type="tel" value={serviceTransactionFormData.customerPhone || ''}
                                onChange={e => setServiceTransactionFormData(p => ({ ...p, customerPhone: e.target.value }))}
                                placeholder="+255..."
                                style={{ width: '100%', padding: 10, borderRadius: 10, border: 'none', background: 'var(--bg-white)', fontSize: 13, boxSizing: 'border-box', outline: 'none' }} />
                            </div>
                            <div style={{ flex: 1 }}>
                              <label style={{ display: 'block', marginBottom: 5, color: 'var(--text-primary)', fontWeight: 600, fontSize: 12 }}>{language === 'sw' ? 'Barua pepe' : 'Email'} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
                              <input type="email" value={serviceTransactionFormData.customerEmail || ''}
                                onChange={e => setServiceTransactionFormData(p => ({ ...p, customerEmail: e.target.value }))}
                                placeholder="email@example.com"
                                style={{ width: '100%', padding: 10, borderRadius: 10, border: 'none', background: 'var(--bg-white)', fontSize: 13, boxSizing: 'border-box', outline: 'none' }} />
                            </div>
                          </div>
                          <div style={{ marginBottom: 10 }}>
                            <label style={{ display: 'block', marginBottom: 5, color: 'var(--text-primary)', fontWeight: 600, fontSize: 12 }}>{language === 'sw' ? 'Anwani' : 'Address'} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
                            <input type="text" value={serviceTransactionFormData.customerAddress || ''}
                              onChange={e => setServiceTransactionFormData(p => ({ ...p, customerAddress: e.target.value }))}
                              placeholder="Street, City"
                              style={{ width: '100%', padding: 10, borderRadius: 10, border: 'none', background: 'var(--bg-white)', fontSize: 13, boxSizing: 'border-box', outline: 'none' }} />
                          </div>
                          <div style={{ display: 'flex', gap: 10 }}>
                            <div style={{ flex: 1 }}>
                              <label style={{ display: 'block', marginBottom: 5, color: 'var(--text-primary)', fontWeight: 600, fontSize: 12 }}>TIN <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
                              <input type="text" value={serviceTransactionFormData.customerTIN || ''}
                                onChange={e => setServiceTransactionFormData(p => ({ ...p, customerTIN: e.target.value }))}
                                placeholder="123-456-789"
                                style={{ width: '100%', padding: 10, borderRadius: 10, border: 'none', background: 'var(--bg-white)', fontSize: 13, boxSizing: 'border-box', outline: 'none' }} />
                            </div>
                            <div style={{ flex: 1 }}>
                              <label style={{ display: 'block', marginBottom: 5, color: 'var(--text-primary)', fontWeight: 600, fontSize: 12 }}>VRN <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
                              <input type="text" value={serviceTransactionFormData.customerVRN || ''}
                                onChange={e => setServiceTransactionFormData(p => ({ ...p, customerVRN: e.target.value }))}
                                placeholder="40-001234-X"
                                style={{ width: '100%', padding: 10, borderRadius: 10, border: 'none', background: 'var(--bg-white)', fontSize: 13, boxSizing: 'border-box', outline: 'none' }} />
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Date */}
                    <div style={{ marginBottom: 16 }}>
                      <label style={{ display: 'block', marginBottom: 6, color: 'var(--text-primary)', fontWeight: 600, fontSize: 14 }}>{t.date || 'Date'}</label>
                      <input type="date" value={serviceTransactionFormData.date}
                        onChange={e => setServiceTransactionFormData(p => ({ ...p, date: e.target.value }))}
                        required
                        style={{ width: '100%', padding: 14, borderRadius: 12, border: 'none', background: 'var(--bg-tab)', fontSize: 15, boxSizing: 'border-box', outline: 'none' }} />
                    </div>

                    {/* Discount */}
                    <div style={{ marginBottom: 16 }}>
                      <label style={{ display: 'block', marginBottom: 6, color: 'var(--text-primary)', fontWeight: 600, fontSize: 14 }}>
                        {language === 'sw' ? 'Punguzo (Discount)' : 'Punguzo (Discount)'} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>({t.optional || 'optional'})</span>
                      </label>
                      <input type="number" min="0" inputMode="numeric" placeholder="0"
                        value={serviceTransactionFormData.discount}
                        onChange={e => setServiceTransactionFormData(p => ({ ...p, discount: e.target.value }))}
                        style={{ width: '100%', padding: '9px 12px', borderRadius: 12, border: 'none', background: 'var(--bg-tab)', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
                    </div>

                    {/* EFD Receipt Number */}
                    <div style={{ marginBottom: 16 }}>
                      <label style={{ display: 'block', marginBottom: 6, color: 'var(--text-primary)', fontWeight: 600, fontSize: 14 }}>EFD Receipt No. <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
                      <input type="text" placeholder="e.g. EFD-0000123"
                        value={serviceTransactionFormData.efdReceiptNo}
                        onChange={e => setServiceTransactionFormData(p => ({ ...p, efdReceiptNo: e.target.value }))}
                        style={{ width: '100%', padding: '9px 12px', borderRadius: 12, border: 'none', background: 'var(--bg-tab)', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
                    </div>

                    {/* Taxes -only for VAT-registered businesses */}
                    {vatRegistered && (<div style={{ marginBottom: 16 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <label style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: 14 }}>Taxes <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>({t.optional || 'optional'})</span></label>
                        <button type="button" onClick={addServiceTax}
                          style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', background: 'var(--bg-input)', border: 'none', borderRadius: 8, padding: '4px 10px', cursor: 'pointer' }}>
                          {t.addTax || '+ Add Tax'}
                        </button>
                      </div>
                      {(serviceTransactionFormData.taxes || []).map((tax, idx) => (
                        <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                          <select value={tax.type} onChange={e => updateServiceTax(idx, 'type', e.target.value)}
                            style={{ flex: 1, padding: '12px 12px', borderRadius: 12, border: '1px solid var(--border-color)', background: '#fff', fontSize: 14, outline: 'none', minWidth: 0, cursor: 'pointer' }}>
                            <option value="vat">{t.vatOption || 'VAT (Value Added Tax)'}</option>
                            <option value="withholding">{t.withholdingOption || 'Withholding Tax'}</option>
                          </select>
                          <input type="number" min="0" inputMode="numeric" placeholder="Rate %"
                            value={tax.rate}
                            onChange={e => updateServiceTax(idx, 'rate', e.target.value)}
                            style={{ width: 70, padding: '12px 10px', borderRadius: 12, border: '1px solid var(--border-color)', background: '#fff', fontSize: 14, outline: 'none', textAlign: 'right', flexShrink: 0, boxSizing: 'border-box' }} />
                          <button type="button" onClick={() => removeServiceTax(idx)}
                            style={{ width: 36, height: 46, borderRadius: 10, border: 'none', background: '#FEE2E2', color: '#DC2626', cursor: 'pointer', fontSize: 20, fontWeight: 700, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                        </div>
                      ))}
                      {(serviceTransactionFormData.taxes || []).length === 0 && (
                        <div style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>{t.noTaxesAdded || 'No taxes added. Click "Add Tax" to include VAT 18% or custom withholding tax.'}</div>
                      )}
                    </div>)}

                    {/* Total Changed banner -edit mode only */}
                    {serviceTransactionEditId && (() => {
                      const orig = (serviceTransactions || []).find(st => st.id === serviceTransactionEditId);
                      if (!orig) return null;
                      const origTotal = parseFloat(orig.totalRevenue || 0);
                      const newTotal = getServiceTotalForPayments();
                      const delta = newTotal - origTotal;
                      if (Math.abs(delta) < 0.005) return null;
                      const isDown = delta < 0;
                      const bg = isDown ? '#FEF3C7' : '#E2E8F0';
                      const border = isDown ? '#FCD34D' : '#93C5FD';
                      const fg = isDown ? '#92400E' : '#334155';
                      const sub = isDown ? '#78350F' : '#1E3A8A';
                      return (
                        <div style={{ marginBottom: 12, padding: '12px 14px', borderRadius: 12, background: bg, border: `1px solid ${border}` }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                            <span style={{ fontSize: 12, fontWeight: 700, color: fg, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                              ⚠ {language === 'sw' ? 'Jumla imebadilika' : 'Total has changed'}
                            </span>
                            <span style={{ fontSize: 13, fontWeight: 800, color: fg }}>
                              {isDown ? '−' : '+'}{fmt(Math.abs(delta))}
                            </span>
                          </div>
                          <div style={{ fontSize: 12, color: sub, lineHeight: 1.4 }}>
                            {language === 'sw' ? 'Awali' : 'Was'}: <strong>{fmt(origTotal)}</strong>{' → '}
                            {language === 'sw' ? 'Sasa' : 'Now'}: <strong>{fmt(newTotal)}</strong>
                            <br/>
                            {language === 'sw'
                              ? 'Hakikisha malipo hapa chini yanalingana na jumla mpya. Punguza Mkopo kwanza, kisha pesa kutoka akaunti.'
                              : 'Adjust the payments below to match the new total. Tip: reduce Credit first, then cash/bank/mobile.'}
                          </div>
                        </div>
                      );
                    })()}

                    {/* Total Display */}
                    {(() => {
                      const displayTotal = getServiceTotalForPayments();
                      return displayTotal > 0 ? (
                        <div style={{ marginBottom: 16, padding: '10px 14px', borderRadius: 14, background: 'var(--bg-tab)', border: '1.5px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-primary)' }}>{language === 'sw' ? 'Jumla' : 'Total'}</span>
                          <span style={{ fontWeight: 800, fontSize: 18, color: '#059669' }}>{fmt(displayTotal)}</span>
                        </div>
                      ) : null;
                    })()}

                    {/* Split Payment */}
                    {(() => {
                      const orderTotal = getServiceTotalForPayments();
                      const paidAmt = (servicePayments || []).filter(p => p.type !== 'credit').reduce((s, p) => s + parseFloat(p.amount || 0), 0);
                      const creditAmt = (servicePayments || []).filter(p => p.type === 'credit').reduce((s, p) => s + parseFloat(p.amount || 0), 0);
                      const coverage = paidAmt + creditAmt;
                      const remaining = Math.max(0, orderTotal - coverage);
                      const isCreditOnly = creditAmt > 0 && paidAmt === 0;
                      const isPartialCredit = creditAmt > 0 && paidAmt > 0;
                      const payOpts = buildPaymentOptions(true);
                      return (
                        <div style={{ marginBottom: 24 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                            <label style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: 14 }}>{t.paymentMethod || 'Payment Method'}</label>
                            {(servicePayments || []).length < (payOpts || []).length && orderTotal > 0 && (
                              <button type="button" onClick={addServicePayment}
                                style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', background: 'var(--bg-input)', border: 'none', borderRadius: 8, padding: '4px 10px', cursor: 'pointer' }}>
                                + {t.add || 'Add'}
                              </button>
                            )}
                          </div>
                          {(servicePayments || []).map((pay, idx) => {
                            const otherRowsTotal = (servicePayments || []).reduce((s, p, i) => i !== idx ? s + parseFloat(p.amount || 0) : s, 0);
                            const maxForRow = Math.max(0, orderTotal - otherRowsTotal);
                            const currentKey = pay.accountKey || pay.type;
                            const usedByOthers = new Set((servicePayments || []).map((p, i) => i !== idx ? (p.accountKey || p.type) : null).filter(Boolean));
                            const availableOpts = (payOpts || []).filter(o => !usedByOthers.has(o.value) || o.value === currentKey);
                            return (
                              <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                                <select value={currentKey} onChange={e => handleServicePaymentTypeChange(idx, e.target.value)}
                                  style={{ flex: 1, padding: '12px 10px', borderRadius: 12, border: 'none', background: 'var(--bg-tab)', fontSize: 14, outline: 'none', minWidth: 0 }}>
                                  {availableOpts.map(o => <option key={o.value + o.label} value={o.value}>{o.label}</option>)}
                                </select>
                                <input type="number" min="0" max={maxForRow} inputMode="numeric" placeholder="0"
                                  value={pay.amount}
                                  onChange={e => {
                                    const raw = parseFloat(e.target.value);
                                    const clamped = isNaN(raw) ? '' : String(Math.min(raw, maxForRow));
                                    handleServicePaymentAmountChange(idx, clamped);
                                  }}
                                  style={{ width: 100, padding: '12px 10px', borderRadius: 12, border: 'none', background: 'var(--bg-tab)', fontSize: 14, outline: 'none', textAlign: 'right', flexShrink: 0, boxSizing: 'border-box' }} />
                                {(servicePayments || []).length > 1 && (
                                  <button type="button" onClick={() => removeServicePayment(idx)}
                                    style={{ width: 36, height: 46, borderRadius: 10, border: 'none', background: '#FEE2E2', color: '#DC2626', cursor: 'pointer', fontSize: 20, fontWeight: 700, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                                )}
                              </div>
                            );
                          })}
                          {orderTotal > 0 && (() => {
                            if (remaining > 0.01) {
                              return (
                                <div style={{ borderRadius: 10, padding: '8px 12px', marginTop: 4, background: '#FEF3C7', color: '#92400E', fontSize: 13, fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <span>{language === 'sw' ? 'Hayajakamilika' : 'Incomplete'}</span>
                                  <span>{fmt(remaining)} {language === 'sw' ? 'bakaza' : 'remaining'}</span>
                                </div>
                              );
                            }
                            if (isCreditOnly) {
                              return (
                                <div style={{ borderRadius: 10, padding: '8px 12px', marginTop: 4, background: '#FEF3C7', color: '#92400E', fontSize: 13, fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <span>{language === 'sw' ? 'Haijalipwa (Mkopo)' : 'Unpaid (Credit)'}</span>
                                  <span>{fmt(creditAmt)} {language === 'sw' ? 'bakaza' : 'remaining'}</span>
                                </div>
                              );
                            }
                            if (isPartialCredit) {
                              return (
                                <div style={{ borderRadius: 10, padding: '8px 12px', marginTop: 4, background: '#FEF3C7', color: '#92400E', fontSize: 13, fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <span>{language === 'sw' ? 'Sehemu kwa Mkopo' : 'Partly on Credit'}</span>
                                  <span>{fmt(creditAmt)} {language === 'sw' ? 'kwa mkopo' : 'on credit'}</span>
                                </div>
                              );
                            }
                            return (
                              <div style={{ borderRadius: 10, padding: '8px 12px', marginTop: 4, background: '#D1FAE5', color: '#065F46', fontSize: 13, fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span>{t.paid || 'Paid'}</span>
                              </div>
                            );
                          })()}
                        </div>
                      );
                    })()}

                    {/* Footer Sections */}
                    <div style={{ background: 'var(--bg-tab)', borderRadius: 12, padding: '12px', marginBottom: 16 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <label style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: 13 }}>
                          {language === 'sw' ? 'Sehemu za Chini' : 'Footer Sections'} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>({t.optional || 'optional'})</span>
                        </label>
                        {serviceFooterSections.length < 2 && (
                          <button type="button" onClick={() => setServiceFooterSections(prev => [...prev, { label: 'Additional Details', content: '' }])}
                            style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', background: 'var(--bg-input)', border: 'none', borderRadius: 8, padding: '4px 10px', cursor: 'pointer' }}>
                            + {language === 'sw' ? 'Ongeza' : 'Add section'}
                          </button>
                        )}
                      </div>
                      <p style={{ fontSize: 11, color: 'var(--text-secondary)', margin: '0 0 8px' }}>
                        {language === 'sw' ? '2 sehemu = kando kwa kando kwenye PDF.' : '2 sections appear side by side on the PDF.'}
                      </p>
                      {serviceFooterSections.map((sec, si) => (
                        <div key={si} style={{ marginBottom: si < serviceFooterSections.length - 1 ? 10 : 0 }}>
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 5 }}>
                            <select value={sec.label}
                              onChange={e => setServiceFooterSections(prev => prev.map((s, i) => i === si ? { ...s, label: e.target.value } : s))}
                              style={{ flex: 1, padding: '7px 8px', borderRadius: 8, border: 'none', background: 'var(--bg-card)', fontSize: 12, outline: 'none' }}>
                              <option>Terms &amp; Conditions</option>
                              <option>Additional Details</option>
                              <option>Notes</option>
                              <option>Delivery Terms</option>
                              <option>Warranty</option>
                              <option>Custom</option>
                            </select>
                            {sec.label === 'Custom' && (
                              <input type="text" placeholder="Section title" value={sec.customLabel || ''}
                                onChange={e => setServiceFooterSections(prev => prev.map((s, i) => i === si ? { ...s, customLabel: e.target.value } : s))}
                                style={{ flex: 1, padding: '7px 8px', borderRadius: 8, border: 'none', background: 'var(--bg-card)', fontSize: 12, outline: 'none' }} />
                            )}
                            {serviceFooterSections.length > 1 && (
                              <button type="button" onClick={() => setServiceFooterSections(prev => prev.filter((_, i) => i !== si))}
                                style={{ width: 26, height: 26, borderRadius: 6, background: '#FEE2E2', color: '#DC2626', border: 'none', fontWeight: 700, fontSize: 15, cursor: 'pointer', flexShrink: 0 }}>×</button>
                            )}
                          </div>
                          <textarea rows={2} value={sec.content}
                            onChange={e => setServiceFooterSections(prev => prev.map((s, i) => i === si ? { ...s, content: e.target.value } : s))}
                            placeholder={language === 'sw' ? 'Andika hapa...' : 'One point per line...'}
                            style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: 'none', background: 'var(--bg-card)', fontSize: 12, color: 'var(--text-primary)', boxSizing: 'border-box', outline: 'none', resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }} />
                        </div>
                      ))}
                    </div>

                    {/* Issue Invoice toggle (paid services only -unpaid auto-issues an invoice) */}
                    {(() => {
                      const orderTotal = getServiceTotalForPayments();
                      const _allPmts = (servicePayments || []).filter(p => p.amount && parseFloat(p.amount) > 0);
                      const _hasCredit = _allPmts.some(p => p.type === 'credit');
                      const _paidNonCredit = _allPmts.filter(p => p.type !== 'credit').reduce((s, p) => s + parseFloat(p.amount || 0), 0);
                      const _fullyPaid = !_hasCredit && _paidNonCredit >= orderTotal - 0.005 && orderTotal > 0;
                      if (!_fullyPaid) return null;
                      return (
                        <div style={{ background: 'var(--bg-tab)', border: '1px solid var(--border-color)', borderRadius: 12, padding: '12px 14px', marginBottom: 12 }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                            <input type="checkbox"
                              checked={!!serviceIssueInvoice}
                              onChange={e => { serviceIssueInvoiceTouched.current = true; setServiceIssueInvoice(e.target.checked); }}
                              style={{ width: 18, height: 18, accentColor: '#334155', cursor: 'pointer', flexShrink: 0 }} />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text-primary)' }}>
                                {language === 'sw' ? 'Toa ankara rasmi kwa huduma hii' : 'Issue formal invoice for this service'}
                              </div>
                              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2, lineHeight: 1.3 }}>
                                {language === 'sw'
                                  ? 'Itazalisha SRV-XXXX na kuwekwa kwenye orodha ya huduma. Inafaa kwa wateja wa biashara.'
                                  : 'Auto-generates SRV-XXXX and lists it in the services log. Useful for business clients.'}
                              </div>
                            </div>
                          </label>
                        </div>
                      );
                    })()}

                    {/* Save / Cancel */}
                    <div style={{ display: 'flex', gap: 10 }}>
                      {serviceLineItems.length === 0 && (
                        <p style={{ color: '#DC2626', fontSize: 13, fontWeight: 600, margin: '0 0 10px', textAlign: 'center', width: '100%' }}>
                          ⚠ {language === 'sw' ? 'Ongeza angalau huduma moja' : 'Add at least one service to the order'}
                        </p>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 10 }}>
                      <button type="submit" className="su-save-btn"
                        disabled={serviceLineItems.length === 0 && !(serviceLineDraft.serviceName && serviceLineDraft.quantity && serviceLineDraft.price)}
                        style={{ flex: 1, padding: 14, borderRadius: 12, border: 'none', fontWeight: 700, fontSize: 15, cursor: 'pointer' }}>{language === 'sw' ? 'Rekodi Huduma' : 'Record Service'}</button>
                      <button type="button"
                        onClick={() => { setShowServiceTransactionForm(false); resetServiceForm(); }}
                        style={{ flex: 1, padding: 14, borderRadius: 12, background: 'var(--bg-tab)', color: 'var(--text-primary)', border: 'none', fontWeight: 600, fontSize: 15, cursor: 'pointer' }}>{t.cancel || 'Cancel'}</button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* SERVICE INVOICE PREVIEW MODAL */}
            {showServiceInvoicePreview && lastSavedServiceTransaction && (
              <div className="popup-overlay" style={{ zIndex: 1200 }}>
                <div className="popup-form" style={{ maxWidth: 420, width: '100%', borderRadius: 16, padding: 28, textAlign: 'center' }}>
                  <div style={{ width: 60, height: 60, borderRadius: '50%', background: '#F0FDF4', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                    <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  </div>
                  <h3 style={{ fontSize: 20, fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 8px' }}>{language === 'sw' ? 'Huduma Imehifadhiwa!' : 'Service Recorded!'}</h3>
                  <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 20px' }}>{language === 'sw' ? 'Invoice yako iko tayari kupakiwa.' : 'Your invoice is ready to download.'}</p>

                  {(() => {
                    const _pmts = lastSavedServiceTransaction.payments || [];
                    const _nonCredit = _pmts.filter(p => p.type !== 'credit').reduce((s, p) => s + parseFloat(p.amount || 0), 0);
                    const _credit = _pmts.filter(p => p.type === 'credit').reduce((s, p) => s + parseFloat(p.amount || 0), 0);
                    const _total = parseFloat(lastSavedServiceTransaction.totalRevenue || 0);
                    const _fullyPaid = lastSavedServiceTransaction.isPaid === true || (_credit === 0 && _nonCredit >= _total - 0.005);
                    const _partial = !_fullyPaid && _nonCredit > 0;
                    const statusLabel = _fullyPaid
                      ? (language === 'sw' ? 'Imelipwa' : 'Paid')
                      : _partial
                        ? (language === 'sw' ? 'Imelipwa Kiasi' : 'Partially Paid')
                        : (language === 'sw' ? 'Haijalipiwa' : 'Unpaid');
                    const statusColor = _fullyPaid ? '#059669' : _partial ? '#D97706' : '#DC2626';
                    return (
                      <div style={{ background: 'var(--bg-tab)', borderRadius: 14, padding: '16px 20px', marginBottom: 14, textAlign: 'left', border: '1px solid var(--border-color)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                          <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--text-primary)' }}>{businessName}</span>
                          <span style={{ fontSize: 11, fontWeight: 700, color: '#059669', background: '#F0FDF4', padding: '3px 10px', borderRadius: 8 }}>{lastSavedServiceTransaction.invoiceNumber}</span>
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 0' }}>
                          <span style={{ color: 'var(--text-muted)' }}>{t.date || 'Date'}:</span><span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{lastSavedServiceTransaction.date}</span>
                          <span style={{ color: 'var(--text-muted)' }}>{t.billTo || 'Bill To'}:</span><span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{lastSavedServiceTransaction.customerName || 'Walk-in'}</span>
                          <span style={{ color: 'var(--text-muted)' }}>{language === 'sw' ? 'Huduma' : 'Service'}:</span>
                          <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>
                            {(lastSavedServiceTransaction.items || []).length > 1
                              ? `${lastSavedServiceTransaction.items.length} ${language === 'sw' ? 'huduma' : 'services'}`
                              : lastSavedServiceTransaction.serviceName}
                          </span>
                          <span style={{ color: 'var(--text-muted)' }}>{t.paymentMethod || 'Payment'}:</span>
                          <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>
                            {_pmts.length > 1
                              ? _pmts.map((p, i) => (
                                  <span key={i} style={{ display: 'block', textTransform: 'capitalize' }}>
                                    {p.type}: {fmt(parseFloat(p.amount || 0))}
                                  </span>
                                ))
                              : <span style={{ textTransform: 'capitalize' }}>{_pmts[0]?.type || lastSavedServiceTransaction.paymentMethod}</span>}
                          </span>
                          <span style={{ color: 'var(--text-muted)' }}>{t.status || 'Status'}:</span>
                          <span style={{ fontWeight: 700, color: statusColor }}>{statusLabel}</span>
                        </div>
                        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>Total</span>
                          <span style={{ fontSize: 16, fontWeight: 800, color: '#059669' }}>{fmt(lastSavedServiceTransaction.totalRevenue)}</span>
                        </div>
                      </div>
                    );
                  })()}

                  <button onClick={() => generateInvoicePDF({
                    ...lastSavedServiceTransaction,
                    // Map first item's keys for legacy PDF paths
                    productName: lastSavedServiceTransaction.serviceName,
                    sellingPrice: lastSavedServiceTransaction.price,
                    // Keep real payments + isPaid so the PDF shows correct status
                    payments: lastSavedServiceTransaction.payments || [{ type: lastSavedServiceTransaction.paymentMethod, amount: lastSavedServiceTransaction.totalRevenue }],
                    isPaid: lastSavedServiceTransaction.isPaid
                  }, getBizInfo(), paymentAccounts)}
                    style={{ width: '100%', padding: '11px 0', borderRadius: 14, background: '#111827', color: '#A3E635', border: 'none', fontWeight: 700, fontSize: 15, cursor: 'pointer', marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                    {t.downloadInvoice || 'Download Invoice'}
                  </button>
                  <button onClick={() => { setShowServiceInvoicePreview(false); setLastSavedServiceTransaction(null); }}
                    style={{ width: '100%', padding: '12px 0', borderRadius: 14, background: 'var(--bg-tab)', color: 'var(--text-primary)', border: 'none', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
                    {t.done || 'Done'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════════
             REPORTS TAB -all business types
        ══════════════════════════════════════════ */}
        {activeTab === 'reports' && (
          <div style={{ minHeight: '100vh', background: 'var(--bg-main)', paddingBottom: 80 }}>
            {!isDataReady ? (
              <div role="status" aria-label={t.loading || 'Loading'} style={{ padding: '24px 20px' }}>
                <div className="skeleton skeleton-title" style={{ width: '30%' }}></div>
                <div className="skeleton skeleton-text" style={{ width: '55%', marginBottom: 20 }}></div>
                <div style={{ background: 'var(--bg-white)', borderRadius: 16, padding: 20, border: '1px solid var(--border-color)' }}>
                  <div className="skeleton skeleton-heading" style={{ marginBottom: 16 }}></div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
                    {[1,2,3,4,5,6].map(i => <div key={i} className="skeleton skeleton-report-card"></div>)}
                  </div>
                </div>
              </div>
            ) : (
            <>
            <div style={{ padding: '24px 20px 16px' }}>
              <h1 style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 4px' }}>
                {language === 'sw' ? 'Ripoti' : 'Reports'}
              </h1>
              <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>
                {language === 'sw' ? 'Chagua ripoti unayotaka kuona' : 'Select a report to view'}
              </p>
            </div>

            <div style={{ padding: '0 20px', display: 'flex', flexDirection: 'column', gap: 20 }}>

              {!showCashflowReport ? (
                <>
              {/* PRIMARY REPORTS SECTION */}
              <div style={{ background: 'var(--bg-white)', borderRadius: 16, padding: '20px', border: '1px solid var(--border-color)' }}>
                <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 14px', paddingBottom: 12, borderBottom: '1px solid var(--border-color)' }}>
                  {language === 'sw' ? 'Ripoti za Msingi' : 'Primary Reports'}
                </h2>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>

                  {/* Sales Report -hidden for service_only */}
                  {businessType !== 'service_only' && (
                    <button onClick={() => { setDrilldownFromTab('reports'); setActiveTab('sales'); setIsSalesReportOpen(true); }}
                      style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 18px', borderRadius: 14, background: '#F0FDF4', border: '1px solid #BBF7D0', cursor: 'pointer', textAlign: 'left', width: '100%' }}>
                      <div style={{ width: 40, height: 40, borderRadius: 10, background: '#DCFCE7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#166534" strokeWidth="2" strokeLinecap="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>
                      </div>
                      <div><div style={{ fontSize: 14, fontWeight: 700, color: '#166534' }}>{language === 'sw' ? 'Ripoti ya Mauzo' : 'Sales Report'}</div><div style={{ fontSize: 12, color: '#16A34A', marginTop: 2 }}>{language === 'sw' ? 'Takwimu za mauzo' : 'Sales performance & data'}</div></div>
                    </button>
                  )}

                  {/* Services Report -hidden for products_only */}
                  {businessType !== 'products_only' && (
                    <button onClick={() => { setDrilldownFromTab('reports'); setActiveTab('services'); setShowServiceReports(true); }}
                      style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 18px', borderRadius: 14, background: '#ECFDF5', border: '1px solid #A5F3FC', cursor: 'pointer', textAlign: 'left', width: '100%' }}>
                      <div style={{ width: 40, height: 40, borderRadius: 10, background: '#CFFAFE', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                      </div>
                      <div><div style={{ fontSize: 14, fontWeight: 700, color: '#059669' }}>{language === 'sw' ? 'Ripoti ya Huduma' : 'Services Report'}</div><div style={{ fontSize: 12, color: '#0891B2', marginTop: 2 }}>{language === 'sw' ? 'Takwimu za huduma' : 'Services performance & data'}</div></div>
                    </button>
                  )}

                  {/* Purchases Report -hidden for service_only */}
                  {businessType !== 'service_only' && (
                    <button onClick={() => { setDrilldownFromTab('reports'); setActiveTab('purchases'); setIsPurchasesReportOpen(true); }}
                      style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 18px', borderRadius: 14, background: '#FFF7ED', border: '1px solid #FED7AA', cursor: 'pointer', textAlign: 'left', width: '100%' }}>
                      <div style={{ width: 40, height: 40, borderRadius: 10, background: '#FFEDD5', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#92400E" strokeWidth="2" strokeLinecap="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>
                      </div>
                      <div><div style={{ fontSize: 14, fontWeight: 700, color: '#92400E' }}>{language === 'sw' ? 'Ripoti ya Manunuzi' : 'Purchases Report'}</div><div style={{ fontSize: 12, color: '#B45309', marginTop: 2 }}>{language === 'sw' ? 'Takwimu za manunuzi' : 'Purchases analysis & data'}</div></div>
                    </button>
                  )}

                  {/* Expenses Report */}
                  <button onClick={() => { setDrilldownFromTab('reports'); setActiveTab('expenses'); setIsExpensesReportOpen(true); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 18px', borderRadius: 14, background: '#FFF1F2', border: '1px solid #FECDD3', cursor: 'pointer', textAlign: 'left', width: '100%' }}>
                    <div style={{ width: 40, height: 40, borderRadius: 10, background: '#FFE4E6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#BE123C" strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                    </div>
                    <div><div style={{ fontSize: 14, fontWeight: 700, color: '#BE123C' }}>{language === 'sw' ? 'Ripoti ya Matumizi' : 'Expenses Report'}</div><div style={{ fontSize: 12, color: '#E11D48', marginTop: 2 }}>{language === 'sw' ? 'Takwimu za matumizi' : 'Expenses tracking & data'}</div></div>
                  </button>

                  {/* Stock Report -hidden for service_only */}
                  {businessType !== 'service_only' && (
                    <button onClick={() => setShowStockReport(true)}
                      style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 18px', borderRadius: 14, background: '#F5F3FF', border: '1px solid #C7D2FE', cursor: 'pointer', textAlign: 'left', width: '100%' }}>
                      <div style={{ width: 40, height: 40, borderRadius: 10, background: '#E0E7FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3730A3" strokeWidth="2" strokeLinecap="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="9" y1="9" x2="9" y2="15"/><line x1="15" y1="9" x2="15" y2="15"/><line x1="9" y1="15" x2="15" y2="15"/><line x1="9" y1="9" x2="15" y2="9"/></svg>
                      </div>
                      <div><div style={{ fontSize: 14, fontWeight: 700, color: '#3730A3' }}>{language === 'sw' ? 'Muhtasari wa Thamani ya Bidhaa' : 'Inventory Valuation Summary'}</div><div style={{ fontSize: 12, color: '#4F46E5', marginTop: 2 }}>{language === 'sw' ? 'Maelezo ya bidhaa za stock' : 'Current inventory details'}</div></div>
                    </button>
                  )}

                </div>
              </div>

              {/* DEBTS REPORTS SECTION */}
              <div style={{ background: 'var(--bg-white)', borderRadius: 16, padding: '20px', border: '1px solid var(--border-color)' }}>
                <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 14px', paddingBottom: 12, borderBottom: '1px solid var(--border-color)' }}>
                  {language === 'sw' ? 'Ripoti za Madeni' : 'Debts Reports'}
                </h2>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
                  {/* Accounts Receivable Report */}
                  <button onClick={() => { setDrilldownFromTab('reports'); setActiveTab('dashboard'); setActiveDrilldown('receivables'); setDrilldownPeriod(dashboardTimeFilter); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 18px', borderRadius: 14, background: '#FEFCE8', border: '1px solid #FDE68A', cursor: 'pointer', textAlign: 'left', width: '100%' }}>
                    <div style={{ width: 40, height: 40, borderRadius: 10, background: '#FEF3C7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#854D0E" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="6" x2="12" y2="12"/><line x1="12" y1="12" x2="16" y2="14"/></svg>
                    </div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#854D0E' }}>{language === 'sw' ? 'Ripoti ya Wateja Wanaodaiwa' : 'Accounts Receivable'}</div>
                      <div style={{ fontSize: 12, color: '#A16207', marginTop: 2 }}>{language === 'sw' ? 'Wateja wanaokudai pesa' : 'Customers who owe you'} · {fmt(totalReceivables)}</div>
                    </div>
                  </button>

                  {/* Accounts Payable Report */}
                  <button onClick={() => { setDrilldownFromTab('reports'); setActiveTab('dashboard'); setActiveDrilldown('payables'); setDrilldownPeriod(dashboardTimeFilter); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 18px', borderRadius: 14, background: '#FFF1F2', border: '1px solid #FECDD3', cursor: 'pointer', textAlign: 'left', width: '100%' }}>
                    <div style={{ width: 40, height: 40, borderRadius: 10, background: '#FFE4E6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#BE123C" strokeWidth="2" strokeLinecap="round"><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/><line x1="12" y1="22" x2="12" y2="12"/></svg>
                    </div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#BE123C' }}>{language === 'sw' ? 'Ripoti ya Wauzaji Unaodai' : 'Accounts Payable'}</div>
                      <div style={{ fontSize: 12, color: '#E11D48', marginTop: 2 }}>{language === 'sw' ? 'Wauzaji unaowadai pesa' : 'Suppliers you owe'} · {fmt(totalPayables)}</div>
                    </div>
                  </button>
                </div>
              </div>

              {/* CASHFLOW REPORTS SECTION -hidden as a whole when cashflowReports toggled */}
              {!isItemHidden('reports', 'cashflowReports') && (
              <div style={{ background: 'var(--bg-white)', borderRadius: 16, padding: '20px', border: '1px solid var(--border-color)' }}>
                <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 14px', paddingBottom: 12, borderBottom: '1px solid var(--border-color)' }}>
                  {language === 'sw' ? 'Ripoti za Mtiririko wa Ki-Fedha' : 'Cashflow Reports'}
                </h2>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>

                  {/* Cash Account Report -opens the same drilldown as the Dashboard click */}
                  <button onClick={() => { setDrilldownFromTab('reports'); setActiveTab('dashboard'); setActiveDrilldown('cash'); setDrilldownPeriod('year'); setSelectedBankAccount('all'); setSelectedMobileMoneyAccount('all'); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 18px', borderRadius: 14, background: '#F0FDF4', border: '1px solid #BBF7D0', cursor: 'pointer', textAlign: 'left', width: '100%' }}>
                    <div style={{ width: 40, height: 40, borderRadius: 10, background: '#DCFCE7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#166534" strokeWidth="2" strokeLinecap="round"><rect x="2" y="6" width="20" height="12" rx="2"/><circle cx="12" cy="12" r="2"/><path d="M6 12h.01M18 12h.01"/></svg>
                    </div>
                    <div><div style={{ fontSize: 14, fontWeight: 700, color: '#166534' }}>{language === 'sw' ? 'Ripoti ya Akaunti ya Pesa Taslimu' : 'Cash Account(s) Report'}</div><div style={{ fontSize: 12, color: '#16A34A', marginTop: 2 }}>{language === 'sw' ? 'Maelezo ya pesa taslimu' : 'Cash transactions & details'}</div></div>
                  </button>

                  {/* Bank Account Report -opens the same drilldown as the Dashboard click */}
                  <button onClick={() => { setDrilldownFromTab('reports'); setActiveTab('dashboard'); setActiveDrilldown('bank'); setDrilldownPeriod('year'); setSelectedBankAccount('all'); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 18px', borderRadius: 14, background: '#EFF6FF', border: '1px solid #BFDBFE', cursor: 'pointer', textAlign: 'left', width: '100%' }}>
                    <div style={{ width: 40, height: 40, borderRadius: 10, background: '#DBEAFE', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1E40AF" strokeWidth="2" strokeLinecap="round"><line x1="3" y1="22" x2="21" y2="22"/><line x1="6" y1="18" x2="6" y2="11"/><line x1="10" y1="18" x2="10" y2="11"/><line x1="14" y1="18" x2="14" y2="11"/><line x1="18" y1="18" x2="18" y2="11"/><polygon points="12 2 20 7 4 7"/></svg>
                    </div>
                    <div><div style={{ fontSize: 14, fontWeight: 700, color: '#1E3A8A' }}>{language === 'sw' ? 'Ripoti ya Akaunti za Benki' : 'Bank Account(s) Report'}</div><div style={{ fontSize: 12, color: '#2563EB', marginTop: 2 }}>{language === 'sw' ? 'Maelezo ya benki' : 'Bank transactions & details'}</div></div>
                  </button>

                  {/* Mobile Money Account Report -opens the same drilldown as the Dashboard click */}
                  <button onClick={() => { setDrilldownFromTab('reports'); setActiveTab('dashboard'); setActiveDrilldown('mobilemoney'); setDrilldownPeriod('year'); setSelectedMobileMoneyAccount('all'); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 18px', borderRadius: 14, background: '#F5F3FF', border: '1px solid #C7D2FE', cursor: 'pointer', textAlign: 'left', width: '100%' }}>
                    <div style={{ width: 40, height: 40, borderRadius: 10, background: '#E0E7FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6B21B6" strokeWidth="2" strokeLinecap="round"><rect x="5" y="2" width="14" height="20" rx="2" ry="2"/><line x1="12" y1="18" x2="12.01" y2="18"/></svg>
                    </div>
                    <div><div style={{ fontSize: 14, fontWeight: 700, color: '#6B21B6' }}>{language === 'sw' ? 'Ripoti ya Akaunti za Simu/Wakala' : 'Mobile Money Account(s) Report'}</div><div style={{ fontSize: 12, color: '#4F46E5', marginTop: 2 }}>{language === 'sw' ? 'Maelezo ya simu/wakala' : 'Mobile money transactions & details'}</div></div>
                  </button>

                  {/* Total Cashflow Report */}
                  <button onClick={() => { setDrilldownFromTab('reports'); setActiveTab('dashboard'); setActiveDrilldown('total'); setDrilldownPeriod('year'); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 18px', borderRadius: 14, background: '#F0FDF4', border: '1px solid #A7F3D0', cursor: 'pointer', textAlign: 'left', width: '100%' }}>
                    <div style={{ width: 40, height: 40, borderRadius: 10, background: '#CCFBF1', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0D9488" strokeWidth="2" strokeLinecap="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>
                    </div>
                    <div><div style={{ fontSize: 14, fontWeight: 700, color: '#0D9488' }}>{language === 'sw' ? 'Ripoti ya Mtiririko wa Ki-Fedha' : 'Total Cashflow Report'}</div><div style={{ fontSize: 12, color: '#059669', marginTop: 2 }}>{language === 'sw' ? 'Muhtasari wa jumla ya mtiririko' : 'Overall cashflow summary'}</div></div>
                  </button>

                </div>
              </div>
              )}

              {/* QUICK REPORTS SECTION */}
              <div style={{ background: 'var(--bg-white)', borderRadius: 16, padding: '20px', border: '1px solid var(--border-color)' }}>
                <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 14px', paddingBottom: 12, borderBottom: '1px solid var(--border-color)' }}>
                  {language === 'sw' ? 'Ripoti za Haraka' : 'Quick Reports'}
                </h2>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>

                  {/* Customer Statement */}
                  <button onClick={() => setShowCustomerStatement(true)}
                    style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 18px', borderRadius: 14, background: '#ECFEFF', border: '1px solid #A5F3FC', cursor: 'pointer', textAlign: 'left', width: '100%' }}>
                    <div style={{ width: 40, height: 40, borderRadius: 10, background: '#CFFAFE', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0E7490" strokeWidth="2" strokeLinecap="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
                    </div>
                    <div><div style={{ fontSize: 14, fontWeight: 700, color: '#164E63' }}>{language === 'sw' ? 'Taarifa ya Mteja' : 'Customer Statement'}</div><div style={{ fontSize: 12, color: '#0891B2', marginTop: 2 }}>{language === 'sw' ? 'Deni la kila mteja' : 'What each customer owes'}</div></div>
                  </button>

                  {/* Aging Report */}
                  <button onClick={() => setShowAgingReport(true)}
                    style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 18px', borderRadius: 14, background: '#FEF9C3', border: '1px solid #FDE68A', cursor: 'pointer', textAlign: 'left', width: '100%' }}>
                    <div style={{ width: 40, height: 40, borderRadius: 10, background: '#FEF3C7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#92400E" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                    </div>
                    <div><div style={{ fontSize: 14, fontWeight: 700, color: '#92400E' }}>{language === 'sw' ? 'Ripoti ya Umri wa Deni' : 'Aging Report'}</div><div style={{ fontSize: 12, color: '#B45309', marginTop: 2 }}>{language === 'sw' ? 'Madeni yaliyopita muda' : 'Overdue receivables breakdown'}</div></div>
                  </button>

                  {/* Daily Summary */}
                  <button onClick={() => setShowDailySummary(true)}
                    style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 18px', borderRadius: 14, background: '#F0FDF4', border: '1px solid #BBF7D0', cursor: 'pointer', textAlign: 'left', width: '100%' }}>
                    <div style={{ width: 40, height: 40, borderRadius: 10, background: '#DCFCE7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#166534" strokeWidth="2" strokeLinecap="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                    </div>
                    <div><div style={{ fontSize: 14, fontWeight: 700, color: '#166534' }}>{language === 'sw' ? 'Muhtasari wa Siku' : 'Daily Summary'}</div><div style={{ fontSize: 12, color: '#16A34A', marginTop: 2 }}>{language === 'sw' ? 'Shughuli za leo' : "Today's transactions"}</div></div>
                  </button>

                  {/* Profit & Loss */}
                  {!isItemHidden('reports', 'plReport') && (
                  <button onClick={() => setShowPandL(true)}
                    style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 18px', borderRadius: 14, background: '#F0FDF4', border: '1px solid #BBF7D0', cursor: 'pointer', textAlign: 'left', width: '100%' }}>
                    <div style={{ width: 40, height: 40, borderRadius: 10, background: '#DCFCE7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#166534" strokeWidth="2" strokeLinecap="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>
                    </div>
                    <div><div style={{ fontSize: 14, fontWeight: 700, color: '#166534' }}>{language === 'sw' ? 'Faida na Hasara' : 'Profit & Loss'}</div><div style={{ fontSize: 12, color: '#16A34A', marginTop: 2 }}>{language === 'sw' ? 'Mapato dhidi ya gharama' : 'Revenue vs costs overview'}</div></div>
                  </button>
                  )}

                  {/* WHT Report */}
                  {!isItemHidden('reports', 'whtVat') && (
                  <button onClick={() => setShowWHTReport(true)}
                    style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 18px', borderRadius: 14, background: '#FFF7ED', border: '1px solid #FED7AA', cursor: 'pointer', textAlign: 'left', width: '100%' }}>
                    <div style={{ width: 40, height: 40, borderRadius: 10, background: '#FFEDD5', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#92400E" strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/></svg>
                    </div>
                    <div><div style={{ fontSize: 14, fontWeight: 700, color: '#92400E' }}>{language === 'sw' ? 'Ripoti ya WHT' : 'WHT Report'}</div><div style={{ fontSize: 12, color: '#B45309', marginTop: 2 }}>{language === 'sw' ? 'Kodi ya kuzuia' : 'Withholding tax collected'}</div></div>
                  </button>
                  )}

                  {/* VAT Report -only for VAT-registered businesses */}
                  {vatRegistered && !isItemHidden('reports', 'whtVat') && (
                  <button onClick={() => setShowVATReport(true)}
                    style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 18px', borderRadius: 14, background: '#F0FDF4', border: '1px solid #BBF7D0', cursor: 'pointer', textAlign: 'left', width: '100%' }}>
                    <div style={{ width: 40, height: 40, borderRadius: 10, background: '#DCFCE7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#166534" strokeWidth="2" strokeLinecap="round"><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>
                    </div>
                    <div><div style={{ fontSize: 14, fontWeight: 700, color: '#166534' }}>{language === 'sw' ? 'Ripoti ya VAT' : 'VAT Report'}</div><div style={{ fontSize: 12, color: '#16A34A', marginTop: 2 }}>{language === 'sw' ? 'Kodi ya ongezeko la thamani' : 'Value added tax summary'}</div></div>
                  </button>
                  )}

                  {/* Stock Valuation duplicate removed -see the primary
                      "Inventory Valuation Summary" card above. */}

                  {/* Returns Report -hidden for service_only */}
                  {businessType !== 'service_only' && (
                    <button onClick={() => { setDrilldownFromTab('reports'); setShowReturnsReport(true); }}
                      style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 18px', borderRadius: 14, background: '#FEF2F2', border: '1px solid #FECACA', cursor: 'pointer', textAlign: 'left', width: '100%' }}>
                      <div style={{ width: 40, height: 40, borderRadius: 10, background: '#FEE2E2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2" strokeLinecap="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.51"/></svg>
                      </div>
                      <div><div style={{ fontSize: 14, fontWeight: 700, color: '#DC2626' }}>{language === 'sw' ? 'Ripoti ya Mauzo Yaliyorudishwa' : 'Returns Report'}</div><div style={{ fontSize: 12, color: '#EF4444', marginTop: 2 }}>{language === 'sw' ? 'Bidhaa zilizorudishwa' : 'Sales returns breakdown'}</div></div>
                    </button>
                  )}

                  {/* Purchase Returns -hidden for service_only */}
                  {businessType !== 'service_only' && (
                    <button onClick={() => { setDrilldownFromTab('reports'); setShowPurchaseReturnsReport(true); }}
                      style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 18px', borderRadius: 14, background: '#FEF2F2', border: '1px solid #FECACA', cursor: 'pointer', textAlign: 'left', width: '100%' }}>
                      <div style={{ width: 40, height: 40, borderRadius: 10, background: '#FEE2E2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2" strokeLinecap="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.51"/></svg>
                      </div>
                      <div><div style={{ fontSize: 14, fontWeight: 700, color: '#DC2626' }}>{language === 'sw' ? 'Manunuzi Yaliyorudishwa' : 'Purchase Returns'}</div><div style={{ fontSize: 12, color: '#EF4444', marginTop: 2 }}>{language === 'sw' ? 'Manunuzi yaliyorudishwa' : 'Purchase returns breakdown'}</div></div>
                    </button>
                  )}

                </div>
              </div>
                </>
              ) : (
                <>
                  {showCashflowReport && (() => {
                    // Helper function to get payment method display label
                    const getPaymentMethodLabel = (method) => {
                      if (!method) return language === 'sw' ? 'Pesa Taslimu' : 'Cash';
                      const map = {
                        'cash': language === 'sw' ? 'Pesa Taslimu' : 'Cash',
                        'bank': language === 'sw' ? 'Benki' : 'Bank',
                        'mobilemoney': language === 'sw' ? 'Simu Pesa' : 'Mobile Money',
                      };
                      return map[method] || method;
                    };

                    // Get report title based on type
                    const getReportTitle = () => {
                      if (cashflowReportType === 'cash') return language === 'sw' ? 'Ripoti ya Pesa Taslimu' : 'Cash Report';
                      if (cashflowReportType === 'bank') return language === 'sw' ? 'Ripoti ya Akaunti za Benki' : 'Bank Accounts Report';
                      if (cashflowReportType === 'mobilemoney') return language === 'sw' ? 'Ripoti ya Simu Pesa' : 'Mobile Money Report';
                      return language === 'sw' ? 'Ripoti ya Mtiririko wa Pesa' : 'Cashflow Report';
                    };

                    // Get selected account details
                    const getSelectedAccountName = () => {
                      if (cashflowReportType === 'bank' && selectedBankAccount !== 'all') {
                        const bankAccts = (paymentAccounts || []).filter(a => a.type === 'bank');
                        if (bankAccts[selectedBankAccount]) {
                          return bankAccts[selectedBankAccount].accountName;
                        }
                      }
                      if (cashflowReportType === 'mobilemoney' && selectedMobileMoneyAccount !== 'all') {
                        const mmAccts = (paymentAccounts || []).filter(a => a.type === 'mobilemoney');
                        if (mmAccts[selectedMobileMoneyAccount]) {
                          return mmAccts[selectedMobileMoneyAccount].accountName;
                        }
                      }
                      return '';
                    };

                    // Filter transactions by payment method and selected account
                    const filterByPaymentMethod = (items) => {
                      if (cashflowReportType === 'total') return items;

                      let filtered = items.filter(item => {
                        const method = item.paymentMethod || 'cash';
                        return method === cashflowReportType;
                      });

                      // Further filter by selected account
                      if (cashflowReportType === 'bank' && selectedBankAccount !== 'all') {
                        const bankAccts = (paymentAccounts || []).filter(a => a.type === 'bank');
                        const selectedAcctName = bankAccts[selectedBankAccount]?.accountName;
                        filtered = filtered.filter(item => item.accountName === selectedAcctName);
                      }

                      if (cashflowReportType === 'mobilemoney' && selectedMobileMoneyAccount !== 'all') {
                        const mmAccts = (paymentAccounts || []).filter(a => a.type === 'mobilemoney');
                        const selectedAcctName = mmAccts[selectedMobileMoneyAccount]?.accountName;
                        filtered = filtered.filter(item => item.accountName === selectedAcctName);
                      }

                      return filtered;
                    };

                    // Get all transactions by type -only include paid ones (money actually moved)
                    const salesProducts = (sales || []).filter(s => (s.type === 'product' || !s.type) && s.isPaid !== false);
                    const salesServices = (sales || []).filter(s => s.type === 'service' && s.isPaid !== false);
                    const filteredSalesProducts = filterByPaymentMethod(salesProducts);
                    const filteredSalesServices = filterByPaymentMethod(salesServices);
                    const filteredPurchases = filterByPaymentMethod((purchases || []).filter(p => p.isPaid !== false));
                    const filteredExpenses = filterByPaymentMethod((expenses || []).filter(e => e.isPaid !== false));

                    // Include manualTransactions (Record Payment entries, reversals, manual adjustments)
                    const manualCredits = (manualTransactions || []).filter(mt => {
                      if (cashflowReportType !== 'total' && mt.account !== cashflowReportType) return false;
                      return mt.type === 'credit';
                    });
                    const manualDebits = (manualTransactions || []).filter(mt => {
                      if (cashflowReportType !== 'total' && mt.account !== cashflowReportType) return false;
                      return mt.type === 'debit';
                    });

                    // Apply date and category filters
                    const applyFilters = (items) => {
                      return items.filter(item => {
                        const itemDate = item.date || '';
                        if (cashflowReportFilter.startDate && itemDate < cashflowReportFilter.startDate) return false;
                        if (cashflowReportFilter.endDate && itemDate > cashflowReportFilter.endDate) return false;
                        return true;
                      });
                    };

                    // Apply category filter
                    const categoryFilter = cashflowReportFilter.category;
                    let displaySalesProducts = [], displaySalesServices = [], displayPurchases = [], displayExpenses = [];

                    if (!categoryFilter || categoryFilter === '' || categoryFilter === 'all') {
                      displaySalesProducts = applyFilters(filteredSalesProducts);
                      displaySalesServices = applyFilters(filteredSalesServices);
                      displayPurchases = applyFilters(filteredPurchases);
                      displayExpenses = applyFilters(filteredExpenses);
                    } else if (categoryFilter === 'sales_products') {
                      displaySalesProducts = applyFilters(filteredSalesProducts);
                    } else if (categoryFilter === 'sales_services') {
                      displaySalesServices = applyFilters(filteredSalesServices);
                    } else if (categoryFilter === 'purchases') {
                      displayPurchases = applyFilters(filteredPurchases);
                    } else if (categoryFilter === 'expenses') {
                      displayExpenses = applyFilters(filteredExpenses);
                    }

                    // Combine all transactions with type info
                    const allTransactions = [
                      ...displaySalesProducts.map(s => ({ ...s, _type: 'sale_product', _category: 'sales_products' })),
                      ...displaySalesServices.map(s => ({ ...s, _type: 'sale_service', _category: 'sales_services' })),
                      ...displayPurchases.map(p => ({ ...p, _type: 'purchase', _category: 'purchases' })),
                      ...displayExpenses.map(e => ({ ...e, _type: 'expense', _category: 'expenses' })),
                      ...manualCredits.map(mt => ({ ...mt, _type: 'manual_credit', _category: 'manual', totalRevenue: mt.amount, productName: mt.description || 'Payment received' })),
                      ...manualDebits.map(mt => ({ ...mt, _type: 'manual_debit', _category: 'manual', totalCost: mt.amount, productName: mt.description || 'Payment out' })),
                    ].sort((a, b) => new Date(b.date) - new Date(a.date));

                    // Calculate totals -include manual credits/debits
                    const totalIncome = displaySalesProducts.reduce((sum, s) => sum + parseFloat(s.totalRevenue || 0), 0) +
                                       displaySalesServices.reduce((sum, s) => sum + parseFloat(s.totalRevenue || 0), 0) +
                                       manualCredits.reduce((sum, mt) => sum + parseFloat(mt.amount || 0), 0);
                    const totalExpenses = displayPurchases.reduce((sum, p) => sum + getPurchaseGrandTotal(p), 0) +
                                         displayExpenses.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0) +
                                         manualDebits.reduce((sum, mt) => sum + parseFloat(mt.amount || 0), 0);
                    const netCashflow = totalIncome - totalExpenses;

                    // Opening / closing / current balances -derived from the SINGLE
                    // cash ledger, so they match the dashboard exactly and correctly
                    // handle split payments, partial payments, refunds and specific
                    // sub-accounts (bank|id). Opening = money in the wallet before the
                    // period start; closing = up to and including the period end;
                    // current = everything to date.
                    const calculateBalances = () => {
                      if (cashflowReportType === 'cash' || cashflowReportType === 'total') {
                        return { opening: 0, closing: 0, current: 0 };
                      }
                      const ledger = cashLedgerData.ledger;
                      const walletMatch = (r) => {
                        if (cashflowReportType === 'bank') {
                          if (r.wallet !== 'bank') return false;
                          if (selectedBankAccount === 'all') return true;
                          const acc = (paymentAccounts || []).filter(a => a.type === 'bank')[selectedBankAccount];
                          return acc ? r.accountKey === `bank|${acc.id}` : true;
                        }
                        if (cashflowReportType === 'mobilemoney') {
                          if (r.wallet !== 'mobilemoney') return false;
                          if (selectedMobileMoneyAccount === 'all') return true;
                          const acc = (paymentAccounts || []).filter(a => a.type === 'mobilemoney')[selectedMobileMoneyAccount];
                          return acc ? r.accountKey === `mobilemoney|${acc.id}` : true;
                        }
                        return false;
                      };
                      return walletStatement(ledger, walletMatch, cashflowReportFilter.startDate, cashflowReportFilter.endDate);
                    };

                    const balances = calculateBalances();

                    // ── Wallet statement lines (bank / mobile money) ──────────
                    // Render the ACTUAL ledger rows for the selected wallet, with a
                    // running balance, so the user can see Opening + listed = Closing.
                    const isWalletStatement = cashflowReportType === 'bank' || cashflowReportType === 'mobilemoney';
                    const ledgerRowLabel = (r) => {
                      const sw = language === 'sw';
                      if (r.source === 'opening') return sw ? 'Salio la awali' : 'Opening balance';
                      if (r.source === 'sale') { const s = (sales || []).find(x => String(x.id) === String(r.refId)); return (sw ? 'Mauzo' : 'Sale') + (s?.customerName ? ': ' + s.customerName : (s?.invoiceNumber ? ': ' + s.invoiceNumber : '')); }
                      if (r.source === 'manualInvoice') return sw ? 'Malipo ya ankara' : 'Invoice payment';
                      if (r.source === 'service') return sw ? 'Huduma' : 'Service';
                      if (r.source === 'purchase') { const p = (purchases || []).find(x => String(x.id) === String(r.refId)); return (sw ? 'Ununuzi' : 'Purchase') + (p?.supplier ? ': ' + p.supplier : ''); }
                      if (r.source === 'expense') { const e = (expenses || []).find(x => String(x.id) === String(r.refId)); return (sw ? 'Matumizi' : 'Expense') + (e?.category ? ': ' + e.category : ''); }
                      if (r.source === 'salesReturn') return sw ? 'Marejesho ya mteja' : 'Customer refund';
                      if (r.source === 'purchaseReturn') return sw ? 'Marejesho ya muuzaji' : 'Supplier refund';
                      if (r.source === 'manual') { const m = (manualTransactions || []).find(x => String(x.id) === String(r.refId)); return m?.description || (sw ? 'Marekebisho' : 'Adjustment'); }
                      return r.source || '—';
                    };
                    // Reference back to the originating document (invoice/receipt).
                    const ledgerRowRef = (r) => {
                      if (r.source === 'sale') { const s = (sales || []).find(x => String(x.id) === String(r.refId)); return s?.invoiceNumber || '—'; }
                      if (r.source === 'manualInvoice') { const m = (manualInvoices || []).find(x => String(x.id) === String(r.refId)); return m?.invoiceNumber || '—'; }
                      if (r.source === 'service') { const s = (serviceTransactions || []).find(x => String(x.id) === String(r.refId)); return s?.invoiceNumber || '—'; }
                      if (r.source === 'purchase') { const p = (purchases || []).find(x => String(x.id) === String(r.refId)); return p?.invoiceNumber || p?.efdReceiptNo || '—'; }
                      if (r.source === 'salesReturn') { const rr = (salesReturns || []).find(x => String(x.id) === String(r.refId)); return rr?.invoiceNumber || '—'; }
                      return '—';
                    };
                    const ledgerLines = isWalletStatement
                      ? [...(balances.periodRows || [])]
                          .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0))
                          .reduce((acc, r) => {
                            const prev = acc.length ? acc[acc.length - 1].running : (balances.opening || 0);
                            const signed = r.direction === 'in' ? r.amount : -r.amount;
                            acc.push({ ...r, signed, running: prev + signed, label: ledgerRowLabel(r), ref: ledgerRowRef(r) });
                            return acc;
                          }, [])
                      : null;

                    // Get type label
                    const getTypeLabel = (item) => {
                      if (item._type === 'sale_product') return language === 'sw' ? 'Bidhaa' : 'Product Sale';
                      if (item._type === 'sale_service') return language === 'sw' ? 'Huduma' : 'Service Sale';
                      if (item._type === 'purchase') return language === 'sw' ? 'Ununuzi' : 'Purchase';
                      if (item._type === 'expense') return language === 'sw' ? 'Matumizi' : 'Expense';
                      if (item._type === 'manual_credit') return 'Payment In';
                      if (item._type === 'manual_debit') return 'Payment Out';
                      return '—';
                    };

                    // Get type color
                    const getTypeColor = (item) => {
                      if (item._type === 'sale_product' || item._type === 'sale_service' || item._type === 'manual_credit') return { bg: '#D1FAE5', color: '#065F46' };
                      if (item._type === 'purchase') return { bg: '#F5F3FF', color: '#3730A3' };
                      if (item._type === 'expense' || item._type === 'manual_debit') return { bg: '#FEE2E2', color: '#991B1B' };
                      return { bg: 'var(--bg-tab)', color: 'var(--text-secondary)' };
                    };

                    // Get description
                    const getDescription = (item) => {
                      if (item.productName) return item.productName;
                      if (item.description) return item.description;
                      return '—';
                    };

                    // Get amount
                    const getAmount = (item) => {
                      if (item._type === 'purchase') return item.totalCost || 0;
                      if (item._type === 'expense') return item.amount || 0;
                      return item.totalRevenue || 0;
                    };

                    // Get amount sign
                    const getAmountSign = (item) => {
                      if (item._type === 'purchase' || item._type === 'expense') return '-';
                      return '+';
                    };

                    // Get amount color
                    const getAmountColor = (item) => {
                      if (item._type === 'purchase' || item._type === 'expense') return '#DC2626';
                      return '#059669';
                    };

                    // Prepare download data
                    const prepareDownloadData = () => {
                      const rows = allTransactions.map(tx => [
                        tx.date,
                        getTypeLabel(tx),
                        getDescription(tx),
                        getAmountSign(tx) + fmt(getAmount(tx)),
                        getPaymentMethodLabel(tx.paymentMethod)
                      ]);
                      return [['Date', 'Type', 'Description', 'Amount', 'Payment Method'], ...rows];
                    };

                    return (
                      <div style={{
                        padding: '24px 0',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 20,
                        fontFamily: 'inherit'
                      }}>
                        {/* Header */}
                        <div style={{
                          paddingBottom: 16,
                          borderBottom: '1px solid var(--border-color)'
                        }}>
                          <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)', margin: 0, marginBottom: 4 }}>
                            {getReportTitle()}
                          </h2>
                          <p style={{ margin: 0, fontSize: 12, color: 'var(--text-secondary)' }}>
                            {language === 'sw' ? 'Kupakua na kuanayze mtiririko wa pesa' : 'Download and analyze your cash flow'}
                          </p>
                          <button onClick={() => setShowCashflowReport(false)} style={{
                            position: 'absolute',
                            top: 24,
                            right: 20,
                            background: 'none',
                            border: 'none',
                            fontSize: 32,
                            cursor: 'pointer',
                            color: 'var(--text-secondary)',
                            padding: 0
                          }}>×</button>
                        </div>

                        {/* Download & Action Buttons */}
                        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                          <button
                            onClick={() => downloadCSV(prepareDownloadData(), 'cashflow-report.csv')}
                            style={{
                              flex: 1,
                              minWidth: 140,
                              padding: '10px 16px',
                              borderRadius: 10,
                              background: '#059669',
                              color: '#fff',
                              border: 'none',
                              fontWeight: 700,
                              fontSize: 13,
                              cursor: 'pointer'
                            }}
                          >
                            {language === 'sw' ? 'Kupakua CSV' : 'Download CSV'}
                          </button>
                          <button
                            onClick={() => downloadExcel(prepareDownloadData(), 'cashflow-report.xlsx')}
                            style={{
                              flex: 1,
                              minWidth: 140,
                              padding: '10px 16px',
                              borderRadius: 10,
                              background: '#2563EB',
                              color: '#fff',
                              border: 'none',
                              fontWeight: 700,
                              fontSize: 13,
                              cursor: 'pointer'
                            }}
                          >
                            {language === 'sw' ? 'Kupakua Excel' : 'Download Excel'}
                          </button>
                        </div>

                        {/* Filters Section */}
                        <div style={{
                          background: 'var(--bg-tab)',
                          borderRadius: 12,
                          padding: 16,
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                          gap: 14
                        }}>
                          {/* Account Selector for Bank Reports */}
                          {cashflowReportType === 'bank' && (
                            <div>
                              <label style={{
                                display: 'block',
                                fontWeight: 700,
                                fontSize: 12,
                                color: 'var(--text-primary)',
                                marginBottom: 6,
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                              }}>
                                {language === 'sw' ? 'Akaunti' : 'Account'}
                              </label>
                              <select
                                value={selectedBankAccount}
                                onChange={e => setSelectedBankAccount(e.target.value)}
                                style={{
                                  width: '100%',
                                  padding: '9px 10px',
                                  borderRadius: 8,
                                  border: '1px solid var(--border-color)',
                                  background: 'var(--bg-white)',
                                  fontSize: 12,
                                  outline: 'none',
                                  color: 'var(--text-primary)',
                                  boxSizing: 'border-box',
                                  fontWeight: 600
                                }}
                              >
                                <option value="all">{language === 'sw' ? 'Akaunti Zote za Benki' : 'All Bank Accounts'}</option>
                                {(paymentAccounts || []).filter(a => a.type === 'bank').map((acct, idx) => (
                                  <option key={idx} value={idx}>
                                    {acct.bankName} · {acct.accountName}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}

                          {/* Account Selector for Mobile Money Reports */}
                          {cashflowReportType === 'mobilemoney' && (
                            <div>
                              <label style={{
                                display: 'block',
                                fontWeight: 700,
                                fontSize: 12,
                                color: 'var(--text-primary)',
                                marginBottom: 6,
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                              }}>
                                {language === 'sw' ? 'Akaunti' : 'Account'}
                              </label>
                              <select
                                value={selectedMobileMoneyAccount}
                                onChange={e => setSelectedMobileMoneyAccount(e.target.value)}
                                style={{
                                  width: '100%',
                                  padding: '9px 10px',
                                  borderRadius: 8,
                                  border: '1px solid var(--border-color)',
                                  background: 'var(--bg-white)',
                                  fontSize: 12,
                                  outline: 'none',
                                  color: 'var(--text-primary)',
                                  boxSizing: 'border-box',
                                  fontWeight: 600
                                }}
                              >
                                <option value="all">{language === 'sw' ? 'Akaunti Zote za Simu Pesa' : 'All Mobile Money Accounts'}</option>
                                {(paymentAccounts || []).filter(a => a.type === 'mobilemoney').map((acct, idx) => (
                                  <option key={idx} value={idx}>
                                    {acct.networkName} · {acct.accountName}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}

                          {/* Period Selector */}
                          <div>
                            <label style={{
                              display: 'block',
                              fontWeight: 700,
                              fontSize: 12,
                              color: 'var(--text-primary)',
                              marginBottom: 6,
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}>
                              {language === 'sw' ? 'Kipindi' : 'Period'}
                            </label>
                            {periodPresetBar(cashflowReportFilter.startDate || '', cashflowReportFilter.endDate || '', (f, t) => setCashflowReportFilter({ ...cashflowReportFilter, startDate: f, endDate: t }))}
                            <div style={{ display: 'flex', gap: 8 }}>
                              <input
                                type="date"
                                value={cashflowReportFilter.startDate || ''}
                                onChange={e => setCashflowReportFilter({ ...cashflowReportFilter, startDate: e.target.value })}
                                style={{
                                  flex: 1,
                                  padding: '9px 10px',
                                  borderRadius: 8,
                                  border: '1px solid var(--border-color)',
                                  background: 'var(--bg-white)',
                                  fontSize: 12,
                                  outline: 'none',
                                  color: 'var(--text-primary)',
                                  boxSizing: 'border-box'
                                }}
                              />
                              <span style={{ display: 'flex', alignItems: 'center', color: 'var(--text-secondary)' }}>→</span>
                              <input
                                type="date"
                                value={cashflowReportFilter.endDate || ''}
                                onChange={e => setCashflowReportFilter({ ...cashflowReportFilter, endDate: e.target.value })}
                                style={{
                                  flex: 1,
                                  padding: '9px 10px',
                                  borderRadius: 8,
                                  border: '1px solid var(--border-color)',
                                  background: 'var(--bg-white)',
                                  fontSize: 12,
                                  outline: 'none',
                                  color: 'var(--text-primary)',
                                  boxSizing: 'border-box'
                                }}
                              />
                            </div>
                          </div>

                          {/* Category Filter (for Cash and Total reports) */}
                          {(cashflowReportType === 'cash' || cashflowReportType === 'total') && (
                            <div>
                              <label style={{
                                display: 'block',
                                fontWeight: 700,
                                fontSize: 12,
                                color: 'var(--text-primary)',
                                marginBottom: 6,
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                              }}>
                                {language === 'sw' ? 'Kategoria' : 'Category'}
                              </label>
                              <select
                                value={cashflowReportFilter.category || ''}
                                onChange={e => setCashflowReportFilter({ ...cashflowReportFilter, category: e.target.value })}
                                style={{
                                  width: '100%',
                                  padding: '9px 10px',
                                  borderRadius: 8,
                                  border: '1px solid var(--border-color)',
                                  background: 'var(--bg-white)',
                                  fontSize: 12,
                                  outline: 'none',
                                  color: 'var(--text-primary)',
                                  boxSizing: 'border-box',
                                  fontWeight: 600
                                }}
                              >
                                <option value="">{language === 'sw' ? 'Zote' : 'All'}</option>
                                <option value="sales_products">{language === 'sw' ? 'Mauzo (Bidhaa)' : 'Sales (Products)'}</option>
                                <option value="sales_services">{language === 'sw' ? 'Mauzo (Huduma)' : 'Sales (Services)'}</option>
                                <option value="purchases">{language === 'sw' ? 'Ununuzi' : 'Purchases'}</option>
                                <option value="expenses">{language === 'sw' ? 'Matumizi' : 'Expenses'}</option>
                              </select>
                            </div>
                          )}
                        </div>

                        {/* Summary Cards */}
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                          gap: 14
                        }}>
                          {(cashflowReportType === 'bank' || cashflowReportType === 'mobilemoney') ? (
                            <>
                              {/* Opening Balance */}
                              <div style={{
                                background: 'linear-gradient(135deg, #E0E7FF 0%, #C7D2FE 100%)',
                                borderRadius: 12,
                                padding: '16px',
                                border: '1px solid #A5B4FC'
                              }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: '#312E81', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                  📊 {language === 'sw' ? 'Balanse ya Awali' : 'Opening Balance'}
                                </div>
                                <div style={{ fontSize: 24, fontWeight: 800, color: '#3730A3' }}>
                                  {fmt(balances.opening)}
                                </div>
                              </div>

                              {/* Closing Balance */}
                              <div style={{
                                background: balances.closing >= 0
                                  ? 'linear-gradient(135deg, #D1FAE5 0%, #A7F3D0 100%)'
                                  : 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)',
                                borderRadius: 12,
                                padding: '16px',
                                border: balances.closing >= 0 ? '1px solid #6EE7B7' : '1px solid #FCA5A5'
                              }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: balances.closing >= 0 ? '#047857' : '#991B1B', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                  💰 {language === 'sw' ? 'Balanse ya Mwisho' : 'Closing Balance'}
                                </div>
                                <div style={{ fontSize: 24, fontWeight: 800, color: balances.closing >= 0 ? '#065F46' : '#7F1D1D' }}>
                                  {balances.closing >= 0 ? '+' : ''}{fmt(balances.closing)}
                                </div>
                              </div>

                              {/* Current Balance */}
                              <div style={{
                                background: balances.current >= 0
                                  ? 'linear-gradient(135deg, #CCFBF1 0%, #99F6E4 100%)'
                                  : 'linear-gradient(135deg, #FECDD3 0%, #FBCFE8 100%)',
                                borderRadius: 12,
                                padding: '16px',
                                border: balances.current >= 0 ? '1px solid #5EE7DF' : '1px solid #F43F5E'
                              }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: balances.current >= 0 ? '#0D3F47' : '#831843', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                  ⭐ {language === 'sw' ? 'Balanse ya Sasa' : 'Current Balance'}
                                </div>
                                <div style={{ fontSize: 24, fontWeight: 800, color: balances.current >= 0 ? '#0D9488' : '#BE185D' }}>
                                  {balances.current >= 0 ? '+' : ''}{fmt(balances.current)}
                                </div>
                              </div>
                            </>
                          ) : (
                            <>
                              {/* Total Income */}
                              <div style={{
                                background: 'linear-gradient(135deg, #D1FAE5 0%, #A7F3D0 100%)',
                                borderRadius: 12,
                                padding: '16px',
                                border: '1px solid #6EE7B7'
                              }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: '#047857', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                  💰 {language === 'sw' ? 'Jumla ya Mapato' : 'Total Income'}
                                </div>
                                <div style={{ fontSize: 24, fontWeight: 800, color: '#065F46' }}>
                                  {fmt(totalIncome)}
                                </div>
                              </div>

                              {/* Total Expenses */}
                              <div style={{
                                background: 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)',
                                borderRadius: 12,
                                padding: '16px',
                                border: '1px solid #FCA5A5'
                              }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: '#991B1B', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                  📊 {language === 'sw' ? 'Jumla ya Matumizi' : 'Total Expenses'}
                                </div>
                                <div style={{ fontSize: 24, fontWeight: 800, color: '#7F1D1D' }}>
                                  {fmt(totalExpenses)}
                                </div>
                              </div>

                              {/* Net Cashflow */}
                              <div style={{
                                background: netCashflow >= 0
                                  ? 'linear-gradient(135deg, #F1F5F9 0%, #E2E8F0 100%)'
                                  : 'linear-gradient(135deg, #FEF2F2 0%, #FEE2E2 100%)',
                                borderRadius: 12,
                                padding: '16px',
                                border: netCashflow >= 0 ? '1px solid #93C5FD' : '1px solid #FECACA'
                              }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: netCashflow >= 0 ? '#334155' : '#991B1B', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                  ⚡ {language === 'sw' ? 'Mtiririko wa Pesa' : 'Net Cashflow'}
                                </div>
                                <div style={{ fontSize: 24, fontWeight: 800, color: netCashflow >= 0 ? '#334155' : '#991B1B' }}>
                                  {netCashflow >= 0 ? '+' : ''}{fmt(netCashflow)}
                                </div>
                              </div>
                            </>
                          )}
                        </div>

                        {/* Transactions Table */}
                        <div style={{
                          background: 'var(--bg-card)',
                          borderRadius: 12,
                          overflow: 'hidden',
                          border: '1px solid var(--border-color)'
                        }}>
                          {isWalletStatement ? (
                            // ── Ledger-based wallet statement (ties to Opening/Closing) ──
                            <>
                              <div style={{ display: 'grid', gridTemplateColumns: '0.9fr 1fr 1.9fr 1.1fr 1.2fr', background: '#111827', padding: '12px 16px', gap: 10 }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: '#A3E635' }}>{language === 'sw' ? 'Tarehe' : 'Date'}</div>
                                <div style={{ fontSize: 11, fontWeight: 700, color: '#A3E635' }}>{language === 'sw' ? 'Kumbukumbu' : 'Reference'}</div>
                                <div style={{ fontSize: 11, fontWeight: 700, color: '#A3E635' }}>{language === 'sw' ? 'Maelezo' : 'Description'}</div>
                                <div style={{ fontSize: 11, fontWeight: 700, color: '#A3E635', textAlign: 'right' }}>{language === 'sw' ? 'Kiasi' : 'Amount'}</div>
                                <div style={{ fontSize: 11, fontWeight: 700, color: '#A3E635', textAlign: 'right' }}>{language === 'sw' ? 'Salio' : 'Balance'}</div>
                              </div>
                              {/* Opening balance row */}
                              <div style={{ display: 'grid', gridTemplateColumns: '0.9fr 1fr 1.9fr 1.1fr 1.2fr', background: 'var(--bg-tab)', padding: '12px 16px', gap: 10, alignItems: 'center' }}>
                                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{cashflowReportFilter.startDate || '—'}</div>
                                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>—</div>
                                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{language === 'sw' ? 'Salio la kufungua' : 'Opening balance'}</div>
                                <div style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>—</div>
                                <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-primary)', textAlign: 'right' }}>{fmt(balances.opening || 0)}</div>
                              </div>
                              {(ledgerLines || []).length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '28px 24px', color: 'var(--text-secondary)', fontSize: 13 }}>
                                  {language === 'sw' ? 'Hakuna miamala katika kipindi hiki' : 'No movements in this period'}
                                </div>
                              ) : (ledgerLines || []).map((ln, i) => (
                                <div key={`${ln.refId}-${ln.source}-${i}`} style={{ display: 'grid', gridTemplateColumns: '0.9fr 1fr 1.9fr 1.1fr 1.2fr', background: 'var(--bg-card)', borderTop: '1px solid var(--border-color)', padding: '14px 16px', gap: 10, alignItems: 'center' }}>
                                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{ln.date}</div>
                                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ln.ref}</div>
                                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ln.label}</div>
                                  <div style={{ fontSize: 13, fontWeight: 700, textAlign: 'right', color: ln.direction === 'in' ? '#059669' : '#DC2626' }}>{ln.direction === 'in' ? '+' : '−'}{fmt(ln.amount)}</div>
                                  <div style={{ fontSize: 13, fontWeight: 800, textAlign: 'right', color: 'var(--text-primary)' }}>{fmt(ln.running)}</div>
                                </div>
                              ))}
                              {/* Closing balance row */}
                              <div style={{ display: 'grid', gridTemplateColumns: '0.9fr 1fr 1.9fr 1.1fr 1.2fr', background: '#ECFDF5', padding: '12px 16px', gap: 10, alignItems: 'center', borderTop: '2px solid #A7F3D0' }}>
                                <div style={{ fontSize: 12, color: '#065F46' }}>{cashflowReportFilter.endDate || '—'}</div>
                                <div style={{ fontSize: 12, color: '#065F46' }}>—</div>
                                <div style={{ fontSize: 13, fontWeight: 800, color: '#065F46' }}>{language === 'sw' ? 'Salio la kufunga' : 'Closing balance'}</div>
                                <div style={{ textAlign: 'right' }}>—</div>
                                <div style={{ fontSize: 14, fontWeight: 900, color: '#065F46', textAlign: 'right' }}>{fmt(balances.closing || 0)}</div>
                              </div>
                            </>
                          ) : allTransactions.length === 0 ? (
                            <div style={{
                              textAlign: 'center',
                              padding: '48px 24px',
                              color: 'var(--text-secondary)'
                            }}>
                              <div style={{ fontSize: 48, marginBottom: 12 }}>📭</div>
                              <p style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>
                                {language === 'sw' ? 'Hakuna miamala iliyopatikana' : 'No transactions found'}
                              </p>
                            </div>
                          ) : (
                            <>
                              <div style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1.2fr 2fr 1.2fr 1.2fr',
                                background: '#111827',
                                padding: '12px 16px',
                                gap: 12,
                                borderBottom: '1px solid var(--border-color)'
                              }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: '#A3E635' }}>{language === 'sw' ? 'Tarehe' : 'Date'}</div>
                                <div style={{ fontSize: 11, fontWeight: 700, color: '#A3E635' }}>{language === 'sw' ? 'Aina' : 'Type'}</div>
                                <div style={{ fontSize: 11, fontWeight: 700, color: '#A3E635' }}>{language === 'sw' ? 'Maelezo' : 'Description'}</div>
                                <div style={{ fontSize: 11, fontWeight: 700, color: '#A3E635', textAlign: 'right' }}>{language === 'sw' ? 'Kiasi' : 'Amount'}</div>
                                <div style={{ fontSize: 11, fontWeight: 700, color: '#A3E635', textAlign: 'right' }}>{language === 'sw' ? 'Njia' : 'Method'}</div>
                              </div>

                              {allTransactions.map((tx, i) => {
                                const typeColors = getTypeColor(tx);
                                const amountColor = getAmountColor(tx);
                                return (
                                  <div
                                    key={`${tx.id}-${i}`}
                                    style={{
                                      display: 'grid',
                                      gridTemplateColumns: '1fr 1.2fr 2fr 1.2fr 1.2fr',
                                      background: 'var(--bg-card)',
                                      borderTop: '1px solid var(--border-color)',
                                      padding: '14px 16px',
                                      gap: 12,
                                      alignItems: 'center'
                                    }}
                                  >
                                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{tx.date}</div>
                                    <div style={{
                                      fontSize: 11,
                                      fontWeight: 700,
                                      padding: '4px 8px',
                                      borderRadius: 6,
                                      background: typeColors.bg,
                                      color: typeColors.color,
                                      textAlign: 'center'
                                    }}>
                                      {getTypeLabel(tx)}
                                    </div>
                                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {getDescription(tx)}
                                    </div>
                                    <div style={{ fontSize: 13, fontWeight: 700, color: amountColor, textAlign: 'right' }}>
                                      {getAmountSign(tx)}{fmt(getAmount(tx))}
                                    </div>
                                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', textAlign: 'right' }}>
                                      {getPaymentMethodLabel(tx.paymentMethod)}
                                    </div>
                                  </div>
                                );
                              })}
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </>
              )}
            </div>
            </>
            )}
          </div>
        )}

        {activeTab === 'setup' && (
          <div style={{ minHeight: '100vh', background: 'var(--bg-main)', paddingBottom: 80 }}>
            {!isDataReady ? (
              <div role="status" aria-label={t.loading || 'Loading'} style={{ padding: '20px 16px' }}>
                <div className="skeleton skeleton-title" style={{ width: '25%', marginBottom: 20 }}></div>
                {[1,2,3,4,5,6,7,8].map(i => <div key={i} className="skeleton skeleton-settings-row"></div>)}
              </div>
            ) : (
            <>
            {/* ── HEADER ── */}
            <div style={{ display: 'flex', alignItems: 'center', padding: '20px 20px 8px', gap: 10, background: 'var(--bg-main)' }}>
              <button
                onClick={() => setupScreen ? setSetupScreen(null) : setActiveTab('dashboard')}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px 4px 0', display: 'flex', alignItems: 'center' }}
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--text-primary)" strokeWidth="2.5" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
              </button>
              <h1 style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>
                {setupScreen === 'businessName' ? t.businessName
                : setupScreen === 'businessAddress' ? t.businessAddress
                : setupScreen === 'paymentDetails' ? t.paymentAccounts
                : setupScreen === 'products' ? t.productServices
                : setupScreen === 'services' ? (t.services || 'Services')
                : setupScreen === 'expenseCategories' ? t.expenseCategories
                : setupScreen === 'suppliers' ? t.suppliers
                : setupScreen === 'customers' ? t.customers
                : setupScreen === 'businessType' ? t.businessType
                : setupScreen === 'currency' ? t.chooseCurrency
                : setupScreen === 'compliance' ? (t.complianceTax || 'Compliance & Tax')
                : setupScreen === 'vatRate' ? (t.vatRateSetting || 'Default VAT Rate')
                : t.setup}
              </h1>
            </div>

            {/* ── MAIN MENU ── */}
            {!setupScreen && (() => {
              const _showProducts = businessType !== 'service_only';
              const _showServices = businessType !== 'products_only';
              const menuItems = [
                { key: 'businessName',       color: '#F1F5F9', stroke: '#475569', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="2" strokeLinecap="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 3H8L6 7h12z"/></svg>, title: t.businessName,       sub: businessName || currentBusiness?.name || t.notSet },
                { key: 'businessAddress',    color: '#F0FDF4', stroke: '#22C55E', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth="2" strokeLinecap="round"><path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>, title: t.businessAddress,    sub: businessAddress || t.notSet },
                { key: 'paymentDetails',     color: '#FFF7ED', stroke: '#F97316', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F97316" strokeWidth="2" strokeLinecap="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>, title: t.paymentAccounts,    sub: (paymentAccounts || []).length > 0 ? `${(paymentAccounts || []).length} ${(paymentAccounts || []).length !== 1 ? t.accountPlural : t.accountSingular}` : t.notSet },
                ...(_showProducts ? [{ key: 'products',           color: '#FDF4FF', stroke: '#6366F1', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6366F1" strokeWidth="2" strokeLinecap="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>, title: t.products || 'Products',    sub: (products || []).length > 0 ? `${(products || []).length} ${(products || []).length !== 1 ? t.itemPlural : t.itemSingular}` : t.noItemsYet }] : []),
                ...(_showServices ? [{ key: 'services',           color: '#ECFDF5', stroke: '#10B981', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2" strokeLinecap="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>, title: t.services || 'Services',    sub: (services || []).length > 0 ? `${(services || []).length} ${(services || []).length !== 1 ? t.itemPlural : t.itemSingular}` : t.noItemsYet }] : []),
                { key: 'expenseCategories',  color: '#FFF1F2', stroke: '#F43F5E', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F43F5E" strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/></svg>, title: t.expenseCategories, sub: (expenseCategories || []).length > 0 ? `${(expenseCategories || []).length} ${(expenseCategories || []).length !== 1 ? t.itemPlural : t.itemSingular}` : t.noItemsYet },
                { key: 'suppliers',          color: '#FFFBEB', stroke: '#F59E0B', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 3H8L6 7h12z"/><path d="M12 12v5M9.5 14.5h5"/></svg>, title: t.suppliers,          sub: (suppliers || []).length > 0 ? `${(suppliers || []).length} ${(suppliers || []).length !== 1 ? t.itemPlural : t.itemSingular}` : t.noItemsYet },
                { key: 'customers',          color: '#ECFDF5', stroke: '#10B981', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2" strokeLinecap="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>, title: t.customers,          sub: (customers || []).length > 0 ? `${(customers || []).length} ${(customers || []).length !== 1 ? t.itemPlural : t.itemSingular}` : t.noItemsYet },
                { key: 'businessType',       color: '#F0F9FF', stroke: '#0EA5E9', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0EA5E9" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/></svg>, title: t.businessType,       sub: businessType === 'service_only' ? t.serviceOnly : businessType === 'products_only' ? t.productsOnly : t.productsAndServices },
                { key: 'currency',           color: '#FFF7ED', stroke: '#D97706', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v8M9 10h4.5a2 2 0 0 1 0 4H9"/></svg>, title: t.currency,            sub: (() => { const c = CURRENCY_LIST.find(x => x.code === currency); return c ? `${c.symbol} ${c.name}` : currency; })() },
                { key: 'vatRate',            color: '#F0FDF4', stroke: '#059669', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2" strokeLinecap="round"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 21 12 17.77 5.82 21 7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>, title: language === 'sw' ? 'VAT / Kodi' : 'VAT / Tax', sub: vatRegistered ? `${language === 'sw' ? 'Imesajiliwa' : 'Registered'} · ${defaultVatRate}%` : (language === 'sw' ? 'Haijasajiliwa -VAT imezimwa' : 'Not registered -VAT off') },
                { key: 'compliance',         color: '#F0FFF4', stroke: '#16A34A', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2" strokeLinecap="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>, title: t.complianceTax || 'Compliance & Tax', sub: businessTIN ? `TIN: ${businessTIN}` : (t.notSet || 'Not set') },
              ];
              return (
                <div style={{ padding: '6px 12px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {menuItems.map(item => (
                    <button key={item.key} onClick={() => {
                      setSetupScreen(item.key);
                      if (item.key === 'businessName') { setBizNameDraft(businessName || currentBusiness?.name || ''); setBizNameEditing(false); }
                      if (item.key === 'businessAddress') { setBizAddressDraft(businessAddress); setBizAddressEditing(false); }
                    }}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px', background: 'var(--bg-card)', borderRadius: 10, border: '1px solid var(--border-color)', cursor: 'pointer', textAlign: 'left', width: '100%' }}>
                      <div style={{ width: 32, height: 32, borderRadius: 8, background: item.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        {React.cloneElement(item.icon, { width: 15, height: 15 })}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 13 }}>{item.title}</div>
                        <div style={{ color: 'var(--text-secondary)', fontSize: 11, marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.sub}</div>
                      </div>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#CBD5E1" strokeWidth="2.5" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>
                    </button>
                  ))}
                </div>
              );
            })()}

            {/* ── BUSINESS NAME SCREEN ── */}
            {setupScreen === 'businessName' && (
              <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>

                {/* Logo card */}
                <div style={{ background: 'var(--bg-card)', borderRadius: 16, padding: 20, border: '1px solid var(--border-color)' }}>
                  <label style={{ display: 'block', fontWeight: 600, color: 'var(--text-primary)', fontSize: 14, marginBottom: 8 }}>{t.businessLogo}</label>

                  {/* Upload Instructions */}
                  <div style={{ background: '#F0FDF4', borderRadius: 10, padding: '10px 12px', marginBottom: 12, fontSize: 12, color: '#166534', lineHeight: 1.5 }}>
                    <strong>Supported formats:</strong> JPG, PNG, WebP · <strong>Max size:</strong> 2MB · <strong>Recommended:</strong> 200x200px or smaller
                  </div>

                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files && e.target.files[0];
                      if (!file) return;
                      setBusinessLogoError('');
                      compressAndValidateImage(file, async (base64Image) => {
                        // Try Supabase Storage upload if logged in
                        if (currentUser && currentUser.accessToken) {
                          const logoUrl = await uploadBusinessLogo(currentUser.uid, currentUser.accessToken, base64Image);
                          if (logoUrl) {
                            setBusinessLogoUrl(logoUrl); // Store the Supabase URL
                            setBusinessLogo(logoUrl); // Also update display
                          } else {
                            // Fallback to localStorage if Supabase fails
                            console.warn('Supabase upload failed, using local storage as fallback');
                            setBusinessLogo(base64Image);
                            setBusinessLogoUrl('');
                          }
                        } else {
                          // Fallback to localStorage if not logged in
                          setBusinessLogo(base64Image);
                        }
                      }, setBusinessLogoError);
                      e.target.value = '';
                    }}
                    style={{ display: 'none' }}
                    id="faida-biz-logo-input"
                  />

                  {businessLogo || businessLogoUrl ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '9px 12px', borderRadius: 12, background: 'var(--bg-input,#F3F4F6)' }}>
                      <img src={businessLogo || businessLogoUrl} alt="Business Logo" style={{ width: 48, height: 40, borderRadius: 10, objectFit: 'contain', background: 'var(--bg-white)', border: '1px solid var(--border-color)', flexShrink: 0 }} />
                      <span style={{ flex: 1, fontSize: 14, color: 'var(--text-secondary)' }}>{t.logoUploaded || 'Logo uploaded'}</span>
                      <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                        <button onClick={() => { setBusinessLogoError(''); const el = document.getElementById('faida-biz-logo-input'); if (el) el.click(); }}
                          style={{ padding: '6px 12px', borderRadius: 8, background: '#E0F2FE', color: '#0369A1', border: 'none', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                          ✎️ {t.edit}
                        </button>
                        <button onClick={async () => {
                          setBusinessLogo('');
                          setBusinessLogoUrl('');
                          setBusinessLogoError('');
                          // Delete from Supabase Storage
                          if (currentUser && currentUser.accessToken) {
                            await deleteBusinessLogo(currentUser.uid, currentUser.accessToken);
                          }
                        }}
                          style={{ padding: '6px 12px', borderRadius: 8, background: '#FEE2E2', color: '#DC2626', border: 'none', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                          {t.removeLogo || 'Remove'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => { const el = document.getElementById('faida-biz-logo-input'); if (el) el.click(); }}
                      style={{ width: '100%', padding: '9px 12px', borderRadius: 12, border: '1px dashed var(--border-color)', background: 'var(--bg-input,#F3F4F6)', color: 'var(--text-secondary)', fontWeight: 600, cursor: 'pointer', boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                      {t.uploadLogo}
                    </button>
                  )}

                  {/* Error Message */}
                  {businessLogoError && (
                    <div style={{ marginTop: 10, padding: '10px 12px', borderRadius: 10, background: '#FEE2E2', color: '#DC2626', fontSize: 13, fontWeight: 500 }}>
                      {businessLogoError}
                    </div>
                  )}
                </div>

                {/* Business Name card -inline save/edit */}
                <div style={{ background: 'var(--bg-card)', borderRadius: 16, padding: 20, border: '1px solid var(--border-color)' }}>
                  <label style={{ display: 'block', fontWeight: 600, color: 'var(--text-primary)', fontSize: 14, marginBottom: 10 }}>{t.businessName}</label>
                  {bizNameEditing ? (
                    <>
                      <input
                        type="text"
                        value={bizNameDraft}
                        onChange={e => setBizNameDraft(e.target.value)}
                        placeholder={t.enterBusinessName || 'Enter business name'}
                        autoFocus
                        style={{ width: '100%', padding: '10px 12px', borderRadius: 12, border: '2px solid #A3E635', background: 'var(--bg-input,#F3F4F6)', fontSize: 15, color: 'var(--text-primary)', boxSizing: 'border-box', outline: 'none', marginBottom: 12 }}
                      />
                      <div style={{ display: 'flex', gap: 10 }}>
                        <button
                          onClick={() => { setBusinessName(bizNameDraft.trim()); setBizNameEditing(false); }}
                          style={{ flex: 1, padding: '9px 12px', borderRadius: 12, background: '#111827', color: '#A3E635', border: 'none', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
                          {t.save}
                        </button>
                        <button
                          onClick={() => { setBizNameDraft(businessName); setBizNameEditing(false); }}
                          style={{ flex: 1, padding: '9px 12px', borderRadius: 12, background: 'var(--bg-tab)', color: 'var(--text-primary)', border: 'none', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
                          {t.cancel}
                        </button>
                      </div>
                    </>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 12, background: 'var(--bg-input,#F3F4F6)' }}>
                      <span style={{ flex: 1, fontSize: 15, color: (businessName || currentBusiness?.name) ? 'var(--text-primary)' : 'var(--text-secondary)', fontWeight: (businessName || currentBusiness?.name) ? 500 : 400 }}>
                        {businessName || currentBusiness?.name || (t.notSet || 'Not set')}
                      </span>
                      <button
                        onClick={() => { setBizNameDraft(businessName || currentBusiness?.name || ''); setBizNameEditing(true); }}
                        style={{ padding: '6px 14px', borderRadius: 8, background: '#E0F2FE', color: '#0369A1', border: 'none', fontWeight: 600, fontSize: 13, cursor: 'pointer', flexShrink: 0 }}>
                        ✎️ {t.edit}
                      </button>
                    </div>
                  )}
                  <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 10, marginBottom: 0 }}>{t.businessNameSub}</p>
                </div>
              </div>
            )}

            {/* ── BUSINESS ADDRESS SCREEN ── */}
            {setupScreen === 'businessAddress' && (
              <div style={{ padding: '16px 20px' }}>
                <div style={{ background: 'var(--bg-card)', borderRadius: 16, padding: 20, border: '1px solid var(--border-color)' }}>
                  <label style={{ display: 'block', fontWeight: 600, color: 'var(--text-primary)', fontSize: 14, marginBottom: 10 }}>{t.businessAddress}</label>
                  {bizAddressEditing ? (
                    <>
                      <textarea
                        value={bizAddressDraft}
                        onChange={e => setBizAddressDraft(e.target.value)}
                        placeholder={t.businessAddressPlaceholder}
                        rows={3}
                        autoFocus
                        style={{ width: '100%', padding: '10px 12px', borderRadius: 12, border: '2px solid #A3E635', background: 'var(--bg-input,#F3F4F6)', fontSize: 15, color: 'var(--text-primary)', boxSizing: 'border-box', outline: 'none', resize: 'vertical', fontFamily: 'inherit', marginBottom: 12 }}
                      />
                      <div style={{ display: 'flex', gap: 10 }}>
                        <button
                          onClick={() => { setBusinessAddress(bizAddressDraft.trim()); setBizAddressEditing(false); }}
                          style={{ flex: 1, padding: '9px 12px', borderRadius: 12, background: '#111827', color: '#A3E635', border: 'none', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
                          {t.save}
                        </button>
                        <button
                          onClick={() => { setBizAddressDraft(businessAddress); setBizAddressEditing(false); }}
                          style={{ flex: 1, padding: '9px 12px', borderRadius: 12, background: 'var(--bg-tab)', color: 'var(--text-primary)', border: 'none', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
                          {t.cancel}
                        </button>
                      </div>
                    </>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px', borderRadius: 12, background: 'var(--bg-input,#F3F4F6)' }}>
                      <span style={{ flex: 1, fontSize: 15, color: businessAddress ? 'var(--text-primary)' : 'var(--text-secondary)', whiteSpace: 'pre-wrap', lineHeight: 1.5, fontWeight: businessAddress ? 500 : 400 }}>
                        {businessAddress || (t.notSet || 'Not set')}
                      </span>
                      <button
                        onClick={() => { setBizAddressDraft(businessAddress); setBizAddressEditing(true); }}
                        style={{ padding: '6px 14px', borderRadius: 8, background: '#E0F2FE', color: '#0369A1', border: 'none', fontWeight: 600, fontSize: 13, cursor: 'pointer', flexShrink: 0 }}>
                        ✎️ {t.edit}
                      </button>
                    </div>
                  )}
                  <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 10, marginBottom: 0 }}>{t.businessAddressSub}</p>
                </div>
              </div>
            )}

            {/* ── PAYMENT DETAILS SCREEN ── */}
            {setupScreen === 'paymentDetails' && (
              <div style={{ padding: '16px 20px' }}>
                {(paymentAccounts || []).length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '48px 20px', background: 'var(--bg-card)', borderRadius: 16, border: '1px solid var(--border-color)' }}>
                    <div style={{ marginBottom: 12 }}>
                      <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="var(--text-secondary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M19 7V4a1 1 0 0 0-1-1H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h13a1 1 0 0 0 1-1v-3"/><circle cx="3" cy="13" r="1"/><path d="M22 8v3m-3-3v3M3 5h18M3 9h18"/>
                      </svg>
                    </div>
                    <p style={{ color: 'var(--text-secondary)', marginBottom: 20 }}>{t.noPaymentAccountsYet}</p>
                    <button onClick={() => { setPayForm({ type: 'bank', bankName: '', networkName: '', accountName: '', accountNumber: '', phoneNumber: '', openingBalance: '', openingBalanceDate: '' }); setPayEditId(null); setShowPayForm(true); }}
                      style={{ padding: '12px 24px', borderRadius: 12, background: '#111827', color: '#A3E635', border: 'none', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
                      + {t.addPaymentAccount}
                    </button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {(paymentAccounts || []).map(acc => (
                      <div key={acc.id} style={{ background: 'var(--bg-card)', borderRadius: 16, padding: '16px 18px', border: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ width: 42, height: 42, borderRadius: 12, background: acc.type === 'bank' ? '#F1F5F9' : acc.type === 'cash' ? '#FFFBEB' : '#F0FDF4', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          {acc.type === 'bank'
                            ? <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="22" x2="21" y2="22"/><line x1="6" y1="18" x2="6" y2="11"/><line x1="10" y1="18" x2="10" y2="11"/><line x1="14" y1="18" x2="14" y2="11"/><line x1="18" y1="18" x2="18" y2="11"/><polygon points="12 2 20 7 4 7"/></svg>
                            : acc.type === 'cash'
                            ? <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#B45309" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="6" width="20" height="12" rx="2"/><circle cx="12" cy="12" r="2"/><path d="M6 12h.01M18 12h.01"/></svg>
                            : <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="2" width="14" height="20" rx="2"/><line x1="12" y1="18" x2="12.01" y2="18" strokeWidth="3"/></svg>
                          }
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: 15 }}>{acc.accountName}</div>
                          {acc.type !== 'cash' && (
                            <div style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 2 }}>
                              {acc.type === 'bank' ? acc.bankName : acc.networkName} · {acc.type === 'bank' ? acc.accountNumber : acc.phoneNumber}
                            </div>
                          )}
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
                            <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 16, fontSize: 11, fontWeight: 600,
                              background: acc.type === 'bank' ? '#E2E8F0' : acc.type === 'cash' ? '#FEF3C7' : '#D1FAE5',
                              color: acc.type === 'bank' ? '#475569' : acc.type === 'cash' ? '#92400E' : '#065F46' }}>
                              {acc.type === 'bank' ? t.bankAccount : acc.type === 'cash' ? (t.cash || 'Cash') : t.mobileMoneyAccount}
                            </span>
                            {acc.openingBalance ? (
                              <span style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 500 }}>
                                {language === 'sw' ? 'Awali:' : 'Opening:'} {fmt(acc.openingBalance)}
                                {acc.openingBalanceDate ? ` (${acc.openingBalanceDate})` : ''}
                              </span>
                            ) : null}
                          </div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
                          <button onClick={() => handlePayEdit(acc)} style={{ padding: '6px 14px', borderRadius: 8, background: 'var(--bg-tab)', color: 'var(--text-primary)', border: 'none', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>{t.edit}</button>
                          <button onClick={() => handlePayDelete(acc.id)} style={{ padding: '6px 14px', borderRadius: 8, background: '#FEE2E2', color: '#DC2626', border: 'none', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>{t.delete}</button>
                        </div>
                      </div>
                    ))}
                    <button onClick={() => { setPayForm({ type: 'bank', bankName: '', networkName: '', accountName: '', accountNumber: '', phoneNumber: '', openingBalance: '', openingBalanceDate: '' }); setPayEditId(null); setShowPayForm(true); }}
                      style={{ padding: '14px', borderRadius: 14, background: '#111827', color: '#A3E635', border: 'none', fontWeight: 700, fontSize: 14, cursor: 'pointer', marginTop: 4 }}>
                      + {t.addPaymentAccount}
                    </button>
                  </div>
                )}

                {/* Pay Form Modal */}
                {showPayForm && (
                  <div className="popup-overlay">
                    <div className="popup-form su-modal" style={{ maxWidth: 420, width: '100%', borderRadius: 16, padding: 24 }}>
                      <h3 style={{ marginBottom: 14, fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>{payEditId ? t.editPaymentAccount : t.addPaymentAccount}</h3>

                      {/* Type selector */}
                      <div style={{ marginBottom: 18 }}>
                        <label style={{ display: 'block', fontWeight: 600, color: 'var(--text-primary)', fontSize: 14, marginBottom: 8 }}>{t.accountType}</label>
                        <div style={{ display: 'flex', gap: 8 }}>
                          {[{val:'cash',label:t.cash || 'Cash'},{val:'bank',label:t.bankAccount},{val:'mobilemoney',label:t.mobileMoneyAccount}].map(opt => (
                            <button key={opt.val} onClick={() => setPayForm(p => ({ ...p, type: opt.val }))}
                              style={{ flex: 1, padding: '11px 4px', borderRadius: 12, border: `2px solid ${payForm.type === opt.val ? '#111827' : 'var(--border-default)'}`, background: payForm.type === opt.val ? '#111827' : 'white', color: payForm.type === opt.val ? '#A3E635' : '#374151', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Cash fields */}
                      {payForm.type === 'cash' && (
                        <>
                          <div style={{ marginBottom: 14 }}>
                            <label style={{ display: 'block', fontWeight: 600, color: 'var(--text-primary)', fontSize: 13, marginBottom: 6 }}>{t.accountName || 'Account Name'}</label>
                            <input value={payForm.accountName} onChange={e => { setPayForm(p => ({ ...p, accountName: e.target.value })); setPayFormErrors(p => ({ ...p, accountName: null })); }} placeholder={language === 'sw' ? 'mfano: Pesa Taslimu' : 'e.g. Petty Cash'} style={{ width: '100%', padding: '9px 12px', borderRadius: 10, border: payFormErrors.accountName ? '1.5px solid #DC2626' : 'none', background: 'var(--bg-tab)', color: 'var(--text-primary)', fontSize: 14, boxSizing: 'border-box', outline: 'none' }} />
                            {payFormErrors.accountName && <p style={{ color: '#DC2626', fontSize: 12, margin: '4px 0 0', fontWeight: 600 }}>⚠ {payFormErrors.accountName}</p>}
                          </div>
                        </>
                      )}

                      {/* Bank fields */}
                      {payForm.type === 'bank' && (
                        <>
                          <div style={{ marginBottom: 14 }}>
                            <label style={{ display: 'block', fontWeight: 600, color: 'var(--text-primary)', fontSize: 13, marginBottom: 6 }}>{t.bankName}</label>
                            <input value={payForm.bankName} onChange={e => setPayForm(p => ({ ...p, bankName: e.target.value }))} placeholder={t.bankNamePlaceholder} style={{ width: '100%', padding: '9px 12px', borderRadius: 10, border: 'none', background: 'var(--bg-tab)', color: 'var(--text-primary)', fontSize: 14, boxSizing: 'border-box', outline: 'none' }} />
                          </div>
                          <div style={{ marginBottom: 14 }}>
                            <label style={{ display: 'block', fontWeight: 600, color: 'var(--text-primary)', fontSize: 13, marginBottom: 6 }}>{t.accountName}</label>
                            <input value={payForm.accountName} onChange={e => setPayForm(p => ({ ...p, accountName: e.target.value }))} placeholder={t.accountHolderPlaceholder} style={{ width: '100%', padding: '9px 12px', borderRadius: 10, border: 'none', background: 'var(--bg-tab)', color: 'var(--text-primary)', fontSize: 14, boxSizing: 'border-box', outline: 'none' }} />
                          </div>
                          <div style={{ marginBottom: 22 }}>
                            <label style={{ display: 'block', fontWeight: 600, color: 'var(--text-primary)', fontSize: 13, marginBottom: 6 }}>{t.accountNumber}</label>
                            <input value={payForm.accountNumber} onChange={e => setPayForm(p => ({ ...p, accountNumber: e.target.value }))} placeholder={t.accountNumberPlaceholder} inputMode="numeric" style={{ width: '100%', padding: '9px 12px', borderRadius: 10, border: 'none', background: 'var(--bg-tab)', color: 'var(--text-primary)', fontSize: 14, boxSizing: 'border-box', outline: 'none' }} />
                          </div>
                        </>
                      )}

                      {/* Mobile Money fields */}
                      {payForm.type === 'mobilemoney' && (
                        <>
                          <div style={{ marginBottom: 14 }}>
                            <label style={{ display: 'block', fontWeight: 600, color: 'var(--text-primary)', fontSize: 13, marginBottom: 6 }}>{t.networkName}</label>
                            <input value={payForm.networkName} onChange={e => setPayForm(p => ({ ...p, networkName: e.target.value }))} placeholder={t.networkNamePlaceholder} style={{ width: '100%', padding: '9px 12px', borderRadius: 10, border: 'none', background: 'var(--bg-tab)', color: 'var(--text-primary)', fontSize: 14, boxSizing: 'border-box', outline: 'none' }} />
                          </div>
                          <div style={{ marginBottom: 14 }}>
                            <label style={{ display: 'block', fontWeight: 600, color: 'var(--text-primary)', fontSize: 13, marginBottom: 6 }}>{t.accountName}</label>
                            <input value={payForm.accountName} onChange={e => setPayForm(p => ({ ...p, accountName: e.target.value }))} placeholder={t.accountHolderPlaceholder} style={{ width: '100%', padding: '9px 12px', borderRadius: 10, border: 'none', background: 'var(--bg-tab)', color: 'var(--text-primary)', fontSize: 14, boxSizing: 'border-box', outline: 'none' }} />
                          </div>
                          <div style={{ marginBottom: 22 }}>
                            <label style={{ display: 'block', fontWeight: 600, color: 'var(--text-primary)', fontSize: 13, marginBottom: 6 }}>{t.phoneNumberLabel}</label>
                            <input value={payForm.phoneNumber} onChange={e => setPayForm(p => ({ ...p, phoneNumber: e.target.value }))} placeholder={t.phonePlaceholder} inputMode="tel" style={{ width: '100%', padding: '9px 12px', borderRadius: 10, border: 'none', background: 'var(--bg-tab)', color: 'var(--text-primary)', fontSize: 14, boxSizing: 'border-box', outline: 'none' }} />
                          </div>
                        </>
                      )}

                      {/* Opening Balance -shared for both account types */}
                      <div style={{ marginBottom: 14, paddingTop: 4, borderTop: '1px solid var(--border-color)' }}>
                        <label style={{ display: 'block', fontWeight: 600, color: 'var(--text-primary)', fontSize: 13, marginBottom: 6, marginTop: 14 }}>
                          {language === 'sw' ? 'Salio la Awali *' : 'Opening Balance *'}
                        </label>
                        <input
                          type="number" inputMode="decimal" min="0" step="0.01"
                          value={payForm.openingBalance}
                          onChange={e => { setPayForm(p => ({ ...p, openingBalance: e.target.value })); setPayFormErrors(p => ({ ...p, openingBalance: null })); }}
                          placeholder="0"
                          style={{ width: '100%', padding: '9px 12px', borderRadius: 10, border: payFormErrors.openingBalance ? '1.5px solid #DC2626' : 'none', background: 'var(--bg-tab)', color: 'var(--text-primary)', fontSize: 14, boxSizing: 'border-box', outline: 'none' }}
                        />
                        {payFormErrors.openingBalance && <p style={{ color: '#DC2626', fontSize: 12, margin: '4px 0 0', fontWeight: 600 }}>⚠ {payFormErrors.openingBalance}</p>}
                      </div>
                      <div style={{ marginBottom: 14 }}>
                        <label style={{ display: 'block', fontWeight: 600, color: 'var(--text-primary)', fontSize: 13, marginBottom: 6 }}>
                          {language === 'sw' ? 'Tarehe ya Salio la Awali *' : 'Opening Balance Date *'}
                        </label>
                        <input
                          type="date"
                          value={payForm.openingBalanceDate}
                          onChange={e => { setPayForm(p => ({ ...p, openingBalanceDate: e.target.value })); setPayFormErrors(p => ({ ...p, openingBalanceDate: null })); }}
                          style={{ width: '100%', padding: '9px 12px', borderRadius: 10, border: payFormErrors.openingBalanceDate ? '1.5px solid #DC2626' : 'none', background: 'var(--bg-tab)', color: 'var(--text-primary)', fontSize: 14, boxSizing: 'border-box', outline: 'none' }}
                        />
                        {payFormErrors.openingBalanceDate && <p style={{ color: '#DC2626', fontSize: 12, margin: '4px 0 0', fontWeight: 600 }}>⚠ {payFormErrors.openingBalanceDate}</p>}
                      </div>

                      {/* Inline field errors for other fields */}
                      {(payFormErrors.accountName || payFormErrors.bankName || payFormErrors.networkName) && (
                        <p style={{ color: '#DC2626', fontSize: 12, margin: '0 0 12px', fontWeight: 600 }}>
                          ⚠ {payFormErrors.accountName || payFormErrors.bankName || payFormErrors.networkName}
                        </p>
                      )}

                      <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                        <button onClick={handlePayFormSave} style={{ flex: 1, padding: 14, borderRadius: 12, background: '#111827', color: '#A3E635', border: 'none', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>{t.save}</button>
                        <button onClick={() => { setShowPayForm(false); setPayEditId(null); setPayFormErrors({}); }} style={{ flex: 1, padding: 14, borderRadius: 12, background: 'var(--bg-tab)', color: 'var(--text-primary)', border: 'none', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>{t.cancel}</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── PRODUCTS SCREEN ── */}
            {setupScreen === 'products' && (
              <div style={{ padding: '16px 20px' }}>
                <div style={{ background: 'var(--bg-card)', borderRadius: 16, border: '1px solid var(--border-color)', overflow: 'hidden' }}>
                  {(products || []).length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                      <div style={{ fontSize: 36, marginBottom: 10 }}>▣</div>
                      <p style={{ color: 'var(--text-secondary)', marginBottom: 16 }}>{t.noItemsYet}</p>
                      <button onClick={() => setShowSetup_ProductForm(true)} style={{ padding: '11px 24px', borderRadius: 12, background: '#111827', color: '#A3E635', border: 'none', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>+ {t.addNew}</button>
                    </div>
                  ) : (
                    <>
                      {(products || []).map((item, idx) => (
                        <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', borderBottom: idx < (products || []).length - 1 ? '1px solid var(--border-color)' : 'none' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 15 }}>{item.productName} {item.unit && item.unit !== 'piece' ? <span style={{ fontWeight: 400, fontSize: 12, color: 'var(--text-muted)' }}>({item.unit})</span> : null}</div>
                            <div style={{ color: 'var(--text-secondary)', fontSize: 12, marginTop: 2 }}>
                              {item.category && <span style={{ background: 'var(--bg-input)', color: 'var(--text-secondary)', borderRadius: 4, padding: '1px 6px', marginRight: 6, fontSize: 11, fontWeight: 600 }}>{item.category}</span>}
                              {t.qty}: {item.openingStockQty} &middot; {t.alertLabel}: {item.minStockAlert}
                              {item.sellingPrice ? ` · ${t.sellingPrice || 'Price'}: ${fmt(item.sellingPrice)}` : ''}
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button onClick={() => handleSetupEdit(item)} style={{ padding: '6px 14px', borderRadius: 8, background: 'var(--bg-tab)', color: 'var(--text-primary)', border: 'none', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>{t.edit}</button>
                            <button onClick={() => handleSetupProductDelete(item.id)} style={{ padding: '6px 14px', borderRadius: 8, background: '#FEE2E2', color: '#DC2626', border: 'none', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>{t.delete}</button>
                          </div>
                        </div>
                      ))}
                      <div style={{ padding: '14px 18px' }}>
                        <button onClick={() => setShowSetup_ProductForm(true)} style={{ width: '100%', padding: '13px', borderRadius: 12, background: '#111827', color: '#A3E635', border: 'none', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>+ {t.addNew}</button>
                      </div>
                    </>
                  )}
                </div>

                {showSetup_ProductForm && (
                  <div className="popup-overlay">
                    <div className="popup-form su-modal" style={{ maxWidth: 420, width: '100%', borderRadius: 16, padding: 24, maxHeight: '90vh', overflowY: 'auto' }}>
                      <h3 style={{ marginBottom: 14, fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>{setup_EditId ? t.editProduct : t.addProduct}</h3>
                      <form onSubmit={handleSetupSubmit}>
                        {/* Product Name */}
                        <div style={{ marginBottom: 14 }}>
                          <label style={{ display: 'block', fontWeight: 600, color: 'var(--text-primary)', fontSize: 13, marginBottom: 6 }}>{t.productName} *</label>
                          <input type="text" value={setup_Name} onChange={e => setSetup_Name(e.target.value)} placeholder={t.productName} required style={{ width: '100%', padding: '9px 12px', borderRadius: 10, border: 'none', background: 'var(--bg-tab)', fontSize: 14, boxSizing: 'border-box', outline: 'none' }} />
                        </div>
                        {/* Category */}
                        <div style={{ marginBottom: 14 }}>
                          <label style={{ display: 'block', fontWeight: 600, color: 'var(--text-primary)', fontSize: 13, marginBottom: 6 }}>{t.category || 'Aina ya Bidhaa'} <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: 11 }}>({t.optional || 'hiari'})</span></label>
                          <input type="text" value={setup_Category} onChange={e => setSetup_Category(e.target.value)} placeholder="mfano: Chakula, Vinywaji, Vifaa vya Nyumbani" style={{ width: '100%', padding: '9px 12px', borderRadius: 10, border: 'none', background: 'var(--bg-tab)', fontSize: 14, boxSizing: 'border-box', outline: 'none' }} />
                        </div>
                        {/* Unit */}
                        <div style={{ marginBottom: 14 }}>
                          <label style={{ display: 'block', fontWeight: 600, color: 'var(--text-primary)', fontSize: 13, marginBottom: 6 }}>{t.unit || 'Kipimo cha Bidhaa'}</label>
                          <select value={setup_Unit} onChange={e => setSetup_Unit(e.target.value)} style={{ width: '100%', padding: '9px 12px', borderRadius: 10, border: 'none', background: 'var(--bg-tab)', fontSize: 14, boxSizing: 'border-box', outline: 'none' }}>
                            {[{v:'piece',l:'Piece'},{v:'kg',l:'Kilo (kg)'},{v:'gram',l:'Gramu'},{v:'litre',l:'Lita'},{v:'ml',l:'Milimita (ml)'},{v:'box',l:'Sanduku'},{v:'dozen',l:'Dazeni'},{v:'bale',l:'Bale'},{v:'bag',l:'Mfuko'},{v:'metre',l:'Meta'},{v:'pair',l:'Jozi'},{v:'set',l:'Seti'},{v:'bottle',l:'Chupa'},{v:'tin',l:'Mkebe'},{v:'packet',l:'Pakiti'},{v:'roll',l:'Roli'},{v:'sheet',l:'Karatasi'},{v:'bundle',l:'Furushi'}].map(u => <option key={u.v} value={u.v}>{u.l}</option>)}
                          </select>
                        </div>
                        {/* Selling Price */}
                        <div style={{ marginBottom: 14 }}>
                          <label style={{ display: 'block', fontWeight: 600, color: 'var(--text-primary)', fontSize: 13, marginBottom: 6 }}>{t.buyingPrice || 'Bei ya Kununua'}</label>
                          <input type="number" min="0" inputMode="numeric" value={setup_SellingPrice} onChange={e => { const price = e.target.value; setSetup_SellingPrice(price); const p = parseFloat(price) || 0; const q = parseFloat(setup_StockQty) || 0; setSetup_StockValue(p > 0 && q > 0 ? String(p * q) : ''); }} placeholder="0" style={{ width: '100%', padding: '9px 12px', borderRadius: 10, border: 'none', background: 'var(--bg-tab)', fontSize: 14, boxSizing: 'border-box', outline: 'none' }} />
                        </div>
                        {/* Opening Stock Qty */}
                        <div style={{ marginBottom: 14 }}>
                          <label style={{ display: 'block', fontWeight: 600, color: 'var(--text-primary)', fontSize: 13, marginBottom: 6 }}>{t.quantityInStock} *</label>
                          <input type="number" min="0" inputMode="numeric" value={setup_StockQty} onChange={e => { const qty = e.target.value; setSetup_StockQty(qty); const price = parseFloat(setup_SellingPrice) || 0; const q = parseFloat(qty) || 0; setSetup_StockValue(price > 0 && q > 0 ? String(price * q) : ''); }} placeholder="0" required style={{ width: '100%', padding: '9px 12px', borderRadius: 10, border: 'none', background: 'var(--bg-tab)', fontSize: 14, boxSizing: 'border-box', outline: 'none' }} />
                        </div>
                        {/* Total Stock Value */}
                        <div style={{ marginBottom: 14 }}>
                          <label style={{ display: 'block', fontWeight: 600, color: 'var(--text-primary)', fontSize: 13, marginBottom: 6 }}>{t.totalStockValue} * <span style={{ fontWeight: 400, fontSize: 11, color: 'var(--text-secondary)' }}>({t.autoCalculatedEditable || 'auto-calculated, editable'})</span></label>
                          <input type="number" min="0" inputMode="numeric" value={setup_StockValue} onChange={e => setSetup_StockValue(e.target.value)} placeholder="0" required style={{ width: '100%', padding: '9px 12px', borderRadius: 10, border: 'none', background: 'var(--bg-tab)', fontSize: 14, boxSizing: 'border-box', outline: 'none' }} />
                        </div>
                        {/* Low Stock Alert */}
                        <div style={{ marginBottom: 14 }}>
                          <label style={{ display: 'block', fontWeight: 600, color: 'var(--text-primary)', fontSize: 13, marginBottom: 6 }}>{t.lowStockThreshold} *</label>
                          <input type="number" min="0" inputMode="numeric" value={setup_MinAlert} onChange={e => setSetup_MinAlert(e.target.value)} placeholder="5" required style={{ width: '100%', padding: '9px 12px', borderRadius: 10, border: 'none', background: 'var(--bg-tab)', fontSize: 14, boxSizing: 'border-box', outline: 'none' }} />
                        </div>
                        <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                          <button type="submit" className="su-save-btn" style={{ flex: 1, padding: 14, borderRadius: 12, border: 'none', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>{t.save}</button>
                          <button type="button" onClick={handleCancelEdit} style={{ flex: 1, padding: 14, borderRadius: 12, background: 'var(--bg-tab)', color: 'var(--text-primary)', border: 'none', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>{t.cancel}</button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── SERVICES SCREEN ── */}
            {setupScreen === 'services' && (
              <div style={{ padding: '16px 20px' }}>
                <div style={{ background: 'var(--bg-card)', borderRadius: 16, border: '1px solid var(--border-color)', overflow: 'hidden' }}>
                  {(services || []).length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                      <div style={{ fontSize: 36, marginBottom: 10 }}>□</div>
                      <p style={{ color: 'var(--text-secondary)', marginBottom: 16 }}>{t.noItemsYet}</p>
                      <button onClick={() => setShowServiceForm(true)} style={{ padding: '11px 24px', borderRadius: 12, background: '#111827', color: '#A3E635', border: 'none', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>+ {t.addNew}</button>
                    </div>
                  ) : (
                    <>
                      {(services || []).map((s, idx) => (
                        <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', borderBottom: idx < (services || []).length - 1 ? '1px solid var(--border-color)' : 'none' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 15 }}>{s.name}</div>
                            {(s.defaultPrice || s.defaultCost) ? (
                              <div style={{ color: 'var(--text-secondary)', fontSize: 12, marginTop: 2 }}>
                                {s.defaultPrice ? `${t.price}: ${fmt(s.defaultPrice)}` : ''}
                                {s.defaultPrice && s.defaultCost ? ' · ' : ''}
                                {s.defaultCost ? `${t.cost}: ${fmt(s.defaultCost)}` : ''}
                              </div>
                            ) : null}
                          </div>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button onClick={() => handleServiceEdit(s)} style={{ padding: '6px 14px', borderRadius: 8, background: 'var(--bg-tab)', color: 'var(--text-primary)', border: 'none', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>{t.edit}</button>
                            <button onClick={() => handleServiceDelete(s.id)} style={{ padding: '6px 14px', borderRadius: 8, background: '#FEE2E2', color: '#DC2626', border: 'none', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>{t.delete}</button>
                          </div>
                        </div>
                      ))}
                      <div style={{ padding: '14px 18px' }}>
                        <button onClick={() => setShowServiceForm(true)} style={{ width: '100%', padding: '13px', borderRadius: 12, background: '#111827', color: '#A3E635', border: 'none', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>+ {t.addNew}</button>
                      </div>
                    </>
                  )}
                </div>

                {showServiceForm && (
                  <div className="popup-overlay">
                    <div className="popup-form su-modal" style={{ maxWidth: 420, width: '100%', borderRadius: 16, padding: 24 }}>
                      <h3 style={{ marginBottom: 14, fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>{serviceEditId ? t.editService : t.addService}</h3>
                      <form onSubmit={handleServiceSubmit}>
                        <div style={{ marginBottom: 14 }}>
                          <label style={{ display: 'block', fontWeight: 600, color: 'var(--text-primary)', fontSize: 13, marginBottom: 6 }}>{t.serviceName || 'Service Name'}</label>
                          <input type="text" value={serviceFormData.name} onChange={e => setServiceFormData(p => ({ ...p, name: e.target.value }))} placeholder={t.serviceName || 'Service Name'} required
                            style={{ width: '100%', padding: '9px 12px', borderRadius: 10, border: 'none', background: 'var(--bg-tab)', fontSize: 14, boxSizing: 'border-box', outline: 'none' }} />
                        </div>
                        <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                          <button type="submit" style={{ flex: 1, padding: 14, borderRadius: 12, background: '#111827', color: '#A3E635', border: 'none', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>{t.save}</button>
                          <button type="button" onClick={() => { setShowServiceForm(false); setServiceFormData({ name: '', defaultPrice: '', defaultCost: '' }); setServiceEditId(null); }} style={{ flex: 1, padding: 14, borderRadius: 12, background: 'var(--bg-tab)', color: 'var(--text-primary)', border: 'none', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>{t.cancel}</button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── EXPENSE CATEGORIES SCREEN ── */}
            {setupScreen === 'expenseCategories' && (
              <div style={{ padding: '16px 20px' }}>
                <div style={{ background: 'var(--bg-card)', borderRadius: 16, border: '1px solid var(--border-color)', overflow: 'hidden' }}>
                  {expenseCategories.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                      <div style={{ fontSize: 36, marginBottom: 10 }}>▼</div>
                      <p style={{ color: 'var(--text-secondary)', marginBottom: 16 }}>{t.noItemsYet}</p>
                      <button onClick={() => setShowExpense_SetupForm(true)} style={{ padding: '11px 24px', borderRadius: 12, background: '#111827', color: '#A3E635', border: 'none', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>+ {t.addNew}</button>
                    </div>
                  ) : (
                    <>
                      {expenseCategories.map((item, idx) => (
                        <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', borderBottom: idx < expenseCategories.length - 1 ? '1px solid var(--border-color)' : 'none' }}>
                          <div style={{ flex: 1, fontWeight: 600, color: 'var(--text-primary)', fontSize: 15 }}>{item.name}</div>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button onClick={() => handleExpenseEdit(item)} style={{ padding: '6px 14px', borderRadius: 8, background: 'var(--bg-tab)', color: 'var(--text-primary)', border: 'none', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>{t.edit}</button>
                            <button onClick={() => handleExpenseTypeDelete(item.id)} style={{ padding: '6px 14px', borderRadius: 8, background: '#FEE2E2', color: '#DC2626', border: 'none', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>{t.delete}</button>
                          </div>
                        </div>
                      ))}
                      <div style={{ padding: '14px 18px' }}>
                        <button onClick={() => setShowExpense_SetupForm(true)} style={{ width: '100%', padding: '13px', borderRadius: 12, background: '#111827', color: '#A3E635', border: 'none', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>+ {t.addNew}</button>
                      </div>
                    </>
                  )}
                </div>
                {showExpense_SetupForm && (
                  <div className="popup-overlay">
                    <div className="popup-form su-modal" style={{ maxWidth: 420, width: '100%', borderRadius: 16, padding: 24 }}>
                      <h3 style={{ marginBottom: 14, fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>{expense_EditId ? t.editCategory : t.addExpenseCategory}</h3>
                      <form onSubmit={handleExpenseSubmit}>
                        <div style={{ marginBottom: 18 }}>
                          <label style={{ display: 'block', fontWeight: 600, color: 'var(--text-primary)', fontSize: 13, marginBottom: 6 }}>{t.categoryName}</label>
                          <input type="text" value={expense_Name} onChange={e => setExpense_Name(e.target.value)} placeholder={t.expenseCategoryPlaceholder} required
                            style={{ width: '100%', padding: '9px 12px', borderRadius: 10, border: 'none', background: 'var(--bg-tab)', fontSize: 14, boxSizing: 'border-box', outline: 'none' }} />
                        </div>
                        <div style={{ display: 'flex', gap: 10 }}>
                          <button type="submit" className="su-save-btn" style={{ flex: 1, padding: 14, borderRadius: 12, border: 'none', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>{t.save}</button>
                          <button type="button" onClick={handleCancelEdit} style={{ flex: 1, padding: 14, borderRadius: 12, background: 'var(--bg-tab)', color: 'var(--text-primary)', border: 'none', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>{t.cancel}</button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── SUPPLIERS SCREEN ── */}
            {setupScreen === 'suppliers' && (
              <div style={{ padding: '16px 20px' }}>
                <div style={{ background: 'var(--bg-card)', borderRadius: 16, border: '1px solid var(--border-color)', overflow: 'hidden' }}>
                  {(suppliers || []).length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                      <div style={{ fontSize: 36, marginBottom: 10 }}>▦</div>
                      <p style={{ color: 'var(--text-secondary)', marginBottom: 16 }}>{t.noItemsYet}</p>
                      <button onClick={() => setShowSupplier_Form(true)} style={{ padding: '11px 24px', borderRadius: 12, background: '#111827', color: '#A3E635', border: 'none', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>+ {t.addNew}</button>
                    </div>
                  ) : (
                    <>
                      {(suppliers || []).map((item, idx) => (
                        <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', borderBottom: idx < (suppliers || []).length - 1 ? '1px solid var(--border-color)' : 'none' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 15 }}>{item.name}</div>
                            {item.contact && <div style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 2 }}>{item.contact}</div>}
                          </div>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button onClick={() => openLedger('supplier', item)} style={{ padding: '6px 12px', borderRadius: 8, background: 'var(--bg-input)', color: 'var(--text-secondary)', border: 'none', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>{t.ledger || 'Ledger'}</button>
                            <button onClick={() => handleSupplierEdit(item)} style={{ padding: '6px 14px', borderRadius: 8, background: 'var(--bg-tab)', color: 'var(--text-primary)', border: 'none', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>{t.edit}</button>
                            <button onClick={() => handleSupplierDelete(item.id)} style={{ padding: '6px 14px', borderRadius: 8, background: '#FEE2E2', color: '#DC2626', border: 'none', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>{t.delete}</button>
                          </div>
                        </div>
                      ))}
                      <div style={{ padding: '14px 18px' }}>
                        <button onClick={() => setShowSupplier_Form(true)} style={{ width: '100%', padding: '13px', borderRadius: 12, background: '#111827', color: '#A3E635', border: 'none', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>+ {t.addNew}</button>
                      </div>
                    </>
                  )}
                </div>
                {showSupplier_Form && (
                  <div className="popup-overlay">
                    <div className="popup-form su-modal" style={{ maxWidth: 420, width: '100%', borderRadius: 16, padding: 24 }}>
                      <h3 style={{ marginBottom: 14, fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>{supplier_EditId ? t.editSupplier : t.addSupplier}</h3>
                      <form onSubmit={handleSupplierSubmit}>
                        <div style={{ marginBottom: 14 }}>
                          <label style={{ display: 'block', fontWeight: 600, color: 'var(--text-primary)', fontSize: 13, marginBottom: 6 }}>{t.supplierName}</label>
                          <input type="text" value={supplier_Name} onChange={e => setSupplier_Name(e.target.value)} placeholder={t.supplierName} required
                            style={{ width: '100%', padding: '9px 12px', borderRadius: 10, border: 'none', background: 'var(--bg-tab)', fontSize: 14, boxSizing: 'border-box', outline: 'none' }} />
                        </div>
                        <div style={{ marginBottom: 18 }}>
                          <label style={{ display: 'block', fontWeight: 600, color: 'var(--text-primary)', fontSize: 13, marginBottom: 6 }}>{t.contactOptional}</label>
                          <input type="text" value={supplier_Contact} onChange={e => setSupplier_Contact(e.target.value)} placeholder={t.phoneOrEmail}
                            style={{ width: '100%', padding: '9px 12px', borderRadius: 10, border: 'none', background: 'var(--bg-tab)', fontSize: 14, boxSizing: 'border-box', outline: 'none' }} />
                        </div>
                        <div style={{ display: 'flex', gap: 10 }}>
                          <button type="submit" className="su-save-btn" style={{ flex: 1, padding: 14, borderRadius: 12, border: 'none', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>{t.save}</button>
                          <button type="button" onClick={handleCancelEdit} style={{ flex: 1, padding: 14, borderRadius: 12, background: 'var(--bg-tab)', color: 'var(--text-primary)', border: 'none', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>{t.cancel}</button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── CUSTOMERS SCREEN ── */}
            {setupScreen === 'customers' && (
              <div style={{ padding: '16px 20px' }}>
                <div style={{ background: 'var(--bg-card)', borderRadius: 16, border: '1px solid var(--border-color)', overflow: 'hidden' }}>
                  {(customers || []).length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                      <div style={{ fontSize: 36, marginBottom: 10 }}>◎</div>
                      <p style={{ color: 'var(--text-secondary)', marginBottom: 16 }}>{t.noItemsYet}</p>
                      <button onClick={() => setShowCustomer_Form(true)} style={{ padding: '11px 24px', borderRadius: 12, background: '#111827', color: '#A3E635', border: 'none', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>+ {t.addNew}</button>
                    </div>
                  ) : (
                    <>
                      {(customers || []).map((item, idx) => (
                        <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', borderBottom: idx < (customers || []).length - 1 ? '1px solid var(--border-color)' : 'none' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 15 }}>{item.name}</div>
                            {item.phone && <div style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 2 }}>{item.phone}</div>}
                          </div>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button onClick={() => openLedger('customer', item)} style={{ padding: '6px 12px', borderRadius: 8, background: '#ECFDF5', color: '#065F46', border: 'none', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>{t.ledger || 'Ledger'}</button>
                            <button onClick={() => handleCustomerEdit(item)} style={{ padding: '6px 14px', borderRadius: 8, background: 'var(--bg-tab)', color: 'var(--text-primary)', border: 'none', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>{t.edit}</button>
                            <button onClick={() => handleCustomerDelete(item.id)} style={{ padding: '6px 14px', borderRadius: 8, background: '#FEE2E2', color: '#DC2626', border: 'none', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>{t.delete}</button>
                          </div>
                        </div>
                      ))}
                      <div style={{ padding: '14px 18px' }}>
                        <button onClick={() => setShowCustomer_Form(true)} style={{ width: '100%', padding: '13px', borderRadius: 12, background: '#111827', color: '#A3E635', border: 'none', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>+ {t.addNew}</button>
                      </div>
                    </>
                  )}
                </div>
                {showCustomer_Form && (
                  <div className="popup-overlay">
                    <div className="popup-form su-modal" style={{ maxWidth: 420, width: '100%', borderRadius: 16, padding: 24 }}>
                      <h3 style={{ marginBottom: 14, fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>{customer_EditId ? t.editCustomer : t.addCustomer}</h3>
                      <form onSubmit={handleCustomerSubmit}>
                        <div style={{ marginBottom: 12 }}>
                          <label style={{ display: 'block', fontWeight: 600, color: 'var(--text-primary)', fontSize: 13, marginBottom: 6 }}>{t.customerName} *</label>
                          <input type="text" value={customer_Name} onChange={e => setCustomer_Name(e.target.value)} placeholder={t.customerName} required
                            style={{ width: '100%', padding: '9px 12px', borderRadius: 10, border: 'none', background: 'var(--bg-tab)', fontSize: 14, boxSizing: 'border-box', outline: 'none' }} />
                        </div>
                        <div style={{ marginBottom: 12 }}>
                          <label style={{ display: 'block', fontWeight: 600, color: 'var(--text-primary)', fontSize: 13, marginBottom: 6 }}>{t.phoneOptional || 'Phone'} <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional)</span></label>
                          <input type="tel" inputMode="numeric" value={customer_Phone} onChange={e => setCustomer_Phone(e.target.value)} placeholder="e.g. 0712 345 678"
                            style={{ width: '100%', padding: '9px 12px', borderRadius: 10, border: 'none', background: 'var(--bg-tab)', fontSize: 14, boxSizing: 'border-box', outline: 'none' }} />
                        </div>
                        <div style={{ marginBottom: 12 }}>
                          <label style={{ display: 'block', fontWeight: 600, color: 'var(--text-primary)', fontSize: 13, marginBottom: 6 }}>Email <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional)</span></label>
                          <input type="email" value={customer_Email} onChange={e => setCustomer_Email(e.target.value)} placeholder="e.g. customer@email.com"
                            style={{ width: '100%', padding: '9px 12px', borderRadius: 10, border: 'none', background: 'var(--bg-tab)', fontSize: 14, boxSizing: 'border-box', outline: 'none' }} />
                        </div>
                        <div style={{ marginBottom: 12 }}>
                          <label style={{ display: 'block', fontWeight: 600, color: 'var(--text-primary)', fontSize: 13, marginBottom: 6 }}>{language === 'sw' ? 'Anwani' : 'Address'} <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional)</span></label>
                          <input type="text" value={customer_Address} onChange={e => setCustomer_Address(e.target.value)} placeholder={language === 'sw' ? 'Mji, Mtaa...' : 'Street, City...'}
                            style={{ width: '100%', padding: '9px 12px', borderRadius: 10, border: 'none', background: 'var(--bg-tab)', fontSize: 14, boxSizing: 'border-box', outline: 'none' }} />
                        </div>
                        <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
                          <div style={{ flex: 1 }}>
                            <label style={{ display: 'block', fontWeight: 600, color: 'var(--text-primary)', fontSize: 13, marginBottom: 6 }}>TIN <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional)</span></label>
                            <input type="text" value={customer_TIN} onChange={e => setCustomer_TIN(e.target.value)} placeholder="123-456-789"
                              style={{ width: '100%', padding: '9px 12px', borderRadius: 10, border: 'none', background: 'var(--bg-tab)', fontSize: 14, boxSizing: 'border-box', outline: 'none' }} />
                          </div>
                          <div style={{ flex: 1 }}>
                            <label style={{ display: 'block', fontWeight: 600, color: 'var(--text-primary)', fontSize: 13, marginBottom: 6 }}>VRN <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional)</span></label>
                            <input type="text" value={customer_VRN} onChange={e => setCustomer_VRN(e.target.value)} placeholder="40-001234-X"
                              style={{ width: '100%', padding: '9px 12px', borderRadius: 10, border: 'none', background: 'var(--bg-tab)', fontSize: 14, boxSizing: 'border-box', outline: 'none' }} />
                          </div>
                        </div>
                        <div style={{ marginBottom: 18 }}>
                          <label style={{ display: 'block', fontWeight: 600, color: 'var(--text-primary)', fontSize: 13, marginBottom: 6 }}>{language === 'sw' ? 'Maelezo' : 'Notes'} <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional)</span></label>
                          <textarea value={customer_Notes} onChange={e => setCustomer_Notes(e.target.value)} placeholder={language === 'sw' ? 'Maelezo ya ziada...' : 'Any additional details...'}
                            rows={2} style={{ width: '100%', padding: '9px 12px', borderRadius: 10, border: 'none', background: 'var(--bg-tab)', fontSize: 14, boxSizing: 'border-box', outline: 'none', resize: 'vertical', fontFamily: 'inherit' }} />
                        </div>
                        <div style={{ display: 'flex', gap: 10 }}>
                          <button type="submit" className="su-save-btn" style={{ flex: 1, padding: 14, borderRadius: 12, border: 'none', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>{t.save}</button>
                          <button type="button" onClick={handleCancelEdit} style={{ flex: 1, padding: 14, borderRadius: 12, background: 'var(--bg-tab)', color: 'var(--text-primary)', border: 'none', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>{t.cancel}</button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── LEDGER MODAL ── */}
            {showLedger && ledgerEntity && (() => {
              const isCustomer = ledgerEntity.type === 'customer';
              const fmt = (n) => `${currency} ${parseFloat(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

              // Build ledger lines
              let lines = [];
              if (isCustomer) {
                const custSales = (sales || [])
                  .filter(s => s.status !== 'void' && (s.customerName || '').toLowerCase() === ledgerEntity.name.toLowerCase())
                  .sort((a, b) => new Date(a.date) - new Date(b.date));
                custSales.forEach(s => {
                  lines.push({ id: s.id, date: s.date, desc: s.productName || s.serviceName || '—', debit: parseFloat(s.totalRevenue || 0), credit: 0, paid: s.isPaid !== false, type: 'sale' });
                });
              } else {
                const suppPurchases = (purchases || [])
                  .filter(p => p.status !== 'void' && (p.supplier || '').toLowerCase() === ledgerEntity.name.toLowerCase())
                  .sort((a, b) => new Date(a.date) - new Date(b.date));
                suppPurchases.forEach(p => {
                  lines.push({ id: p.id, date: p.date, desc: p.productName || '—', debit: parseFloat(p.totalCost || 0), credit: 0, paid: p.isPaid !== false, type: 'purchase' });
                });
              }

              // Running balance
              let running = 0;
              const linesWithBalance = lines.map(l => {
                if (l.paid) { /* no-op */ }
                else { running += l.debit - l.credit; }
                return { ...l, balance: running };
              });
              const totalOwed = lines.filter(l => !l.paid).reduce((s, l) => s + l.debit - l.credit, 0);
              const totalPaid = lines.filter(l => l.paid).reduce((s, l) => s + l.debit, 0);

              return (
                <div className="popup-overlay" style={{ zIndex: 1000 }}>
                  <div className="popup-form" style={{ maxWidth: 480, width: '100%', borderRadius: 16, padding: 0, overflow: 'hidden', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
                    {/* Header */}
                    <div style={{ background: '#111827', padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div>
                        <div style={{ color: '#A3E635', fontSize: 12, fontWeight: 600, marginBottom: 2 }}>{isCustomer ? (t.customerLedger || 'Customer Ledger') : (t.supplierLedger || 'Supplier Ledger')}</div>
                        <div style={{ color: '#FFFFFF', fontSize: 18, fontWeight: 800 }}>{ledgerEntity.name}</div>
                        {ledgerEntity.contact && <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, marginTop: 2 }}>{ledgerEntity.contact}</div>}
                      </div>
                      <button onClick={() => setShowLedger(false)} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 10, padding: '8px 14px', color: '#FFFFFF', fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>× {t.close || 'Close'}</button>
                    </div>

                    {/* Summary strip */}
                    <div style={{ display: 'flex', background: '#F8FAFC', borderBottom: '1px solid var(--border-default)' }}>
                      <div style={{ flex: 1, padding: '10px 16px', borderRight: '1px solid var(--border-default)', textAlign: 'center' }}>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, marginBottom: 4 }}>{t.totalTransactions || 'TRANSACTIONS'}</div>
                        <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--text-primary)' }}>{lines.length}</div>
                      </div>
                      <div style={{ flex: 1, padding: '10px 16px', borderRight: '1px solid var(--border-default)', textAlign: 'center' }}>
                        <div style={{ fontSize: 11, color: '#059669', fontWeight: 600, marginBottom: 4 }}>{t.paidLabel || 'PAID'}</div>
                        <div style={{ fontSize: 17, fontWeight: 800, color: '#059669' }}>{fmt(totalPaid)}</div>
                      </div>
                      <div style={{ flex: 1, padding: '10px 16px', textAlign: 'center' }}>
                        <div style={{ fontSize: 11, color: totalOwed > 0 ? '#DC2626' : '#059669', fontWeight: 600, marginBottom: 4 }}>{isCustomer ? (t.outstanding || 'OUTSTANDING') : (t.owedToSupplier || 'YOU OWE')}</div>
                        <div style={{ fontSize: 17, fontWeight: 800, color: totalOwed > 0 ? '#DC2626' : '#059669' }}>{fmt(totalOwed)}</div>
                      </div>
                    </div>

                    {/* Transaction list */}
                    <div style={{ overflowY: 'auto', flex: 1 }}>
                      {linesWithBalance.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--text-secondary)' }}>
                          <div style={{ fontSize: 32, marginBottom: 10 }}>≡</div>
                          <div style={{ fontWeight: 600 }}>{t.noTransactionsYet || 'No transactions yet'}</div>
                        </div>
                      ) : (
                        linesWithBalance.map((line, idx) => (
                          <div key={line.id} style={{ padding: '10px 16px', borderBottom: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', gap: 12, background: line.paid ? 'var(--bg-card)' : '#FFFBEB' }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                                <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 14 }}>{line.desc}</span>
                                <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 8, background: line.paid ? '#D1FAE5' : '#FEF3C7', color: line.paid ? '#065F46' : '#92400E' }}>
                                  {line.paid ? (t.paidLabel || 'PAID') : (t.unpaidLabel || 'UNPAID')}
                                </span>
                              </div>
                              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{line.date}</div>
                            </div>
                            <div style={{ textAlign: 'right', minWidth: 90 }}>
                              <div style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: 14 }}>{fmt(line.debit)}</div>
                              {!line.paid && <div style={{ fontSize: 11, color: '#DC2626', marginTop: 1 }}>Balance: {fmt(line.balance)}</div>}
                            </div>
                            {!line.paid && (
                              <button
                                onClick={() => { if(isCustomer) markSalePaid(line.id); else markPurchasePaid(line.id); }}
                                style={{ padding: '6px 12px', borderRadius: 8, background: '#111827', color: '#A3E635', border: 'none', fontWeight: 700, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                {t.markPaid || 'Mark Paid'}
                              </button>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* ── CURRENCY SCREEN ── */}
            {setupScreen === 'currency' && (
              <div style={{ padding: '16px 20px' }}>
                <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 16, marginTop: 0 }}>{t.currencySubLabel}</p>
                <div style={{ background: 'var(--bg-card)', borderRadius: 16, border: '1px solid var(--border-color)', overflow: 'hidden' }}>
                  {CURRENCY_LIST.map((cur, idx) => {
                    const isSelected = currency === cur.code;
                    return (
                      <button key={cur.code} onClick={() => setCurrency(cur.code)}
                        style={{ display: 'flex', alignItems: 'center', width: '100%', padding: '14px 18px', background: isSelected ? '#111827' : 'transparent', border: 'none', borderBottom: idx < CURRENCY_LIST.length - 1 ? '1px solid var(--border-color)' : 'none', cursor: 'pointer', textAlign: 'left', gap: 14 }}>
                        <div style={{ width: 40, height: 40, borderRadius: 12, background: isSelected ? '#1F2937' : '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <span style={{ fontSize: 16, fontWeight: 800, color: isSelected ? '#A3E635' : '#374151' }}>{cur.symbol}</span>
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: 15, color: isSelected ? 'white' : 'var(--text-primary)' }}>{cur.name}</div>
                          <div style={{ fontSize: 12, color: isSelected ? '#9CA3AF' : 'var(--text-secondary)', marginTop: 1 }}>{cur.code} · {cur.symbol}</div>
                        </div>
                        {isSelected && (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#A3E635" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── COMPLIANCE SCREEN ── */}
            {setupScreen === 'compliance' && (
              <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                <p style={{ color: 'var(--text-secondary)', fontSize: 14, margin: '0 0 4px' }}>
                  {t.complianceTaxDesc || 'Maelezo haya yanaonekana kwenye invoisi na risiti zako kwa uzingatiaji wa kisheria.'}
                </p>
                {complianceEditMode ? (
                  <>
                    {[
                      { label: t.tinLabel || 'TIN (Nambari ya Utambulisho wa Kodi)', value: businessTIN, setter: setBusinessTIN, key: 'businessTIN', placeholder: 'mfano 100-123-456' },
                      { label: t.vrnLabel || 'VRN (Nambari ya Usajili wa VAT)', value: businessVRN, setter: setBusinessVRN, key: 'businessVRN', placeholder: 'mfano 40-000123-L' },
                      { label: t.businessRegLabel || 'Nambari ya Usajili wa Biashara', value: businessRegNo, setter: setBusinessRegNo, key: 'businessRegNo', placeholder: 'mfano 12345678' },
                      { label: t.tradeLicenseLabel || 'Nambari ya Leseni ya Biashara', value: tradeLicenseNo, setter: setTradeLicenseNo, key: 'tradeLicenseNo', placeholder: 'mfano TL-2024-001' },
                    ].map(field => (
                      <div key={field.key}>
                        <label style={{ display: 'block', fontWeight: 600, color: 'var(--text-primary)', fontSize: 14, marginBottom: 8 }}>{field.label}</label>
                        <input
                          type="text"
                          value={field.value}
                          onChange={e => field.setter(e.target.value)}
                          placeholder={field.placeholder}
                          style={{ width: '100%', padding: '9px 12px', borderRadius: 12, border: '1.5px solid var(--border-color)', background: 'var(--bg-input,#F9FAFB)', fontSize: 15, color: 'var(--text-primary)', boxSizing: 'border-box', outline: 'none' }}
                        />
                      </div>
                    ))}
                    <div style={{ background: '#FFF9C4', border: '1px solid #F9E04B', borderRadius: 12, padding: '9px 12px', marginTop: 4 }}>
                      <p style={{ margin: 0, fontSize: 13, color: '#92700A', lineHeight: 1.5 }}>
                        ◆ {t.complianceOptionalNote || 'Sehemu hizi si lazima. Jaza kama biashara yako imesajiliwa na TRA au ina leseni ya biashara.'}
                      </p>
                    </div>
                    <button
                      onClick={() => setComplianceEditMode(false)}
                      style={{ width: '100%', padding: '14px', borderRadius: 12, border: 'none', background: '#111827', color: '#fff', fontWeight: 700, fontSize: 16, cursor: 'pointer', marginTop: 4 }}
                    >
                      {t.save || 'Hifadhi'}
                    </button>
                  </>
                ) : (
                  <>
                    {[
                      { label: t.tinLabel || 'TIN (Nambari ya Utambulisho wa Kodi)', value: businessTIN },
                      { label: t.vrnLabel || 'VRN (Nambari ya Usajili wa VAT)', value: businessVRN },
                      { label: t.businessRegLabel || 'Nambari ya Usajili wa Biashara', value: businessRegNo },
                      { label: t.tradeLicenseLabel || 'Nambari ya Leseni ya Biashara', value: tradeLicenseNo },
                    ].map((field, i) => (
                      <div key={i} style={{ padding: '9px 12px', borderRadius: 12, border: '1px solid var(--border-color)', background: 'var(--bg-card)' }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 6 }}>{field.label}</div>
                        <div style={{ fontSize: 15, color: field.value ? 'var(--text-primary)' : 'var(--text-secondary)', fontStyle: field.value ? 'normal' : 'italic' }}>
                          {field.value || (t.notSet || 'Haijawekwa')}
                        </div>
                      </div>
                    ))}
                    <button
                      onClick={() => setComplianceEditMode(true)}
                      style={{ width: '100%', padding: '14px', borderRadius: 12, border: '2px solid var(--border-color)', background: 'transparent', color: 'var(--text-primary)', fontWeight: 700, fontSize: 16, cursor: 'pointer', marginTop: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                    >
                      ✎️ {t.edit || 'Hariri'}
                    </button>
                  </>
                )}
              </div>
            )}

            {/* ── VAT RATE SCREEN ── */}
            {setupScreen === 'vatRate' && (
              <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                {/* VAT-registered toggle -the master switch. OFF by default so
                    non-registered micro-SMEs never see VAT anywhere. */}
                <div style={{ background: 'var(--bg-card)', borderRadius: 16, border: '1px solid var(--border-color)', padding: '16px 18px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: 14 }}>
                        {language === 'sw' ? 'Biashara imesajiliwa VAT?' : 'Is this business VAT-registered?'}
                      </div>
                      <div style={{ color: 'var(--text-secondary)', fontSize: 12, marginTop: 3 }}>
                        {language === 'sw' ? 'Washa tu kama una VRN kutoka TRA. Ikizimwa, VAT haitaonekana popote.' : 'Only turn on if you have a TRA-issued VRN. When off, VAT is hidden everywhere.'}
                      </div>
                    </div>
                    <button type="button" role="switch" aria-checked={vatRegistered}
                      onClick={() => { const nv = !vatRegistered; setVatRegistered(nv); try { localStorage.setItem('faida-vat-registered', nv ? '1' : '0'); } catch(e) {} }}
                      style={{ width: 52, height: 30, borderRadius: 999, border: 'none', cursor: 'pointer', flexShrink: 0, position: 'relative', background: vatRegistered ? '#059669' : '#CBD5E1', transition: 'background 0.15s' }}>
                      <span style={{ position: 'absolute', top: 3, left: vatRegistered ? 25 : 3, width: 24, height: 24, borderRadius: '50%', background: '#fff', transition: 'left 0.15s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
                    </button>
                  </div>
                </div>
                {!vatRegistered && (
                  <div style={{ background: 'var(--bg-input)', borderRadius: 12, padding: '10px 14px', fontSize: 13, color: 'var(--text-secondary)' }}>
                    {language === 'sw' ? '◆ VAT imezimwa. Bei zako zote hazina VAT, na faida haiathiriwi na kodi. Washa hapo juu tu kama umesajiliwa TRA.' : '◆ VAT is off. All your prices are VAT-free and profit is unaffected by tax. Turn it on above only if you are TRA-registered.'}
                  </div>
                )}
                {vatRegistered && (<>
                <p style={{ color: 'var(--text-secondary)', fontSize: 14, margin: 0 }}>
                  {language === 'sw' ? 'Kiwango hiki kitatumika kiotomatiki unapobonyeza "+ Add Tax" kwenye mauzo au manunuzi.' : 'This rate will be pre-filled automatically when you click "+ Add Tax" on sales or purchases.'}
                </p>
                <div style={{ background: 'var(--bg-card)', borderRadius: 16, border: '1px solid var(--border-color)', padding: '20px 18px' }}>
                  <label style={{ display: 'block', fontWeight: 700, color: 'var(--text-primary)', fontSize: 14, marginBottom: 10 }}>
                    {t.vatRateSetting || 'Default VAT Rate'} (%)
                  </label>
                  <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
                    {[0, 5, 10, 15, 18].map(rate => (
                      <button key={rate} onClick={() => { setDefaultVatRate(rate); try { localStorage.setItem('faida-vat-rate', rate); } catch(e) {} }}
                        style={{ flex: 1, padding: '12px 4px', borderRadius: 12, border: `2px solid ${defaultVatRate === rate ? '#059669' : 'var(--border-color)'}`, background: defaultVatRate === rate ? '#DCFCE7' : 'var(--bg-tab)', color: defaultVatRate === rate ? '#065F46' : 'var(--text-primary)', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
                        {rate}%
                      </button>
                    ))}
                  </div>
                  <label style={{ display: 'block', fontWeight: 600, color: 'var(--text-secondary)', fontSize: 13, marginBottom: 6 }}>
                    {language === 'sw' ? 'Au weka kiwango maalum:' : 'Or enter custom rate:'}
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <input type="number" min="0" max="100" step="0.5" value={defaultVatRate} inputMode="decimal"
                      onChange={e => { const v = parseFloat(e.target.value) || 0; setDefaultVatRate(v); try { localStorage.setItem('faida-vat-rate', v); } catch(e2) {} }}
                      style={{ flex: 1, padding: '9px 12px', borderRadius: 12, border: '1.5px solid var(--border-color)', background: 'var(--bg-input,#F9FAFB)', fontSize: 16, fontWeight: 700, outline: 'none', boxSizing: 'border-box' }} />
                    <span style={{ fontWeight: 700, fontSize: 18, color: 'var(--text-secondary)' }}>%</span>
                  </div>
                </div>
                <div style={{ background: 'var(--bg-input)', borderRadius: 12, padding: '9px 12px' }}>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)', fontWeight: 500 }}>
                    ◆ {language === 'sw' ? 'Tanzania: VAT ya kawaida ni 18%. Biashara zilizosajiliwa na TRA zinatakiwa kukusanya VAT.' : 'Tanzania standard VAT rate is 18%. Only VAT-registered businesses (with VRN) are required to charge VAT.'}
                  </div>
                </div>
                </>)}
              </div>
            )}

            {/* Invoice Terms setup screen removed -terms are entered per-sale only. */}

            {setupScreen === 'businessType' && (
              <div style={{ padding: '16px 20px' }}>
                <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 16, marginTop: 0 }}>{t.selectBusinessType}</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {[
                    { val: 'service_only', label: t.serviceOnly, desc: t.serviceOnlyDesc, icon: '⚒️' },
                    { val: 'products_only', label: t.productsOnly, desc: t.productsOnlyDesc, icon: '▣' },
                    { val: 'products_and_services', label: t.productsAndServices, desc: t.productsAndServicesDesc, icon: '▥' },
                  ].map(opt => (
                    <button key={opt.val} onClick={() => {
                      if (opt.val === businessType) return;
                      // Determine transition risk
                      let risk = 'safe';
                      if ((businessType === 'products_and_services' && opt.val === 'service_only') ||
                          (businessType === 'products_only' && opt.val === 'service_only') ||
                          (businessType === 'service_only' && opt.val === 'products_only')) risk = 'high';
                      else if (businessType === 'products_and_services' && opt.val === 'products_only') risk = 'medium';
                      if (risk === 'safe') {
                        const p = currentUser ? `faida_${currentUser.uid}_` : 'faida_';
                        setBusinessType(opt.val);
                        localStorage.setItem(p + 'businessType', opt.val);
                        localStorage.setItem(p + 'businessTypeUpdatedAt', new Date().toISOString());
                      } else {
                        setBusinessTypeTransitionWarning({ newType: opt.val, newLabel: opt.label, risk });
                      }
                    }}
                      style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 18px', borderRadius: 16, border: `2px solid ${businessType === opt.val ? '#111827' : 'var(--border-color)'}`, background: businessType === opt.val ? '#111827' : 'var(--bg-card)', cursor: 'pointer', textAlign: 'left', width: '100%' }}>
                      <span style={{ fontSize: 26 }}>{opt.icon}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, color: businessType === opt.val ? '#A3E635' : 'var(--text-primary)', fontSize: 15 }}>{opt.label}</div>
                        <div style={{ color: businessType === opt.val ? '#A3E63599' : 'var(--text-secondary)', fontSize: 13, marginTop: 3 }}>{opt.desc}</div>
                      </div>
                      {businessType === opt.val && (
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" fill="#A3E635"/><polyline points="9 12 11 14 15 10" stroke="#111827" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
            </>
            )}
          </div>
        )}

        {activeTab === 'settings' && (() => {
          if (!isDataReady) return (
            <div style={{ minHeight: '100vh', background: 'var(--bg-main)', paddingBottom: 80 }}>
              <div role="status" aria-label={t.loading || 'Loading'} style={{ padding: '20px 16px' }}>
                <div className="skeleton skeleton-title" style={{ width: '25%', marginBottom: 20 }}></div>
                <div className="skeleton skeleton-card" style={{ height: 80, marginBottom: 16 }}></div>
                {[1,2,3,4,5,6,7].map(i => <div key={i} className="skeleton skeleton-settings-row"></div>)}
              </div>
            </div>
          );
          const subScreen = settingsScreen;
          const subTitle = subScreen === 'privacyPolicy' ? t.privacyPolicy : subScreen === 'contactUs' ? t.contactUs : subScreen === 'subscription' ? (t.choosePlanTitle || 'Choose Your Plan') : subScreen === 'businessType' ? t.businessType : subScreen === 'faq' ? (t.faq || 'FAQ') : subScreen === 'userManual' ? (t.userManual || 'User Manual') : subScreen === 'account' ? (language === 'sw' ? 'Akaunti Yangu' : 'My Account') : subScreen === 'privacyMode' ? (language === 'sw' ? 'Hali ya Faragha' : 'Privacy Mode') : subScreen === 'team' ? (language === 'sw' ? 'Timu na Wanachama' : 'Team & Members') : subScreen === 'joinBusiness' ? (language === 'sw' ? 'Jiunge na Biashara' : 'Join a Business') : subScreen === 'myTeam' ? (language === 'sw' ? 'Timu Yangu' : 'My Team') : subScreen === 'permissions' ? (language === 'sw' ? 'Ruhusa za Majukumu' : 'Role Permissions') : '';
          const userSub = subscriptionData || {};
          const subPlanObj = PLANS.find(p => p.id === userSub.plan);
          const subPlan = subPlanObj ? subPlanObj.name : (userSub.plan === 'trial' ? (t.trialPlan || 'Free Trial') : t.trialPlan);
          const subExpiry = userSub.endDate ? new Date(userSub.endDate).toLocaleDateString() : userSub.trialEndsAt ? new Date(userSub.trialEndsAt).toLocaleDateString() : userSub.expiresAt ? new Date(userSub.expiresAt).toLocaleDateString() : '—';
          const subActive = isSubscriptionActive(userSub);
          const subDaysLeft = getDaysRemaining(userSub);
          return (
            <div style={{ minHeight: '100vh', background: 'var(--bg-main)', paddingBottom: 80 }}>
              {/* HEADER */}
              <div style={{ display: 'flex', alignItems: 'center', padding: '20px 20px 8px', gap: 10 }}>
                <button onClick={() => subScreen ? setSettingsScreen(null) : setActiveTab('dashboard')}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px 4px 0', display: 'flex', alignItems: 'center' }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--text-primary)" strokeWidth="2.5" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
                </button>
                <h1 style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-primary)', margin: 0, flex: 1 }}>
                  {subScreen ? subTitle : t.settings}
                </h1>
                {/* Sync status pill + Manual refresh button */}
                {!subScreen && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: 16, background: syncStatus === 'synced' ? '#D1FAE5' : syncStatus === 'syncing' ? '#FEF3C7' : '#F3F4F6', border: `1px solid ${syncStatus === 'synced' ? '#6EE7B7' : syncStatus === 'syncing' ? '#FCD34D' : 'var(--border-default)'}` }}>
                      {syncStatus === 'synced' ? (
                        <>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="3" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                          <span style={{ fontSize: 11, fontWeight: 700, color: '#059669' }}>{t.synced || 'Synced'}</span>
                        </>
                      ) : syncStatus === 'syncing' ? (
                        <>
                          <div style={{ width: 10, height: 10, border: '2px solid #D97706', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                          <span style={{ fontSize: 11, fontWeight: 700, color: '#D97706' }}>{t.syncing || 'Syncing…'}</span>
                        </>
                      ) : (
                        <>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 8 12 12 16 16"/></svg>
                          <span style={{ fontSize: 11, fontWeight: 700, color: '#EF4444' }}>{t.pending || 'Pending'}</span>
                        </>
                      )}
                    </div>
                    <button
                      onClick={() => {
                        setSyncStatus('syncing');
                        fetchAllData(currentUser.uid, currentUser.accessToken).then(cloudData => {
                          if (cloudData) {
                            const s = cloudData.settings;
                            if (s && typeof s === 'object' && !Array.isArray(s)) {
                              if (Array.isArray(s.paymentAccounts)) setPaymentAccounts(prev => mergeById(s.paymentAccounts, prev));
                              // Same LAST-WRITE-WINS guard as login: cloud only
                              // overrides when strictly newer than local choice.
                              if (s.businessType) {
                                const _bp = `faida_${currentUser.uid}_`;
                                const _lts = localStorage.getItem(_bp + 'businessTypeUpdatedAt') || '';
                                const _cts = s.businessTypeUpdatedAt || '';
                                if (!_lts || (_cts && _cts > _lts)) {
                                  setBusinessType(s.businessType);
                                  localStorage.setItem(_bp + 'businessType', s.businessType);
                                  if (_cts) localStorage.setItem(_bp + 'businessTypeUpdatedAt', _cts);
                                }
                              }
                              if (s.currency) setCurrency(s.currency);
                              if (s.businessName) setBusinessName(s.businessName);
                              if (s.businessAddress) setBusinessAddress(s.businessAddress);
                              // Privacy Mode configuration
                              if (typeof s.privacyEnabled === 'boolean') setPrivacyEnabled(s.privacyEnabled);
                              if (typeof s.privacyPinHash === 'string') setPrivacyPinHash(s.privacyPinHash);
                              if (typeof s.privacyRecoveryCodeHash === 'string') setPrivacyRecoveryCodeHash(s.privacyRecoveryCodeHash);
                              if (Array.isArray(s.privacyHiddenTabs)) setPrivacyHiddenTabs(s.privacyHiddenTabs);
                              if (s.privacyHiddenItems && typeof s.privacyHiddenItems === 'object') setPrivacyHiddenItems(s.privacyHiddenItems);
                              if (typeof s.privacyAutoLockMinutes === 'number') setPrivacyAutoLockMinutes(s.privacyAutoLockMinutes);
                            }
                            // TOMBSTONE-SAFE MERGE (C3): manual refresh merges by id
                            // so cross-device edits arrive and local deletions are kept.
                            if (Array.isArray(cloudData.services)) setServices(prev => mergeById(cloudData.services, prev));
                            if (Array.isArray(cloudData.proformaInvoices)) setProformaInvoices(prev => mergeById(cloudData.proformaInvoices, prev));
                            if (Array.isArray(cloudData.issuedInvoices)) setIssuedInvoices(prev => mergeById(cloudData.issuedInvoices, prev));
                            if (Array.isArray(cloudData.manualInvoices)) setManualInvoices(prev => mergeById(cloudData.manualInvoices, prev));
                            if (Array.isArray(cloudData.salesReturns)) setSalesReturns(prev => mergeById(cloudData.salesReturns, prev));
                            if (Array.isArray(cloudData.purchaseReturns)) setPurchaseReturns(prev => mergeById(cloudData.purchaseReturns, prev));
                          }
                          setSyncStatus('synced');
                        }).catch(() => setSyncStatus('pending'));
                      }}
                      style={{ background: '#2563EB', border: 'none', padding: '5px 10px', borderRadius: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round"><path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/></svg>
                      <span style={{ fontSize: 11, fontWeight: 700, color: 'white' }}>{t.refresh || 'Refresh'}</span>
                    </button>
                  </div>
                )}
              </div>

              {/* ── MAIN SETTINGS LIST ── */}
              {!subScreen && (
                <div style={{ padding: '6px 12px', display: 'flex', flexDirection: 'column', gap: 4 }}>

                  {/* ── SECTION: ACCOUNT ── */}
                  <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', color: 'var(--text-secondary)', margin: '6px 2px 2px' }}>{language === 'sw' ? 'Akaunti' : 'Account'}</div>

                  {/* ACCOUNT SETTINGS */}
                  <button onClick={() => setSettingsScreen('account')}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: 'var(--bg-card)', borderRadius: 10, border: '1px solid var(--border-color)', cursor: 'pointer', width: '100%' }}>
                    <div style={{ textAlign: 'left', minWidth: 0, flex: 1 }}>
                      <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 13 }}>{language === 'sw' ? 'Mipangilio ya Akaunti' : 'Account Settings'}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{currentUser?.email || ''}</div>
                    </div>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#CBD5E1" strokeWidth="2.5" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>
                  </button>

                  {/* SUBSCRIPTION STATUS -clickable */}
                  <button onClick={() => setSettingsScreen('subscription')}
                    style={{ background: 'var(--bg-card)', borderRadius: 10, padding: '8px 10px', border: '1px solid var(--border-color)', cursor: 'pointer', width: '100%', textAlign: 'left' }}>
                    <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 13, marginBottom: 6 }}>{t.subscriptionStatus}</div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div>
                        <div style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: 13 }}>{subPlan}</div>
                        <div style={{ color: 'var(--text-secondary)', fontSize: 11, marginTop: 1 }}>
                          {subActive
                            ? `${t.expiresOn}: ${subExpiry} (${subDaysLeft} ${t.daysLeft || 'days left'})`
                            : (t.expiredLabel || 'Expired')
                          }
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ padding: '4px 12px', borderRadius: 16, background: subActive ? '#D1FAE5' : '#FEE2E2', color: subActive ? '#065F46' : '#991B1B', fontWeight: 700, fontSize: 12 }}>
                          {subActive ? t.activePlan : (t.expiredLabel || 'Expired')}
                        </span>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#CBD5E1" strokeWidth="2.5" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>
                      </div>
                    </div>
                  </button>

                  {/* TEAM & MEMBERS -Owner/Admin only */}
                  {(perms.inviteMembers || perms.manageMembers) && (
                  <button onClick={() => setSettingsScreen('team')}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', background: 'var(--bg-card)', borderRadius: 10, border: '1px solid var(--border-color)', cursor: 'pointer', width: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 28, height: 28, borderRadius: 7, background: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                      </div>
                      <div style={{ textAlign: 'left' }}>
                        <div style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: 13 }}>{language === 'sw' ? 'Timu na Wanachama' : 'Team & Members'}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 1 }}>
                          {language === 'sw' ? 'Simamia watumiaji wa biashara' : 'Manage business users'}
                        </div>
                      </div>
                    </div>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#CBD5E1" strokeWidth="2.5" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>
                  </button>
                  )}

                  {/* ROLE PERMISSIONS -Owner only */}
                  {isOwner && (
                  <button onClick={() => setSettingsScreen('permissions')}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', background: 'var(--bg-card)', borderRadius: 10, border: '1px solid var(--border-color)', cursor: 'pointer', width: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 28, height: 28, borderRadius: 7, background: '#FFF7ED', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#EA580C" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                      </div>
                      <div style={{ textAlign: 'left' }}>
                        <div style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: 13 }}>{language === 'sw' ? 'Ruhusa za Majukumu' : 'Role Permissions'}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 1 }}>
                          {language === 'sw' ? 'Dhibiti kila jukumu linachoweza kuona na kufanya' : 'Control what each role can see and do'}
                        </div>
                      </div>
                    </div>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#CBD5E1" strokeWidth="2.5" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>
                  </button>
                  )}

                  {/* JOIN A BUSINESS / JOINED TEAM */}
                  {(() => {
                    const joinedTeam = businesses.find(b => !b.isOwner);
                    if (joinedTeam) {
                      // Already joined a team -clickable, opens detail screen
                      return (
                        <button onClick={() => setSettingsScreen('myTeam')}
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', background: 'var(--bg-card)', borderRadius: 10, border: '1px solid var(--border-color)', cursor: 'pointer', width: '100%' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                            <div style={{ width: 28, height: 28, borderRadius: 7, background: '#F0FDF4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                            </div>
                            <div style={{ textAlign: 'left' }}>
                              <div style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: 13 }}>{joinedTeam.name}</div>
                              <div style={{ fontSize: 11, color: '#059669', marginTop: 1, fontWeight: 500 }}>
                                {language === 'sw' ? 'Umejiunga na timu hii' : 'Joined team'}
                                {joinedTeam.roleName ? ` · ${joinedTeam.roleName}` : ''}
                              </div>
                            </div>
                          </div>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#CBD5E1" strokeWidth="2.5" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>
                        </button>
                      );
                    }
                    // Not yet joined -show join button
                    return (
                      <button onClick={() => { setSettingsScreen('joinBusiness'); setJoinInviteCode(''); setJoinError(''); }}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', background: 'var(--bg-card)', borderRadius: 10, border: '1px solid var(--border-color)', cursor: 'pointer', width: '100%' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ width: 28, height: 28, borderRadius: 7, background: '#F0FDF4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.2" strokeLinecap="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>
                          </div>
                          <div style={{ textAlign: 'left' }}>
                            <div style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: 13 }}>{language === 'sw' ? 'Jiunge na Biashara' : 'Join a Business'}</div>
                            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 1 }}>
                              {language === 'sw' ? 'Tumia kodi ya mwaliko kujiunga' : 'Use an invite code to join a team'}
                            </div>
                          </div>
                        </div>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#CBD5E1" strokeWidth="2.5" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>
                      </button>
                    );
                  })()}

                  {/* ── SECTION: PREFERENCES ── */}
                  <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', color: 'var(--text-secondary)', margin: '10px 2px 2px' }}>{language === 'sw' ? 'Mapendeleo' : 'Preferences'}</div>

                  {/* LANGUAGE (dropdown) */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: 'var(--bg-card)', borderRadius: 10, border: '1px solid var(--border-color)' }}>
                    <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 13 }}>{t.language}</span>
                    <select value={language} onChange={e => setLanguage(e.target.value)}
                      style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border-color)', background: 'var(--bg-input,#F9FAFB)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', cursor: 'pointer', minWidth: 130 }}>
                      <option value="en">{t.english}</option>
                      <option value="sw">{t.swahili}</option>
                    </select>
                  </div>

                  {/* THEME (dropdown) */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: 'var(--bg-card)', borderRadius: 10, border: '1px solid var(--border-color)' }}>
                    <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 13 }}>{t.appearance || 'Theme'}</span>
                    <select value={theme} onChange={e => { setTheme(e.target.value); try { localStorage.setItem('faida_theme', e.target.value); } catch(err) {} }}
                      style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border-color)', background: 'var(--bg-input,#F9FAFB)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', cursor: 'pointer', minWidth: 130 }}>
                      <option value="light">{t.themeVibrant || 'Vibrant'}</option>
                      <option value="dark">{t.themeDark || 'Dark'}</option>
                    </select>
                  </div>

                  {/* FONT SIZE (dropdown) */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: 'var(--bg-card)', borderRadius: 10, border: '1px solid var(--border-color)' }}>
                    <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 13 }}>{t.fontSize}</span>
                    <select value={fontSize} onChange={e => setFontSize(e.target.value)}
                      style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border-color)', background: 'var(--bg-input,#F9FAFB)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', cursor: 'pointer', minWidth: 130 }}>
                      <option value="small">{t.small}</option>
                      <option value="medium">{t.medium}</option>
                      <option value="large">{t.large}</option>
                    </select>
                  </div>

                  {/* ── SECTION: DATA ── */}
                  <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', color: 'var(--text-secondary)', margin: '10px 2px 2px' }}>{language === 'sw' ? 'Data' : 'Data'}</div>

                  {/* EXPORT ALL DATA */}
                  <button onClick={() => exportAllDataToExcel({ products, sales, purchases, expenses, customers, suppliers, expenseCategories, businessName })}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 10px', background: 'var(--bg-card)', borderRadius: 10, border: '1px solid var(--border-color)', cursor: 'pointer', width: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 28, height: 28, borderRadius: 7, background: '#D1FAE5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                      </div>
                      <div style={{ textAlign: 'left' }}>
                        <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 13 }}>{t.exportAllData || 'Export All Data'}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 1 }}>{t.exportAllDataDesc || 'Download as Excel (.xlsx)'}</div>
                      </div>
                    </div>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#CBD5E1" strokeWidth="2.5" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>
                  </button>

                  {/* ── SECTION: SUPPORT ── */}
                  <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', color: 'var(--text-secondary)', margin: '10px 2px 2px' }}>{language === 'sw' ? 'Msaada' : 'Support'}</div>

                  {/* FAQ */}
                  <button onClick={() => setSettingsScreen('faq')}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 12px', background: 'var(--bg-card)', borderRadius: 10, border: '1px solid var(--border-color)', cursor: 'pointer', width: '100%' }}>
                    <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 13 }}>FAQ</span>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#CBD5E1" strokeWidth="2.5" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>
                  </button>

                  {/* USER MANUAL */}
                  <button onClick={() => setSettingsScreen('userManual')}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 12px', background: 'var(--bg-card)', borderRadius: 10, border: '1px solid var(--border-color)', cursor: 'pointer', width: '100%' }}>
                    <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 13 }}>{t.userManual || 'User Manual'}</span>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#CBD5E1" strokeWidth="2.5" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>
                  </button>

                  {/* CONTACT US */}
                  <button onClick={() => setSettingsScreen('contactUs')}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 12px', background: 'var(--bg-card)', borderRadius: 10, border: '1px solid var(--border-color)', cursor: 'pointer', width: '100%' }}>
                    <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 13 }}>{t.contactUs}</span>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#CBD5E1" strokeWidth="2.5" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>
                  </button>

                  {/* PRIVACY POLICY */}
                  <button onClick={() => setSettingsScreen('privacyPolicy')}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 12px', background: 'var(--bg-card)', borderRadius: 10, border: '1px solid var(--border-color)', cursor: 'pointer', width: '100%' }}>
                    <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 13 }}>{t.privacyPolicy}</span>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#CBD5E1" strokeWidth="2.5" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>
                  </button>

                  {/* APP VERSION */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 10px', background: 'var(--bg-card)', borderRadius: 10, border: '1px solid var(--border-color)' }}>
                    <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 13 }}>{t.appVersion}</span>
                    <span style={{ color: 'var(--text-secondary)', fontSize: 12, fontWeight: 500 }}>v1.0.0</span>
                  </div>

                  {/* LOGOUT */}
                  <button onClick={handleLogout}
                    style={{ padding: '10px', borderRadius: 10, background: '#FEE2E2', color: '#DC2626', border: 'none', fontWeight: 700, fontSize: 13, cursor: 'pointer', marginTop: 4, width: '100%' }}>
                    {t.logout}
                  </button>

                  {/* DELETE ACCOUNT */}
                  <button onClick={() => { setDeleteConfirmText(''); setShowDeleteAccountModal(true); }}
                    style={{ padding: '9px', borderRadius: 10, background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--border-default)', fontWeight: 600, fontSize: 12, cursor: 'pointer', width: '100%' }}>
                    {t.deleteAccount || 'Delete Account'}
                  </button>
                </div>
              )}

              {/* ── ACCOUNT SCREEN ── */}
              {subScreen === 'account' && (() => {
                const sw = language === 'sw';
                const email = currentUser?.email || '—';
                const initial = ((currentUser?.displayName || email)[0] || '?').toUpperCase();
                const storedName = (typeof window !== 'undefined' && currentUser) ? (localStorage.getItem(`faida_${currentUser.uid}_displayName`) || currentUser.displayName || '') : '';
                const handleChangePassword = async () => {
                  if (!email || email === '—') return;
                  showConfirm(
                    sw ? `Tutatuma kiungo cha kubadili nywila kwa ${email}. Endelea?` : `We'll send a password-reset link to ${email}. Continue?`,
                    async () => {
                      try {
                        await sendPasswordResetEmail(auth, email);
                        showAlert(sw ? 'Kiungo cha kubadili nywila kimetumwa kwa barua pepe yako.' : 'Password reset link sent to your email.', { tone: 'success' });
                      } catch (e) {
                        showAlert(sw ? 'Kushindwa kutuma kiungo. Jaribu tena.' : 'Failed to send reset link. Try again.', { tone: 'error' });
                      }
                    },
                    { title: sw ? 'Badilisha Nywila' : 'Change Password' }
                  );
                };
                const saveDisplayName = (val) => {
                  if (!currentUser) return;
                  try { localStorage.setItem(`faida_${currentUser.uid}_displayName`, val); } catch {}
                };
                return (
                  <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {/* AVATAR + EMAIL CARD */}
                    <div style={{ background: 'var(--bg-card)', borderRadius: 14, padding: 18, border: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', gap: 14 }}>
                      <div style={{ width: 56, height: 56, borderRadius: 28, background: '#1A4B3A', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 22 }}>
                        {initial}
                      </div>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: 15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{storedName || email.split('@')[0]}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{email}</div>
                      </div>
                    </div>

                    {/* EMAIL (read-only) */}
                    <div style={{ background: 'var(--bg-card)', borderRadius: 12, padding: '12px 14px', border: '1px solid var(--border-color)' }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: 4 }}>{sw ? 'Barua Pepe' : 'Email'}</div>
                      <div style={{ fontSize: 14, color: 'var(--text-primary)', wordBreak: 'break-all' }}>{email}</div>
                    </div>

                    {/* USER ID (read-only, truncated) */}
                    <div style={{ background: 'var(--bg-card)', borderRadius: 12, padding: '12px 14px', border: '1px solid var(--border-color)' }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: 4 }}>{sw ? 'Kitambulisho cha Mtumiaji' : 'User ID'}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'monospace', wordBreak: 'break-all' }}>{currentUser?.uid || '—'}</div>
                    </div>

                    {/* CHANGE PASSWORD */}
                    <button onClick={handleChangePassword}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border-color)', cursor: 'pointer', width: '100%' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1A4B3A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                        <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 14 }}>{sw ? 'Badilisha Nywila' : 'Change Password'}</span>
                      </div>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#CBD5E1" strokeWidth="2.5" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>
                    </button>

                    {/* SIGN OUT */}
                    <button onClick={() => showConfirm(
                      sw ? 'Una uhakika unataka kutoka?' : 'Are you sure you want to sign out?',
                      handleLogout,
                      { title: sw ? 'Toka' : 'Sign Out' }
                    )}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '12px 14px', background: '#FEE2E2', color: '#DC2626', borderRadius: 12, border: 'none', cursor: 'pointer', width: '100%', fontWeight: 700, fontSize: 14 }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                      {sw ? 'Toka' : 'Sign Out'}
                    </button>

                    {/* DELETE ACCOUNT */}
                    <button onClick={() => { setDeleteConfirmText(''); setShowDeleteAccountModal(true); }}
                      style={{ padding: '10px', borderRadius: 10, background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--border-default)', fontWeight: 600, fontSize: 12, cursor: 'pointer', width: '100%' }}>
                      {sw ? 'Futa Akaunti' : 'Delete Account'}
                    </button>
                  </div>
                );
              })()}

              {/* ── PRIVACY MODE SCREEN ── */}
              {subScreen === 'privacyMode' && (() => {
                const sw = language === 'sw';
                const pinIsSet = !!privacyPinHash;

                // Per-tab hideable item definitions. Presented in Privacy Settings screen.
                const tabDefs = [
                  { key: 'dashboard', label: sw ? 'Dashibodi' : 'Dashboard', items: [
                    { key: 'profitCard',      label: sw ? 'Boksi ya Faida / Hasara' : 'Profit / Loss card' },
                    { key: 'bankBalance',     label: sw ? 'Salio la Benki' : 'Bank balances' },
                    { key: 'cashBalance',     label: sw ? 'Salio la Pesa Taslimu' : 'Cash balances' },
                    { key: 'mobileBalance',   label: sw ? 'Salio la Simu' : 'Mobile Money balances' },
                    { key: 'purchasesCard',   label: sw ? 'Boksi ya Manunuzi' : 'Purchases card' },
                    { key: 'cashflowReport',  label: sw ? 'Ripoti ya Mtiririko wa Ki-Fedha' : 'Total Cashflow report' },
                  ]},
                  { key: 'sales',     label: sw ? 'Mauzo' : 'Sales',        items: [] },
                  { key: 'purchases', label: sw ? 'Manunuzi' : 'Purchases',  items: [] },
                  { key: 'services',  label: sw ? 'Huduma' : 'Services', items: [
                    { key: 'profitColumn',    label: sw ? 'Safu ya Faida' : 'Service profit column' },
                  ]},
                  { key: 'inventory', label: sw ? 'Stock' : 'Stock',        items: [] },
                  { key: 'reports', label: sw ? 'Ripoti' : 'Reports', items: [
                    { key: 'plReport',        label: sw ? 'Faida na Hasara' : 'Profit & Loss report' },
                    { key: 'cashflowReports', label: sw ? 'Ripoti za Mtiririko' : 'Cashflow reports' },
                    { key: 'stockValuation',  label: sw ? 'Thamani ya Stock' : 'Stock Valuation' },
                    { key: 'whtVat',          label: sw ? 'WHT / VAT' : 'WHT / VAT reports' },
                  ]},
                  { key: 'expenses', label: sw ? 'Matumizi' : 'Expenses', items: [
                    { key: 'totalCard',       label: sw ? 'Boksi ya Jumla ya Matumizi' : 'Total expenses card' },
                  ]},
                ];

                const isTabHiddenCfg = (tabKey) => Array.isArray(privacyHiddenTabs) && privacyHiddenTabs.includes(tabKey);
                const isItemHiddenCfg = (tabKey, itemKey) => (privacyHiddenItems?.[tabKey] || []).includes(itemKey);

                const toggleTab = (tabKey) => {
                  setPrivacyHiddenTabs(prev => {
                    const cur = Array.isArray(prev) ? prev : [];
                    return cur.includes(tabKey) ? cur.filter(k => k !== tabKey) : [...cur, tabKey];
                  });
                };
                const toggleItem = (tabKey, itemKey) => {
                  setPrivacyHiddenItems(prev => {
                    const cur = { ...(prev || {}) };
                    const list = Array.isArray(cur[tabKey]) ? cur[tabKey] : [];
                    cur[tabKey] = list.includes(itemKey) ? list.filter(k => k !== itemKey) : [...list, itemKey];
                    return cur;
                  });
                };

                const toggleSwitch = (checked, onClick, disabled) => (
                  <button onClick={disabled ? undefined : onClick} disabled={disabled}
                    style={{ width: 44, height: 24, borderRadius: 14, background: checked ? '#166534' : '#CBD5E1', border: 'none', position: 'relative', cursor: disabled ? 'not-allowed' : 'pointer', flexShrink: 0, opacity: disabled ? 0.4 : 1 }}>
                    <span style={{ position: 'absolute', top: 2, left: checked ? 22 : 2, width: 20, height: 20, borderRadius: 10, background: '#fff', transition: 'left 0.15s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                  </button>
                );

                // Progressive disclosure:
                //  Step 1 (always active): Master switch at top
                //  Step 2 (grayed until master ON): PIN section -shows Setup once, then Change/Forgot
                //  Step 3 (grayed until master ON + PIN set): per-tab hide toggles
                const masterOn = privacyEnabled;
                const pinReady = masterOn && pinIsSet;
                const dimmedStyle = (dimmed) => ({ opacity: dimmed ? 0.42 : 1, pointerEvents: dimmed ? 'none' : 'auto', filter: dimmed ? 'grayscale(0.35)' : 'none', transition: 'opacity 0.15s' });

                return (
                  <div style={{ padding: '16px 20px', maxWidth: 640 }}>
                    {/* ── STEP 1 -Master switch (always active) ── */}
                    <div style={{
                      background: masterOn ? 'linear-gradient(180deg, #D1FAE5 0%, #A7F3D0 100%)' : 'var(--bg-white)',
                      border: masterOn ? '2px solid #166534' : '2px solid #CBD5E1',
                      borderRadius: 14, padding: '16px 16px', marginBottom: 16
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={masterOn ? '#065F46' : '#64748B'} strokeWidth="2.4" strokeLinecap="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                            <div style={{ fontSize: 15, fontWeight: 800, color: masterOn ? '#065F46' : 'var(--text-primary)' }}>
                              {sw ? 'Hali ya Faragha' : 'Privacy Mode'}
                            </div>
                          </div>
                          <div style={{ fontSize: 11.5, color: masterOn ? '#065F46' : 'var(--text-secondary)', marginTop: 2, lineHeight: 1.5, fontWeight: masterOn ? 600 : 400 }}>
                            {masterOn
                              ? (pinIsSet
                                  ? (sw ? 'Inatumika. Vitu ulivyoteua hapo chini vinafichwa (kifaa kikiwa kimefungwa).' : 'ACTIVE. Ticked items hide when this device is locked.')
                                  : (sw ? 'Weka PIN yako hapa chini kukamilisha.' : 'Set your PIN below to activate hiding.'))
                              : (sw ? 'Washa ili kuweka PIN na kuchagua unachoficha.' : 'Turn ON to set a PIN and choose what to hide.')}
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            if (!masterOn) {
                              setPrivacyEnabled(true);
                            } else {
                              // Turning OFF: if PIN set, confirm via PIN modal; otherwise just flip
                              if (pinIsSet) setShowPinModal('disable');
                              else setPrivacyEnabled(false);
                            }
                          }}
                          style={{ width: 54, height: 30, borderRadius: 15, background: masterOn ? '#166534' : '#CBD5E1', border: 'none', position: 'relative', cursor: 'pointer', flexShrink: 0 }}>
                          <span style={{ position: 'absolute', top: 3, left: masterOn ? 27 : 3, width: 24, height: 24, borderRadius: 12, background: '#fff', transition: 'left 0.18s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                        </button>
                      </div>

                      {/* Device lock status + Lock Now button -only meaningful once PIN is set */}
                      {masterOn && pinIsSet && (
                        <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid #166534', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {privacyUnlocked ? (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#B45309" strokeWidth="2.5" strokeLinecap="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 9.9-1"/></svg>
                            ) : (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#065F46" strokeWidth="2.5" strokeLinecap="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                            )}
                            <span style={{ fontSize: 12.5, fontWeight: 700, color: privacyUnlocked ? '#B45309' : '#065F46' }}>
                              {privacyUnlocked
                                ? (sw ? 'Kifaa hiki KIMEFUNGULIWA -vitu vyote vinaonekana' : 'This device is UNLOCKED -items are visible')
                                : (sw ? 'Kifaa hiki KIMEFUNGWA -vitu vimefichwa' : 'This device is LOCKED -items are hidden')}
                            </span>
                          </div>
                          {privacyUnlocked ? (
                            <button onClick={() => setPrivacyUnlocked(false)}
                              style={{ padding: '8px 14px', borderRadius: 8, background: '#18222F', color: '#A3E635', border: 'none', fontWeight: 800, fontSize: 12, cursor: 'pointer' }}>
                              {sw ? 'Funga sasa' : 'Lock now'}
                            </button>
                          ) : (
                            <button onClick={() => setShowPinModal('unlock')}
                              style={{ padding: '8px 14px', borderRadius: 8, background: 'var(--bg-white)', color: '#065F46', border: '1px solid #166534', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                              {sw ? 'Fungua kuona' : 'Unlock to view'}
                            </button>
                          )}
                        </div>
                      )}

                      {/* Auto-lock selector -active only when master ON */}
                      <div style={{ ...dimmedStyle(!masterOn), marginTop: 14, paddingTop: 12, borderTop: `1px solid ${masterOn ? '#166534' : 'var(--border-color)'}` }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: masterOn ? '#065F46' : 'var(--text-primary)', marginBottom: 4 }}>{sw ? 'Baada ya kufungua, ficha tena baada ya' : 'After unlocking, re-hide after'}</div>
                        <div style={{ fontSize: 11, color: masterOn ? '#065F46' : 'var(--text-secondary)', marginBottom: 8, lineHeight: 1.5, opacity: 0.85 }}>
                          {sw
                            ? 'Ukiingiza PIN kuona vitu vilivyofichwa, vitaonekana kwa muda huu tu, kisha vitafichwa tena kiotomatiki.'
                            : 'When you enter your PIN to view hidden items, they stay visible for this long, then auto-hide again.'}
                        </div>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          {[[5, sw?'Dakika 5':'5 min'], [15, sw?'Dakika 15':'15 min'], [30, sw?'Dakika 30':'30 min'], [-1, sw?'Kamwe':'Never']].map(([mins, label]) => (
                            <button key={mins} onClick={() => setPrivacyAutoLockMinutes(mins)}
                              style={{ padding: '7px 14px', borderRadius: 20, border: 'none', fontWeight: 700, fontSize: 12, cursor: 'pointer',
                                background: privacyAutoLockMinutes === mins ? '#18222F' : (masterOn ? '#FFFFFF' : '#F1F5F9'),
                                color:      privacyAutoLockMinutes === mins ? '#fff' : '#334155' }}>
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* ── STEP 2 -PIN section (grayed until master ON) ── */}
                    <div style={dimmedStyle(!masterOn)}>
                      {!pinIsSet ? (
                        /* First-time setup -one-time UI */
                        <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 14, padding: '18px 18px', marginBottom: 16 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#166534" strokeWidth="2.2" strokeLinecap="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: '#166534' }}>{sw ? 'Weka PIN yako ya Faragha' : 'Set your Privacy PIN'}</h3>
                          </div>
                          <p style={{ margin: '0 0 12px', fontSize: 13, color: '#166534', lineHeight: 1.55 }}>
                            {sw
                              ? 'PIN ya tarakimu 4 utakayoihitaji kufungua vitu vilivyofichwa. Utaipata mara moja tu.'
                              : 'A 4-digit code you\'ll need to unlock hidden items. You only set this once.'}
                          </p>
                          <button onClick={() => setShowPinModal('setup')}
                            style={{ padding: '11px 18px', borderRadius: 10, background: '#166534', color: '#fff', border: 'none', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                            {sw ? 'Weka PIN sasa' : 'Set PIN now'}
                          </button>
                        </div>
                      ) : (
                        /* After PIN is set -show Change PIN / New recovery code / Forgot PIN */
                        <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 14, padding: '14px 16px', marginBottom: 16 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#166534" strokeWidth="2.2" strokeLinecap="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                            <div style={{ fontSize: 13, fontWeight: 700, color: '#166534' }}>{sw ? 'PIN ya Faragha imewekwa' : 'Privacy PIN is set'}</div>
                          </div>
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            <button onClick={() => setShowPinModal('change')} style={{ padding: '8px 14px', borderRadius: 8, background: '#fff', color: '#166534', border: '1px solid #166534', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>{sw ? 'Badilisha PIN' : 'Change PIN'}</button>
                            <button onClick={() => setShowPinModal('regenCode')} style={{ padding: '8px 14px', borderRadius: 8, background: '#fff', color: '#166534', border: '1px solid #166534', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>{sw ? 'Pata msimbo mpya wa uokoaji' : 'Get new recovery code'}</button>
                            <button onClick={() => setShowPinModal('recover')} style={{ padding: '8px 14px', borderRadius: 8, background: 'transparent', color: '#4F46E5', border: 'none', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>{sw ? 'Sahau PIN?' : 'Forgot PIN?'}</button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* ── STEP 3 -Per-tab hide config (grayed until master ON + PIN set) ── */}
                    <div style={dimmedStyle(!pinReady)}>
                      <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.6, color: 'var(--text-secondary)', fontWeight: 700, marginBottom: 10 }}>
                        {sw ? 'Chagua unachoficha' : 'Choose what to hide'}
                      </div>
                      {tabDefs.map(tab => {
                        const tabHidden = isTabHiddenCfg(tab.key);
                        return (
                          <div key={tab.key} style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: 12, padding: '14px 14px', marginBottom: 12 }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                              <div style={{ minWidth: 0 }}>
                                <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--text-primary)' }}>{tab.label}</div>
                                <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>{sw ? 'Ficha kichupo kizima' : 'Hide the entire tab'}</div>
                              </div>
                              {toggleSwitch(tabHidden, () => toggleTab(tab.key))}
                            </div>
                            {!tabHidden && tab.items.length > 0 && (
                              <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px dashed var(--border-color)' }}>
                                <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 6, fontStyle: 'italic' }}>
                                  {sw ? 'Au ficha vitu vichache ndani' : 'Or hide specific items inside'}
                                </div>
                                {tab.items.map(item => {
                                  const checked = isItemHiddenCfg(tab.key, item.key);
                                  return (
                                    <div key={item.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 0', gap: 10 }}>
                                      <span style={{ fontSize: 13, color: 'var(--text-primary)' }}>{item.label}</span>
                                      {toggleSwitch(checked, () => toggleItem(tab.key, item.key))}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              {/* ── BUSINESS TYPE SCREEN ── */}
              {subScreen === 'businessType' && (
                <div style={{ padding: '16px 20px' }}>
                  <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 16, marginTop: 0 }}>{t.selectBusinessType}</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {[
                      { val: 'service_only', label: t.serviceOnly, desc: t.serviceOnlyDesc, icon: '⚒️' },
                      { val: 'products_only', label: t.productsOnly, desc: t.productsOnlyDesc, icon: '▣' },
                      { val: 'products_and_services', label: t.productsAndServices, desc: t.productsAndServicesDesc, icon: '▥' },
                    ].map(opt => (
                      <button key={opt.val} onClick={() => {
                        if (opt.val === businessType) return;
                        let risk = 'safe';
                        if ((businessType === 'products_and_services' && opt.val === 'service_only') ||
                            (businessType === 'products_only' && opt.val === 'service_only') ||
                            (businessType === 'service_only' && opt.val === 'products_only')) risk = 'high';
                        else if (businessType === 'products_and_services' && opt.val === 'products_only') risk = 'medium';
                        if (risk === 'safe') {
                          const p = currentUser ? `faida_${currentUser.uid}_` : 'faida_';
                          setBusinessType(opt.val);
                          localStorage.setItem(p + 'businessType', opt.val);
                          localStorage.setItem(p + 'businessTypeUpdatedAt', new Date().toISOString());
                        } else {
                          setBusinessTypeTransitionWarning({ newType: opt.val, newLabel: opt.label, risk });
                        }
                      }}
                        style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 18px', background: 'var(--bg-card)', borderRadius: 16, border: `2px solid ${businessType === opt.val ? '#A3E635' : 'var(--border-color)'}`, cursor: 'pointer', width: '100%', textAlign: 'left' }}>
                        <span style={{ fontSize: 28 }}>{opt.icon}</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: 15 }}>{opt.label}</div>
                          <div style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 2 }}>{opt.desc}</div>
                        </div>
                        {businessType === opt.val && (
                          <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" fill="#A3E635"/><polyline points="9 12 11 14 15 10" stroke="#111827" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* ── SUBSCRIPTION PLANS SCREEN ── */}
              {subScreen === 'subscription' && (
                <div style={{ padding: '8px 14px', display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 1040, margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>
                  {/* Current Subscription Summary */}
                  <div style={{ background: subActive ? (theme === 'dark' ? '#0B2E23' : '#ECFDF5') : 'var(--bg-card)', borderRadius: 10, padding: '14px 16px', border: subActive ? '1.5px solid #059669' : '1px solid var(--border-color)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                      <div style={{ fontWeight: 600, fontSize: 15, color: 'var(--text-primary)' }}>{subPlan}</div>
                      <span style={{ padding: '2px 10px', borderRadius: 4, background: subActive ? '#059669' : 'transparent', border: subActive ? 'none' : '1px solid #C99A9A', color: subActive ? '#FFFFFF' : (theme === 'dark' ? '#D8A7A7' : '#8C4646'), fontWeight: 600, fontSize: 11 }}>
                        {subActive ? t.activePlan : (t.expiredLabel || 'Expired')}
                      </span>
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                      {subActive
                        ? `${t.expiresOn}: ${subExpiry} · ${subDaysLeft} ${t.daysLeft || 'days left'}`
                        : (t.subExpiredTitle || 'Your subscription has expired')
                      }
                    </div>
                  </div>

                  {/* Section heading */}
                  <div style={{ fontWeight: 600, fontSize: 12, color: 'var(--text-secondary)', marginTop: 6, letterSpacing: 0.8, textTransform: 'uppercase' }}>{subActive ? (t.upgradeOrRenew || 'Upgrade or Renew') : (t.choosePlan || 'Choose Your Plan')}</div>

                  {/* Plan Cards */}
                  {PLANS.map(plan => {
                    const isCurrent = userSub.plan === plan.id && subActive;
                    const emerald = '#059669';
                    const badgeTint = plan.badge === 'Most Popular'
                      ? (theme === 'dark' ? { bg: '#3A2E10', fg: '#FACC15' } : { bg: '#FEF3C7', fg: '#92400E' })
                      : plan.badge === 'Best Value'
                      ? (theme === 'dark' ? { bg: '#12283F', fg: '#7CB2E8' } : { bg: '#DBEAFE', fg: '#1E40AF' })
                      : (theme === 'dark' ? { bg: '#2A2140', fg: '#C4B5FD' } : { bg: '#EDE9FE', fg: '#5B21B6' });
                    const emeraldText = theme === 'dark' ? '#34D399' : '#047857';
                    const priceBlue = theme === 'dark' ? '#7CB2E8' : '#185FA5';
                    return (
                      <div key={plan.id} style={{
                        background: isCurrent ? (theme === 'dark' ? '#0B2E23' : '#ECFDF5') : 'var(--bg-card)',
                        borderRadius: 10,
                        padding: '16px 18px',
                        border: isCurrent ? `1.5px solid ${emerald}` : '1px solid var(--border-color)',
                        display: 'flex', flexDirection: 'column',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontWeight: 600, fontSize: 15, color: 'var(--text-primary)' }}>{plan.name}</span>
                            {plan.badge && (
                              <span style={{ padding: '2px 9px', borderRadius: 4, fontSize: 10, fontWeight: 600, letterSpacing: 0.3, background: badgeTint.bg, color: badgeTint.fg, whiteSpace: 'nowrap' }}>{plan.badge}</span>
                            )}
                          </div>
                          {isCurrent ? (
                            <span style={{ padding: '2px 10px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: emerald, color: '#FFFFFF', whiteSpace: 'nowrap' }}>{t.currentPlanBadge || 'Current Plan'}</span>
                          ) : (
                            <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{plan.duration}</span>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                          {plan.oldPrice && plan.oldPrice > plan.price && (
                            <span style={{ fontSize: 13, color: theme === 'dark' ? '#F09595' : '#B91C1C', textDecoration: 'line-through', textDecorationColor: '#DC2626' }}>{plan.currency} {Number(plan.oldPrice).toLocaleString()}</span>
                          )}
                          <span style={{ fontWeight: 700, fontSize: 17, color: priceBlue }}>{plan.currency} {Number(plan.price).toLocaleString()}</span>
                          {plan.oldPrice && plan.oldPrice > plan.price && (
                            <span style={{ padding: '1px 7px', borderRadius: 4, fontSize: 10, fontWeight: 600, background: theme === 'dark' ? '#0B2E23' : '#ECFDF5', color: theme === 'dark' ? '#34D399' : '#047857', border: `1px solid ${theme === 'dark' ? '#065F46' : '#A7F3D0'}` }}>
                              {(language === 'sw' ? 'Okoa ' : 'Save ') + Math.round((1 - plan.price / plan.oldPrice) * 100) + '%'}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: isCurrent ? 0 : 12, display: 'flex', alignItems: 'center', gap: 5 }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                          {t.accessAllFeatures || 'Access to all features'}
                        </div>
                        {isCurrent ? (
                          <div style={{ fontSize: 12, color: emeraldText, marginTop: 8 }}>
                            {plan.duration} · {t.expiresOn}: {subExpiry} · {subDaysLeft} {t.daysLeft || 'days left'}
                          </div>
                        ) : (
                          <button onClick={() => handleStartPayment(plan.id)}
                            style={{ width: '100%', padding: '10px 0', borderRadius: 8, background: 'transparent', color: emeraldText, fontWeight: 600, fontSize: 13.5, border: `1.5px solid ${emerald}`, cursor: 'pointer', letterSpacing: 0.2, marginTop: 'auto' }}>
                            {t.selectThisPlan || 'Select This Plan'}
                          </button>
                        )}
                      </div>
                    );
                  })}

                  {/* Payment Modal */}
                  {paymentFlow && (
                    <div style={{ position: 'fixed', inset: 0, zIndex: 1400, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
                      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)' }} onClick={paymentFlow.step === 'phone' || paymentFlow.step === 'failed' ? handleCancelPayment : undefined} />
                      <div style={{ position: 'relative', zIndex: 1, background: 'var(--bg-white)', borderTopLeftRadius: 24, borderTopRightRadius: 24, padding: '24px 20px 32px', width: '100%', maxWidth: 480 }}>
                        <div style={{ width: 40, height: 4, borderRadius: 2, background: '#D1D5DB', margin: '0 auto 20px' }} />

                        {paymentFlow.step === 'phone' && (() => {
                          const plan = PLANS.find(p => p.id === paymentFlow.planId);
                          return (
                            <div>
                              <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 4 }}>{t.payForPlan || 'Pay for'} {plan?.name}</div>
                              <div style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 20 }}>{plan?.currency} {Number(plan?.price).toLocaleString()} · {plan?.duration}</div>
                              <label style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 6, display: 'block' }}>{t.mobileMoneyPhone || 'Mobile Money Number'}</label>
                              <input type="tel" inputMode="numeric" value={paymentFlow.phone}
                                onChange={(e) => setPaymentFlow(prev => ({ ...prev, phone: e.target.value, error: '' }))}
                                placeholder="07XXXXXXXX"
                                style={{ width: '100%', padding: '10px 14px', borderRadius: 12, border: '2px solid var(--border-default)', fontSize: 16, boxSizing: 'border-box', outline: 'none', marginBottom: 8 }} />
                              {paymentFlow.error && <div style={{ color: '#EF4444', fontSize: 13, fontWeight: 600, marginBottom: 8 }}>{paymentFlow.error}</div>}
                              <button onClick={handleSubmitPayment}
                                style={{ width: '100%', padding: 15, borderRadius: 14, background: '#111827', color: '#A3E635', border: 'none', fontWeight: 800, fontSize: 15, cursor: 'pointer', marginTop: 10 }}>
                                {t.payNow || 'Pay Now'}
                              </button>
                              <button onClick={handleCancelPayment}
                                style={{ width: '100%', padding: 12, borderRadius: 14, background: 'transparent', color: 'var(--text-muted)', border: 'none', fontWeight: 700, fontSize: 14, cursor: 'pointer', marginTop: 4 }}>
                                {t.cancel}
                              </button>
                            </div>
                          );
                        })()}

                        {['processing', 'waiting', 'verifying'].includes(paymentFlow.step) && (
                          <div style={{ textAlign: 'center', padding: '20px 0' }}>
                            <div style={{ width: 56, height: 46, borderRadius: '50%', border: '4px solid var(--border-default)', borderTopColor: '#111827', margin: '0 auto 20px', animation: 'spin 1s linear infinite' }} />
                            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                            <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 6 }}>
                              {paymentFlow.step === 'processing' ? (t.creatingPayment || 'Creating payment...') :
                               paymentFlow.step === 'waiting' ? (t.waitingForPayment || 'Waiting for payment...') :
                               (t.verifyingPayment || 'Verifying payment...')}
                            </div>
                            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                              {paymentFlow.step === 'waiting' ? (t.checkYourPhone || 'Check your phone for the payment prompt') : (t.pleaseWait || 'Please wait')}
                            </div>
                          </div>
                        )}

                        {paymentFlow.step === 'success' && (
                          <div style={{ textAlign: 'center', padding: '20px 0' }}>
                            <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#D1FAE5', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="3" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                            </div>
                            <div style={{ fontSize: 18, fontWeight: 800, color: '#059669', marginBottom: 6 }}>{t.paymentSuccess || 'Payment Successful!'}</div>
                            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{t.subscriptionActivated || 'Your subscription has been activated.'}</div>
                          </div>
                        )}

                        {paymentFlow.step === 'failed' && (
                          <div style={{ textAlign: 'center', padding: '20px 0' }}>
                            <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#FEE2E2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                              <span style={{ fontSize: 32 }}>×</span>
                            </div>
                            <div style={{ fontSize: 18, fontWeight: 800, color: '#DC2626', marginBottom: 6 }}>{t.paymentFailed || 'Payment Failed'}</div>
                            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>{paymentFlow.error}</div>
                            <button onClick={() => setPaymentFlow(prev => ({ ...prev, step: 'phone', error: '' }))}
                              style={{ width: '100%', padding: 14, borderRadius: 14, background: '#111827', color: '#A3E635', border: 'none', fontWeight: 800, fontSize: 15, cursor: 'pointer' }}>
                              {t.tryAgain || 'Try Again'}
                            </button>
                            <button onClick={handleCancelPayment}
                              style={{ width: '100%', padding: 12, borderRadius: 14, background: 'transparent', color: 'var(--text-muted)', border: 'none', fontWeight: 700, fontSize: 14, cursor: 'pointer', marginTop: 4 }}>
                              {t.cancel}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ── PRIVACY POLICY SCREEN ── */}
              {subScreen === 'privacyPolicy' && (
                <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {/* Header badge */}
                  <div style={{ textAlign: 'center', padding: '20px 0 4px' }}>
                    <div style={{ fontSize: 40, marginBottom: 8 }}>▪</div>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 600 }}>Last updated: May 2025 · Hesabu Solutions</div>
                  </div>

                  {/* Terms of Service card */}
                  {[
                    {
                      title: 'TERMS OF SERVICE',
                      sections: [
                        { h: '1. Acceptance of Terms', p: 'By creating an account on Biashara Track, you agree to these Terms of Service and our Privacy Policy. If you do not agree, please do not use the app.' },
                        { h: '2. Use of the Service', p: 'Biashara Track is a business management tool for tracking sales, purchases, expenses, and inventory. You are responsible for the accuracy of data you enter and for keeping your login credentials secure. You must not use Biashara Track for unlawful purposes or to store fraudulent data.' },
                        { h: '3. Cloud Data Storage', p: 'Your business data is stored both on your device (for offline access) and on secure cloud servers provided by Supabase (supabase.com). By using Biashara Track, you consent to this cloud storage. Your data is protected by Row Level Security -only you can access your own data.' },
                        { h: '4. Subscriptions & Payments', p: 'Biashara Track offers subscription plans billed in advance. Payments are processed securely via ClickPesa. Subscriptions are non-refundable except where required by law. You may cancel at any time; access continues until the end of the billing period.' },
                        { h: '5. Intellectual Property', p: 'The Biashara Track app, its design, and all content (excluding your business data) are owned by Hesabu Solutions. You may not copy, modify, or distribute any part of the app without written permission.' },
                        { h: '6. Limitation of Liability', p: 'Biashara Track is provided "as is" without warranty. We are not liable for data loss, financial loss, or business decisions made using the app. Always verify records independently and keep backups.' },
                        { h: '7. Account Termination', p: 'You may delete your account at any time in Settings. Cloud data is permanently erased within 30 days of deletion. We may suspend accounts that violate these terms.' },
                        { h: '8. Changes to Terms', p: 'We may update these terms from time to time. Significant changes will be communicated via in-app notice. Continued use after changes constitutes acceptance.' },
                      ]
                    },
                    {
                      title: 'PRIVACY POLICY',
                      sections: [
                        { h: '1. Information We Collect', p: 'We collect: your email address and business name (at registration); business data you enter (sales, purchases, expenses, products, customers, suppliers); and basic usage data to improve app performance.' },
                        { h: '2. How Your Data Is Stored', p: 'Data is stored locally on your device AND on Supabase cloud servers (AES-256 encrypted). This enables offline access, automatic backup, and multi-device sync. Only you can access your data.' },
                        { h: '3. Third-Party Services', p: 'Supabase (supabase.com) -cloud database & authentication. ClickPesa -payment processing. Both providers are contractually required to protect your data and have their own privacy policies.' },
                        { h: '4. Your Rights', p: 'You can access, export, correct, or delete your data at any time through Settings. Use "Delete Account" in Settings for complete data removal.' },
                        { h: '5. Data Sharing', p: 'We do not sell, rent, or share your personal or business data with any third parties for commercial purposes. Data may be disclosed only if required by applicable law.' },
                        { h: '6. Data Retention', p: 'Your data is retained as long as your account is active. When you delete your account, all cloud data is permanently deleted within 30 days.' },
                        { h: '7. Security', p: 'We use encryption in transit (HTTPS/TLS) and at rest (AES-256 via Supabase). However, no system is 100% secure -please keep your login credentials private.' },
                        { h: '8. Contact', p: 'For privacy questions or data requests: hesabusolutionstz@gmail.com · +255 680 072 640' },
                      ]
                    }
                  ].map(doc => (
                    <div key={doc.title} style={{ background: 'var(--bg-card)', borderRadius: 16, padding: 20, border: '1px solid var(--border-color)' }}>
                      <p style={{ fontWeight: 800, fontSize: 14, color: '#0A3D2E', margin: '0 0 14px', paddingBottom: 8, borderBottom: '2px solid #0A3D2E' }}>{doc.title}</p>
                      {doc.sections.map((s, i) => (
                        <div key={i} style={{ marginBottom: 14 }}>
                          <p style={{ fontWeight: 700, fontSize: 13, color: 'var(--text-primary)', margin: '0 0 4px' }}>{s.h}</p>
                          <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.7, margin: 0 }}>{s.p}</p>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}

              {/* ── CONTACT US SCREEN ── */}
              {subScreen === 'contactUs' && (
                <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {[
                    { icon: '✉️', label: t.contactEmailLabel, val: t.contactEmail },
                    { icon: '☎', label: t.contactPhoneLabel, val: t.contactPhone },
                  ].map(item => (
                    <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 18px', background: 'var(--bg-card)', borderRadius: 16, border: '1px solid var(--border-color)' }}>
                      <span style={{ fontSize: 24 }}>{item.icon}</span>
                      <div>
                        <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 14 }}>{item.label}</div>
                        <div style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 2 }}>{item.val}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* ── FAQ SCREEN ── */}
              {subScreen === 'faq' && (() => {
                const sw = language === 'sw';
                const faqs = [
                  { cat: sw ? 'Mauzo' : 'Sales', catColor: '#D1FAE5', catText: '#065F46', icon: '↑',
                    q: sw ? 'Ninawezaje kurekodi mauzo?' : 'How do I record a sale?',
                    a: sw ? 'Nenda kichupo cha Mauzo na bonyeza kitufe cha kijani "+ Rekodi Mauzo". Chagua bidhaa au huduma, weka idadi na bei, chagua njia ya malipo, na bonyeza Hifadhi. Invoisi ya PDF inatengenezwa moja kwa moja.'
                         : 'Go to the Sales tab and tap the green "+ Record Sale" button. Select the product or service, enter the quantity and price, choose the payment method, and tap Save. A PDF invoice is automatically generated.' },
                  { cat: sw ? 'Mauzo' : 'Sales', catColor: '#D1FAE5', catText: '#065F46', icon: '↑',
                    q: sw ? 'Ninawezaje kurekodi mauzo ya mkopo (mteja analipa baadaye)?' : 'How do I record a credit sale (customer pays later)?',
                    a: sw ? 'Unapoandika mauzo, chagua "Mkopo" au acha kiasi cha malipo kuwa 0. Mauzo yanahifadhiwa kama Haijalipiwa na yanaonekana katika Madeni. Mteja anapolipa, bonyeza kitufe cha "+ Lipa" kwenye mauzo hayo.'
                         : 'When recording a sale, choose "Credit" or leave the payment amount at 0. The sale is saved as Unpaid and appears in your Receivables. When the customer pays, tap the "+ Pay" button on that sale to record the payment.' },
                  { cat: sw ? 'Mauzo' : 'Sales', catColor: '#D1FAE5', catText: '#065F46', icon: '↑',
                    q: sw ? 'Ninawezaje kushughulikia marejesho ya mauzo au kurudisha pesa?' : 'How do I handle a sales return or refund?',
                    a: sw ? 'Nenda kichupo cha Mauzo na bonyeza "↩ Marejesho ya Mauzo". Chagua mauzo ya awali, weka idadi inayorudishwa na sababu. Biashara Track itarekodi marejesho na kurejesha stok moja kwa moja.'
                         : 'Go to the Sales tab and tap "↩ Sales Returns". Select the original sale, enter the quantity being returned and the reason. Biashara Track will record the return and restore the stock automatically.' },
                  { cat: sw ? 'Invoisi' : 'Invoices', catColor: '#FEF3C7', catText: '#92400E', icon: '▱',
                    q: sw ? 'Invoisi ya Proforma ni nini?' : 'What is a Proforma Invoice?',
                    a: sw ? 'Invoisi ya Proforma ni kisio au makisio yanayotumwa kwa mteja kabla mauzo hayajathibitishwa. Si invoisi ya kodi. Nenda Mauzo → Invoisi ya Proforma kuunda moja. Mteja akithibitisha, unaweza kuibadilisha kuwa mauzo halisi.'
                         : 'A Proforma Invoice is a quote or estimate sent to a customer before the actual sale is confirmed. It is not a tax invoice. Go to Sales → Proforma Invoice to create one. Once the customer confirms, you can convert it to a real sale.' },
                  { cat: sw ? 'Invoisi' : 'Invoices', catColor: '#FEF3C7', catText: '#92400E', icon: '▱',
                    q: sw ? 'Ninawezaje kuongeza kodi kama VAT kwenye mauzo?' : 'How do I add taxes like VAT to a sale?',
                    a: sw ? 'Unapoandika mauzo, tembeza chini hadi sehemu ya "Kodi" na bonyeza "+ Ongeza Kodi". Chagua VAT (18%) au Kodi ya Kuzuia na weka kiwango. Kodi inakokotolewa moja kwa moja na kuongezwa kwenye jumla ya invoisi.'
                         : 'When recording a sale, scroll down to the "Taxes" section and tap "+ Add Tax". Choose VAT (18%) or Withholding Tax and enter the rate. The tax is calculated automatically and added to the invoice total.' },
                  { cat: sw ? 'Invoisi' : 'Invoices', catColor: '#FEF3C7', catText: '#92400E', icon: '▱',
                    q: sw ? '"Nambari ya Risiti ya EFD" inamaanisha nini?' : 'What does "EFD Receipt No." mean?',
                    a: sw ? 'EFD inamaanisha Electronic Fiscal Device -mashine inayohitajika na serikali kwa biashara zilizosajiliwa kwa VAT Tanzania. Biashara yako ikitumia EFD, weka nambari ya risiti inayochapishwa kwa kila muamala ili ionekane kwenye invoisi yako ya Biashara Track kwa utiifu.'
                         : 'EFD stands for Electronic Fiscal Device -a government-required machine for VAT-registered businesses in Tanzania. If your business uses an EFD, enter the receipt number it prints for each transaction so it appears on your Biashara Track invoice for compliance.' },
                  { cat: sw ? 'Stok & Bidhaa' : 'Stock & Products', catColor: '#E0E7FF', catText: '#3730A3', icon: '▣',
                    q: sw ? 'Ninawezaje kuongeza bidhaa kwenye hesabu yangu?' : 'How do I add products to my inventory?',
                    a: sw ? 'Nenda Mipangilio (ikoni ya gia) → Bidhaa. Bonyeza "+ Ongeza Mpya" na jaza jina la bidhaa, gharama ya kununua, bei ya kuuza, idadi ya stok, na maelezo mengine. Bonyeza Hifadhi kuiongeza kwenye hesabu yako.'
                         : 'Go to Setup (the gear icon) → Products. Tap "+ Add New" and fill in the product name, buying cost, selling price, quantity in stock, and any other details. Tap Save to add it to your inventory.' },
                  { cat: sw ? 'Stok & Bidhaa' : 'Stock & Products', catColor: '#E0E7FF', catText: '#3730A3', icon: '▣',
                    q: sw ? 'Ufuatiliaji wa stok unafanya kazije?' : 'How does stock tracking work?',
                    a: sw ? 'Kila unaporekodi mauzo, idadi ya stok ya bidhaa hiyo hupungua moja kwa moja. Kila unaponunua, stok huongezeka. Unaweza pia kufanya marekebisho ya stok kwa mkono kutoka kichupo cha Mauzo → kitufe cha Marekebisho ya Stok.'
                         : 'Every time you record a sale, the stock quantity for that product automatically decreases. Every time you record a purchase, stock increases. You can also do manual stock adjustments from the Sales tab → Stock Adjustment button.' },
                  { cat: sw ? 'Salio za Akaunti' : 'Account Balances', catColor: '#E2E8F0', catText: '#334155', icon: '$',
                    q: sw ? 'Tofauti kati ya Taslimu, Benki, na Pesa ya Simu ni nini?' : 'What is the difference between Cash, Bank, and Mobile Money balances?',
                    a: sw ? 'Biashara Track inafuatilia pesa yako katika akaunti tatu: Taslimu (pesa halisi), Benki (akaunti ya benki), na Pesa ya Simu (M-Pesa, Tigopesa, n.k.). Unapoandika mauzo au matumizi, unachagua akaunti iliyotumiwa, na Biashara Track inasasisha salio hilo.'
                         : 'Biashara Track tracks your money across three accounts: Cash (physical money), Bank (bank account), and Mobile Money (M-Pesa, Tigopesa, etc.). When you record a sale or expense, you choose which account was used, and Biashara Track updates that balance.' },
                  { cat: sw ? 'Programu' : 'App', catColor: '#ECFDF5', catText: '#065F46', icon: '✻️',
                    q: sw ? 'Je, ninaweza kutumia Biashara Track bila mtandao?' : 'Can I use Biashara Track offline?',
                    a: sw ? 'Ndiyo! Biashara Track inafanya kazi kikamilifu bila mtandao. Data yako yote inahifadhiwa kwenye kifaa chako. Unapounganika tena na mtandao, Biashara Track inasawazisha kila kitu kwenye wingu moja kwa moja.'
                         : 'Yes! Biashara Track works fully offline. All your data is saved locally on your device. When you reconnect to the internet, Biashara Track automatically syncs everything to the cloud so your data is backed up and available across devices.' },
                  { cat: sw ? 'Programu' : 'App', catColor: '#ECFDF5', catText: '#065F46', icon: '✻️',
                    q: sw ? 'Ninawezaje kubadilisha aina ya biashara yangu?' : 'How do I change my business type?',
                    a: sw ? 'Nenda Mipangilio → Aina ya Biashara. Unaweza kubadilisha kati ya Bidhaa Tu, Huduma Tu, na Bidhaa & Huduma. Kumbuka: kubadilisha kunaweza kuficha data fulani kwa muda, lakini hakuna kinachofutwa -unaweza kurudi wakati wowote.'
                         : 'Go to Settings → Business Type. You can switch between Products Only, Services Only, and Products & Services. Note: switching may temporarily hide some data, but nothing is deleted -you can switch back anytime.' },
                  { cat: sw ? 'Programu' : 'App', catColor: '#ECFDF5', catText: '#065F46', icon: '✻️',
                    q: sw ? 'Ninawezaje kuongeza TIN au VRN kwenye invoisi?' : 'How do I add my TIN or VRN to invoices?',
                    a: sw ? 'Nenda Mipangilio (ikoni ya gia) → Uzingatiaji na Kodi. Weka TIN (Nambari ya Utambulisho wa Kodi), VRN (Nambari ya Usajili wa VAT), Nambari ya Usajili wa Biashara, au Nambari ya Leseni ya Biashara. Hizi zitaonekana moja kwa moja kwenye invoisi zako zote.'
                         : 'Go to Setup (gear icon) → Compliance & Tax. Enter your TIN (Tax Identification Number), VRN (VAT Registration Number), Business Registration Number, or Trade License Number. These will automatically appear on all your invoices.' },
                  { cat: sw ? 'Programu' : 'App', catColor: '#ECFDF5', catText: '#065F46', icon: '✻️',
                    q: sw ? 'Ninawezaje kuhamisha data yangu?' : 'How do I export my data?',
                    a: sw ? 'Nenda Mipangilio na bonyeza "Hamisha Data Yote". Hii inapakua faili ya Excel yenye mauzo yako yote, manunuzi, na matumizi kwa rekodi zako au kwa mhasibu wako.'
                         : 'Go to Settings and tap "Export All Data". This downloads an Excel file with all your sales, purchases, and expenses for your records or for your accountant.' },
                  { cat: sw ? 'Usalama' : 'Security', catColor: '#FEF2F2', catText: '#991B1B', icon: '▪',
                    q: sw ? 'Je, data yangu iko salama?' : 'Is my data secure?',
                    a: sw ? 'Ndiyo. Data yako inahifadhiwa kwenye kifaa chako na kusawazishwa kwenye seva salama za wingu (Supabase). Wewe peke yako unaweza kufikia data ya biashara yako kupitia akaunti yako. Hatuwahi kushiriki au kuuza data yako kwa watu wengine.'
                         : 'Yes. Your data is stored locally on your device and synced to secure cloud servers (Supabase). Only you can access your business data through your account. We never share or sell your data to third parties.' },
                ];
                // Group by category
                const cats = [...new Set(faqs.map(f => f.cat))];
                return (
                  <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {cats.map(cat => {
                      const catFaqs = faqs.filter(f => f.cat === cat);
                      const { catColor, catText, icon } = catFaqs[0];
                      return (
                        <div key={cat}>
                          {/* Category header button */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: catColor, borderRadius: 10, marginBottom: 6, border: `1px solid ${catText}22` }}>
                            <span style={{ fontSize: 16 }}>{icon}</span>
                            <span style={{ fontWeight: 800, fontSize: 13, color: catText, letterSpacing: 0.3 }}>{cat}</span>
                          </div>
                          {/* Questions under this category */}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {catFaqs.map((faq, i) => {
                              const gi = faqs.indexOf(faq);
                              return (
                                <div key={gi} style={{ background: 'var(--bg-card)', borderRadius: 12, border: faqOpenIdx === gi ? `1.5px solid ${catText}55` : '1px solid var(--border-color)', overflow: 'hidden', transition: 'border-color 0.2s' }}>
                                  <button onClick={() => setFaqOpenIdx(faqOpenIdx === gi ? null : gi)}
                                    style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: faqOpenIdx === gi ? catColor : 'none', border: 'none', cursor: 'pointer', textAlign: 'left', gap: 10, transition: 'background 0.2s' }}>
                                    <span style={{ fontWeight: 600, color: faqOpenIdx === gi ? catText : 'var(--text-primary)', fontSize: 13.5, flex: 1, lineHeight: 1.4 }}>{faq.q}</span>
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={faqOpenIdx === gi ? catText : 'var(--text-secondary)'} strokeWidth="2.5" strokeLinecap="round"
                                      style={{ flexShrink: 0, transform: faqOpenIdx === gi ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                                      <polyline points="6 9 12 15 18 9"/>
                                    </svg>
                                  </button>
                                  {faqOpenIdx === gi && (
                                    <div style={{ padding: '4px 14px 14px', fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.7, borderTop: `1px solid ${catText}22` }}>{faq.a}</div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}

              {/* ── TEAM & MEMBERS SCREEN ── */}
              {subScreen === 'team' && (() => {
                const sw = language === 'sw';
                const isOwner = teamMembers.some(m => m.is_owner && m.user_id === currentUser?.uid);
                const isAdminOrOwner = isOwner || teamMembers.some(m => m.user_id === currentUser?.uid && (m.role_key === 'ADMIN'));
                const myMemberId = teamMembers.find(m => m.user_id === currentUser?.uid)?.id;

                const loadTeam = () => {
                  if (!businessId || !currentUser?.accessToken) return;
                  setTeamLoading(true); setTeamError('');
                  Promise.all([fetchTeamMembers(businessId, currentUser.accessToken), fetchAvailableRoles(currentUser.accessToken)])
                    .then(([td, roles]) => { setTeamMembers(td?.members || []); setTeamInvitations(td?.invitations || []); setTeamRoles(roles || []); })
                    .catch(err => setTeamError(err?.message || 'Failed to load'))
                    .finally(() => setTeamLoading(false));
                };

                const handleInvite = async () => {
                  if (!teamInviteEmail.trim() || !teamInviteRole || teamInviting) return;
                  setTeamInviting(true); setTeamError(''); setTeamInviteCode('');
                  try {
                    const token = crypto.randomUUID();
                    const inviteeEmail = teamInviteEmail.trim().toLowerCase();
                    const selectedRole = teamRoles.find(r => r.id === teamInviteRole);
                    await inviteMember(businessId, inviteeEmail, teamInviteRole, token, currentUser.accessToken);
                    setTeamInviteCode(token); setTeamInviteEmail(''); setShowTeamInviteModal(false);
                    setInviteCodes(prev => ({ ...prev, [inviteeEmail]: token }));
                    loadTeam();
                    // Send email (best-effort -invite code shown as fallback regardless)
                    const emailResult = await sendInviteEmail({
                      email: inviteeEmail,
                      businessName: businessName || 'Your Business',
                      inviterName: currentUser.displayName || currentUser.email || 'A team member',
                      inviteCode: token,
                      roleName: selectedRole?.name || roleLabel(selectedRole?.system_key || ''),
                    }, currentUser.accessToken);
                    showAlert(
                      emailResult.sent
                        ? (sw ? 'Mwaliko umetumwa kwa barua pepe!' : 'Invitation email sent!')
                        : (sw ? 'Mwaliko umeundwa -tuma kodi kwa mkono' : 'Invite created -share the code manually'),
                      { tone: emailResult.sent ? 'success' : 'warning' }
                    );
                  } catch (err) {
                    const msg = err?.message || '';
                    setTeamError(msg.includes('ERR_VALIDATION') ? (sw ? 'Barua pepe hii tayari imealikwa au ni mwanachama' : 'This email is already invited or is a member') : msg.includes('ERR_PERMISSION') ? (sw ? 'Huna ruhusa' : 'No permission') : (msg || 'Failed'));
                  } finally { setTeamInviting(false); }
                };

                const handleChangeRole = async (memberId, newRoleId) => {
                  setTeamActionBusy(memberId);
                  try { await changeMemberRole(businessId, memberId, newRoleId, currentUser.accessToken); showAlert(sw ? 'Jukumu limebadilishwa' : 'Role updated', { tone: 'success' }); setTeamActionMember(null); loadTeam(); }
                  catch (err) { showAlert(err?.message || 'Failed', { tone: 'error' }); }
                  finally { setTeamActionBusy(null); }
                };

                const handleSetStatus = async (memberId, status) => {
                  const label = status === 'suspended' ? (sw ? 'Simamisha' : 'Suspend') : status === 'removed' ? (sw ? 'Ondoa' : 'Remove') : (sw ? 'Rejesha' : 'Reactivate');
                  showConfirm(sw ? `${label} mwanachama huyu?` : `${label} this member?`, async () => {
                    setTeamActionBusy(memberId);
                    try { await setMemberStatus(businessId, memberId, status, currentUser.accessToken); showAlert(sw ? 'Imefanikiwa' : 'Done', { tone: 'success' }); setTeamActionMember(null); loadTeam(); }
                    catch (err) { showAlert(err?.message || 'Failed', { tone: 'error' }); }
                    finally { setTeamActionBusy(null); }
                  }, { title: label });
                };

                const handleTransfer = (toMemberId) => {
                  if (!myMemberId) return;
                  const demoteRole = teamRoles.find(r => r.system_key === 'ADMIN');
                  const ownerRole = teamMembers.find(m => m.is_owner)?.role_id;
                  if (!demoteRole || !ownerRole) return;
                  showConfirm(sw ? 'Hamisha umiliki? Huwezi kurudisha.' : 'Transfer ownership? This cannot be undone.', async () => {
                    setTeamActionBusy(toMemberId);
                    try { await transferOwnership(businessId, myMemberId, toMemberId, ownerRole, demoteRole.id, currentUser.accessToken); showAlert(sw ? 'Umiliki umehamishwa' : 'Ownership transferred', { tone: 'success' }); setTeamActionMember(null); loadTeam(); }
                    catch (err) { showAlert(err?.message || 'Failed', { tone: 'error' }); }
                    finally { setTeamActionBusy(null); }
                  }, { title: sw ? 'Hamisha Umiliki' : 'Transfer Ownership' });
                };

                const roleLabel = (key) => ({ OWNER: sw?'Mmiliki':'Owner', ADMIN: sw?'Msimamizi':'Admin', MANAGER: sw?'Meneja':'Manager', ACCOUNTANT: sw?'Mhasibu':'Accountant', CASHIER: sw?'Karani':'Cashier', VIEWER: sw?'Mtazamaji':'Viewer' })[key] || key;
                const roleDesc = (key) => ({ OWNER: sw?'Ufikiaji kamili':'Full access to all features', ADMIN: sw?'Simamia mipangilio yote':'Manage all settings and data', MANAGER: sw?'Simamia shughuli za kila siku':'Manage day-to-day operations', ACCOUNTANT: sw?'Ufikiaji wa fedha na ripoti':'Financial data and reports', CASHIER: sw?'Ufikiaji wa mauzo na malipo':'Sales and payment access', VIEWER: sw?'Tazama ripoti tu':'View reports and basic info' })[key] || '';
                const roleColor = (key) => ({ OWNER: '#D97706', ADMIN: '#DC2626', MANAGER: '#2563EB', ACCOUNTANT: '#7C3AED', CASHIER: '#059669', VIEWER: '#64748B' })[key] || '#64748B';
                const roleBg = (key) => ({ OWNER: '#FFFBEB', ADMIN: '#FEF2F2', MANAGER: '#EFF6FF', ACCOUNTANT: '#F5F3FF', CASHIER: '#ECFDF5', VIEWER: '#F8FAFC' })[key] || '#F8FAFC';
                const statusColor = (s) => s === 'active' ? '#059669' : s === 'suspended' ? '#D97706' : '#9CA3AF';
                const statusBg = (s) => s === 'active' ? '#D1FAE5' : s === 'suspended' ? '#FEF3C7' : '#F3F4F6';
                const activeCount = teamMembers.filter(m => m.status === 'active').length;
                const suspendedCount = teamMembers.filter(m => m.status === 'suspended').length;
                const pendingCount = teamInvitations.filter(i => i.status === 'pending').length;
                const am = teamActionMember;

                return (
                  <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 12, paddingBottom: 100 }}>

                    {/* Loading */}
                    {teamLoading && <div style={{ display: 'flex', justifyContent: 'center', padding: 30 }}><div style={{ width: 24, height: 24, border: '3px solid var(--border-default)', borderTop: '3px solid #059669', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} /></div>}

                    {/* Error */}
                    {teamError && <div style={{ padding: '10px 14px', background: '#FEF2F2', borderRadius: 10, border: '1px solid #FECACA', color: '#DC2626', fontSize: 13, fontWeight: 500 }}>{teamError}</div>}

                    {!teamLoading && teamView === 'main' && (
                      <>
                        {/* Subtitle + Invite button */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{sw ? 'Simamia watumiaji wa biashara yako' : 'Manage people who have access to your business.'}</span>
                          {isAdminOrOwner && (
                            <button onClick={() => { setShowTeamInviteModal(true); setTeamInviteCode(''); setTeamError(''); }}
                              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 10, border: 'none', background: '#059669', color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer', flexShrink: 0 }}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="20" y1="8" x2="20" y2="14"/><line x1="23" y1="11" x2="17" y2="11"/></svg>
                              {sw ? 'Alika' : 'Invite member'}
                            </button>
                          )}
                        </div>

                        {/* Stats cards */}
                        <div style={{ display: 'flex', gap: 8 }}>
                          {[
                            { label: sw ? 'Hai' : 'Active members', count: activeCount, color: '#059669', bg: '#ECFDF5', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2" strokeLinecap="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg> },
                            { label: sw ? 'Inasubiri' : 'Pending invites', count: pendingCount, color: '#D97706', bg: '#FFFBEB', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth="2" strokeLinecap="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="20" y1="8" x2="20" y2="14"/><line x1="23" y1="11" x2="17" y2="11"/></svg> },
                            { label: sw ? 'Simamishwa' : 'Suspended', count: suspendedCount, color: '#DC2626', bg: '#FEF2F2', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2" strokeLinecap="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="17" y1="8" x2="23" y2="14"/></svg> },
                          ].map((s, i) => (
                            <div key={i} style={{ flex: 1, background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border-color)', padding: '10px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                              <div style={{ width: 28, height: 28, borderRadius: 8, background: s.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{s.icon}</div>
                              <div style={{ fontWeight: 800, fontSize: 18, color: 'var(--text-primary)' }}>{s.count}</div>
                              <div style={{ fontSize: 10, color: 'var(--text-secondary)', textAlign: 'center', lineHeight: 1.2 }}>{s.label}</div>
                            </div>
                          ))}
                        </div>

                        {/* Members section */}
                        {teamMembers.length > 0 && (
                          <>
                            <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', color: 'var(--text-secondary)', margin: '4px 2px 0' }}>{sw ? 'Wanachama' : 'Members'}</div>
                            <div style={{ background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border-color)', overflow: 'hidden' }}>
                              {/* Table header */}
                              <div style={{ display: 'flex', padding: '8px 12px', borderBottom: '1px solid var(--border-color)', gap: 8 }}>
                                <span style={{ flex: 1, fontSize: 10, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{sw ? 'Mwanachama' : 'Member'}</span>
                                <span style={{ width: 70, fontSize: 10, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5, textAlign: 'center' }}>{sw ? 'Jukumu' : 'Role'}</span>
                                <span style={{ width: 60, fontSize: 10, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5, textAlign: 'center' }}>{sw ? 'Hali' : 'Status'}</span>
                                <span style={{ width: 24 }}></span>
                              </div>
                              {teamMembers.map((member, idx) => {
                                const initial = (member.display_name || member.email || '?')[0].toUpperCase();
                                const isSelf = member.user_id === currentUser?.uid;
                                const canManage = isAdminOrOwner && !member.is_owner && !isSelf;
                                return (
                                  <div key={member.id} style={{ display: 'flex', alignItems: 'center', padding: '10px 12px', gap: 8, borderBottom: idx < teamMembers.length - 1 ? '1px solid var(--border-color)' : 'none' }}>
                                    {/* Avatar + name */}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
                                      <div style={{ width: 32, height: 32, borderRadius: '50%', background: member.is_owner ? '#DCFCE7' : '#F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13, color: member.is_owner ? '#166534' : '#64748B', flexShrink: 0 }}>{initial}</div>
                                      <div style={{ minWidth: 0 }}>
                                        <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{member.display_name || member.email.split('@')[0]}</div>
                                        <div style={{ fontSize: 10, color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{member.email}</div>
                                      </div>
                                    </div>
                                    {/* Role badge */}
                                    <div style={{ width: 70, textAlign: 'center' }}>
                                      <span style={{ fontSize: 10, fontWeight: 700, color: roleColor(member.role_key), background: roleBg(member.role_key), padding: '3px 8px', borderRadius: 8, whiteSpace: 'nowrap' }}>{roleLabel(member.role_key)}</span>
                                    </div>
                                    {/* Status */}
                                    <div style={{ width: 60, textAlign: 'center' }}>
                                      <span style={{ fontSize: 10, fontWeight: 600, color: statusColor(member.status) }}>{member.status === 'active' ? (sw ? 'Hai' : 'Active') : member.status === 'suspended' ? (sw ? 'Simamishwa' : 'Suspended') : member.status}</span>
                                    </div>
                                    {/* Three-dot menu */}
                                    <div style={{ width: 24, display: 'flex', justifyContent: 'center' }}>
                                      {canManage ? (
                                        <button onClick={() => setTeamActionMember(member)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex' }}>
                                          <svg width="16" height="16" viewBox="0 0 24 24" fill="var(--text-secondary)"><circle cx="12" cy="5" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="12" cy="19" r="1.5"/></svg>
                                        </button>
                                      ) : isSelf ? (
                                        <span style={{ fontSize: 9, color: '#2563EB', fontWeight: 700 }}>{sw ? 'Wewe' : 'You'}</span>
                                      ) : null}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </>
                        )}

                        {/* Invitations button */}
                        <button onClick={() => { setTeamView('invitations'); setInvFilter('all'); }}
                          style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border-color)', cursor: 'pointer', width: '100%', textAlign: 'left' }}>
                          <div style={{ width: 36, height: 36, borderRadius: 10, background: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2" strokeLinecap="round"><rect x="2" y="4" width="20" height="16" rx="2"/><polyline points="22,7 12,13 2,7"/></svg>
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)' }}>{sw ? 'Mialiko' : 'Invitations'}</div>
                            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 1 }}>{sw ? `Mialiko ${teamInvitations.length} jumla` : `${teamInvitations.length} total invitation${teamInvitations.length !== 1 ? 's' : ''}`}</div>
                          </div>
                          {teamInvitations.filter(i => i.status === 'pending').length > 0 && (
                            <span style={{ fontSize: 11, fontWeight: 700, color: '#D97706', background: '#FFFBEB', padding: '3px 10px', borderRadius: 10 }}>{teamInvitations.filter(i => i.status === 'pending').length} {sw ? 'inasubiri' : 'pending'}</span>
                          )}
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#CBD5E1" strokeWidth="2.5" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>
                        </button>

                        {/* Invite code display (most recent) */}
                        {teamInviteCode && (
                          <div style={{ background: '#ECFDF5', borderRadius: 12, border: '1px solid #A7F3D0', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <div style={{ fontWeight: 700, fontSize: 13, color: '#065F46' }}>{sw ? 'Kodi ya Mwaliko wa Hivi Karibuni' : 'Latest Invite Code'}</div>
                            <div style={{ fontSize: 11, color: '#047857', lineHeight: 1.5 }}>{sw ? 'Mpe mwanachama kodi hii ili akubali mwaliko:' : 'Share this code with the invited member:'}</div>
                            <div style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: '#064E3B', background: '#D1FAE5', padding: '8px 12px', borderRadius: 8, wordBreak: 'break-all', userSelect: 'all' }}>{teamInviteCode}</div>
                            <button onClick={() => { try { navigator.clipboard.writeText(teamInviteCode); showAlert(sw ? 'Imenakiliwa!' : 'Copied!', { tone: 'success' }); } catch {} }}
                              style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #6EE7B7', background: '#D1FAE5', color: '#065F46', fontSize: 12, fontWeight: 600, cursor: 'pointer', alignSelf: 'flex-start' }}>{sw ? 'Nakili' : 'Copy'}</button>
                          </div>
                        )}

                        {/* Roles overview */}
                        <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', color: 'var(--text-secondary)', margin: '4px 2px 0' }}>{sw ? 'Majukumu Yanayopatikana' : 'Roles overview'}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-secondary)', margin: '-6px 2px 0' }}>{sw ? 'Majukumu yanayopatikana katika biashara yako.' : 'These are the roles available in your business.'}</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {[
                            { key: 'OWNER', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={roleColor('OWNER')} strokeWidth="2" strokeLinecap="round"><path d="M2 20h20"/><path d="M5 20V8l7-5 7 5v12"/><rect x="9" y="12" width="6" height="8"/></svg> },
                            { key: 'ADMIN', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={roleColor('ADMIN')} strokeWidth="2" strokeLinecap="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg> },
                            { key: 'MANAGER', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={roleColor('MANAGER')} strokeWidth="2" strokeLinecap="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg> },
                            { key: 'ACCOUNTANT', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={roleColor('ACCOUNTANT')} strokeWidth="2" strokeLinecap="round"><rect x="2" y="3" width="20" height="18" rx="2"/><line x1="2" y1="9" x2="22" y2="9"/><line x1="9" y1="3" x2="9" y2="21"/></svg> },
                            { key: 'CASHIER', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={roleColor('CASHIER')} strokeWidth="2" strokeLinecap="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg> },
                            { key: 'VIEWER', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={roleColor('VIEWER')} strokeWidth="2" strokeLinecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg> },
                          ].map(r => {
                            const roleMembers = r.key === 'OWNER' ? teamMembers.filter(m => m.is_owner) : teamMembers.filter(m => m.role_key === r.key && !m.is_owner);
                            const count = roleMembers.length;
                            const isExpanded = expandedRole === r.key;
                            return (
                              <div key={r.key} style={{ borderRadius: 10, border: `1px solid ${isExpanded ? roleColor(r.key) + '40' : 'var(--border-color)'}`, overflow: 'hidden' }}>
                                <button onClick={() => setExpandedRole(isExpanded ? null : r.key)}
                                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: 'var(--bg-card)', width: '100%', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                                  <div style={{ width: 32, height: 32, borderRadius: 8, background: roleBg(r.key), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{r.icon}</div>
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text-primary)' }}>{roleLabel(r.key)}</div>
                                    <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 1 }}>{roleDesc(r.key)}</div>
                                  </div>
                                  <span style={{ fontSize: 12, fontWeight: 700, color: roleColor(r.key), background: roleBg(r.key), padding: '3px 10px', borderRadius: 8 }}>{count} {count === 1 ? (sw ? 'mwanachama' : 'member') : (sw ? 'wanachama' : 'members')}</span>
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#CBD5E1" strokeWidth="2.5" strokeLinecap="round" style={{ transition: 'transform 0.2s', transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}><polyline points="9 18 15 12 9 6"/></svg>
                                </button>
                                {isExpanded && (
                                  <div style={{ borderTop: '1px solid var(--border-color)', background: roleBg(r.key) + '80' }}>
                                    {count === 0 ? (
                                      <div style={{ padding: '12px 14px', textAlign: 'center', fontSize: 12, color: 'var(--text-secondary)' }}>{sw ? 'Hakuna wanachama katika jukumu hili' : 'No members with this role'}</div>
                                    ) : (
                                      roleMembers.map((member, idx) => (
                                        <div key={member.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderBottom: idx < roleMembers.length - 1 ? '1px solid var(--border-color)' : 'none' }}>
                                          <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--bg-card)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 12, color: roleColor(r.key), flexShrink: 0 }}>{(member.display_name || member.email || '?')[0].toUpperCase()}</div>
                                          <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontWeight: 600, fontSize: 12, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{member.display_name || member.email.split('@')[0]}</div>
                                            <div style={{ fontSize: 10, color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{member.email}</div>
                                          </div>
                                          <span style={{ fontSize: 10, fontWeight: 600, color: statusColor(member.status) }}>{member.status === 'active' ? (sw ? 'Hai' : 'Active') : (sw ? 'Simamishwa' : 'Suspended')}</span>
                                        </div>
                                      ))
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>

                        {/* Empty state */}
                        {teamMembers.length === 0 && !teamError && (
                          <div style={{ textAlign: 'center', padding: '30px 20px', color: 'var(--text-secondary)' }}>
                            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#CBD5E1" strokeWidth="1.5" strokeLinecap="round" style={{ margin: '0 auto 12px' }}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                            <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-primary)', marginBottom: 4 }}>{sw ? 'Hakuna wanachama' : 'No team members yet'}</div>
                            <div style={{ fontSize: 12 }}>{sw ? 'Alika watu kutumia biashara yako' : 'Invite people to collaborate on your business'}</div>
                          </div>
                        )}
                      </>
                    )}

                    {/* ── INVITATIONS SUB-VIEW ── */}
                    {!teamLoading && teamView === 'invitations' && (() => {
                      const statusConf = {
                        pending: { color: '#D97706', bg: '#FFFBEB', border: '#FCD34D', label: sw ? 'Inasubiri' : 'Pending', icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg> },
                        accepted: { color: '#059669', bg: '#ECFDF5', border: '#6EE7B7', label: sw ? 'Imekubaliwa' : 'Accepted', icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" strokeLinecap="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg> },
                        revoked: { color: '#DC2626', bg: '#FEF2F2', border: '#FECACA', label: sw ? 'Imefutwa' : 'Revoked', icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg> },
                        expired: { color: 'var(--text-muted)', bg: '#F8FAFC', border: '#E2E8F0', label: sw ? 'Imeisha' : 'Expired', icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg> },
                      };
                      const filtered = invFilter === 'all' ? teamInvitations : teamInvitations.filter(i => i.status === invFilter);
                      const counts = { all: teamInvitations.length, pending: teamInvitations.filter(i => i.status === 'pending').length, accepted: teamInvitations.filter(i => i.status === 'accepted').length, revoked: teamInvitations.filter(i => i.status === 'revoked').length };
                      return (
                        <>
                          {/* Back button */}
                          <button onClick={() => setTeamView('main')} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', padding: '0 0 4px', color: '#2563EB', fontWeight: 600, fontSize: 13 }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
                            {sw ? 'Rudi kwa timu' : 'Back to team'}
                          </button>

                          {/* Filter tabs */}
                          <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 2 }}>
                            {['all', 'pending', 'accepted', 'revoked'].map(f => (
                              <button key={f} onClick={() => setInvFilter(f)}
                                style={{ padding: '6px 14px', borderRadius: 20, border: invFilter === f ? '1.5px solid #059669' : '1px solid var(--border-color)', background: invFilter === f ? '#ECFDF5' : 'var(--bg-card)', color: invFilter === f ? '#059669' : 'var(--text-secondary)', fontWeight: 600, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                {f === 'all' ? (sw ? 'Zote' : 'All') : (statusConf[f]?.label || f)} ({counts[f] || 0})
                              </button>
                            ))}
                          </div>

                          {/* Invitation cards */}
                          {filtered.length === 0 && (
                            <div style={{ textAlign: 'center', padding: '30px 20px', color: 'var(--text-secondary)' }}>
                              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#CBD5E1" strokeWidth="1.5" strokeLinecap="round" style={{ margin: '0 auto 10px' }}><rect x="2" y="4" width="20" height="16" rx="2"/><polyline points="22,7 12,13 2,7"/></svg>
                              <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>{sw ? 'Hakuna mialiko' : 'No invitations'}</div>
                              <div style={{ fontSize: 12, marginTop: 2 }}>{invFilter !== 'all' ? (sw ? 'Hakuna mialiko katika kundi hili' : 'No invitations in this category') : (sw ? 'Bado hujaalika mtu yeyote' : 'You haven\'t invited anyone yet')}</div>
                            </div>
                          )}

                          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {filtered.map(inv => {
                              const sc = statusConf[inv.status] || statusConf.expired;
                              return (
                                <button key={inv.id} onClick={() => setSelectedInvitation(inv)}
                                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border-color)', cursor: 'pointer', width: '100%', textAlign: 'left' }}>
                                  <div style={{ width: 34, height: 34, borderRadius: '50%', background: sc.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={sc.color} strokeWidth="2" strokeLinecap="round"><rect x="2" y="4" width="20" height="16" rx="2"/><polyline points="22,7 12,13 2,7"/></svg>
                                  </div>
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{inv.email}</div>
                                    <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>{inv.role_name} · {new Date(inv.created_at).toLocaleDateString()}</div>
                                  </div>
                                  <span style={{ fontSize: 10, fontWeight: 700, color: sc.color, background: sc.bg, border: `1px solid ${sc.border}`, padding: '3px 8px', borderRadius: 8, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 4 }}>{sc.icon} {sc.label}</span>
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#CBD5E1" strokeWidth="2.5" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>
                                </button>
                              );
                            })}
                          </div>
                        </>
                      );
                    })()}

                    {/* ── INVITE MEMBER MODAL ── */}
                    {showTeamInviteModal && (
                      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={() => setShowTeamInviteModal(false)}>
                        <div onClick={e => e.stopPropagation()} style={{ background: 'var(--bg-card)', borderRadius: 16, width: '100%', maxWidth: 380, padding: '20px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>{sw ? 'Alika mwanachama' : 'Invite member'}</h3>
                            <button onClick={() => setShowTeamInviteModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--text-secondary)" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                            </button>
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            <div>
                              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>{sw ? 'Barua pepe' : 'Email address'}</label>
                              <input type="email" placeholder={sw ? 'mwanachama@mfano.com' : 'member@example.com'} value={teamInviteEmail} onChange={e => setTeamInviteEmail(e.target.value)}
                                style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid var(--border-color)', background: 'var(--bg-input,#F9FAFB)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', width: '100%', boxSizing: 'border-box' }} />
                            </div>
                            <div>
                              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>{sw ? 'Jukumu' : 'Role'}</label>
                              <select value={teamInviteRole} onChange={e => setTeamInviteRole(e.target.value)}
                                style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid var(--border-color)', background: 'var(--bg-input,#F9FAFB)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', cursor: 'pointer', width: '100%', boxSizing: 'border-box' }}>
                                {teamRoles.map(r => <option key={r.id} value={r.id}>{roleLabel(r.system_key)}</option>)}
                              </select>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', background: '#F0FDF4', borderRadius: 10, border: '1px solid #BBF7D0' }}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                              <span style={{ fontSize: 11, color: '#065F46', lineHeight: 1.4 }}>{sw ? 'Kodi ya mwaliko itatolewa ili kushiriki na mwanachama.' : 'An invite code will be generated to share with the member.'}</span>
                            </div>
                            <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                              <button onClick={() => setShowTeamInviteModal(false)} style={{ flex: 1, padding: '10px', borderRadius: 10, border: '1px solid var(--border-color)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>{sw ? 'Ghairi' : 'Cancel'}</button>
                              <button onClick={handleInvite} disabled={teamInviting || !teamInviteEmail.trim()}
                                style={{ flex: 1, padding: '10px', borderRadius: 10, border: 'none', background: teamInviting || !teamInviteEmail.trim() ? '#94A3B8' : '#059669', color: '#fff', fontWeight: 700, fontSize: 13, cursor: teamInviting ? 'wait' : 'pointer' }}>
                                {teamInviting ? (sw ? 'Inatuma...' : 'Sending...') : (sw ? 'Tuma mwaliko' : 'Send invitation')}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* ── MEMBER ACTIONS SHEET ── */}
                    {am && (
                      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }} onClick={() => setTeamActionMember(null)}>
                        <div onClick={e => e.stopPropagation()} style={{ background: 'var(--bg-card)', borderRadius: '20px 20px 0 0', width: '100%', maxWidth: 420, padding: '20px', paddingBottom: 40, maxHeight: '80vh', overflowY: 'auto' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>{sw ? 'Vitendo vya mwanachama' : 'Member actions'}</h3>
                            <button onClick={() => setTeamActionMember(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--text-secondary)" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                            </button>
                          </div>
                          {/* Member info */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, padding: '10px 12px', background: 'var(--bg-main)', borderRadius: 12 }}>
                            <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--bg-input)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 16, color: 'var(--text-muted)' }}>{(am.display_name || am.email || '?')[0].toUpperCase()}</div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)' }}>{am.display_name || am.email.split('@')[0]}</div>
                              <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{am.email}</div>
                            </div>
                            <span style={{ fontSize: 11, fontWeight: 600, color: statusColor(am.status), background: statusBg(am.status), padding: '3px 10px', borderRadius: 8 }}>{am.status === 'active' ? (sw ? 'Hai' : 'Active') : (sw ? 'Simamishwa' : 'Suspended')}</span>
                          </div>
                          {/* Change role */}
                          <div style={{ marginBottom: 16 }}>
                            <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)', marginBottom: 6 }}>{sw ? 'Badilisha jukumu' : 'Change role'}</div>
                            <select value={am.role_id} onChange={e => handleChangeRole(am.id, e.target.value)} disabled={teamActionBusy === am.id}
                              style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid var(--border-color)', background: 'var(--bg-input,#F9FAFB)', color: 'var(--text-primary)', fontSize: 13, width: '100%', boxSizing: 'border-box', cursor: 'pointer', marginBottom: 6 }}>
                              {teamRoles.map(r => <option key={r.id} value={r.id}>{roleLabel(r.system_key)}</option>)}
                            </select>
                            <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{sw ? 'Chagua jukumu jipya kwa mwanachama huyu.' : 'Choose a new role for this member.'}</div>
                          </div>
                          {/* Change status */}
                          <div style={{ marginBottom: 16 }}>
                            <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)', marginBottom: 8 }}>{sw ? 'Badilisha hali' : 'Change status'}</div>
                            <div style={{ display: 'flex', gap: 8 }}>
                              {am.status === 'active' ? (
                                <button onClick={() => handleSetStatus(am.id, 'suspended')} disabled={teamActionBusy === am.id}
                                  style={{ flex: 1, padding: '9px', borderRadius: 10, border: '1px solid #FCD34D', background: '#FFFBEB', color: '#92400E', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>{sw ? 'Simamisha' : 'Suspend member'}</button>
                              ) : (
                                <button onClick={() => handleSetStatus(am.id, 'active')} disabled={teamActionBusy === am.id}
                                  style={{ flex: 1, padding: '9px', borderRadius: 10, border: '1px solid #6EE7B7', background: '#D1FAE5', color: '#065F46', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>{sw ? 'Rejesha' : 'Reactivate member'}</button>
                              )}
                            </div>
                          </div>
                          {/* Transfer ownership (owner only) */}
                          {isOwner && am.status === 'active' && (
                            <div style={{ marginBottom: 16 }}>
                              <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)', marginBottom: 6 }}>{sw ? 'Hamisha umiliki' : 'Transfer ownership'}</div>
                              <div style={{ padding: '10px 12px', background: '#FEF3C7', borderRadius: 10, border: '1px solid #FCD34D', marginBottom: 8 }}>
                                <div style={{ fontSize: 11, color: '#92400E', lineHeight: 1.5 }}>
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth="2.5" strokeLinecap="round" style={{ verticalAlign: 'middle', marginRight: 4 }}><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                                  {sw ? 'Kitendo hiki hakiwezi kurudishwa.' : 'You are transferring ownership. This action cannot be undone.'}
                                </div>
                              </div>
                              <button onClick={() => handleTransfer(am.id)} disabled={teamActionBusy === am.id}
                                style={{ width: '100%', padding: '9px', borderRadius: 10, border: '1px solid #FECACA', background: '#FEF2F2', color: '#DC2626', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>{sw ? 'Hamisha umiliki' : 'Transfer ownership'}</button>
                            </div>
                          )}
                          {/* Remove member */}
                          <div>
                            <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)', marginBottom: 6 }}>{sw ? 'Ondoa mwanachama' : 'Remove member'}</div>
                            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 8 }}>{sw ? 'Mwanachama ataondolewa kwenye biashara yako.' : 'This will remove the member from your business.'}</div>
                            <button onClick={() => handleSetStatus(am.id, 'removed')} disabled={teamActionBusy === am.id}
                              style={{ width: '100%', padding: '9px', borderRadius: 10, border: '1px solid #FECACA', background: '#FEF2F2', color: '#DC2626', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>{sw ? 'Ondoa mwanachama' : 'Remove member'}</button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* ── INVITATION DETAIL SHEET ── */}
                    {selectedInvitation && (
                      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }} onClick={() => setSelectedInvitation(null)}>
                        <div onClick={e => e.stopPropagation()} style={{ background: 'var(--bg-card)', borderRadius: '20px 20px 0 0', width: '100%', maxWidth: 420, padding: '20px', paddingBottom: 40, maxHeight: '80vh', overflowY: 'auto' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>{sw ? 'Maelezo ya Mwaliko' : 'Invitation Details'}</h3>
                            <button onClick={() => setSelectedInvitation(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--text-secondary)" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                            </button>
                          </div>
                          {/* Invitation info card */}
                          {(() => {
                            const invSc = { pending: { color: '#D97706', bg: '#FFFBEB', border: '#FCD34D', label: sw ? 'Inasubiri' : 'Pending' }, accepted: { color: '#059669', bg: '#ECFDF5', border: '#6EE7B7', label: sw ? 'Imekubaliwa' : 'Accepted' }, revoked: { color: '#DC2626', bg: '#FEF2F2', border: '#FECACA', label: sw ? 'Imefutwa' : 'Revoked' }, expired: { color: 'var(--text-muted)', bg: '#F8FAFC', border: '#E2E8F0', label: sw ? 'Imeisha' : 'Expired' } };
                            const sc = invSc[selectedInvitation.status] || invSc.expired;
                            return (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, padding: '12px 14px', background: sc.bg, borderRadius: 12, border: `1px solid ${sc.border}` }}>
                                <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'rgba(255,255,255,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={sc.color} strokeWidth="2" strokeLinecap="round"><rect x="2" y="4" width="20" height="16" rx="2"/><polyline points="22,7 12,13 2,7"/></svg>
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)', wordBreak: 'break-all' }}>{selectedInvitation.email}</div>
                                  <div style={{ fontSize: 11, color: sc.color, fontWeight: 600, marginTop: 2 }}>{sc.label}</div>
                                </div>
                              </div>
                            );
                          })()}
                          {/* Details rows */}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: 'var(--bg-main)', borderRadius: 10 }}>
                              <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 }}>{sw ? 'Jukumu' : 'Role'}</span>
                              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', background: 'var(--bg-input)', padding: '3px 10px', borderRadius: 8 }}>{selectedInvitation.role_name}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: 'var(--bg-main)', borderRadius: 10 }}>
                              <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 }}>{sw ? 'Tarehe ya mwaliko' : 'Invited on'}</span>
                              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>{new Date(selectedInvitation.created_at).toLocaleDateString()}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: 'var(--bg-main)', borderRadius: 10 }}>
                              <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 }}>{sw ? 'Inaisha' : 'Expires'}</span>
                              <span style={{ fontSize: 12, fontWeight: 600, color: '#D97706' }}>{selectedInvitation.expires_at ? new Date(selectedInvitation.expires_at).toLocaleDateString() : (sw ? 'Haijulikani' : 'Unknown')}</span>
                            </div>
                          </div>
                          {/* Invite code section */}
                          {inviteCodes[selectedInvitation.email] ? (
                            <div style={{ background: '#ECFDF5', borderRadius: 12, border: '1px solid #A7F3D0', padding: '14px', marginBottom: 16 }}>
                              <div style={{ fontWeight: 700, fontSize: 13, color: '#065F46', marginBottom: 6 }}>{sw ? 'Kodi ya Mwaliko' : 'Invite Code'}</div>
                              <div style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 700, color: '#064E3B', background: '#D1FAE5', padding: '10px 12px', borderRadius: 8, wordBreak: 'break-all', userSelect: 'all', marginBottom: 8 }}>{inviteCodes[selectedInvitation.email]}</div>
                              <button onClick={() => { try { navigator.clipboard.writeText(inviteCodes[selectedInvitation.email]); showAlert(sw ? 'Imenakiliwa!' : 'Copied!', { tone: 'success' }); } catch {} }}
                                style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid #6EE7B7', background: '#D1FAE5', color: '#065F46', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                                {sw ? 'Nakili kodi' : 'Copy code'}
                              </button>
                            </div>
                          ) : (
                            <div style={{ background: '#FFFBEB', borderRadius: 12, border: '1px solid #FDE68A', padding: '12px 14px', marginBottom: 16 }}>
                              <div style={{ fontSize: 12, color: '#92400E', lineHeight: 1.5, display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth="2" strokeLinecap="round" style={{ marginTop: 1, flexShrink: 0 }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                                <span>{sw ? 'Kodi ya mwaliko inaonyeshwa mara moja tu wakati wa kutuma. Angalia barua pepe iliyotumwa au tuma mwaliko mpya.' : 'The invite code is only shown once when first sent. Check the email that was sent, or revoke and send a new invitation.'}</span>
                              </div>
                            </div>
                          )}
                          {/* Revoke button -only for pending invitations */}
                          {isAdminOrOwner && selectedInvitation.status === 'pending' && (
                            <div>
                              <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)', marginBottom: 6 }}>{sw ? 'Futa mwaliko' : 'Revoke invitation'}</div>
                              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 8, lineHeight: 1.5 }}>{sw ? 'Kodi ya mwaliko haitafanya kazi tena. Mtu aliyealikwa hataweza kujiunga.' : 'The invite code will stop working. The invited person will no longer be able to join.'}</div>
                              <button
                                onClick={async () => {
                                  if (teamActionBusy) return;
                                  try {
                                    setTeamActionBusy(selectedInvitation.id);
                                    await revokeInvitation(businessId, selectedInvitation.id, currentUser.accessToken);
                                    showAlert(sw ? 'Mwaliko umefutwa' : 'Invitation revoked', { tone: 'success' });
                                    setSelectedInvitation(null);
                                    // Refresh team data
                                    const token = currentUser.accessToken;
                                    const teamData = await fetchTeamMembers(businessId, token);
                                    setTeamMembers(teamData?.members || []);
                                    setTeamInvitations(teamData?.invitations || []);
                                  } catch (err) {
                                    showAlert(err?.message || (sw ? 'Imeshindikana' : 'Failed to revoke'), { tone: 'error' });
                                  } finally { setTeamActionBusy(null); }
                                }}
                                disabled={!!teamActionBusy}
                                style={{ width: '100%', padding: '10px', borderRadius: 10, border: '1px solid #FECACA', background: '#FEF2F2', color: '#DC2626', fontWeight: 700, fontSize: 13, cursor: teamActionBusy ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                                {teamActionBusy === selectedInvitation.id ? (
                                  <div style={{ width: 16, height: 16, border: '2px solid #DC2626', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                                ) : (
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                                )}
                                {teamActionBusy === selectedInvitation.id ? (sw ? 'Inafuta...' : 'Revoking...') : (sw ? 'Futa mwaliko' : 'Revoke invitation')}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* ── MY TEAM DETAIL SCREEN ── */}
              {subScreen === 'myTeam' && (() => {
                const sw = language === 'sw';
                const joinedTeam = businesses.find(b => !b.isOwner);
                if (!joinedTeam) return null;
                const joinDate = joinedTeam.joinedAt ? new Date(joinedTeam.joinedAt).toLocaleDateString(sw ? 'sw-TZ' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : (sw ? 'Haijulikani' : 'Unknown');
                return (
                  <div style={{ padding: '16px 14px', display: 'flex', flexDirection: 'column', gap: 14, paddingBottom: 100 }}>
                    {/* Header illustration */}
                    <div style={{ textAlign: 'center', padding: '20px 20px 10px' }}>
                      <div style={{ width: 56, height: 56, borderRadius: 16, background: '#F0FDF4', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                      </div>
                      <h2 style={{ margin: '0 0 6px', fontSize: 18, fontWeight: 800, color: 'var(--text-primary)' }}>{sw ? 'Timu Yangu' : 'My Team'}</h2>
                      <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>{sw ? 'Maelezo ya timu uliyojiunga nayo.' : 'Details of the team you\'ve joined.'}</p>
                    </div>

                    {/* Team details card */}
                    <div style={{ background: 'var(--bg-card)', borderRadius: 14, border: '1px solid var(--border-color)', padding: '16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                      {/* Business name */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>{sw ? 'Jina la Biashara' : 'Business Name'}</span>
                        <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{joinedTeam.name}</span>
                      </div>
                      <div style={{ height: 1, background: 'var(--border-color)' }} />

                      {/* Role */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>{sw ? 'Jukumu' : 'Role'}</span>
                        <span style={{ padding: '4px 12px', borderRadius: 16, background: '#DBEAFE', color: '#1E40AF', fontWeight: 700, fontSize: 12 }}>{joinedTeam.roleName || joinedTeam.roleSystemKey || 'Member'}</span>
                      </div>
                      <div style={{ height: 1, background: 'var(--border-color)' }} />

                      {/* Date joined */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>{sw ? 'Tarehe ya Kujiunga' : 'Date Joined'}</span>
                        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{joinDate}</span>
                      </div>
                      <div style={{ height: 1, background: 'var(--border-color)' }} />

                      {/* Country */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>{sw ? 'Nchi' : 'Country'}</span>
                        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{joinedTeam.country === 'TZ' ? (sw ? 'Tanzania' : 'Tanzania') : joinedTeam.country}</span>
                      </div>
                    </div>

                    {/* Info box */}
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 12px', background: '#F0FDF4', borderRadius: 10, border: '1px solid #BBF7D0' }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2" strokeLinecap="round" style={{ marginTop: 1, flexShrink: 0 }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                      <span style={{ fontSize: 12, color: '#065F46', lineHeight: 1.5 }}>{sw ? 'Jukumu lako linawekwa na mmiliki wa biashara. Unaweza kuondoka kwenye timu wakati wowote, data ya biashara itabaki salama.' : 'Your role is managed by the business owner. You can leave the team at any time. Business data will remain intact.'}</span>
                    </div>

                    {/* Leave Team button */}
                    <button
                      onClick={() => {
                        showConfirm(
                          sw
                            ? `Utapoteza ufikiaji wa data yote ya ${joinedTeam.name}. Biashara yako binafsi itabaki.`
                            : `You will lose access to all data from ${joinedTeam.name}. Your personal business will still be available.`,
                          async () => {
                            try {
                              await leaveTeam(joinedTeam.businessId, currentUser.accessToken);
                              await refreshBusinesses();
                              showAlert(sw ? 'Umeondoka kwenye timu.' : 'You have left the team.', { tone: 'success' });
                              setSettingsScreen(null);
                            } catch (err) {
                              showAlert(err?.message || (sw ? 'Imeshindikana kuondoka' : 'Failed to leave team'), { tone: 'error' });
                            }
                          },
                          { title: sw ? `Ondoka ${joinedTeam.name}?` : `Leave ${joinedTeam.name}?`, tone: 'warn' }
                        );
                      }}
                      style={{ padding: '12px', borderRadius: 12, border: '2px solid #FCA5A5', background: '#FEF2F2', color: '#DC2626', fontWeight: 700, fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 6 }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                      {sw ? 'Ondoka kwenye Timu' : 'Leave Team'}
                    </button>
                  </div>
                );
              })()}

              {/* ── ROLE PERMISSIONS GRID ── */}
              {subScreen === 'permissions' && (() => {
                const sw = language === 'sw';
                const { matrixData, loading: permLoading, saving: permSaving, togglePermission } = permMatrix;
                const roles = matrixData?.roles || [];
                const matrix = matrixData?.matrix || {};

                if (permLoading) return (
                  <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-secondary)' }}>
                    {sw ? 'Inapakia...' : 'Loading permissions...'}
                  </div>
                );

                return (
                  <div style={{ padding: '12px 10px', paddingBottom: 100 }}>
                    <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '0 0 12px', lineHeight: 1.5 }}>
                      {sw ? 'Bonyeza kisanduku ili kubadilisha ruhusa kwa kila jukumu. Mabadiliko yanatekelezwa mara moja.' : 'Toggle checkboxes to customize what each role can see and do. Changes take effect immediately.'}
                    </p>

                    {/* Scrollable grid */}
                    <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                      <table style={{ width: '100%', minWidth: roles.length * 70 + 140, borderCollapse: 'collapse', fontSize: 11 }}>
                        <thead>
                          <tr style={{ borderBottom: '2px solid var(--border-color)' }}>
                            <th style={{ textAlign: 'left', padding: '6px 6px', fontWeight: 700, color: 'var(--text-primary)', position: 'sticky', left: 0, background: 'var(--bg-main)', zIndex: 2, minWidth: 130 }}>
                              {sw ? 'Kipengele' : 'Feature'}
                            </th>
                            {roles.map(r => (
                              <th key={r.system_key} style={{ textAlign: 'center', padding: '6px 4px', fontWeight: 700, color: 'var(--text-primary)', minWidth: 58 }}>
                                <div style={{ fontSize: 11 }}>{ROLE_INFO[r.system_key]?.label || r.name}</div>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {PERMISSION_GRID.map((group, gi) => (
                            <React.Fragment key={group.areaKey}>
                              {/* Area header row */}
                              <tr>
                                <td colSpan={roles.length + 1} style={{ padding: '10px 6px 4px', fontWeight: 800, fontSize: 12, color: 'var(--text-primary)', borderBottom: '1px solid var(--border-color)', background: 'var(--bg-main)' }}>
                                  {group.area}
                                </td>
                              </tr>
                              {/* Permission rows */}
                              {group.permissions.map((perm, pi) => (
                                <tr key={perm.key} style={{ borderBottom: '1px solid var(--border-color)', background: pi % 2 === 0 ? 'var(--bg-card)' : 'transparent' }}>
                                  <td style={{ padding: '5px 6px', color: 'var(--text-secondary)', fontWeight: 500, position: 'sticky', left: 0, background: pi % 2 === 0 ? 'var(--bg-card)' : 'var(--bg-main)', zIndex: 1 }}>
                                    {perm.label}
                                  </td>
                                  {roles.map(r => {
                                    const granted = matrix[r.system_key]?.[perm.key] === true;
                                    return (
                                      <td key={r.system_key} style={{ textAlign: 'center', padding: '5px 4px' }}>
                                        <label style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: permSaving ? 'wait' : 'pointer' }}>
                                          <input
                                            type="checkbox"
                                            checked={granted}
                                            onChange={() => togglePermission(r.system_key, perm.key, !granted)}
                                            style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#2563EB' }}
                                          />
                                        </label>
                                      </td>
                                    );
                                  })}
                                </tr>
                              ))}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Reset to defaults button */}
                    <div style={{ marginTop: 16, textAlign: 'center' }}>
                      <button
                        onClick={async () => {
                          if (!window.confirm(sw ? 'Rudisha ruhusa zote kwa chaguo-msingi?' : 'Reset all permissions to defaults? This will remove all custom overrides.')) return;
                          // Delete all overrides by toggling each back — or we could add a bulk reset command
                          // For now just reload to show global defaults
                          permMatrix.reload();
                        }}
                        style={{ padding: '8px 20px', fontSize: 12, fontWeight: 600, background: 'transparent', border: '1px solid var(--border-color)', borderRadius: 8, color: 'var(--text-secondary)', cursor: 'pointer' }}
                      >
                        {sw ? 'Rudisha Chaguo-msingi' : 'Reload Defaults'}
                      </button>
                    </div>
                  </div>
                );
              })()}

              {/* ── JOIN A BUSINESS SCREEN ── */}
              {subScreen === 'joinBusiness' && (
                <div style={{ padding: '16px 14px', display: 'flex', flexDirection: 'column', gap: 14, paddingBottom: 100 }}>
                  {/* Illustration */}
                  <div style={{ textAlign: 'center', padding: '20px 20px 10px' }}>
                    <div style={{ width: 56, height: 56, borderRadius: 16, background: '#F0FDF4', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2" strokeLinecap="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>
                    </div>
                    <h2 style={{ margin: '0 0 6px', fontSize: 18, fontWeight: 800, color: 'var(--text-primary)' }}>{language === 'sw' ? 'Jiunge na Biashara' : 'Join a Business'}</h2>
                    <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>{language === 'sw' ? 'Weka kodi ya mwaliko uliyopewa ili kujiunga na timu.' : 'Enter the invite code you received to join a team.'}</p>
                  </div>

                  {/* Error */}
                  {joinError && <div style={{ padding: '10px 14px', background: '#FEF2F2', borderRadius: 10, border: '1px solid #FECACA', color: '#DC2626', fontSize: 13, fontWeight: 500 }}>{joinError}</div>}

                  {/* Code input */}
                  <div style={{ background: 'var(--bg-card)', borderRadius: 14, border: '1px solid var(--border-color)', padding: '16px' }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6, display: 'block' }}>{language === 'sw' ? 'Kodi ya Mwaliko' : 'Invite Code'}</label>
                    <input
                      type="text"
                      placeholder={language === 'sw' ? 'Bandika kodi yako hapa...' : 'Paste your invite code here...'}
                      value={joinInviteCode}
                      onChange={e => setJoinInviteCode(e.target.value)}
                      style={{ padding: '12px 14px', borderRadius: 10, border: '1px solid var(--border-color)', background: 'var(--bg-input,#F9FAFB)', color: 'var(--text-primary)', fontSize: 14, fontFamily: 'monospace', outline: 'none', width: '100%', boxSizing: 'border-box', letterSpacing: 0.5 }}
                    />
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 6 }}>{language === 'sw' ? 'Kodi hii ilitumwa kwako na mmiliki wa biashara.' : 'This code was shared with you by the business owner.'}</div>
                  </div>

                  {/* Info box */}
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 12px', background: '#F0FDF4', borderRadius: 10, border: '1px solid #BBF7D0' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2" strokeLinecap="round" style={{ marginTop: 1, flexShrink: 0 }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                    <span style={{ fontSize: 12, color: '#065F46', lineHeight: 1.5 }}>{language === 'sw' ? 'Baada ya kujiunga, utaweza kutumia biashara hiyo kama mwanachama wa timu. Jukumu lako litawekwa na mtu aliyekualika.' : 'After joining, you\'ll have access to the business as a team member. Your role is set by whoever invited you.'}</span>
                  </div>

                  {/* Submit button */}
                  <button
                    onClick={async () => {
                      if (!joinInviteCode.trim() || joinBusy) return;
                      setJoinBusy(true); setJoinError('');
                      try {
                        const joinResult = await acceptInvitation(joinInviteCode.trim(), currentUser.accessToken);
                        const joinedBizId = joinResult?.data?.business_id;
                        // Refresh business list and switch to the joined business
                        await refreshBusinesses(joinedBizId || null);
                        showAlert(language === 'sw' ? 'Umejiunga na biashara!' : 'You\'ve joined the business!', { tone: 'success' });
                        setJoinInviteCode('');
                        setSettingsScreen(null);
                      } catch (err) {
                        const msg = err?.message || '';
                        if (msg.includes('ERR_VALIDATION')) {
                          setJoinError(language === 'sw' ? 'Kodi batili au imeisha muda' : 'Invalid or expired invite code');
                        } else if (msg.includes('ERR_PERMISSION')) {
                          setJoinError(language === 'sw' ? 'Huna ruhusa' : 'Permission denied');
                        } else {
                          setJoinError(msg || (language === 'sw' ? 'Imeshindikana' : 'Failed to join'));
                        }
                      } finally { setJoinBusy(false); }
                    }}
                    disabled={joinBusy || !joinInviteCode.trim()}
                    style={{ padding: '12px', borderRadius: 12, border: 'none', background: joinBusy || !joinInviteCode.trim() ? '#94A3B8' : '#059669', color: '#fff', fontWeight: 700, fontSize: 14, cursor: joinBusy ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                    {joinBusy ? (
                      <div style={{ width: 18, height: 18, border: '2.5px solid #fff', borderTop: '2.5px solid transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>
                    )}
                    {joinBusy ? (language === 'sw' ? 'Inajiunga...' : 'Joining...') : (language === 'sw' ? 'Jiunge na Biashara' : 'Join Business')}
                  </button>
                </div>
              )}

              {/* ── USER MANUAL SCREEN ── */}
              {subScreen === 'userManual' && (
                <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 10, paddingBottom: 40 }}>
                  {/* Download PDF button */}
                  <a href={language === 'sw' ? '/faida-user-manual-sw.pdf' : '/faida-user-manual.pdf'} target="_blank" rel="noopener noreferrer"
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '10px 16px', background: 'linear-gradient(135deg,#F59E0B,#D97706)', borderRadius: 14, textDecoration: 'none', color: '#fff', fontWeight: 700, fontSize: 14, boxShadow: '0 3px 12px rgba(245,158,11,0.35)', marginBottom: 6 }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                    {language === 'sw' ? 'Pakua Mwongozo (PDF)' : 'Download Guide (PDF)'}
                  </a>
                  {[
                    { icon: '⌂', title: language === 'sw' ? 'Dashibodi' : 'Dashboard', color: '#F1F5F9', accent: '#475569',
                      body: language === 'sw'
                        ? 'Dashibodi ni muhtasari wa biashara yako. Juu utaona jumla ya Mauzo, Matumizi, Manunuzi, na Biashara Track Halisi kwa kipindi kilichochaguliwa. Chini kuna salio za akaunti (Taslimu, Benki, Pesa ya Simu), chati ya bidhaa zinazouzwa zaidi, na orodha ya shughuli za hivi karibuni. Bonyeza kadi yoyote kuona maelezo zaidi.'
                        : 'The Dashboard is your business overview. At the top you will see your total Sales, Expenses, Purchases, and Net Profit for the selected time period. Below that are your account balances (Cash, Bank, Mobile Money), a chart of your top-selling products, and a list of recent activity. Tap any card to drill down into the details.'
                    },
                    { icon: '↑', title: language === 'sw' ? 'Kurekodi Mauzo' : 'Recording a Sale', color: '#F0FDF4', accent: '#10B981',
                      body: language === 'sw'
                        ? 'Bonyeza kichupo cha Mauzo. Bonyeza kitufe cha kijani "+ Rekodi Mauzo".\n\n1. Chagua Bidhaa au Huduma.\n2. Weka Idadi na Bei ya Kuuza.\n3. Weka Jina la Mteja (au acha kama Walk-in).\n4. Chagua Njia ya Malipo: Taslimu, Benki, au Pesa ya Simu.\n5. Ongeza Punguzo (%) au Nambari ya EFD kama inahitajika.\n6. Ongeza Kodi (VAT 18% au Kodi ya Kuzuia) kama inahitajika.\n7. Bonyeza Hifadhi. Invoisi ya PDF inatengenezwa moja kwa moja.'
                        : 'Tap the Sales tab. Press the green "+ Record Sale" button.\n\n1. Select a Product or Service.\n2. Enter Quantity and Selling Price (auto-fills from your catalogue).\n3. Enter the Customer Name (or leave as Walk-in).\n4. Choose the Payment Method: Cash, Bank, or Mobile Money.\n5. Optionally add a Discount (%) or EFD Receipt Number.\n6. Optionally add Taxes (VAT 18% or Withholding Tax).\n7. Tap Save. A PDF invoice is generated automatically.'
                    },
                    { icon: '▱', title: language === 'sw' ? 'Invoisi na Proforma' : 'Invoices & Proforma', color: '#FFF7ED', accent: '#F97316',
                      body: language === 'sw'
                        ? 'Biashara Track inatengeneza invoisi ya PDF yenye nambari kwa kila mauzo (INV-0001, INV-0002, n.k.).\n\nInvoisi za Proforma ni makisio yanayotumwa kabla mauzo hayajathibitishwa -si invoisi za kodi. Nenda Mauzo → Invoisi ya Proforma kuunda moja.\n\nUsimamizi wa Invoisi unaonyesha invoisi zote zilizotolewa. Bonyeza invoisi yoyote kuona maelezo na kupakua PDF tena.'
                        : 'Biashara Track auto-generates a numbered PDF invoice for every sale (INV-0001, INV-0002, etc.).\n\nProforma Invoices are quotes sent before a sale is confirmed -they are NOT tax invoices. Go to Sales → Proforma Invoice to create one.\n\nInvoice Management shows all issued invoices. Tap any invoice to view details and re-download the PDF.'
                    },
                    { icon: '▣', title: language === 'sw' ? 'Manunuzi na Stok' : 'Purchases & Stock', color: '#FDF4FF', accent: '#6366F1',
                      body: language === 'sw'
                        ? 'Nenda kichupo cha Manunuzi kurekodi unaponunua bidhaa kutoka kwa wasambazaji. Kila ununuzi huongeza stok yako moja kwa moja.\n\nKurekebisha stok kwa mkono (k.m. bidhaa zilizoharibika), nenda Mauzo → Marekebisho ya Stok.\n\nKichupo cha Ripoti kina ripoti ya Tathmini ya Stok inayoonyesha thamani ya sasa ya bidhaa zote.'
                        : 'Go to the Purchases tab to record when you buy goods from suppliers. Each purchase increases your stock automatically.\n\nTo manually adjust stock (e.g. for damaged goods), go to Sales → Stock Adjustment.\n\nThe Reports tab includes a Stock Valuation report showing the current value of all your inventory.'
                    },
                    { icon: '↓', title: language === 'sw' ? 'Matumizi' : 'Expenses', color: '#FFF1F2', accent: '#EF4444',
                      body: language === 'sw'
                        ? 'Nenda kichupo cha Matumizi kurekodi gharama za biashara kama kodi, umeme, usafiri, na mishahara.\n\nWaweza kupanga matumizi (nenda Mipangilio → Makundi ya Matumizi kuunda makundi maalum).\n\nMatumizi hupunguza faida yako na yanaonekana katika jumla za Dashibodi.'
                        : 'Go to the Expenses tab to record business costs like rent, electricity, transport, and salaries.\n\nYou can categorise expenses (go to Setup → Expense Categories to create custom categories).\n\nExpenses reduce your profit and are reflected in the Dashboard totals.'
                    },
                    { icon: '', title: language === 'sw' ? 'Ripoti' : 'Reports', color: '#ECFDF5', accent: '#10B981',
                      body: language === 'sw'
                        ? 'Kichupo cha Ripoti kinatoa:\n• Ripoti ya Mauzo -chujua kwa tarehe, ona jumla na maelezo kwa kila kipande\n• Ripoti ya Matumizi -matumizi yote kwa kipindi\n• Biashara Track na Hasara -mapato minus gharama\n• Daftari la Akaunti -fuatilia Taslimu, Benki, na Pesa ya Simu\n• Tathmini ya Stok -thamani ya sasa ya bidhaa\n• Taarifa za Wateja -deni la kila mteja\n• Ripoti ya WHT -Kodi ya Kuzuia iliyokusanywa\n\nUnaweza kuhamisha data yote kwenye Excel kutoka Mipangilio → Hamisha Data Yote.'
                        : 'The Reports tab provides:\n• Sales Report -filter by date range, see totals and per-item breakdowns\n• Expense Report -all expenses in a period\n• Profit & Loss -revenue minus costs\n• Account Ledger -track Cash, Bank, and Mobile Money movements\n• Stock Valuation -current inventory value\n• Customer Statements -what each customer owes\n• WHT Report -Withholding Tax collected\n\nYou can export all data to Excel from Settings → Export All Data.'
                    },
                    { icon: '✻️', title: language === 'sw' ? 'Mipangilio ya Biashara' : 'Setup', color: '#F0F9FF', accent: '#0EA5E9',
                      body: language === 'sw'
                        ? 'Kichupo cha Mipangilio (ikoni ya gia juu) kina:\n• Bidhaa -ongeza, hariri, na simamia orodha ya bidhaa\n• Huduma -simamia huduma za biashara yako\n• Makundi ya Matumizi -panga matumizi yako\n• Wasambazaji -fuatilia unaoponunua kutoka kwao\n• Wateja -simamia wateja walioorodheshwa\n• Uzingatiaji na Kodi -ongeza TIN, VRN na nambari za usajili\n• Aina ya Biashara -badili kati ya Bidhaa, Huduma, au Zote\n• Sarafu -badilisha sarafu inayoonyeshwa'
                        : 'The Setup tab (gear icon at the top) contains:\n• Products -add, edit, and manage your product catalogue\n• Services -manage the services your business offers\n• Expense Categories -organise your expenses\n• Suppliers -keep track of who you buy from\n• Customers -manage registered customers for invoicing\n• Compliance & Tax -add your TIN, VRN, and registration numbers\n• Business Type -switch between Products Only, Services Only, or Both\n• Currency -change the currency displayed throughout the app'
                    },
                    { icon: '○', title: language === 'sw' ? 'Mipangilio' : 'Settings', color: '#F0FDF4', accent: '#059669',
                      body: language === 'sw'
                        ? 'Mipangilio ina mapendeleo ya akaunti na programu:\n• Lugha -Kiingereza au Kiswahili\n• Ukubwa wa Herufi -Ndogo, Wastani, au Kubwa\n• Hali ya Giza -washa mandhari ya giza\n• Usajili -angalia au boresha mpango wako\n• Hamisha Data Yote -pakua data yako kama Excel\n• Sera ya Faragha -soma jinsi data yako inavyotumiwa\n• Toka -ondoka kwenye akaunti yako'
                        : 'Settings contains your account and app preferences:\n• Language -English or Swahili\n• Font Size -Small, Medium, or Large\n• Dark Mode -toggle dark theme\n• Subscription -view or upgrade your plan\n• Export All Data -download your data as Excel\n• Privacy Policy -read how your data is used\n• Logout -sign out of your account'
                    },
                    { icon: '☁️', title: language === 'sw' ? 'Hifadhi ya Wingu na Nje ya Mtandao' : 'Cloud Sync & Offline', color: '#FFFBEB', accent: '#F59E0B',
                      body: language === 'sw'
                        ? 'Biashara Track inafanya kazi bila mtandao. Data yote inahifadhiwa kwenye kifaa chako mara moja.\n\nUnapounganika na mtandao, Biashara Track inasawazisha kwenye wingu moja kwa moja. Utaona beji ya "Imesawazishwa" katika Mipangilio.\n\nUkitumia vifaa vingi, data inasawazishwa kote kupitia akaunti moja.'
                        : 'Biashara Track works offline. All data is saved on your device instantly.\n\nWhen you connect to the internet, Biashara Track automatically syncs to the cloud in the background. You will see a "Synced" badge in Settings when sync is complete.\n\nIf you use multiple devices, data syncs across all of them through the same account.'
                    },
                  ].map((section, i) => (
                    <div key={i} style={{ background: 'var(--bg-card)', borderRadius: 16, border: `1.5px solid ${userManualOpenIdx === i ? section.accent : 'var(--border-color)'}`, overflow: 'hidden', transition: 'border-color 0.2s' }}>
                      <button onClick={() => setUserManualOpenIdx(userManualOpenIdx === i ? null : i)}
                        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: userManualOpenIdx === i ? section.color : 'var(--bg-card)', border: 'none', cursor: 'pointer', textAlign: 'left', transition: 'background 0.2s' }}>
                        <div style={{ width: 38, height: 38, borderRadius: 10, background: section.color, border: `1.5px solid ${section.accent}22`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>{section.icon}</div>
                        <span style={{ fontWeight: 700, color: userManualOpenIdx === i ? section.accent : 'var(--text-primary)', fontSize: 14, flex: 1, lineHeight: 1.3 }}>{section.title}</span>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={userManualOpenIdx === i ? section.accent : 'var(--text-secondary)'} strokeWidth="2.5" strokeLinecap="round"
                          style={{ flexShrink: 0, transform: userManualOpenIdx === i ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                          <polyline points="6 9 12 15 18 9"/>
                        </svg>
                      </button>
                      {userManualOpenIdx === i && (
                        <div style={{ padding: '4px 16px 16px', fontSize: 13.5, color: 'var(--text-secondary)', lineHeight: 1.75, whiteSpace: 'pre-line', borderTop: `1px solid ${section.accent}33` }}>{section.body}</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })()}

      </div></main>

      {/* ═══════════════════════════════════════════════════
          BUSINESS TYPE TRANSITION WARNING DIALOG
      ════════════════════════════════════════════════════ */}
      {businessTypeTransitionWarning && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', backdropFilter: 'blur(4px)' }}>
          <div style={{ width: '100%', maxWidth: 480, background: 'var(--bg-white)', borderRadius: '24px 24px 0 0', padding: '28px 24px 36px', boxShadow: '0 -8px 40px rgba(0,0,0,0.2)' }}>
            {/* Handle */}
            <div style={{ width: 40, height: 4, borderRadius: 2, background: '#CBD5E1', margin: '0 auto 24px' }} />

            {/* Icon */}
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <div style={{ width: 64, height: 64, borderRadius: 16, background: businessTypeTransitionWarning.risk === 'high' ? '#FEF2F2' : '#FFFBEB', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                {businessTypeTransitionWarning.risk === 'high' ? (
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2.5" strokeLinecap="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                ) : (
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                )}
              </div>
              <h2 style={{ fontSize: 20, fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>
                {businessTypeTransitionWarning.risk === 'high'
                  ? (language === 'sw' ? 'Taarifa Muhimu' : 'Important Notice')
                  : (language === 'sw' ? 'Tahadhari' : 'Heads Up')}
              </h2>
            </div>

            {/* Message */}
            <div style={{ background: businessTypeTransitionWarning.risk === 'high' ? '#FEF2F2' : '#FFFBEB', borderRadius: 14, padding: '10px 14px', marginBottom: 14, border: `1px solid ${businessTypeTransitionWarning.risk === 'high' ? '#FECACA' : '#FDE68A'}` }}>
              <p style={{ margin: 0, fontSize: 14, color: businessTypeTransitionWarning.risk === 'high' ? '#991B1B' : '#92400E', lineHeight: 1.6 }}>
                {businessTypeTransitionWarning.risk === 'high'
                  ? (language === 'sw'
                      ? `Kubadili hadi "${businessTypeTransitionWarning.newLabel}" kutaficha baadhi ya data yako (Mauzo, Manunuzi, au Stok). Data yako HAIFUTWA -unaweza kubadili tena wakati wowote kuiona.`
                      : `Switching to "${businessTypeTransitionWarning.newLabel}" will hide some of your existing data (Sales, Purchases, or Stock). Your data is NOT deleted -you can switch back anytime to see it again.`)
                  : (language === 'sw'
                      ? `Kubadili hadi "${businessTypeTransitionWarning.newLabel}" kutaficha data ya Huduma zako. Inabaki imehifadhiwa na itaonekana tena ukibadili kurudi.`
                      : `Switching to "${businessTypeTransitionWarning.newLabel}" will hide your Services data. It remains saved and will reappear if you switch back.`)
                }
              </p>
            </div>

            <p style={{ fontSize: 13, color: 'var(--text-secondary)', textAlign: 'center', marginBottom: 20 }}>
              {language === 'sw' ? 'Je, unataka kuendelea?' : 'Do you want to continue?'}
            </p>

            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={() => setBusinessTypeTransitionWarning(null)}
                style={{ flex: 1, padding: '14px', borderRadius: 14, background: 'var(--bg-tab)', color: 'var(--text-primary)', border: 'none', fontWeight: 700, fontSize: 15, cursor: 'pointer' }}>
                {language === 'sw' ? 'Ghairi' : 'Cancel'}
              </button>
              <button onClick={() => {
                const { newType } = businessTypeTransitionWarning;
                const p = currentUser ? `faida_${currentUser.uid}_` : 'faida_';
                setBusinessType(newType);
                localStorage.setItem(p + 'businessType', newType);
                localStorage.setItem(p + 'businessTypeUpdatedAt', new Date().toISOString());
                setBusinessTypeTransitionWarning(null);
              }}
                style={{ flex: 1, padding: '14px', borderRadius: 14, background: businessTypeTransitionWarning.risk === 'high' ? '#DC2626' : '#111827', color: '#fff', border: 'none', fontWeight: 700, fontSize: 15, cursor: 'pointer' }}>
                {language === 'sw' ? 'Ndiyo, Badili' : 'Yes, Switch'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════
          PRIVACY MODE PIN MODAL -unlock / setup / change / disable / recover
      ════════════════════════════════════════════════════ */}
      {showPinModal && (() => {
        const sw = language === 'sw';
        const mode = showPinModal; // 'unlock' | 'setup' | 'change' | 'disable' | 'recover'
        const isSetup = mode === 'setup';
        const isChange = mode === 'change';
        const inLockout = privacyLockoutUntil > Date.now();
        const lockoutMinsRemaining = Math.ceil((privacyLockoutUntil - Date.now()) / 60000);

        // NOTE: input state is hoisted to App scope (pmStep, pmPin, pmPin2, pmOldPin,
        // pmRecoveryInput, pmOtpInput, pmError, pmBusy) via the reset useEffect above.
        // Aliased here for readability so we don't have to rename every reference below.
        const step = pmStep;        const setStep = setPmStep;
        const pin = pmPin;          const setPin = setPmPin;
        const pin2 = pmPin2;        const setPin2 = setPmPin2;
        const oldPin = pmOldPin;    const setOldPin = setPmOldPin;
        const recoveryInput = pmRecoveryInput; const setRecoveryInput = setPmRecoveryInput;
        const passwordInput = pmOtpInput; const setPasswordInput = setPmOtpInput;
        const error = pmError;      const setError = setPmError;
        const busy = pmBusy;        const setBusy = setPmBusy;

        const close = () => { setShowPinModal(null); };

          const handleWrongPin = async () => {
            const nextAttempts = privacyPinAttempts + 1;
            setPrivacyPinAttempts(nextAttempts);
            if (nextAttempts >= 3) {
              // 5-minute cooldown
              setPrivacyLockoutUntil(Date.now() + 5 * 60 * 1000);
              setPrivacyPinAttempts(0);
              setError(sw ? 'Umeingiza PIN vibaya mara 3. Jaribu tena baada ya dakika 5.' : 'Wrong PIN 3 times. Try again in 5 minutes.');
            } else {
              setError(sw ? `PIN si sahihi. Umebaki majaribio ${3 - nextAttempts}.` : `Wrong PIN. ${3 - nextAttempts} attempts left.`);
            }
          };

          const submitUnlock = async () => {
            if (inLockout) return;
            if (pin.length !== 4) { setError(sw ? 'PIN lazima iwe tarakimu 4.' : 'PIN must be 4 digits.'); return; }
            setBusy(true);
            const h = await sha256Hex(pin);
            setBusy(false);
            if (h === privacyPinHash) {
              setPrivacyUnlocked(true);
              privacyLastActivityRef.current = Date.now();
              setPrivacyPinAttempts(0);
              close();
            } else {
              handleWrongPin();
              setPin('');
            }
          };

          const submitDisable = async () => {
            if (inLockout) return;
            if (pin.length !== 4) { setError(sw ? 'PIN lazima iwe tarakimu 4.' : 'PIN must be 4 digits.'); return; }
            setBusy(true);
            const h = await sha256Hex(pin);
            setBusy(false);
            if (h === privacyPinHash) {
              setPrivacyEnabled(false);
              setPrivacyPinHash('');
              setPrivacyRecoveryCodeHash('');
              setPrivacyUnlocked(false);
              setPrivacyPinAttempts(0);
              close();
            } else {
              handleWrongPin();
              setPin('');
            }
          };

          // Regenerate recovery code: verify current PIN, rotate the code, show new one
          const submitRegenCode = async () => {
            if (inLockout) return;
            if (pin.length !== 4) { setError(sw ? 'PIN lazima iwe tarakimu 4.' : 'PIN must be 4 digits.'); return; }
            setBusy(true);
            const h = await sha256Hex(pin);
            if (h !== privacyPinHash) {
              setBusy(false);
              handleWrongPin();
              setPin('');
              return;
            }
            const newRecovery = generateRecoveryCode();
            const newRecoveryHash = await sha256Hex(newRecovery);
            setPrivacyRecoveryCodeHash(newRecoveryHash);
            setShowRecoveryCodeOnce(newRecovery);
            setPrivacyPinAttempts(0);
            setBusy(false);
            close();
          };

          const submitSetup = async () => {
            if (pin.length !== 4 || pin2.length !== 4) { setError(sw ? 'PIN lazima iwe tarakimu 4.' : 'PIN must be 4 digits.'); return; }
            if (pin !== pin2) { setError(sw ? 'PIN mbili hazifanani.' : 'PINs do not match.'); return; }
            if (!/^\d{4}$/.test(pin)) { setError(sw ? 'Tumia tarakimu tu.' : 'Digits only.'); return; }
            setBusy(true);
            const hash = await sha256Hex(pin);
            const recoveryCode = generateRecoveryCode();
            const recoveryHash = await sha256Hex(recoveryCode);
            setPrivacyPinHash(hash);
            setPrivacyRecoveryCodeHash(recoveryHash);
            setShowRecoveryCodeOnce(recoveryCode);
            setBusy(false);
            close();
          };

          const submitChange = async () => {
            if (oldPin.length !== 4) { setError(sw ? 'Ingiza PIN ya sasa.' : 'Enter current PIN.'); return; }
            setBusy(true);
            const oldHash = await sha256Hex(oldPin);
            if (oldHash !== privacyPinHash) { setBusy(false); handleWrongPin(); setOldPin(''); return; }
            if (pin.length !== 4 || pin2.length !== 4) { setBusy(false); setError(sw ? 'PIN mpya lazima iwe tarakimu 4.' : 'New PIN must be 4 digits.'); return; }
            if (pin !== pin2) { setBusy(false); setError(sw ? 'PIN mbili hazifanani.' : 'PINs do not match.'); return; }
            if (!/^\d{4}$/.test(pin)) { setBusy(false); setError(sw ? 'Tumia tarakimu tu.' : 'Digits only.'); return; }
            const newHash = await sha256Hex(pin);
            setPrivacyPinHash(newHash);
            // Also rotate the recovery code -every PIN change gets a fresh code
            const newRecovery = generateRecoveryCode();
            const newRecoveryHash = await sha256Hex(newRecovery);
            setPrivacyRecoveryCodeHash(newRecoveryHash);
            setShowRecoveryCodeOnce(newRecovery);
            setPrivacyPinAttempts(0);
            setBusy(false);
            close();
          };

          const submitRecovery = async () => {
            const normalized = recoveryInput.trim().toUpperCase();
            if (!normalized) { setError(sw ? 'Ingiza msimbo wa uokoaji.' : 'Enter your recovery code.'); return; }
            setBusy(true);
            const h = await sha256Hex(normalized);
            setBusy(false);
            if (h !== privacyRecoveryCodeHash) {
              setError(sw ? 'Msimbo wa uokoaji si sahihi.' : 'Recovery code is not correct.');
              return;
            }
            // Recovery valid → jump to new-PIN step
            setStep('recoveryNewPin');
            setError('');
            setRecoveryInput('');
          };

          // Alt recovery path: email OTP. Send a 6-digit code to the owner's
          // account email -cashier sharing the login doesn't have inbox access,
          // so this proves the person resetting the PIN really is the owner.
          const sendOtp = async () => {
            const email = currentUser?.email;
            if (!email) { setError(sw ? 'Barua pepe ya akaunti haipatikani.' : 'Account email not available.'); return; }
            setBusy(true);
            try {
              await sendOtpToEmail(email);
              setBusy(false);
              setError('');
              setStep('emailOtpVerify');
            } catch (err) {
              setBusy(false);
              setError(sw ? 'Kushindwa kutuma msimbo. Angalia mtandao na jaribu tena.' : 'Failed to send code. Check your connection and try again.');
            }
          };
          const submitOtpVerify = async () => {
            const email = currentUser?.email;
            if (!email) { setError(sw ? 'Barua pepe ya akaunti haipatikani.' : 'Account email not available.'); return; }
            // Accept 6–10 digits: the code length is a Supabase dashboard
            // setting, so the app must tolerate whatever length arrives
            // (an 8-digit code once locked users out of a 6-digit input).
            if (!passwordInput || passwordInput.length < 6) {
              setError(sw ? 'Ingiza msimbo kamili kutoka kwa barua pepe yako.' : 'Enter the complete code from your email.');
              return;
            }
            setBusy(true);
            try {
              await verifyEmailOtp(email, passwordInput);
              setBusy(false);
              setPasswordInput('');
              setError('');
              // Jump to new-PIN step (reuses recoveryNewPin UI)
              setStep('recoveryNewPin');
            } catch (err) {
              setBusy(false);
              setError(sw ? 'Msimbo si sahihi au umepitwa na muda. Omba mwingine.' : 'Code is wrong or expired. Request a new one.');
            }
          };

          const submitRecoveryNewPin = async () => {
            if (pin.length !== 4 || pin2.length !== 4) { setError(sw ? 'PIN lazima iwe tarakimu 4.' : 'PIN must be 4 digits.'); return; }
            if (pin !== pin2) { setError(sw ? 'PIN mbili hazifanani.' : 'PINs do not match.'); return; }
            if (!/^\d{4}$/.test(pin)) { setError(sw ? 'Tumia tarakimu tu.' : 'Digits only.'); return; }
            setBusy(true);
            const newHash = await sha256Hex(pin);
            // Also rotate the recovery code -the old one is spent
            const newRecovery = generateRecoveryCode();
            const newRecoveryHash = await sha256Hex(newRecovery);
            setPrivacyPinHash(newHash);
            setPrivacyRecoveryCodeHash(newRecoveryHash);
            setShowRecoveryCodeOnce(newRecovery);
            setPrivacyPinAttempts(0);
            setPrivacyLockoutUntil(0);
            setBusy(false);
            close();
          };

          const titles = {
            unlock: sw ? 'Ingiza PIN kufungua' : 'Enter PIN to unlock',
            setup: sw ? 'Weka PIN yako ya Faragha' : 'Set your Privacy PIN',
            change: sw ? 'Badilisha PIN' : 'Change PIN',
            disable: sw ? 'Zima Hali ya Faragha' : 'Turn OFF Privacy Mode',
            recover: sw ? 'Sahau PIN' : 'Forgot PIN',
            regenCode: sw ? 'Pata msimbo mpya wa uokoaji' : 'Get new recovery code'
          };

          const pinInput = (val, setVal, placeholder, autoFocus) => (
            <input
              type="password"
              inputMode="numeric"
              maxLength={4}
              autoFocus={autoFocus}
              value={val}
              onChange={e => { const v = e.target.value.replace(/\D/g, '').slice(0, 4); setVal(v); if (error) setError(''); }}
              placeholder={placeholder}
              style={{ width: '100%', padding: '14px 16px', fontSize: 20, letterSpacing: 8, textAlign: 'center', borderRadius: 12, border: '1.5px solid #CBD5E1', outline: 'none', fontWeight: 700, boxSizing: 'border-box' }}
            />
          );

          return (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, backdropFilter: 'blur(4px)' }}>
              <div style={{ width: '100%', maxWidth: 400, background: 'var(--bg-white)', borderRadius: 20, padding: '24px 22px 22px', boxShadow: '0 8px 32px rgba(0,0,0,0.28)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
                  <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: 'var(--text-primary)' }}>{titles[mode]}</h3>
                  <button onClick={close} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: 'var(--text-secondary)' }}>×</button>
                </div>

                {/* Lockout notice */}
                {inLockout && (mode === 'unlock' || mode === 'disable') && (
                  <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 10, padding: '10px 12px', marginBottom: 14, color: '#B91C1C', fontSize: 12.5, fontWeight: 600 }}>
                    {sw ? `Umezuiwa kwa muda. Subiri dakika ${lockoutMinsRemaining} zaidi.` : `Locked out. Wait ${lockoutMinsRemaining} more min.`}
                  </div>
                )}

                {/* UNLOCK */}
                {mode === 'unlock' && (
                  <>
                    <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 14px' }}>
                      {sw ? 'Ingiza PIN yako ya tarakimu 4 kufungua vitu vilivyofichwa kwenye kifaa hiki.' : 'Enter your 4-digit PIN to unlock hidden items on this device.'}
                    </p>
                    {pinInput(pin, setPin, '••••', true)}
                    {error && <div style={{ color: '#B91C1C', fontSize: 12.5, marginTop: 8, fontWeight: 600 }}>{error}</div>}
                    <button onClick={submitUnlock} disabled={busy || inLockout}
                      style={{ width: '100%', marginTop: 14, padding: '13px', borderRadius: 12, background: inLockout ? '#94A3B8' : '#18222F', color: '#fff', border: 'none', fontWeight: 700, fontSize: 15, cursor: inLockout ? 'not-allowed' : 'pointer' }}>
                      {sw ? 'Fungua' : 'Unlock'}
                    </button>
                    <button onClick={() => setShowPinModal('recover')}
                      style={{ width: '100%', marginTop: 8, padding: '10px', borderRadius: 10, background: 'none', color: '#4F46E5', border: 'none', fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>
                      {sw ? 'Sahau PIN?' : 'Forgot PIN?'}
                    </button>
                  </>
                )}

                {/* SETUP */}
                {mode === 'setup' && (
                  <>
                    <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 14px' }}>
                      {sw ? 'Chagua PIN ya tarakimu 4. Utaihitaji ili kufungua vitu vilivyofichwa.' : 'Choose a 4-digit PIN. You will need it to unlock hidden items.'}
                    </p>
                    <div style={{ marginBottom: 12 }}>{pinInput(pin, setPin, sw ? 'PIN mpya' : 'New PIN', true)}</div>
                    <div>{pinInput(pin2, setPin2, sw ? 'Thibitisha PIN' : 'Confirm PIN', false)}</div>
                    {error && <div style={{ color: '#B91C1C', fontSize: 12.5, marginTop: 8, fontWeight: 600 }}>{error}</div>}
                    <button onClick={submitSetup} disabled={busy}
                      style={{ width: '100%', marginTop: 14, padding: '13px', borderRadius: 12, background: '#18222F', color: '#fff', border: 'none', fontWeight: 700, fontSize: 15, cursor: 'pointer' }}>
                      {sw ? 'Hifadhi PIN' : 'Save PIN'}
                    </button>
                    <div style={{ marginTop: 12, padding: '10px 12px', background: '#FEF9C3', border: '1px solid #FDE68A', borderRadius: 10, fontSize: 12, color: '#78350F', lineHeight: 1.5 }}>
                      {sw ? 'Baada ya kuhifadhi tutakupa msimbo wa uokoaji wa mara moja. Uandike na uweke mahali salama.' : 'After saving, you\'ll get a one-time recovery code. Write it down and keep it safe.'}
                    </div>
                  </>
                )}

                {/* CHANGE */}
                {mode === 'change' && (
                  <>
                    <div style={{ marginBottom: 12 }}>{pinInput(oldPin, setOldPin, sw ? 'PIN ya sasa' : 'Current PIN', true)}</div>
                    <div style={{ marginBottom: 12 }}>{pinInput(pin, setPin, sw ? 'PIN mpya' : 'New PIN', false)}</div>
                    <div>{pinInput(pin2, setPin2, sw ? 'Thibitisha PIN mpya' : 'Confirm new PIN', false)}</div>
                    {error && <div style={{ color: '#B91C1C', fontSize: 12.5, marginTop: 8, fontWeight: 600 }}>{error}</div>}
                    <button onClick={submitChange} disabled={busy || inLockout}
                      style={{ width: '100%', marginTop: 14, padding: '13px', borderRadius: 12, background: inLockout ? '#94A3B8' : '#18222F', color: '#fff', border: 'none', fontWeight: 700, fontSize: 15, cursor: inLockout ? 'not-allowed' : 'pointer' }}>
                      {sw ? 'Badilisha PIN' : 'Change PIN'}
                    </button>
                  </>
                )}

                {/* DISABLE */}
                {mode === 'disable' && (
                  <>
                    <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 14px' }}>
                      {sw ? 'Ingiza PIN kuzima Hali ya Faragha kabisa. Mipangilio yako yote ya uficho itafutwa.' : 'Enter your PIN to disable Privacy Mode entirely. All your hiding settings will be cleared.'}
                    </p>
                    {pinInput(pin, setPin, sw ? 'PIN yako' : 'Your PIN', true)}
                    {error && <div style={{ color: '#B91C1C', fontSize: 12.5, marginTop: 8, fontWeight: 600 }}>{error}</div>}
                    <button onClick={submitDisable} disabled={busy || inLockout}
                      style={{ width: '100%', marginTop: 14, padding: '13px', borderRadius: 12, background: inLockout ? '#94A3B8' : '#DC2626', color: '#fff', border: 'none', fontWeight: 700, fontSize: 15, cursor: inLockout ? 'not-allowed' : 'pointer' }}>
                      {sw ? 'Zima Hali ya Faragha' : 'Turn OFF Privacy Mode'}
                    </button>
                  </>
                )}

                {/* REGENERATE recovery code -verify PIN, then rotate the code */}
                {mode === 'regenCode' && (
                  <>
                    <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 14px' }}>
                      {sw
                        ? 'Ingiza PIN kupata msimbo mpya wa uokoaji. Msimbo wa zamani utabatilishwa.'
                        : 'Enter your PIN to get a new recovery code. The old code will be invalidated.'}
                    </p>
                    {pinInput(pin, setPin, sw ? 'PIN yako' : 'Your PIN', true)}
                    {error && <div style={{ color: '#B91C1C', fontSize: 12.5, marginTop: 8, fontWeight: 600 }}>{error}</div>}
                    <button onClick={submitRegenCode} disabled={busy || inLockout}
                      style={{ width: '100%', marginTop: 14, padding: '13px', borderRadius: 12, background: inLockout ? '#94A3B8' : '#166534', color: '#fff', border: 'none', fontWeight: 700, fontSize: 15, cursor: inLockout ? 'not-allowed' : 'pointer' }}>
                      {sw ? 'Tengeneza msimbo mpya' : 'Generate new code'}
                    </button>
                  </>
                )}

                {/* RECOVER -two-path chooser */}
                {mode === 'recover' && step === 'chooseMethod' && (
                  <>
                    <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 14px' }}>
                      {sw ? 'Chagua jinsi ya kuweka PIN mpya:' : 'Choose how to reset your PIN:'}
                    </p>
                    <button onClick={() => { setStep('recoveryCode'); setError(''); }}
                      style={{ width: '100%', padding: '14px 16px', borderRadius: 12, background: 'var(--bg-white)', border: '2px solid #18222F', textAlign: 'left', cursor: 'pointer', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span style={{ width: 34, height: 34, borderRadius: 8, background: 'var(--bg-input)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#334155" strokeWidth="2.4" strokeLinecap="round"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M8 2v4M16 2v4M3 10h18"/></svg>
                      </span>
                      <span style={{ minWidth: 0 }}>
                        <span style={{ display: 'block', fontSize: 13, fontWeight: 800, color: 'var(--text-primary)' }}>{sw ? 'Msimbo wa Uokoaji' : 'Recovery code'}</span>
                        <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-secondary)', marginTop: 2 }}>{sw ? 'Msimbo wa TRACK-XXXX-XXXX ulioupata ulipoweka PIN' : 'The TRACK-XXXX-XXXX code from when you set your PIN'}</span>
                      </span>
                    </button>
                    <button onClick={() => { setStep('emailOtpSend'); setError(''); }}
                      style={{ width: '100%', padding: '14px 16px', borderRadius: 12, background: 'var(--bg-white)', border: '2px solid #18222F', textAlign: 'left', cursor: 'pointer', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span style={{ width: 34, height: 34, borderRadius: 8, background: '#DCFCE7', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#166534" strokeWidth="2.4" strokeLinecap="round"><path d="M4 4h16v16H4z"/><polyline points="4 4 12 13 20 4"/></svg>
                      </span>
                      <span style={{ minWidth: 0 }}>
                        <span style={{ display: 'block', fontSize: 13, fontWeight: 800, color: 'var(--text-primary)' }}>{sw ? 'Tuma msimbo kwenye barua pepe' : 'Send code to email'}</span>
                        <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-secondary)', marginTop: 2, wordBreak: 'break-word' }}>{sw ? `Msimbo wa uthibitisho utatumwa kwa ${currentUser?.email || 'barua pepe yako'}` : `A verification code will be sent to ${currentUser?.email || 'your email'}`}</span>
                      </span>
                    </button>
                    <div style={{ marginTop: 12, fontSize: 11.5, color: 'var(--text-secondary)', textAlign: 'center', lineHeight: 1.5 }}>
                      {sw ? 'Chagua unayoipata sahali' : 'Pick whichever is easier for you'}
                    </div>
                  </>
                )}

                {/* RECOVER -path A: enter recovery code */}
                {mode === 'recover' && step === 'recoveryCode' && (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '-6px 0 12px', color: '#4F46E5', fontSize: 12, fontWeight: 600, cursor: 'pointer' }} onClick={() => { setStep('chooseMethod'); setError(''); }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4F46E5" strokeWidth="2.4" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
                      {sw ? 'Rudi' : 'Back'}
                    </div>
                    <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 14px' }}>
                      {sw ? 'Ingiza msimbo wa uokoaji uliopewa wakati ulipowekea PIN.' : 'Enter the recovery code you received when you set your PIN.'}
                    </p>
                    <input
                      type="text"
                      value={recoveryInput}
                      onChange={e => { setRecoveryInput(e.target.value.toUpperCase()); if (error) setError(''); }}
                      placeholder="TRACK-XXXX-XXXX"
                      autoFocus
                      style={{ width: '100%', padding: '13px 14px', fontSize: 14, letterSpacing: 1.5, textAlign: 'center', borderRadius: 12, border: '1.5px solid #CBD5E1', outline: 'none', fontWeight: 700, boxSizing: 'border-box' }}
                    />
                    {error && <div style={{ color: '#B91C1C', fontSize: 12.5, marginTop: 8, fontWeight: 600 }}>{error}</div>}
                    <button onClick={submitRecovery} disabled={busy}
                      style={{ width: '100%', marginTop: 14, padding: '13px', borderRadius: 12, background: '#18222F', color: '#fff', border: 'none', fontWeight: 700, fontSize: 15, cursor: 'pointer' }}>
                      {sw ? 'Endelea' : 'Continue'}
                    </button>
                  </>
                )}

                {/* RECOVER -path B step 1: confirm & send OTP */}
                {mode === 'recover' && step === 'emailOtpSend' && (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '-6px 0 12px', color: '#4F46E5', fontSize: 12, fontWeight: 600, cursor: 'pointer' }} onClick={() => { setStep('chooseMethod'); setError(''); }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4F46E5" strokeWidth="2.4" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
                      {sw ? 'Rudi' : 'Back'}
                    </div>
                    <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 12px', lineHeight: 1.55 }}>
                      {sw
                        ? 'Tutatuma msimbo wa uthibitisho kwa barua pepe hii:'
                        : "We'll send a verification code to this email:"}
                    </p>
                    <div style={{ background: 'var(--bg-input)', border: '1px solid #CBD5E1', borderRadius: 10, padding: '10px 14px', marginBottom: 14, wordBreak: 'break-word' }}>
                      <div style={{ fontSize: 13, fontWeight: 800, color: '#0F172A' }}>{currentUser?.email || '—'}</div>
                    </div>
                    {error && <div style={{ color: '#B91C1C', fontSize: 12.5, marginTop: 8, marginBottom: 6, fontWeight: 600 }}>{error}</div>}
                    <button onClick={sendOtp} disabled={busy || !currentUser?.email}
                      style={{ width: '100%', padding: '13px', borderRadius: 12, background: '#166534', color: '#fff', border: 'none', fontWeight: 700, fontSize: 15, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.7 : 1 }}>
                      {busy ? (sw ? 'Inatuma…' : 'Sending…') : (sw ? 'Tuma msimbo' : 'Send code')}
                    </button>
                    <div style={{ marginTop: 10, fontSize: 11, color: 'var(--text-secondary)', textAlign: 'center', lineHeight: 1.5 }}>
                      {sw ? 'Ni yule tu mwenye kupata barua pepe hii ndiye anayeweza kubadili PIN.' : 'Only whoever can read this inbox can reset the PIN.'}
                    </div>
                  </>
                )}

                {/* RECOVER -path B step 2: verify 6-digit code */}
                {mode === 'recover' && step === 'emailOtpVerify' && (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '-6px 0 12px', color: '#4F46E5', fontSize: 12, fontWeight: 600, cursor: 'pointer' }} onClick={() => { setStep('emailOtpSend'); setError(''); setPasswordInput(''); }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4F46E5" strokeWidth="2.4" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
                      {sw ? 'Rudi' : 'Back'}
                    </div>
                    <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 14px', lineHeight: 1.55 }}>
                      {sw
                        ? `Angalia barua pepe ya ${currentUser?.email} na uingize msimbo uliotumwa.`
                        : `Check ${currentUser?.email} and enter the code we sent.`}
                    </p>
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={10}
                      value={passwordInput}
                      onChange={e => { setPasswordInput(e.target.value.replace(/\D/g, '').slice(0, 10)); if (error) setError(''); }}
                      placeholder="——————"
                      autoFocus
                      style={{ width: '100%', padding: '14px 16px', fontSize: 22, letterSpacing: 8, textAlign: 'center', borderRadius: 12, border: '1.5px solid #CBD5E1', outline: 'none', fontWeight: 700, boxSizing: 'border-box' }}
                    />
                    {error && <div style={{ color: '#B91C1C', fontSize: 12.5, marginTop: 8, fontWeight: 600 }}>{error}</div>}
                    <button onClick={submitOtpVerify} disabled={busy}
                      style={{ width: '100%', marginTop: 14, padding: '13px', borderRadius: 12, background: '#166534', color: '#fff', border: 'none', fontWeight: 700, fontSize: 15, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.7 : 1 }}>
                      {busy ? (sw ? 'Inathibitisha…' : 'Verifying…') : (sw ? 'Thibitisha' : 'Verify')}
                    </button>
                    <button onClick={sendOtp} disabled={busy}
                      style={{ width: '100%', marginTop: 8, padding: '10px', borderRadius: 10, background: 'transparent', color: '#4F46E5', border: 'none', fontWeight: 700, fontSize: 12.5, cursor: 'pointer' }}>
                      {sw ? 'Tuma msimbo mwingine' : 'Send another code'}
                    </button>
                  </>
                )}

                {/* RECOVER -new PIN step */}
                {mode === 'recover' && step === 'recoveryNewPin' && (
                  <>
                    <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 14px' }}>
                      {sw ? 'Msimbo umethibitishwa. Weka PIN mpya.' : 'Code verified. Set your new PIN.'}
                    </p>
                    <div style={{ marginBottom: 12 }}>{pinInput(pin, setPin, sw ? 'PIN mpya' : 'New PIN', true)}</div>
                    <div>{pinInput(pin2, setPin2, sw ? 'Thibitisha' : 'Confirm', false)}</div>
                    {error && <div style={{ color: '#B91C1C', fontSize: 12.5, marginTop: 8, fontWeight: 600 }}>{error}</div>}
                    <button onClick={submitRecoveryNewPin} disabled={busy}
                      style={{ width: '100%', marginTop: 14, padding: '13px', borderRadius: 12, background: '#18222F', color: '#fff', border: 'none', fontWeight: 700, fontSize: 15, cursor: 'pointer' }}>
                      {sw ? 'Hifadhi' : 'Save new PIN'}
                    </button>
                  </>
                )}
              </div>
            </div>
          );
      })()}

      {/* Recovery code shown ONCE after PIN setup (or after recovery-driven PIN reset) */}
      {showRecoveryCodeOnce && (() => {
        const sw = language === 'sw';
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 9600, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, backdropFilter: 'blur(4px)' }}>
            <div style={{ width: '100%', maxWidth: 420, background: 'var(--bg-white)', borderRadius: 20, padding: '24px 22px 22px', boxShadow: '0 8px 32px rgba(0,0,0,0.3)' }}>
              <div style={{ fontSize: 42, textAlign: 'center', marginBottom: 4 }}>🔐</div>
              <h3 style={{ margin: '0 0 8px', fontSize: 17, fontWeight: 800, color: 'var(--text-primary)', textAlign: 'center' }}>
                {sw ? 'Msimbo wako wa Uokoaji' : 'Your Recovery Code'}
              </h3>
              <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', margin: '0 0 14px', textAlign: 'center', lineHeight: 1.55 }}>
                {sw ? 'Uandike na uweke mahali salama. Utahitaji msimbo huu kama utasahau PIN yako.' : 'Write this down and keep it safe. You\'ll need it if you forget your PIN.'}
              </p>
              <div style={{ background: 'var(--bg-input)', border: '1.5px dashed #94A3B8', borderRadius: 12, padding: '18px', textAlign: 'center', marginBottom: 14 }}>
                <div style={{ fontSize: 20, fontWeight: 900, letterSpacing: 2, fontFamily: 'monospace', color: '#0F172A' }}>{showRecoveryCodeOnce}</div>
              </div>
              <button onClick={() => { navigator.clipboard?.writeText(showRecoveryCodeOnce).catch(() => {}); }}
                style={{ width: '100%', padding: '10px', borderRadius: 10, background: 'var(--bg-input)', color: 'var(--text-secondary)', border: '1px solid #CBD5E1', fontWeight: 700, fontSize: 13, cursor: 'pointer', marginBottom: 8 }}>
                {sw ? 'Nakili' : 'Copy'}
              </button>
              <button onClick={() => setShowRecoveryCodeOnce('')}
                style={{ width: '100%', padding: '13px', borderRadius: 12, background: '#18222F', color: '#fff', border: 'none', fontWeight: 700, fontSize: 15, cursor: 'pointer' }}>
                {sw ? 'Nimeukumbuka' : 'I\'ve saved it'}
              </button>
              <div style={{ marginTop: 10, padding: '10px 12px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 10, fontSize: 11.5, color: '#991B1B', lineHeight: 1.5 }}>
                {sw ? 'Msimbo huu utaonyeshwa mara moja tu. Ukiupoteza utahitaji kutumia barua pepe kupata upya.' : 'This code is shown ONCE. If you lose it, use email recovery to reset.'}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── STOCK VALUATION FULL SCREEN (outermost level -covers everything) ── */}
      {showStockValuation && (() => {
        const rows = (products || []).map(p => {
          const qty = p.balance ?? p.currentStock ?? p.openingStockQty ?? 0;
          const wacUnit = getProductWAC(p.productName);
          const cost = wacUnit > 0 ? wacUnit : parseFloat(p.costPrice || (p.openingStockValue && qty > 0 ? (p.openingStockValue / Math.max(1, p.openingStockQty || 1)) : 0) || 0);
          const sellingPrice = parseFloat(p.sellingPrice || 0);
          return { name: p.productName, qty, cost, sellingPrice, totalCostVal: qty * cost, totalSellingVal: qty * sellingPrice };
        });
        const totalCostAll = rows.reduce((s, r) => s + r.totalCostVal, 0);
        const totalSellAll = rows.reduce((s, r) => s + r.totalSellingVal, 0);
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'var(--bg-tab)', zIndex: 9999, display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
            {/* Sticky header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: 'var(--bg-white)', borderBottom: '1px solid var(--border-default)', position: 'sticky', top: 0, zIndex: 10 }}>
              <button className="back-btn" onClick={() => setShowStockValuation(false)}>{t.back || '← Back'}</button>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>{t.stockValuation || 'Stock Valuation'}</h3>
              <div style={{ width: 56 }} />
            </div>
            {/* Content */}
            <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: 12, paddingBottom: 40 }}>
              {/* Summary cards */}
              <div style={{ display: 'flex', gap: 10 }}>
                <div style={{ flex: 1, background: 'var(--bg-input)', borderRadius: 14, padding: '8px 14px', textAlign: 'center' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.3 }}>{t.totalCostValue || 'Total Cost Value'}</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-secondary)' }}>{fmt(totalCostAll)}</div>
                </div>
                <div style={{ flex: 1, background: '#D1FAE5', borderRadius: 14, padding: '8px 14px', textAlign: 'center' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#065F46', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.3 }}>{t.totalSellingValue || 'Total Selling Value'}</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: '#065F46' }}>{fmt(totalSellAll)}</div>
                </div>
              </div>
              {/* Column headers */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 52px 1fr 1fr', background: '#111827', borderRadius: '12px 12px 0 0', padding: '10px 14px' }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#A3E635' }}>{t.product || 'Product'}</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#A3E635', textAlign: 'center' }}>{t.qty || 'Qty'}</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#A3E635', textAlign: 'right' }}>{t.costPerUnit || 'Cost/Unit'}</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#A3E635', textAlign: 'right' }}>{t.sellPerUnit || 'Sell/Unit'}</span>
              </div>
              {/* Product rows */}
              {rows.length === 0 ? (
                <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-secondary)', background: 'var(--bg-card)', borderRadius: '0 0 12px 12px' }}>{t.noProductsFound || 'No products found'}</div>
              ) : rows.map((r, i) => (
                <div key={r.name} style={{ background: 'var(--bg-card)', borderRadius: i === rows.length - 1 ? '0 0 12px 12px' : 0, borderTop: i === 0 ? 'none' : '1px solid var(--border-color)', marginTop: i === 0 ? -12 : 0 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 52px 1fr 1fr', padding: '11px 14px 5px' }}>
                    <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: 6 }}>{r.name}</span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', textAlign: 'center' }}>{r.qty}</span>
                    <span style={{ fontSize: 13, color: 'var(--text-secondary)', textAlign: 'right' }}>{fmt(r.cost)}</span>
                    <span style={{ fontSize: 13, color: 'var(--text-secondary)', textAlign: 'right' }}>{fmt(r.sellingPrice)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '0 14px 10px' }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', background: 'var(--bg-input)', borderRadius: 6, padding: '3px 8px' }}>{t.costValue || 'Cost'}: {fmt(r.totalCostVal)}</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#065F46', background: '#D1FAE5', borderRadius: 6, padding: '3px 8px' }}>{t.sellValue || 'Sell'}: {fmt(r.totalSellingVal)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* ── STOCK REPORT FULL SCREEN -with download options ── */}
      {showStockReport && (() => {
        // ── Weighted Average Cost (WAC) -IFRS-compliant valuation ──
        // For each product: sum all purchase line totals ÷ sum of all purchase qty.
        // Falls back to the legacy costPrice / openingStock fields when no purchase
        // history exists yet.
        const normSR = (s) => (s || '').trim().toLowerCase().replace(/\s+/g, ' ');
        // Unit cost = the perpetual moving-average WAC (shared source of truth);
        // this helper just also reports the last purchase date for display.
        const computeWAC = (productName) => {
          const tgt = normSR(productName);
          let lastPurchaseDate = null;
          (purchases || []).forEach(p => {
            const items = Array.isArray(p.items) && p.items.length > 0
              ? p.items
              : [{ productName: p.productName }];
            if (items.some(it => normSR(it.productName || '') === tgt)) {
              if (!lastPurchaseDate || (p.date && p.date > lastPurchaseDate)) lastPurchaseDate = p.date;
            }
          });
          return { wac: getProductWAC(productName), lastPurchaseDate };
        };
        const stockData = (products || [])
          .filter(p => (p.balance ?? p.currentStock ?? p.openingStockQty ?? 0) > 0)
          .map(p => {
            const qty = p.balance ?? p.currentStock ?? p.openingStockQty ?? 0;
            const wacResult = computeWAC(p.productName);
            // Use WAC if we have purchase history; otherwise fall back to opening cost
            const unitCostRaw = wacResult.wac > 0
              ? wacResult.wac
              : parseFloat(p.costPrice || (parseFloat(p.openingStockValue || 0) / Math.max(1, parseFloat(p.openingStockQty || 1))) || 0);
            const unitCost = Math.round(unitCostRaw); // whole shillings -avoids 1,392,857.143-style display
            return {
              productName: p.productName,
              category: p.category || '—',
              quantity: qty,
              unitCost,
              totalValue: qty * unitCost,
              lastPurchase: wacResult.lastPurchaseDate || '—'
            };
          });

        const headers = ['Product Name', 'Category', 'Stock Qty', 'Unit Cost (WAC)', 'Total Value'];
        const rows = stockData.map(s => [s.productName, s.category, s.quantity, parseFloat(s.unitCost).toFixed(2), parseFloat(s.totalValue).toFixed(2)]);

        const handleDownloadPDF = () => {
          downloadPDF(headers, rows, language === 'sw' ? 'Muhtasari wa Thamani ya Bidhaa' : 'Inventory Valuation Summary', 'inventory-valuation-summary.pdf');
        };

        const handleDownloadCSV = () => {
          downloadCSV([headers, ...rows], 'stock-report.csv');
        };

        const handleDownloadExcel = () => {
          downloadExcel([headers, ...rows], 'stock-report.xlsx');
        };

        const totalValue = stockData.reduce((s, p) => s + p.totalValue, 0);
        const totalQty = stockData.reduce((s, p) => s + p.quantity, 0);
        const sw = language === 'sw';
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'var(--bg-main)', zIndex: 9999, display: 'flex', flexDirection: 'column', overflowY: 'auto', paddingBottom: 80 }}>
            {/* Header -back arrow + title (matches Product History) */}
            <div style={{ display: 'flex', alignItems: 'center', padding: '20px 20px 8px', gap: 10 }}>
              <button onClick={() => setShowStockReport(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px 4px 0', display: 'flex', alignItems: 'center' }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--text-primary)" strokeWidth="2.5" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
              </button>
              <h1 style={{ fontSize: 20, fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>{sw ? 'Muhtasari wa Thamani ya Bidhaa' : 'Inventory Valuation Summary'}</h1>
            </div>

            <div style={{ padding: '0 16px' }}>
              {/* Summary card -2 small + 1 wide value (long currency strings need room) */}
              <div style={{ background: 'var(--bg-card)', borderRadius: 16, padding: '14px 16px', marginBottom: 12, boxShadow: 'var(--shadow-card)' }}>
                <div style={{ fontWeight: 800, fontSize: 16, color: 'var(--text-primary)', marginBottom: 10 }}>{sw ? 'Muhtasari wa Stock' : 'Inventory Summary'}</div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  <div style={{ flex: 1, background: 'var(--bg-input, #F3F4F6)', borderRadius: 10, padding: '8px 10px', textAlign: 'center' }}>
                    <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 2 }}>{sw ? 'Bidhaa' : 'Products'}</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-primary)' }}>{stockData.length}</div>
                  </div>
                  <div style={{ flex: 1, background: 'var(--bg-input, #F3F4F6)', borderRadius: 10, padding: '8px 10px', textAlign: 'center' }}>
                    <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 2 }}>{sw ? 'Vipande' : 'Total Units'}</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-primary)' }}>{totalQty}</div>
                  </div>
                </div>
                <div style={{ background: '#F0FDF4', borderRadius: 10, padding: '8px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#166534' }}>{sw ? 'Jumla ya Thamani' : 'Total Stock Value'}</span>
                  <span style={{ fontSize: 16, fontWeight: 800, color: '#059669', whiteSpace: 'nowrap' }}>{fmt(totalValue)}</span>
                </div>
              </div>

              {/* WAC explainer moved to the User Manual (see USER_MANUAL_WAC.md). */}

              {/* Download buttons -compact */}
              <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
                <button onClick={handleDownloadPDF} style={{ flex: 1, padding: '7px 10px', borderRadius: 8, background: '#EF4444', color: '#fff', border: 'none', fontWeight: 700, fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap' }}>PDF</button>
                <button onClick={handleDownloadCSV} style={{ flex: 1, padding: '7px 10px', borderRadius: 8, background: '#059669', color: '#fff', border: 'none', fontWeight: 700, fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap' }}>CSV</button>
                <button onClick={handleDownloadExcel} style={{ flex: 1, padding: '7px 10px', borderRadius: 8, background: '#2563EB', color: '#fff', border: 'none', fontWeight: 700, fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap' }}>Excel</button>
              </div>

              {/* Stock table -matches Product History table styling */}
              {stockData.length === 0 ? (
                <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-secondary)', background: 'var(--bg-card)', borderRadius: 12 }}>
                  {t.noProductsFound || 'No products in stock'}
                </div>
              ) : (
                <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                  <table className="st-table" style={{ width: '100%', minWidth: 480 }}>
                    <thead>
                      <tr>
                        <th style={{ whiteSpace: 'nowrap' }}>{t.product || 'Product'}</th>
                        <th style={{ whiteSpace: 'nowrap' }}>{t.category || 'Category'}</th>
                        <th style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>{sw ? 'Idadi' : 'Stock'}</th>
                        <th style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{sw ? 'Bei (WAC)' : 'Unit Cost (WAC)'}</th>
                        <th style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{sw ? 'Thamani (WAC)' : 'Value (WAC)'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stockData.map((p) => (
                        <tr key={p.productName}>
                          <td style={{ fontWeight: 700, fontSize: 13 }}>{p.productName}</td>
                          <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{p.category}</td>
                          <td style={{ textAlign: 'center', fontWeight: 700 }}>
                            <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 8, fontSize: 12, fontWeight: 700, background: '#ECFCCB', color: '#059669' }}>{p.quantity}</span>
                          </td>
                          <td style={{ textAlign: 'right', fontSize: 12, color: 'var(--text-secondary)' }}>{fmt(p.unitCost)}</td>
                          <td style={{ textAlign: 'right', fontWeight: 800, color: '#059669' }}>{fmt(p.totalValue)}</td>
                        </tr>
                      ))}
                      <tr style={{ background: '#F0FDF4', fontWeight: 800 }}>
                        <td style={{ fontSize: 13, fontWeight: 800 }}>{sw ? 'Jumla' : 'Total'}</td>
                        <td></td>
                        <td style={{ textAlign: 'center', fontWeight: 800 }}>{totalQty}</td>
                        <td></td>
                        <td style={{ textAlign: 'right', fontWeight: 800, color: '#059669', fontSize: 14 }}>{fmt(totalValue)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        );
      })()}


    </div>
  );
}

function AppWithBoundary() {
  return (
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  );
}

export default AppWithBoundary;